// src/hooks/use-file-drag-and-drop.ts
import { useState } from "react";
var useFileDragAndDrop = ({
  onDropHandler
}) => {
  const [dropDepth, setDropDepth] = useState(0);
  const isDragging = dropDepth > 0;
  const onDragOver = (e) => {
    e.preventDefault();
  };
  const onDragEnter = () => {
    setDropDepth((prev) => prev + 1);
  };
  const onDragLeave = () => {
    setDropDepth((prev) => prev - 1);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDropDepth(0);
    const {
      files
    } = e.dataTransfer;
    onDropHandler(Array.from(files));
  };
  return {
    isDragging,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop
  };
};

// src/hooks/use-media-query.ts
import { useEffect, useState as useState2 } from "react";

// src/constants.ts
var Z_INDEX_MODALS = 2147483100;
var Z_INDEX_GLOBAL_SPINNER = 2147483200;
var Z_INDEX_TOASTER = 2147483300;
var breakpoints = {
  tablet: 737,
  desktop: 1025,
  desktopLarge: 1281
};

// src/hooks/use-media-query.ts
var getMatches = (query) => {
  if (typeof window === "undefined")
    return false;
  return window.matchMedia(query).matches;
};
var useMediaQuery = (query) => {
  const [matches, setMatches] = useState2(false);
  useEffect(() => {
    if (typeof window !== "undefined")
      setMatches(getMatches(query));
  }, [query]);
  useEffect(() => {
    const listener = () => {
      setMatches(getMatches(query));
    };
    const options = {
      passive: true
    };
    window.addEventListener("resize", listener, options);
    return () => {
      window.removeEventListener("resize", listener);
    };
  }, [query]);
  return matches;
};
var useIsTablet = () => useMediaQuery(`(min-width: ${breakpoints.tablet}px)`);
var useIsDesktop = () => useMediaQuery(`(min-width: ${breakpoints.desktop}px)`);
var useIsDesktopLarge = () => useMediaQuery(`(min-width: ${breakpoints.desktopLarge}px)`);

// src/styles.ts
import { createStatic } from "styled-components-breakpoint";

// src/tokens/colors.ts
import { hexToRgbA } from "@scouts/helpers";
var theme = {
  primary: {
    backgroundLight: "#ECFFC7",
    background: "#CEF5A4",
    main: "#154618",
    light: "#36893B",
    primaryAction: "#A0D766",
    secondaryAction: hexToRgbA("#A9D481", 0.4),
    hover: hexToRgbA("#A9D481", 0.2),
    pressed: hexToRgbA("#A9D481", 0.4),
    contrast: "#154618",
    resting: hexToRgbA("#A9D481", 0.4)
  },
  secondary: {
    backgroundLight: "#E8F0FF",
    background: "#B6C5F3",
    main: "#4C4991",
    light: "#668CFF",
    primaryAction: "#C6D2F6",
    secondaryAction: hexToRgbA("#C6D2F6", 0.4),
    hover: hexToRgbA("#C6D2F6", 0.2),
    pressed: hexToRgbA("#C6D2F6", 0.4),
    contrast: "#4C4991",
    resting: hexToRgbA("#C6D2F6", 0.4)
  },
  neutral: {
    backgroundLight: "#F9F7F5",
    background: "#F2EFED",
    main: hexToRgbA("#0C0B0A", 0.8),
    light: hexToRgbA("#0C0B0A", 0.65),
    primaryAction: "#DCD8D2",
    secondaryAction: hexToRgbA("#DCD8D2", 0.5),
    hover: hexToRgbA("#DCD8D2", 0.25),
    pressed: hexToRgbA("#DCD8D2", 0.5),
    contrast: hexToRgbA("#0C0B0A", 0.8),
    resting: hexToRgbA("#DCD8D2", 0.5)
  },
  success: {
    backgroundLight: "#ECFFC7",
    background: "#CEF5A4",
    main: "#154618",
    light: "#36893B",
    primaryAction: "#A0D766",
    secondaryAction: hexToRgbA("#A9D481", 0.4),
    hover: hexToRgbA("#A9D481", 0.2),
    pressed: hexToRgbA("#A9D481", 0.4),
    contrast: "#154618",
    resting: hexToRgbA("#A9D481", 0.4)
  },
  error: {
    backgroundLight: "#FEEBE7",
    background: "#F5A894",
    main: "#912003",
    light: "#FB5E36",
    primaryAction: "#FBB3A2",
    secondaryAction: hexToRgbA("#FBB3A2", 0.4),
    hover: hexToRgbA("#FBB3A2", 0.2),
    pressed: hexToRgbA("#FBB3A2", 0.4),
    contrast: "#912003",
    resting: hexToRgbA("#FBB3A2", 0.4)
  },
  accent1: {
    backgroundLight: "#FDF8F2",
    background: "#EAE0D7",
    main: "#0C0B0A",
    light: "#9A9288"
  },
  accent2: {
    backgroundLight: "#F6EBFE",
    background: "#DBB9F3",
    main: "#604587",
    light: "#BC73F2"
  },
  accent3: {
    backgroundLight: "#FFEFD3",
    background: "#F8C677",
    main: "#66541A",
    light: "#F8A21A"
  },
  accent4: {
    backgroundLight: "#FFF0F6",
    background: "#FEB9D6",
    main: "#9D2054",
    light: "#F53183"
  },
  accent5: {
    backgroundLight: "#D9F7E9",
    background: "#96E4BE",
    main: "#0D6359",
    light: "#04A491"
  },
  accent6: {
    backgroundLight: "#F2F5F7",
    background: "#BDCCD6",
    main: "#415562",
    light: "#758E9F"
  },
  accent7: {
    backgroundLight: "#FDF9D3",
    background: "#EFE272",
    main: "#5C5200",
    light: "#FDE200"
  },
  accent8: {
    backgroundLight: "#FCF6F2",
    background: "#ECD3C1",
    main: "#824417",
    light: "#CE7B40"
  },
  text: {
    title: "#0C0B0A",
    primary: hexToRgbA("#0C0B0A", 0.8),
    secondary: hexToRgbA("#0C0B0A", 0.65),
    disabled: hexToRgbA("#0C0B0A", 0.5),
    contrastText: "#FFFFFF"
  },
  grey: {
    background: "#FFFFFF",
    surface1: "#F2EFED",
    surface2: "#F9F7F5",
    surface3: "#FFFFFF",
    divider: hexToRgbA("#0C0B0A", 0.12),
    dividerContrast: hexToRgbA("#FFFFFF", 0.16),
    graphics1: "#0C0B0A",
    graphics2: "#96928E",
    graphics3: "#E4E1DD",
    graphics90: hexToRgbA("#0C0B0A", 0.9),
    graphics70: hexToRgbA("#0C0B0A", 0.7),
    graphics30: hexToRgbA("#0C0B0A", 0.3),
    graphics10: hexToRgbA("#0C0B0A", 0.1)
  },
  common: {
    active: hexToRgbA("#A0D766", 0.2),
    hover: hexToRgbA("#0C0B0A", 0.1),
    pressed: hexToRgbA("#0C0B0A", 0.2),
    disabled1: "#E4E1DD",
    disabled2: "#F2EFED",
    handle: "#FFFFFF"
  },
  background: {
    sky: "#E8F0FF",
    straw: "#FDF9D3",
    peach: "#FEEBE7",
    lime: "#ECFFC7",
    background1: "#E8F0FF",
    background2: "#FDF9D3",
    background3: "#FEEBE7",
    background4: "#ECFFC7",
    background5: "#F6EBFE",
    background6: "#FFEFD3",
    background7: "#FFF0F6",
    background8: "#D9F7E9",
    background9: "#F2F5F7",
    background10: "#FCF6F2"
  }
};
var mint = {
  20: "#dcfdf3",
  40: "#a4f5d3",
  60: "#6fe69d",
  70: "#16cf98",
  // old mint
  80: "#007963"
};
var yellow = {
  // 20: undefined,
  40: "#fff4a0",
  60: "#ffd676"
  // 80: undefined,
};
var colors = {
  surface: {
    base: theme.grey.background,
    inverted: theme.grey.graphics1,
    neutral: theme.neutral.backgroundLight,
    // @TODO: Could merge?
    highlightSoft: theme.secondary.backgroundLight,
    highlight: theme.secondary.backgroundLight,
    affirmSoft: theme.primary.backgroundLight,
    affirm: theme.primary.background,
    affirmInverted: theme.primary.contrast,
    // @TODO: Remove, replace with accent
    accentSoft: theme.accent2.backgroundLight,
    accent: theme.accent2.backgroundLight,
    accentInverted: theme.accent2.main,
    attention: theme.accent3.backgroundLight,
    alert: theme.error.background,
    // @TODO: TBD how to approach 'interactive'
    interactive: theme.primary.primaryAction,
    // @TODO: 'Note' is too specific, remove?
    note: theme.accent3.backgroundLight,
    noteStrong: theme.accent3.background,
    // @TODO: Remove
    affirmStrong: mint[60],
    // NOT USED-ish
    attentionStrong: yellow[60]
    // NOT USED-ish
  },
  text: {
    base: theme.text.title,
    inverted: theme.text.contrastText,
    neutral: theme.text.secondary,
    interactive: theme.primary.main,
    affirm: theme.success.main,
    alert: theme.error.main
  },
  line: {
    base: theme.grey.graphics1,
    inverted: theme.grey.background,
    // @TODO: Could merge neutral + neutralLight?
    neutral: theme.grey.graphics2,
    neutralLight: theme.grey.graphics3,
    neutralSoft: theme.neutral.backgroundLight,
    affirm: theme.primary.background,
    highlight: theme.secondary.background,
    // @TODO: TBD how to approach 'interactive'
    interactive: theme.secondary.light
  },
  icon: {
    base: theme.grey.graphics1,
    neutral: theme.grey.graphics2
  },
  brand: {
    whatsapp: "#25d366",
    facebook: "#4267B2",
    google: "#DB4437"
  }
};

// src/tokens/borders.ts
var borders = {
  interactive: {
    thin: `1px solid ${colors.line.interactive}`,
    medium: `2px solid ${colors.line.interactive}`
  }
};

// src/tokens/radius.ts
var radius = {
  small: "4px",
  medium: "8px",
  large: "16px",
  xlarge: "24px",
  xxlarge: "48px",
  full: "9999px",
  buttonMedium: "8px",
  buttonLarge: "10px"
};

// src/tokens/shadows.ts
var shadows = {
  low: "0px 0px 2px rgba(0, 0, 0, 0.08), 1px 2px 8px rgba(0, 0, 0, 0.08)",
  medium: "0px 0px 2px rgba(0, 0, 0, 0.08), 2px 4px 16px rgba(0, 0, 0, 0.08)",
  high: "0px 0px 2px rgba(0, 0, 0, 0.08), 2px 4px 24px rgba(0, 0, 0, 0.08)",
  higher: "0px 0px 2px rgba(0, 0, 0, 0.08), 4px 8px 40px rgba(0, 0, 0, 0.08)"
};

// src/tokens/fonts.ts
var legacyFontSizes = {
  /** @deprecated */
  tiny: "10px",
  /** @deprecated */
  smaller: "12px",
  /** @deprecated */
  small: "14px",
  /** @deprecated */
  normal: "16px",
  /** @deprecated */
  large: "20px",
  /** @deprecated */
  larger: "24px",
  /** @deprecated */
  big: "36px",
  /** @deprecated */
  huge: "48px"
};
var font = {
  family: {
    body: "ABCROM, Helvetica Neue, helvetica, arial, sans-serif",
    display: "ABCROMExtended, arial, sans-serif"
  },
  weight: {
    normal: "normal",
    medium: "500",
    semiBold: "600",
    bold: "700"
  },
  body: {
    small: "12px",
    medium: "14px",
    large: "16px",
    xlarge: "20px"
  },
  title: {
    smaller: "12px",
    small: "14px",
    medium: "16px",
    large: "20px",
    xlarge: "24px"
  },
  display: {
    small: "20px",
    medium: "24px",
    large: "30px",
    xlarge: "36px",
    xxlarge: "48px"
  },
  ...legacyFontSizes
};
var emToPx = (baseVal, emVal) => baseVal * emVal;
var themeFonts = {
  fonts: {
    ABCROM: "ABCROM, Helvetica Neue, helvetica, arial, sans-serif",
    ABCROMExtended: "ABCROMExtended, arial, sans-serif"
  },
  typography: {
    heading1Large: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 32,
        sm: 48,
        lg: 64
      },
      fontWeight: 700,
      letterSpacing: {
        xs: emToPx(32, -0.01),
        sm: emToPx(48, -0.01),
        lg: emToPx(64, -0.01)
      },
      lineHeight: {
        xs: 40,
        sm: 60,
        lg: 84
      }
    },
    heading2Large: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 32,
        sm: 48,
        lg: 64
      },
      fontWeight: 700,
      letterSpacing: {
        xs: emToPx(32, -0.01),
        sm: emToPx(48, -0.01),
        lg: emToPx(64, -0.01)
      },
      lineHeight: {
        xs: 40,
        sm: 60,
        lg: 84
      }
    },
    h1: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 28,
        sm: 40,
        lg: 48
      },
      fontWeight: 700,
      letterSpacing: {
        xs: emToPx(28, -0.01),
        sm: emToPx(40, -0.01),
        lg: emToPx(48, -0.01)
      },
      lineHeight: {
        xs: 36,
        sm: 52,
        lg: 60
      }
    },
    h2: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 24,
        sm: 32,
        lg: 40
      },
      fontWeight: 700,
      letterSpacing: {
        xs: emToPx(24, -0.01),
        sm: emToPx(32, -0.01),
        lg: emToPx(40, -0.01)
      },
      lineHeight: {
        xs: 32,
        sm: 40,
        lg: 52
      }
    },
    h3: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 20,
        sm: 24,
        lg: 28
      },
      fontWeight: 700,
      letterSpacing: {
        xs: emToPx(20, -0.01),
        sm: emToPx(24, -0.01),
        lg: emToPx(28, -0.01)
      },
      lineHeight: {
        xs: 28,
        sm: 32,
        lg: 36
      }
    },
    h4: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 18,
        sm: 20
      },
      fontWeight: 700,
      letterSpacing: {
        xs: emToPx(18, -0.01),
        sm: emToPx(20, -0.01)
      },
      lineHeight: {
        xs: 24,
        sm: 28
      }
    },
    h5: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 16
      },
      fontWeight: 700,
      letterSpacing: {
        xs: emToPx(16, -0.01)
      },
      lineHeight: {
        xs: 20
      }
    },
    h6: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 14
      },
      fontWeight: 700,
      letterSpacing: {
        xs: emToPx(14, -0.01)
      },
      lineHeight: {
        xs: 20
      }
    },
    "accent-h1": {
      fontFamily: "ABCROMExtended",
      fontSize: {
        xs: 28,
        sm: 40,
        lg: 48
      },
      fontWeight: 900,
      letterSpacing: {
        xs: emToPx(28, -0.01),
        sm: emToPx(40, -0.01),
        lg: emToPx(48, -0.01)
      },
      lineHeight: {
        xs: 36,
        sm: 52,
        lg: 60
      }
    },
    "accent-h2": {
      fontFamily: "ABCROMExtended",
      fontSize: {
        xs: 24,
        sm: 32,
        lg: 40
      },
      fontWeight: 900,
      letterSpacing: {
        xs: emToPx(24, -0.01),
        sm: emToPx(32, -0.01),
        lg: emToPx(40, -0.01)
      },
      lineHeight: {
        xs: 32,
        sm: 40,
        lg: 52
      }
    },
    "accent-h3": {
      fontFamily: "ABCROMExtended",
      fontSize: {
        xs: 20,
        sm: 24,
        lg: 28
      },
      fontWeight: 900,
      letterSpacing: {
        xs: emToPx(20, -0.01),
        sm: emToPx(24, -0.01),
        lg: emToPx(28, -0.01)
      },
      lineHeight: {
        xs: 28,
        sm: 32,
        lg: 36
      }
    },
    "accent-h4": {
      fontFamily: "ABCROMExtended",
      fontSize: {
        xs: 18,
        sm: 20
      },
      fontWeight: 900,
      letterSpacing: {
        xs: emToPx(18, -0.01),
        sm: emToPx(20, -0.01)
      },
      lineHeight: {
        xs: 24,
        sm: 28
      }
    },
    "accent-h5": {
      fontFamily: "ABCROMExtended",
      fontSize: {
        xs: 16
      },
      fontWeight: 900,
      letterSpacing: {
        xs: emToPx(16, -0.01)
      },
      lineHeight: {
        xs: 20
      }
    },
    "accent-h6": {
      fontFamily: "ABCROMExtended",
      fontSize: {
        xs: 14
      },
      fontWeight: 900,
      letterSpacing: {
        xs: emToPx(14, -0.01)
      },
      lineHeight: {
        xs: 20
      }
    },
    bodyLarge: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 20,
        sm: 24,
        lg: 28
      },
      fontWeight: 400,
      lineHeight: {
        xs: 28,
        sm: 32,
        lg: 36
      }
    },
    body: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 16
      },
      fontWeight: 400,
      lineHeight: {
        xs: 20
      }
    },
    bodyLong: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 16
      },
      fontWeight: 400,
      lineHeight: {
        xs: 24
      }
    },
    bodySmall: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 14
      },
      fontWeight: 400,
      lineHeight: {
        xs: 20
      }
    },
    overline: {
      fontFamily: "ABCROM",
      textTransform: "uppercase",
      fontSize: {
        xs: 10,
        lg: 12
      },
      fontWeight: 500,
      letterSpacing: {
        xs: emToPx(10, 0.04),
        lg: emToPx(12, 0.04)
      },
      lineHeight: {
        xs: 16
      }
    },
    link: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 16
      },
      fontWeight: 500,
      lineHeight: {
        xs: 20
      },
      textTransform: "none"
    },
    linkSmall: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 14
      },
      fontWeight: 500,
      lineHeight: {
        xs: 20
      },
      textTransform: "none"
    },
    button: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 16
      },
      fontWeight: 500,
      lineHeight: {
        xs: 20
      }
    },
    buttonSmall: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 14
      },
      fontWeight: 500,
      lineHeight: {
        xs: 20
      }
    },
    buttonCaption: {
      fontFamily: "ABCROM",
      fontSize: {
        xs: 12
      },
      fontWeight: 500,
      lineHeight: {
        xs: 16
      }
    }
  }
};

// src/styles.ts
var {
  tablet,
  desktop,
  desktopLarge
} = createStatic(breakpoints);

// src/media.ts
var media = {
  tablet: `@media (min-width: ${breakpoints.tablet}px)`,
  desktop: `@media (min-width: ${breakpoints.desktop}px)`,
  desktopLarge: `@media (min-width: ${breakpoints.desktopLarge}px)`,
  pointer: "@media (pointer: fine)"
};

// src/icons/line/Icon360Degrees.tsx
import { jsx as _jsx } from "react/jsx-runtime";
var SvgIcon360Degrees = (props) => /* @__PURE__ */ _jsx("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx("path", {
    d: "M22 5c0-1.657-5.03-3-10-3S2 3.343 2 5m20 0v14c0 1.5-3 3-7 3V8c4 0 7-1.694 7-3ZM2 5v14c0 1.5 4 3 7 3V8C5.5 8 2 6.306 2 5Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var Icon360Degrees_default = SvgIcon360Degrees;

// src/icons/line/IconActivity2.tsx
import { jsx as _jsx2 } from "react/jsx-runtime";
var SvgIconActivity2 = (props) => /* @__PURE__ */ _jsx2("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx2("path", {
    d: "M2 12h4l3 7 6-14 3 7h4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconActivity2_default = SvgIconActivity2;

// src/icons/line/IconActivityRec.tsx
import { jsx as _jsx3 } from "react/jsx-runtime";
var SvgIconActivityRec = (props) => /* @__PURE__ */ _jsx3("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx3("path", {
    d: "M6 12h2l2 3 4-6 2 3h2m-6.4 10h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconActivityRec_default = SvgIconActivityRec;

// src/icons/line/IconAirplay.tsx
import { jsx as _jsx4 } from "react/jsx-runtime";
var SvgIconAirplay = (props) => /* @__PURE__ */ _jsx4("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx4("path", {
    d: "M17.5 18h.5v0a4 4 0 0 0 4-4v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4a4 4 0 0 0 4 4v0h.5m7.403-.097-.206-.206c-.594-.594-.891-.891-1.233-1.002a1.5 1.5 0 0 0-.928 0c-.342.111-.639.408-1.233 1.002l-.206.206c-1.285 1.285-1.928 1.928-1.971 2.48a1.5 1.5 0 0 0 .521 1.258c.42.359 1.33.359 3.147.359h.412c1.817 0 2.726 0 3.147-.36a1.5 1.5 0 0 0 .521-1.258c-.043-.551-.686-1.194-1.971-2.48Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAirplay_default = SvgIconAirplay;

// src/icons/line/IconAlertCircle.tsx
import { jsx as _jsx5 } from "react/jsx-runtime";
var SvgIconAlertCircle = (props) => /* @__PURE__ */ _jsx5("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx5("path", {
    d: "M12 13V7m.5 9.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlertCircle_default = SvgIconAlertCircle;

// src/icons/line/IconAlertOctagon.tsx
import { jsx as _jsx6 } from "react/jsx-runtime";
var SvgIconAlertOctagon = (props) => /* @__PURE__ */ _jsx6("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx6("path", {
    d: "M12 13V7m.5 9.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-7.625 1.375 2.25 2.25c.692.692 1.038 1.038 1.442 1.286a4 4 0 0 0 1.156.479c.46.11.95.11 1.928.11h2.698c.978 0 1.468 0 1.928-.11.408-.099.798-.26 1.156-.48.404-.247.75-.593 1.442-1.285l2.25-2.25c.692-.692 1.038-1.038 1.286-1.442.219-.358.38-.748.479-1.156.11-.46.11-.95.11-1.928v-2.698c0-.978 0-1.468-.11-1.928a4 4 0 0 0-.48-1.156c-.247-.404-.593-.75-1.285-1.442l-2.25-2.25c-.692-.692-1.038-1.038-1.442-1.286a3.998 3.998 0 0 0-1.156-.478C14.817 2 14.327 2 13.349 2h-2.698c-.978 0-1.468 0-1.928.11a4 4 0 0 0-1.156.48c-.404.247-.75.593-1.442 1.285l-2.25 2.25c-.692.692-1.038 1.038-1.286 1.442a4 4 0 0 0-.478 1.156C2 9.183 2 9.673 2 10.651v2.698c0 .978 0 1.468.11 1.928.099.408.26.798.48 1.156.247.404.593.75 1.285 1.442Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlertOctagon_default = SvgIconAlertOctagon;

// src/icons/line/IconAlertTriangle.tsx
import { jsx as _jsx7 } from "react/jsx-runtime";
var SvgIconAlertTriangle = (props) => /* @__PURE__ */ _jsx7("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx7("path", {
    d: "M12 13V9m.5 7.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m7.831-6.409-.351-.627c-2.281-4.073-3.422-6.11-4.922-6.788a5 5 0 0 0-4.116 0c-1.5.678-2.64 2.715-4.922 6.788l-.351.627c-2.193 3.916-3.29 5.874-3.106 7.477a5 5 0 0 0 2.045 3.489C4.917 22 7.16 22 11.649 22h.702c4.488 0 6.732 0 8.041-.943a5 5 0 0 0 2.045-3.489c.183-1.603-.913-3.561-3.106-7.477Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlertTriangle_default = SvgIconAlertTriangle;

// src/icons/line/IconAlignBottom2.tsx
import { jsx as _jsx8 } from "react/jsx-runtime";
var SvgIconAlignBottom2 = (props) => /* @__PURE__ */ _jsx8("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx8("path", {
    d: "M3 22h18m-9.2-4h.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C17 15.72 17 14.88 17 13.2V6.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C14.72 2 13.88 2 12.2 2h-.4c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C7 4.28 7 5.12 7 6.8v6.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C9.28 18 10.12 18 11.8 18Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignBottom2_default = SvgIconAlignBottom2;

// src/icons/line/IconAlignBottomRec.tsx
import { jsx as _jsx9 } from "react/jsx-runtime";
var SvgIconAlignBottomRec = (props) => /* @__PURE__ */ _jsx9("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx9("path", {
    d: "M12 6v8m0 0-2.5-2.5M12 14l2.461-2.461M7 18h10m-7 4h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignBottomRec_default = SvgIconAlignBottomRec;

// src/icons/line/IconAlignBottom.tsx
import { jsx as _jsx10 } from "react/jsx-runtime";
var SvgIconAlignBottom = (props) => /* @__PURE__ */ _jsx10("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx10("path", {
    d: "M12 3v13m0 0-4-4m4 4 4-4M3 21h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignBottom_default = SvgIconAlignBottom;

// src/icons/line/IconAlignCenter2Rec.tsx
import { jsx as _jsx11 } from "react/jsx-runtime";
var SvgIconAlignCenter2Rec = (props) => /* @__PURE__ */ _jsx11("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx11("path", {
    d: "M7 17h10m-8-5h6M7 7h10m-7 15h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignCenter2Rec_default = SvgIconAlignCenter2Rec;

// src/icons/line/IconAlignCenter2.tsx
import { jsx as _jsx12 } from "react/jsx-runtime";
var SvgIconAlignCenter2 = (props) => /* @__PURE__ */ _jsx12("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx12("path", {
    d: "M3 20h18M7 12h10M3 4h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignCenter2_default = SvgIconAlignCenter2;

// src/icons/line/IconAlignCenterRec.tsx
import { jsx as _jsx13 } from "react/jsx-runtime";
var SvgIconAlignCenterRec = (props) => /* @__PURE__ */ _jsx13("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx13("path", {
    d: "M9 18h6m-9-4h12M8 10h8M6 6h12m-8 16h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignCenterRec_default = SvgIconAlignCenterRec;

// src/icons/line/IconAlignCenter.tsx
import { jsx as _jsx14 } from "react/jsx-runtime";
var SvgIconAlignCenter = (props) => /* @__PURE__ */ _jsx14("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx14("path", {
    d: "M8 21h8M6 9h12M3 15h18M3 3h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignCenter_default = SvgIconAlignCenter;

// src/icons/line/IconAlignHorizontally.tsx
import { jsx as _jsx15 } from "react/jsx-runtime";
var SvgIconAlignHorizontally = (props) => /* @__PURE__ */ _jsx15("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx15("path", {
    d: "M12 2v5m0 10v5m-4.2-5h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 14.72 21 13.88 21 12.2v-.4c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C18.72 7 17.88 7 16.2 7H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 9.28 3 10.12 3 11.8v.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 17 6.12 17 7.8 17Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignHorizontally_default = SvgIconAlignHorizontally;

// src/icons/line/IconAlignJustify2Rec.tsx
import { jsx as _jsx16 } from "react/jsx-runtime";
var SvgIconAlignJustify2Rec = (props) => /* @__PURE__ */ _jsx16("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx16("path", {
    d: "M7 17h10M7 12h10M7 7h10m-7 15h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignJustify2Rec_default = SvgIconAlignJustify2Rec;

// src/icons/line/IconAlignJustify2.tsx
import { jsx as _jsx17 } from "react/jsx-runtime";
var SvgIconAlignJustify2 = (props) => /* @__PURE__ */ _jsx17("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx17("path", {
    d: "M3 20h18M3 12h18M3 4h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignJustify2_default = SvgIconAlignJustify2;

// src/icons/line/IconAlignJustifyRec.tsx
import { jsx as _jsx18 } from "react/jsx-runtime";
var SvgIconAlignJustifyRec = (props) => /* @__PURE__ */ _jsx18("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx18("path", {
    d: "M6 18h12M6 14h12M6 10h12M6 6h12m-8 16h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignJustifyRec_default = SvgIconAlignJustifyRec;

// src/icons/line/IconAlignJustify.tsx
import { jsx as _jsx19 } from "react/jsx-runtime";
var SvgIconAlignJustify = (props) => /* @__PURE__ */ _jsx19("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx19("path", {
    d: "M3 21h18M3 15h18M3 9h18M3 3h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignJustify_default = SvgIconAlignJustify;

// src/icons/line/IconAlignLeft2Rec.tsx
import { jsx as _jsx20 } from "react/jsx-runtime";
var SvgIconAlignLeft2Rec = (props) => /* @__PURE__ */ _jsx20("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx20("path", {
    d: "M7 17h10M7 12h6M7 7h10m-7 15h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignLeft2Rec_default = SvgIconAlignLeft2Rec;

// src/icons/line/IconAlignLeft2.tsx
import { jsx as _jsx21 } from "react/jsx-runtime";
var SvgIconAlignLeft2 = (props) => /* @__PURE__ */ _jsx21("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx21("path", {
    d: "M3 20h18M3 12h10M3 4h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignLeft2_default = SvgIconAlignLeft2;

// src/icons/line/IconAlignLeft3.tsx
import { jsx as _jsx22 } from "react/jsx-runtime";
var SvgIconAlignLeft3 = (props) => /* @__PURE__ */ _jsx22("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx22("path", {
    d: "M2 3v18m8.8-4h6.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C22 14.72 22 13.88 22 12.2v-.4c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C19.72 7 18.88 7 17.2 7h-6.4c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C6 9.28 6 10.12 6 11.8v.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C8.28 17 9.12 17 10.8 17Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignLeft3_default = SvgIconAlignLeft3;

// src/icons/line/IconAlignLeftRec.tsx
import { jsx as _jsx23 } from "react/jsx-runtime";
var SvgIconAlignLeftRec = (props) => /* @__PURE__ */ _jsx23("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx23("path", {
    d: "M6 18h6m-6-4h10M6 10h8M6 6h12m-8 16h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignLeftRec_default = SvgIconAlignLeftRec;

// src/icons/line/IconAlignLeft.tsx
import { jsx as _jsx24 } from "react/jsx-runtime";
var SvgIconAlignLeft = (props) => /* @__PURE__ */ _jsx24("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx24("path", {
    d: "M3 21h8m-8-6h14M3 9h12M3 3h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignLeft_default = SvgIconAlignLeft;

// src/icons/line/IconAlignMiddleRec.tsx
import { jsx as _jsx25 } from "react/jsx-runtime";
var SvgIconAlignMiddleRec = (props) => /* @__PURE__ */ _jsx25("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx25("path", {
    d: "M12 4.5V9m0 0-2-2m2 2 2-2m-2 12.5V15m0 0-2 2m2-2 2 2m-8-5h12m-8 10h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignMiddleRec_default = SvgIconAlignMiddleRec;

// src/icons/line/IconAlignMiddle.tsx
import { jsx as _jsx26 } from "react/jsx-runtime";
var SvgIconAlignMiddle = (props) => /* @__PURE__ */ _jsx26("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx26("path", {
    d: "M12 2v7m0 0L9 6m3 3 3-3m-3 16v-7m0 0-3 3m3-3 3 3M3 12h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignMiddle_default = SvgIconAlignMiddle;

// src/icons/line/IconAlignRight2Rec.tsx
import { jsx as _jsx27 } from "react/jsx-runtime";
var SvgIconAlignRight2Rec = (props) => /* @__PURE__ */ _jsx27("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx27("path", {
    d: "M7 17h10m-6-5h6M7 7h10m-7 15h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignRight2Rec_default = SvgIconAlignRight2Rec;

// src/icons/line/IconAlignRight2.tsx
import { jsx as _jsx28 } from "react/jsx-runtime";
var SvgIconAlignRight2 = (props) => /* @__PURE__ */ _jsx28("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx28("path", {
    d: "M3 20h18m-10-8h10M3 4h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignRight2_default = SvgIconAlignRight2;

// src/icons/line/IconAlignRight3.tsx
import { jsx as _jsx29 } from "react/jsx-runtime";
var SvgIconAlignRight3 = (props) => /* @__PURE__ */ _jsx29("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx29("path", {
    d: "M22 3v18M6.8 17h6.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C18 14.72 18 13.88 18 12.2v-.4c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C15.72 7 14.88 7 13.2 7H6.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 9.28 2 10.12 2 11.8v.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 17 5.12 17 6.8 17Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignRight3_default = SvgIconAlignRight3;

// src/icons/line/IconAlignRightRec.tsx
import { jsx as _jsx30 } from "react/jsx-runtime";
var SvgIconAlignRightRec = (props) => /* @__PURE__ */ _jsx30("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx30("path", {
    d: "M12 18h6M8 14h10m-8-4h8M6 6h12m-8 16h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignRightRec_default = SvgIconAlignRightRec;

// src/icons/line/IconAlignRight.tsx
import { jsx as _jsx31 } from "react/jsx-runtime";
var SvgIconAlignRight = (props) => /* @__PURE__ */ _jsx31("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx31("path", {
    d: "M13 21h8M7 15h14M9 9h12M3 3h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignRight_default = SvgIconAlignRight;

// src/icons/line/IconAlignTop2.tsx
import { jsx as _jsx32 } from "react/jsx-runtime";
var SvgIconAlignTop2 = (props) => /* @__PURE__ */ _jsx32("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx32("path", {
    d: "M3 2h18m-9.2 20h.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C17 19.72 17 18.88 17 17.2v-6.4c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C14.72 6 13.88 6 12.2 6h-.4c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C7 8.28 7 9.12 7 10.8v6.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C9.28 22 10.12 22 11.8 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignTop2_default = SvgIconAlignTop2;

// src/icons/line/IconAlignTopRec.tsx
import { jsx as _jsx33 } from "react/jsx-runtime";
var SvgIconAlignTopRec = (props) => /* @__PURE__ */ _jsx33("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx33("path", {
    d: "M12 18v-8m0 0-2.5 2.5M12 10l2.461 2.461M7 6h10m-7 16h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignTopRec_default = SvgIconAlignTopRec;

// src/icons/line/IconAlignTop.tsx
import { jsx as _jsx34 } from "react/jsx-runtime";
var SvgIconAlignTop = (props) => /* @__PURE__ */ _jsx34("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx34("path", {
    d: "M12 21V8m0 0-4 4m4-4 4 4M3 3h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignTop_default = SvgIconAlignTop;

// src/icons/line/IconAlignVertically.tsx
import { jsx as _jsx35 } from "react/jsx-runtime";
var SvgIconAlignVertically = (props) => /* @__PURE__ */ _jsx35("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx35("path", {
    d: "M17 12h5M2 12h5m4.8 9h.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C17 18.72 17 17.88 17 16.2V7.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C14.72 3 13.88 3 12.2 3h-.4c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C7 5.28 7 6.12 7 7.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C9.28 21 10.12 21 11.8 21Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAlignVertically_default = SvgIconAlignVertically;

// src/icons/line/IconAnchorRec.tsx
import { jsx as _jsx36 } from "react/jsx-runtime";
var SvgIconAnchorRec = (props) => /* @__PURE__ */ _jsx36("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx36("path", {
    d: "M12 10a2 2 0 1 1 0-4 2 2 0 0 1 0 4Zm0 0v7m0 0a5 5 0 0 0 5-5h-1.875M12 17a5 5 0 0 1-5-5h1.875M11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAnchorRec_default = SvgIconAnchorRec;

// src/icons/line/IconAnchor.tsx
import { jsx as _jsx37 } from "react/jsx-runtime";
var SvgIconAnchor = (props) => /* @__PURE__ */ _jsx37("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx37("path", {
    d: "M12 8a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm0 0v14m0 0a8 8 0 0 0 8-8h-3m-5 8a8 8 0 0 1-8-8h3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAnchor_default = SvgIconAnchor;

// src/icons/line/IconAndroid.tsx
import { jsx as _jsx38 } from "react/jsx-runtime";
var SvgIconAndroid = (props) => /* @__PURE__ */ _jsx38("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx38("path", {
    d: "M9 19h-.8c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C5 17.48 5 16.92 5 15.8V10m4 9v3m0-3h6m0 0h.8c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C19 17.48 19 16.92 19 15.8V10m-4 9v3M5 10h14M5 10a7 7 0 0 1 14 0M2 10v6m20-6v6M5.5 1.5 8 4m9.5-2.5-2 2M10 7a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H9m6 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAndroid_default = SvgIconAndroid;

// src/icons/line/IconApperture.tsx
import { jsx as _jsx39 } from "react/jsx-runtime";
var SvgIconApperture = (props) => /* @__PURE__ */ _jsx39("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx39("path", {
    d: "M14 16H3m7 0L3.5 7.5m17 9L14 8m-4 13.5 7-9.5M7 12l7-9.5M21 8H10m12 4c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconApperture_default = SvgIconApperture;

// src/icons/line/IconApple.tsx
import { jsx as _jsx40 } from "react/jsx-runtime";
var SvgIconApple = (props) => /* @__PURE__ */ _jsx40("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx40("path", {
    d: "M16.362 12.318c0-1.522.723-2.633 2.127-3.497-.808-1.11-2-1.686-3.574-1.81-1.532-.123-3.192.823-3.787.823-.639 0-2.085-.782-3.234-.782C5.51 7.093 3 8.862 3 12.524c0 1.07.17 2.18.596 3.332.553 1.522 2.51 5.225 4.553 5.143 1.064 0 1.83-.74 3.234-.74 1.362 0 2.043.74 3.234.74 2.085 0 3.872-3.374 4.383-4.896-2.766-1.275-2.638-3.703-2.638-3.785ZM14.996 1c0 .41.127 1.53-1.018 2.724-.848.858-1.865 1.343-2.967 1.268-.085-1.007.339-1.94 1.102-2.686.678-.672 1.865-1.231 2.883-1.306Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconApple_default = SvgIconApple;

// src/icons/line/IconAppstore.tsx
import { jsx as _jsx41 } from "react/jsx-runtime";
var SvgIconAppstore = (props) => /* @__PURE__ */ _jsx41("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx41("path", {
    d: "M15.663 14.105H18m-2.337 0L17.4 17m-1.737-2.895L13.5 10.5M6 14.105h2.337m0 0H13m-4.663 0L12 8m-5.4 9 .6-1M12 8l1.2-2M12 8l-1.2-2M2 14v-4c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAppstore_default = SvgIconAppstore;

// src/icons/line/IconArchive.tsx
import { jsx as _jsx42 } from "react/jsx-runtime";
var SvgIconArchive = (props) => /* @__PURE__ */ _jsx42("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx42("path", {
    d: "M20 6v9.6c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C16.96 22 15.84 22 13.6 22h-3.2c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C4 18.96 4 17.84 4 15.6V6m5 4h6M4 6h16a2 2 0 0 0 2-2v0a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArchive_default = SvgIconArchive;

// src/icons/line/IconArrowBottomCircle.tsx
import { jsx as _jsx43 } from "react/jsx-runtime";
var SvgIconArrowBottomCircle = (props) => /* @__PURE__ */ _jsx43("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx43("path", {
    d: "M12 8v8m0 0 3-3m-3 3-3-3m13-1c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowBottomCircle_default = SvgIconArrowBottomCircle;

// src/icons/line/IconArrowBottomLeftCircle.tsx
import { jsx as _jsx44 } from "react/jsx-runtime";
var SvgIconArrowBottomLeftCircle = (props) => /* @__PURE__ */ _jsx44("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx44("path", {
    d: "m15 9-6 6m0 0v-4.5M9 15h4.5m8.5-3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowBottomLeftCircle_default = SvgIconArrowBottomLeftCircle;

// src/icons/line/IconArrowBottomLeftRec.tsx
import { jsx as _jsx45 } from "react/jsx-runtime";
var SvgIconArrowBottomLeftRec = (props) => /* @__PURE__ */ _jsx45("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx45("path", {
    d: "m16 8-8 8m0 0v-6m0 6h6m-4 6h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowBottomLeftRec_default = SvgIconArrowBottomLeftRec;

// src/icons/line/IconArrowBottomLeft.tsx
import { jsx as _jsx46 } from "react/jsx-runtime";
var SvgIconArrowBottomLeft = (props) => /* @__PURE__ */ _jsx46("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx46("path", {
    d: "M20 4 4 20m0 0V9m0 11h11",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowBottomLeft_default = SvgIconArrowBottomLeft;

// src/icons/line/IconArrowBottomRec.tsx
import { jsx as _jsx47 } from "react/jsx-runtime";
var SvgIconArrowBottomRec = (props) => /* @__PURE__ */ _jsx47("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx47("path", {
    d: "m16 13-4 4m0 0-4-4m4 4V7m-2 15h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowBottomRec_default = SvgIconArrowBottomRec;

// src/icons/line/IconArrowBottomRightCircle.tsx
import { jsx as _jsx48 } from "react/jsx-runtime";
var SvgIconArrowBottomRightCircle = (props) => /* @__PURE__ */ _jsx48("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx48("path", {
    d: "m9 9 6 6m0 0v-4.5m0 4.5h-4.5M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowBottomRightCircle_default = SvgIconArrowBottomRightCircle;

// src/icons/line/IconArrowBottomRightRec.tsx
import { jsx as _jsx49 } from "react/jsx-runtime";
var SvgIconArrowBottomRightRec = (props) => /* @__PURE__ */ _jsx49("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx49("path", {
    d: "m8 8 8 8m0 0v-6m0 6h-6m0 6h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowBottomRightRec_default = SvgIconArrowBottomRightRec;

// src/icons/line/IconArrowBottomRight.tsx
import { jsx as _jsx50 } from "react/jsx-runtime";
var SvgIconArrowBottomRight = (props) => /* @__PURE__ */ _jsx50("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx50("path", {
    d: "m4 4 16 16m0 0V9m0 11H9",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowBottomRight_default = SvgIconArrowBottomRight;

// src/icons/line/IconArrowBottom.tsx
import { jsx as _jsx51 } from "react/jsx-runtime";
var SvgIconArrowBottom = (props) => /* @__PURE__ */ _jsx51("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx51("path", {
    d: "M12 3v18m0 0 7-7m-7 7-7-7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowBottom_default = SvgIconArrowBottom;

// src/icons/line/IconArrowCornerCcwLbRec.tsx
import { jsx as _jsx52 } from "react/jsx-runtime";
var SvgIconArrowCornerCcwLbRec = (props) => /* @__PURE__ */ _jsx52("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx52("path", {
    d: "m9 17-3-3m3 3 3-3m-3 3v-3.5c0-2.33 0-3.495.38-4.413a5 5 0 0 1 2.707-2.706C13.005 6 14.17 6 16.5 6v0h.5m-7 16h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCcwLbRec_default = SvgIconArrowCornerCcwLbRec;

// src/icons/line/IconArrowCornerCcwLb.tsx
import { jsx as _jsx53 } from "react/jsx-runtime";
var SvgIconArrowCornerCcwLb = (props) => /* @__PURE__ */ _jsx53("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx53("path", {
    d: "m8 21-5-5m5 5 5-5m-5 5V9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C11.04 3 12.16 3 14.4 3H21",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCcwLb_default = SvgIconArrowCornerCcwLb;

// src/icons/line/IconArrowCornerCcwLtRec.tsx
import { jsx as _jsx54 } from "react/jsx-runtime";
var SvgIconArrowCornerCcwLtRec = (props) => /* @__PURE__ */ _jsx54("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx54("path", {
    d: "m6 9 3-3M6 9l3 3M6 9h3.5c2.33 0 3.495 0 4.413.38a5 5 0 0 1 2.706 2.707C17 13.005 17 14.17 17 16.5v.5M10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14v-4c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCcwLtRec_default = SvgIconArrowCornerCcwLtRec;

// src/icons/line/IconArrowCornerCcwLt.tsx
import { jsx as _jsx55 } from "react/jsx-runtime";
var SvgIconArrowCornerCcwLt = (props) => /* @__PURE__ */ _jsx55("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx55("path", {
    d: "m3 8 5-5M3 8l5 5M3 8h11.605c2.237 0 3.355 0 4.211.436a4 4 0 0 1 1.748 1.748C21 11.04 21 12.16 21 14.4V21",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCcwLt_default = SvgIconArrowCornerCcwLt;

// src/icons/line/IconArrowCornerCcwRbRec.tsx
import { jsx as _jsx56 } from "react/jsx-runtime";
var SvgIconArrowCornerCcwRbRec = (props) => /* @__PURE__ */ _jsx56("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx56("path", {
    d: "m18 15-3 3m3-3-3-3m3 3h-3.5c-2.33 0-3.495 0-4.413-.38a5 5 0 0 1-2.706-2.707C7 10.995 7 9.83 7 7.5v0V7m3 15h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCcwRbRec_default = SvgIconArrowCornerCcwRbRec;

// src/icons/line/IconArrowCornerCcwRb.tsx
import { jsx as _jsx57 } from "react/jsx-runtime";
var SvgIconArrowCornerCcwRb = (props) => /* @__PURE__ */ _jsx57("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx57("path", {
    d: "m21 16-5 5m5-5-5-5m5 5H9.399c-2.24 0-3.36 0-4.215-.436a4 4 0 0 1-1.748-1.748C3 12.96 3 11.84 3 9.6V3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCcwRb_default = SvgIconArrowCornerCcwRb;

// src/icons/line/IconArrowCornerCcwRtRec.tsx
import { jsx as _jsx58 } from "react/jsx-runtime";
var SvgIconArrowCornerCcwRtRec = (props) => /* @__PURE__ */ _jsx58("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx58("path", {
    d: "m15 6 3 3m-3-3-3 3m3-3v3.5c0 2.33 0 3.495-.38 4.414a5 5 0 0 1-2.706 2.705C10.994 17 9.83 17 7.5 17v0H7m3 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCcwRtRec_default = SvgIconArrowCornerCcwRtRec;

// src/icons/line/IconArrowCornerCcwRt.tsx
import { jsx as _jsx59 } from "react/jsx-runtime";
var SvgIconArrowCornerCcwRt = (props) => /* @__PURE__ */ _jsx59("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx59("path", {
    d: "m16 3 5 5m-5-5-5 5m5-5v11.601c0 2.24 0 3.36-.436 4.215a4 4 0 0 1-1.748 1.748C12.96 21 11.84 21 9.6 21H3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCcwRt_default = SvgIconArrowCornerCcwRt;

// src/icons/line/IconArrowCornerCwLbRec.tsx
import { jsx as _jsx60 } from "react/jsx-runtime";
var SvgIconArrowCornerCwLbRec = (props) => /* @__PURE__ */ _jsx60("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx60("path", {
    d: "m7 15 3 3m-3-3 3-3m-3 3h3.5c2.33 0 3.495 0 4.414-.38a5 5 0 0 0 2.705-2.706C18 10.994 18 9.83 18 7.5v0V7m-8 15h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCwLbRec_default = SvgIconArrowCornerCwLbRec;

// src/icons/line/IconArrowCornerCwLb.tsx
import { jsx as _jsx61 } from "react/jsx-runtime";
var SvgIconArrowCornerCwLb = (props) => /* @__PURE__ */ _jsx61("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx61("path", {
    d: "m3 16 5 5m-5-5 5-5m-5 5h11.6c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C21 12.96 21 11.84 21 9.6V3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCwLb_default = SvgIconArrowCornerCwLb;

// src/icons/line/IconArrowCornerCwLtRec.tsx
import { jsx as _jsx62 } from "react/jsx-runtime";
var SvgIconArrowCornerCwLtRec = (props) => /* @__PURE__ */ _jsx62("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx62("path", {
    d: "M9 6 6 9m3-3 3 3M9 6v3.5c0 2.33 0 3.495.38 4.413a5 5 0 0 0 2.707 2.706C13.005 17 14.17 17 16.5 17v0h.5m-7 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCwLtRec_default = SvgIconArrowCornerCwLtRec;

// src/icons/line/IconArrowCornerCwLt.tsx
import { jsx as _jsx63 } from "react/jsx-runtime";
var SvgIconArrowCornerCwLt = (props) => /* @__PURE__ */ _jsx63("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx63("path", {
    d: "M8 3 3 8m5-5 5 5M8 3v11.6c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C11.04 21 12.16 21 14.4 21H21",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCwLt_default = SvgIconArrowCornerCwLt;

// src/icons/line/IconArrowCornerCwRbRec.tsx
import { jsx as _jsx64 } from "react/jsx-runtime";
var SvgIconArrowCornerCwRbRec = (props) => /* @__PURE__ */ _jsx64("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx64("path", {
    d: "m15 17 3-3m-3 3-3-3m3 3v-3.5c0-2.33 0-3.495-.38-4.413a5 5 0 0 0-2.706-2.706C10.994 6 9.83 6 7.5 6v0H7m3 16h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCwRbRec_default = SvgIconArrowCornerCwRbRec;

// src/icons/line/IconArrowCornerCwRb.tsx
import { jsx as _jsx65 } from "react/jsx-runtime";
var SvgIconArrowCornerCwRb = (props) => /* @__PURE__ */ _jsx65("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx65("path", {
    d: "m16 21 5-5m-5 5-5-5m5 5V9.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C12.96 3 11.84 3 9.6 3H3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCwRb_default = SvgIconArrowCornerCwRb;

// src/icons/line/IconArrowCornerCwRtRec.tsx
import { jsx as _jsx66 } from "react/jsx-runtime";
var SvgIconArrowCornerCwRtRec = (props) => /* @__PURE__ */ _jsx66("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx66("path", {
    d: "m18 9-3-3m3 3-3 3m3-3h-3.5c-2.33 0-3.495 0-4.413.38a5 5 0 0 0-2.706 2.707C7 13.005 7 14.17 7 16.5v.5m3-15h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14v-4c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCwRtRec_default = SvgIconArrowCornerCwRtRec;

// src/icons/line/IconArrowCornerCwRt.tsx
import { jsx as _jsx67 } from "react/jsx-runtime";
var SvgIconArrowCornerCwRt = (props) => /* @__PURE__ */ _jsx67("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx67("path", {
    d: "m21 8-5-5m5 5-5 5m5-5H9.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C3 11.04 3 12.16 3 14.4V21",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowCornerCwRt_default = SvgIconArrowCornerCwRt;

// src/icons/line/IconArrowLeftCircle.tsx
import { jsx as _jsx68 } from "react/jsx-runtime";
var SvgIconArrowLeftCircle = (props) => /* @__PURE__ */ _jsx68("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx68("path", {
    d: "m11 9-3 3m0 0 3 3m-3-3h8m6 0c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowLeftCircle_default = SvgIconArrowLeftCircle;

// src/icons/line/IconArrowLeftCurvedCircle.tsx
import { jsx as _jsx69 } from "react/jsx-runtime";
var SvgIconArrowLeftCurvedCircle = (props) => /* @__PURE__ */ _jsx69("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx69("path", {
    d: "M11 16h2a3 3 0 1 0 0-6H8l2-2m12 4c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowLeftCurvedCircle_default = SvgIconArrowLeftCurvedCircle;

// src/icons/line/IconArrowLeftRec.tsx
import { jsx as _jsx70 } from "react/jsx-runtime";
var SvgIconArrowLeftRec = (props) => /* @__PURE__ */ _jsx70("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx70("path", {
    d: "m11 8-4 4m0 0 4 4m-4-4h10m-7 10h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowLeftRec_default = SvgIconArrowLeftRec;

// src/icons/line/IconArrowLeft.tsx
import { jsx as _jsx71 } from "react/jsx-runtime";
var SvgIconArrowLeft = (props) => /* @__PURE__ */ _jsx71("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx71("path", {
    d: "m10 5-7 7m0 0 7 7m-7-7h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowLeft_default = SvgIconArrowLeft;

// src/icons/line/IconArrowRightCircle.tsx
import { jsx as _jsx72 } from "react/jsx-runtime";
var SvgIconArrowRightCircle = (props) => /* @__PURE__ */ _jsx72("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx72("path", {
    d: "M8 12h8m0 0-3-3m3 3-3 3m9-3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowRightCircle_default = SvgIconArrowRightCircle;

// src/icons/line/IconArrowRightCurvedCircle.tsx
import { jsx as _jsx73 } from "react/jsx-runtime";
var SvgIconArrowRightCurvedCircle = (props) => /* @__PURE__ */ _jsx73("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx73("path", {
    d: "M13 16h-2a3 3 0 1 1 0-6h5l-2-2m8 4c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowRightCurvedCircle_default = SvgIconArrowRightCurvedCircle;

// src/icons/line/IconArrowRightRec.tsx
import { jsx as _jsx74 } from "react/jsx-runtime";
var SvgIconArrowRightRec = (props) => /* @__PURE__ */ _jsx74("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx74("path", {
    d: "m13 8 4 4m0 0-4 4m4-4H7m3 10h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowRightRec_default = SvgIconArrowRightRec;

// src/icons/line/IconArrowRight.tsx
import { jsx as _jsx75 } from "react/jsx-runtime";
var SvgIconArrowRight = (props) => /* @__PURE__ */ _jsx75("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx75("path", {
    d: "M3 12h18m0 0-7-7m7 7-7 7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowRight_default = SvgIconArrowRight;

// src/icons/line/IconArrowSwapRec.tsx
import { jsx as _jsx76 } from "react/jsx-runtime";
var SvgIconArrowSwapRec = (props) => /* @__PURE__ */ _jsx76("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx76("path", {
    d: "M8.5 6 6 8.5m0 0L8.5 11M6 8.5h8m1.5 4.5 2.5 2.5m0 0L15.5 18m2.5-2.5h-8m0 6.5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowSwapRec_default = SvgIconArrowSwapRec;

// src/icons/line/IconArrowSwap.tsx
import { jsx as _jsx77 } from "react/jsx-runtime";
var SvgIconArrowSwap = (props) => /* @__PURE__ */ _jsx77("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx77("path", {
    d: "M6 2 2 6m0 0 4 4M2 6h14m2 8 4 4m0 0-4 4m4-4H8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowSwap_default = SvgIconArrowSwap;

// src/icons/line/IconArrowTopCircle.tsx
import { jsx as _jsx78 } from "react/jsx-runtime";
var SvgIconArrowTopCircle = (props) => /* @__PURE__ */ _jsx78("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx78("path", {
    d: "M12 16V8m0 0-3 3m3-3 3 3m7 1c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowTopCircle_default = SvgIconArrowTopCircle;

// src/icons/line/IconArrowTopLeftCircle.tsx
import { jsx as _jsx79 } from "react/jsx-runtime";
var SvgIconArrowTopLeftCircle = (props) => /* @__PURE__ */ _jsx79("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx79("path", {
    d: "M15 15 9 9m0 0v4.5M9 9h4.5m8.5 3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowTopLeftCircle_default = SvgIconArrowTopLeftCircle;

// src/icons/line/IconArrowTopLeftRec.tsx
import { jsx as _jsx80 } from "react/jsx-runtime";
var SvgIconArrowTopLeftRec = (props) => /* @__PURE__ */ _jsx80("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx80("path", {
    d: "M16 16 8 8m0 0v6m0-6h6m-4 14h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowTopLeftRec_default = SvgIconArrowTopLeftRec;

// src/icons/line/IconArrowTopLeft.tsx
import { jsx as _jsx81 } from "react/jsx-runtime";
var SvgIconArrowTopLeft = (props) => /* @__PURE__ */ _jsx81("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx81("path", {
    d: "M20 20 4 4m0 0v11M4 4h11",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowTopLeft_default = SvgIconArrowTopLeft;

// src/icons/line/IconArrowTopRec.tsx
import { jsx as _jsx82 } from "react/jsx-runtime";
var SvgIconArrowTopRec = (props) => /* @__PURE__ */ _jsx82("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx82("path", {
    d: "m8 11 4-4m0 0 4 4m-4-4v10m-2 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowTopRec_default = SvgIconArrowTopRec;

// src/icons/line/IconArrowTopRightCircle.tsx
import { jsx as _jsx83 } from "react/jsx-runtime";
var SvgIconArrowTopRightCircle = (props) => /* @__PURE__ */ _jsx83("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx83("path", {
    d: "m9 15 6-6m0 0v4.5M15 9h-4.5M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowTopRightCircle_default = SvgIconArrowTopRightCircle;

// src/icons/line/IconArrowTopRightRec.tsx
import { jsx as _jsx84 } from "react/jsx-runtime";
var SvgIconArrowTopRightRec = (props) => /* @__PURE__ */ _jsx84("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx84("path", {
    d: "m8 16 8-8m0 0v6m0-6h-6m0 14h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowTopRightRec_default = SvgIconArrowTopRightRec;

// src/icons/line/IconArrowTopRight.tsx
import { jsx as _jsx85 } from "react/jsx-runtime";
var SvgIconArrowTopRight = (props) => /* @__PURE__ */ _jsx85("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx85("path", {
    d: "M4 20 20 4m0 0v11m0-11H9",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowTopRight_default = SvgIconArrowTopRight;

// src/icons/line/IconArrowTop.tsx
import { jsx as _jsx86 } from "react/jsx-runtime";
var SvgIconArrowTop = (props) => /* @__PURE__ */ _jsx86("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx86("path", {
    d: "M12 21V3m0 0 7 7m-7-7-7 7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowTop_default = SvgIconArrowTop;

// src/icons/line/IconArrowsExpand2Rec.tsx
import { jsx as _jsx87 } from "react/jsx-runtime";
var SvgIconArrowsExpand2Rec = (props) => /* @__PURE__ */ _jsx87("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx87("path", {
    d: "M6 18v-3m0 3h3m-3 0L18 6m0 0h-3m3 0v3M6 6v3m0-3h3M6 6l12 12m0 0h-3m3 0v-3m-8 7h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowsExpand2Rec_default = SvgIconArrowsExpand2Rec;

// src/icons/line/IconArrowsExpand2.tsx
import { jsx as _jsx88 } from "react/jsx-runtime";
var SvgIconArrowsExpand2 = (props) => /* @__PURE__ */ _jsx88("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx88("path", {
    d: "M3 21v-5.625M3 21h5.625M3 21 21 3m0 0h-5.625M21 3v5.625M3 3v5.625M3 3h5.625M3 3l18 18m0 0h-5.625M21 21v-5.625",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowsExpand2_default = SvgIconArrowsExpand2;

// src/icons/line/IconArrowsExpandRec.tsx
import { jsx as _jsx89 } from "react/jsx-runtime";
var SvgIconArrowsExpandRec = (props) => /* @__PURE__ */ _jsx89("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx89("path", {
    d: "M6 6v4m0-4h4M6 6l4 4m8 8v-4m0 4h-4m4 0-4-4m4-8v4m0-4h-4m4 0-4 4m-8 8v-4m0 4h4m-4 0 4-4m0 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowsExpandRec_default = SvgIconArrowsExpandRec;

// src/icons/line/IconArrowsExpand.tsx
import { jsx as _jsx90 } from "react/jsx-runtime";
var SvgIconArrowsExpand = (props) => /* @__PURE__ */ _jsx90("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx90("path", {
    d: "M3 21v-4m0 4h4m-4 0 6-6M21 3h-4m4 0v4m0-4-6 6M3 3v4m0-4h4M3 3l6 6m12 12h-4m4 0v-4m0 4-6-6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowsExpand_default = SvgIconArrowsExpand;

// src/icons/line/IconArrowsMove2Rec.tsx
import { jsx as _jsx91 } from "react/jsx-runtime";
var SvgIconArrowsMove2Rec = (props) => /* @__PURE__ */ _jsx91("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx91("path", {
    d: "m12 19-2-2m2 2 2-2m-2 2V5m0 0-2 2m2-2 2 2m-9 5 2-2m-2 2 2 2m-2-2h14m0 0-2-2m2 2-2 2m-7 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowsMove2Rec_default = SvgIconArrowsMove2Rec;

// src/icons/line/IconArrowsMove2.tsx
import { jsx as _jsx92 } from "react/jsx-runtime";
var SvgIconArrowsMove2 = (props) => /* @__PURE__ */ _jsx92("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx92("path", {
    d: "m2 12 3-3m-3 3 3 3m-3-3h10m0 0h10m-10 0v10m0-10V2m10 10-3-3m3 3-3 3M12 2 9 5m3-3 3 3m-3 17-3-3m3 3 3-3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowsMove2_default = SvgIconArrowsMove2;

// src/icons/line/IconArrowsMoveRec.tsx
import { jsx as _jsx93 } from "react/jsx-runtime";
var SvgIconArrowsMoveRec = (props) => /* @__PURE__ */ _jsx93("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx93("path", {
    d: "M15 12h4m0 0-2-2m2 2-2 2m-8-2H5m0 0 2-2m-2 2 2 2m5-5V5m0 0-2 2m2-2 2 2m-2 8v4m0 0-2-2m2 2 2-2m-4 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowsMoveRec_default = SvgIconArrowsMoveRec;

// src/icons/line/IconArrowsMove.tsx
import { jsx as _jsx94 } from "react/jsx-runtime";
var SvgIconArrowsMove = (props) => /* @__PURE__ */ _jsx94("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx94("path", {
    d: "M16 12h6m0 0-3-3m3 3-3 3M8 12H2m0 0 3-3m-3 3 3 3m7-7V2m0 0L9 5m3-3 3 3m-3 11v6m0 0-3-3m3 3 3-3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconArrowsMove_default = SvgIconArrowsMove;

// src/icons/line/IconAtRec.tsx
import { jsx as _jsx95 } from "react/jsx-runtime";
var SvgIconAtRec = (props) => /* @__PURE__ */ _jsx95("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx95("path", {
    d: "M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm0 0v1.167c0 .633.3 1.533 1.2 1.533 1.2 0 1.8-1.38 1.8-2.7a6 6 0 1 0-3.333 5.376M11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAtRec_default = SvgIconAtRec;

// src/icons/line/IconAt.tsx
import { jsx as _jsx96 } from "react/jsx-runtime";
var SvgIconAt = (props) => /* @__PURE__ */ _jsx96("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx96("path", {
    d: "M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0Zm0 0V8m0 4v1.945c0 1.055.5 2.555 2 2.555 2 0 3-2.299 3-4.5 0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10a9.958 9.958 0 0 0 4.444-1.04",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAt_default = SvgIconAt;

// src/icons/line/IconAtom.tsx
import { jsx as _jsx97 } from "react/jsx-runtime";
var SvgIconAtom = (props) => /* @__PURE__ */ _jsx97("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx97("path", {
    d: "M12 12h.01m4.233-4.243c4.686 4.686 6.585 10.385 4.242 12.728s-8.041.444-12.728-4.243C3.071 11.556 1.172 5.858 3.515 3.514c2.343-2.343 8.041-.443 12.728 4.243Zm0 8.485c-4.687 4.687-10.385 6.586-12.728 4.243S3.07 12.443 7.757 7.757c4.687-4.686 10.385-6.586 12.728-4.242 2.343 2.343.444 8.041-4.242 12.727ZM12.5 12a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAtom_default = SvgIconAtom;

// src/icons/line/IconAward.tsx
import { jsx as _jsx98 } from "react/jsx-runtime";
var SvgIconAward = (props) => /* @__PURE__ */ _jsx98("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx98("path", {
    d: "M7 16.5 6 22c1.982-.33 3.984-.78 6-.78 2.015 0 4.018.45 6 .78l-1-5.5M12 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconAward_default = SvgIconAward;

// src/icons/line/IconBabyCarriage.tsx
import { jsx as _jsx99 } from "react/jsx-runtime";
var SvgIconBabyCarriage = (props) => /* @__PURE__ */ _jsx99("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx99("path", {
    d: "M1 1h.5v0a2.883 2.883 0 0 1 2.854 2.475l.861 6.03c.278 1.948.418 2.923.888 3.655a4 4 0 0 0 1.704 1.478C8.598 15 9.583 15 11.55 15h4.87c1.046 0 1.569 0 2.016-.104a4 4 0 0 0 2.823-2.448c.166-.428.24-.946.387-1.98v0c.085-.595.128-.892.148-1.188a8 8 0 0 0-.226-2.503c-.072-.287-.167-.572-.357-1.142l-.144-.432c-.368-1.104-.552-1.656-.883-2.071a3 3 0 0 0-1.317-.95C18.369 2 17.788 2 16.624 2H15.46c-1.245 0-1.867 0-2.396.208a3 3 0 0 0-1.203.868c-.365.436-.562 1.026-.955 2.206L10 8M5.5 8h16M11 20a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm10 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBabyCarriage_default = SvgIconBabyCarriage;

// src/icons/line/IconBarchart.tsx
import { jsx as _jsx100 } from "react/jsx-runtime";
var SvgIconBarchart = (props) => /* @__PURE__ */ _jsx100("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx100("path", {
    d: "M7 8v8m5-5v5m5-7v7m-5.4 6h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBarchart_default = SvgIconBarchart;

// src/icons/line/IconBattery1.tsx
import { jsx as _jsx101 } from "react/jsx-runtime";
var SvgIconBattery1 = (props) => /* @__PURE__ */ _jsx101("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx101("path", {
    d: "M7 10v4m13-5 .643.257a2.954 2.954 0 0 1 0 5.486L20 15M8.4 19h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C20 15.96 20 14.84 20 12.6v-1.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C16.96 5 15.84 5 13.6 5H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 8.04 2 9.16 2 11.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 19 6.16 19 8.4 19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBattery1_default = SvgIconBattery1;

// src/icons/line/IconBattery2.tsx
import { jsx as _jsx102 } from "react/jsx-runtime";
var SvgIconBattery2 = (props) => /* @__PURE__ */ _jsx102("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx102("path", {
    d: "M7 10v4m4-4v4m9-5 .643.257a2.954 2.954 0 0 1 0 5.486L20 15M8.4 19h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C20 15.96 20 14.84 20 12.6v-1.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C16.96 5 15.84 5 13.6 5H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 8.04 2 9.16 2 11.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 19 6.16 19 8.4 19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBattery2_default = SvgIconBattery2;

// src/icons/line/IconBattery3.tsx
import { jsx as _jsx103 } from "react/jsx-runtime";
var SvgIconBattery3 = (props) => /* @__PURE__ */ _jsx103("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx103("path", {
    d: "M7 10v4m4-4v4m4-4v4m5-5 .643.257a2.954 2.954 0 0 1 0 5.486L20 15M8.4 19h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C20 15.96 20 14.84 20 12.6v-1.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C16.96 5 15.84 5 13.6 5H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 8.04 2 9.16 2 11.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 19 6.16 19 8.4 19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBattery3_default = SvgIconBattery3;

// src/icons/line/IconBatteryCharging.tsx
import { jsx as _jsx104 } from "react/jsx-runtime";
var SvgIconBatteryCharging = (props) => /* @__PURE__ */ _jsx104("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx104("path", {
    d: "M7 5h-.5v0c-.464 0-.697 0-.892.02a4 4 0 0 0-3.589 3.588C2 8.803 2 9.036 2 9.5v5c0 .464 0 .697.02.892a4 4 0 0 0 3.588 3.589c.195.019.428.019.892.019v0H7m8-14h.5v0c.464 0 .697 0 .892.02a4 4 0 0 1 3.589 3.588c.019.195.019.428.019.892V15m0 0v0a4 4 0 0 1-4 4h-1m5-4 .643-.257a2.954 2.954 0 0 0 0-5.486L20 9m-9-3-2 6h4l-2 6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBatteryCharging_default = SvgIconBatteryCharging;

// src/icons/line/IconBatterySlash.tsx
import { jsx as _jsx105 } from "react/jsx-runtime";
var SvgIconBatterySlash = (props) => /* @__PURE__ */ _jsx105("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx105("path", {
    d: "M10 5h6.029A4 4 0 0 1 20 9.037v5.27c0 .332.335.559.643.436v0a2.954 2.954 0 0 0 0-5.486L20 9M2 2l3 3m16 16-2-2m0 0H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 15.96 2 14.84 2 12.6V8a3 3 0 0 1 3-3v0m14 14L5 5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBatterySlash_default = SvgIconBatterySlash;

// src/icons/line/IconBattery.tsx
import { jsx as _jsx106 } from "react/jsx-runtime";
var SvgIconBattery = (props) => /* @__PURE__ */ _jsx106("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx106("path", {
    d: "m20 9 .643.257a2.954 2.954 0 0 1 0 5.486L20 15M8.4 19h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C20 15.96 20 14.84 20 12.6v-1.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C16.96 5 15.84 5 13.6 5H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 8.04 2 9.16 2 11.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 19 6.16 19 8.4 19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBattery_default = SvgIconBattery;

// src/icons/line/IconBed2.tsx
import { jsx as _jsx107 } from "react/jsx-runtime";
var SvgIconBed2 = (props) => /* @__PURE__ */ _jsx107("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx107("path", {
    d: "M12 8h2.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C18 9.52 18 10.08 18 11.2v.8m-6-4v4m0-4H9.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C6 9.52 6 10.08 6 11.2v.8m-4 0h20m-11.022 5h2.044c.718 0 1.077 0 1.394.11a2 2 0 0 1 .747.461c.24.235.4.556.721 1.198l.262.523c.291.583.437.874.648 1.094a2 2 0 0 0 .848.524c.29.09.616.09 1.268.09v0c1.017 0 1.525 0 1.92-.18a2 2 0 0 0 .99-.99c.18-.395.18-.903.18-1.92V9.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 3 17.84 3 15.6 3H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 6.04 2 7.16 2 9.4v8.51c0 1.017 0 1.525.18 1.92a2 2 0 0 0 .99.99c.395.18.904.18 1.92.18v0c.652 0 .978 0 1.268-.09.323-.1.614-.28.848-.524.21-.22.357-.511.648-1.094l.262-.523c.32-.642.481-.963.72-1.198a2 2 0 0 1 .748-.462C9.9 17 10.26 17 10.978 17Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBed2_default = SvgIconBed2;

// src/icons/line/IconBed3.tsx
import { jsx as _jsx108 } from "react/jsx-runtime";
var SvgIconBed3 = (props) => /* @__PURE__ */ _jsx108("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx108("path", {
    d: "M20 12V9.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C16.96 3 15.84 3 13.6 3h-3.2c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C4 6.04 4 7.16 4 9.4V12m-2 3v6m20-6v6m-6-9v-.8c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C14.48 8 13.92 8 12.8 8h-1.6c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C8 9.52 8 10.08 8 11.2v.8m14 7v-3.8c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C20.48 12 19.92 12 18.8 12H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 13.52 2 14.08 2 15.2V19h20Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBed3_default = SvgIconBed3;

// src/icons/line/IconBehance.tsx
import { jsx as _jsx109 } from "react/jsx-runtime";
var SvgIconBehance = (props) => /* @__PURE__ */ _jsx109("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx109("path", {
    d: "M13.5 13c0 2 1 3 2.5 3s2.25-1 2.25-1m-4.75-2a2.5 2.5 0 0 1 5 0h-5Zm1-5h3M6 11.5h2.25a1.75 1.75 0 1 0 0-3.5H6v3.5Zm0 0h2.75a2.25 2.25 0 0 1 0 4.5H6v-4.5ZM2 14v-4c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBehance_default = SvgIconBehance;

// src/icons/line/IconBellCheck.tsx
import { jsx as _jsx110 } from "react/jsx-runtime";
var SvgIconBellCheck = (props) => /* @__PURE__ */ _jsx110("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx110("path", {
    d: "M8 19.546C8.659 21.027 10.378 22 12 22c1.622 0 3.341-.973 4-2.454M9.5 12.5 11 14l3.5-3.5m3.5.69V8a6 6 0 0 0-6-6v0a6 6 0 0 0-6 6v3.19c0 .834-.354 1.629-.974 2.186l-1.107.997c-1.323 1.19-.901 3.36.771 3.969l.211.077a20.773 20.773 0 0 0 14.198 0l.21-.077c1.673-.608 2.095-2.778.772-3.969l-1.107-.997A2.941 2.941 0 0 1 18 11.19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBellCheck_default = SvgIconBellCheck;

// src/icons/line/IconBellMinus.tsx
import { jsx as _jsx111 } from "react/jsx-runtime";
var SvgIconBellMinus = (props) => /* @__PURE__ */ _jsx111("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx111("path", {
    d: "M8 19.546C8.659 21.027 10.378 22 12 22c1.622 0 3.341-.973 4-2.454M9.5 11h5m3.5.19V8a6 6 0 0 0-6-6v0a6 6 0 0 0-6 6v3.19c0 .834-.354 1.629-.974 2.186l-1.107.997c-1.323 1.19-.901 3.36.771 3.969l.211.077a20.773 20.773 0 0 0 14.198 0l.21-.077c1.673-.608 2.095-2.778.772-3.969l-1.107-.997A2.941 2.941 0 0 1 18 11.19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBellMinus_default = SvgIconBellMinus;

// src/icons/line/IconBellPlus.tsx
import { jsx as _jsx112 } from "react/jsx-runtime";
var SvgIconBellPlus = (props) => /* @__PURE__ */ _jsx112("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx112("path", {
    d: "M8 19.546C8.659 21.027 10.378 22 12 22c1.622 0 3.341-.973 4-2.454M12 8.5V11m0 0v2.5m0-2.5H9.5m2.5 0h2.5m3.5.19V8a6 6 0 0 0-6-6v0a6 6 0 0 0-6 6v3.19c0 .834-.354 1.629-.974 2.186l-1.107.997c-1.323 1.19-.901 3.36.771 3.969l.211.077a20.773 20.773 0 0 0 14.198 0l.21-.077c1.673-.608 2.095-2.778.772-3.969l-1.107-.997A2.941 2.941 0 0 1 18 11.19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBellPlus_default = SvgIconBellPlus;

// src/icons/line/IconBellSlash.tsx
import { jsx as _jsx113 } from "react/jsx-runtime";
var SvgIconBellSlash = (props) => /* @__PURE__ */ _jsx113("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx113("path", {
    d: "M8 19.546C8.659 21.027 10.378 22 12 22c1.622 0 3.341-.973 4-2.454m4-5.046-1.026-1.124A2.94 2.94 0 0 1 18 11.19V8a6 6 0 0 0-9.318-5M3 2l3.53 3.53M22 21l-2.667-2.667M6.53 5.53C6.19 6.283 6 7.12 6 8v3.19c0 .834-.354 1.629-.974 2.186l-1.107.997c-1.323 1.19-.901 3.36.771 3.969l.211.077a20.773 20.773 0 0 0 14.198 0l.234-.086M6.53 5.53l12.803 12.803",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBellSlash_default = SvgIconBellSlash;

// src/icons/line/IconBell.tsx
import { jsx as _jsx114 } from "react/jsx-runtime";
var SvgIconBell = (props) => /* @__PURE__ */ _jsx114("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx114("path", {
    d: "M8 19.546C8.659 21.027 10.378 22 12 22c1.622 0 3.341-.973 4-2.454m2-8.356V8a6 6 0 0 0-6-6v0a6 6 0 0 0-6 6v3.19c0 .834-.354 1.629-.974 2.186l-1.107.997c-1.323 1.19-.901 3.36.771 3.969l.211.077a20.773 20.773 0 0 0 14.198 0l.21-.077c1.673-.608 2.095-2.778.772-3.969l-1.107-.997A2.941 2.941 0 0 1 18 11.19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBell_default = SvgIconBell;

// src/icons/line/IconBluetoothRec.tsx
import { jsx as _jsx115 } from "react/jsx-runtime";
var SvgIconBluetoothRec = (props) => /* @__PURE__ */ _jsx115("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx115("path", {
    d: "M10.846 12V7.75c0-.903 0-1.355.19-1.617a1 1 0 0 1 .701-.408c.322-.035.715.188 1.5.635l.095.054c1.201.683 1.802 1.025 2.003 1.472a1.5 1.5 0 0 1 0 1.228c-.201.447-.802.789-2.003 1.472L10.846 12Zm0 0v4.25c0 .903 0 1.355.19 1.617a1 1 0 0 0 .701.408c.322.035.715-.188 1.5-.635l.095-.054c1.201-.683 1.802-1.025 2.003-1.472a1.5 1.5 0 0 0 0-1.228c-.201-.447-.802-.789-2.003-1.472L10.846 12Zm0 0L7 10.09M10.846 12 7 14.546M11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBluetoothRec_default = SvgIconBluetoothRec;

// src/icons/line/IconBluetooth.tsx
import { jsx as _jsx116 } from "react/jsx-runtime";
var SvgIconBluetooth = (props) => /* @__PURE__ */ _jsx116("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx116("path", {
    d: "M10 12V4.042c0-.986 0-1.479.205-1.748a1 1 0 0 1 .745-.392c.338-.017.744.263 1.556.821l2.617 1.8c.966.663 1.449.995 1.618 1.41a1.5 1.5 0 0 1 0 1.134c-.17.415-.652.747-1.618 1.41L10 12Zm0 0v7.958c0 .986 0 1.479.205 1.748a1 1 0 0 0 .745.392c.338.017.744-.263 1.556-.821l2.617-1.8c.966-.663 1.449-.995 1.618-1.41a1.5 1.5 0 0 0 0-1.134c-.17-.415-.652-.747-1.618-1.41L10 12Zm0 0L5 9m5 3-5 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBluetooth_default = SvgIconBluetooth;

// src/icons/line/IconBookLibrary.tsx
import { jsx as _jsx117 } from "react/jsx-runtime";
var SvgIconBookLibrary = (props) => /* @__PURE__ */ _jsx117("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx117("path", {
    d: "M5 6v0a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3v0M19 6v0a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3v0M9 7h6m-6 4h6m-6 4h2m.4 7h1.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C19 18.96 19 17.84 19 15.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C15.96 2 14.84 2 12.6 2h-1.2c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C5 5.04 5 6.16 5 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C8.04 22 9.16 22 11.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBookLibrary_default = SvgIconBookLibrary;

// src/icons/line/IconBookOpen.tsx
import { jsx as _jsx118 } from "react/jsx-runtime";
var SvgIconBookOpen = (props) => /* @__PURE__ */ _jsx118("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx118("path", {
    d: "M12 18c2.427-1.56 4.265-1.984 6.8-2.1 1.17-.053 1.756-.08 2.164-.304.37-.204.644-.49.83-.869.206-.418.206-.968.206-2.07V2c-4.444 0-6.667 0-10 2.143M12 18c-2.427-1.56-4.265-1.984-6.8-2.1-1.17-.053-1.756-.08-2.164-.304a1.944 1.944 0 0 1-.83-.869C2 14.31 2 13.76 2 12.657V2c4.444 0 6.667 0 10 2.143M12 18V4.143M21 19.5c-4 0-6 .5-9 2.5-3-2-5-2.5-9-2.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBookOpen_default = SvgIconBookOpen;

// src/icons/line/IconBookPlus.tsx
import { jsx as _jsx119 } from "react/jsx-runtime";
var SvgIconBookPlus = (props) => /* @__PURE__ */ _jsx119("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx119("path", {
    d: "M18 10.5v5.1c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C14.96 22 13.84 22 11.6 22h-1.2c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C4 18.96 4 17.84 4 15.6v-5.2c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C7.04 4 8.16 4 10.4 4h1.1M18 1v3m0 0v3m0-3h-3m3 0h3M8 8h6m-6 4h4m-4 4h2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBookPlus_default = SvgIconBookPlus;

// src/icons/line/IconBookQueue.tsx
import { jsx as _jsx120 } from "react/jsx-runtime";
var SvgIconBookQueue = (props) => /* @__PURE__ */ _jsx120("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx120("path", {
    d: "M6 7h6m-6 4h6m-6 4h2m11-9v12m3-10v8M8.4 22h1.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C16 18.96 16 17.84 16 15.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C12.96 2 11.84 2 9.6 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBookQueue_default = SvgIconBookQueue;

// src/icons/line/IconBook.tsx
import { jsx as _jsx121 } from "react/jsx-runtime";
var SvgIconBook = (props) => /* @__PURE__ */ _jsx121("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx121("path", {
    d: "M4 19.5V8.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C7.04 2 8.16 2 10.4 2h3.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C20 5.04 20 6.16 20 8.4v9.1M9 7h6m5 10H6.5A2.5 2.5 0 0 0 4 19.5v0A2.5 2.5 0 0 0 6.5 22H15c.93 0 1.394 0 1.78-.077a4 4 0 0 0 3.143-3.143C20 18.394 20 17.93 20 17v0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBook_default = SvgIconBook;

// src/icons/line/IconBookmarkCheck.tsx
import { jsx as _jsx122 } from "react/jsx-runtime";
var SvgIconBookmarkCheck = (props) => /* @__PURE__ */ _jsx122("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx122("path", {
    d: "m9 10.5 2 2L15.5 8M20 22V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C16.2 2 14.8 2 12 2v0c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 4.545 4.73C4 5.8 4 7.2 4 10v12l2.276-1.138c2.099-1.05 3.148-1.574 4.249-1.78a7.997 7.997 0 0 1 2.95 0c1.101.206 2.15.73 4.25 1.78L20 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBookmarkCheck_default = SvgIconBookmarkCheck;

// src/icons/line/IconBookmarkMinus.tsx
import { jsx as _jsx123 } from "react/jsx-runtime";
var SvgIconBookmarkMinus = (props) => /* @__PURE__ */ _jsx123("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx123("path", {
    d: "M9 11h6m5 11V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C16.2 2 14.8 2 12 2v0c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 4.545 4.73C4 5.8 4 7.2 4 10v12l2.276-1.138c2.099-1.05 3.148-1.574 4.249-1.78a7.997 7.997 0 0 1 2.95 0c1.101.206 2.15.73 4.25 1.78L20 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBookmarkMinus_default = SvgIconBookmarkMinus;

// src/icons/line/IconBookmarkPlus.tsx
import { jsx as _jsx124 } from "react/jsx-runtime";
var SvgIconBookmarkPlus = (props) => /* @__PURE__ */ _jsx124("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx124("path", {
    d: "M9 11h3m0 0h3m-3 0V8m0 3v3m8 8V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C16.2 2 14.8 2 12 2v0c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 4.545 4.73C4 5.8 4 7.2 4 10v12l2.276-1.138c2.099-1.05 3.148-1.574 4.249-1.78a7.997 7.997 0 0 1 2.95 0c1.101.206 2.15.73 4.25 1.78L20 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBookmarkPlus_default = SvgIconBookmarkPlus;

// src/icons/line/IconBookmarkSlash.tsx
import { jsx as _jsx125 } from "react/jsx-runtime";
var SvgIconBookmarkSlash = (props) => /* @__PURE__ */ _jsx125("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx125("path", {
    d: "M6.73 2.545C7.8 2 9.2 2 12 2c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C20 5.8 20 7.2 20 10v4M2 4l2.198 1.758M22 20l-2-1.6m0 0V22l-.845-.422c-3.51-1.756-5.267-2.634-7.155-2.634-1.889 0-3.644.878-7.155 2.634L4 22V10c0-1.997 0-3.281.198-4.242M20 18.4 4.198 5.758",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBookmarkSlash_default = SvgIconBookmarkSlash;

// src/icons/line/IconBookmark.tsx
import { jsx as _jsx126 } from "react/jsx-runtime";
var SvgIconBookmark = (props) => /* @__PURE__ */ _jsx126("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx126("path", {
    d: "M20 22V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C16.2 2 14.8 2 12 2c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 4.545 4.73C4 5.8 4 7.2 4 10v12l2.276-1.138h0c2.099-1.05 3.148-1.574 4.249-1.78a7.997 7.997 0 0 1 2.95 0c1.101.206 2.15.73 4.25 1.78h0L20 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBookmark_default = SvgIconBookmark;

// src/icons/line/IconBookmarks.tsx
import { jsx as _jsx127 } from "react/jsx-runtime";
var SvgIconBookmarks = (props) => /* @__PURE__ */ _jsx127("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx127("path", {
    d: "M21 19V8c0-.929 0-1.393-.051-1.783a6 6 0 0 0-5.166-5.166C15.393 1 14.93 1 14 1h-4a3 3 0 0 0-3 3v0m11 18V10.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C14.96 4 13.84 4 11.6 4h-1.2c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C4 7.04 4 8.16 4 10.4V22l2.237-1.278c1.736-.992 2.604-1.488 3.526-1.683a5.999 5.999 0 0 1 2.474 0c.922.195 1.79.69 3.526 1.683L18 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBookmarks_default = SvgIconBookmarks;

// src/icons/line/IconBorderBottom.tsx
import { jsx as _jsx128 } from "react/jsx-runtime";
var SvgIconBorderBottom = (props) => /* @__PURE__ */ _jsx128("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx128("path", {
    d: "M2 21h20M3.5 11.5a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m19 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1M8 11.5a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0H7m10 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m-3.5 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1M3.5 7a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m19 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m-8 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m-8 9a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m19 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m-8 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m-8-13a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m19 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1M8 3a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0H7m10 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m-3.5 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBorderBottom_default = SvgIconBorderBottom;

// src/icons/line/IconBorderClear.tsx
import { jsx as _jsx129 } from "react/jsx-runtime";
var SvgIconBorderClear = (props) => /* @__PURE__ */ _jsx129("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx129("path", {
    d: "M11.5 20.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-19a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 12.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-10a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 3.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m-4.5 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-19a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m9 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-19a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m-13 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-19a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1M3 16a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1M3 7a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 3.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m17.5 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-19a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 12.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-10a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 3.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBorderClear_default = SvgIconBorderClear;

// src/icons/line/IconBorderCommon.tsx
import { jsx as _jsx130 } from "react/jsx-runtime";
var SvgIconBorderCommon = (props) => /* @__PURE__ */ _jsx130("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx130("path", {
    d: "M12 22h3.6c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6V12M12 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V12m10 10V2M2 12V8.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C5.04 2 6.16 2 8.4 2H12M2 12h20m0 0V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 2 17.84 2 15.6 2H12",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBorderCommon_default = SvgIconBorderCommon;

// src/icons/line/IconBorderHorizontal.tsx
import { jsx as _jsx131 } from "react/jsx-runtime";
var SvgIconBorderHorizontal = (props) => /* @__PURE__ */ _jsx131("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx131("path", {
    d: "M2 12h20M3.5 3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M8 3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m10 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5.5 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-17 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 9a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m19-4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M8 21a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m10 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M12.5 3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 18a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1-13.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 9a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBorderHorizontal_default = SvgIconBorderHorizontal;

// src/icons/line/IconBorderInner.tsx
import { jsx as _jsx132 } from "react/jsx-runtime";
var SvgIconBorderInner = (props) => /* @__PURE__ */ _jsx132("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx132("path", {
    d: "M12 2v20M2 12h20M3.5 3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M8 3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m10 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5.5 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-17 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 9a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m19-4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M8 21a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m10 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBorderInner_default = SvgIconBorderInner;

// src/icons/line/IconBorderLeft.tsx
import { jsx as _jsx133 } from "react/jsx-runtime";
var SvgIconBorderLeft = (props) => /* @__PURE__ */ _jsx133("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx133("path", {
    d: "M3 22.25v-20m9.5 18.5a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0-19a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0 12.5a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0-10a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0 3.5a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m4.5 8a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0-19a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0 8a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m-9 8a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0-19a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0 8a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m13 8a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0-19a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0 12.5a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0-10a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1m0 3.5a.5.5 0 0 1 0 1m0-1a.5.5 0 0 0 0 1m0-1v1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBorderLeft_default = SvgIconBorderLeft;

// src/icons/line/IconBorderOuter.tsx
import { jsx as _jsx134 } from "react/jsx-runtime";
var SvgIconBorderOuter = (props) => /* @__PURE__ */ _jsx134("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx134("path", {
    d: "M8 12a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0H7m10 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m-3.5 0a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m1-4.5a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1m1 9a.5.5 0 0 0-1 0m1 0a.5.5 0 0 1-1 0m1 0h-1M12 22h3.6c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 2 17.84 2 15.6 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22H12Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBorderOuter_default = SvgIconBorderOuter;

// src/icons/line/IconBorderRight.tsx
import { jsx as _jsx135 } from "react/jsx-runtime";
var SvgIconBorderRight = (props) => /* @__PURE__ */ _jsx135("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx135("path", {
    d: "M21 22.25v-20m-9.5 18.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-19a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 12.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-10a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 3.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m-4.5 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-19a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m9 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-19a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m-13 8a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-19a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 12.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0-10a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1m0 3.5a.5.5 0 0 0 0 1m0-1a.5.5 0 0 1 0 1m0-1v1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBorderRight_default = SvgIconBorderRight;

// src/icons/line/IconBorderTop.tsx
import { jsx as _jsx136 } from "react/jsx-runtime";
var SvgIconBorderTop = (props) => /* @__PURE__ */ _jsx136("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx136("path", {
    d: "M2 3h20M3.5 12.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m19 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M8 12.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m10 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-3.5 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-8 4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m19 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-8 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-8-9a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m19 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-8 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-8 13a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m19 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M8 21a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m10 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-3.5 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBorderTop_default = SvgIconBorderTop;

// src/icons/line/IconBorderVertical.tsx
import { jsx as _jsx137 } from "react/jsx-runtime";
var SvgIconBorderVertical = (props) => /* @__PURE__ */ _jsx137("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx137("path", {
    d: "M12 2v20M3.5 3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M8 3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m10 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5.5 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-17 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 9a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m19-4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M8 21a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m10 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5.5-9a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-17 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M17 12a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-8 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBorderVertical_default = SvgIconBorderVertical;

// src/icons/line/IconBox2.tsx
import { jsx as _jsx138 } from "react/jsx-runtime";
var SvgIconBox2 = (props) => /* @__PURE__ */ _jsx138("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx138("path", {
    d: "m7.5 9.5 9-5.5M12 12.5 21 7m-9 5.5L3 7m9 5.5v10M2 9.718v4.564c0 .991 0 1.487.142 1.932a3 3 0 0 0 .605 1.068c.309.351.734.606 1.583 1.116l5.2 3.12c.898.539 1.347.808 1.827.914a3 3 0 0 0 1.287 0c.479-.106.928-.375 1.826-.914l5.2-3.12c.85-.51 1.274-.765 1.583-1.116a3 3 0 0 0 .605-1.068c.142-.445.142-.94.142-1.932V9.718c0-.991 0-1.487-.142-1.932a3 3 0 0 0-.605-1.068c-.309-.351-.734-.606-1.583-1.116l-5.2-3.12c-.898-.539-1.347-.808-1.827-.914a3 3 0 0 0-1.287 0c-.479.106-.928.375-1.826.914l-5.2 3.12c-.85.51-1.274.765-1.583 1.116a3 3 0 0 0-.605 1.068C2 8.23 2 8.726 2 9.718Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBox2_default = SvgIconBox2;

// src/icons/line/IconBox.tsx
import { jsx as _jsx139 } from "react/jsx-runtime";
var SvgIconBox = (props) => /* @__PURE__ */ _jsx139("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx139("path", {
    d: "M12 12.5 21 7m-9 5.5L3 7m9 5.5v10M2 9.718v4.564c0 .991 0 1.487.142 1.932a3 3 0 0 0 .605 1.068c.309.351.734.606 1.583 1.116l5.2 3.12c.898.539 1.347.808 1.827.914a3 3 0 0 0 1.287 0c.479-.106.928-.375 1.826-.914l5.2-3.12c.85-.51 1.274-.765 1.583-1.116a3 3 0 0 0 .605-1.068c.142-.445.142-.94.142-1.932V9.718c0-.991 0-1.487-.142-1.932a3 3 0 0 0-.605-1.068c-.309-.351-.734-.606-1.583-1.116l-5.2-3.12c-.898-.539-1.347-.808-1.827-.914a3 3 0 0 0-1.287 0c-.479.106-.928.375-1.826.914l-5.2 3.12c-.85.51-1.274.765-1.583 1.116a3 3 0 0 0-.605 1.068C2 8.23 2 8.726 2 9.718Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBox_default = SvgIconBox;

// src/icons/line/IconBracketsRec.tsx
import { jsx as _jsx140 } from "react/jsx-runtime";
var SvgIconBracketsRec = (props) => /* @__PURE__ */ _jsx140("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx140("path", {
    d: "M15 6h.5v0A2.5 2.5 0 0 1 18 8.5v7a2.5 2.5 0 0 1-2.5 2.5v0H15m-6 0h-.5v0A2.5 2.5 0 0 1 6 15.5v-7A2.5 2.5 0 0 1 8.5 6v0H9m3.5 2.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 7a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1-3.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m.1 10h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBracketsRec_default = SvgIconBracketsRec;

// src/icons/line/IconBrackets.tsx
import { jsx as _jsx141 } from "react/jsx-runtime";
var SvgIconBrackets = (props) => /* @__PURE__ */ _jsx141("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx141("path", {
    d: "M9 22H7.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C4 20.48 4 19.92 4 18.8v-1.149c0-.978 0-1.468-.11-1.928a4 4 0 0 0-.48-1.156c-.247-.404-.593-.75-1.285-1.441L1 12l1.125-1.126c.692-.691 1.038-1.037 1.285-1.44a4 4 0 0 0 .48-1.157c.11-.46.11-.95.11-1.928V5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C5.52 2 6.08 2 7.2 2H9m6 0h1.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C20 3.52 20 4.08 20 5.2v1.149c0 .978 0 1.468.11 1.928a4 4 0 0 0 .48 1.156c.247.404.593.75 1.285 1.441L23 12l-1.125 1.126c-.692.691-1.038 1.037-1.285 1.44a4 4 0 0 0-.48 1.157c-.11.46-.11.95-.11 1.928V18.8c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C18.48 22 17.92 22 16.8 22H15M12.5 7a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBrackets_default = SvgIconBrackets;

// src/icons/line/IconBriefcase2.tsx
import { jsx as _jsx142 } from "react/jsx-runtime";
var SvgIconBriefcase2 = (props) => /* @__PURE__ */ _jsx142("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx142("path", {
    d: "M16 6a4 4 0 0 0-8 0m0 4.5v3m8-3v3m5-1.5v3.6c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C17.96 22 16.84 22 14.6 22H9.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C3 18.96 3 17.84 3 15.6V12m2 0h14c.932 0 1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C22 10.398 22 9.932 22 9v0c0-.932 0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C20.398 6 19.932 6 19 6H5c-.932 0-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C2 7.602 2 8.068 2 9v0c0 .932 0 1.398.152 1.765a2 2 0 0 0 1.083 1.083C3.602 12 4.068 12 5 12Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBriefcase2_default = SvgIconBriefcase2;

// src/icons/line/IconBriefcase.tsx
import { jsx as _jsx143 } from "react/jsx-runtime";
var SvgIconBriefcase = (props) => /* @__PURE__ */ _jsx143("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx143("path", {
    d: "M2.5 11s2.33 1.036 5.5 1.616m0 0c1.235.227 2.598.384 4 .384 1.402 0 2.765-.157 4-.384m-8 0V11m0 1.616V14m8-1.384c3.17-.58 5.5-1.616 5.5-1.616M16 12.616V11m0 1.616V14m0-8a4 4 0 0 0-8 0m.4 16h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6v-3.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 6 17.84 6 15.6 6H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 9.04 2 10.16 2 12.4v3.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBriefcase_default = SvgIconBriefcase;

// src/icons/line/IconBroadcastRec.tsx
import { jsx as _jsx144 } from "react/jsx-runtime";
var SvgIconBroadcastRec = (props) => /* @__PURE__ */ _jsx144("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx144("path", {
    d: "M15.5 6c2.074 1.096 3.5 3.37 3.5 6s-1.426 4.904-3.5 6m-7-12C6.426 7.096 5 9.37 5 12s1.426 4.904 3.5 6m6.167-9c.81.684 1.333 1.773 1.333 3s-.524 2.316-1.333 3M9.333 9C8.523 9.684 8 10.773 8 12s.524 2.316 1.333 3m3.167-3a.5.5 0 0 1-1 0m1 0a.5.5 0 1 0-1 0m1 0h-1m.1 10h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBroadcastRec_default = SvgIconBroadcastRec;

// src/icons/line/IconBroadcast.tsx
import { jsx as _jsx145 } from "react/jsx-runtime";
var SvgIconBroadcast = (props) => /* @__PURE__ */ _jsx145("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx145("path", {
    d: "M17 4c2.963 1.46 5 4.495 5 8s-2.037 6.54-5 8M7 4c-2.963 1.46-5 4.495-5 8s2.037 6.54 5 8m9-12c1.215.912 2 2.364 2 4a4.992 4.992 0 0 1-2 4M8 8a4.992 4.992 0 0 0-2 4c0 1.636.785 3.088 2 4m6-4a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBroadcast_default = SvgIconBroadcast;

// src/icons/line/IconBrush.tsx
import { jsx as _jsx146 } from "react/jsx-runtime";
var SvgIconBrush = (props) => /* @__PURE__ */ _jsx146("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx146("path", {
    d: "M9 2v4m4-4v2M4 9s3.5 2 8 2 8-2 8-2m-8 13v0c1.57 0 2.765-1.41 2.507-2.96l-.188-1.124a2.63 2.63 0 0 1 1.419-2.785l2.113-1.057A3.887 3.887 0 0 0 20 10.598v-5.43A3.168 3.168 0 0 0 16.832 2H7.168A3.168 3.168 0 0 0 4 5.168v5.43c0 1.472.832 2.818 2.149 3.476l2.113 1.057a2.63 2.63 0 0 1 1.419 2.785l-.188 1.125A2.541 2.541 0 0 0 12 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBrush_default = SvgIconBrush;

// src/icons/line/IconBug.tsx
import { jsx as _jsx147 } from "react/jsx-runtime";
var SvgIconBug = (props) => /* @__PURE__ */ _jsx147("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx147("path", {
    d: "m19 9 1.594-1.594c.519-.519.778-.778.964-1.081a3 3 0 0 0 .36-.867C22 5.112 22 4.746 22 4.012V3m-3 13 1.594 1.594c.519.519.778.778.964 1.081.164.269.286.561.36.867.082.346.082.712.082 1.446V22M5 9 3.406 7.406c-.519-.519-.778-.778-.964-1.081a3 3 0 0 1-.36-.867C2 5.112 2 4.746 2 4.012V3m3 13-1.594 1.594c-.519.519-.778.778-.964 1.081a3.001 3.001 0 0 0-.36.867C2 19.888 2 20.254 2 20.988V22M15 5.5V5a3 3 0 0 0-3-3v0a3 3 0 0 0-3 3v.5m3 9.5v7m0 0v0a7 7 0 0 0 7-7v-3a7 7 0 0 0-7-7v0a7 7 0 0 0-7 7v3a7 7 0 0 0 7 7Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBug_default = SvgIconBug;

// src/icons/line/IconBulbSlash.tsx
import { jsx as _jsx148 } from "react/jsx-runtime";
var SvgIconBulbSlash = (props) => /* @__PURE__ */ _jsx148("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx148("path", {
    d: "m3 3 2.432 2.432M20 20l-3.563-3.563m0 0a13.3 13.3 0 0 1-.124.105c-.778.643-1.313 1.575-1.313 2.585v0A2.873 2.873 0 0 1 12.127 22h-.24A2.888 2.888 0 0 1 9 19.112v0c0-1.002-.524-1.928-1.292-2.571C6.226 15.3 4 12.948 4 10c0-1.698.53-3.273 1.432-4.568m11.005 11.005L5.432 5.432M7.5 3.385A8 8 0 0 1 20 10c0 1.525-.574 2.878-1.334 4M9 18s1 .5 3 .5 3-.5 3-.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBulbSlash_default = SvgIconBulbSlash;

// src/icons/line/IconBulb.tsx
import { jsx as _jsx149 } from "react/jsx-runtime";
var SvgIconBulb = (props) => /* @__PURE__ */ _jsx149("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx149("path", {
    d: "M9 18s1 .5 3 .5m0 0c2 0 3-.5 3-.5m-3 .5V14m8-4a8 8 0 1 0-16 0c0 2.948 2.226 5.301 3.708 6.54C8.476 17.185 9 18.11 9 19.113v0A2.888 2.888 0 0 0 11.888 22h.239A2.873 2.873 0 0 0 15 19.127v0c0-1.01.534-1.94 1.312-2.584C17.793 15.317 20 12.99 20 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconBulb_default = SvgIconBulb;

// src/icons/line/IconCalendarArrowRight.tsx
import { jsx as _jsx150 } from "react/jsx-runtime";
var SvgIconCalendarArrowRight = (props) => /* @__PURE__ */ _jsx150("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx150("path", {
    d: "M8 1v3m8-3v3m0 8H8m8 0-3-3m3 3-3 3m-1.4 7h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCalendarArrowRight_default = SvgIconCalendarArrowRight;

// src/icons/line/IconCalendarCross.tsx
import { jsx as _jsx151 } from "react/jsx-runtime";
var SvgIconCalendarCross = (props) => /* @__PURE__ */ _jsx151("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx151("path", {
    d: "M8 1v3m8-3v3M9 15l3-3m0 0 3-3m-3 3L9 9m3 3 3 3m-3.4 7h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCalendarCross_default = SvgIconCalendarCross;

// src/icons/line/IconCalendarDates.tsx
import { jsx as _jsx152 } from "react/jsx-runtime";
var SvgIconCalendarDates = (props) => /* @__PURE__ */ _jsx152("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx152("path", {
    d: "M8 1v3m8-3v3m1.5 4h-11M8 12.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m1 4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m5.5-4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5.5-4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCalendarDates_default = SvgIconCalendarDates;

// src/icons/line/IconCalendarEvent.tsx
import { jsx as _jsx153 } from "react/jsx-runtime";
var SvgIconCalendarEvent = (props) => /* @__PURE__ */ _jsx153("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx153("path", {
    d: "M8 1v3m8-3v3m1 4H7m10 9h-2m-3.4 5h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCalendarEvent_default = SvgIconCalendarEvent;

// src/icons/line/IconCalendarMinus.tsx
import { jsx as _jsx154 } from "react/jsx-runtime";
var SvgIconCalendarMinus = (props) => /* @__PURE__ */ _jsx154("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx154("path", {
    d: "M8 1v3m8-3v3m-8 8h8m-4.4 10h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCalendarMinus_default = SvgIconCalendarMinus;

// src/icons/line/IconCalendarNote.tsx
import { jsx as _jsx155 } from "react/jsx-runtime";
var SvgIconCalendarNote = (props) => /* @__PURE__ */ _jsx155("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx155("path", {
    d: "M17 8H7m6 4H7m3 4H7M8 1v3m8-3v3m-4.4 18h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCalendarNote_default = SvgIconCalendarNote;

// src/icons/line/IconCalendarNumber.tsx
import { jsx as _jsx156 } from "react/jsx-runtime";
var SvgIconCalendarNumber = (props) => /* @__PURE__ */ _jsx156("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx156("path", {
    d: "M8 1v3m8-3v3M9 9h6l-4.5 7m1.1 6h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCalendarNumber_default = SvgIconCalendarNumber;

// src/icons/line/IconCalendarPerson.tsx
import { jsx as _jsx157 } from "react/jsx-runtime";
var SvgIconCalendarPerson = (props) => /* @__PURE__ */ _jsx157("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx157("path", {
    d: "M8 1v3m8-3v3m-2 5a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm-5 8h6a1 1 0 0 0 1-1v0a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v0a1 1 0 0 0 1 1Zm2.6 5h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCalendarPerson_default = SvgIconCalendarPerson;

// src/icons/line/IconCalendarPlus.tsx
import { jsx as _jsx158 } from "react/jsx-runtime";
var SvgIconCalendarPlus = (props) => /* @__PURE__ */ _jsx158("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx158("path", {
    d: "M8 1v3m8-3v3m-8 8h4m0 0h4m-4 0V8m0 4v4m-.4 6h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCalendarPlus_default = SvgIconCalendarPlus;

// src/icons/line/IconCalendar.tsx
import { jsx as _jsx159 } from "react/jsx-runtime";
var SvgIconCalendar = (props) => /* @__PURE__ */ _jsx159("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx159("path", {
    d: "M8 1v3m8-3v3m1 4H7m4.6 14h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCalendar_default = SvgIconCalendar;

// src/icons/line/IconCallAdd.tsx
import { jsx as _jsx160 } from "react/jsx-runtime";
var SvgIconCallAdd = (props) => /* @__PURE__ */ _jsx160("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx160("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0a17.45 17.45 0 0 0 5.865 6m0 0c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19ZM17 4v3m0 0v3m0-3h-3m3 0h3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallAdd_default = SvgIconCallAdd;

// src/icons/line/IconCallCalling.tsx
import { jsx as _jsx161 } from "react/jsx-runtime";
var SvgIconCallCalling = (props) => /* @__PURE__ */ _jsx161("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx161("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0a17.45 17.45 0 0 0 5.865 6m0 0c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19ZM14 7c.723 0 1.445.464 2 1.057.597.638 1 1.425 1 1.943m-3-6a6 6 0 0 1 6 6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallCalling_default = SvgIconCallCalling;

// src/icons/line/IconCallDecline.tsx
import { jsx as _jsx162 } from "react/jsx-runtime";
var SvgIconCallDecline = (props) => /* @__PURE__ */ _jsx162("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx162("path", {
    d: "M16 7.629c1.807.565 3.646 1.524 5.402 2.936.383.307.575.461.743.709a2.16 2.16 0 0 1 .302 1.54c-.062.293-.205.55-.49 1.064v0c-.402.723-.603 1.085-.865 1.314a2 2 0 0 1-1.743.45c-.34-.074-.69-.293-1.392-.731l-.162-.102c-.43-.268-.645-.403-.813-.576a2 2 0 0 1-.466-.768c-.076-.23-.096-.482-.135-.988L16 7.63Zm0 0A13.426 13.426 0 0 0 12 7c-1.283 0-2.632.201-4 .629m0 0c-1.807.565-3.646 1.524-5.402 2.936-.383.307-.575.461-.743.709a2.16 2.16 0 0 0-.302 1.54c.062.293.205.55.49 1.064v0c.402.723.603 1.085.865 1.314a2 2 0 0 0 1.743.45c.34-.074.69-.293 1.392-.732l.162-.1c.43-.27.645-.404.813-.577a2 2 0 0 0 .466-.768c.076-.23.096-.482.135-.988L8 7.63Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallDecline_default = SvgIconCallDecline;

// src/icons/line/IconCallEnd.tsx
import { jsx as _jsx163 } from "react/jsx-runtime";
var SvgIconCallEnd = (props) => /* @__PURE__ */ _jsx163("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx163("path", {
    d: "M16 5.629c1.836.574 3.705 1.556 5.488 3.005.156.127.235.191.338.305.514.569.647 1.557.302 2.241-.07.137-.15.251-.313.479v0c-.208.29-.312.436-.42.546a2 2 0 0 1-2.218.44c-.142-.06-.293-.155-.596-.344l-.738-.461c-.493-.308-.74-.462-.926-.668a2 2 0 0 1-.384-.623c-.1-.258-.127-.548-.18-1.126l-.354-3.794Zm0 0A13.426 13.426 0 0 0 12 5c-1.283 0-2.633.201-4 .629m0 0c-1.837.574-3.706 1.556-5.488 3.005-.157.127-.236.19-.339.305-.514.569-.647 1.556-.302 2.241.07.137.15.251.313.479v0c.208.29.312.436.42.546a2 2 0 0 0 2.219.44c.14-.06.292-.155.595-.344l.738-.461c.493-.308.74-.462.926-.668a2 2 0 0 0 .384-.623c.1-.258.126-.548.18-1.126L8 5.629ZM12 12v8m0 0-3-3m3 3 3-3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallEnd_default = SvgIconCallEnd;

// src/icons/line/IconCallIncoming.tsx
import { jsx as _jsx164 } from "react/jsx-runtime";
var SvgIconCallIncoming = (props) => /* @__PURE__ */ _jsx164("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx164("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0a17.45 17.45 0 0 0 5.865 6m0 0c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19ZM21 3l-6 6m0 0V4m0 5h5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallIncoming_default = SvgIconCallIncoming;

// src/icons/line/IconCallMissed.tsx
import { jsx as _jsx165 } from "react/jsx-runtime";
var SvgIconCallMissed = (props) => /* @__PURE__ */ _jsx165("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx165("path", {
    d: "M16 12.629c1.837.574 3.706 1.556 5.488 3.005.157.127.235.191.338.305.514.569.647 1.557.302 2.241-.069.137-.15.251-.313.479v0c-.208.29-.311.436-.419.546a2 2 0 0 1-2.219.44c-.141-.06-.293-.155-.596-.344l-.738-.461c-.492-.308-.739-.462-.925-.668a2 2 0 0 1-.384-.623c-.1-.258-.127-.548-.18-1.126L16 12.629Zm0 0A13.426 13.426 0 0 0 12 12c-1.283 0-2.632.201-4 .629m0 0c-1.837.574-3.706 1.556-5.488 3.005-.157.127-.235.19-.338.305-.514.569-.647 1.556-.302 2.241.069.137.15.251.313.479v0c.208.29.311.436.419.546a2 2 0 0 0 2.219.44c.141-.06.293-.155.596-.344l.738-.461c.492-.308.739-.462.925-.668a2 2 0 0 0 .384-.623c.1-.258.127-.548.18-1.126L8 12.629ZM7 4l2.737 2.737c.792.792 1.188 1.188 1.645 1.337a2 2 0 0 0 1.236 0c.457-.149.853-.545 1.645-1.337L16.5 4.5M7 4v4m0-4h4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallMissed_default = SvgIconCallMissed;

// src/icons/line/IconCallMuted.tsx
import { jsx as _jsx166 } from "react/jsx-runtime";
var SvgIconCallMuted = (props) => /* @__PURE__ */ _jsx166("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx166("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0a17.45 17.45 0 0 0 5.865 6m0 0c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19ZM15 9l2.5-2.5m0 0L20 4m-2.5 2.5L15 4m2.5 2.5L20 9",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallMuted_default = SvgIconCallMuted;

// src/icons/line/IconCallOnHold.tsx
import { jsx as _jsx167 } from "react/jsx-runtime";
var SvgIconCallOnHold = (props) => /* @__PURE__ */ _jsx167("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx167("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0a17.45 17.45 0 0 0 5.865 6m0 0c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19ZM16 4v4m4-4v4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallOnHold_default = SvgIconCallOnHold;

// src/icons/line/IconCallOutgoing.tsx
import { jsx as _jsx168 } from "react/jsx-runtime";
var SvgIconCallOutgoing = (props) => /* @__PURE__ */ _jsx168("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx168("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0a17.45 17.45 0 0 0 5.865 6m0 0c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19ZM15 9l6-6m0 0v5m0-5h-5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallOutgoing_default = SvgIconCallOutgoing;

// src/icons/line/IconCallReceived.tsx
import { jsx as _jsx169 } from "react/jsx-runtime";
var SvgIconCallReceived = (props) => /* @__PURE__ */ _jsx169("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx169("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0a17.45 17.45 0 0 0 5.865 6m0 0c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19ZM14 6l2 2 5-5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallReceived_default = SvgIconCallReceived;

// src/icons/line/IconCallRedirected.tsx
import { jsx as _jsx170 } from "react/jsx-runtime";
var SvgIconCallRedirected = (props) => /* @__PURE__ */ _jsx170("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx170("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0a17.45 17.45 0 0 0 5.865 6m0 0c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19ZM14 6h8m0 0-3 3m3-3-3-3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallRedirected_default = SvgIconCallRedirected;

// src/icons/line/IconCallRemove.tsx
import { jsx as _jsx171 } from "react/jsx-runtime";
var SvgIconCallRemove = (props) => /* @__PURE__ */ _jsx171("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx171("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0a17.45 17.45 0 0 0 5.865 6m0 0c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19ZM14 7h6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallRemove_default = SvgIconCallRemove;

// src/icons/line/IconCallSlash.tsx
import { jsx as _jsx172 } from "react/jsx-runtime";
var SvgIconCallSlash = (props) => /* @__PURE__ */ _jsx172("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx172("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0c.202.342.414.675.635 1m5.23 5c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19Zm0 0A17.518 17.518 0 0 1 7.5 16.5M3 21l4.5-4.5M19 5 7.5 16.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCallSlash_default = SvgIconCallSlash;

// src/icons/line/IconCameraSlash.tsx
import { jsx as _jsx173 } from "react/jsx-runtime";
var SvgIconCameraSlash = (props) => /* @__PURE__ */ _jsx173("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx173("path", {
    d: "M5.333 5.333c.988-.197 1.98-.804 2.54-1.642A3.798 3.798 0 0 1 11.033 2h1.934c1.27 0 2.456.634 3.16 1.69.595.893 1.513 1.438 2.551 1.645A4.132 4.132 0 0 1 22 9.387v3.82c0 3.552 0 5.328-.861 6.598-.178.263-.38.507-.604.73M5.333 5.333 2 2m3.333 3.333 15.202 15.202M22.5 22.5l-1.965-1.965M17 21.925c-.988.075-2.215.075-3.792.075h-2.416c-3.551 0-5.327 0-6.597-.861a5 5 0 0 1-1.334-1.334C2 18.535 2 16.76 2 13.208v-3.82c0-.882.279-1.71.76-2.388m6.956 2.716a4 4 0 0 1 5.568 5.568M8.126 12A4 4 0 0 0 13 16.874",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCameraSlash_default = SvgIconCameraSlash;

// src/icons/line/IconCamera.tsx
import { jsx as _jsx174, jsxs as _jsxs } from "react/jsx-runtime";
var SvgIconCamera = (props) => /* @__PURE__ */ _jsxs("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx174("path", {
    d: "M2 9.387c0-1.97 1.39-3.665 3.322-4.051l.135-.027A3.798 3.798 0 0 0 7.873 3.69v0A3.798 3.798 0 0 1 11.033 2h1.934c1.27 0 2.456.635 3.16 1.691v0a3.798 3.798 0 0 0 2.416 1.618l.135.027A4.132 4.132 0 0 1 22 9.387V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14V9.387Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx174("path", {
    d: "M16 13a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconCamera_default = SvgIconCamera;

// src/icons/line/IconCaptions2.tsx
import { jsx as _jsx175 } from "react/jsx-runtime";
var SvgIconCaptions2 = (props) => /* @__PURE__ */ _jsx175("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx175("path", {
    d: "M10.5 13.5A1.5 1.5 0 0 1 9 15h-.1c-.84 0-1.26 0-1.581-.164a1.5 1.5 0 0 1-.656-.655c-.163-.32-.163-.74-.163-1.581v-1.2c0-.84 0-1.26.163-1.581a1.5 1.5 0 0 1 .656-.656C7.639 9 8.059 9 8.9 9H9a1.5 1.5 0 0 1 1.5 1.5m7 3A1.5 1.5 0 0 1 16 15h-.1c-.84 0-1.26 0-1.581-.164a1.5 1.5 0 0 1-.655-.655c-.164-.32-.164-.74-.164-1.581v-1.2c0-.84 0-1.26.164-1.581a1.5 1.5 0 0 1 .655-.656C14.639 9 15.059 9 15.9 9h.1a1.5 1.5 0 0 1 1.5 1.5M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCaptions2_default = SvgIconCaptions2;

// src/icons/line/IconCaptions.tsx
import { jsx as _jsx176 } from "react/jsx-runtime";
var SvgIconCaptions = (props) => /* @__PURE__ */ _jsx176("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx176("path", {
    d: "M6 17h3m2.5 0h1m2.5 0h3m-9-4h6m-9 0h.5m11 0h.5m-8 9h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCaptions_default = SvgIconCaptions;

// src/icons/line/IconCash.tsx
import { jsx as _jsx177 } from "react/jsx-runtime";
var SvgIconCash = (props) => /* @__PURE__ */ _jsx177("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx177("path", {
    d: "M7 2.5v0c0 1.398 0 2.097-.228 2.648a3 3 0 0 1-1.624 1.624C4.597 7 3.898 7 2.5 7v0M17 2.5v0c0 1.398 0 2.097.228 2.648a3 3 0 0 0 1.624 1.624C19.403 7 20.102 7 21.5 7v0M17 17.5v0c0-1.398 0-2.097.228-2.648a3 3 0 0 1 1.624-1.624C19.403 13 20.102 13 21.5 13v0M7 17.5v0c0-1.398 0-2.097-.228-2.648a3 3 0 0 0-1.624-1.624C4.597 13 3.898 13 2.5 13v0M5 22h14m-5-12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm8 1.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 2 17.84 2 15.6 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v3.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 18 6.16 18 8.4 18h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 14.96 22 13.84 22 11.6Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCash_default = SvgIconCash;

// src/icons/line/IconCast.tsx
import { jsx as _jsx178 } from "react/jsx-runtime";
var SvgIconCast = (props) => /* @__PURE__ */ _jsx178("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx178("path", {
    d: "M2 10.5V10v0c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22v0h-.5M6 22c0-2.154-1.825-4-4-4m8 4c0-4.308-3.65-8-8-8m.5 8a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCast_default = SvgIconCast;

// src/icons/line/IconCd.tsx
import { jsx as _jsx179 } from "react/jsx-runtime";
var SvgIconCd = (props) => /* @__PURE__ */ _jsx179("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx179("path", {
    d: "M12 6a6 6 0 0 1 6 6m4 0c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Zm-8 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCd_default = SvgIconCd;

// src/icons/line/IconChartBar.tsx
import { jsx as _jsx180 } from "react/jsx-runtime";
var SvgIconChartBar = (props) => /* @__PURE__ */ _jsx180("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx180("path", {
    d: "M8 8h6m-6 4h8m-8 4h4m-2 6h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChartBar_default = SvgIconChartBar;

// src/icons/line/IconChartColumn.tsx
import { jsx as _jsx181 } from "react/jsx-runtime";
var SvgIconChartColumn = (props) => /* @__PURE__ */ _jsx181("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx181("path", {
    d: "M8 16v-6m4 6V8m4 8v-4m-6 10h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChartColumn_default = SvgIconChartColumn;

// src/icons/line/IconChartLine.tsx
import { jsx as _jsx182 } from "react/jsx-runtime";
var SvgIconChartLine = (props) => /* @__PURE__ */ _jsx182("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx182("path", {
    d: "m7 15 4-4 2 2 4-4m-7 13h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChartLine_default = SvgIconChartLine;

// src/icons/line/IconChartPie.tsx
import { jsx as _jsx183 } from "react/jsx-runtime";
var SvgIconChartPie = (props) => /* @__PURE__ */ _jsx183("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx183("path", {
    d: "M9 2.458C4.943 3.732 2 7.523 2 12c0 5.523 4.477 10 10 10 4.478 0 8.268-2.943 9.542-7M15.2 12h5.2c.548 0 .822 0 1.052-.132.183-.104.365-.318.44-.516.093-.248.056-.48-.018-.946a10.008 10.008 0 0 0-8.28-8.28c-.465-.074-.698-.111-.946-.018a1.097 1.097 0 0 0-.516.44C12 2.778 12 3.052 12 3.6v5.2c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C13.52 12 14.08 12 15.2 12Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChartPie_default = SvgIconChartPie;

// src/icons/line/IconCheckCircle2.tsx
import { jsx as _jsx184 } from "react/jsx-runtime";
var SvgIconCheckCircle2 = (props) => /* @__PURE__ */ _jsx184("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx184("path", {
    d: "m9 12 2 2 5-5m6 3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCheckCircle2_default = SvgIconCheckCircle2;

// src/icons/line/IconCheckCircle.tsx
import { jsx as _jsx185 } from "react/jsx-runtime";
var SvgIconCheckCircle = (props) => /* @__PURE__ */ _jsx185("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx185("path", {
    d: "M8.5 12.5 11 15l5.5-5.5M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCheckCircle_default = SvgIconCheckCircle;

// src/icons/line/IconCheckRec.tsx
import { jsx as _jsx186 } from "react/jsx-runtime";
var SvgIconCheckRec = (props) => /* @__PURE__ */ _jsx186("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx186("path", {
    d: "M8.5 12.5 11 15l6-6m-5.4 13h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCheckRec_default = SvgIconCheckRec;

// src/icons/line/IconCheck.tsx
import { jsx as _jsx187 } from "react/jsx-runtime";
var SvgIconCheck = (props) => /* @__PURE__ */ _jsx187("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx187("path", {
    d: "m4 13 5 5L20 6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCheck_default = SvgIconCheck;

// src/icons/line/IconCheckboxCircle.tsx
import { jsx as _jsx188 } from "react/jsx-runtime";
var SvgIconCheckboxCircle = (props) => /* @__PURE__ */ _jsx188("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx188("path", {
    d: "M16.444 3.04A9.958 9.958 0 0 0 12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10c0-.376-.02-.746-.061-1.111M9 11l3 3L22 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCheckboxCircle_default = SvgIconCheckboxCircle;

// src/icons/line/IconCheckboxRec.tsx
import { jsx as _jsx189 } from "react/jsx-runtime";
var SvgIconCheckboxRec = (props) => /* @__PURE__ */ _jsx189("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx189("path", {
    d: "M16.444 2H11.6c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-1.511M9 11l3 3L22 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCheckboxRec_default = SvgIconCheckboxRec;

// src/icons/line/IconChevronDownRec.tsx
import { jsx as _jsx190, jsxs as _jsxs2 } from "react/jsx-runtime";
var SvgIconChevronDownRec = (props) => /* @__PURE__ */ _jsxs2("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx190("path", {
    d: "M2 10c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14v-4Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx190("path", {
    d: "M13.806 14.936c-.623.713-.935 1.069-1.305 1.2a1.499 1.499 0 0 1-1.002 0c-.37-.131-.682-.487-1.305-1.2L8.483 12.98c-1.106-1.264-1.66-1.896-1.677-2.432a1.5 1.5 0 0 1 .548-1.208C7.77 9 8.61 9 10.29 9h3.422c1.68 0 2.52 0 2.934.34a1.5 1.5 0 0 1 .549 1.208c-.017.536-.57 1.168-1.677 2.432l-1.71 1.956Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconChevronDownRec_default = SvgIconChevronDownRec;

// src/icons/line/IconChevronDown.tsx
import { jsx as _jsx191 } from "react/jsx-runtime";
var SvgIconChevronDown = (props) => /* @__PURE__ */ _jsx191("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx191("path", {
    d: "m6 9 6 6 6-6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronDown_default = SvgIconChevronDown;

// src/icons/line/IconChevronLeftRec.tsx
import { jsx as _jsx192, jsxs as _jsxs3 } from "react/jsx-runtime";
var SvgIconChevronLeftRec = (props) => /* @__PURE__ */ _jsxs3("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx192("path", {
    d: "M2 10c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14v-4Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx192("path", {
    d: "M9.064 10.194c-.712.623-1.069.935-1.2 1.304a1.5 1.5 0 0 0 0 1.003c.131.37.488.681 1.2 1.305l1.956 1.711c1.264 1.106 1.896 1.66 2.432 1.677a1.5 1.5 0 0 0 1.208-.549c.34-.414.34-1.254.34-2.934v-3.422c0-1.68 0-2.52-.34-2.935a1.5 1.5 0 0 0-1.208-.548c-.536.017-1.168.57-2.432 1.677l-1.956 1.71Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconChevronLeftRec_default = SvgIconChevronLeftRec;

// src/icons/line/IconChevronLeft.tsx
import { jsx as _jsx193 } from "react/jsx-runtime";
var SvgIconChevronLeft = (props) => /* @__PURE__ */ _jsx193("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx193("path", {
    d: "m14 6-6 6 6 6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronLeft_default = SvgIconChevronLeft;

// src/icons/line/IconChevronRightRec.tsx
import { jsx as _jsx194, jsxs as _jsxs4 } from "react/jsx-runtime";
var SvgIconChevronRightRec = (props) => /* @__PURE__ */ _jsxs4("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx194("path", {
    d: "M2 10c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14v-4Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx194("path", {
    d: "M14.936 10.194c.713.623 1.069.935 1.2 1.305.115.324.115.678 0 1.002-.131.37-.487.682-1.2 1.305l-1.956 1.711c-1.264 1.106-1.896 1.66-2.432 1.677a1.5 1.5 0 0 1-1.208-.549C9 16.231 9 15.391 9 13.711v-3.422c0-1.68 0-2.52.34-2.935a1.5 1.5 0 0 1 1.208-.548c.536.018 1.168.57 2.432 1.677l1.956 1.71Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconChevronRightRec_default = SvgIconChevronRightRec;

// src/icons/line/IconChevronRight.tsx
import { jsx as _jsx195 } from "react/jsx-runtime";
var SvgIconChevronRight = (props) => /* @__PURE__ */ _jsx195("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx195("path", {
    d: "m10 6 6 6-6 6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronRight_default = SvgIconChevronRight;

// src/icons/line/IconChevronUpRec.tsx
import { jsx as _jsx196, jsxs as _jsxs5 } from "react/jsx-runtime";
var SvgIconChevronUpRec = (props) => /* @__PURE__ */ _jsxs5("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx196("path", {
    d: "M2 10c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14v-4Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx196("path", {
    d: "M13.806 9.064c-.623-.712-.935-1.069-1.305-1.2a1.5 1.5 0 0 0-1.002 0c-.37.131-.682.488-1.305 1.2L8.483 11.02c-1.106 1.264-1.66 1.896-1.677 2.432a1.5 1.5 0 0 0 .548 1.208C7.77 15 8.61 15 10.29 15h3.422c1.68 0 2.52 0 2.934-.34a1.5 1.5 0 0 0 .549-1.208c-.017-.536-.57-1.168-1.677-2.432l-1.71-1.956Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconChevronUpRec_default = SvgIconChevronUpRec;

// src/icons/line/IconChevronUp.tsx
import { jsx as _jsx197 } from "react/jsx-runtime";
var SvgIconChevronUp = (props) => /* @__PURE__ */ _jsx197("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx197("path", {
    d: "m6 15 6-6 6 6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronUp_default = SvgIconChevronUp;

// src/icons/line/IconChevronsDownRec.tsx
import { jsx as _jsx198 } from "react/jsx-runtime";
var SvgIconChevronsDownRec = (props) => /* @__PURE__ */ _jsx198("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx198("path", {
    d: "m15 8-.375.375L12 11 9.375 8.375 9 8m-2.5 2.5L12 16l5.5-5.5M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronsDownRec_default = SvgIconChevronsDownRec;

// src/icons/line/IconChevronsDown.tsx
import { jsx as _jsx199 } from "react/jsx-runtime";
var SvgIconChevronsDown = (props) => /* @__PURE__ */ _jsx199("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx199("path", {
    d: "m16 7-4 4-4-4m-4 3 8 8 8-8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronsDown_default = SvgIconChevronsDown;

// src/icons/line/IconChevronsLeftRec.tsx
import { jsx as _jsx200 } from "react/jsx-runtime";
var SvgIconChevronsLeftRec = (props) => /* @__PURE__ */ _jsx200("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx200("path", {
    d: "m15 9-3 3 3 3m-2.5 2.5L7 12l5.5-5.5M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronsLeftRec_default = SvgIconChevronsLeftRec;

// src/icons/line/IconChevronsLeft.tsx
import { jsx as _jsx201 } from "react/jsx-runtime";
var SvgIconChevronsLeft = (props) => /* @__PURE__ */ _jsx201("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx201("path", {
    d: "m13 4-8 8 8 8m3-12-4 4 4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronsLeft_default = SvgIconChevronsLeft;

// src/icons/line/IconChevronsRightRec.tsx
import { jsx as _jsx202 } from "react/jsx-runtime";
var SvgIconChevronsRightRec = (props) => /* @__PURE__ */ _jsx202("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx202("path", {
    d: "m9 9 3 3-3 3m2.5 2.5L17 12l-5.5-5.5M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronsRightRec_default = SvgIconChevronsRightRec;

// src/icons/line/IconChevronsRight.tsx
import { jsx as _jsx203 } from "react/jsx-runtime";
var SvgIconChevronsRight = (props) => /* @__PURE__ */ _jsx203("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx203("path", {
    d: "m11 4 8 8-8 8M8 8l4 4-4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronsRight_default = SvgIconChevronsRight;

// src/icons/line/IconChevronsUpRec.tsx
import { jsx as _jsx204 } from "react/jsx-runtime";
var SvgIconChevronsUpRec = (props) => /* @__PURE__ */ _jsx204("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx204("path", {
    d: "m15 16-.375-.375L12 13l-2.625 2.625L9 16m-2.5-2.5L12 8l5.5 5.5M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronsUpRec_default = SvgIconChevronsUpRec;

// src/icons/line/IconChevronsUp.tsx
import { jsx as _jsx205 } from "react/jsx-runtime";
var SvgIconChevronsUp = (props) => /* @__PURE__ */ _jsx205("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx205("path", {
    d: "m4 14 8-8 8 8M8 17l4-4 4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconChevronsUp_default = SvgIconChevronsUp;

// src/icons/line/IconClipboardCheck.tsx
import { jsx as _jsx206 } from "react/jsx-runtime";
var SvgIconClipboardCheck = (props) => /* @__PURE__ */ _jsx206("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx206("path", {
    d: "M16 3v0a4 4 0 0 1 4 4v7c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V7a4 4 0 0 1 4-4v0m8 0v0a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v0m8 0v0a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v0m1.5 10.5L11 15l4-4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClipboardCheck_default = SvgIconClipboardCheck;

// src/icons/line/IconClipboardCross.tsx
import { jsx as _jsx207 } from "react/jsx-runtime";
var SvgIconClipboardCross = (props) => /* @__PURE__ */ _jsx207("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx207("path", {
    d: "M16 3v0a4 4 0 0 1 4 4v7c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V7a4 4 0 0 1 4-4v0m8 0v0a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v0m8 0v0a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v0m2 12 2-2m0 0 2-2m-2 2-2-2m2 2 2 2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClipboardCross_default = SvgIconClipboardCross;

// src/icons/line/IconClipboardMinus.tsx
import { jsx as _jsx208 } from "react/jsx-runtime";
var SvgIconClipboardMinus = (props) => /* @__PURE__ */ _jsx208("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx208("path", {
    d: "M16 3v0a4 4 0 0 1 4 4v7c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V7a4 4 0 0 1 4-4v0m8 0v0a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v0m8 0v0a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v0m1.5 10h5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClipboardMinus_default = SvgIconClipboardMinus;

// src/icons/line/IconClipboardPlus.tsx
import { jsx as _jsx209 } from "react/jsx-runtime";
var SvgIconClipboardPlus = (props) => /* @__PURE__ */ _jsx209("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx209("path", {
    d: "M16 3v0a4 4 0 0 1 4 4v7c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V7a4 4 0 0 1 4-4v0m8 0v0a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v0m8 0v0a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v0m1.5 10H12m0 0h2.5M12 13v-2.5m0 2.5v2.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClipboardPlus_default = SvgIconClipboardPlus;

// src/icons/line/IconClipboardSlash.tsx
import { jsx as _jsx210 } from "react/jsx-runtime";
var SvgIconClipboardSlash = (props) => /* @__PURE__ */ _jsx210("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx210("path", {
    d: "M16 3a2 2 0 0 1-2 2h-4a2 2 0 1 1 0-4h4a2 2 0 0 1 2 2Zm0 0a4 4 0 0 1 4 4v7c0 .552 0 1.049-.004 1.5M2 2l2.716 2.716M22 22l-2.609-2.609M4.716 4.716A3.982 3.982 0 0 0 4 7v7c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C7.8 22 9.2 22 12 22c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.121-2.064M4.716 4.716 19.39 19.39",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClipboardSlash_default = SvgIconClipboardSlash;

// src/icons/line/IconClipboardText.tsx
import { jsx as _jsx211 } from "react/jsx-runtime";
var SvgIconClipboardText = (props) => /* @__PURE__ */ _jsx211("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx211("path", {
    d: "M16 3v0a4 4 0 0 1 4 4v7c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V7a4 4 0 0 1 4-4v0m8 0v0a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v0m8 0v0a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v0m1 8h6m-6 4h4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClipboardText_default = SvgIconClipboardText;

// src/icons/line/IconClipboard.tsx
import { jsx as _jsx212 } from "react/jsx-runtime";
var SvgIconClipboard = (props) => /* @__PURE__ */ _jsx212("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx212("path", {
    d: "M16 3v0a4 4 0 0 1 4 4v7c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V7a4 4 0 0 1 4-4v0m8 0v0a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v0m8 0v0a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v0",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClipboard_default = SvgIconClipboard;

// src/icons/line/IconClockCheck.tsx
import { jsx as _jsx213 } from "react/jsx-runtime";
var SvgIconClockCheck = (props) => /* @__PURE__ */ _jsx213("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx213("path", {
    d: "M4 1 1 4m19-3 3 3M9 12l2 2 5-5m6 3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClockCheck_default = SvgIconClockCheck;

// src/icons/line/IconClockCross.tsx
import { jsx as _jsx214 } from "react/jsx-runtime";
var SvgIconClockCross = (props) => /* @__PURE__ */ _jsx214("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx214("path", {
    d: "M4 1 1 4m19-3 3 3M9 15l3-3m0 0 3-3m-3 3L9 9m3 3 3 3m7-3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClockCross_default = SvgIconClockCross;

// src/icons/line/IconClockMinus.tsx
import { jsx as _jsx215 } from "react/jsx-runtime";
var SvgIconClockMinus = (props) => /* @__PURE__ */ _jsx215("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx215("path", {
    d: "M4 1 1 4m19-3 3 3M8 12h8m6 0c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClockMinus_default = SvgIconClockMinus;

// src/icons/line/IconClockPlus.tsx
import { jsx as _jsx216 } from "react/jsx-runtime";
var SvgIconClockPlus = (props) => /* @__PURE__ */ _jsx216("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx216("path", {
    d: "M4 1 1 4m19-3 3 3M8 12h4m0 0h4m-4 0V8m0 4v4m10-4c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClockPlus_default = SvgIconClockPlus;

// src/icons/line/IconClockSlash.tsx
import { jsx as _jsx217 } from "react/jsx-runtime";
var SvgIconClockSlash = (props) => /* @__PURE__ */ _jsx217("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx217("path", {
    d: "M1 3.667 2.692 2l2.974 2.929m2.667-1.89A10.24 10.24 0 0 1 12.846 2C18.454 2 23 6.477 23 12c0 1.597-.38 3.106-1.055 4.444m-1.919 2.627A10.2 10.2 0 0 1 12.846 22C7.238 22 2.692 17.523 2.692 12a9.894 9.894 0 0 1 2.974-7.071m14.36 14.142L23 22m-2.974-2.929L5.666 4.93M20 1l3 3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClockSlash_default = SvgIconClockSlash;

// src/icons/line/IconClockZzz.tsx
import { jsx as _jsx218 } from "react/jsx-runtime";
var SvgIconClockZzz = (props) => /* @__PURE__ */ _jsx218("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx218("path", {
    d: "M4 1 1 4m19-3 3 3M10 9h4l-4 6h4m8-3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClockZzz_default = SvgIconClockZzz;

// src/icons/line/IconClock.tsx
import { jsx as _jsx219 } from "react/jsx-runtime";
var SvgIconClock = (props) => /* @__PURE__ */ _jsx219("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx219("path", {
    d: "M12 8v4l3 2M4 1 1 4m19-3 3 3m-1 8c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconClock_default = SvgIconClock;

// src/icons/line/IconCloudCheck.tsx
import { jsx as _jsx220 } from "react/jsx-runtime";
var SvgIconCloudCheck = (props) => /* @__PURE__ */ _jsx220("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx220("path", {
    d: "m15 11-4 4-1.5-1.5m-5.2-2.567C4.3 6.552 7.747 3 12 3c3.621 0 6.658 2.575 7.48 6.045 2.06.82 3.52 2.878 3.52 5.288C23 17.463 20.538 20 17.5 20H5.4C2.97 20 1 17.97 1 15.467c0-2.113 1.403-3.888 3.301-4.391a8.34 8.34 0 0 1-.001-.143Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCloudCheck_default = SvgIconCloudCheck;

// src/icons/line/IconCloudCross.tsx
import { jsx as _jsx221 } from "react/jsx-runtime";
var SvgIconCloudCross = (props) => /* @__PURE__ */ _jsx221("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx221("path", {
    d: "m10 15 2-2m0 0 2-2m-2 2-2-2m2 2 2 2m-9.7-4.067C4.3 6.552 7.747 3 12 3c3.621 0 6.658 2.575 7.48 6.045 2.06.82 3.52 2.878 3.52 5.288C23 17.463 20.538 20 17.5 20H5.4C2.97 20 1 17.97 1 15.467c0-2.113 1.403-3.888 3.301-4.391a8.34 8.34 0 0 1-.001-.143Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCloudCross_default = SvgIconCloudCross;

// src/icons/line/IconCloudDownload.tsx
import { jsx as _jsx222 } from "react/jsx-runtime";
var SvgIconCloudDownload = (props) => /* @__PURE__ */ _jsx222("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx222("path", {
    d: "M3.2 18A4.427 4.427 0 0 1 1 14.167a4.422 4.422 0 0 1 3.301-4.285l-.001-.14C4.3 5.468 7.747 2 12 2c3.621 0 6.658 2.513 7.48 5.9A5.532 5.532 0 0 1 23 13.061c0 1.81-.864 3.416-2.2 4.425M12 13v9m0 0 3.5-3.5M12 22l-3.5-3.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCloudDownload_default = SvgIconCloudDownload;

// src/icons/line/IconCloudDrizzle.tsx
import { jsx as _jsx223 } from "react/jsx-runtime";
var SvgIconCloudDrizzle = (props) => /* @__PURE__ */ _jsx223("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx223("path", {
    d: "M3.2 18A4.427 4.427 0 0 1 1 14.167a4.422 4.422 0 0 1 3.301-4.285l-.001-.14C4.3 5.468 7.747 2 12 2c3.621 0 6.658 2.513 7.48 5.9A5.532 5.532 0 0 1 23 13.061c0 1.81-.864 3.416-2.2 4.425M8 14v2m0 3v2m8-7v2m0 3v2m-4-6v2m0 3v2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCloudDrizzle_default = SvgIconCloudDrizzle;

// src/icons/line/IconCloudLightning.tsx
import { jsx as _jsx224 } from "react/jsx-runtime";
var SvgIconCloudLightning = (props) => /* @__PURE__ */ _jsx224("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx224("path", {
    d: "M3.2 18A4.427 4.427 0 0 1 1 14.167a4.422 4.422 0 0 1 3.301-4.285l-.001-.14C4.3 5.468 7.747 2 12 2c3.621 0 6.658 2.513 7.48 5.9A5.532 5.532 0 0 1 23 13.061c0 1.81-.864 3.416-2.2 4.425M13 14l-4 4h6l-4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCloudLightning_default = SvgIconCloudLightning;

// src/icons/line/IconCloudSnow.tsx
import { jsx as _jsx225 } from "react/jsx-runtime";
var SvgIconCloudSnow = (props) => /* @__PURE__ */ _jsx225("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx225("path", {
    d: "M3.2 18A4.427 4.427 0 0 1 1 14.167a4.422 4.422 0 0 1 3.301-4.285l-.001-.14C4.3 5.468 7.747 2 12 2c3.621 0 6.658 2.513 7.48 5.9A5.532 5.532 0 0 1 23 13.061c0 1.81-.864 3.416-2.2 4.425M8.5 15a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m9 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-7 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5-3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCloudSnow_default = SvgIconCloudSnow;

// src/icons/line/IconCloudUpload.tsx
import { jsx as _jsx226 } from "react/jsx-runtime";
var SvgIconCloudUpload = (props) => /* @__PURE__ */ _jsx226("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx226("path", {
    d: "M3.2 18A4.427 4.427 0 0 1 1 14.167a4.422 4.422 0 0 1 3.301-4.285l-.001-.14C4.3 5.468 7.747 2 12 2c3.621 0 6.658 2.513 7.48 5.9A5.532 5.532 0 0 1 23 13.061c0 1.81-.864 3.416-2.2 4.425M12 22v-9m0 0-3.5 3.5M12 13l3.5 3.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCloudUpload_default = SvgIconCloudUpload;

// src/icons/line/IconCloud.tsx
import { jsx as _jsx227 } from "react/jsx-runtime";
var SvgIconCloud = (props) => /* @__PURE__ */ _jsx227("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx227("path", {
    d: "M12 3c-4.253 0-7.7 3.552-7.7 7.933l.001.143C2.403 11.579 1 13.354 1 15.466 1 17.97 2.97 20 5.4 20h12.1c3.038 0 5.5-2.537 5.5-5.667 0-2.41-1.46-4.469-3.52-5.288C18.659 5.575 15.622 3 12 3Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCloud_default = SvgIconCloud;

// src/icons/line/IconCodeRec.tsx
import { jsx as _jsx228 } from "react/jsx-runtime";
var SvgIconCodeRec = (props) => /* @__PURE__ */ _jsx228("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx228("path", {
    d: "m10 8-4 4 4 4m4-8 4 4-4 4m-4 6h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCodeRec_default = SvgIconCodeRec;

// src/icons/line/IconCode.tsx
import { jsx as _jsx229 } from "react/jsx-runtime";
var SvgIconCode = (props) => /* @__PURE__ */ _jsx229("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx229("path", {
    d: "m8 6-6 6 6 6m8-12 6 6-6 6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCode_default = SvgIconCode;

// src/icons/line/IconCoffee.tsx
import { jsx as _jsx230 } from "react/jsx-runtime";
var SvgIconCoffee = (props) => /* @__PURE__ */ _jsx230("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx230("path", {
    d: "M2 22h18M19 6v0c.932 0 1.398 0 1.765.152a2 2 0 0 1 1.083 1.083C22 7.602 22 8.068 22 9v1c0 .932 0 1.398-.152 1.765a2 2 0 0 1-1.083 1.083C20.398 13 19.932 13 19 13v0m-8 5v0c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C19 14.2 19 12.8 19 10V6.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C16.72 2 15.88 2 14.2 2H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 4.28 3 5.12 3 6.8V10c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C6.8 18 8.2 18 11 18Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCoffee_default = SvgIconCoffee;

// src/icons/line/IconColorFilter.tsx
import { jsx as _jsx231 } from "react/jsx-runtime";
var SvgIconColorFilter = (props) => /* @__PURE__ */ _jsx231("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx231("path", {
    d: "M6 10.224a6.06 6.06 0 0 0-4 5.715C2 19.287 4.686 22 8 22s6-2.713 6-6.06c0-.679-.11-1.331-.314-1.94M12 20.614A6.116 6.116 0 0 0 15.89 22c3.374 0 6.11-2.713 6.11-6.06 0-2.612-1.665-4.838-4-5.69M18 8A6 6 0 1 1 6 8a6 6 0 0 1 12 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconColorFilter_default = SvgIconColorFilter;

// src/icons/line/IconColumnSpacingRec.tsx
import { jsx as _jsx232 } from "react/jsx-runtime";
var SvgIconColumnSpacingRec = (props) => /* @__PURE__ */ _jsx232("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx232("path", {
    d: "M15.5 12h-7m7 0L14 10.5m1.5 1.5L14 13.5M8.5 12l1.5-1.5M8.5 12l1.5 1.5m8 3.5V7M6 17V7m4 15h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconColumnSpacingRec_default = SvgIconColumnSpacingRec;

// src/icons/line/IconColumnSpacing.tsx
import { jsx as _jsx233 } from "react/jsx-runtime";
var SvgIconColumnSpacing = (props) => /* @__PURE__ */ _jsx233("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx233("path", {
    d: "M2 21V3m20 0v18m-4-9H6m12 0-2.5-2.5M18 12l-2.5 2.5M6 12l2.5-2.5M6 12l2.5 2.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconColumnSpacing_default = SvgIconColumnSpacing;

// src/icons/line/IconComment2Alert.tsx
import { jsx as _jsx234 } from "react/jsx-runtime";
var SvgIconComment2Alert = (props) => /* @__PURE__ */ _jsx234("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx234("path", {
    d: "M12 12.5v-5m.5 8.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M22 12c0 5.523-4.477 10-10 10-1.14 0-2.116-.162-3.024-.487-.857-.307-1.286-.46-1.45-.499-1.518-.357-2.148.684-3.456.902a1 1 0 0 1-1.161-1.069c.046-.567.439-1.104.596-1.65.325-1.133-.117-1.992-.584-3A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2Alert_default = SvgIconComment2Alert;

// src/icons/line/IconComment2Cancel.tsx
import { jsx as _jsx235 } from "react/jsx-runtime";
var SvgIconComment2Cancel = (props) => /* @__PURE__ */ _jsx235("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx235("path", {
    d: "M9.5 14.5 12 12m0 0 2.5-2.5M12 12 9.5 9.5M12 12l2.5 2.5M22 12c0 5.523-4.477 10-10 10-1.14 0-2.116-.162-3.024-.487-.857-.307-1.286-.46-1.45-.499-1.518-.357-2.148.684-3.456.902a1 1 0 0 1-1.161-1.069c.046-.567.439-1.104.596-1.65.325-1.133-.117-1.992-.584-3A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2Cancel_default = SvgIconComment2Cancel;

// src/icons/line/IconComment2Check.tsx
import { jsx as _jsx236 } from "react/jsx-runtime";
var SvgIconComment2Check = (props) => /* @__PURE__ */ _jsx236("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx236("path", {
    d: "m9 12 2 2 4.5-4.5M22 12c0 5.523-4.477 10-10 10-1.14 0-2.116-.162-3.024-.487-.857-.307-1.286-.46-1.45-.499-1.518-.357-2.148.684-3.456.902a1 1 0 0 1-1.161-1.069c.046-.567.439-1.104.596-1.65.325-1.133-.117-1.992-.584-3A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2Check_default = SvgIconComment2Check;

// src/icons/line/IconComment2Dots.tsx
import { jsx as _jsx237 } from "react/jsx-runtime";
var SvgIconComment2Dots = (props) => /* @__PURE__ */ _jsx237("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx237("path", {
    d: "M8 12a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m5.5 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5.5 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m6 0c0 5.523-4.477 10-10 10-1.14 0-2.116-.162-3.024-.487-.857-.307-1.286-.46-1.45-.499-1.518-.357-2.148.684-3.456.902a1 1 0 0 1-1.161-1.069c.046-.567.439-1.104.596-1.65.325-1.133-.117-1.992-.584-3A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2Dots_default = SvgIconComment2Dots;

// src/icons/line/IconComment2Info.tsx
import { jsx as _jsx238 } from "react/jsx-runtime";
var SvgIconComment2Info = (props) => /* @__PURE__ */ _jsx238("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx238("path", {
    d: "M12 16v-5m.5-3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M22 12c0 5.523-4.477 10-10 10-1.14 0-2.116-.162-3.024-.487-.857-.307-1.286-.46-1.45-.499-1.518-.357-2.148.684-3.456.902a1 1 0 0 1-1.161-1.069c.046-.567.439-1.104.596-1.65.325-1.133-.117-1.992-.584-3A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2Info_default = SvgIconComment2Info;

// src/icons/line/IconComment2Minus.tsx
import { jsx as _jsx239 } from "react/jsx-runtime";
var SvgIconComment2Minus = (props) => /* @__PURE__ */ _jsx239("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx239("path", {
    d: "M9 12h6m7 0c0 5.523-4.477 10-10 10-1.14 0-2.116-.162-3.024-.487-.857-.307-1.286-.46-1.45-.499-1.518-.357-2.148.684-3.456.902a1 1 0 0 1-1.161-1.069c.046-.567.439-1.104.596-1.65.325-1.133-.117-1.992-.584-3A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2Minus_default = SvgIconComment2Minus;

// src/icons/line/IconComment2Plus.tsx
import { jsx as _jsx240 } from "react/jsx-runtime";
var SvgIconComment2Plus = (props) => /* @__PURE__ */ _jsx240("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx240("path", {
    d: "M9 12h3m0 0h3m-3 0V9m0 3v3m10-3c0 5.523-4.477 10-10 10-1.14 0-2.116-.162-3.024-.487-.857-.307-1.286-.46-1.45-.499-1.518-.357-2.148.684-3.456.902a1 1 0 0 1-1.161-1.069c.046-.567.439-1.104.596-1.65.325-1.133-.117-1.992-.584-3A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2Plus_default = SvgIconComment2Plus;

// src/icons/line/IconComment2Question.tsx
import { jsx as _jsx241 } from "react/jsx-runtime";
var SvgIconComment2Question = (props) => /* @__PURE__ */ _jsx241("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx241("path", {
    d: "M12.5 16a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M10 10.5V10a2 2 0 1 1 4 0v.121c0 .563-.223 1.102-.621 1.5L12 13m10-1c0 5.523-4.477 10-10 10-1.14 0-2.116-.162-3.024-.487-.857-.307-1.286-.46-1.45-.499-1.518-.357-2.148.684-3.456.902a1 1 0 0 1-1.161-1.069c.046-.567.439-1.104.596-1.65.325-1.133-.117-1.992-.584-3A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2Question_default = SvgIconComment2Question;

// src/icons/line/IconComment2Slash.tsx
import { jsx as _jsx242 } from "react/jsx-runtime";
var SvgIconComment2Slash = (props) => /* @__PURE__ */ _jsx242("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx242("path", {
    d: "m2 2 2.929 2.929M22 22l-2.929-2.929m0 0A9.97 9.97 0 0 1 12 22c-1.14 0-2.116-.162-3.024-.487-.857-.307-1.286-.46-1.45-.499-1.518-.357-2.148.684-3.456.902a1 1 0 0 1-1.161-1.069c.046-.567.439-1.104.596-1.65.325-1.133-.117-1.992-.584-3A9.96 9.96 0 0 1 2 12a9.969 9.969 0 0 1 2.929-7.071M19.07 19.07 4.93 4.93M8 2.832A9.966 9.966 0 0 1 12 2c5.523 0 10 4.477 10 10a9.966 9.966 0 0 1-.832 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2Slash_default = SvgIconComment2Slash;

// src/icons/line/IconComment2Text.tsx
import { jsx as _jsx243 } from "react/jsx-runtime";
var SvgIconComment2Text = (props) => /* @__PURE__ */ _jsx243("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx243("path", {
    d: "M8 10h8m-8 4h4m10-2c0 5.523-4.477 10-10 10-1.14 0-2.116-.162-3.024-.487-.857-.307-1.286-.46-1.45-.499-1.518-.357-2.148.684-3.456.902a1 1 0 0 1-1.161-1.069c.046-.567.439-1.104.596-1.65.325-1.133-.117-1.992-.584-3A9.96 9.96 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2Text_default = SvgIconComment2Text;

// src/icons/line/IconComment2.tsx
import { jsx as _jsx244 } from "react/jsx-runtime";
var SvgIconComment2 = (props) => /* @__PURE__ */ _jsx244("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx244("path", {
    d: "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12c0 1.499.33 2.921.921 4.197.467 1.008.91 1.867.584 3-.157.546-.55 1.083-.596 1.65a1 1 0 0 0 1.16 1.069c1.309-.218 1.939-1.259 3.457-.902.164.04.593.192 1.45.5.908.324 1.885.486 3.024.486Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment2_default = SvgIconComment2;

// src/icons/line/IconCommentAlert.tsx
import { jsx as _jsx245 } from "react/jsx-runtime";
var SvgIconCommentAlert = (props) => /* @__PURE__ */ _jsx245("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx245("path", {
    d: "M12 11V7m.5 7.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m7.216 6.53v0c.88.528 1.32.792 1.679.817a1.5 1.5 0 0 0 1.442-.817c.163-.32.163-.833.163-1.86V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 20 7.2 20 10 20h4.998c.612 0 .918 0 1.217.036.497.06.982.195 1.44.399.274.123.537.28 1.061.595Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCommentAlert_default = SvgIconCommentAlert;

// src/icons/line/IconCommentCancel.tsx
import { jsx as _jsx246 } from "react/jsx-runtime";
var SvgIconCommentCancel = (props) => /* @__PURE__ */ _jsx246("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx246("path", {
    d: "M9.5 13.5 12 11m0 0 2.5-2.5M12 11 9.5 8.5M12 11l2.5 2.5m4.216 7.53v0c.88.528 1.32.792 1.679.817a1.5 1.5 0 0 0 1.442-.817c.163-.32.163-.833.163-1.86V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 20 7.2 20 10 20h4.998c.612 0 .918 0 1.217.036.497.06.982.195 1.44.399.274.123.537.28 1.061.595Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCommentCancel_default = SvgIconCommentCancel;

// src/icons/line/IconCommentCheck.tsx
import { jsx as _jsx247 } from "react/jsx-runtime";
var SvgIconCommentCheck = (props) => /* @__PURE__ */ _jsx247("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx247("path", {
    d: "m9 11 2 2 4.5-4.5m3.216 12.53v0c.88.528 1.32.792 1.679.817a1.5 1.5 0 0 0 1.442-.817c.163-.32.163-.833.163-1.86V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 20 7.2 20 10 20h4.998c.612 0 .918 0 1.217.036.497.06.982.195 1.44.399.274.123.537.28 1.061.595Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCommentCheck_default = SvgIconCommentCheck;

// src/icons/line/IconCommentDots.tsx
import { jsx as _jsx248 } from "react/jsx-runtime";
var SvgIconCommentDots = (props) => /* @__PURE__ */ _jsx248("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx248("path", {
    d: "M8 11a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7m5.5 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5.5 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m2.716 10.03v0c.88.528 1.32.792 1.679.817a1.5 1.5 0 0 0 1.442-.817c.163-.32.163-.833.163-1.86V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 20 7.2 20 10 20h4.998c.612 0 .918 0 1.217.036.497.06.982.195 1.44.399.274.123.537.28 1.061.595Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCommentDots_default = SvgIconCommentDots;

// src/icons/line/IconCommentMinus.tsx
import { jsx as _jsx249 } from "react/jsx-runtime";
var SvgIconCommentMinus = (props) => /* @__PURE__ */ _jsx249("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx249("path", {
    d: "M9 11h6m3.716 10.03v0c.88.528 1.32.792 1.679.817a1.5 1.5 0 0 0 1.442-.817c.163-.32.163-.833.163-1.86V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 20 7.2 20 10 20h4.998c.612 0 .918 0 1.217.036.497.06.982.195 1.44.399.274.123.537.28 1.061.595Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCommentMinus_default = SvgIconCommentMinus;

// src/icons/line/IconCommentPlus.tsx
import { jsx as _jsx250 } from "react/jsx-runtime";
var SvgIconCommentPlus = (props) => /* @__PURE__ */ _jsx250("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx250("path", {
    d: "M9 11h3m0 0h3m-3 0V8m0 3v3m6.716 7.03v0c.88.528 1.32.792 1.679.817a1.5 1.5 0 0 0 1.442-.817c.163-.32.163-.833.163-1.86V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 20 7.2 20 10 20h4.998c.612 0 .918 0 1.217.036.497.06.982.195 1.44.399.274.123.537.28 1.061.595Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCommentPlus_default = SvgIconCommentPlus;

// src/icons/line/IconCommentSlash.tsx
import { jsx as _jsx251 } from "react/jsx-runtime";
var SvgIconCommentSlash = (props) => /* @__PURE__ */ _jsx251("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx251("path", {
    d: "m22.5 1.5-1.965 1.964M2 22l2.621-2.621M20.535 3.464a4.998 4.998 0 0 0-.922-.727C18.41 2 16.773 2 13.5 2H11c-3.75 0-5.625 0-6.939.955A5 5 0 0 0 2.955 4.06C2 5.375 2 7.251 2 11c0 3.75 0 5.625.955 6.939a5 5 0 0 0 1.106 1.106c.176.128.361.238.56.334M20.535 3.464 4.621 19.38M21.935 7S22 13.025 22 16v3.17c0 1.027 0 1.54-.163 1.86a1.5 1.5 0 0 1-1.442.817c-.359-.025-.799-.29-1.678-.817-.525-.315-.788-.472-1.063-.595a5.001 5.001 0 0 0-1.439-.399C15.916 20 15.61 20 14.998 20c-2.165 0-4.332.026-6.498 0",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCommentSlash_default = SvgIconCommentSlash;

// src/icons/line/IconCommentText.tsx
import { jsx as _jsx252 } from "react/jsx-runtime";
var SvgIconCommentText = (props) => /* @__PURE__ */ _jsx252("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx252("path", {
    d: "M7 8h8m-8 4h4m7.716 9.03v0c.88.528 1.32.792 1.679.817a1.5 1.5 0 0 0 1.442-.817c.163-.32.163-.833.163-1.86V10c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 20 7.2 20 10 20h4.998c.612 0 .918 0 1.217.036.497.06.982.195 1.44.399.274.123.537.28 1.061.595Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCommentText_default = SvgIconCommentText;

// src/icons/line/IconComment.tsx
import { jsx as _jsx253 } from "react/jsx-runtime";
var SvgIconComment = (props) => /* @__PURE__ */ _jsx253("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx253("path", {
    d: "M2 10c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v9.17c0 1.027 0 1.54-.163 1.86a1.5 1.5 0 0 1-1.442.817c-.359-.025-.799-.29-1.678-.817v0c-.525-.315-.788-.472-1.063-.595a4.998 4.998 0 0 0-1.439-.399C15.916 20 15.61 20 14.998 20H10c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 16.2 2 14.8 2 12v-2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComment_default = SvgIconComment;

// src/icons/line/IconComments2.tsx
import { jsx as _jsx254 } from "react/jsx-runtime";
var SvgIconComments2 = (props) => /* @__PURE__ */ _jsx254("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx254("path", {
    d: "M8.977 18.868a6.767 6.767 0 0 1-1.047-.282c-.729-.26-1.093-.39-1.233-.424-1.29-.303-1.826.581-2.938.766a.85.85 0 0 1-.987-.908c.04-.482.374-.939.507-1.402.277-.963-.099-1.694-.496-2.55A8.5 8.5 0 0 1 17.713 6M11 15a6 6 0 0 0 6 6c.684 0 1.27-.097 1.814-.292.515-.184.772-.276.87-.3.911-.213 1.29.41 2.074.541a.6.6 0 0 0 .697-.64c-.028-.341-.264-.663-.358-.99-.195-.68.07-1.196.35-1.8A6 6 0 1 0 11 15Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComments2_default = SvgIconComments2;

// src/icons/line/IconComments.tsx
import { jsx as _jsx255 } from "react/jsx-runtime";
var SvgIconComments = (props) => /* @__PURE__ */ _jsx255("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx255("path", {
    d: "M9 14H7.25c-.247 0-.37 0-.491.01-.483.039-.95.194-1.36.453-.102.065-.201.139-.399.287v0c-.511.384-.767.575-.962.65a1.5 1.5 0 0 1-1.98-.99C2 14.209 2 13.89 2 13.25V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2h6.4c1.68 0 2.52 0 3.162.327a3 3 0 0 1 1.311 1.311C18 4.28 18 5.12 18 6.8v.7m2.09 13.682v0c.72.685 1.91.175 1.91-.818V13.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C20.48 10 19.92 10 18.8 10h-6.6c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C9 11.52 9 12.08 9 13.2v3.743c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874c.428.218.988.218 2.108.218h5.294c.967 0 1.897.372 2.597 1.039Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconComments_default = SvgIconComments;

// src/icons/line/IconCompass.tsx
import { jsx as _jsx256, jsxs as _jsxs6 } from "react/jsx-runtime";
var SvgIconCompass = (props) => /* @__PURE__ */ _jsxs6("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx256("path", {
    d: "M9.755 12.139c.276-.607.414-.91.608-1.17.172-.23.376-.434.606-.606.26-.194.563-.332 1.17-.608L16 8l-1.755 3.861c-.276.607-.414.91-.608 1.17-.172.23-.376.434-.606.606-.26.194-.563.332-1.17.608L8 16l1.755-3.861Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx256("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconCompass_default = SvgIconCompass;

// src/icons/line/IconCopy.tsx
import { jsx as _jsx257 } from "react/jsx-runtime";
var SvgIconCopy = (props) => /* @__PURE__ */ _jsx257("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx257("path", {
    d: "M16.167 8v0c1.707 0 2.56 0 3.242.256a4 4 0 0 1 2.335 2.335c.256.681.256 1.535.256 3.242V15.6c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22h-1.766c-1.708 0-2.562 0-3.243-.256a4 4 0 0 1-2.334-2.335C8 18.728 8 17.874 8 16.167v0M8.4 16h1.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C16 12.96 16 11.84 16 9.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C12.96 2 11.84 2 9.6 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 16 6.16 16 8.4 16Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCopy_default = SvgIconCopy;

// src/icons/line/IconCopyright.tsx
import { jsx as _jsx258 } from "react/jsx-runtime";
var SvgIconCopyright = (props) => /* @__PURE__ */ _jsx258("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx258("path", {
    d: "M15 9.6a3.968 3.968 0 0 0-.778-.8A3.801 3.801 0 0 0 11.89 8C9.74 8 8 9.79 8 12s1.741 4 3.889 4c.875 0 1.683-.298 2.333-.8.295-.227.557-.497.778-.8M12 22v0c5.523 0 10-4.477 10-10v0c0-5.523-4.477-10-10-10v0C6.477 2 2 6.477 2 12v0c0 5.523 4.477 10 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCopyright_default = SvgIconCopyright;

// src/icons/line/IconCreditCardIncome.tsx
import { jsx as _jsx259 } from "react/jsx-runtime";
var SvgIconCreditCardIncome = (props) => /* @__PURE__ */ _jsx259("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx259("path", {
    d: "M2 15.286V9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C5.04 3 6.16 3 8.4 3h7.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 6.04 22 7.16 22 9.4v5.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 21 17.84 21 15.6 21H11m-9-2h6m0 0-2-2m2 2-2 2M2.5 8h19M15 12h3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCreditCardIncome_default = SvgIconCreditCardIncome;

// src/icons/line/IconCreditCardMinus.tsx
import { jsx as _jsx260 } from "react/jsx-runtime";
var SvgIconCreditCardMinus = (props) => /* @__PURE__ */ _jsx260("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx260("path", {
    d: "M22 13.286V9.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 3 17.84 3 15.6 3H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 6.04 2 7.16 2 9.4v5.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 21 6.16 21 8.4 21H14M2.5 8h19M6 12h3m8.5 7h5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCreditCardMinus_default = SvgIconCreditCardMinus;

// src/icons/line/IconCreditCardOutcome.tsx
import { jsx as _jsx261 } from "react/jsx-runtime";
var SvgIconCreditCardOutcome = (props) => /* @__PURE__ */ _jsx261("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx261("path", {
    d: "M22 13.286V9.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 3 17.84 3 15.6 3H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 6.04 2 7.16 2 9.4v5.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 21 6.16 21 8.4 21H14M2.5 8h19M6 12h3m8 7h6m0 0-2-2m2 2-2 2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCreditCardOutcome_default = SvgIconCreditCardOutcome;

// src/icons/line/IconCreditCardPlus.tsx
import { jsx as _jsx262 } from "react/jsx-runtime";
var SvgIconCreditCardPlus = (props) => /* @__PURE__ */ _jsx262("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx262("path", {
    d: "M22 13.286V9.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 3 17.84 3 15.6 3H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 6.04 2 7.16 2 9.4v5.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 21 6.16 21 8.4 21H14M2.5 8h19M20 16.5V19m0 0v2.5m0-2.5h-2.5m2.5 0h2.5M6 12h3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCreditCardPlus_default = SvgIconCreditCardPlus;

// src/icons/line/IconCreditCardScan.tsx
import { jsx as _jsx263 } from "react/jsx-runtime";
var SvgIconCreditCardScan = (props) => /* @__PURE__ */ _jsx263("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx263("path", {
    d: "M22 7v-.2c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C19.72 2 18.88 2 17.2 2H17m5 15v.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C19.72 22 18.88 22 17.2 22H17M2 17v.2c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 22 5.12 22 6.8 22H7M2 7v-.2c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2H7m-1 9h12m0 2.8v-3.6c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C16.48 7 15.92 7 14.8 7H9.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C6 8.52 6 9.08 6 10.2v3.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C7.52 17 8.08 17 9.2 17h5.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C18 15.48 18 14.92 18 13.8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCreditCardScan_default = SvgIconCreditCardScan;

// src/icons/line/IconCreditCardSlash.tsx
import { jsx as _jsx264 } from "react/jsx-runtime";
var SvgIconCreditCardSlash = (props) => /* @__PURE__ */ _jsx264("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx264("path", {
    d: "M2.006 8H8m13.006 0H12M2 2l1.716 1.716M22 22l-1.716-1.716m0 0a4.005 4.005 0 0 1-.468.28C18.96 21 17.84 21 15.6 21H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 17.96 2 16.84 2 14.6V9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.28-1.468m16.568 16.568L8 8M3.716 3.716 8 8M7 3.006C7.413 3 7.876 3 8.4 3h7.2c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 6.04 22 7.16 22 9.4v5.2c0 .997 0 1.771-.038 2.4-.011.178-.025.344-.043.5M6 12h2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCreditCardSlash_default = SvgIconCreditCardSlash;

// src/icons/line/IconCreditCard.tsx
import { jsx as _jsx265 } from "react/jsx-runtime";
var SvgIconCreditCard = (props) => /* @__PURE__ */ _jsx265("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx265("path", {
    d: "M2.5 8h19M6 12h4m-1.6 9h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 17.96 22 16.84 22 14.6V9.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 3 17.84 3 15.6 3H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 6.04 2 7.16 2 9.4v5.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 21 6.16 21 8.4 21Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCreditCard_default = SvgIconCreditCard;

// src/icons/line/IconCropRec.tsx
import { jsx as _jsx266 } from "react/jsx-runtime";
var SvgIconCropRec = (props) => /* @__PURE__ */ _jsx266("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx266("path", {
    d: "M16 16h-4.8c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C8 14.48 8 13.92 8 12.8V8m8 8v2m0-2h2M8 8V6m0 2H6m5 0h1.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C16 9.52 16 10.08 16 11.2V13m-6 9h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCropRec_default = SvgIconCropRec;

// src/icons/line/IconCropRotateRec.tsx
import { jsx as _jsx267 } from "react/jsx-runtime";
var SvgIconCropRotateRec = (props) => /* @__PURE__ */ _jsx267("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx267("path", {
    d: "M5 12a7 7 0 0 0 7 7l-1-1.5m8-5.5a7 7 0 0 0-7-7l1 1.5m1.5 8h-2.9c-.56 0-.84 0-1.054-.109a1 1 0 0 1-.437-.437C10 13.74 10 13.46 10 12.9V10m4.5 4.5v1m0-1h1M10 10V9m0 1H9m3 0h.9c.56 0 .84 0 1.054.109a1 1 0 0 1 .437.437c.109.214.109.494.109 1.054v.9M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCropRotateRec_default = SvgIconCropRotateRec;

// src/icons/line/IconCropRotate.tsx
import { jsx as _jsx268 } from "react/jsx-runtime";
var SvgIconCropRotate = (props) => /* @__PURE__ */ _jsx268("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx268("path", {
    d: "M16 16h-4.8c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C8 14.48 8 13.92 8 12.8V8m8 8v2m0-2h2M8 8V6m0 2H6m-4 4c0 5.523 4.477 10 10 10l-1.5-2.5M22 12c0-5.523-4.477-10-10-10l1.5 2.5m-3 3.5h2.3c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C16 9.52 16 10.08 16 11.2v2.3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCropRotate_default = SvgIconCropRotate;

// src/icons/line/IconCrop.tsx
import { jsx as _jsx269 } from "react/jsx-runtime";
var SvgIconCrop = (props) => /* @__PURE__ */ _jsx269("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx269("path", {
    d: "M19 19H8.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C5 17.48 5 16.92 5 15.8V5m14 14v3m0-3h3M5 5V2m0 3H2m6 0h7.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C19 6.52 19 7.08 19 8.2V16",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCrop_default = SvgIconCrop;

// src/icons/line/IconCrossCircle.tsx
import { jsx as _jsx270 } from "react/jsx-runtime";
var SvgIconCrossCircle = (props) => /* @__PURE__ */ _jsx270("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx270("path", {
    d: "m9 15 3-3m0 0 3-3m-3 3L9 9m3 3 3 3m7-3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCrossCircle_default = SvgIconCrossCircle;

// src/icons/line/IconCrossOctagon.tsx
import { jsx as _jsx271 } from "react/jsx-runtime";
var SvgIconCrossOctagon = (props) => /* @__PURE__ */ _jsx271("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx271("path", {
    d: "m9 15 3-3m0 0 3-3m-3 3L9 9m3 3 3 3M3.875 17.875l2.25 2.25c.692.692 1.038 1.038 1.442 1.286a4 4 0 0 0 1.156.479c.46.11.95.11 1.928.11h2.698c.978 0 1.468 0 1.928-.11.408-.099.798-.26 1.156-.48.404-.247.75-.593 1.442-1.285l2.25-2.25c.692-.692 1.038-1.038 1.286-1.442.219-.358.38-.748.479-1.156.11-.46.11-.95.11-1.928v-2.698c0-.978 0-1.468-.11-1.928a4 4 0 0 0-.48-1.156c-.247-.404-.593-.75-1.285-1.442l-2.25-2.25c-.692-.692-1.038-1.038-1.442-1.286a3.998 3.998 0 0 0-1.156-.478C14.817 2 14.327 2 13.349 2h-2.698c-.978 0-1.468 0-1.928.11a4 4 0 0 0-1.156.48c-.404.247-.75.593-1.442 1.285l-2.25 2.25c-.692.692-1.038 1.038-1.286 1.442a4 4 0 0 0-.478 1.156C2 9.183 2 9.673 2 10.651v2.698c0 .978 0 1.468.11 1.928.099.408.26.798.48 1.156.247.404.593.75 1.285 1.442Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCrossOctagon_default = SvgIconCrossOctagon;

// src/icons/line/IconCrossRec.tsx
import { jsx as _jsx272 } from "react/jsx-runtime";
var SvgIconCrossRec = (props) => /* @__PURE__ */ _jsx272("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx272("path", {
    d: "m8 16 4-4m0 0 4-4m-4 4L8 8m4 4 4 4m-4.4 6h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCrossRec_default = SvgIconCrossRec;

// src/icons/line/IconCross.tsx
import { jsx as _jsx273 } from "react/jsx-runtime";
var SvgIconCross = (props) => /* @__PURE__ */ _jsx273("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx273("path", {
    d: "M6 18 18 6M6 6l12 12",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCross_default = SvgIconCross;

// src/icons/line/IconCrosshair.tsx
import { jsx as _jsx274 } from "react/jsx-runtime";
var SvgIconCrosshair = (props) => /* @__PURE__ */ _jsx274("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx274("path", {
    d: "M22 12c0 5.523-4.477 10-10 10m10-10c0-5.523-4.477-10-10-10m10 10h-3m-7 10C6.477 22 2 17.523 2 12m10 10v-3M2 12C2 6.477 6.477 2 12 2M2 12h3m7-10v3m2 7a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCrosshair_default = SvgIconCrosshair;

// src/icons/line/IconCut.tsx
import { jsx as _jsx275, jsxs as _jsxs7 } from "react/jsx-runtime";
var SvgIconCut = (props) => /* @__PURE__ */ _jsxs7("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx275("g", {
    clipPath: "url(#a)",
    children: /* @__PURE__ */ _jsx275("path", {
      d: "M7 14 17.5 1.5m0 12.5-5.25-6.25M10.36 5.5 7 1.5M22 18a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm-12 0a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z",
      stroke: props.color,
      strokeWidth: 1.5,
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  }), /* @__PURE__ */ _jsx275("defs", {
    children: /* @__PURE__ */ _jsx275("clipPath", {
      id: "a",
      children: /* @__PURE__ */ _jsx275("path", {
        fill: "#fff",
        d: "M0 0h24v24H0z"
      })
    })
  })]
});
var IconCut_default = SvgIconCut;

// src/icons/line/IconCutlery2.tsx
import { jsx as _jsx276 } from "react/jsx-runtime";
var SvgIconCutlery2 = (props) => /* @__PURE__ */ _jsx276("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx276("path", {
    d: "m2 21 6-6 3-3m5 1 4.737-4.737c.792-.792 1.188-1.188 1.337-1.645a2 2 0 0 0 0-1.236c-.149-.457-.545-.853-1.337-1.645L20 3l-7 7M3.5 4.5l6 6m0 0-1.916.274c-.58.083-.871.124-1.149.086a2.001 2.001 0 0 1-.7-.233c-.244-.136-.452-.344-.866-.758L2 7m7.5 3.5.274-1.916c.083-.58.124-.871.086-1.149a2 2 0 0 0-.233-.7c-.136-.244-.344-.452-.758-.866L6 3m3.5 7.5L20 21",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCutlery2_default = SvgIconCutlery2;

// src/icons/line/IconCutlery3.tsx
import { jsx as _jsx277 } from "react/jsx-runtime";
var SvgIconCutlery3 = (props) => /* @__PURE__ */ _jsx277("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx277("path", {
    d: "M7 2v9m0 11V11m0 0L5.425 9.95c-.521-.347-.782-.521-.97-.75a2 2 0 0 1-.369-.69C4 8.228 4 7.915 4 7.288V2m3 9 1.575-1.05c.521-.347.782-.521.97-.75a2 2 0 0 0 .369-.69C10 8.228 10 7.915 10 7.288V2m7 0v12m0 0c-1.657 0-3.5 1.79-3.5 4s1.843 4 3.5 4 3.5-1.79 3.5-4-1.843-4-3.5-4Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCutlery3_default = SvgIconCutlery3;

// src/icons/line/IconCutlery4.tsx
import { jsx as _jsx278 } from "react/jsx-runtime";
var SvgIconCutlery4 = (props) => /* @__PURE__ */ _jsx278("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx278("path", {
    d: "M15 22v-8m0 0V2l1.576.394c.867.217 1.3.325 1.623.567.284.213.507.498.645.826.156.371.156.818.156 1.711v4.524c0 .718 0 1.077-.11 1.394a2 2 0 0 1-.461.747c-.235.24-.556.4-1.198.721L15 14ZM7 2v9m0 0v11m0-11L5.425 9.95c-.521-.347-.782-.521-.97-.75a2 2 0 0 1-.369-.69C4 8.228 4 7.915 4 7.288V2m3 9 1.575-1.05c.521-.347.782-.521.97-.75a2 2 0 0 0 .369-.69C10 8.228 10 7.915 10 7.288V2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCutlery4_default = SvgIconCutlery4;

// src/icons/line/IconCutlery.tsx
import { jsx as _jsx279 } from "react/jsx-runtime";
var SvgIconCutlery = (props) => /* @__PURE__ */ _jsx279("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx279("path", {
    d: "m2 21 7-7 2-2m5 1 4.737-4.737c.792-.792 1.188-1.188 1.337-1.645a2 2 0 0 0 0-1.236c-.149-.457-.545-.853-1.337-1.645L20 3l-7 7m7 11-3.3-3.3-5.5-5.5L9 10m-.474-5.526c1.64 1.64 2.084 3.856.444 5.496-1.64 1.64-3.855 1.196-5.496-.444C1.834 7.886 1.39 5.67 3.03 4.03c1.64-1.64 3.855-1.196 5.496.444Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconCutlery_default = SvgIconCutlery;

// src/icons/line/IconDashboard2.tsx
import { jsx as _jsx280 } from "react/jsx-runtime";
var SvgIconDashboard2 = (props) => /* @__PURE__ */ _jsx280("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx280("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12m20 0c0-5.523-4.477-10-10-10m10 10h-2M2 12C2 6.477 6.477 2 12 2M2 12h2m8-10v2M4 18l1.232-1.232A6.035 6.035 0 0 1 9.5 15v0M20 18l-1.232-1.232A6.036 6.036 0 0 0 14.5 15v0M19 5l-1 1M5 5l1 1m6 7a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm0 0V9",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDashboard2_default = SvgIconDashboard2;

// src/icons/line/IconDashboard.tsx
import { jsx as _jsx281 } from "react/jsx-runtime";
var SvgIconDashboard = (props) => /* @__PURE__ */ _jsx281("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx281("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12m20 0c0-5.523-4.477-10-10-10m10 10h-2M2 12C2 6.477 6.477 2 12 2M2 12h2m8-10v2m7 1-1 1m1 13-1-1M5 19l1-1M5 5l1 1m6 3s-2 1.482-2 3.556c0 3.259 4 3.259 4 0C14 10.482 12 9 12 9Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDashboard_default = SvgIconDashboard;

// src/icons/line/IconDevices.tsx
import { jsx as _jsx282 } from "react/jsx-runtime";
var SvgIconDevices = (props) => /* @__PURE__ */ _jsx282("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx282("path", {
    d: "M19 12h.01M19 21H4.556A3.556 3.556 0 0 1 1 17.444v0c0-.245.199-.444.444-.444H15M3 17V9.4c0-2.24 0-3.36.436-4.216a4 4 0 0 1 1.748-1.748C6.04 3 7.16 3 9.4 3h9.1A2.5 2.5 0 0 1 21 5.5V6m-1.5 6a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm-1.3 9h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C23 19.48 23 18.92 23 17.8v-5.6c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C21.48 9 20.92 9 19.8 9h-1.6c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C15 10.52 15 11.08 15 12.2v5.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C16.52 21 17.08 21 18.2 21Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDevices_default = SvgIconDevices;

// src/icons/line/IconDialpad.tsx
import { jsx as _jsx283 } from "react/jsx-runtime";
var SvgIconDialpad = (props) => /* @__PURE__ */ _jsx283("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx283("path", {
    d: "M6 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM6 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14 20a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM22 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM22 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDialpad_default = SvgIconDialpad;

// src/icons/line/IconDirection2.tsx
import { jsx as _jsx284, jsxs as _jsxs8 } from "react/jsx-runtime";
var SvgIconDirection2 = (props) => /* @__PURE__ */ _jsxs8("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx284("g", {
    clipPath: "url(#a)",
    children: /* @__PURE__ */ _jsx284("path", {
      d: "M21.466 6.478c.502-1.791.752-2.687.52-3.302a2 2 0 0 0-1.162-1.162c-.615-.233-1.51.018-3.302.52L7.296 5.397C4.466 6.19 3.05 6.586 2.64 7.258a2 2 0 0 0-.09 1.917c.345.708 1.716 1.236 4.459 2.29l2.663 1.024c.488.188.733.282.938.429a2 2 0 0 1 .471.47c.146.206.24.45.428.94l1.024 2.662c1.055 2.743 1.583 4.114 2.291 4.459a2 2 0 0 0 1.916-.09c.673-.41 1.07-1.825 1.862-4.655l2.863-10.226Z",
      stroke: props.color,
      strokeWidth: 1.5,
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  }), /* @__PURE__ */ _jsx284("defs", {
    children: /* @__PURE__ */ _jsx284("clipPath", {
      id: "a",
      children: /* @__PURE__ */ _jsx284("path", {
        fill: "#fff",
        d: "M0 0h24v24H0z"
      })
    })
  })]
});
var IconDirection2_default = SvgIconDirection2;

// src/icons/line/IconDirection.tsx
import { jsx as _jsx285, jsxs as _jsxs9 } from "react/jsx-runtime";
var SvgIconDirection = (props) => /* @__PURE__ */ _jsxs9("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx285("g", {
    clipPath: "url(#a)",
    children: /* @__PURE__ */ _jsx285("path", {
      d: "M14.862 4.724c-.921-1.842-1.382-2.763-2.005-3.059a2 2 0 0 0-1.714 0c-.623.296-1.084 1.217-2.005 3.06L4.019 14.96c-1.498 2.997-2.247 4.496-1.996 5.274A2 2 0 0 0 3.48 21.57c.797.182 2.224-.696 5.078-2.452l1.764-1.086c.609-.375.913-.562 1.24-.635a2 2 0 0 1 .875 0c.326.073.63.26 1.24.635l1.763 1.086c2.854 1.756 4.28 2.634 5.078 2.452a2 2 0 0 0 1.458-1.335c.251-.778-.498-2.277-1.996-5.274L14.862 4.724Z",
      stroke: props.color,
      strokeWidth: 1.5,
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  }), /* @__PURE__ */ _jsx285("defs", {
    children: /* @__PURE__ */ _jsx285("clipPath", {
      id: "a",
      children: /* @__PURE__ */ _jsx285("path", {
        fill: "#fff",
        d: "M0 0h24v24H0z"
      })
    })
  })]
});
var IconDirection_default = SvgIconDirection;

// src/icons/line/IconDiskette.tsx
import { jsx as _jsx286 } from "react/jsx-runtime";
var SvgIconDiskette = (props) => /* @__PURE__ */ _jsx286("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx286("path", {
    d: "M8 8h8m0 13.5v-5.3c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C14.48 13 13.92 13 12.8 13h-1.6c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C8 14.52 8 15.08 8 16.2v5.3M14.349 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6V9.651c0-.978 0-1.468-.11-1.928a4 4 0 0 0-.48-1.156c-.247-.404-.593-.75-1.285-1.442l-1.25-1.25c-.692-.692-1.038-1.038-1.442-1.286a3.998 3.998 0 0 0-1.156-.478C15.817 2 15.327 2 14.349 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconDiskette_default = SvgIconDiskette;

// src/icons/line/IconDistributeHorizontally.tsx
import { jsx as _jsx287 } from "react/jsx-runtime";
var SvgIconDistributeHorizontally = (props) => /* @__PURE__ */ _jsx287("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx287("path", {
    d: "M2 3v18M22 3v18m-10.2-1h.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C17 17.72 17 16.88 17 15.2V8.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C14.72 4 13.88 4 12.2 4h-.4c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C7 6.28 7 7.12 7 8.8v6.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C9.28 20 10.12 20 11.8 20Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDistributeHorizontally_default = SvgIconDistributeHorizontally;

// src/icons/line/IconDistributeVertically.tsx
import { jsx as _jsx288 } from "react/jsx-runtime";
var SvgIconDistributeVertically = (props) => /* @__PURE__ */ _jsx288("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx288("path", {
    d: "M3 22h18M3 2h18M8.8 17h6.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C20 14.72 20 13.88 20 12.2v-.4c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C17.72 7 16.88 7 15.2 7H8.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C4 9.28 4 10.12 4 11.8v.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C6.28 17 7.12 17 8.8 17Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDistributeVertically_default = SvgIconDistributeVertically;

// src/icons/line/IconDollarCircle.tsx
import { jsx as _jsx289 } from "react/jsx-runtime";
var SvgIconDollarCircle = (props) => /* @__PURE__ */ _jsx289("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx289("path", {
    d: "M9 14a2 2 0 0 0 2 2h2.257a1.743 1.743 0 0 0 .55-3.397l-3.615-1.206A1.743 1.743 0 0 1 10.743 8H13a2 2 0 0 1 2 2m-3-3.5v11M22 12v0c0-5.523-4.477-10-10-10v0C6.477 2 2 6.477 2 12v0c0 5.523 4.477 10 10 10v0c5.523 0 10-4.477 10-10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDollarCircle_default = SvgIconDollarCircle;

// src/icons/line/IconDollar.tsx
import { jsx as _jsx290 } from "react/jsx-runtime";
var SvgIconDollar = (props) => /* @__PURE__ */ _jsx290("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx290("path", {
    d: "M6 16a4 4 0 0 0 4 4h4.513a3.487 3.487 0 0 0 1.103-6.795l-7.232-2.41A3.487 3.487 0 0 1 9.487 4H14a4 4 0 0 1 4 4m-6-6v20",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDollar_default = SvgIconDollar;

// src/icons/line/IconDribbble.tsx
import { jsx as _jsx291 } from "react/jsx-runtime";
var SvgIconDribbble = (props) => /* @__PURE__ */ _jsx291("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx291("path", {
    d: "M8.744 2.554a49.721 49.721 0 0 1 7.801 18.35m5.43-8.013a16.979 16.979 0 0 0-17.045 6.18m0 0c3.905 3.905 10.236 3.905 14.141 0 3.905-3.905 3.905-10.237 0-14.142M4.93 19.07C1.024 15.166 1.023 8.834 4.929 4.93c3.905-3.905 10.237-3.905 14.142 0m-17.01 6c6.067.643 12.36-1.351 17.01-6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDribbble_default = SvgIconDribbble;

// src/icons/line/IconDropletSlash.tsx
import { jsx as _jsx292 } from "react/jsx-runtime";
var SvgIconDropletSlash = (props) => /* @__PURE__ */ _jsx292("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx292("path", {
    d: "m2 2 4.762 4.762M22 22l-3.356-3.356M6.762 6.762C5.25 8.695 4 11.119 4 13.852c0 8.918 10.782 10.516 14.644 4.792M6.762 6.762l11.882 11.882M9.386 4c.358-.317.7-.6 1.011-.847.454-.359.68-.538 1.073-.646a2.412 2.412 0 0 1 1.059 0c.393.108.619.287 1.072.645C15.935 5 20 8.924 20 13.852c0 .397-.021.78-.063 1.148",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDropletSlash_default = SvgIconDropletSlash;

// src/icons/line/IconDroplet.tsx
import { jsx as _jsx293 } from "react/jsx-runtime";
var SvgIconDroplet = (props) => /* @__PURE__ */ _jsx293("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx293("path", {
    d: "M20 13.852c0 10.864-16 10.864-16 0C4 8.924 8.064 5 10.399 3.152c.453-.358.679-.537 1.072-.645a2.412 2.412 0 0 1 1.058 0c.393.108.619.287 1.072.645C15.935 5 20 8.924 20 13.852Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDroplet_default = SvgIconDroplet;

// src/icons/line/IconDumbell.tsx
import { jsx as _jsx294 } from "react/jsx-runtime";
var SvgIconDumbell = (props) => /* @__PURE__ */ _jsx294("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx294("path", {
    d: "M3.5 20.5 5 19M19 5l1.5-1.5M15 9l-6 6m2 6v0c.545-.545.818-.818.964-1.112a2 2 0 0 0 0-1.776c-.146-.294-.419-.567-.964-1.112l-4-4c-.545-.545-.818-.818-1.112-.964a2 2 0 0 0-1.776 0c-.294.146-.567.419-1.112.964v0c-.545.545-.818.818-.964 1.112a2 2 0 0 0 0 1.776c.146.294.419.567.964 1.112l4 4c.545.545.818.818 1.112.964a2 2 0 0 0 1.776 0c.294-.146.567-.419 1.112-.964Zm10-10v0c.545-.545.818-.818.964-1.112a2 2 0 0 0 0-1.776C21.818 7.818 21.545 7.545 21 7l-4-4c-.545-.545-.818-.818-1.112-.964a2 2 0 0 0-1.776 0c-.294.146-.567.419-1.112.964v0c-.545.545-.818.818-.964 1.112a2 2 0 0 0 0 1.776c.146.294.419.567.964 1.112l4 4c.545.545.818.818 1.112.964a2 2 0 0 0 1.776 0c.294-.146.567-.419 1.112-.964Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconDumbell_default = SvgIconDumbell;

// src/icons/line/IconEdit2.tsx
import { jsx as _jsx295 } from "react/jsx-runtime";
var SvgIconEdit2 = (props) => /* @__PURE__ */ _jsx295("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx295("path", {
    d: "M20 21H4M16 4a2.652 2.652 0 0 0 3 3M7 16l.57-2.28c.16-.64.24-.96.369-1.259.114-.265.256-.517.425-.75.19-.265.423-.498.89-.965L16.5 3.5c.08-.08.12-.12.155-.151a2 2 0 0 1 2.69 0c.035.032.075.071.155.151v0c.08.08.12.12.151.155a2 2 0 0 1 0 2.69c-.032.035-.072.075-.151.155l-7.247 7.246c-.466.467-.7.7-.963.89a3.996 3.996 0 0 1-.751.425c-.299.129-.619.209-1.259.369L7 16Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEdit2_default = SvgIconEdit2;

// src/icons/line/IconEdit3.tsx
import { jsx as _jsx296 } from "react/jsx-runtime";
var SvgIconEdit3 = (props) => /* @__PURE__ */ _jsx296("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx296("path", {
    d: "M15 5a4 4 0 0 0 4 4M4 20l.62-3.1c.141-.706.212-1.06.341-1.389.115-.292.263-.57.443-.827.202-.29.457-.545.966-1.054L16 4c.545-.545.818-.818 1.112-.964a2 2 0 0 1 1.776 0c.294.146.567.419 1.112.964v0c.545.545.818.818.964 1.112a2 2 0 0 1 0 1.776c-.146.294-.419.567-.964 1.112l-9.63 9.63c-.51.51-.764.764-1.054.966-.257.18-.535.328-.827.443-.33.13-.683.2-1.39.341L4 20Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEdit3_default = SvgIconEdit3;

// src/icons/line/IconEditSlash.tsx
import { jsx as _jsx297 } from "react/jsx-runtime";
var SvgIconEditSlash = (props) => /* @__PURE__ */ _jsx297("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx297("path", {
    d: "m4 3 6.5 6.5M21 20l-6.5-6.5m0 0-4.13 4.13c-.51.51-.764.764-1.054.966-.257.18-.535.328-.827.443-.33.13-.683.2-1.39.341L4 20l.62-3.1c.141-.706.212-1.06.341-1.389.115-.292.263-.57.443-.827.202-.29.457-.545.966-1.054L10.5 9.5m4 4-4-4M13 7l3-3c.545-.545.818-.818 1.112-.964a2 2 0 0 1 1.776 0c.294.146.567.419 1.112.964v0c.545.545.818.818.964 1.112a2 2 0 0 1 0 1.776c-.146.294-.419.567-.964 1.112l-3 3m-2-6a4 4 0 0 0 4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEditSlash_default = SvgIconEditSlash;

// src/icons/line/IconEdit.tsx
import { jsx as _jsx298 } from "react/jsx-runtime";
var SvgIconEdit = (props) => /* @__PURE__ */ _jsx298("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx298("path", {
    d: "M12 2h-2c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-2m-5-8a2.652 2.652 0 0 0 3 3m-9 6 .57-2.28c.16-.64.24-.96.369-1.259.114-.265.257-.517.425-.75.19-.265.423-.498.89-.964L17.5 3.5c.08-.08.12-.12.155-.152a2 2 0 0 1 2.69 0c.036.033.075.072.155.152v0c.08.08.12.12.151.155a2 2 0 0 1 0 2.69 4.899 4.899 0 0 1-.151.155l-4.247 4.246c-.466.467-.7.7-.963.89a4 4 0 0 1-.751.425c-.299.129-.619.209-1.259.369L11 13Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEdit_default = SvgIconEdit;

// src/icons/line/IconElements2.tsx
import { jsx as _jsx299 } from "react/jsx-runtime";
var SvgIconElements2 = (props) => /* @__PURE__ */ _jsx299("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx299("path", {
    d: "M10 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM22 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM22 18a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM10 18a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconElements2_default = SvgIconElements2;

// src/icons/line/IconElements3.tsx
import { jsx as _jsx300 } from "react/jsx-runtime";
var SvgIconElements3 = (props) => /* @__PURE__ */ _jsx300("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx300("path", {
    d: "M6 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM6 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM6 20a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM14 20a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM22 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM22 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM22 20a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconElements3_default = SvgIconElements3;

// src/icons/line/IconElements4.tsx
import { jsx as _jsx301 } from "react/jsx-runtime";
var SvgIconElements4 = (props) => /* @__PURE__ */ _jsx301("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx301("path", {
    d: "M2 5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 2 4.08 2 5.2 2h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C10 3.52 10 4.08 10 5.2v13.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C8.48 22 7.92 22 6.8 22H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 20.48 2 19.92 2 18.8V5.2ZM14 5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C15.52 2 16.08 2 17.2 2h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 3.52 22 4.08 22 5.2v1.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 10 19.92 10 18.8 10h-1.6c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C14 8.48 14 7.92 14 6.8V5.2ZM14 17.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C15.52 14 16.08 14 17.2 14h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 15.52 22 16.08 22 17.2v1.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 22 19.92 22 18.8 22h-1.6c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C14 20.48 14 19.92 14 18.8v-1.6Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconElements4_default = SvgIconElements4;

// src/icons/line/IconElements5.tsx
import { jsx as _jsx302 } from "react/jsx-runtime";
var SvgIconElements5 = (props) => /* @__PURE__ */ _jsx302("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx302("path", {
    d: "M2 5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 2 4.08 2 5.2 2h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C10 3.52 10 4.08 10 5.2v3.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C8.48 12 7.92 12 6.8 12H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 10.48 2 9.92 2 8.8V5.2ZM2 19c0-.932 0-1.398.152-1.765a2 2 0 0 1 1.083-1.083C3.602 16 4.068 16 5 16h2c.932 0 1.398 0 1.765.152a2 2 0 0 1 1.083 1.083C10 17.602 10 18.068 10 19v0c0 .932 0 1.398-.152 1.765a2 2 0 0 1-1.083 1.083C8.398 22 7.932 22 7 22H5c-.932 0-1.398 0-1.765-.152a2 2 0 0 1-1.083-1.083C2 20.398 2 19.932 2 19v0ZM14 5c0-.932 0-1.398.152-1.765a2 2 0 0 1 1.083-1.083C15.602 2 16.068 2 17 2h2c.932 0 1.398 0 1.765.152a2 2 0 0 1 1.083 1.083C22 3.602 22 4.068 22 5v0c0 .932 0 1.398-.152 1.765a2 2 0 0 1-1.083 1.083C20.398 8 19.932 8 19 8h-2c-.932 0-1.398 0-1.765-.152a2 2 0 0 1-1.083-1.083C14 6.398 14 5.932 14 5v0ZM14 15.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C15.52 12 16.08 12 17.2 12h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 13.52 22 14.08 22 15.2v3.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 22 19.92 22 18.8 22h-1.6c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C14 20.48 14 19.92 14 18.8v-3.6Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconElements5_default = SvgIconElements5;

// src/icons/line/IconElementsPlus.tsx
import { jsx as _jsx303 } from "react/jsx-runtime";
var SvgIconElementsPlus = (props) => /* @__PURE__ */ _jsx303("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx303("path", {
    d: "M14 6h4m0 0h4m-4 0v4m0-4V2m-.8 20h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C22 20.48 22 19.92 22 18.8v-1.6c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C20.48 14 19.92 14 18.8 14h-1.6c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C14 15.52 14 16.08 14 17.2v1.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 22 16.08 22 17.2 22Zm-12 0h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C10 20.48 10 19.92 10 18.8v-1.6c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C8.48 14 7.92 14 6.8 14H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 15.52 2 16.08 2 17.2v1.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C3.52 22 4.08 22 5.2 22Zm0-12h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C10 8.48 10 7.92 10 6.8V5.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C8.48 2 7.92 2 6.8 2H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 3.52 2 4.08 2 5.2v1.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C3.52 10 4.08 10 5.2 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconElementsPlus_default = SvgIconElementsPlus;

// src/icons/line/IconElements.tsx
import { jsx as _jsx304 } from "react/jsx-runtime";
var SvgIconElements = (props) => /* @__PURE__ */ _jsx304("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx304("path", {
    d: "M2 5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 2 4.08 2 5.2 2h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C10 3.52 10 4.08 10 5.2v1.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C8.48 10 7.92 10 6.8 10H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 8.48 2 7.92 2 6.8V5.2ZM14 5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C15.52 2 16.08 2 17.2 2h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 3.52 22 4.08 22 5.2v1.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 10 19.92 10 18.8 10h-1.6c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C14 8.48 14 7.92 14 6.8V5.2ZM2 17.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 14 4.08 14 5.2 14h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C10 15.52 10 16.08 10 17.2v1.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C8.48 22 7.92 22 6.8 22H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 20.48 2 19.92 2 18.8v-1.6ZM14 17.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C15.52 14 16.08 14 17.2 14h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 15.52 22 16.08 22 17.2v1.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 22 19.92 22 18.8 22h-1.6c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C14 20.48 14 19.92 14 18.8v-1.6Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconElements_default = SvgIconElements;

// src/icons/line/IconEmail.tsx
import { jsx as _jsx305 } from "react/jsx-runtime";
var SvgIconEmail = (props) => /* @__PURE__ */ _jsx305("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx305("path", {
    d: "m18 8-1 .667-.562.375c-1.604 1.069-2.406 1.603-3.273 1.811a5.001 5.001 0 0 1-2.33 0c-.867-.208-1.669-.742-3.273-1.811L7 8.667 6 8m4 13h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 17.2 22 15.8 22 13v-2c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 3 16.8 3 14 3h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 5.73C2 6.8 2 8.2 2 11v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 21 7.2 21 10 21Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconEmail_default = SvgIconEmail;

// src/icons/line/IconEmails.tsx
import { jsx as _jsx306 } from "react/jsx-runtime";
var SvgIconEmails = (props) => /* @__PURE__ */ _jsx306("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx306("path", {
    d: "M1 7v4.4c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C5.56 21 7.24 21 10.6 21H16a3 3 0 0 0 3-3v0M18 6l-.833.667-.169.135c-1.427 1.141-2.14 1.712-2.932 1.93a4 4 0 0 1-2.133 0c-.79-.218-1.504-.789-2.931-1.93l-.169-.135L8 6m2.4 12h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 14.96 22 13.84 22 11.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 2 17.84 2 15.6 2h-5.2c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C4 5.04 4 6.16 4 8.4v3.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C7.04 18 8.16 18 10.4 18Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconEmails_default = SvgIconEmails;

// src/icons/line/IconEmotionHappy.tsx
import { jsx as _jsx307 } from "react/jsx-runtime";
var SvgIconEmotionHappy = (props) => /* @__PURE__ */ _jsx307("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx307("path", {
    d: "M9.5 9a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m7 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10Zm5-9s-1.25 4-5 4-5-4-5-4h10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEmotionHappy_default = SvgIconEmotionHappy;

// src/icons/line/IconEmotionNeutral.tsx
import { jsx as _jsx308 } from "react/jsx-runtime";
var SvgIconEmotionNeutral = (props) => /* @__PURE__ */ _jsx308("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx308("path", {
    d: "M9.5 10a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m7 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m.5 5H9m3 7v0c5.523 0 10-4.477 10-10v0c0-5.523-4.477-10-10-10v0C6.477 2 2 6.477 2 12v0c0 5.523 4.477 10 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEmotionNeutral_default = SvgIconEmotionNeutral;

// src/icons/line/IconEmotionSad.tsx
import { jsx as _jsx309 } from "react/jsx-runtime";
var SvgIconEmotionSad = (props) => /* @__PURE__ */ _jsx309("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx309("path", {
    d: "M9.5 10a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m7 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1.5 6a4.992 4.992 0 0 0-4-2 4.993 4.993 0 0 0-4 2m4 6v0c5.523 0 10-4.477 10-10v0c0-5.523-4.477-10-10-10v0C6.477 2 2 6.477 2 12v0c0 5.523 4.477 10 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEmotionSad_default = SvgIconEmotionSad;

// src/icons/line/IconEmotionSmile.tsx
import { jsx as _jsx310 } from "react/jsx-runtime";
var SvgIconEmotionSmile = (props) => /* @__PURE__ */ _jsx310("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx310("path", {
    d: "M9.5 10a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m7 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1.5 5a4.992 4.992 0 0 1-4 2 4.993 4.993 0 0 1-4-2m4 7v0c5.523 0 10-4.477 10-10v0c0-5.523-4.477-10-10-10v0C6.477 2 2 6.477 2 12v0c0 5.523 4.477 10 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEmotionSmile_default = SvgIconEmotionSmile;

// src/icons/line/IconEmotionWow.tsx
import { jsx as _jsx311 } from "react/jsx-runtime";
var SvgIconEmotionWow = (props) => /* @__PURE__ */ _jsx311("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx311("path", {
    d: "M9.5 9a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m7 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M12 18a2 2 0 0 1-2-2v-2a2 2 0 1 1 4 0v2a2 2 0 0 1-2 2Zm0 4v0c5.523 0 10-4.477 10-10v0c0-5.523-4.477-10-10-10v0C6.477 2 2 6.477 2 12v0c0 5.523 4.477 10 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEmotionWow_default = SvgIconEmotionWow;

// src/icons/line/IconEqualizer.tsx
import { jsx as _jsx312 } from "react/jsx-runtime";
var SvgIconEqualizer = (props) => /* @__PURE__ */ _jsx312("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx312("path", {
    d: "M7 8v8m5-5v5m5-7v7m-7 6h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEqualizer_default = SvgIconEqualizer;

// src/icons/line/IconEuroCircle.tsx
import { jsx as _jsx313 } from "react/jsx-runtime";
var SvgIconEuroCircle = (props) => /* @__PURE__ */ _jsx313("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx313("path", {
    d: "M15 9.354a4 4 0 1 0 0 5.292M7 11h5m-5 2h4m11-1v0c0-5.523-4.477-10-10-10v0C6.477 2 2 6.477 2 12v0c0 5.523 4.477 10 10 10v0c5.523 0 10-4.477 10-10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEuroCircle_default = SvgIconEuroCircle;

// src/icons/line/IconEuro.tsx
import { jsx as _jsx314 } from "react/jsx-runtime";
var SvgIconEuro = (props) => /* @__PURE__ */ _jsx314("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx314("path", {
    d: "M20 6.047A9.183 9.183 0 0 0 13.143 3C8.093 3 4 7.03 4 12s4.093 9 9.143 9A9.183 9.183 0 0 0 20 17.953M2 10h10M2 14h8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconEuro_default = SvgIconEuro;

// src/icons/line/IconExport2.tsx
import { jsx as _jsx315 } from "react/jsx-runtime";
var SvgIconExport2 = (props) => /* @__PURE__ */ _jsx315("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx315("path", {
    d: "M8 10h-.5v0c-1.396 0-2.093 0-2.661.172a4 4 0 0 0-2.667 2.667C2 13.407 2 14.104 2 15.5v.5c0 1.864 0 2.796.304 3.53a4 4 0 0 0 2.165 2.165C5.204 22 6.136 22 8 22h8c1.864 0 2.796 0 3.53-.305a4 4 0 0 0 2.165-2.164C22 18.796 22 17.864 22 16v-.5c0-1.396 0-2.093-.172-2.661a4 4 0 0 0-2.667-2.667C18.593 10 17.896 10 16.5 10v0H16m-4 5V2m0 0L8 6m4-4 4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconExport2_default = SvgIconExport2;

// src/icons/line/IconExport3.tsx
import { jsx as _jsx316 } from "react/jsx-runtime";
var SvgIconExport3 = (props) => /* @__PURE__ */ _jsx316("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx316("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2m1 9 9-9m0 0h-5m5 0v5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconExport3_default = SvgIconExport3;

// src/icons/line/IconExport4.tsx
import { jsx as _jsx317 } from "react/jsx-runtime";
var SvgIconExport4 = (props) => /* @__PURE__ */ _jsx317("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx317("path", {
    d: "M12 16V2m0 0L8 6m4-4 4 4M8 22h8a6 6 0 0 0 6-6v0a6 6 0 0 0-6-6H8a6 6 0 0 0-6 6v0a6 6 0 0 0 6 6Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconExport4_default = SvgIconExport4;

// src/icons/line/IconExport.tsx
import { jsx as _jsx318 } from "react/jsx-runtime";
var SvgIconExport = (props) => /* @__PURE__ */ _jsx318("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx318("path", {
    d: "M2 14v1.6c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6V14m-10 0V2m0 0 4 4m-4-4L8 6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconExport_default = SvgIconExport;

// src/icons/line/IconExternalLink2.tsx
import { jsx as _jsx319 } from "react/jsx-runtime";
var SvgIconExternalLink2 = (props) => /* @__PURE__ */ _jsx319("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx319("path", {
    d: "M13 4h-3c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 6.73C2 7.8 2 9.2 2 12v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22h2c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C20 18.2 20 16.8 20 14v-3m-8 1L22 2m0 0h-5m5 0v5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconExternalLink2_default = SvgIconExternalLink2;

// src/icons/line/IconExternalLinkRec.tsx
import { jsx as _jsx320 } from "react/jsx-runtime";
var SvgIconExternalLinkRec = (props) => /* @__PURE__ */ _jsx320("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx320("path", {
    d: "m8 16 8-8m0 0h-6m6 0v6m-6 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconExternalLinkRec_default = SvgIconExternalLinkRec;

// src/icons/line/IconExternalLink.tsx
import { jsx as _jsx321 } from "react/jsx-runtime";
var SvgIconExternalLink = (props) => /* @__PURE__ */ _jsx321("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx321("path", {
    d: "M10 3h-.5v0c-1.395 0-2.092 0-2.667.138a5 5 0 0 0-3.695 3.695C3 7.408 3 8.105 3 9.5V13c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C6.8 21 8.2 21 11 21h3.5c1.395 0 2.092 0 2.667-.138a5 5 0 0 0 3.695-3.695C21 16.592 21 15.894 21 14.5v0-.5m-9-2 9-9m0 0h-6m6 0v6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconExternalLink_default = SvgIconExternalLink;

// src/icons/line/IconEyeSlash.tsx
import { jsx as _jsx322 } from "react/jsx-runtime";
var SvgIconEyeSlash = (props) => /* @__PURE__ */ _jsx322("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx322("path", {
    d: "m3 3 2.826 2.826M21 21l-2.826-2.826m0 0C16.63 19.248 14.588 20 12 20c-7 0-10-5.5-10-8 0-1.576 1.192-4.343 3.826-6.174m12.348 12.348-4.053-4.053M5.826 5.826l4.053 4.053m0 0a3 3 0 1 0 4.243 4.243M9.878 9.878l4.242 4.242M20.42 16c.123-.166.239-.333.347-.5C21.605 14.21 22 12.907 22 12c0-2.5-3-8-10-8-1.097 0-2.096.135-3 .372-.17.045-.337.093-.5.145",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconEyeSlash_default = SvgIconEyeSlash;

// src/icons/line/IconEye.tsx
import { jsx as _jsx323, jsxs as _jsxs10 } from "react/jsx-runtime";
var SvgIconEye = (props) => /* @__PURE__ */ _jsxs10("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx323("path", {
    d: "M22 12c0 2.5-3 8-10 8S2 14.5 2 12s3-8 10-8 10 5.5 10 8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  }), /* @__PURE__ */ _jsx323("path", {
    d: "M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })]
});
var IconEye_default = SvgIconEye;

// src/icons/line/IconFaceIdRec.tsx
import { jsx as _jsx324 } from "react/jsx-runtime";
var SvgIconFaceIdRec = (props) => /* @__PURE__ */ _jsx324("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx324("path", {
    d: "M6 10v-.8c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C7.52 6 8.08 6 9.2 6h.8m4 0h.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C18 7.52 18 8.08 18 9.2v.8m0 4.1v.7c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C16.48 18 15.92 18 14.8 18h-.7M10 18h-.8c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C6 16.48 6 15.92 6 14.8v-.7m6-4.6V12a.6.6 0 0 1-.6.6M9.5 9.5v1m5-1v1m0 4c-.5.5-1.5 1-2.5 1s-2-.5-2.5-1M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFaceIdRec_default = SvgIconFaceIdRec;

// src/icons/line/IconFaceId.tsx
import { jsx as _jsx325 } from "react/jsx-runtime";
var SvgIconFaceId = (props) => /* @__PURE__ */ _jsx325("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx325("path", {
    d: "M22 8v-.5 0A5.5 5.5 0 0 0 16.5 2v0H16m6 14v.5a5.5 5.5 0 0 1-5.5 5.5v0H16M2 16v.5A5.5 5.5 0 0 0 7.5 22v0H8M2 8v-.5 0A5.5 5.5 0 0 1 7.5 2v0H8m4 6v4a1 1 0 0 1-1 1M8 8v1.5M16 8v1.5m0 5.5a4.992 4.992 0 0 1-4 2 4.993 4.993 0 0 1-4-2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFaceId_default = SvgIconFaceId;

// src/icons/line/IconFacebook.tsx
import { jsx as _jsx326 } from "react/jsx-runtime";
var SvgIconFacebook = (props) => /* @__PURE__ */ _jsx326("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx326("path", {
    d: "M13 22h1c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22h3Zm0 0V11.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C14.52 8 15.08 8 16.2 8h.8m-7 6h7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFacebook_default = SvgIconFacebook;

// src/icons/line/IconFastForward.tsx
import { jsx as _jsx327 } from "react/jsx-runtime";
var SvgIconFastForward = (props) => /* @__PURE__ */ _jsx327("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx327("path", {
    d: "M10 8.143 6.93 6.17C5.324 5.137 4.522 4.62 3.858 4.668a2 2 0 0 0-1.459.797C2 5.998 2 6.952 2 8.86v6.278c0 1.909 0 2.863.399 3.396a2 2 0 0 0 1.459.797c.664.047 1.466-.47 3.072-1.502L10 15.857m9.813-1.165L14.93 17.83c-1.605 1.033-2.409 1.549-3.072 1.502a2 2 0 0 1-1.459-.797C10 18.002 10 17.048 10 15.14V8.86c0-1.909 0-2.863.399-3.396a2 2 0 0 1 1.459-.797c.663-.047 1.466.47 3.072 1.502l4.883 3.138c1.393.896 2.09 1.344 2.33 1.914a2 2 0 0 1 0 1.556c-.24.57-.937 1.018-2.33 1.914Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFastForward_default = SvgIconFastForward;

// src/icons/line/IconFastRewind.tsx
import { jsx as _jsx328 } from "react/jsx-runtime";
var SvgIconFastRewind = (props) => /* @__PURE__ */ _jsx328("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx328("path", {
    d: "m14 8.143 3.07-1.973c1.605-1.033 2.409-1.549 3.072-1.502a2 2 0 0 1 1.459.797C22 5.998 22 6.952 22 8.86v6.278c0 1.909 0 2.863-.399 3.396a2 2 0 0 1-1.459.797c-.663.047-1.466-.47-3.072-1.502L14 15.857m-9.813-1.165L9.07 17.83c1.605 1.033 2.409 1.549 3.072 1.502a2 2 0 0 0 1.459-.797C14 18.002 14 17.048 14 15.14V8.86c0-1.909 0-2.863-.399-3.396a2 2 0 0 0-1.459-.797c-.663-.047-1.466.47-3.072 1.502L4.187 9.308c-1.393.896-2.09 1.344-2.33 1.914a2 2 0 0 0 0 1.556c.24.57.937 1.018 2.33 1.914Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFastRewind_default = SvgIconFastRewind;

// src/icons/line/IconFatRows.tsx
import { jsx as _jsx329 } from "react/jsx-runtime";
var SvgIconFatRows = (props) => /* @__PURE__ */ _jsx329("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx329("path", {
    d: "M2 5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 2 4.08 2 5.2 2h13.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 3.52 22 4.08 22 5.2v1.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 10 19.92 10 18.8 10H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 8.48 2 7.92 2 6.8V5.2ZM2 17.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 14 4.08 14 5.2 14h5.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C14 15.52 14 16.08 14 17.2v1.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C12.48 22 11.92 22 10.8 22H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 20.48 2 19.92 2 18.8v-1.6Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFatRows_default = SvgIconFatRows;

// src/icons/line/IconFigma.tsx
import { jsx as _jsx330 } from "react/jsx-runtime";
var SvgIconFigma = (props) => /* @__PURE__ */ _jsx330("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx330("path", {
    d: "M9 9h3M9 9a3 3 0 1 0 0 6m0-6a3 3 0 0 1 0-6h3m0 6v6m0-6h3m-3 0V3M9 15h3m-3 0a3 3 0 1 0 3 3v-3m0-12h3a3 3 0 1 1 0 6m0 0a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFigma_default = SvgIconFigma;

// src/icons/line/IconFileAgreement.tsx
import { jsx as _jsx331 } from "react/jsx-runtime";
var SvgIconFileAgreement = (props) => /* @__PURE__ */ _jsx331("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx331("path", {
    d: "M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3M9 17h1l1.5-3 1.5 3h2m5-6.686V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileAgreement_default = SvgIconFileAgreement;

// src/icons/line/IconFileCheck.tsx
import { jsx as _jsx332 } from "react/jsx-runtime";
var SvgIconFileCheck = (props) => /* @__PURE__ */ _jsx332("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx332("path", {
    d: "M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3m-10 6.5L11 16l4-4m5-1.686V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileCheck_default = SvgIconFileCheck;

// src/icons/line/IconFileCode.tsx
import { jsx as _jsx333 } from "react/jsx-runtime";
var SvgIconFileCode = (props) => /* @__PURE__ */ _jsx333("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx333("path", {
    d: "M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3M11 11l-2 2 2 2m2-2 2 2-2 2m7-6.686V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileCode_default = SvgIconFileCode;

// src/icons/line/IconFileCross.tsx
import { jsx as _jsx334 } from "react/jsx-runtime";
var SvgIconFileCross = (props) => /* @__PURE__ */ _jsx334("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx334("path", {
    d: "m10 16 2-2m0 0 2-2m-2 2-2-2m2 2 2 2m0-13.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3m.5 2.314V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileCross_default = SvgIconFileCross;

// src/icons/line/IconFileDownload.tsx
import { jsx as _jsx335 } from "react/jsx-runtime";
var SvgIconFileDownload = (props) => /* @__PURE__ */ _jsx335("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx335("path", {
    d: "M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3M12 11v6m0 0 2.5-2.5M12 17l-2.5-2.5M20 10.314V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileDownload_default = SvgIconFileDownload;

// src/icons/line/IconFileDraft.tsx
import { jsx as _jsx336 } from "react/jsx-runtime";
var SvgIconFileDraft = (props) => /* @__PURE__ */ _jsx336("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx336("path", {
    d: "M4 11V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41V11m0 4v1.5c0 .464 0 .697-.015.892a5 5 0 0 1-4.593 4.593c-.195.015-.428.015-.892.015v0H14M4 15v1.5c0 .464 0 .697.015.892a5 5 0 0 0 4.593 4.593c.196.015.428.015.892.015v0h.5m4-19.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileDraft_default = SvgIconFileDraft;

// src/icons/line/IconFileHeart.tsx
import { jsx as _jsx337 } from "react/jsx-runtime";
var SvgIconFileHeart = (props) => /* @__PURE__ */ _jsx337("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx337("path", {
    d: "M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3m-7.716 8.81-2.587-2.263a1.662 1.662 0 0 1 2.092-2.58v0c.421.316 1 .316 1.422 0v0a1.662 1.662 0 0 1 2.092 2.58l-2.587 2.264a.329.329 0 0 1-.432 0ZM20 10.315V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileHeart_default = SvgIconFileHeart;

// src/icons/line/IconFileInvoice.tsx
import { jsx as _jsx338 } from "react/jsx-runtime";
var SvgIconFileInvoice = (props) => /* @__PURE__ */ _jsx338("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx338("path", {
    d: "M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3M14 11h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3H10m2-6v-1m0 7v1m8-7.686V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileInvoice_default = SvgIconFileInvoice;

// src/icons/line/IconFileMinus.tsx
import { jsx as _jsx339 } from "react/jsx-runtime";
var SvgIconFileMinus = (props) => /* @__PURE__ */ _jsx339("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx339("path", {
    d: "M9.5 14h5M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3m.5 2.314V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileMinus_default = SvgIconFileMinus;

// src/icons/line/IconFileMove.tsx
import { jsx as _jsx340 } from "react/jsx-runtime";
var SvgIconFileMove = (props) => /* @__PURE__ */ _jsx340("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx340("path", {
    d: "M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3M9 14h6m0 0-2.5 2.5M15 14l-2.5-2.5m7.5-1.186V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileMove_default = SvgIconFileMove;

// src/icons/line/IconFilePlus.tsx
import { jsx as _jsx341 } from "react/jsx-runtime";
var SvgIconFilePlus = (props) => /* @__PURE__ */ _jsx341("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx341("path", {
    d: "M9.5 14H12m0 0h2.5M12 14v-2.5m0 2.5v2.5m2-14v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3m.5 2.314V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFilePlus_default = SvgIconFilePlus;

// src/icons/line/IconFileScan.tsx
import { jsx as _jsx342 } from "react/jsx-runtime";
var SvgIconFileScan = (props) => /* @__PURE__ */ _jsx342("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx342("path", {
    d: "M4 12V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41V12m0 4v.5c0 .464 0 .697-.015.892a5 5 0 0 1-4.593 4.593c-.195.015-.428.015-.892.015h-5c-.464 0-.696 0-.892-.015a5 5 0 0 1-4.593-4.593C4 17.197 4 16.964 4 16.5v0-.5M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3M2 12h20",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileScan_default = SvgIconFileScan;

// src/icons/line/IconFileShredder.tsx
import { jsx as _jsx343 } from "react/jsx-runtime";
var SvgIconFileShredder = (props) => /* @__PURE__ */ _jsx343("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx343("path", {
    d: "M4 12V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41V12M2 12h20M6 19v-3m4 6v-6m4 6v-6m4 3v-3M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileShredder_default = SvgIconFileShredder;

// src/icons/line/IconFileSlash.tsx
import { jsx as _jsx344 } from "react/jsx-runtime";
var SvgIconFileSlash = (props) => /* @__PURE__ */ _jsx344("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx344("path", {
    d: "M6.887 2.469C7.89 2 9.187 2 11.778 2c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.222.222c1.02 1.02 1.53 1.53 1.87 2.14a5 5 0 0 1 .438 1.06C20 8.372 20 9.093 20 10.536V16M2 2l2.609 2.609M22 22l-2.609-2.609m0 0a5 5 0 0 1-2.121 2.064C16.2 22 14.8 22 12 22c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891.044-.095.09-.187.14-.278M19.39 19.39 4.61 4.61M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileSlash_default = SvgIconFileSlash;

// src/icons/line/IconFileText.tsx
import { jsx as _jsx345 } from "react/jsx-runtime";
var SvgIconFileText = (props) => /* @__PURE__ */ _jsx345("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx345("path", {
    d: "M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3M9 12h6m-6 4h4m7-5.686V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileText_default = SvgIconFileText;

// src/icons/line/IconFileUpload.tsx
import { jsx as _jsx346 } from "react/jsx-runtime";
var SvgIconFileUpload = (props) => /* @__PURE__ */ _jsx346("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx346("path", {
    d: "M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3M12 17v-6m0 0 2.5 2.5M12 11l-2.5 2.5M20 10.314V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFileUpload_default = SvgIconFileUpload;

// src/icons/line/IconFile.tsx
import { jsx as _jsx347 } from "react/jsx-runtime";
var SvgIconFile = (props) => /* @__PURE__ */ _jsx347("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx347("path", {
    d: "M14 2.5v2.3c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 8 16.08 8 17.2 8h2.3m.5 2.314V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14V9.778c0-2.591 0-3.887.469-4.891a5 5 0 0 1 2.418-2.418C7.89 2 9.187 2 11.778 2v0c1.133 0 1.699 0 2.233.119a5 5 0 0 1 1.604.664c.462.294.862.695 1.663 1.495l.379.379c.865.865 1.297 1.297 1.606 1.802.274.447.476.935.599 1.445.138.575.138 1.187.138 2.41Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFile_default = SvgIconFile;

// src/icons/line/IconFilmBoard.tsx
import { jsx as _jsx348 } from "react/jsx-runtime";
var SvgIconFilmBoard = (props) => /* @__PURE__ */ _jsx348("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx348("path", {
    d: "M2.5 7h19m-5.2-4.5L19 7m-8.5-5 3 5m-8-4.5L8 7m-.5 10a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m3.5 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFilmBoard_default = SvgIconFilmBoard;

// src/icons/line/IconFilmReel.tsx
import { jsx as _jsx349 } from "react/jsx-runtime";
var SvgIconFilmReel = (props) => /* @__PURE__ */ _jsx349("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx349("path", {
    d: "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10Zm0 0h10M6.5 12a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m13 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-5-6a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 12a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m2.5-6a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFilmReel_default = SvgIconFilmReel;

// src/icons/line/IconFilmTape.tsx
import { jsx as _jsx350 } from "react/jsx-runtime";
var SvgIconFilmTape = (props) => /* @__PURE__ */ _jsx350("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx350("path", {
    d: "M8 21.5v-19m8 0v19M2.5 12h19m-19-5H8m8 0h5.5M16 17h5.5m-19 0H8m2 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFilmTape_default = SvgIconFilmTape;

// src/icons/line/IconFilter2.tsx
import { jsx as _jsx351 } from "react/jsx-runtime";
var SvgIconFilter2 = (props) => /* @__PURE__ */ _jsx351("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx351("path", {
    d: "M19.365 2H4.635C3.732 2 3 2.732 3 3.635a6.54 6.54 0 0 0 2.738 5.321l1.929 1.377h0c.642.459.963.688 1.222.959a4 4 0 0 1 1.041 2.024c.07.368.07.762.07 1.551V20a2 2 0 1 0 4 0v-5.133c0-.789 0-1.183.07-1.551a3.999 3.999 0 0 1 1.041-2.024c.26-.27.58-.5 1.222-.959l1.929-1.377A6.54 6.54 0 0 0 21 3.635C21 2.732 20.268 2 19.365 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFilter2_default = SvgIconFilter2;

// src/icons/line/IconFilterRec.tsx
import { jsx as _jsx352 } from "react/jsx-runtime";
var SvgIconFilterRec = (props) => /* @__PURE__ */ _jsx352("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx352("path", {
    d: "M7 8h10m-8 4h6m-4 4h2m-1.4 6h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFilterRec_default = SvgIconFilterRec;

// src/icons/line/IconFilter.tsx
import { jsx as _jsx353 } from "react/jsx-runtime";
var SvgIconFilter = (props) => /* @__PURE__ */ _jsx353("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx353("path", {
    d: "M3 6h18M6 12h12m-8 6h4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconFilter_default = SvgIconFilter;

// src/icons/line/IconFingerprint2Rec.tsx
import { jsx as _jsx354 } from "react/jsx-runtime";
var SvgIconFingerprint2Rec = (props) => /* @__PURE__ */ _jsx354("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx354("path", {
    d: "M19 12.07C19 8.167 15.866 5 12 5c-.995 0-1.943.21-2.8.588m-2.8 2.24A7.086 7.086 0 0 0 5 12.071c0 3.42 2.405 6.274 5.6 6.929m7.464-3.394A7.014 7.014 0 0 1 13.4 19M12 8a4 4 0 0 0-3.333 6.212m6-5.193a4 4 0 0 1-4 6.754M12 11v2m-2 9h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFingerprint2Rec_default = SvgIconFingerprint2Rec;

// src/icons/line/IconFingerprint2.tsx
import { jsx as _jsx355 } from "react/jsx-runtime";
var SvgIconFingerprint2 = (props) => /* @__PURE__ */ _jsx355("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx355("path", {
    d: "M22 12c0-5.523-4.477-10-10-10a9.966 9.966 0 0 0-4 .832M4 6a9.956 9.956 0 0 0-2 6c0 4.838 3.436 8.873 8 9.8M20.662 17A10.013 10.013 0 0 1 14 21.8M12 6a6 6 0 0 0-5 9.318m9-7.79a6 6 0 0 1-6 10.13M12 10v4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFingerprint2_default = SvgIconFingerprint2;

// src/icons/line/IconFingerprintRec.tsx
import { jsx as _jsx356 } from "react/jsx-runtime";
var SvgIconFingerprintRec = (props) => /* @__PURE__ */ _jsx356("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx356("path", {
    d: "M18 10.714C18 7.558 15.314 5 12 5c-.796 0-1.555.147-2.25.415M13.875 19C16.27 18.25 18 16.103 18 13.57M10.125 19C7.73 18.25 6 16.103 6 13.57v-2.856c0-1.351.493-2.593 1.316-3.571M12 11v2m-3 .333v-2.666C9 9.194 10.343 8 12 8m2.599 1.333c.255.393.401.848.401 1.334v2.666C15 14.806 13.657 16 12 16c-.546 0-1.059-.13-1.5-.357M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFingerprintRec_default = SvgIconFingerprintRec;

// src/icons/line/IconFingerprint.tsx
import { jsx as _jsx357 } from "react/jsx-runtime";
var SvgIconFingerprint = (props) => /* @__PURE__ */ _jsx357("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx357("path", {
    d: "M20 10A8 8 0 0 0 9 2.582m5.5 19.02A8.004 8.004 0 0 0 20 14M9.5 21.602A8.003 8.003 0 0 1 4 14v-4c0-1.892.657-3.63 1.755-5M12 10v4m-4 0v-4a4 4 0 0 1 4-4m3.465 2c.34.588.535 1.271.535 2v4a4 4 0 0 1-6 3.465",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFingerprint_default = SvgIconFingerprint;

// src/icons/line/IconFire.tsx
import { jsx as _jsx358 } from "react/jsx-runtime";
var SvgIconFire = (props) => /* @__PURE__ */ _jsx358("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx358("path", {
    d: "M4 14c0 4.546 3.5 8 8 8s8-3.454 8-8c0-8.182-8-12-8-12 0 4 1 10-2 10-2 0-2.5-2-2.5-5.5C7.5 6.5 4 9.455 4 14Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFire_default = SvgIconFire;

// src/icons/line/IconFirstLine1Rec.tsx
import { jsx as _jsx359 } from "react/jsx-runtime";
var SvgIconFirstLine1Rec = (props) => /* @__PURE__ */ _jsx359("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx359("path", {
    d: "M6 18h12m-4-8h4m-4-4h4M6 14h12M7.6 10h.8c.56 0 .84 0 1.054-.109a1 1 0 0 0 .437-.437C10 9.24 10 8.96 10 8.4v-.8c0-.56 0-.84-.109-1.054a1 1 0 0 0-.437-.437C9.24 6 8.96 6 8.4 6h-.8c-.56 0-.84 0-1.054.109a1 1 0 0 0-.437.437C6 6.76 6 7.04 6 7.6v.8c0 .56 0 .84.109 1.054a1 1 0 0 0 .437.437C6.76 10 7.04 10 7.6 10ZM10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFirstLine1Rec_default = SvgIconFirstLine1Rec;

// src/icons/line/IconFirstLine1.tsx
import { jsx as _jsx360 } from "react/jsx-runtime";
var SvgIconFirstLine1 = (props) => /* @__PURE__ */ _jsx360("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx360("path", {
    d: "M3 21h18M13 3h8m-8 6h8M3 15h18M5.4 9h1.2c.84 0 1.26 0 1.581-.163a1.5 1.5 0 0 0 .656-.656C9 7.861 9 7.441 9 6.601V5.4c0-.841 0-1.261-.163-1.582a1.5 1.5 0 0 0-.656-.655C7.861 3 7.441 3 6.601 3H5.4c-.84 0-1.26 0-1.582.164a1.5 1.5 0 0 0-.655.655C3 4.14 3 4.56 3 5.4v1.2c0 .84 0 1.26.164 1.581a1.5 1.5 0 0 0 .655.656C4.14 9 4.56 9 5.4 9Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFirstLine1_default = SvgIconFirstLine1;

// src/icons/line/IconFirstLine2Rec.tsx
import { jsx as _jsx361 } from "react/jsx-runtime";
var SvgIconFirstLine2Rec = (props) => /* @__PURE__ */ _jsx361("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx361("path", {
    d: "M6 18h12m-4-8h4m-4-4h4M6 14h12m-8-6a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 14h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFirstLine2Rec_default = SvgIconFirstLine2Rec;

// src/icons/line/IconFirstLine2.tsx
import { jsx as _jsx362 } from "react/jsx-runtime";
var SvgIconFirstLine2 = (props) => /* @__PURE__ */ _jsx362("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx362("path", {
    d: "M3 21h18M13 3h8m-8 6h8M3 15h18M9 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFirstLine2_default = SvgIconFirstLine2;

// src/icons/line/IconFirstLine3Rec.tsx
import { jsx as _jsx363 } from "react/jsx-runtime";
var SvgIconFirstLine3Rec = (props) => /* @__PURE__ */ _jsx363("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx363("path", {
    d: "M6 18h12m-4-8h4m-4-4h4M6 14h12M8 10V6m0 0H6m2 0h2m0 16h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFirstLine3Rec_default = SvgIconFirstLine3Rec;

// src/icons/line/IconFirstLine3.tsx
import { jsx as _jsx364 } from "react/jsx-runtime";
var SvgIconFirstLine3 = (props) => /* @__PURE__ */ _jsx364("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx364("path", {
    d: "M6 9V3m0 0h3M6 3H3m0 18h18M13 3h8m-8 6h8M3 15h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFirstLine3_default = SvgIconFirstLine3;

// src/icons/line/IconFlag2.tsx
import { jsx as _jsx365 } from "react/jsx-runtime";
var SvgIconFlag2 = (props) => /* @__PURE__ */ _jsx365("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx365("path", {
    d: "M5 4v11M5 4V2m0 2 1.32-.33a6.67 6.67 0 0 1 5.05.752v0a6.67 6.67 0 0 0 5.54.608L20 4v11l-3.09 1.03a6.67 6.67 0 0 1-5.54-.609v0a6.67 6.67 0 0 0-5.05-.75L5 15m0 7v-7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFlag2_default = SvgIconFlag2;

// src/icons/line/IconFlag3.tsx
import { jsx as _jsx366 } from "react/jsx-runtime";
var SvgIconFlag3 = (props) => /* @__PURE__ */ _jsx366("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx366("path", {
    d: "M5 4v12M5 4V2m0 2c8 0 7 4 15 4-2 8-12 3-15 8m0 6v-6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFlag3_default = SvgIconFlag3;

// src/icons/line/IconFlag.tsx
import { jsx as _jsx367 } from "react/jsx-runtime";
var SvgIconFlag = (props) => /* @__PURE__ */ _jsx367("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx367("path", {
    d: "M5 4h15l-1.569 3.138c-.525 1.05-.787 1.574-.89 2.124a4.003 4.003 0 0 0 0 1.476c.103.55.365 1.075.89 2.124L20 16H5M5 4v12M5 4V2m0 20v-6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFlag_default = SvgIconFlag;

// src/icons/line/IconFlashcard.tsx
import { jsx as _jsx368 } from "react/jsx-runtime";
var SvgIconFlashcard = (props) => /* @__PURE__ */ _jsx368("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx368("path", {
    d: "m7 14 3 3m9-8 2-2c.545-.545.818-.818.964-1.112a2 2 0 0 0 0-1.776C21.818 3.818 21.545 3.545 21 3v0c-.545-.545-.818-.818-1.112-.964a2 2 0 0 0-1.776 0c-.294.146-.567.419-1.112.964l-2 2m-.475 14.474 2.95-2.948c1.584-1.585 2.376-2.377 2.672-3.29a4 4 0 0 0 0-2.472c-.296-.913-1.088-1.705-2.672-3.29l-.95-.949c-1.584-1.584-2.376-2.376-3.289-2.672a4 4 0 0 0-2.472 0c-.913.296-1.705 1.088-3.29 2.672l-2.949 2.95c-1.584 1.584-2.376 2.376-2.672 3.289a4 4 0 0 0 0 2.472c.296.913 1.088 1.705 2.672 3.29l.95.948c1.584 1.585 2.376 2.377 3.289 2.673a4 4 0 0 0 2.472 0c.913-.296 1.705-1.088 3.29-2.672Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconFlashcard_default = SvgIconFlashcard;

// src/icons/line/IconFolderCheck.tsx
import { jsx as _jsx369 } from "react/jsx-runtime";
var SvgIconFolderCheck = (props) => /* @__PURE__ */ _jsx369("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx369("path", {
    d: "m9 14 2 2 4.5-4.5M6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderCheck_default = SvgIconFolderCheck;

// src/icons/line/IconFolderCloud.tsx
import { jsx as _jsx370, jsxs as _jsxs11 } from "react/jsx-runtime";
var SvgIconFolderCloud = (props) => /* @__PURE__ */ _jsxs11("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx370("path", {
    d: "M2 6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx370("path", {
    d: "M7 14a3 3 0 0 0 3 3h4a2 2 0 1 0 0-4v0c-.556 0-1.222-.263-1.54-.718A3 3 0 0 0 7 14Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconFolderCloud_default = SvgIconFolderCloud;

// src/icons/line/IconFolderCompressed.tsx
import { jsx as _jsx371 } from "react/jsx-runtime";
var SvgIconFolderCompressed = (props) => /* @__PURE__ */ _jsx371("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx371("path", {
    d: "M18 6.5V7a2 2 0 1 1-4 0V6m2 7v1m0 3v1M6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderCompressed_default = SvgIconFolderCompressed;

// src/icons/line/IconFolderCross.tsx
import { jsx as _jsx372 } from "react/jsx-runtime";
var SvgIconFolderCross = (props) => /* @__PURE__ */ _jsx372("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx372("path", {
    d: "M9.5 16.5 12 14m0 0 2.5-2.5M12 14l-2.5-2.5M12 14l2.5 2.5M6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderCross_default = SvgIconFolderCross;

// src/icons/line/IconFolderDownload.tsx
import { jsx as _jsx373 } from "react/jsx-runtime";
var SvgIconFolderDownload = (props) => /* @__PURE__ */ _jsx373("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx373("path", {
    d: "M12 11v6m0 0 2.5-2.5M12 17l-2.5-2.5M6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderDownload_default = SvgIconFolderDownload;

// src/icons/line/IconFolderHeart.tsx
import { jsx as _jsx374, jsxs as _jsxs12 } from "react/jsx-runtime";
var SvgIconFolderHeart = (props) => /* @__PURE__ */ _jsxs12("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx374("path", {
    d: "M2 6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx374("path", {
    d: "M8.5 14.5 11 17a1.414 1.414 0 0 0 2 0l2.5-2.5a2.121 2.121 0 0 0-3-3v0a.707.707 0 0 1-1 0v0a2.121 2.121 0 0 0-3 3Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconFolderHeart_default = SvgIconFolderHeart;

// src/icons/line/IconFolderMinus.tsx
import { jsx as _jsx375 } from "react/jsx-runtime";
var SvgIconFolderMinus = (props) => /* @__PURE__ */ _jsx375("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx375("path", {
    d: "M9 14h6M6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderMinus_default = SvgIconFolderMinus;

// src/icons/line/IconFolderMove.tsx
import { jsx as _jsx376 } from "react/jsx-runtime";
var SvgIconFolderMove = (props) => /* @__PURE__ */ _jsx376("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx376("path", {
    d: "M9 14h6m0 0-2.5 2.5M15 14l-2.5-2.5M6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderMove_default = SvgIconFolderMove;

// src/icons/line/IconFolderMusic.tsx
import { jsx as _jsx377 } from "react/jsx-runtime";
var SvgIconFolderMusic = (props) => /* @__PURE__ */ _jsx377("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx377("path", {
    d: "M13 15.5a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 0V10l2 2M6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderMusic_default = SvgIconFolderMusic;

// src/icons/line/IconFolderOpen.tsx
import { jsx as _jsx378 } from "react/jsx-runtime";
var SvgIconFolderOpen = (props) => /* @__PURE__ */ _jsx378("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx378("path", {
    d: "M15 22H6.8c-1.68 0-2.52 0-3.162-.328a3 3 0 0 1-1.311-1.31C2 19.72 2 18.88 2 17.2V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.31-1.312C4.28 2 5.12 2 6.8 2h.964c.369 0 .553 0 .723.019a3 3 0 0 1 2.172 1.342c.093.144.176.309.34.639v0c.166.33.248.494.342.638a3 3 0 0 0 2.171 1.342c.17.02.355.02.724.02h1.763c.93 0 1.396 0 1.777.102a3 3 0 0 1 2.122 2.12C20 8.605 20 9.07 20 10v0M6 22l1.331-7.99c.238-1.424.357-2.136.712-2.671a3 3 0 0 1 1.27-1.076C9.9 10 10.621 10 12.066 10h7.156c1.3 0 1.95 0 2.412.265a2 2 0 0 1 .88 1.039c.185.5.078 1.14-.135 2.422l-.71 4.263c-.238 1.425-.357 2.137-.713 2.672a3.001 3.001 0 0 1-1.27 1.076c-.585.263-1.308.263-2.753.263H6Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderOpen_default = SvgIconFolderOpen;

// src/icons/line/IconFolderPhoto.tsx
import { jsx as _jsx379 } from "react/jsx-runtime";
var SvgIconFolderPhoto = (props) => /* @__PURE__ */ _jsx379("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx379("path", {
    d: "M18 10.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M15 14a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderPhoto_default = SvgIconFolderPhoto;

// src/icons/line/IconFolderPlus.tsx
import { jsx as _jsx380 } from "react/jsx-runtime";
var SvgIconFolderPlus = (props) => /* @__PURE__ */ _jsx380("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx380("path", {
    d: "M9 14h3m0 0h3m-3 0v-3m0 3v3M6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderPlus_default = SvgIconFolderPlus;

// src/icons/line/IconFolderSlash.tsx
import { jsx as _jsx381 } from "react/jsx-runtime";
var SvgIconFolderSlash = (props) => /* @__PURE__ */ _jsx381("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx381("path", {
    d: "M3.172 20.828C2 19.657 2 17.771 2 14V7.562c0-2.405 0-3.608.65-4.427a3 3 0 0 1 .485-.485C3.955 2 5.157 2 7.562 2h.755c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.336c1.168 0 2.083 0 2.824.05m3.43 1.81c.621.98.621 2.325.621 5.015V14c0 3.771 0 5.657-1.172 6.828C19.657 22 17.771 22 14 22H7M22 2l-4.05 4.05M1 23 17.95 6.05",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderSlash_default = SvgIconFolderSlash;

// src/icons/line/IconFolderUpload.tsx
import { jsx as _jsx382 } from "react/jsx-runtime";
var SvgIconFolderUpload = (props) => /* @__PURE__ */ _jsx382("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx382("path", {
    d: "M12 17v-6m0 0 2.5 2.5M12 11l-2.5 2.5M6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolderUpload_default = SvgIconFolderUpload;

// src/icons/line/IconFolder.tsx
import { jsx as _jsx383 } from "react/jsx-runtime";
var SvgIconFolder = (props) => /* @__PURE__ */ _jsx383("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx383("path", {
    d: "M2 6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2h1.517c.801 0 1.202 0 1.564.091a3 3 0 0 1 1.538.95c.243.284.422.642.781 1.359v0c.239.478.359.717.52.906a2 2 0 0 0 1.026.633c.241.061.508.061 1.043.061h.811c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 9.04 22 10.16 22 12.4v3.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V6.8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFolder_default = SvgIconFolder;

// src/icons/line/IconForward10Rec.tsx
import { jsx as _jsx384 } from "react/jsx-runtime";
var SvgIconForward10Rec = (props) => /* @__PURE__ */ _jsx384("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx384("path", {
    d: "M18.71 10A7 7 0 1 1 17 7.101L16 4M9.5 9.5v5m3.5 0v0a1.5 1.5 0 0 0 1.5-1.5v-2A1.5 1.5 0 0 0 13 9.5v0a1.5 1.5 0 0 0-1.5 1.5v2a1.5 1.5 0 0 0 1.5 1.5ZM10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconForward10Rec_default = SvgIconForward10Rec;

// src/icons/line/IconForward10.tsx
import { jsx as _jsx385 } from "react/jsx-runtime";
var SvgIconForward10 = (props) => /* @__PURE__ */ _jsx385("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx385("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12A9.98 9.98 0 0 1 12 2c6.667 0 10 5.556 10 5.556v-5M9 9v6m4.5 0v0a1.5 1.5 0 0 0 1.5-1.5v-3A1.5 1.5 0 0 0 13.5 9v0a1.5 1.5 0 0 0-1.5 1.5v3a1.5 1.5 0 0 0 1.5 1.5Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconForward10_default = SvgIconForward10;

// src/icons/line/IconForward15Rec.tsx
import { jsx as _jsx386 } from "react/jsx-runtime";
var SvgIconForward15Rec = (props) => /* @__PURE__ */ _jsx386("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx386("path", {
    d: "M18.71 10A7 7 0 1 1 17 7.101L16 4m-4.5 10.5h3V12h-3V9.5h3m-5 0v5M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconForward15Rec_default = SvgIconForward15Rec;

// src/icons/line/IconForward15.tsx
import { jsx as _jsx387 } from "react/jsx-runtime";
var SvgIconForward15 = (props) => /* @__PURE__ */ _jsx387("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx387("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12A9.98 9.98 0 0 1 12 2c6.667 0 10 5.556 10 5.556v-5M9 9v6m3 0h3v-3h-3V9h3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconForward15_default = SvgIconForward15;

// src/icons/line/IconForward5Rec.tsx
import { jsx as _jsx388 } from "react/jsx-runtime";
var SvgIconForward5Rec = (props) => /* @__PURE__ */ _jsx388("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx388("path", {
    d: "M18.71 10A7 7 0 1 1 17 7.101L16 4m-5.5 10.5h3V12h-3V9.5h3M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconForward5Rec_default = SvgIconForward5Rec;

// src/icons/line/IconForward5.tsx
import { jsx as _jsx389 } from "react/jsx-runtime";
var SvgIconForward5 = (props) => /* @__PURE__ */ _jsx389("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx389("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12A9.98 9.98 0 0 1 12 2c6.667 0 10 5.556 10 5.556v-5M10 15h4v-3h-4V9h4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconForward5_default = SvgIconForward5;

// src/icons/line/IconForwardAllRec.tsx
import { jsx as _jsx390 } from "react/jsx-runtime";
var SvgIconForwardAllRec = (props) => /* @__PURE__ */ _jsx390("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx390("path", {
    d: "m14 11-3-3m3 3-3 3m3-3H9.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C6 12.52 6 13.08 6 14.2V16m9-8 3 3-3 3m-5 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconForwardAllRec_default = SvgIconForwardAllRec;

// src/icons/line/IconForwardAll.tsx
import { jsx as _jsx391 } from "react/jsx-runtime";
var SvgIconForwardAll = (props) => /* @__PURE__ */ _jsx391("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx391("path", {
    d: "m17 5 5 5-5 5m1-5-5-5m5 5-5 5m5-5H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 11.52 2 12.08 2 13.2V17",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconForwardAll_default = SvgIconForwardAll;

// src/icons/line/IconForwardRec.tsx
import { jsx as _jsx392 } from "react/jsx-runtime";
var SvgIconForwardRec = (props) => /* @__PURE__ */ _jsx392("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx392("path", {
    d: "m18 11-3-3m3 3-3 3m3-3h-7.333A4.667 4.667 0 0 0 6 15.667V16m4 6h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconForwardRec_default = SvgIconForwardRec;

// src/icons/line/IconForward.tsx
import { jsx as _jsx393 } from "react/jsx-runtime";
var SvgIconForward = (props) => /* @__PURE__ */ _jsx393("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx393("path", {
    d: "m22 11-5-5m5 5-5 5m5-5H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 14.04 2 15.16 2 17.4v.6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconForward_default = SvgIconForward;

// src/icons/line/IconFrameBoundsRec.tsx
import { jsx as _jsx394 } from "react/jsx-runtime";
var SvgIconFrameBoundsRec = (props) => /* @__PURE__ */ _jsx394("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx394("path", {
    d: "M6 8h12M8 6v12m8-12v12M6 16h12m-8 6h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFrameBoundsRec_default = SvgIconFrameBoundsRec;

// src/icons/line/IconFrameBounds.tsx
import { jsx as _jsx395 } from "react/jsx-runtime";
var SvgIconFrameBounds = (props) => /* @__PURE__ */ _jsx395("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx395("path", {
    d: "M2 5h20M5 2v20M19 2v20M2 19h20",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFrameBounds_default = SvgIconFrameBounds;

// src/icons/line/IconFrame.tsx
import { jsx as _jsx396, jsxs as _jsxs13 } from "react/jsx-runtime";
var SvgIconFrame = (props) => /* @__PURE__ */ _jsxs13("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx396("path", {
    d: "M2 10c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14v-4Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx396("path", {
    d: "M6 9.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C7.52 6 8.08 6 9.2 6h5.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C18 7.52 18 8.08 18 9.2v5.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C16.48 18 15.92 18 14.8 18H9.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C6 16.48 6 15.92 6 14.8V9.2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconFrame_default = SvgIconFrame;

// src/icons/line/IconFramer.tsx
import { jsx as _jsx397 } from "react/jsx-runtime";
var SvgIconFramer = (props) => /* @__PURE__ */ _jsx397("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx397("path", {
    d: "M12 9h6V3H6l6 6Zm0 0H6v6m6-6 6 6H6m0 0 6 6v-6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFramer_default = SvgIconFramer;

// src/icons/line/IconFunctionsRec.tsx
import { jsx as _jsx398 } from "react/jsx-runtime";
var SvgIconFunctionsRec = (props) => /* @__PURE__ */ _jsx398("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx398("path", {
    d: "M16 7H8l5.152 5L8 17h8m-6 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFunctionsRec_default = SvgIconFunctionsRec;

// src/icons/line/IconFunctions.tsx
import { jsx as _jsx399 } from "react/jsx-runtime";
var SvgIconFunctions = (props) => /* @__PURE__ */ _jsx399("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx399("path", {
    d: "M19 3H5l9.015 9L5 21h14",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconFunctions_default = SvgIconFunctions;

// src/icons/line/IconGames.tsx
import { jsx as _jsx400 } from "react/jsx-runtime";
var SvgIconGames = (props) => /* @__PURE__ */ _jsx400("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx400("path", {
    d: "M2 11.4c0-.84 0-1.26.163-1.581a1.5 1.5 0 0 1 .656-.656C3.139 9 3.559 9 4.4 9h1.606c.367 0 .55 0 .723.041.153.037.3.098.433.18.152.093.282.223.54.482L10 12l-2.297 2.297c-.26.26-.39.39-.54.482-.135.082-.281.143-.434.18-.173.041-.356.041-.723.041H4.4c-.84 0-1.26 0-1.581-.164a1.5 1.5 0 0 1-.656-.655C2 13.861 2 13.441 2 12.6v-1.2ZM22 11.4c0-.84 0-1.26-.163-1.581a1.5 1.5 0 0 0-.656-.656C20.861 9 20.441 9 19.6 9h-1.606c-.367 0-.55 0-.723.041a1.5 1.5 0 0 0-.433.18c-.152.093-.282.223-.54.482L14 12l2.297 2.297c.26.26.39.39.54.482.135.082.281.143.434.18.173.041.356.041.723.041H19.6c.84 0 1.26 0 1.581-.164a1.5 1.5 0 0 0 .655-.655c.164-.32.164-.74.164-1.581v-1.2ZM11.4 2c-.84 0-1.26 0-1.581.163a1.5 1.5 0 0 0-.656.656C9 3.139 9 3.559 9 4.4v1.606c0 .367 0 .55.041.723.037.153.098.3.18.433.093.152.223.282.482.54L12 10l2.297-2.297c.26-.26.39-.39.482-.54.082-.135.143-.281.18-.434.041-.173.041-.356.041-.723V4.4c0-.84 0-1.26-.164-1.581a1.5 1.5 0 0 0-.655-.656C13.861 2 13.441 2 12.6 2h-1.2ZM11.4 22c-.84 0-1.26 0-1.581-.163a1.5 1.5 0 0 1-.656-.656C9 20.861 9 20.441 9 19.6v-1.606c0-.367 0-.55.041-.723a1.5 1.5 0 0 1 .18-.433c.093-.152.223-.282.482-.54L12 14l2.297 2.297c.26.26.39.39.482.54.082.135.143.281.18.434.041.173.041.356.041.723V19.6c0 .84 0 1.26-.164 1.581a1.5 1.5 0 0 1-.655.655c-.32.164-.74.164-1.581.164h-1.2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGames_default = SvgIconGames;

// src/icons/line/IconGear2.tsx
import { jsx as _jsx401, jsxs as _jsxs14 } from "react/jsx-runtime";
var SvgIconGear2 = (props) => /* @__PURE__ */ _jsxs14("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx401("path", {
    d: "M4.107 13.313c.1.606.008 1.24-.349 1.74l-1.38 1.933a2 2 0 0 0 .213 2.576l1.847 1.847a2 2 0 0 0 2.577.213l1.932-1.38c.5-.357 1.135-.45 1.741-.35a8.048 8.048 0 0 0 2.624 0c.607-.1 1.241-.007 1.741.35l1.932 1.38a2 2 0 0 0 2.577-.213l1.847-1.847a2 2 0 0 0 .213-2.577l-1.38-1.931c-.357-.5-.45-1.135-.35-1.742a8.065 8.065 0 0 0 0-2.624c-.1-.607-.007-1.241.35-1.741l1.38-1.932a2 2 0 0 0-.213-2.577l-1.847-1.846a2 2 0 0 0-2.577-.213l-1.931 1.379c-.5.357-1.135.45-1.741.35a8.056 8.056 0 0 0-2.626 0c-.606.1-1.24.007-1.74-.35l-1.932-1.38a2 2 0 0 0-2.577.214L2.591 4.438a2 2 0 0 0-.213 2.577l1.38 1.932c.357.5.45 1.134.35 1.74a8.057 8.057 0 0 0 0 2.626Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx401("path", {
    d: "M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconGear2_default = SvgIconGear2;

// src/icons/line/IconGear.tsx
import { jsx as _jsx402, jsxs as _jsxs15 } from "react/jsx-runtime";
var SvgIconGear = (props) => /* @__PURE__ */ _jsxs15("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx402("path", {
    d: "M12 4c-.697 0-1.387-.262-1.814-.813l-.452-.583a2 2 0 0 0-2.58-.507l-1.307.754a2 2 0 0 0-.851 2.489l.279.683c.264.645.147 1.37-.203 1.974v0C4.723 8.6 4.15 9.07 3.46 9.165l-.73.1A2 2 0 0 0 1 11.244v1.51a2 2 0 0 0 1.73 1.98l.73.1c.69.095 1.263.565 1.612 1.168v0c.35.604.467 1.329.203 1.974l-.28.683a2 2 0 0 0 .852 2.49l1.306.754a2 2 0 0 0 2.581-.508l.452-.583C10.613 20.262 11.303 20 12 20v0c.697 0 1.387.262 1.814.813l.452.583a2 2 0 0 0 2.58.507l1.307-.754a2 2 0 0 0 .852-2.489l-.28-.683c-.264-.645-.147-1.37.203-1.974v0c.349-.603.922-1.073 1.612-1.168l.73-.1a2 2 0 0 0 1.73-1.98v-1.51a2 2 0 0 0-1.73-1.98l-.73-.1c-.69-.095-1.263-.565-1.612-1.168v0c-.35-.604-.467-1.329-.203-1.974l.28-.683a2 2 0 0 0-.852-2.49l-1.306-.753a2 2 0 0 0-2.581.507l-.452.583C13.387 3.738 12.697 4 12 4v0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx402("path", {
    d: "M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconGear_default = SvgIconGear;

// src/icons/line/IconGift.tsx
import { jsx as _jsx403 } from "react/jsx-runtime";
var SvgIconGift = (props) => /* @__PURE__ */ _jsx403("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx403("path", {
    d: "M20 11v6.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C17.72 22 16.88 22 15.2 22H8.8c-1.68 0-2.52 0-3.162-.327a3 3 0 0 1-1.311-1.311C4 19.72 4 18.88 4 17.2V11m8-4v15m0-15h3.5a2.5 2.5 0 0 0 0-5h-.08a2.58 2.58 0 0 0-2.472 1.839L12 7Zm0 0H8.5a2.5 2.5 0 0 1 0-5h.08a2.58 2.58 0 0 1 2.472 1.839L12 7Zm-8 4h16a2 2 0 0 0 2-2v0a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGift_default = SvgIconGift;

// src/icons/line/IconGithub.tsx
import { jsx as _jsx404 } from "react/jsx-runtime";
var SvgIconGithub = (props) => /* @__PURE__ */ _jsx404("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx404("path", {
    d: "M2 12.076C2 6.377 6.274 2 11.839 2 17.443 2 22 6.377 22 12.076c0 4.584-2.742 8.507-6.774 9.87-.524.124-.726-.207-.726-.496 0-.254.035-1.45.035-2.45 0-1-.68-1.918-.68-1.918 2.258-.248 4.637-1.578 4.637-5.543 0-1.156-.403-1.693-1.049-2.436.081-.29.444-1.363-.12-2.808-.847-.29-2.783 1.115-2.783 1.115-.806-.248-1.653-.33-2.54-.33-.847 0-1.694.082-2.5.33 0 0-1.976-1.363-2.782-1.115-.565 1.445-.242 2.519-.121 2.808-.645.743-.968 1.28-.968 2.436 0 3.965 2.298 5.295 4.556 5.543 0 0-.685.78-.685 1.85v2.56c0 .247-.161.578-.685.495C4.782 20.583 2 16.66 2 12.077Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGithub_default = SvgIconGithub;

// src/icons/line/IconGlassCocktail.tsx
import { jsx as _jsx405 } from "react/jsx-runtime";
var SvgIconGlassCocktail = (props) => /* @__PURE__ */ _jsx405("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx405("path", {
    d: "m12 16 8.193-11.47c.584-.818.876-1.226.863-1.567a1 1 0 0 0-.388-.755C20.398 2 19.895 2 18.89 2H5.109c-1.005 0-1.507 0-1.777.208a1 1 0 0 0-.388.755c-.013.34.28.75.863 1.567L12 16Zm0 0v6m0 0H8m4 0h4M7 9h10",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGlassCocktail_default = SvgIconGlassCocktail;

// src/icons/line/IconGlassWine.tsx
import { jsx as _jsx406 } from "react/jsx-runtime";
var SvgIconGlassWine = (props) => /* @__PURE__ */ _jsx406("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx406("path", {
    d: "M12 15v7m0 0H8m4 0h4m-4-7v0a9 9 0 0 0 9-9V3.444C21 2.647 20.353 2 19.556 2H4.444C3.647 2 3 2.647 3 3.444V6a9 9 0 0 0 9 9Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGlassWine_default = SvgIconGlassWine;

// src/icons/line/IconGlassesVr.tsx
import { jsx as _jsx407 } from "react/jsx-runtime";
var SvgIconGlassesVr = (props) => /* @__PURE__ */ _jsx407("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx407("path", {
    d: "M22 14V6.713c0-.627 0-.94-.087-1.224a1.999 1.999 0 0 0-.368-.689c-.189-.229-.45-.403-.97-.75L19 3M2 14V6.713c0-.627 0-.94.086-1.224a2 2 0 0 1 .369-.689c.188-.229.449-.403.97-.75L5 3m5 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm8 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM8 21h8c.929 0 1.393 0 1.782-.062a5 5 0 0 0 4.156-4.156C22 16.393 22 15.93 22 15v0c0-.929 0-1.393-.062-1.782a5 5 0 0 0-4.156-4.156C17.393 9 16.93 9 16 9H8c-.929 0-1.394 0-1.782.062a5 5 0 0 0-4.156 4.156C2 13.607 2 14.07 2 15v0c0 .929 0 1.393.062 1.782a5 5 0 0 0 4.156 4.156C6.606 21 7.07 21 8 21Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGlassesVr_default = SvgIconGlassesVr;

// src/icons/line/IconGlasses.tsx
import { jsx as _jsx408 } from "react/jsx-runtime";
var SvgIconGlasses = (props) => /* @__PURE__ */ _jsx408("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx408("path", {
    d: "M2 16a4 4 0 1 0 8 0 4 4 0 0 0-8 0Zm0 0V7.713c0-.627 0-.94.086-1.224a2 2 0 0 1 .369-.689c.188-.229.449-.403.97-.75L5 4m5 12.5s.5-1 2-1 2 1 2 1m8-.5V7.713c0-.627 0-.94-.087-1.224a2 2 0 0 0-.368-.689c-.189-.229-.45-.403-.97-.75L19 4m3 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGlasses_default = SvgIconGlasses;

// src/icons/line/IconGlobe2.tsx
import { jsx as _jsx409 } from "react/jsx-runtime";
var SvgIconGlobe2 = (props) => /* @__PURE__ */ _jsx409("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx409("path", {
    d: "M12 19c5.523 0 10-4.477 10-10a9.967 9.967 0 0 0-2.859-7M12 19a9.967 9.967 0 0 1-7-2.859M12 19v3m0 0h-2m2 0h2m5-13A7 7 0 1 1 5 9a7 7 0 0 1 14 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGlobe2_default = SvgIconGlobe2;

// src/icons/line/IconGlobe.tsx
import { jsx as _jsx410 } from "react/jsx-runtime";
var SvgIconGlobe = (props) => /* @__PURE__ */ _jsx410("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx410("path", {
    d: "M22 12c0 5.523-4.477 10-10 10m10-10c0-5.523-4.477-10-10-10m10 10c0-2.21-4.477-4-10-4S2 9.79 2 12m20 0c0 2.21-4.477 4-10 4S2 14.21 2 12m10 10C6.477 22 2 17.523 2 12m10 10c2.21 0 4-4.477 4-10S14.21 2 12 2m0 20c-2.21 0-4-4.477-4-10S9.79 2 12 2M2 12C2 6.477 6.477 2 12 2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGlobe_default = SvgIconGlobe;

// src/icons/line/IconGoogleplay.tsx
import { jsx as _jsx411 } from "react/jsx-runtime";
var SvgIconGoogleplay = (props) => /* @__PURE__ */ _jsx411("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx411("path", {
    d: "M15 8 5 21M5 3l10 13m3.217-1.23-9.414 5.45c-1.576.912-2.364 1.368-3.01 1.3a2 2 0 0 1-1.41-.813C4 20.181 4 19.271 4 17.45V6.55c0-1.82 0-2.731.382-3.257a2 2 0 0 1 1.41-.813c.647-.068 1.435.388 3.011 1.3l9.414 5.45c1.57.91 2.355 1.364 2.619 1.957a2 2 0 0 1 0 1.626c-.264.593-1.05 1.047-2.62 1.956Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinejoin: "round"
  })
});
var IconGoogleplay_default = SvgIconGoogleplay;

// src/icons/line/IconGpsSlash.tsx
import { jsx as _jsx412 } from "react/jsx-runtime";
var SvgIconGpsSlash = (props) => /* @__PURE__ */ _jsx412("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx412("path", {
    d: "M12 21a9 9 0 0 1-9-9m9 9a8.972 8.972 0 0 0 6.364-2.636M12 21v2M3 12a8.972 8.972 0 0 1 2.636-6.364M3 12H1m11-9a9 9 0 0 1 9 9m-9-9a8.963 8.963 0 0 0-4 .936M12 3V1m9 11a8.963 8.963 0 0 1-.936 4M21 12h2m-4.636 6.364L22 22m-3.636-3.636-4.235-4.25-4.25-4.235M2 2l3.636 3.636m0 0 4.243 4.243m0 0A2.986 2.986 0 0 0 9 12a3 3 0 0 0 5 2.236",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGpsSlash_default = SvgIconGpsSlash;

// src/icons/line/IconGps.tsx
import { jsx as _jsx413 } from "react/jsx-runtime";
var SvgIconGps = (props) => /* @__PURE__ */ _jsx413("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx413("path", {
    d: "M21 12a9 9 0 0 1-9 9m9-9a9 9 0 0 0-9-9m9 9h2m-11 9a9 9 0 0 1-9-9m9 9v2M3 12a9 9 0 0 1 9-9m-9 9H1m11-9V1m3 11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGps_default = SvgIconGps;

// src/icons/line/IconGrid02.tsx
import { jsx as _jsx414 } from "react/jsx-runtime";
var SvgIconGrid02 = (props) => /* @__PURE__ */ _jsx414("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx414("path", {
    d: "M12 2v20M2 12h20M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid02_default = SvgIconGrid02;

// src/icons/line/IconGrid03.tsx
import { jsx as _jsx415 } from "react/jsx-runtime";
var SvgIconGrid03 = (props) => /* @__PURE__ */ _jsx415("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx415("path", {
    d: "M12 2v20m-2 0h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid03_default = SvgIconGrid03;

// src/icons/line/IconGrid04.tsx
import { jsx as _jsx416 } from "react/jsx-runtime";
var SvgIconGrid04 = (props) => /* @__PURE__ */ _jsx416("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx416("path", {
    d: "M2 12h20M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid04_default = SvgIconGrid04;

// src/icons/line/IconGrid05.tsx
import { jsx as _jsx417 } from "react/jsx-runtime";
var SvgIconGrid05 = (props) => /* @__PURE__ */ _jsx417("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx417("path", {
    d: "M9 2v20m1 0h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid05_default = SvgIconGrid05;

// src/icons/line/IconGrid06.tsx
import { jsx as _jsx418 } from "react/jsx-runtime";
var SvgIconGrid06 = (props) => /* @__PURE__ */ _jsx418("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx418("path", {
    d: "M15 2v20m-5 0h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid06_default = SvgIconGrid06;

// src/icons/line/IconGrid07.tsx
import { jsx as _jsx419 } from "react/jsx-runtime";
var SvgIconGrid07 = (props) => /* @__PURE__ */ _jsx419("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx419("path", {
    d: "M9 9v13M2 9h20M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid07_default = SvgIconGrid07;

// src/icons/line/IconGrid08.tsx
import { jsx as _jsx420 } from "react/jsx-runtime";
var SvgIconGrid08 = (props) => /* @__PURE__ */ _jsx420("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx420("path", {
    d: "M2 9h7m0 0h13M9 9v6m0 0v7m0-7h13m-12 7h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid08_default = SvgIconGrid08;

// src/icons/line/IconGrid09.tsx
import { jsx as _jsx421 } from "react/jsx-runtime";
var SvgIconGrid09 = (props) => /* @__PURE__ */ _jsx421("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx421("path", {
    d: "M12 9v13M2 9h20M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid09_default = SvgIconGrid09;

// src/icons/line/IconGrid10.tsx
import { jsx as _jsx422 } from "react/jsx-runtime";
var SvgIconGrid10 = (props) => /* @__PURE__ */ _jsx422("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx422("path", {
    d: "M2 9h7m0 0h6M9 9v13m6-13h7m-7 0v13m-5 0h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid10_default = SvgIconGrid10;

// src/icons/line/IconGrid11.tsx
import { jsx as _jsx423 } from "react/jsx-runtime";
var SvgIconGrid11 = (props) => /* @__PURE__ */ _jsx423("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx423("path", {
    d: "M12 9v13M2 9h20M2 15h20m-12 7h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid11_default = SvgIconGrid11;

// src/icons/line/IconGrid12.tsx
import { jsx as _jsx424 } from "react/jsx-runtime";
var SvgIconGrid12 = (props) => /* @__PURE__ */ _jsx424("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx424("path", {
    d: "M9 12h13M9 2v20m6-20v20m-5 0h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid12_default = SvgIconGrid12;

// src/icons/line/IconGrid13.tsx
import { jsx as _jsx425 } from "react/jsx-runtime";
var SvgIconGrid13 = (props) => /* @__PURE__ */ _jsx425("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx425("path", {
    d: "M9 2v20M2 12h20M15 2v20m-5 0h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid13_default = SvgIconGrid13;

// src/icons/line/IconGrid14.tsx
import { jsx as _jsx426 } from "react/jsx-runtime";
var SvgIconGrid14 = (props) => /* @__PURE__ */ _jsx426("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx426("path", {
    d: "M9 2v20m6-20v20m-5 0h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid14_default = SvgIconGrid14;

// src/icons/line/IconGrid15.tsx
import { jsx as _jsx427 } from "react/jsx-runtime";
var SvgIconGrid15 = (props) => /* @__PURE__ */ _jsx427("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx427("path", {
    d: "M2 9h20M2 15h20m-12 7h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid15_default = SvgIconGrid15;

// src/icons/line/IconGrid16.tsx
import { jsx as _jsx428 } from "react/jsx-runtime";
var SvgIconGrid16 = (props) => /* @__PURE__ */ _jsx428("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx428("path", {
    d: "M12 2v20M2 9h10m0 6h10m-12 7h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid16_default = SvgIconGrid16;

// src/icons/line/IconGrid.tsx
import { jsx as _jsx429 } from "react/jsx-runtime";
var SvgIconGrid = (props) => /* @__PURE__ */ _jsx429("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx429("path", {
    d: "M9 2v20m6-20v20M2 15h20M2 9h20M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconGrid_default = SvgIconGrid;

// src/icons/line/IconHardDrive.tsx
import { jsx as _jsx430 } from "react/jsx-runtime";
var SvgIconHardDrive = (props) => /* @__PURE__ */ _jsx430("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx430("path", {
    d: "M2 17v-1.453c0-.205 0-.307.007-.41a3 3 0 0 1 .03-.27c.017-.1.04-.2.086-.4l1.732-7.506c.409-1.771.613-2.657 1.095-3.318a4 4 0 0 1 1.663-1.322C7.365 2 8.273 2 10.09 2h3.818c1.817 0 2.726 0 3.478.32a4 4 0 0 1 1.663 1.323c.482.661.686 1.547 1.095 3.318l1.732 7.506c.046.2.07.3.085.4.015.09.025.18.031.27.007.103.007.205.007.41V17m-11 1h6M7 18h.01m.49 0a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0ZM6 22h12c.93 0 1.395 0 1.776-.102a3 3 0 0 0 2.122-2.122C22 19.395 22 18.93 22 18v0c0-.93 0-1.395-.102-1.776a3 3 0 0 0-2.122-2.122C19.395 14 18.93 14 18 14H6c-.93 0-1.395 0-1.776.102a3 3 0 0 0-2.122 2.122C2 16.605 2 17.07 2 18v0c0 .93 0 1.395.102 1.776a3 3 0 0 0 2.122 2.122C4.605 22 5.07 22 6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHardDrive_default = SvgIconHardDrive;

// src/icons/line/IconHashtagCircle.tsx
import { jsx as _jsx431 } from "react/jsx-runtime";
var SvgIconHashtagCircle = (props) => /* @__PURE__ */ _jsx431("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx431("path", {
    d: "m11 8-1 8m4-8-1 8m-4.5-5.5H16m-8 3h7.5M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHashtagCircle_default = SvgIconHashtagCircle;

// src/icons/line/IconHashtagRec.tsx
import { jsx as _jsx432 } from "react/jsx-runtime";
var SvgIconHashtagRec = (props) => /* @__PURE__ */ _jsx432("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx432("path", {
    d: "m10.5 7-1 10m5-10-1 10m-5.875-7H17M7 14h9.375M11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHashtagRec_default = SvgIconHashtagRec;

// src/icons/line/IconHashtag.tsx
import { jsx as _jsx433 } from "react/jsx-runtime";
var SvgIconHashtag = (props) => /* @__PURE__ */ _jsx433("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx433("path", {
    d: "M10 3 8 21m8-18-2 18M3 9h18M2 15h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHashtag_default = SvgIconHashtag;

// src/icons/line/IconHeadphones2.tsx
import { jsx as _jsx434 } from "react/jsx-runtime";
var SvgIconHeadphones2 = (props) => /* @__PURE__ */ _jsx434("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx434("path", {
    d: "M19 13V9a7 7 0 0 0-7-7v0a7 7 0 0 0-7 7v4m17 6v-3c0-.932 0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C20.398 13 19.932 13 19 13v0c-.932 0-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C16 14.602 16 15.068 16 16v3c0 .932 0 1.398.152 1.765a2 2 0 0 0 1.083 1.083C17.602 22 18.068 22 19 22v0c.932 0 1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C22 20.398 22 19.932 22 19ZM5 22v0c.932 0 1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C8 20.398 8 19.932 8 19v-3c0-.932 0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C6.398 13 5.932 13 5 13v0c-.932 0-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C2 14.602 2 15.068 2 16v3c0 .932 0 1.398.152 1.765a2 2 0 0 0 1.083 1.083C3.602 22 4.068 22 5 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHeadphones2_default = SvgIconHeadphones2;

// src/icons/line/IconHeadphones.tsx
import { jsx as _jsx435 } from "react/jsx-runtime";
var SvgIconHeadphones = (props) => /* @__PURE__ */ _jsx435("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx435("path", {
    d: "M22 17v-5c0-5.523-4.477-10-10-10v0C6.477 2 2 6.477 2 12v5m20 2v-3c0-.932 0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C20.398 13 19.932 13 19 13v0c-.932 0-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C16 14.602 16 15.068 16 16v3c0 .932 0 1.398.152 1.765a2 2 0 0 0 1.083 1.083C17.602 22 18.068 22 19 22v0c.932 0 1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C22 20.398 22 19.932 22 19ZM5 22v0c.932 0 1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C8 20.398 8 19.932 8 19v-3c0-.932 0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C6.398 13 5.932 13 5 13v0c-.932 0-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C2 14.602 2 15.068 2 16v3c0 .932 0 1.398.152 1.765a2 2 0 0 0 1.083 1.083C3.602 22 4.068 22 5 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHeadphones_default = SvgIconHeadphones;

// src/icons/line/IconHeartSlash.tsx
import { jsx as _jsx436 } from "react/jsx-runtime";
var SvgIconHeartSlash = (props) => /* @__PURE__ */ _jsx436("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx436("path", {
    d: "m2 2 2.312 2.312M21 21l-3.494-3.494m0 0C15.093 19.703 12.51 21 12 21 11 21 2 16 2 9c0-1.968.948-3.62 2.312-4.688m13.194 13.194L4.312 4.312M19.798 15c.068-.092.136-.186.202-.28 1.177-1.668 2-3.603 2-5.72 0-3.5-3-5.957-6-6-1.5-.022-3 .5-4 2-1-1.5-2.526-2-4-2-.167 0-.334.008-.5.023",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHeartSlash_default = SvgIconHeartSlash;

// src/icons/line/IconHeart.tsx
import { jsx as _jsx437 } from "react/jsx-runtime";
var SvgIconHeart = (props) => /* @__PURE__ */ _jsx437("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx437("path", {
    d: "M12 21c1 0 10-5 10-12 0-3.5-3-5.956-6-6-1.5-.021-3 .5-4 2-1-1.5-2.526-2-4-2-3 0-6 2.5-6 6 0 7 9 12 10 12Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHeart_default = SvgIconHeart;

// src/icons/line/IconHistoryRec.tsx
import { jsx as _jsx438 } from "react/jsx-runtime";
var SvgIconHistoryRec = (props) => /* @__PURE__ */ _jsx438("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx438("path", {
    d: "M11.5 10v.264c0 1.37.774 2.623 2 3.236v0M6 12a6 6 0 1 0 6-6C9.78 6 8 7 6.5 8.5l1-3.5m4.1 17h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHistoryRec_default = SvgIconHistoryRec;

// src/icons/line/IconHistory.tsx
import { jsx as _jsx439 } from "react/jsx-runtime";
var SvgIconHistory = (props) => /* @__PURE__ */ _jsx439("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx439("path", {
    d: "M2 12c0 4.97 4 10 10 10s10-4 10-10S18 2 12 2 2 7 2 7m0 0V3m0 4h3.5M12 8v4l3 2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHistory_default = SvgIconHistory;

// src/icons/line/IconHome2.tsx
import { jsx as _jsx440 } from "react/jsx-runtime";
var SvgIconHome2 = (props) => /* @__PURE__ */ _jsx440("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx440("path", {
    d: "M9 16h6M7.002 3.998l-2 1.6C3.9 6.48 3.35 6.921 2.952 7.468a5 5 0 0 0-.772 1.607C2 9.727 2 10.433 2 11.845V14c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-2.155c0-1.412 0-2.118-.18-2.77a5.001 5.001 0 0 0-.772-1.606c-.397-.548-.948-.989-2.05-1.871l-2-1.6c-1.784-1.427-2.676-2.14-3.665-2.414a5 5 0 0 0-2.666 0c-.99.274-1.881.987-3.665 2.414Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHome2_default = SvgIconHome2;

// src/icons/line/IconHome3.tsx
import { jsx as _jsx441 } from "react/jsx-runtime";
var SvgIconHome3 = (props) => /* @__PURE__ */ _jsx441("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx441("path", {
    d: "M15 22v-7c0-.932 0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C13.398 12 12.932 12 12 12v0c-.932 0-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C9 13.602 9 14.068 9 15v7M7.002 3.998l-2 1.6C3.9 6.48 3.35 6.921 2.952 7.468a5 5 0 0 0-.772 1.607C2 9.727 2 10.433 2 11.845V14c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-2.155c0-1.412 0-2.118-.18-2.77a5.001 5.001 0 0 0-.772-1.606c-.397-.548-.948-.989-2.05-1.871l-2-1.6c-1.784-1.427-2.676-2.14-3.665-2.414a5 5 0 0 0-2.666 0c-.99.274-1.881.987-3.665 2.414Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHome3_default = SvgIconHome3;

// src/icons/line/IconHome4.tsx
import { jsx as _jsx442 } from "react/jsx-runtime";
var SvgIconHome4 = (props) => /* @__PURE__ */ _jsx442("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx442("path", {
    d: "m2.5 9.5 1.471 7.355c.367 1.833.55 2.75 1.028 3.435a4 4 0 0 0 1.678 1.375c.766.335 1.7.335 3.57.335h3.506c1.87 0 2.804 0 3.57-.335A4 4 0 0 0 19 20.29c.478-.686.661-1.602 1.028-3.435L21.5 9.5M1 11l6.475-6.475C9.059 2.941 9.85 2.15 10.764 1.853a4 4 0 0 1 2.472 0c.913.296 1.705 1.088 3.29 2.672L23 11m-8 1a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHome4_default = SvgIconHome4;

// src/icons/line/IconHome.tsx
import { jsx as _jsx443 } from "react/jsx-runtime";
var SvgIconHome = (props) => /* @__PURE__ */ _jsx443("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx443("path", {
    d: "M22 17v-5.155c0-1.412 0-2.118-.18-2.77a5.001 5.001 0 0 0-.772-1.606c-.397-.548-.948-.989-2.05-1.871l-2-1.6c-1.784-1.427-2.676-2.14-3.665-2.414a5 5 0 0 0-2.666 0c-.99.274-1.881.987-3.665 2.414l-2 1.6C3.9 6.48 3.35 6.921 2.952 7.468a5 5 0 0 0-.772 1.607C2 9.727 2 10.433 2 11.845V17a5 5 0 0 0 5 5 2 2 0 0 0 2-2v-4a3 3 0 0 1 6 0v4a2 2 0 0 0 2 2 5 5 0 0 0 5-5Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconHome_default = SvgIconHome;

// src/icons/line/IconIframe.tsx
import { jsx as _jsx444 } from "react/jsx-runtime";
var SvgIconIframe = (props) => /* @__PURE__ */ _jsx444("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx444("path", {
    d: "M2 12h3.6c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C12 15.04 12 16.16 12 18.4V22m-3.6 0h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 2 17.84 2 15.6 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconIframe_default = SvgIconIframe;

// src/icons/line/IconIllustrator.tsx
import { jsx as _jsx445 } from "react/jsx-runtime";
var SvgIconIllustrator = (props) => /* @__PURE__ */ _jsx445("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx445("path", {
    d: "m6 16 1.25-2.5M14 16l-1.25-2.5M16.5 16v-4m.5-3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-8.75 4.5h5.5m-5.5 0L10 8l2.75 5.5M2 14v-4c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconIllustrator_default = SvgIconIllustrator;

// src/icons/line/IconImage2.tsx
import { jsx as _jsx446 } from "react/jsx-runtime";
var SvgIconImage2 = (props) => /* @__PURE__ */ _jsx446("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx446("path", {
    d: "M18 22H5.861c-1.909 0-2.863 0-3.396-.399a2 2 0 0 1-.797-1.459c-.047-.663.47-1.466 1.502-3.072l3.138-4.883c.896-1.393 1.344-2.09 1.914-2.33a2 2 0 0 1 1.556 0c.57.24 1.018.937 1.914 2.33l2.808 4.369M18 22h.139c1.909 0 2.863 0 3.396-.399a2 2 0 0 0 .797-1.459c.047-.663-.47-1.466-1.502-3.072l-.638-.994c-.896-1.394-1.344-2.09-1.914-2.33a2 2 0 0 0-1.556 0c-.57.24-1.018.936-1.914 2.33l-.308.48M18 22l-3.5-5.444M20 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImage2_default = SvgIconImage2;

// src/icons/line/IconImage3.tsx
import { jsx as _jsx447 } from "react/jsx-runtime";
var SvgIconImage3 = (props) => /* @__PURE__ */ _jsx447("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx447("path", {
    d: "m3 18 3.77-3.77c.782-.782 1.173-1.173 1.624-1.322A2 2 0 0 1 9.62 12.9c.454.143.85.528 1.643 1.297l3.224 3.13m7.014.361-1.425-1.424c-.792-.792-1.188-1.188-1.645-1.337a2 2 0 0 0-1.235 0c-.457.149-.853.545-1.646 1.337l-1.063 1.063M18.5 21.34l-4.014-4.014M18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm-6.4 14h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImage3_default = SvgIconImage3;

// src/icons/line/IconImageCheck.tsx
import { jsx as _jsx448 } from "react/jsx-runtime";
var SvgIconImageCheck = (props) => /* @__PURE__ */ _jsx448("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx448("path", {
    d: "m2.5 18.5 4.27-4.27c.782-.782 1.173-1.173 1.624-1.322A2 2 0 0 1 9.62 12.9c.454.143.85.528 1.643 1.297l3.224 3.13m7.014.361-1.425-1.424c-.792-.792-1.188-1.188-1.645-1.337a2 2 0 0 0-1.235 0c-.457.149-.853.545-1.646 1.337l-1.063 1.063M18.5 21.34l-4.014-4.014M15 6l2 2 4-4m-9-2h-.5v0c-3.266 0-4.899 0-6.154.619A6 6 0 0 0 2.62 5.346C2 6.601 2 8.234 2 11.5v.9c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22h.9c3.266 0 4.899 0 6.154-.619a6 6 0 0 0 2.727-2.727C22 17.399 22 15.766 22 12.5v0-.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImageCheck_default = SvgIconImageCheck;

// src/icons/line/IconImageCross.tsx
import { jsx as _jsx449 } from "react/jsx-runtime";
var SvgIconImageCross = (props) => /* @__PURE__ */ _jsx449("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx449("path", {
    d: "m2.5 18.5 4.27-4.27c.782-.782 1.173-1.173 1.624-1.322A2 2 0 0 1 9.62 12.9c.454.143.85.528 1.643 1.297l3.224 3.13m7.014.361-1.425-1.424c-.792-.792-1.188-1.188-1.645-1.337a2 2 0 0 0-1.235 0c-.457.149-.853.545-1.646 1.337l-1.063 1.063M18.5 21.34l-4.014-4.014M20 8l-2-2m0 0-2-2m2 2-2 2m2-2 2-2m-8-2h-.5v0c-3.266 0-4.899 0-6.154.619A6 6 0 0 0 2.62 5.346C2 6.601 2 8.234 2 11.5v.9c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22h.9c3.266 0 4.899 0 6.154-.619a6 6 0 0 0 2.727-2.727C22 17.399 22 15.766 22 12.5v0-.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImageCross_default = SvgIconImageCross;

// src/icons/line/IconImageDownload.tsx
import { jsx as _jsx450 } from "react/jsx-runtime";
var SvgIconImageDownload = (props) => /* @__PURE__ */ _jsx450("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx450("path", {
    d: "m3 18 3.77-3.77c.782-.782 1.173-1.173 1.624-1.322A2 2 0 0 1 9.62 12.9c.454.143.85.528 1.643 1.297l3.224 3.13m7.014.361-1.425-1.424c-.792-.792-1.188-1.188-1.645-1.337a2 2 0 0 0-1.235 0c-.457.149-.853.545-1.646 1.337l-1.063 1.063M18.5 21.34l-4.014-4.014M17 3v6m0 0-2.5-2.5M17 9l2.5-2.5M12 2h-.5v0c-3.266 0-4.899 0-6.154.619A6 6 0 0 0 2.62 5.346C2 6.601 2 8.234 2 11.5v.9c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22h.9c3.266 0 4.899 0 6.154-.619a6 6 0 0 0 2.727-2.727C22 17.399 22 15.766 22 12.5v0-.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImageDownload_default = SvgIconImageDownload;

// src/icons/line/IconImageEdit.tsx
import { jsx as _jsx451 } from "react/jsx-runtime";
var SvgIconImageEdit = (props) => /* @__PURE__ */ _jsx451("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx451("path", {
    d: "M12 2h-.5v0c-3.266 0-4.899 0-6.154.619A6 6 0 0 0 2.62 5.346C2 6.601 2 8.234 2 11.5v.9c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22h.9c3.266 0 4.899 0 6.154-.619a6 6 0 0 0 2.727-2.727C22 17.399 22 15.766 22 12.5v0-.5m-.5 5.688-1.425-1.425c-.792-.792-1.188-1.188-1.645-1.337a2 2 0 0 0-1.235 0c-.457.149-.853.545-1.645 1.337l-1.064 1.063M2.5 18.5l4.27-4.27c.782-.782 1.173-1.173 1.624-1.322A2 2 0 0 1 9.62 12.9c.454.143.85.528 1.643 1.297l3.224 3.13m0 0L18.5 21.34M14 10l.12-.6c.141-.706.212-1.06.341-1.389.115-.292.263-.57.443-.827.202-.29.457-.545.966-1.054L18 4a1.414 1.414 0 0 1 2 0v0a1.414 1.414 0 0 1 0 2l-2.13 2.13c-.51.51-.764.764-1.054.966-.257.18-.535.328-.827.443-.33.13-.683.2-1.39.341L14 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImageEdit_default = SvgIconImageEdit;

// src/icons/line/IconImagePlus.tsx
import { jsx as _jsx452 } from "react/jsx-runtime";
var SvgIconImagePlus = (props) => /* @__PURE__ */ _jsx452("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx452("path", {
    d: "m2.5 18.5 4.27-4.27c.782-.782 1.173-1.173 1.624-1.322A2 2 0 0 1 9.62 12.9c.454.143.85.528 1.643 1.297l3.224 3.13m7.014.361-1.425-1.424c-.792-.792-1.188-1.188-1.645-1.337a2 2 0 0 0-1.235 0c-.457.149-.853.545-1.646 1.337l-1.063 1.063M18.5 21.34l-4.014-4.014M18 9V6m0 0V3m0 3h-3m3 0h3m-9-4h-.5v0c-3.266 0-4.899 0-6.154.619A6 6 0 0 0 2.62 5.346C2 6.601 2 8.234 2 11.5v.9c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22h.9c3.266 0 4.899 0 6.154-.619a6 6 0 0 0 2.727-2.727C22 17.399 22 15.766 22 12.5v0-.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImagePlus_default = SvgIconImagePlus;

// src/icons/line/IconImageSlash.tsx
import { jsx as _jsx453 } from "react/jsx-runtime";
var SvgIconImageSlash = (props) => /* @__PURE__ */ _jsx453("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx453("path", {
    d: "m2 2 1.757 1.757M22 22l-1.757-1.757m0 0c-.37.369-.787.69-1.243.953C17.608 22 15.739 22 12 22c-3.738 0-5.608 0-7-.804A6 6 0 0 1 2.804 19C2 17.608 2 15.739 2 12c0-3.738 0-5.608.804-7a6 6 0 0 1 .953-1.243m16.486 16.486L3.757 3.757M7 2.172C8.197 2 9.764 2 12 2c3.739 0 5.608 0 7 .804A6 6 0 0 1 21.196 5C22 6.392 22 8.262 22 12c0 2.236 0 3.803-.172 5M3 18l2.737-2.737c.792-.792 1.188-1.188 1.645-1.337a2 2 0 0 1 1.236 0c.457.149.853.545 1.645 1.337L16.5 21.5M18 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImageSlash_default = SvgIconImageSlash;

// src/icons/line/IconImageUpload.tsx
import { jsx as _jsx454 } from "react/jsx-runtime";
var SvgIconImageUpload = (props) => /* @__PURE__ */ _jsx454("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx454("path", {
    d: "m2.5 18.5 4.27-4.27c.782-.782 1.173-1.173 1.624-1.322A2 2 0 0 1 9.62 12.9c.454.143.85.528 1.643 1.297l3.224 3.13m7.014.361-1.425-1.424c-.792-.792-1.188-1.188-1.645-1.337a2 2 0 0 0-1.235 0c-.457.149-.853.545-1.646 1.337l-1.063 1.063M18.5 21.34l-4.014-4.014M18 9V3m0 0-2.5 2.5M18 3l2.5 2.5M12 2h-.5v0c-3.266 0-4.899 0-6.154.619A6 6 0 0 0 2.62 5.346C2 6.601 2 8.234 2 11.5v.9c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22h.9c3.266 0 4.899 0 6.154-.619a6 6 0 0 0 2.727-2.727C22 17.399 22 15.766 22 12.5v0-.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImageUpload_default = SvgIconImageUpload;

// src/icons/line/IconImage.tsx
import { jsx as _jsx455 } from "react/jsx-runtime";
var SvgIconImage = (props) => /* @__PURE__ */ _jsx455("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx455("path", {
    d: "m14 17-2.237-2.237c-.792-.792-1.188-1.188-1.645-1.337a2 2 0 0 0-1.236 0c-.457.149-.853.545-1.645 1.337L5 17l-.5.5M14 17l.737-.737c.792-.792 1.188-1.188 1.645-1.337a2 2 0 0 1 1.236 0c.457.149.853.545 1.645 1.337L20 17m-6 0 3 3M16 9a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm6 3c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImage_default = SvgIconImage;

// src/icons/line/IconImport2.tsx
import { jsx as _jsx456 } from "react/jsx-runtime";
var SvgIconImport2 = (props) => /* @__PURE__ */ _jsx456("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx456("path", {
    d: "M16 8h.5v0c1.396 0 2.093 0 2.661.172a4 4 0 0 1 2.667 2.667C22 11.407 22 12.104 22 13.5v2.1c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6v-2.1c0-1.396 0-2.093.172-2.661A4 4 0 0 1 4.84 8.172C5.407 8 6.104 8 7.5 8v0H8m4-6v15m0 0 4-4m-4 4-4-4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImport2_default = SvgIconImport2;

// src/icons/line/IconImport3.tsx
import { jsx as _jsx457 } from "react/jsx-runtime";
var SvgIconImport3 = (props) => /* @__PURE__ */ _jsx457("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx457("path", {
    d: "M14 2.2c-.646-.131-1.315-.2-2-.2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10c0-.685-.069-1.354-.2-2M10 14 20 4M10 14V9m0 5h5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImport3_default = SvgIconImport3;

// src/icons/line/IconImport4.tsx
import { jsx as _jsx458 } from "react/jsx-runtime";
var SvgIconImport4 = (props) => /* @__PURE__ */ _jsx458("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx458("path", {
    d: "M12 2v15m0 0 4-4m-4 4-4-4m1 9h6a7 7 0 0 0 7-7v0a7 7 0 0 0-7-7H9a7 7 0 0 0-7 7v0a7 7 0 0 0 7 7Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImport4_default = SvgIconImport4;

// src/icons/line/IconImport.tsx
import { jsx as _jsx459 } from "react/jsx-runtime";
var SvgIconImport = (props) => /* @__PURE__ */ _jsx459("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx459("path", {
    d: "M22 14v1.6c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H8.4c-2.24 0-3.36 0-4.216-.436a4 4 0 0 1-1.748-1.748C2 18.96 2 17.84 2 15.6V14M12 2v12m0 0 4-4m-4 4-4-4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconImport_default = SvgIconImport;

// src/icons/line/IconInboxFilled.tsx
import { jsx as _jsx460 } from "react/jsx-runtime";
var SvgIconInboxFilled = (props) => /* @__PURE__ */ _jsx460("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx460("path", {
    d: "M2.5 14h2.787c.626 0 .94 0 1.224.086a2 2 0 0 1 .688.369c.23.188.403.449.75.97l.1.15c.348.521.522.782.751.97a2 2 0 0 0 .689.369c.284.086.597.086 1.223.086h2.575c.626 0 .94 0 1.224-.086a2 2 0 0 0 .688-.369c.23-.189.403-.449.75-.97l.1-.15c.348-.521.522-.782.751-.97.203-.167.437-.292.689-.369.284-.086.597-.086 1.223-.086H21.5M9 7h6m-5 4h4M3.485 8.06l-.616 2.464c-.14.562-.21.843-.267 1.123a12 12 0 0 0 0 4.706c.056.28.126.561.267 1.123v0c.175.7.262 1.05.38 1.35a5 5 0 0 0 4.012 3.133c.32.041.68.041 1.402.041h6.674c.721 0 1.082 0 1.402-.041a5 5 0 0 0 4.011-3.133c.118-.3.206-.65.38-1.35v0c.141-.562.212-.843.268-1.123.31-1.553.31-3.153 0-4.706-.056-.28-.127-.561-.267-1.123l-.616-2.464c-.542-2.167-.813-3.25-1.417-4.057a5 5 0 0 0-2.065-1.613C16.103 2 14.987 2 12.753 2h-1.507c-2.233 0-3.35 0-4.279.39a5 5 0 0 0-2.065 1.613c-.604.807-.875 1.89-1.417 4.057Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconInboxFilled_default = SvgIconInboxFilled;

// src/icons/line/IconInboxIn.tsx
import { jsx as _jsx461 } from "react/jsx-runtime";
var SvgIconInboxIn = (props) => /* @__PURE__ */ _jsx461("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx461("path", {
    d: "M15.5 3h.5v0a3.928 3.928 0 0 1 3.79 2.894l1.243 4.562c.14.515.211.772.269 1.029.375 1.659.393 3.378.054 5.045-.053.257-.118.516-.247 1.034v0c-.154.616-.231.924-.331 1.19a5 5 0 0 1-4.115 3.214c-.283.032-.6.032-1.236.032H8.573c-.636 0-.954 0-1.236-.032a5 5 0 0 1-4.115-3.213c-.1-.267-.177-.575-.331-1.191v0a25.13 25.13 0 0 1-.247-1.034 12 12 0 0 1 .054-5.046c.058-.256.128-.513.268-1.028l1.245-4.562A3.928 3.928 0 0 1 8 3v0h.5m-6 11h2.787c.627 0 .94 0 1.224.086a2 2 0 0 1 .689.369c.229.188.403.449.75.97l.1.15c.347.521.521.782.75.97a2 2 0 0 0 .69.369c.283.086.596.086 1.223.086h2.574c.627 0 .94 0 1.224-.086a2 2 0 0 0 .689-.369c.229-.189.403-.449.75-.97l.1-.15c.347-.521.521-.782.75-.97a2 2 0 0 1 .69-.369c.283-.086.596-.086 1.223-.086H21.5M12 11l3-3m-3 3L9 8m3 3V2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconInboxIn_default = SvgIconInboxIn;

// src/icons/line/IconInboxOut.tsx
import { jsx as _jsx462 } from "react/jsx-runtime";
var SvgIconInboxOut = (props) => /* @__PURE__ */ _jsx462("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx462("path", {
    d: "M15.5 8h.916c.705 0 1.058 0 1.378.07a3 3 0 0 1 1.682 1.04c.206.255.364.57.68 1.201l.254.509c.263.526.394.789.503 1.053a8 8 0 0 1 .488 4.44c-.05.28-.12.566-.264 1.137v0c-.18.725-.271 1.087-.395 1.398a5 5 0 0 1-3.98 3.108c-.332.044-.705.044-1.453.044H8.69c-.747 0-1.12 0-1.452-.044a5 5 0 0 1-3.98-3.108c-.124-.31-.214-.673-.396-1.398v0c-.142-.57-.214-.856-.263-1.137a8 8 0 0 1 .487-4.44c.11-.264.241-.527.504-1.053l.254-.509c.316-.63.474-.946.68-1.201a3 3 0 0 1 1.682-1.04C6.526 8 6.879 8 7.584 8H8.5M3 14h2.287c.626 0 .94 0 1.224.086a2 2 0 0 1 .688.369c.23.188.404.449.75.97l.1.15c.348.521.522.782.751.97a2 2 0 0 0 .689.369c.284.086.597.086 1.223.086h2.575c.626 0 .94 0 1.224-.086a2 2 0 0 0 .688-.369c.23-.189.403-.449.75-.97l.1-.15c.348-.521.522-.782.751-.97.203-.167.438-.292.689-.369.284-.086.597-.086 1.223-.086H21M12 2l3 3m-3-3L9 5m3-3v9",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconInboxOut_default = SvgIconInboxOut;

// src/icons/line/IconInbox.tsx
import { jsx as _jsx463 } from "react/jsx-runtime";
var SvgIconInbox = (props) => /* @__PURE__ */ _jsx463("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx463("path", {
    d: "M2.5 14h2.787c.626 0 .94 0 1.224.086a2 2 0 0 1 .688.369c.23.188.404.449.75.97l.1.15c.348.521.522.782.751.97a2 2 0 0 0 .689.369c.284.086.597.086 1.223.086h2.575c.626 0 .94 0 1.224-.086a2 2 0 0 0 .688-.369c.23-.189.403-.449.75-.97l.1-.15c.348-.521.522-.782.751-.97.203-.167.438-.292.689-.369.284-.086.597-.086 1.223-.086H21.5M3.485 8.06l-.616 2.464c-.14.562-.21.843-.267 1.123a12 12 0 0 0 0 4.706c.056.28.126.561.267 1.123v0c.175.7.262 1.05.38 1.35a5 5 0 0 0 4.012 3.133c.32.041.68.041 1.402.041h6.674c.721 0 1.082 0 1.402-.041a5 5 0 0 0 4.012-3.133c.117-.3.205-.65.38-1.35v0c.14-.562.21-.843.267-1.123a12 12 0 0 0 0-4.706c-.056-.28-.127-.561-.267-1.123l-.616-2.464c-.542-2.167-.813-3.25-1.416-4.057a5 5 0 0 0-2.066-1.613C16.103 2 14.987 2 12.753 2h-1.507c-2.233 0-3.35 0-4.279.39a5 5 0 0 0-2.065 1.613c-.604.807-.875 1.89-1.417 4.057Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconInbox_default = SvgIconInbox;

// src/icons/line/IconIndentDecreaseRec.tsx
import { jsx as _jsx464 } from "react/jsx-runtime";
var SvgIconIndentDecreaseRec = (props) => /* @__PURE__ */ _jsx464("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx464("path", {
    d: "M6 18h12m-6-4h6m-6-4h6M6 6h12M8 10l-2 2 2 2m2 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconIndentDecreaseRec_default = SvgIconIndentDecreaseRec;

// src/icons/line/IconIndentDecrease.tsx
import { jsx as _jsx465 } from "react/jsx-runtime";
var SvgIconIndentDecrease = (props) => /* @__PURE__ */ _jsx465("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx465("path", {
    d: "m6 9-3 3 3 3m-3 6h18M11 9h10m-10 6h10M3 3h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconIndentDecrease_default = SvgIconIndentDecrease;

// src/icons/line/IconIndentFirstLineRec.tsx
import { jsx as _jsx466 } from "react/jsx-runtime";
var SvgIconIndentFirstLineRec = (props) => /* @__PURE__ */ _jsx466("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx466("path", {
    d: "M6 18h12m-7-8h7m-7-4h7M6 14h12M6 6l2 2-2 2m4 12h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconIndentFirstLineRec_default = SvgIconIndentFirstLineRec;

// src/icons/line/IconIndentFirstLine.tsx
import { jsx as _jsx467 } from "react/jsx-runtime";
var SvgIconIndentFirstLine = (props) => /* @__PURE__ */ _jsx467("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx467("path", {
    d: "M3 21h18M12 3h9m-9 6h9M4 3l3 3-3 3m-1 6h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconIndentFirstLine_default = SvgIconIndentFirstLine;

// src/icons/line/IconIndentIncreaseRec.tsx
import { jsx as _jsx468 } from "react/jsx-runtime";
var SvgIconIndentIncreaseRec = (props) => /* @__PURE__ */ _jsx468("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx468("path", {
    d: "M6 18h12m-7-4h7m-7-4h7M6 6h12M6 10l2 2-2 2m4 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconIndentIncreaseRec_default = SvgIconIndentIncreaseRec;

// src/icons/line/IconIndentIncrease.tsx
import { jsx as _jsx469 } from "react/jsx-runtime";
var SvgIconIndentIncrease = (props) => /* @__PURE__ */ _jsx469("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx469("path", {
    d: "M3 21h18M11 9h10m-10 6h10M3 3h18M3 9l3 3-3 3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconIndentIncrease_default = SvgIconIndentIncrease;

// src/icons/line/IconInfinityRec.tsx
import { jsx as _jsx470 } from "react/jsx-runtime";
var SvgIconInfinityRec = (props) => /* @__PURE__ */ _jsx470("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx470("path", {
    d: "M14 9.17A3 3 0 1 1 12 12a3 3 0 1 0-2 2.83M11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconInfinityRec_default = SvgIconInfinityRec;

// src/icons/line/IconInfinity.tsx
import { jsx as _jsx471 } from "react/jsx-runtime";
var SvgIconInfinity = (props) => /* @__PURE__ */ _jsx471("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx471("path", {
    d: "M10 16a5 5 0 1 1 2-4 5 5 0 1 0 2-4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconInfinity_default = SvgIconInfinity;

// src/icons/line/IconInfo.tsx
import { jsx as _jsx472 } from "react/jsx-runtime";
var SvgIconInfo = (props) => /* @__PURE__ */ _jsx472("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx472("path", {
    d: "M12 16v-5m.5-3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconInfo_default = SvgIconInfo;

// src/icons/line/IconInstagram.tsx
import { jsx as _jsx473 } from "react/jsx-runtime";
var SvgIconInstagram = (props) => /* @__PURE__ */ _jsx473("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx473("path", {
    d: "M18 6a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-1 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0Zm-6 10h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconInstagram_default = SvgIconInstagram;

// src/icons/line/IconInvoice.tsx
import { jsx as _jsx474 } from "react/jsx-runtime";
var SvgIconInvoice = (props) => /* @__PURE__ */ _jsx474("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx474("path", {
    d: "M18 12V2l-2.713 1.206c-.611.271-.917.407-1.226.441a2 2 0 0 1-.997-.146c-.287-.12-.541-.338-1.05-.774v0c-.674-.578-1.011-.867-1.384-.991a2 2 0 0 0-1.26 0c-.373.124-.71.413-1.385.991v0c-.508.436-.762.653-1.049.774a2 2 0 0 1-.997.146c-.31-.034-.615-.17-1.226-.441L2 2v13.6c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22H20m-2-10h.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 13.52 22 14.08 22 15.2V20a2 2 0 0 1-2 2v0a2 2 0 0 1-2-2v-8Zm-6-2h-2m0 0h-.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3H10m0-6V9m0 7H8m2 0v1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconInvoice_default = SvgIconInvoice;

// src/icons/line/IconKanban.tsx
import { jsx as _jsx475 } from "react/jsx-runtime";
var SvgIconKanban = (props) => /* @__PURE__ */ _jsx475("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx475("path", {
    d: "M2 5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 2 4.08 2 5.2 2h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C10 3.52 10 4.08 10 5.2v13.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C8.48 22 7.92 22 6.8 22H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 20.48 2 19.92 2 18.8V5.2ZM14 5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C15.52 2 16.08 2 17.2 2h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 3.52 22 4.08 22 5.2v5.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 14 19.92 14 18.8 14h-1.6c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C14 12.48 14 11.92 14 10.8V5.2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconKanban_default = SvgIconKanban;

// src/icons/line/IconKey.tsx
import { jsx as _jsx476 } from "react/jsx-runtime";
var SvgIconKey = (props) => /* @__PURE__ */ _jsx476("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx476("path", {
    d: "m20 2-8.5 8.5M14 16a6 6 0 1 1-12 0 6 6 0 0 1 12 0Zm8-10-3.5 3.5-5-1L19 3l3 3Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconKey_default = SvgIconKey;

// src/icons/line/IconKeyboardHide.tsx
import { jsx as _jsx477 } from "react/jsx-runtime";
var SvgIconKeyboardHide = (props) => /* @__PURE__ */ _jsx477("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx477("path", {
    d: "M10 11h4M6 7h.05M10 7h.05M14 7h.05M18 7h.05M18 11h.05M6 11h.05M9 20l.737.737c.792.792 1.188 1.188 1.645 1.337a2 2 0 0 0 1.236 0c.457-.149.853-.545 1.645-1.337L15 20M6.25 7a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm4 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm4 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm4 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm0 4a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm-12 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm2.15 5h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 12.96 22 11.84 22 9.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 2 17.84 2 15.6 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 16 6.16 16 8.4 16Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconKeyboardHide_default = SvgIconKeyboardHide;

// src/icons/line/IconKeyboardShow.tsx
import { jsx as _jsx478 } from "react/jsx-runtime";
var SvgIconKeyboardShow = (props) => /* @__PURE__ */ _jsx478("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx478("path", {
    d: "M10 11h4M6 7h.05M10 7h.05M14 7h.05M18 7h.05M18 11h.05M6 11h.05M9 22l.737-.737c.792-.792 1.188-1.188 1.645-1.337a2 2 0 0 1 1.236 0c.457.149.853.545 1.645 1.337L15 22M6.25 7a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm4 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm4 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm4 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm0 4a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm-12 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm2.15 5h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 12.96 22 11.84 22 9.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 2 17.84 2 15.6 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 16 6.16 16 8.4 16Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconKeyboardShow_default = SvgIconKeyboardShow;

// src/icons/line/IconKeyboard.tsx
import { jsx as _jsx479 } from "react/jsx-runtime";
var SvgIconKeyboard = (props) => /* @__PURE__ */ _jsx479("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx479("path", {
    d: "M10 14h4m-8-4h.05M10 10h.05M14 10h.05M18 10h.05M18 14h.05M6 14h.05m.2-4a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm4 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm4 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm4 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm0 4a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm-12 0a.25.25 0 1 1-.5 0 .25.25 0 0 1 .5 0Zm2.15 5h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 15.96 22 14.84 22 12.6v-1.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 5 17.84 5 15.6 5H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 8.04 2 9.16 2 11.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 19 6.16 19 8.4 19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconKeyboard_default = SvgIconKeyboard;

// src/icons/line/IconLabel4K.tsx
import { jsx as _jsx480 } from "react/jsx-runtime";
var SvgIconLabel4K = (props) => /* @__PURE__ */ _jsx480("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx480("path", {
    d: "M11 13H7l4-4v4Zm0 0v2m2.5-6v4m0 0v2m0-2 .875-1m0 0L17 9m-2.625 3L17 15m-7 7h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLabel4K_default = SvgIconLabel4K;

// src/icons/line/IconLabelExplicit.tsx
import { jsx as _jsx481 } from "react/jsx-runtime";
var SvgIconLabelExplicit = (props) => /* @__PURE__ */ _jsx481("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx481("path", {
    d: "M10 12V9h4m-4 3v3h4m-4-3h4m-4 10h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLabelExplicit_default = SvgIconLabelExplicit;

// src/icons/line/IconLabelHd.tsx
import { jsx as _jsx482 } from "react/jsx-runtime";
var SvgIconLabelHd = (props) => /* @__PURE__ */ _jsx482("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx482("path", {
    d: "M7 9v3m0 0v3m0-3h4m0 0V9m0 3v3m2.5-6v6h1a3 3 0 0 0 3-3v0a3 3 0 0 0-3-3h-1ZM10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLabelHd_default = SvgIconLabelHd;

// src/icons/line/IconLabelHq.tsx
import { jsx as _jsx483 } from "react/jsx-runtime";
var SvgIconLabelHq = (props) => /* @__PURE__ */ _jsx483("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx483("path", {
    d: "M7 9v3m0 0v3m0-3h4m0 0V9m0 3v3m5 0 1 1m-3.5-5v2a2 2 0 1 0 4 0v-2a2 2 0 1 0-4 0ZM10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLabelHq_default = SvgIconLabelHq;

// src/icons/line/IconLaptop.tsx
import { jsx as _jsx484 } from "react/jsx-runtime";
var SvgIconLaptop = (props) => /* @__PURE__ */ _jsx484("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx484("path", {
    d: "M21 17V9.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C17.96 3 16.84 3 14.6 3H9.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C3 6.04 3 7.16 3 9.4V17m1.556 4h14.888A3.556 3.556 0 0 0 23 17.444v0a.444.444 0 0 0-.444-.444H1.444a.444.444 0 0 0-.444.444v0A3.556 3.556 0 0 0 4.556 21Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLaptop_default = SvgIconLaptop;

// src/icons/line/IconLayers.tsx
import { jsx as _jsx485 } from "react/jsx-runtime";
var SvgIconLayers = (props) => /* @__PURE__ */ _jsx485("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx485("path", {
    d: "m22 13-5.707 2.853c-1.574.787-2.361 1.181-3.187 1.336a6 6 0 0 1-2.212 0c-.826-.155-1.613-.549-3.187-1.336L2 13m20 5-5.707 2.853c-1.574.787-2.361 1.181-3.187 1.336a6 6 0 0 1-2.212 0c-.826-.155-1.613-.549-3.187-1.336L2 18m3.724-8.138 3.414 1.707c1.05.525 1.574.787 2.124.89.488.092.988.092 1.476 0 .55-.103 1.075-.365 2.124-.89l3.414-1.707c1.842-.921 2.763-1.382 3.059-2.005a2 2 0 0 0 0-1.714c-.296-.623-1.217-1.084-3.06-2.005L14.863 2.43c-1.05-.525-1.574-.787-2.124-.89a4 4 0 0 0-1.476 0c-.55.103-1.075.365-2.124.89L5.724 4.138c-1.842.921-2.763 1.382-3.059 2.005a2 2 0 0 0 0 1.714c.296.623 1.217 1.084 3.06 2.005Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLayers_default = SvgIconLayers;

// src/icons/line/IconLetterSpacing2Rec.tsx
import { jsx as _jsx486 } from "react/jsx-runtime";
var SvgIconLetterSpacing2Rec = (props) => /* @__PURE__ */ _jsx486("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx486("path", {
    d: "M12 6v6m0-6H9m3 0h3m4 10H5m14 0-2-2m2 2-2 2M5 16l2-2m-2 2 2 2m3 4h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLetterSpacing2Rec_default = SvgIconLetterSpacing2Rec;

// src/icons/line/IconLetterSpacing2.tsx
import { jsx as _jsx487 } from "react/jsx-runtime";
var SvgIconLetterSpacing2 = (props) => /* @__PURE__ */ _jsx487("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx487("path", {
    d: "M12 3v10m0-10H7m5 0h5m5 16H2m20 0-2.5-2.5M22 19l-2.5 2.5M2 19l2.5-2.5M2 19l2.5 2.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLetterSpacing2_default = SvgIconLetterSpacing2;

// src/icons/line/IconLetterSpacingRec.tsx
import { jsx as _jsx488 } from "react/jsx-runtime";
var SvgIconLetterSpacingRec = (props) => /* @__PURE__ */ _jsx488("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx488("path", {
    d: "M6 17V7m12 10V7m-9 8 1.2-2.4m0 0L12 9l1.8 3.6m-3.6 0s.6.6 1.8.6c1.2 0 1.8-.6 1.8-.6m0 0L15 15m-5 7h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLetterSpacingRec_default = SvgIconLetterSpacingRec;

// src/icons/line/IconLetterSpacing.tsx
import { jsx as _jsx489 } from "react/jsx-runtime";
var SvgIconLetterSpacing = (props) => /* @__PURE__ */ _jsx489("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx489("path", {
    d: "M2 21V3m20 18V3M7 17l2-4m0 0 3-6 3 6m-6 0s1 1 3 1 3-1 3-1m0 0 2 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLetterSpacing_default = SvgIconLetterSpacing;

// src/icons/line/IconLifebuoy.tsx
import { jsx as _jsx490 } from "react/jsx-runtime";
var SvgIconLifebuoy = (props) => /* @__PURE__ */ _jsx490("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx490("path", {
    d: "m19.071 4.929.53-.53-.53.53Zm-4.601 3.54a.75.75 0 0 0 1.06 1.061l-1.06-1.06ZM9.53 15.53a.75.75 0 1 0-1.06-1.06l1.06 1.06Zm-1.06-6a.75.75 0 0 0 1.06-1.06L8.47 9.53Zm7.06 4.94a.75.75 0 1 0-1.06 1.06l1.06-1.06ZM12 21.25a9.219 9.219 0 0 1-6.54-2.71l-1.061 1.061A10.719 10.719 0 0 0 12 22.75v-1.5Zm-6.54-2.71A9.219 9.219 0 0 1 2.75 12h-1.5c0 2.968 1.204 5.657 3.149 7.601l1.06-1.06ZM21.25 12a9.218 9.218 0 0 1-2.71 6.54l1.061 1.061A10.719 10.719 0 0 0 22.75 12h-1.5Zm-2.71 6.54A9.218 9.218 0 0 1 12 21.25v1.5c2.968 0 5.657-1.204 7.601-3.149l-1.06-1.06ZM12 2.75a9.219 9.219 0 0 1 6.54 2.71l1.061-1.061A10.719 10.719 0 0 0 12 1.25v1.5Zm6.54 2.71A9.219 9.219 0 0 1 21.25 12h1.5c0-2.968-1.204-5.657-3.149-7.601l-1.06 1.06ZM2.75 12a9.219 9.219 0 0 1 2.71-6.54L4.398 4.398A10.719 10.719 0 0 0 1.25 12h1.5Zm2.71-6.54A9.219 9.219 0 0 1 12 2.75v-1.5a10.719 10.719 0 0 0-7.601 3.149l1.06 1.06Zm13.08-1.061-4.07 4.07 1.06 1.061 4.071-4.07-1.06-1.061ZM8.47 14.469l-4.071 4.072 1.06 1.06 4.071-4.07-1.06-1.061ZM4.399 5.46l4.07 4.071L9.53 8.47 5.46 4.399l-1.061 1.06Zm10.07 10.071 4.072 4.071 1.06-1.06-4.07-4.071-1.061 1.06ZM15.25 12A3.25 3.25 0 0 1 12 15.25v1.5A4.75 4.75 0 0 0 16.75 12h-1.5ZM12 15.25A3.25 3.25 0 0 1 8.75 12h-1.5A4.75 4.75 0 0 0 12 16.75v-1.5ZM8.75 12A3.25 3.25 0 0 1 12 8.75v-1.5A4.75 4.75 0 0 0 7.25 12h1.5ZM12 8.75A3.25 3.25 0 0 1 15.25 12h1.5A4.75 4.75 0 0 0 12 7.25v1.5Z",
    fill: props.color
  })
});
var IconLifebuoy_default = SvgIconLifebuoy;

// src/icons/line/IconLightningSlash.tsx
import { jsx as _jsx491 } from "react/jsx-runtime";
var SvgIconLightningSlash = (props) => /* @__PURE__ */ _jsx491("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx491("path", {
    d: "m3 3 4.333 4.333M21 21l-4.333-4.333m0 0-2.393 2.99c-1.31 1.639-1.966 2.458-2.54 2.549a1.5 1.5 0 0 1-1.342-.47C10 21.305 10 20.255 10 18.157V16.4c0-.84 0-1.26-.163-1.581a1.5 1.5 0 0 0-.656-.655C8.861 14 8.441 14 7.6 14h-.606c-1.599 0-2.398 0-2.809-.328a1.5 1.5 0 0 1-.564-1.174c0-.525.5-1.149 1.498-2.397l2.214-2.768m9.334 9.334L7.333 7.333M9.2 5l.4-.5.126-.157c1.31-1.639 1.966-2.458 2.54-2.549a1.5 1.5 0 0 1 1.342.47c.392.43.392 1.48.392 3.578V7.6c0 .84 0 1.26.164 1.581a1.5 1.5 0 0 0 .655.656c.32.163.74.163 1.581.163h.798c1.433 0 2.15 0 2.534.266.436.301.68.81.643 1.339-.032.467-.48 1.026-1.375 2.145v0l-.2.25",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLightningSlash_default = SvgIconLightningSlash;

// src/icons/line/IconLightning.tsx
import { jsx as _jsx492 } from "react/jsx-runtime";
var SvgIconLightning = (props) => /* @__PURE__ */ _jsx492("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx492("path", {
    d: "m5.12 10.1 4.606-5.757c1.31-1.639 1.966-2.458 2.54-2.549a1.5 1.5 0 0 1 1.342.47c.392.43.392 1.48.392 3.578V7.6c0 .84 0 1.26.164 1.581a1.5 1.5 0 0 0 .655.656c.32.163.74.163 1.581.163h.607c1.598 0 2.397 0 2.808.328a1.5 1.5 0 0 1 .564 1.174c0 .525-.5 1.149-1.498 2.397l-4.607 5.758c-1.31 1.639-1.966 2.458-2.54 2.549a1.5 1.5 0 0 1-1.342-.47C10 21.305 10 20.255 10 18.157V16.4c0-.84 0-1.26-.163-1.581a1.5 1.5 0 0 0-.656-.655C8.861 14 8.441 14 7.6 14h-.606c-1.599 0-2.398 0-2.809-.328a1.5 1.5 0 0 1-.564-1.174c0-.525.5-1.149 1.498-2.397Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLightning_default = SvgIconLightning;

// src/icons/line/IconLineHeight2Rec.tsx
import { jsx as _jsx493 } from "react/jsx-runtime";
var SvgIconLineHeight2Rec = (props) => /* @__PURE__ */ _jsx493("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx493("path", {
    d: "M13 17h5m-5-5h5m-5-5h5M8 6v12M8 6 6 8m2-2 2 2M8 18l-2-2m2 2 2-2m0 6h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLineHeight2Rec_default = SvgIconLineHeight2Rec;

// src/icons/line/IconLineHeight2.tsx
import { jsx as _jsx494 } from "react/jsx-runtime";
var SvgIconLineHeight2 = (props) => /* @__PURE__ */ _jsx494("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx494("path", {
    d: "M12 20h10m-10-8h10M12 4h10M5 3v18M5 3 2 6m3-3 3 3M5 21l-3-3m3 3 3-3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLineHeight2_default = SvgIconLineHeight2;

// src/icons/line/IconLineHeightRec.tsx
import { jsx as _jsx495 } from "react/jsx-runtime";
var SvgIconLineHeightRec = (props) => /* @__PURE__ */ _jsx495("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx495("path", {
    d: "M7 18h10m0-12H7m2 9 1.2-2.4m0 0L12 9l1.8 3.6m-3.6 0s.6.6 1.8.6c1.2 0 1.8-.6 1.8-.6m0 0L15 15m-5 7h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLineHeightRec_default = SvgIconLineHeightRec;

// src/icons/line/IconLineHeight.tsx
import { jsx as _jsx496 } from "react/jsx-runtime";
var SvgIconLineHeight = (props) => /* @__PURE__ */ _jsx496("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx496("path", {
    d: "M3 3h18M3 21h18M7 17l2-4m0 0 3-6 3 6m-6 0s1 1 3 1 3-1 3-1m0 0 2 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLineHeight_default = SvgIconLineHeight;

// src/icons/line/IconLink1Rec.tsx
import { jsx as _jsx497 } from "react/jsx-runtime";
var SvgIconLink1Rec = (props) => /* @__PURE__ */ _jsx497("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx497("path", {
    d: "M13.75 8h.35v0a3.9 3.9 0 0 1 3.9 3.9v.2a3.9 3.9 0 0 1-3.9 3.9v0h-.35m-3.5 0H9.9v0A3.9 3.9 0 0 1 6 12.1v-.2A3.9 3.9 0 0 1 9.9 8v0h.35M10 12h4m-4 10h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLink1Rec_default = SvgIconLink1Rec;

// src/icons/line/IconLink1.tsx
import { jsx as _jsx498 } from "react/jsx-runtime";
var SvgIconLink1 = (props) => /* @__PURE__ */ _jsx498("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx498("path", {
    d: "M14.5 5h.5v0a7 7 0 0 1 7 7v0a7 7 0 0 1-7 7v0h-.5m-5-14H9v0a7 7 0 0 0-7 7v0a7 7 0 0 0 7 7v0h.5M8 12h8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLink1_default = SvgIconLink1;

// src/icons/line/IconLink2Rec.tsx
import { jsx as _jsx499 } from "react/jsx-runtime";
var SvgIconLink2Rec = (props) => /* @__PURE__ */ _jsx499("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx499("path", {
    d: "m12.193 16.499-.474.489a3.278 3.278 0 0 1-4.738 0c-1.308-1.35-1.308-3.538 0-4.888l1.13-1.166a3.026 3.026 0 0 1 4.373 0l.183.188m-.86-3.62.474-.49a3.278 3.278 0 0 1 4.738 0c1.308 1.35 1.308 3.538 0 4.888l-1.13 1.166a3.026 3.026 0 0 1-4.373 0l-.182-.188M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLink2Rec_default = SvgIconLink2Rec;

// src/icons/line/IconLink2.tsx
import { jsx as _jsx500 } from "react/jsx-runtime";
var SvgIconLink2 = (props) => /* @__PURE__ */ _jsx500("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx500("path", {
    d: "m12.29 18.748-.711.734a4.917 4.917 0 0 1-7.107 0c-1.963-2.025-1.963-5.307 0-7.332l1.695-1.748a4.539 4.539 0 0 1 6.56 0l.273.282m-1.29-5.432.711-.734a4.917 4.917 0 0 1 7.107 0c1.963 2.025 1.963 5.307 0 7.332l-1.695 1.748a4.539 4.539 0 0 1-6.56 0L11 13.316",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLink2_default = SvgIconLink2;

// src/icons/line/IconLinkedin.tsx
import { jsx as _jsx501 } from "react/jsx-runtime";
var SvgIconLinkedin = (props) => /* @__PURE__ */ _jsx501("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx501("path", {
    d: "M11.5 18v-4a3 3 0 0 1 3-3v0a3 3 0 0 1 3 3v4m-10 0v-7M8 7.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H7M2 14v-4c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLinkedin_default = SvgIconLinkedin;

// src/icons/line/IconListOrderedRec.tsx
import { jsx as _jsx502 } from "react/jsx-runtime";
var SvgIconListOrderedRec = (props) => /* @__PURE__ */ _jsx502("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx502("path", {
    d: "M7 10V6l-1 .8M7 10H6m1 0h1m-2 3h2v2H6v2h2m3 0h7m-7-5h7m-7-5h7m-8 15h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListOrderedRec_default = SvgIconListOrderedRec;

// src/icons/line/IconListOrdered.tsx
import { jsx as _jsx503 } from "react/jsx-runtime";
var SvgIconListOrdered = (props) => /* @__PURE__ */ _jsx503("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx503("path", {
    d: "M9 20h13M9 12h13M9 4h13M3.5 8V3L2 4m1.5 4H2m1.5 0H5m-3 7h3v2.5H2V20h3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListOrdered_default = SvgIconListOrdered;

// src/icons/line/IconListUnordered2Rec.tsx
import { jsx as _jsx504 } from "react/jsx-runtime";
var SvgIconListUnordered2Rec = (props) => /* @__PURE__ */ _jsx504("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx504("path", {
    d: "M10 17h8m-8-5h8m-8-5h8M7 7v.5H6V7m1 0v-.5H6V7m1 0H6m1 5v.5H6V12m1 0v-.5H6v.5m1 0H6m1 5v.5H6V17m1 0v-.5H6v.5m1 0H6m4 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListUnordered2Rec_default = SvgIconListUnordered2Rec;

// src/icons/line/IconListUnordered2.tsx
import { jsx as _jsx505 } from "react/jsx-runtime";
var SvgIconListUnordered2 = (props) => /* @__PURE__ */ _jsx505("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx505("path", {
    d: "M11 20h11m-11-8h11M11 4h11M3.6 22h.8c.56 0 .84 0 1.054-.109a1 1 0 0 0 .437-.437c.11-.214.11-.494.11-1.054v-.8c0-.56 0-.84-.11-1.054a1 1 0 0 0-.437-.437C5.24 18 4.96 18 4.4 18h-.8c-.56 0-.84 0-1.054.11a1 1 0 0 0-.437.436C2 18.76 2 19.04 2 19.6v.8c0 .56 0 .84.11 1.054a1 1 0 0 0 .436.437c.214.11.494.11 1.054.11Zm0-8h.8c.56 0 .84 0 1.054-.109a1 1 0 0 0 .437-.437c.11-.214.11-.494.11-1.054v-.8c0-.56 0-.84-.11-1.054a1 1 0 0 0-.437-.437C5.24 10 4.96 10 4.4 10h-.8c-.56 0-.84 0-1.054.11a1 1 0 0 0-.437.436C2 10.76 2 11.04 2 11.6v.8c0 .56 0 .84.11 1.054a1 1 0 0 0 .436.437c.214.11.494.11 1.054.11Zm0-8h.8c.56 0 .84 0 1.054-.109a1 1 0 0 0 .437-.437c.11-.214.11-.494.11-1.054v-.8c0-.56 0-.84-.11-1.054a1 1 0 0 0-.437-.437C5.24 2 4.96 2 4.4 2h-.8c-.56 0-.84 0-1.054.11a1 1 0 0 0-.437.436C2 2.76 2 3.04 2 3.6v.8c0 .56 0 .84.11 1.054a1 1 0 0 0 .436.437c.214.11.494.11 1.054.11Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListUnordered2_default = SvgIconListUnordered2;

// src/icons/line/IconListUnordered3.tsx
import { jsx as _jsx506 } from "react/jsx-runtime";
var SvgIconListUnordered3 = (props) => /* @__PURE__ */ _jsx506("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx506("path", {
    d: "M11 20h11m-11-8h11M11 4h11M6 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListUnordered3_default = SvgIconListUnordered3;

// src/icons/line/IconListUnordered4Rec.tsx
import { jsx as _jsx507 } from "react/jsx-runtime";
var SvgIconListUnordered4Rec = (props) => /* @__PURE__ */ _jsx507("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx507("path", {
    d: "M12 17h6m-6-5h6m-6-5h6M6 7l1 1 2-2m-3 6 1 1 2-2m-3 6 1 1 2-2m1 6h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListUnordered4Rec_default = SvgIconListUnordered4Rec;

// src/icons/line/IconListUnordered4.tsx
import { jsx as _jsx508 } from "react/jsx-runtime";
var SvgIconListUnordered4 = (props) => /* @__PURE__ */ _jsx508("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx508("path", {
    d: "M11 20h11m-11-8h11M11 4h11M2 4l1 1 3-3M2 12l1 1 3-3M2 20l1 1 3-3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListUnordered4_default = SvgIconListUnordered4;

// src/icons/line/IconListUnordered5Rec.tsx
import { jsx as _jsx509 } from "react/jsx-runtime";
var SvgIconListUnordered5Rec = (props) => /* @__PURE__ */ _jsx509("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx509("path", {
    d: "M11 17h7m-7-5h7m-7-5h7M6 6l1 1m0 0 1-1M7 7l1 1M7 7 6 8m0 3 1 1m0 0 1-1m-1 1 1 1m-1-1-1 1m0 3 1 1m0 0 1-1m-1 1 1 1m-1-1-1 1m4 4h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListUnordered5Rec_default = SvgIconListUnordered5Rec;

// src/icons/line/IconListUnordered5.tsx
import { jsx as _jsx510 } from "react/jsx-runtime";
var SvgIconListUnordered5 = (props) => /* @__PURE__ */ _jsx510("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx510("path", {
    d: "M3.5 5.5 5 4m0 0 1.5-1.5M5 4 3.5 2.5M5 4l1.5 1.5m-3 8L5 12m0 0 1.5-1.5M5 12l-1.5-1.5M5 12l1.5 1.5m-3 8L5 20m0 0 1.5-1.5M5 20l-1.5-1.5M5 20l1.5 1.5M11 20h11m-11-8h11M11 4h11",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListUnordered5_default = SvgIconListUnordered5;

// src/icons/line/IconListUnorderedRec.tsx
import { jsx as _jsx511 } from "react/jsx-runtime";
var SvgIconListUnorderedRec = (props) => /* @__PURE__ */ _jsx511("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx511("path", {
    d: "M10 17h8m-8-5h8m-8-5h8M7 7a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H6m1 5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H6m1 5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0H6m4 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListUnorderedRec_default = SvgIconListUnorderedRec;

// src/icons/line/IconListUnordered.tsx
import { jsx as _jsx512 } from "react/jsx-runtime";
var SvgIconListUnordered = (props) => /* @__PURE__ */ _jsx512("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx512("path", {
    d: "M9 20h13M9 12h13M9 4h13M3.5 4a.5.5 0 1 1-1 0m1 0a.5.5 0 1 0-1 0m1 0h-1m1 8a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 8a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconListUnordered_default = SvgIconListUnordered;

// src/icons/line/IconLoader2Rec.tsx
import { jsx as _jsx513 } from "react/jsx-runtime";
var SvgIconLoader2Rec = (props) => /* @__PURE__ */ _jsx513("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx513("path", {
    d: "M12 6a6 6 0 1 1-5.376 3.333M11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLoader2Rec_default = SvgIconLoader2Rec;

// src/icons/line/IconLoader2.tsx
import { jsx as _jsx514 } from "react/jsx-runtime";
var SvgIconLoader2 = (props) => /* @__PURE__ */ _jsx514("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx514("path", {
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12c0-1.597.374-3.106 1.04-4.444",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLoader2_default = SvgIconLoader2;

// src/icons/line/IconLoaderRec.tsx
import { jsx as _jsx515 } from "react/jsx-runtime";
var SvgIconLoaderRec = (props) => /* @__PURE__ */ _jsx515("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx515("path", {
    d: "M14.716 9.284 16 8m-8 8 1.284-1.284m0-5.432L8 8m8 8-1.284-1.284M15.994 12H18M6 12h2.006M12 8.006V6m0 12v-2.006M11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLoaderRec_default = SvgIconLoaderRec;

// src/icons/line/IconLoader.tsx
import { jsx as _jsx516 } from "react/jsx-runtime";
var SvgIconLoader = (props) => /* @__PURE__ */ _jsx516("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx516("path", {
    d: "m17 7 2.364-2.364M4.636 19.364 7 17M7 7 4.636 4.636m14.728 14.728L17 17m1.657-5H22M2 12h3.343M12 5.343V2m0 20v-3.343",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLoader_default = SvgIconLoader;

// src/icons/line/IconLoadingTrolley.tsx
import { jsx as _jsx517 } from "react/jsx-runtime";
var SvgIconLoadingTrolley = (props) => /* @__PURE__ */ _jsx517("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx517("path", {
    d: "M22 15H10.163c-1.476 0-2.214 0-2.808-.271a3 3 0 0 1-1.278-1.109c-.353-.55-.457-1.28-.666-2.741L4.354 3.475A2.883 2.883 0 0 0 1.5 1v0H1m10 19a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm10 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm-6.2-5h.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C20 12.72 20 11.88 20 10.2v-.4c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C17.72 5 16.88 5 15.2 5h-.4c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C10 7.28 10 8.12 10 9.8v.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C12.28 15 13.12 15 14.8 15Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLoadingTrolley_default = SvgIconLoadingTrolley;

// src/icons/line/IconLock2Opened.tsx
import { jsx as _jsx518 } from "react/jsx-runtime";
var SvgIconLock2Opened = (props) => /* @__PURE__ */ _jsx518("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx518("path", {
    d: "M7 8V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C9.28 2 10.12 2 11.8 2H15a2 2 0 0 1 2 2v.5M12 14v2m-2.6 6h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C21 18.96 21 17.84 21 15.6v-1.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C17.96 8 16.84 8 14.6 8H9.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C3 11.04 3 12.16 3 14.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C6.04 22 7.16 22 9.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconLock2Opened_default = SvgIconLock2Opened;

// src/icons/line/IconLock2Slash.tsx
import { jsx as _jsx519 } from "react/jsx-runtime";
var SvgIconLock2Slash = (props) => /* @__PURE__ */ _jsx519("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx519("path", {
    d: "M12 8h2c2.809 0 4.213 0 5.222.674.437.292.812.667 1.104 1.104C21 10.787 21 12.19 21 15c0 .764 0 1.424-.014 2M4 4l4.013 4.013M22 22l-1.716-1.716M8.014 8.014c-1.552.035-2.5.168-3.236.66a4 4 0 0 0-1.104 1.104C3 10.787 3 12.19 3 15c0 2.809 0 4.213.674 5.222.292.437.667.812 1.104 1.104C5.787 22 7.19 22 10 22h4c2.809 0 4.213 0 5.222-.674a4 4 0 0 0 1.062-1.042M8.014 8.014l12.27 12.27M17 8V7c0-1.87 0-2.804-.402-3.5A3 3 0 0 0 15.5 2.402C14.804 2 13.87 2 12 2s-2.804 0-3.5.402A3 3 0 0 0 7.402 3.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconLock2Slash_default = SvgIconLock2Slash;

// src/icons/line/IconLock2.tsx
import { jsx as _jsx520 } from "react/jsx-runtime";
var SvgIconLock2 = (props) => /* @__PURE__ */ _jsx520("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx520("path", {
    d: "M7 8V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C9.28 2 10.12 2 11.8 2h.4c1.68 0 2.52 0 3.162.327a3 3 0 0 1 1.311 1.311C17 4.28 17 5.12 17 6.8V8m-5 6v2m-2.6 6h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C21 18.96 21 17.84 21 15.6v-1.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C17.96 8 16.84 8 14.6 8H9.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C3 11.04 3 12.16 3 14.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C6.04 22 7.16 22 9.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconLock2_default = SvgIconLock2;

// src/icons/line/IconLockOpened.tsx
import { jsx as _jsx521 } from "react/jsx-runtime";
var SvgIconLockOpened = (props) => /* @__PURE__ */ _jsx521("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx521("path", {
    d: "M12 13v2M8 7V5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C9.52 2 10.08 2 11.2 2h3.3A1.5 1.5 0 0 1 16 3.5V4m-4 18v0a8 8 0 0 0 8-8v0a8 8 0 0 0-8-8v0a8 8 0 0 0-8 8v0a8 8 0 0 0 8 8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconLockOpened_default = SvgIconLockOpened;

// src/icons/line/IconLockSlash.tsx
import { jsx as _jsx522 } from "react/jsx-runtime";
var SvgIconLockSlash = (props) => /* @__PURE__ */ _jsx522("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx522("path", {
    d: "m3 3 4.432 4.432M22 22l-3.432-3.432M7.432 7.432a8 8 0 1 0 11.137 11.137M7.431 7.431l11.136 11.136M19.86 15.5A7.98 7.98 0 0 0 20 14a8 8 0 0 0-9.5-7.86M16 7V6c0-1.886 0-2.828-.586-3.414C14.83 2 13.886 2 12 2c-1.886 0-2.828 0-3.414.586-.327.326-.471.764-.535 1.414",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLockSlash_default = SvgIconLockSlash;

// src/icons/line/IconLock.tsx
import { jsx as _jsx523 } from "react/jsx-runtime";
var SvgIconLock = (props) => /* @__PURE__ */ _jsx523("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx523("path", {
    d: "M8 7V5.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C9.52 2 10.08 2 11.2 2h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C16 3.52 16 4.08 16 5.2V7m-4 6v2m0 7v0a8 8 0 0 0 8-8v0a8 8 0 0 0-8-8v0a8 8 0 0 0-8 8v0a8 8 0 0 0 8 8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconLock_default = SvgIconLock;

// src/icons/line/IconLogIn2.tsx
import { jsx as _jsx524 } from "react/jsx-runtime";
var SvgIconLogIn2 = (props) => /* @__PURE__ */ _jsx524("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx524("path", {
    d: "M8 8v-.5 0c0-1.396 0-2.093.172-2.661a4 4 0 0 1 2.667-2.667C11.407 2 12.104 2 13.5 2h2.1c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 5.04 22 6.16 22 8.4v7.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22h-2.1c-1.396 0-2.093 0-2.661-.172a4 4 0 0 1-2.667-2.667C8 18.593 8 17.896 8 16.5v0-.5m-6-4h15m0 0-4-4m4 4-4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLogIn2_default = SvgIconLogIn2;

// src/icons/line/IconLogIn3.tsx
import { jsx as _jsx525 } from "react/jsx-runtime";
var SvgIconLogIn3 = (props) => /* @__PURE__ */ _jsx525("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx525("path", {
    d: "M2 12h15m0 0-4-4m4 4-4 4m2 6v0a7 7 0 0 0 7-7V9a7 7 0 0 0-7-7v0a7 7 0 0 0-7 7v6a7 7 0 0 0 7 7Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLogIn3_default = SvgIconLogIn3;

// src/icons/line/IconLogIn.tsx
import { jsx as _jsx526 } from "react/jsx-runtime";
var SvgIconLogIn = (props) => /* @__PURE__ */ _jsx526("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx526("path", {
    d: "M14 2h1.6c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 5.04 22 6.16 22 8.4v7.2c0 2.24 0 3.36-.436 4.216a4 4 0 0 1-1.748 1.748C18.96 22 17.84 22 15.6 22H14M2 12h12m0 0-4-4m4 4-4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLogIn_default = SvgIconLogIn;

// src/icons/line/IconLogOut2.tsx
import { jsx as _jsx527 } from "react/jsx-runtime";
var SvgIconLogOut2 = (props) => /* @__PURE__ */ _jsx527("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx527("path", {
    d: "M14 16v.5c0 1.396 0 2.094-.172 2.662a4 4 0 0 1-2.667 2.666C10.594 22 9.895 22 8.5 22H8c-1.864 0-2.796 0-3.53-.304a4 4 0 0 1-2.166-2.165C2 18.796 2 17.864 2 16.001V8c0-1.864 0-2.796.304-3.531A4 4 0 0 1 4.47 2.305C5.204 2 6.136 2 8 2h.5c1.396 0 2.093 0 2.66.173a4 4 0 0 1 2.668 2.667C14 5.407 14 6.105 14 7.5V8m-5 4h13m0 0-4-4m4 4-4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLogOut2_default = SvgIconLogOut2;

// src/icons/line/IconLogOut3.tsx
import { jsx as _jsx528 } from "react/jsx-runtime";
var SvgIconLogOut3 = (props) => /* @__PURE__ */ _jsx528("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx528("path", {
    d: "M8 12h14m0 0-4-4m4 4-4 4M8 22v0a6 6 0 0 0 6-6V8a6 6 0 0 0-6-6v0a6 6 0 0 0-6 6v8a6 6 0 0 0 6 6Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLogOut3_default = SvgIconLogOut3;

// src/icons/line/IconLogOut.tsx
import { jsx as _jsx529 } from "react/jsx-runtime";
var SvgIconLogOut = (props) => /* @__PURE__ */ _jsx529("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx529("path", {
    d: "M10 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22H10m0-10h12m0 0-4-4m4 4-4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconLogOut_default = SvgIconLogOut;

// src/icons/line/IconMapView.tsx
import { jsx as _jsx530 } from "react/jsx-runtime";
var SvgIconMapView = (props) => /* @__PURE__ */ _jsx530("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx530("path", {
    d: "M8 3v16m8-14v16m-5.184-1.12 2.367.968c1.035.423 1.553.635 2.084.7.472.058.95.031 1.411-.078.521-.124 1.012-.392 1.993-.927l2.078-1.133c.455-.249.683-.373.849-.55a1.5 1.5 0 0 0 .325-.55c.077-.23.077-.49.077-1.007V4.965c0-.977 0-1.465-.205-1.752a1.1 1.1 0 0 0-.762-.452c-.35-.043-.779.191-1.636.659l-.726.396c-.981.535-1.472.803-1.993.926a4 4 0 0 1-1.41.079c-.532-.066-1.05-.277-2.085-.7l-2.367-.969c-1.034-.423-1.552-.635-2.083-.7a4 4 0 0 0-1.411.078c-.521.124-1.012.392-1.993.927L3.25 4.59c-.455.249-.683.373-.849.55a1.5 1.5 0 0 0-.325.55C2 5.92 2 6.18 2 6.696v12.338c0 .977 0 1.465.205 1.752.18.25.456.415.761.452.35.043.78-.191 1.637-.659l.726-.396c.981-.535 1.472-.803 1.993-.926a4 4 0 0 1 1.41-.079c.532.066 1.05.277 2.085.7Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMapView_default = SvgIconMapView;

// src/icons/line/IconMaximize2FlippedRec.tsx
import { jsx as _jsx531 } from "react/jsx-runtime";
var SvgIconMaximize2FlippedRec = (props) => /* @__PURE__ */ _jsx531("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx531("path", {
    d: "M6 6v4m0-4h4M6 6l12 12m0 0v-4m0 4h-4m-4 4h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMaximize2FlippedRec_default = SvgIconMaximize2FlippedRec;

// src/icons/line/IconMaximize2Flipped.tsx
import { jsx as _jsx532 } from "react/jsx-runtime";
var SvgIconMaximize2Flipped = (props) => /* @__PURE__ */ _jsx532("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx532("path", {
    d: "M21 21 3 3m18 18v-6m0 6h-6M3 3v6m0-6h6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMaximize2Flipped_default = SvgIconMaximize2Flipped;

// src/icons/line/IconMaximize2Rec.tsx
import { jsx as _jsx533 } from "react/jsx-runtime";
var SvgIconMaximize2Rec = (props) => /* @__PURE__ */ _jsx533("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx533("path", {
    d: "M18 6v4m0-4h-4m4 0L6 18m0 0v-4m0 4h4m0 4h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMaximize2Rec_default = SvgIconMaximize2Rec;

// src/icons/line/IconMaximize2.tsx
import { jsx as _jsx534 } from "react/jsx-runtime";
var SvgIconMaximize2 = (props) => /* @__PURE__ */ _jsx534("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx534("path", {
    d: "M3 21 21 3M3 21v-6m0 6h6M21 3v6m0-6h-6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMaximize2_default = SvgIconMaximize2;

// src/icons/line/IconMaximize3Rec.tsx
import { jsx as _jsx535 } from "react/jsx-runtime";
var SvgIconMaximize3Rec = (props) => /* @__PURE__ */ _jsx535("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx535("path", {
    d: "M6 14.4v.4c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C7.52 18 8.08 18 9.2 18h.4M6 9.6v-.4c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C7.52 6 8.08 6 9.2 6h.4m4.8 0h.4c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C18 7.52 18 8.08 18 9.2v.4m0 4.8v.4c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C16.48 18 15.92 18 14.8 18h-.4M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMaximize3Rec_default = SvgIconMaximize3Rec;

// src/icons/line/IconMaximize3.tsx
import { jsx as _jsx536 } from "react/jsx-runtime";
var SvgIconMaximize3 = (props) => /* @__PURE__ */ _jsx536("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx536("path", {
    d: "M22 8v-.5 0c0-1.396 0-2.093-.172-2.661a4 4 0 0 0-2.667-2.667C18.593 2 17.896 2 16.5 2v0H16m6 14v.5c0 1.396 0 2.093-.172 2.661a4 4 0 0 1-2.667 2.667C18.593 22 17.896 22 16.5 22v0H16M2 16v.5c0 1.396 0 2.093.172 2.661a4 4 0 0 0 2.667 2.667C5.407 22 6.104 22 7.5 22v0H8M2 8v-.5 0c0-1.396 0-2.093.172-2.661A4 4 0 0 1 4.84 2.172C5.407 2 6.104 2 7.5 2v0H8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMaximize3_default = SvgIconMaximize3;

// src/icons/line/IconMaximizeFlippedRec.tsx
import { jsx as _jsx537 } from "react/jsx-runtime";
var SvgIconMaximizeFlippedRec = (props) => /* @__PURE__ */ _jsx537("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx537("path", {
    d: "M6 6v4m0-4h4M6 6l4 4m8 8v-4m0 4h-4m4 0-4-4m-4 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMaximizeFlippedRec_default = SvgIconMaximizeFlippedRec;

// src/icons/line/IconMaximizeFlipped.tsx
import { jsx as _jsx538 } from "react/jsx-runtime";
var SvgIconMaximizeFlipped = (props) => /* @__PURE__ */ _jsx538("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx538("path", {
    d: "M3 3v6m0-6h6M3 3l6 6m12 12v-6m0 6h-6m6 0-6-6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMaximizeFlipped_default = SvgIconMaximizeFlipped;

// src/icons/line/IconMaximizeRec.tsx
import { jsx as _jsx539 } from "react/jsx-runtime";
var SvgIconMaximizeRec = (props) => /* @__PURE__ */ _jsx539("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx539("path", {
    d: "M18 6v4m0-4h-4m4 0-2.333 2.333L14 10m-8 8v-4m0 4h4m-4 0 2.333-2.333L10 14m0 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMaximizeRec_default = SvgIconMaximizeRec;

// src/icons/line/IconMaximize.tsx
import { jsx as _jsx540 } from "react/jsx-runtime";
var SvgIconMaximize = (props) => /* @__PURE__ */ _jsx540("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx540("path", {
    d: "M3 21v-6m0 6h6m-6 0 3.5-3.5L8 16M21 3v6m0-6h-6m6 0-3.5 3.5L16 8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMaximize_default = SvgIconMaximize;

// src/icons/line/IconMegaphone.tsx
import { jsx as _jsx541 } from "react/jsx-runtime";
var SvgIconMegaphone = (props) => /* @__PURE__ */ _jsx541("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx541("path", {
    d: "M10 7H6a4 4 0 0 0-4 4v0a4 4 0 0 0 4 4h4m0-8v8m0-8 4.965-3.475c1.63-1.141 2.444-1.712 3.122-1.682a2 2 0 0 1 1.5.78C20 3.164 20 4.158 20 6.147V8m-10 7 4.965 3.476c1.63 1.14 2.444 1.71 3.122 1.68a2 2 0 0 0 1.5-.78c.413-.539.413-1.533.413-3.522V14M6 15v4.5A1.5 1.5 0 0 0 7.5 21v0A1.5 1.5 0 0 0 9 19.5V15m11-7v0a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3v0m0-6v6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMegaphone_default = SvgIconMegaphone;

// src/icons/line/IconMemoryCard.tsx
import { jsx as _jsx542 } from "react/jsx-runtime";
var SvgIconMemoryCard = (props) => /* @__PURE__ */ _jsx542("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx542("path", {
    d: "M9 9v2m6-4v4m-3-4v4M5.854 5.775l.9-1.08c.731-.878 1.097-1.317 1.53-1.652a5 5 0 0 1 1.975-.924C10.794 2 11.365 2 12.51 2v0c2.321 0 3.482 0 4.398.378a5 5 0 0 1 2.715 2.715C20 6.009 20 7.17 20 9.49V14c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C16.2 22 14.8 22 12 22v0c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C4 18.2 4 16.8 4 14v-3.104c0-1.072 0-1.609.108-2.12a5 5 0 0 1 .471-1.303c.245-.462.588-.874 1.275-1.698Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMemoryCard_default = SvgIconMemoryCard;

// src/icons/line/IconMenu2Rec.tsx
import { jsx as _jsx543 } from "react/jsx-runtime";
var SvgIconMenu2Rec = (props) => /* @__PURE__ */ _jsx543("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx543("path", {
    d: "M7 8h10M7 12h6m-6 4h8m-3.4 6h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMenu2Rec_default = SvgIconMenu2Rec;

// src/icons/line/IconMenu2.tsx
import { jsx as _jsx544 } from "react/jsx-runtime";
var SvgIconMenu2 = (props) => /* @__PURE__ */ _jsx544("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx544("path", {
    d: "M3 6h18M3 12h8m-8 6h13",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconMenu2_default = SvgIconMenu2;

// src/icons/line/IconMenuRec.tsx
import { jsx as _jsx545 } from "react/jsx-runtime";
var SvgIconMenuRec = (props) => /* @__PURE__ */ _jsx545("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx545("path", {
    d: "M7 8h10M7 12h10M7 16h10m-5.4 6h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMenuRec_default = SvgIconMenuRec;

// src/icons/line/IconMenu.tsx
import { jsx as _jsx546 } from "react/jsx-runtime";
var SvgIconMenu = (props) => /* @__PURE__ */ _jsx546("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx546("path", {
    d: "M3 6h18M3 12h18M3 18h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconMenu_default = SvgIconMenu;

// src/icons/line/IconMessanger.tsx
import { jsx as _jsx547 } from "react/jsx-runtime";
var SvgIconMessanger = (props) => /* @__PURE__ */ _jsx547("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx547("path", {
    d: "m7.255 12.964 3.884-2.913 1.644 2.448 3.962-2.971M2 11.126c0 2.992 1.578 5.626 3.999 7.287C5.998 19.367 6 20.652 6 22l3.923-1.948c.671.129 1.365.2 2.077.2 5.502 0 10-4.064 10-9.126C22 6.063 17.502 2 12 2S2 6.063 2 11.126Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMessanger_default = SvgIconMessanger;

// src/icons/line/IconMicSlash.tsx
import { jsx as _jsx548 } from "react/jsx-runtime";
var SvgIconMicSlash = (props) => /* @__PURE__ */ _jsx548("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx548("path", {
    d: "M12 19c-3.429 0-8-3-8-8m8 8v3m0-3c1.634 0 3.528-.682 5.063-1.937M20 11c0 1.53-.428 2.873-1.12 4M7 7v4a5 5 0 0 0 8 4M3 3l12 12m6 6-3.937-3.937M15 15l2.063 2.063M8 4a5 5 0 0 1 9 3v4c0 .342-.034.677-.1 1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMicSlash_default = SvgIconMicSlash;

// src/icons/line/IconMic.tsx
import { jsx as _jsx549 } from "react/jsx-runtime";
var SvgIconMic = (props) => /* @__PURE__ */ _jsx549("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx549("path", {
    d: "M12 19c3.429 0 8-3 8-8m-8 8c-3.429 0-8-3-8-8m8 8v3m0-6a5 5 0 0 1-5-5V7a5 5 0 0 1 10 0v4a5 5 0 0 1-5 5Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMic_default = SvgIconMic;

// src/icons/line/IconMinimizeRec.tsx
import { jsx as _jsx550 } from "react/jsx-runtime";
var SvgIconMinimizeRec = (props) => /* @__PURE__ */ _jsx550("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx550("path", {
    d: "M10 6v1.6c0 .84 0 1.26-.163 1.581a1.5 1.5 0 0 1-.656.656C8.861 10 8.441 10 7.6 10H6m4 8v-1.6c0-.84 0-1.26-.163-1.581a1.5 1.5 0 0 0-.656-.655C8.861 14 8.441 14 7.6 14H6m8-8v1.6c0 .84 0 1.26.164 1.581a1.5 1.5 0 0 0 .655.656c.32.163.74.163 1.581.163H18m-4 8v-1.6c0-.84 0-1.26.164-1.581a1.5 1.5 0 0 1 .655-.655c.32-.164.74-.164 1.581-.164H18m-8 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMinimizeRec_default = SvgIconMinimizeRec;

// src/icons/line/IconMinimize.tsx
import { jsx as _jsx551 } from "react/jsx-runtime";
var SvgIconMinimize = (props) => /* @__PURE__ */ _jsx551("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx551("path", {
    d: "M8 2v2.8c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C6.48 8 5.92 8 4.8 8H2m6 14v-2.8c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C6.48 16 5.92 16 4.8 16H2M16 2v2.8c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C17.52 8 18.08 8 19.2 8H22m-6 14v-2.8c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C17.52 16 18.08 16 19.2 16H22",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMinimize_default = SvgIconMinimize;

// src/icons/line/IconMiniplayer.tsx
import { jsx as _jsx552, jsxs as _jsxs16 } from "react/jsx-runtime";
var SvgIconMiniplayer = (props) => /* @__PURE__ */ _jsxs16("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx552("path", {
    d: "M2 10c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14v-4Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx552("path", {
    d: "M10 15c0-.932 0-1.398.152-1.765a2 2 0 0 1 1.083-1.083C11.602 12 12.068 12 13 12h2c.932 0 1.398 0 1.765.152a2 2 0 0 1 1.083 1.083C18 13.602 18 14.068 18 15v0c0 .932 0 1.398-.152 1.765a2 2 0 0 1-1.083 1.083C16.398 18 15.932 18 15 18h-2c-.932 0-1.398 0-1.765-.152a2 2 0 0 1-1.083-1.083C10 16.398 10 15.932 10 15v0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconMiniplayer_default = SvgIconMiniplayer;

// src/icons/line/IconMinusCircle.tsx
import { jsx as _jsx553 } from "react/jsx-runtime";
var SvgIconMinusCircle = (props) => /* @__PURE__ */ _jsx553("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx553("path", {
    d: "M8 12h8m6 0c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMinusCircle_default = SvgIconMinusCircle;

// src/icons/line/IconMinusOctagon.tsx
import { jsx as _jsx554 } from "react/jsx-runtime";
var SvgIconMinusOctagon = (props) => /* @__PURE__ */ _jsx554("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx554("path", {
    d: "M8 12h8M3.875 17.875l2.25 2.25c.692.692 1.038 1.038 1.442 1.286a4 4 0 0 0 1.156.479c.46.11.95.11 1.928.11h2.698c.978 0 1.468 0 1.928-.11.408-.099.798-.26 1.156-.48.404-.247.75-.593 1.442-1.285l2.25-2.25c.692-.692 1.038-1.038 1.286-1.442.219-.358.38-.748.479-1.156.11-.46.11-.95.11-1.928v-2.698c0-.978 0-1.468-.11-1.928a4 4 0 0 0-.48-1.156c-.247-.404-.593-.75-1.285-1.442l-2.25-2.25c-.692-.692-1.038-1.038-1.442-1.286a3.998 3.998 0 0 0-1.156-.478C14.817 2 14.327 2 13.349 2h-2.698c-.978 0-1.468 0-1.928.11a4 4 0 0 0-1.156.48c-.404.247-.75.593-1.442 1.285l-2.25 2.25c-.692.692-1.038 1.038-1.286 1.442a4 4 0 0 0-.478 1.156C2 9.183 2 9.673 2 10.651v2.698c0 .978 0 1.468.11 1.928.099.408.26.798.48 1.156.247.404.593.75 1.285 1.442Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMinusOctagon_default = SvgIconMinusOctagon;

// src/icons/line/IconMinusRec.tsx
import { jsx as _jsx555 } from "react/jsx-runtime";
var SvgIconMinusRec = (props) => /* @__PURE__ */ _jsx555("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx555("path", {
    d: "M8 12h8m-4.4 10h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMinusRec_default = SvgIconMinusRec;

// src/icons/line/IconMinus.tsx
import { jsx as _jsx556 } from "react/jsx-runtime";
var SvgIconMinus = (props) => /* @__PURE__ */ _jsx556("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx556("path", {
    d: "M5 12h14",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMinus_default = SvgIconMinus;

// src/icons/line/IconModules.tsx
import { jsx as _jsx557 } from "react/jsx-runtime";
var SvgIconModules = (props) => /* @__PURE__ */ _jsx557("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx557("path", {
    d: "M8 5h4m-7 7v4m14-8v4m-7 7h4m3 3v0a3 3 0 0 0 3-3v0a3 3 0 0 0-3-3v0a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3ZM5 22v0a3 3 0 0 0 3-3v0a3 3 0 0 0-3-3v0a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3ZM19 8v0a3 3 0 0 0 3-3v0a3 3 0 0 0-3-3v0a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3ZM5 8v0a3 3 0 0 0 3-3v0a3 3 0 0 0-3-3v0a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconModules_default = SvgIconModules;

// src/icons/line/IconMonitor.tsx
import { jsx as _jsx558 } from "react/jsx-runtime";
var SvgIconMonitor = (props) => /* @__PURE__ */ _jsx558("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx558("path", {
    d: "M14 18c0 4 2 4 2 4m0 0h1m-1 0H8m-1 0h1m-6-9v.2c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 18 5.12 18 6.8 18H10m-8-5V6.8c0-1.68 0-2.52.327-3.162a3 3 0 0 1 1.311-1.311C4.28 2 5.12 2 6.8 2h10.4c1.68 0 2.52 0 3.162.327a3 3 0 0 1 1.311 1.311C22 4.28 22 5.12 22 6.8V13M2 13h20m0 0v.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C19.72 18 18.88 18 17.2 18H10m-2 4s2-.5 2-4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMonitor_default = SvgIconMonitor;

// src/icons/line/IconMoon.tsx
import { jsx as _jsx559 } from "react/jsx-runtime";
var SvgIconMoon = (props) => /* @__PURE__ */ _jsx559("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx559("path", {
    d: "M3 11.449C3 16.724 7.169 21 12.312 21c3.959 0 7.34-2.534 8.688-6.107a8.074 8.074 0 0 1-3.515.8c-4.571 0-8.277-3.8-8.277-8.489 0-1.961.648-3.767 1.737-5.204C6.45 2.678 3 6.65 3 11.449Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMoon_default = SvgIconMoon;

// src/icons/line/IconMoreHorizRec.tsx
import { jsx as _jsx560 } from "react/jsx-runtime";
var SvgIconMoreHorizRec = (props) => /* @__PURE__ */ _jsx560("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx560("path", {
    d: "M7.25 11.5c.412 0 .75.338.75.75s-.338.75-.75.75m0-1.5a.752.752 0 0 0-.75.75c0 .412.338.75.75.75m0-1.5V13m4.5-1.5c.412 0 .75.338.75.75s-.338.75-.75.75m0-1.5a.752.752 0 0 0-.75.75c0 .412.338.75.75.75m0-1.5V13m5-1.5c.413 0 .75.338.75.75s-.337.75-.75.75m0-1.5a.752.752 0 0 0-.75.75c0 .412.337.75.75.75m0-1.5V13m-5.15 9h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMoreHorizRec_default = SvgIconMoreHorizRec;

// src/icons/line/IconMoreHoriz.tsx
import { jsx as _jsx561 } from "react/jsx-runtime";
var SvgIconMoreHoriz = (props) => /* @__PURE__ */ _jsx561("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx561("path", {
    d: "M4.5 9.5A2.507 2.507 0 0 0 2 12c0 1.375 1.125 2.5 2.5 2.5S7 13.375 7 12 5.875 9.5 4.5 9.5ZM19.5 9.5A2.507 2.507 0 0 0 17 12c0 1.375 1.125 2.5 2.5 2.5S22 13.375 22 12s-1.125-2.5-2.5-2.5ZM12 9.5A2.507 2.507 0 0 0 9.5 12c0 1.375 1.125 2.5 2.5 2.5s2.5-1.125 2.5-2.5-1.125-2.5-2.5-2.5Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconMoreHoriz_default = SvgIconMoreHoriz;

// src/icons/line/IconMoreVertRec.tsx
import { jsx as _jsx562 } from "react/jsx-runtime";
var SvgIconMoreVertRec = (props) => /* @__PURE__ */ _jsx562("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx562("path", {
    d: "M12.75 7.5c0 .412-.338.75-.75.75a.752.752 0 0 1-.75-.75m1.5 0a.752.752 0 0 0-.75-.75.752.752 0 0 0-.75.75m1.5 0h-1.5m1.5 4.5c0 .412-.338.75-.75.75a.752.752 0 0 1-.75-.75m1.5 0a.752.752 0 0 0-.75-.75.752.752 0 0 0-.75.75m1.5 0h-1.5m1.5 5c0 .413-.338.75-.75.75a.752.752 0 0 1-.75-.75m1.5 0a.752.752 0 0 0-.75-.75.752.752 0 0 0-.75.75m1.5 0h-1.5m.35 5h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMoreVertRec_default = SvgIconMoreVertRec;

// src/icons/line/IconMoreVert.tsx
import { jsx as _jsx563 } from "react/jsx-runtime";
var SvgIconMoreVert = (props) => /* @__PURE__ */ _jsx563("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx563("path", {
    d: "M11.5 7C12.875 7 14 5.875 14 4.5S12.875 2 11.5 2A2.507 2.507 0 0 0 9 4.5C9 5.875 10.125 7 11.5 7ZM11.5 9.5A2.507 2.507 0 0 0 9 12c0 1.375 1.125 2.5 2.5 2.5S14 13.375 14 12s-1.125-2.5-2.5-2.5ZM11.5 17A2.507 2.507 0 0 0 9 19.5c0 1.375 1.125 2.5 2.5 2.5s2.5-1.125 2.5-2.5-1.125-2.5-2.5-2.5Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconMoreVert_default = SvgIconMoreVert;

// src/icons/line/IconMouse.tsx
import { jsx as _jsx564 } from "react/jsx-runtime";
var SvgIconMouse = (props) => /* @__PURE__ */ _jsx564("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx564("path", {
    d: "M12 8v2m0 12v0a8 8 0 0 0 8-8v-4a8 8 0 0 0-8-8v0a8 8 0 0 0-8 8v4a8 8 0 0 0 8 8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMouse_default = SvgIconMouse;

// src/icons/line/IconMusicLibrary.tsx
import { jsx as _jsx565 } from "react/jsx-runtime";
var SvgIconMusicLibrary = (props) => /* @__PURE__ */ _jsx565("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx565("path", {
    d: "M5 6v0a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3v0M19 6v0a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3v0m-6-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 0V7l3 2m-4.6 13h1.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C19 18.96 19 17.84 19 15.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C15.96 2 14.84 2 12.6 2h-1.2c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C5 5.04 5 6.16 5 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C8.04 22 9.16 22 11.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMusicLibrary_default = SvgIconMusicLibrary;

// src/icons/line/IconMusicNote2.tsx
import { jsx as _jsx566 } from "react/jsx-runtime";
var SvgIconMusicNote2 = (props) => /* @__PURE__ */ _jsx566("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx566("path", {
    d: "M9 19V9m0 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm12-3V6m0 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM9 9V7.358C9 6.453 9 6 9.16 5.625a2 2 0 0 1 .657-.83c.328-.241.769-.345 1.65-.552l5.64-1.327c1.306-.307 1.96-.461 2.475-.304a2 2 0 0 1 1.148.909C21 3.987 21 4.658 21 6v0M9 9l12-3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMusicNote2_default = SvgIconMusicNote2;

// src/icons/line/IconMusicNote3.tsx
import { jsx as _jsx567 } from "react/jsx-runtime";
var SvgIconMusicNote3 = (props) => /* @__PURE__ */ _jsx567("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx567("path", {
    d: "M9 19v-8m0 8a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm0-8V6.724c0-.679 0-1.018.12-1.3a1.5 1.5 0 0 1 .493-.622c.246-.18.576-.258 1.237-.414l7.2-1.694c1.007-.237 1.511-.355 1.907-.229a1.5 1.5 0 0 1 .83.657C21 3.478 21 3.996 21 5.03v1.096c0 .67 0 1.005-.117 1.284a1.5 1.5 0 0 1-.484.62c-.242.18-.567.262-1.217.424L9 11Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMusicNote3_default = SvgIconMusicNote3;

// src/icons/line/IconMusicNote.tsx
import { jsx as _jsx568 } from "react/jsx-runtime";
var SvgIconMusicNote = (props) => /* @__PURE__ */ _jsx568("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx568("path", {
    d: "M9 19V7.358C9 6.453 9 6 9.16 5.625a2 2 0 0 1 .657-.83c.328-.241.769-.345 1.65-.552l5.6-1.318c1.343-.316 2.014-.474 2.542-.305a2 2 0 0 1 1.106.876C21 3.971 21 4.661 21 6.04V16M9 19a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm12-3a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMusicNote_default = SvgIconMusicNote;

// src/icons/line/IconMusicPlus.tsx
import { jsx as _jsx569 } from "react/jsx-runtime";
var SvgIconMusicPlus = (props) => /* @__PURE__ */ _jsx569("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx569("path", {
    d: "M13.5 4H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 7.04 2 8.16 2 10.4v5.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C20 18.96 20 17.84 20 15.6v-5.1M12 16a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 0V8l3 2m5-9v3m0 0v3m0-3h-3m3 0h3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMusicPlus_default = SvgIconMusicPlus;

// src/icons/line/IconMusicQueue.tsx
import { jsx as _jsx570 } from "react/jsx-runtime";
var SvgIconMusicQueue = (props) => /* @__PURE__ */ _jsx570("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx570("path", {
    d: "M19 6v12m3-10v8m-12-1a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 0V7l3 2M8.4 22h1.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C16 18.96 16 17.84 16 15.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C12.96 2 11.84 2 9.6 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconMusicQueue_default = SvgIconMusicQueue;

// src/icons/line/IconNewRelease.tsx
import { jsx as _jsx571 } from "react/jsx-runtime";
var SvgIconNewRelease = (props) => /* @__PURE__ */ _jsx571("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx571("path", {
    d: "M12 13V8m.5 8a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M7.612 4H6.366c-.84 0-1.26 0-1.581.163a1.5 1.5 0 0 0-.656.656c-.163.32-.163.74-.163 1.581v1.22c0 .326 0 .489-.033.644a1.5 1.5 0 0 1-.145.395c-.075.14-.18.264-.391.512l-1.082 1.277c-.467.552-.7.827-.79 1.136a1.5 1.5 0 0 0 0 .832c.09.309.323.584.79 1.136l1.082 1.277c.21.248.316.372.39.512a1.5 1.5 0 0 1 .146.395c.033.155.033.318.033.644v1.22c0 .84 0 1.26.163 1.581a1.5 1.5 0 0 0 .656.655c.32.164.74.164 1.58.164h1.247c.329 0 .493 0 .65.034.138.03.272.08.397.147.14.076.266.183.515.397l1.264 1.083c.554.475.832.713 1.143.803.274.08.565.08.838 0 .311-.09.588-.328 1.143-.803l1.264-1.083c.25-.214.374-.321.515-.397.125-.068.259-.117.397-.147.157-.034.321-.034.65-.034H17.6c.84 0 1.26 0 1.581-.163a1.5 1.5 0 0 0 .655-.656c.164-.32.164-.74.164-1.581v-1.212c0-.329 0-.493.034-.65.03-.138.08-.272.147-.397.076-.14.183-.266.397-.515l1.083-1.264c.475-.554.713-.832.803-1.143a1.5 1.5 0 0 0 0-.838c-.09-.311-.328-.588-.803-1.143l-1.083-1.264c-.214-.25-.321-.374-.397-.515a1.497 1.497 0 0 1-.147-.397C20 8.105 20 7.94 20 7.612V6.4c0-.84 0-1.26-.163-1.581a1.5 1.5 0 0 0-.656-.656C18.861 4 18.441 4 17.6 4h-1.212c-.329 0-.493 0-.65-.034a1.498 1.498 0 0 1-.397-.147c-.14-.076-.266-.183-.515-.397L13.562 2.34c-.554-.475-.832-.713-1.143-.804a1.5 1.5 0 0 0-.838 0c-.311.091-.588.329-1.143.804L9.174 3.422c-.25.214-.374.321-.515.397a1.5 1.5 0 0 1-.397.147C8.105 4 7.94 4 7.612 4Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconNewRelease_default = SvgIconNewRelease;

// src/icons/line/IconNewspaper.tsx
import { jsx as _jsx572 } from "react/jsx-runtime";
var SvgIconNewspaper = (props) => /* @__PURE__ */ _jsx572("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx572("path", {
    d: "M18 12V2l-2.713 1.206c-.611.271-.917.407-1.226.441a2 2 0 0 1-.997-.146c-.287-.12-.541-.338-1.05-.774v0c-.674-.578-1.011-.867-1.384-.991a2 2 0 0 0-1.26 0c-.373.124-.71.413-1.385.991v0c-.508.436-.762.653-1.049.774a2 2 0 0 1-.997.146c-.31-.034-.615-.17-1.226-.441L2 2v13.6c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22H20m-2-10h.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 13.52 22 14.08 22 15.2V20a2 2 0 0 1-2 2v0a2 2 0 0 1-2-2v-8ZM7 17h6M7 9h4m-4 4h6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconNewspaper_default = SvgIconNewspaper;

// src/icons/line/IconNote2Text.tsx
import { jsx as _jsx573 } from "react/jsx-runtime";
var SvgIconNote2Text = (props) => /* @__PURE__ */ _jsx573("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx573("path", {
    d: "M7 9h10M7 13h6m-6 4h2M8 1v3m4-3v3m4-3v3m-4.4 18h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconNote2Text_default = SvgIconNote2Text;

// src/icons/line/IconNote2.tsx
import { jsx as _jsx574 } from "react/jsx-runtime";
var SvgIconNote2 = (props) => /* @__PURE__ */ _jsx574("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx574("path", {
    d: "M8 1v3m4-3v3m4-3v3m-4.4 18h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconNote2_default = SvgIconNote2;

// src/icons/line/IconNoteText.tsx
import { jsx as _jsx575 } from "react/jsx-runtime";
var SvgIconNoteText = (props) => /* @__PURE__ */ _jsx575("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx575("path", {
    d: "M16 21.5v-2.3c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C17.52 16 18.08 16 19.2 16h2.3M7 7h8m-8 4h6m-6 4h2m6.172 7H11.6c-3.36 0-5.04 0-6.324-.654a6 6 0 0 1-2.622-2.622C2 17.44 2 15.76 2 12.4v-.8c0-3.36 0-5.04.654-6.324a6 6 0 0 1 2.622-2.622C6.56 2 8.24 2 11.6 2h.8c3.36 0 5.04 0 6.324.654a6 6 0 0 1 2.622 2.622C22 6.56 22 8.24 22 11.6v3.572c0 .337 0 .505-.009.668a6 6 0 0 1-1.525 3.681c-.108.121-.228.24-.466.479v0c-.238.238-.358.358-.479.466a6 6 0 0 1-3.681 1.525c-.163.009-.331.009-.668.009Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconNoteText_default = SvgIconNoteText;

// src/icons/line/IconNote.tsx
import { jsx as _jsx576 } from "react/jsx-runtime";
var SvgIconNote = (props) => /* @__PURE__ */ _jsx576("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx576("path", {
    d: "M16 21.5v-2.3c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C17.52 16 18.08 16 19.2 16h2.3m-6.328 6H11.6c-3.36 0-5.04 0-6.324-.654a6 6 0 0 1-2.622-2.622C2 17.44 2 15.76 2 12.4v-.8c0-3.36 0-5.04.654-6.324a6 6 0 0 1 2.622-2.622C6.56 2 8.24 2 11.6 2h.8c3.36 0 5.04 0 6.324.654a6 6 0 0 1 2.622 2.622C22 6.56 22 8.24 22 11.6v3.572c0 .337 0 .505-.009.668a6 6 0 0 1-1.525 3.681c-.108.121-.228.24-.466.479v0c-.238.238-.358.358-.479.466a6 6 0 0 1-3.681 1.525c-.163.009-.331.009-.668.009Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconNote_default = SvgIconNote;

// src/icons/line/IconNotebook.tsx
import { jsx as _jsx577 } from "react/jsx-runtime";
var SvgIconNotebook = (props) => /* @__PURE__ */ _jsx577("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx577("path", {
    d: "M16 7v4M6 16H2m4-4H2m4-4H2m10 14v0c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C20 18.2 20 16.8 20 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C16.2 2 14.8 2 12 2v0c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 4.545 4.73C4 5.8 4 7.2 4 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C7.8 22 9.2 22 12 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconNotebook_default = SvgIconNotebook;

// src/icons/line/IconNotificationIndicator.tsx
import { jsx as _jsx578 } from "react/jsx-runtime";
var SvgIconNotificationIndicator = (props) => /* @__PURE__ */ _jsx578("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx578("path", {
    d: "M12 3h-2c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 5.73C2 6.8 2 8.2 2 11v3c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22h3c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C21 18.2 21 16.8 21 14v-2m1-7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconNotificationIndicator_default = SvgIconNotificationIndicator;

// src/icons/line/IconNotion.tsx
import { jsx as _jsx579 } from "react/jsx-runtime";
var SvgIconNotion = (props) => /* @__PURE__ */ _jsx579("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx579("path", {
    d: "M5.5 6v15m0-15L3 3.5M5.5 6 20 5.094l.75-.047M9.125 9.375l1.25-.078m0 0 7.125 8.547V8.812m-7.125.485v9.031M17.5 8.813l1-.063m-1 .063-1 .062m-6.125 9.453-.875.055m.875-.055 1.125-.07M2 16.402V5.542c0-1.04 0-1.561.197-1.971a2 2 0 0 1 .796-.864c.393-.229.912-.27 1.95-.354l9.555-.772c.372-.03.558-.045.74-.027.162.017.321.053.474.108.172.062.334.156.656.344l1.957 1.142c1.157.675 1.735 1.012 2.156 1.482.372.416.653.905.825 1.437.194.6.194 1.27.194 2.609v9.318c0 1.058 0 1.587-.201 2.002a2 2 0 0 1-.814.866c-.4.227-.929.26-1.985.326l-9.625.601c-1.235.077-1.852.116-2.424-.018a4 4 0 0 1-1.41-.635c-.478-.34-.858-.829-1.617-1.805l-.75-.964c-.25-.322-.375-.482-.464-.66a2 2 0 0 1-.17-.5C2 17.014 2 16.81 2 16.403Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconNotion_default = SvgIconNotion;

// src/icons/line/IconNut.tsx
import { jsx as _jsx580, jsxs as _jsxs17 } from "react/jsx-runtime";
var SvgIconNut = (props) => /* @__PURE__ */ _jsxs17("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx580("path", {
    d: "M5.107 18.864c-1.133-.68-1.7-1.02-2.111-1.487a4 4 0 0 1-.807-1.425C2 15.358 2 14.698 2 13.376v-2.752c0-1.322 0-1.982.19-2.576a4 4 0 0 1 .807-1.425c.411-.468.978-.808 2.11-1.487l3.6-2.16c1.198-.719 1.797-1.078 2.436-1.218a4 4 0 0 1 1.716 0c.639.14 1.237.5 2.434 1.218l3.6 2.16c1.134.68 1.7 1.02 2.111 1.487.365.415.64.9.807 1.425.19.594.19 1.254.19 2.576v2.752c0 1.322 0 1.982-.19 2.576a4 4 0 0 1-.806 1.425c-.412.468-.979.808-2.112 1.487l-3.6 2.16c-1.197.719-1.795 1.078-2.434 1.218a3.998 3.998 0 0 1-1.716 0c-.64-.14-1.238-.5-2.435-1.218l-3.6-2.16Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx580("path", {
    d: "M9 12a3 3 0 1 1 6 0 3 3 0 0 1-6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconNut_default = SvgIconNut;

// src/icons/line/IconPadding.tsx
import { jsx as _jsx581 } from "react/jsx-runtime";
var SvgIconPadding = (props) => /* @__PURE__ */ _jsx581("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx581("path", {
    d: "M6 12h12M6 12v-2m0 2v2m12-2v2m0-2v-2m-6-4v12m0-12h-2m2 0h2m-2 12h2m-2 0h-2m-1.6 4h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 2 17.84 2 15.6 2H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 5.04 2 6.16 2 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPadding_default = SvgIconPadding;

// src/icons/line/IconPageBreak.tsx
import { jsx as _jsx582 } from "react/jsx-runtime";
var SvgIconPageBreak = (props) => /* @__PURE__ */ _jsx582("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx582("path", {
    d: "M21 2v1.8c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C19.48 7 18.92 7 17.8 7H6.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C3 5.48 3 4.92 3 3.8V2m18 20v-1.8c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C19.48 17 18.92 17 17.8 17H6.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C3 18.52 3 19.08 3 20.2V22m1.5-10h-1m5 0h-1m5 0h-1m5 0h-1m5 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPageBreak_default = SvgIconPageBreak;

// src/icons/line/IconPaintBucket.tsx
import { jsx as _jsx583 } from "react/jsx-runtime";
var SvgIconPaintBucket = (props) => /* @__PURE__ */ _jsx583("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx583("path", {
    d: "M2 11c5.175-3.293 10.5 8.5 15 4l1.5-1.5m-.5 6.055c0-1.14.604-2.1 1.148-2.735.177-.205.265-.308.514-.398a1.34 1.34 0 0 1 .676 0c.249.09.337.192.514.398.544.635 1.148 1.596 1.148 2.735 0 3.26-4 3.26-4 0Zm-3.475-2.08 2.95-2.95c1.584-1.584 2.376-2.376 2.672-3.289a4 4 0 0 0 0-2.472c-.296-.913-1.088-1.705-2.672-3.29l-.95-.949c-1.584-1.584-2.376-2.376-3.289-2.672a4 4 0 0 0-2.472 0c-.913.296-1.705 1.088-3.29 2.672l-2.949 2.95C2.941 9.059 2.15 9.85 1.853 10.764a4 4 0 0 0 0 2.472c.296.913 1.088 1.705 2.672 3.29l.95.948c1.584 1.585 2.376 2.377 3.289 2.673a4 4 0 0 0 2.472 0c.913-.296 1.705-1.088 3.29-2.672Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPaintBucket_default = SvgIconPaintBucket;

// src/icons/line/IconPaintTool.tsx
import { jsx as _jsx584 } from "react/jsx-runtime";
var SvgIconPaintTool = (props) => /* @__PURE__ */ _jsx584("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx584("path", {
    d: "M17 5h.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C21 6.52 21 7.08 21 8.2v.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C19.48 12 18.92 12 17.8 12h-3.6c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C11 13.52 11 14.08 11 15.2v.8m0 6v0a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2a2 2 0 0 0 2 2ZM8 8h6c.932 0 1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C17 6.398 17 5.932 17 5v0c0-.932 0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C15.398 2 14.932 2 14 2H8c-.932 0-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C5 3.602 5 4.068 5 5v0c0 .932 0 1.398.152 1.765a2 2 0 0 0 1.083 1.083C6.602 8 7.068 8 8 8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPaintTool_default = SvgIconPaintTool;

// src/icons/line/IconPaperclip2Rec.tsx
import { jsx as _jsx585 } from "react/jsx-runtime";
var SvgIconPaperclip2Rec = (props) => /* @__PURE__ */ _jsx585("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx585("path", {
    d: "M17 8v5.5a4.5 4.5 0 0 1-4.5 4.5v0A4.5 4.5 0 0 1 8 13.5V9a3 3 0 0 1 3-3v0a3 3 0 0 1 3 3v4.5a1.5 1.5 0 0 1-1.5 1.5v0a1.5 1.5 0 0 1-1.5-1.5V9m-1 13h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPaperclip2Rec_default = SvgIconPaperclip2Rec;

// src/icons/line/IconPaperclip2SlashRec.tsx
import { jsx as _jsx586 } from "react/jsx-runtime";
var SvgIconPaperclip2SlashRec = (props) => /* @__PURE__ */ _jsx586("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx586("path", {
    d: "M17 8v5.5m-.857 2.643L18 18m-1.857-1.857A4.5 4.5 0 0 1 8 13.5V9a3 3 0 0 1 .129-.871m8.014 8.014-2.207-2.207m0 0A1.5 1.5 0 0 1 11 13.5V11m2.936 2.936L11 11m0 0L8.129 8.129M6 6l2.129 2.129M10 6.17A3 3 0 0 1 14 9v1.499M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPaperclip2SlashRec_default = SvgIconPaperclip2SlashRec;

// src/icons/line/IconPaperclip2Slash.tsx
import { jsx as _jsx587 } from "react/jsx-runtime";
var SvgIconPaperclip2Slash = (props) => /* @__PURE__ */ _jsx587("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx587("path", {
    d: "M20 5v9.5c0 .34-.023.673-.066 1M2 2l3.297 3.297M22 22l-3.291-3.291M5.297 5.297A4.991 4.991 0 0 0 5 7v7.5a7.5 7.5 0 0 0 13.709 4.209M5.297 5.297 10 10m8.709 8.709-3.75-3.75M15 11V7a5 5 0 0 0-8-4m7.958 11.958A2.5 2.5 0 0 1 10 14.5V10m4.958 4.958L10 10",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPaperclip2Slash_default = SvgIconPaperclip2Slash;

// src/icons/line/IconPaperclip2.tsx
import { jsx as _jsx588 } from "react/jsx-runtime";
var SvgIconPaperclip2 = (props) => /* @__PURE__ */ _jsx588("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx588("path", {
    d: "M20 5v9.5a7.5 7.5 0 0 1-7.5 7.5v0A7.5 7.5 0 0 1 5 14.5V7a5 5 0 0 1 5-5v0a5 5 0 0 1 5 5v7.5a2.5 2.5 0 0 1-2.5 2.5v0a2.5 2.5 0 0 1-2.5-2.5V7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPaperclip2_default = SvgIconPaperclip2;

// src/icons/line/IconPaperclipRec.tsx
import { jsx as _jsx589 } from "react/jsx-runtime";
var SvgIconPaperclipRec = (props) => /* @__PURE__ */ _jsx589("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx589("path", {
    d: "m19 12-4.5 4.5a4.95 4.95 0 0 1-7 0v0a4.95 4.95 0 0 1 0-7l3.208-3.208a3.241 3.241 0 0 1 4.584 0v0a3.24 3.24 0 0 1 .083 4.497l-3.15 3.392c-.669.72-1.803.741-2.498.046v0a1.735 1.735 0 0 1 0-2.454L13 8.5M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPaperclipRec_default = SvgIconPaperclipRec;

// src/icons/line/IconPaperclipSlashRec.tsx
import { jsx as _jsx590 } from "react/jsx-runtime";
var SvgIconPaperclipSlashRec = (props) => /* @__PURE__ */ _jsx590("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx590("path", {
    d: "m16 15-1.5 1.5a4.95 4.95 0 1 1-7-7L9 8m7 7-2.352-2.352M16 15l2 2M9 8 7 6m2 2 2.25 2.25m2.398 2.398-1.423 1.532a1.735 1.735 0 1 1-2.498-2.407l1.523-1.523m2.398 2.398L11.25 10.25M19 12l-1 1m-2.435-2.435a3.241 3.241 0 0 0-4.547-4.547M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPaperclipSlashRec_default = SvgIconPaperclipSlashRec;

// src/icons/line/IconPaperclipSlash.tsx
import { jsx as _jsx591 } from "react/jsx-runtime";
var SvgIconPaperclipSlash = (props) => /* @__PURE__ */ _jsx591("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx591("path", {
    d: "m22 12-3 3m-6-8-1 1M3 3l7 7m11 11-4-4m-7-7-1.75 1.75a2.475 2.475 0 0 0 0 3.5v0a2.475 2.475 0 0 0 3.5 0l1.75-1.75M10 10l3.5 3.5M6.25 6.75l-1.5 1.5c-2.9 2.9-2.9 7.6 0 10.5v0c2.9 2.9 7.6 2.9 10.5 0L17 17m-3.5-3.5L17 17m-1.5-5.5 1-1a4.95 4.95 0 0 0 0-7v0a4.95 4.95 0 0 0-7 0l-1 1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPaperclipSlash_default = SvgIconPaperclipSlash;

// src/icons/line/IconPaperclip.tsx
import { jsx as _jsx592 } from "react/jsx-runtime";
var SvgIconPaperclip = (props) => /* @__PURE__ */ _jsx592("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx592("path", {
    d: "m22 12-6.75 6.75c-2.9 2.9-7.6 2.9-10.5 0v0c-2.9-2.9-2.9-7.6 0-10.5L9.5 3.5a4.95 4.95 0 0 1 7 0v0a4.95 4.95 0 0 1 0 7l-4.75 4.75a2.475 2.475 0 0 1-3.5 0v0a2.475 2.475 0 0 1 0-3.5L13 7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPaperclip_default = SvgIconPaperclip;

// src/icons/line/IconPauseCircle.tsx
import { jsx as _jsx593 } from "react/jsx-runtime";
var SvgIconPauseCircle = (props) => /* @__PURE__ */ _jsx593("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx593("path", {
    d: "M10 9.5v5m4-5v5m8-2.5c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPauseCircle_default = SvgIconPauseCircle;

// src/icons/line/IconPause.tsx
import { jsx as _jsx594 } from "react/jsx-runtime";
var SvgIconPause = (props) => /* @__PURE__ */ _jsx594("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx594("path", {
    d: "M3 6.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C4.52 3 5.08 3 6.2 3h.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C10 4.52 10 5.08 10 6.2v11.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C8.48 21 7.92 21 6.8 21h-.6c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C3 19.48 3 18.92 3 17.8V6.2ZM14 6.2c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C15.52 3 16.08 3 17.2 3h.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C21 4.52 21 5.08 21 6.2v11.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C19.48 21 18.92 21 17.8 21h-.6c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C14 19.48 14 18.92 14 17.8V6.2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPause_default = SvgIconPause;

// src/icons/line/IconPenTool2.tsx
import { jsx as _jsx595 } from "react/jsx-runtime";
var SvgIconPenTool2 = (props) => /* @__PURE__ */ _jsx595("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx595("path", {
    d: "m3.5 3.5 4 4M13.999 19l5-5v-3.63c0-1.91 0-2.864-.346-3.64a4 4 0 0 0-1.417-1.69c-.705-.475-1.645-.641-3.525-.973l-7.21-1.273c-1.515-.267-2.272-.4-2.817-.169a2 2 0 0 0-1.06 1.06c-.231.545-.098 1.302.17 2.817l1.272 7.21c.332 1.88.498 2.82.973 3.525a4 4 0 0 0 1.69 1.417c.776.346 1.73.346 3.64.346h3.63ZM13 11a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm-.001 9 6.5-6.5a1.414 1.414 0 0 1 2 0v0a1.414 1.414 0 0 1 0 2l-6.5 6.5a1.414 1.414 0 0 1-2 0v0a1.414 1.414 0 0 1 0-2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPenTool2_default = SvgIconPenTool2;

// src/icons/line/IconPenTool.tsx
import { jsx as _jsx596 } from "react/jsx-runtime";
var SvgIconPenTool = (props) => /* @__PURE__ */ _jsx596("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx596("path", {
    d: "m8 19-2.026-2.026c-1.408-1.408-2.112-2.112-2.415-2.936a4 4 0 0 1-.148-2.269c.195-.857.802-1.645 2.015-3.223l4.038-5.249c.863-1.122 1.294-1.683 1.82-1.884a2 2 0 0 1 1.432 0c.526.201.957.762 1.82 1.884l4.038 5.249c1.213 1.578 1.82 2.366 2.015 3.223a4 4 0 0 1-.148 2.27c-.303.823-1.007 1.527-2.415 2.935L16 19M12 2v5m2 5a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM7.5 22h9a1.5 1.5 0 0 0 1.5-1.5v0a1.5 1.5 0 0 0-1.5-1.5h-9A1.5 1.5 0 0 0 6 20.5v0A1.5 1.5 0 0 0 7.5 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPenTool_default = SvgIconPenTool;

// src/icons/line/IconPharagraphSpacingRec.tsx
import { jsx as _jsx597 } from "react/jsx-runtime";
var SvgIconPharagraphSpacingRec = (props) => /* @__PURE__ */ _jsx597("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx597("path", {
    d: "M17 6H7m5 2.5v7m0-7L10.5 10M12 8.5l1.5 1.5M12 15.5 10.5 14m1.5 1.5 1.5-1.5m3.5 4H7m3 4h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPharagraphSpacingRec_default = SvgIconPharagraphSpacingRec;

// src/icons/line/IconPharagraphSpacing.tsx
import { jsx as _jsx598 } from "react/jsx-runtime";
var SvgIconPharagraphSpacing = (props) => /* @__PURE__ */ _jsx598("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx598("path", {
    d: "M21 3H3m9 4v10m0-10L9.5 9.5M12 7l2.5 2.5M12 17l-2.5-2.5M12 17l2.5-2.5M21 21H3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPharagraphSpacing_default = SvgIconPharagraphSpacing;

// src/icons/line/IconPhone.tsx
import { jsx as _jsx599 } from "react/jsx-runtime";
var SvgIconPhone = (props) => /* @__PURE__ */ _jsx599("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx599("path", {
    d: "M4.793 13c-1.351-2.29-2.245-4.953-2.609-7.919-.106-.869-.16-1.303-.004-1.745.16-.455.565-.913.998-1.128C3.598 2 4.095 2 5.088 2v0c.81 0 1.215 0 1.556.129a2 2 0 0 1 .939.732c.207.3.305.693.502 1.478l.122.49c.29 1.161.436 1.742.414 2.312a4 4 0 0 1-.338 1.464c-.231.522-.617.98-1.388 1.896L4.793 13Zm0 0a17.45 17.45 0 0 0 5.865 6m0 0c2.364 1.467 5.146 2.434 8.26 2.816.87.106 1.304.16 1.746.004.455-.16.913-.565 1.127-.997.209-.42.209-.928.209-1.942v0c0-.79 0-1.184-.129-1.526a2 2 0 0 0-.732-.938c-.3-.207-.693-.306-1.479-.502l-.708-.177c-1.084-.271-1.627-.407-2.162-.395a4 4 0 0 0-1.385.279c-.499.197-.946.532-1.84 1.202L10.657 19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPhone_default = SvgIconPhone;

// src/icons/line/IconPhotoshop.tsx
import { jsx as _jsx600 } from "react/jsx-runtime";
var SvgIconPhotoshop = (props) => /* @__PURE__ */ _jsx600("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx600("path", {
    d: "M7 16v-3m0 0V8h2.5a2.5 2.5 0 0 1 0 5H7Zm10.555-1.583A1.57 1.57 0 0 0 16.483 11h-.79c-.69 0-1.248.524-1.248 1.17 0 .537.39 1.005.945 1.135l1.665.39c.555.13.945.598.945 1.135 0 .646-.559 1.17-1.248 1.17h-1.314c-.609 0-1.166-.323-1.438-.833M2 14v-4c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPhotoshop_default = SvgIconPhotoshop;

// src/icons/line/IconPin2.tsx
import { jsx as _jsx601 } from "react/jsx-runtime";
var SvgIconPin2 = (props) => /* @__PURE__ */ _jsx601("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx601("path", {
    d: "M17 14c3.293.69 5 2.124 5 3.78C22 20.112 17.523 22 12 22S2 20.111 2 17.78c0-1.656 1.707-3.09 5-3.78m5-2a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 0v6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPin2_default = SvgIconPin2;

// src/icons/line/IconPin3.tsx
import { jsx as _jsx602 } from "react/jsx-runtime";
var SvgIconPin3 = (props) => /* @__PURE__ */ _jsx602("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx602("path", {
    d: "M19.778 17c1.39.527 2.222 1.199 2.222 1.93C22 20.625 17.523 22 12 22S2 20.625 2 18.93c0-.731.832-1.403 2.222-1.93M14 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm-2 10c1.556 0 7-2.918 7-9.2 0-4-3.111-6.8-7-6.8S5 4.8 5 8.8c0 6.282 5.444 9.2 7 9.2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPin3_default = SvgIconPin3;

// src/icons/line/IconPinCheck.tsx
import { jsx as _jsx603 } from "react/jsx-runtime";
var SvgIconPinCheck = (props) => /* @__PURE__ */ _jsx603("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx603("path", {
    d: "m9 12 2 2 5-5m-4 13c2 0 9-3.648 9-11.5 0-5-4-8.5-9-8.5s-9 3.5-9 8.5C3 18.352 10 22 12 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPinCheck_default = SvgIconPinCheck;

// src/icons/line/IconPinCross.tsx
import { jsx as _jsx604 } from "react/jsx-runtime";
var SvgIconPinCross = (props) => /* @__PURE__ */ _jsx604("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx604("path", {
    d: "M9.5 9.5 12 12m0 0 2.5 2.5M12 12l-2.5 2.5M12 12l2.5-2.5M12 22c2 0 9-3.648 9-11.5 0-5-4-8.5-9-8.5s-9 3.5-9 8.5C3 18.352 10 22 12 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPinCross_default = SvgIconPinCross;

// src/icons/line/IconPinMinus.tsx
import { jsx as _jsx605 } from "react/jsx-runtime";
var SvgIconPinMinus = (props) => /* @__PURE__ */ _jsx605("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx605("path", {
    d: "M9 12h6m-3 10c2 0 9-3.648 9-11.5 0-5-4-8.5-9-8.5s-9 3.5-9 8.5C3 18.352 10 22 12 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPinMinus_default = SvgIconPinMinus;

// src/icons/line/IconPinPlus.tsx
import { jsx as _jsx606 } from "react/jsx-runtime";
var SvgIconPinPlus = (props) => /* @__PURE__ */ _jsx606("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx606("path", {
    d: "M12 9v3m0 0v3m0-3H9m3 0h3m-3 10c2 0 9-3.648 9-11.5 0-5-4-8.5-9-8.5s-9 3.5-9 8.5C3 18.352 10 22 12 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPinPlus_default = SvgIconPinPlus;

// src/icons/line/IconPinSlash.tsx
import { jsx as _jsx607 } from "react/jsx-runtime";
var SvgIconPinSlash = (props) => /* @__PURE__ */ _jsx607("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx607("path", {
    d: "m2 2 3.009 3.009M22 22l-3.804-3.804m0 0C16.012 20.768 13.162 22 12 22c-2 0-9-3.648-9-11.5 0-2.166.75-4.051 2.009-5.491m13.187 13.187L5.01 5.01M7.625 3C8.917 2.355 10.408 2 12 2c5 0 9 3.5 9 8.5 0 1.694-.326 3.192-.857 4.5M12 8a3 3 0 0 1 3 3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPinSlash_default = SvgIconPinSlash;

// src/icons/line/IconPin.tsx
import { jsx as _jsx608, jsxs as _jsxs18 } from "react/jsx-runtime";
var SvgIconPin = (props) => /* @__PURE__ */ _jsxs18("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx608("path", {
    d: "M3 10.5C3 5.5 7 2 12 2s9 3.5 9 8.5c0 7.852-7 11.5-9 11.5s-9-3.648-9-11.5Z",
    stroke: props.color,
    strokeWidth: 1.5
  }), /* @__PURE__ */ _jsx608("path", {
    d: "M15 11a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5
  })]
});
var IconPin_default = SvgIconPin;

// src/icons/line/IconPinterest.tsx
import { jsx as _jsx609 } from "react/jsx-runtime";
var SvgIconPinterest = (props) => /* @__PURE__ */ _jsx609("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx609("path", {
    d: "M11.726 11.188a.75.75 0 0 0-1.452-.376l1.452.376Zm7.345-6.26-.53.531.53-.53Zm0 14.143-.53-.53.53.53Zm-14.142 0 .53-.53-.53.53Zm0-14.142.53.53-.53-.53Zm1.141 8.41a.75.75 0 0 0 1.413-.503l-1.413.503Zm2.258 7.965-.274.698.274-.698ZM18.541 5.46a9.25 9.25 0 0 1 0 13.082l1.06 1.06c4.198-4.198 4.198-11.004 0-15.202l-1.06 1.06ZM5.459 18.541a9.25 9.25 0 0 1 0-13.082L4.4 4.4C.2 8.597.2 15.403 4.399 19.6l1.06-1.06Zm0-13.082a9.25 9.25 0 0 1 13.082 0L19.6 4.4C15.403.2 8.597.2 4.4 4.399l1.06 1.06ZM12 7.194c2.71 0 4.751 2.012 4.751 4.306h1.5c0-3.228-2.823-5.806-6.25-5.806v1.5Zm-4.516 5.642c-.543-1.526-.18-2.909.686-3.93.88-1.039 2.293-1.712 3.83-1.712v-1.5c-1.962 0-3.799.855-4.974 2.242-1.19 1.403-1.688 3.342-.955 5.403l1.413-.503ZM16.75 11.5c0 3.346-3.822 5.275-6.528 3.48l-.829 1.25c3.658 2.426 8.857-.134 8.857-4.73h-1.5Zm-6.216 4.294 1.192-4.606-1.452-.376-1.192 4.606 1.452.376Zm8.007 2.747a9.252 9.252 0 0 1-9.938 2.066l-.55 1.395c3.86 1.521 8.425.722 11.548-2.4l-1.06-1.061Zm-9.938 2.066A9.218 9.218 0 0 1 5.46 18.54L4.4 19.6a10.716 10.716 0 0 0 3.655 2.401l.55-1.395Zm.451.886 1.48-5.699-1.452-.377-1.48 5.699 1.452.377Z",
    fill: props.color
  })
});
var IconPinterest_default = SvgIconPinterest;

// src/icons/line/IconPlanet.tsx
import { jsx as _jsx610 } from "react/jsx-runtime";
var SvgIconPlanet = (props) => /* @__PURE__ */ _jsx610("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx610("path", {
    d: "M20.81 10.153C22.295 8.462 22.94 6.947 22.392 6c-.546-.947-2.181-1.147-4.39-.706m2.808 4.859C19.294 11.88 16.9 13.79 14 15.464 8.26 18.778 2.712 19.914 1.608 18c-.547-.947.097-2.462 1.582-4.153m17.62-3.694a9 9 0 0 1-17.62 3.694m17.62-3.694a8.99 8.99 0 0 0-2.807-4.859m0 0A9 9 0 0 0 3.19 13.847",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlanet_default = SvgIconPlanet;

// src/icons/line/IconPlayCircle.tsx
import { jsx as _jsx611 } from "react/jsx-runtime";
var SvgIconPlayCircle = (props) => /* @__PURE__ */ _jsx611("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx611("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlayCircle_default = SvgIconPlayCircle;

// src/icons/line/IconPlayNext.tsx
import { jsx as _jsx612 } from "react/jsx-runtime";
var SvgIconPlayNext = (props) => /* @__PURE__ */ _jsx612("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx612("path", {
    d: "M21 5v14m-7.187-4.308L8.93 17.83c-1.606 1.033-2.408 1.549-3.072 1.502a2 2 0 0 1-1.459-.797C4 18.002 4 17.048 4 15.14V8.86c0-1.909 0-2.863.399-3.396a2 2 0 0 1 1.459-.797c.664-.047 1.466.47 3.072 1.502l4.883 3.138c1.393.896 2.09 1.344 2.33 1.914a2 2 0 0 1 0 1.556c-.24.57-.937 1.018-2.33 1.914Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlayNext_default = SvgIconPlayNext;

// src/icons/line/IconPlayPrevious.tsx
import { jsx as _jsx613 } from "react/jsx-runtime";
var SvgIconPlayPrevious = (props) => /* @__PURE__ */ _jsx613("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx613("path", {
    d: "M4 5v14m7.187-4.308 4.883 3.138c1.605 1.033 2.409 1.549 3.072 1.502a2 2 0 0 0 1.459-.797C21 18.002 21 17.048 21 15.14V8.86c0-1.909 0-2.863-.399-3.396a2 2 0 0 0-1.459-.797c-.663-.047-1.466.47-3.072 1.502l-4.883 3.138c-1.393.896-2.09 1.344-2.33 1.914a2 2 0 0 0 0 1.556c.24.57.937 1.018 2.33 1.914Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlayPrevious_default = SvgIconPlayPrevious;

// src/icons/line/IconPlaySlowmotion.tsx
import { jsx as _jsx614 } from "react/jsx-runtime";
var SvgIconPlaySlowmotion = (props) => /* @__PURE__ */ _jsx614("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx614("path", {
    d: "M2.832 8A9.99 9.99 0 0 1 4 6M2.832 16A9.992 9.992 0 0 0 4 18M8 21.168A9.99 9.99 0 0 1 5.999 20M8 2.832A9.99 9.99 0 0 0 6 4M2.05 11a10.118 10.118 0 0 0 0 2M11 21.95c.329.033.662.05 1 .05 5.523 0 10-4.477 10-10S17.523 2 12 2c-.338 0-.671.017-1 .05m4.002 10.616-3.758 2.505c-.404.27-.606.404-.774.394a.5.5 0 0 1-.369-.198C10 15.234 10 14.991 10 14.505v-5.01c0-.486 0-.729.101-.862a.5.5 0 0 1 .37-.198c.167-.01.369.125.773.394l3.758 2.505c.333.223.5.334.558.475a.5.5 0 0 1 0 .383c-.058.14-.225.251-.559.474Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlaySlowmotion_default = SvgIconPlaySlowmotion;

// src/icons/line/IconPlay.tsx
import { jsx as _jsx615 } from "react/jsx-runtime";
var SvgIconPlay = (props) => /* @__PURE__ */ _jsx615("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx615("path", {
    d: "M6 17.294V6.706c0-1.865 0-2.798.39-3.328a2 2 0 0 1 1.436-.804c.655-.058 1.451.429 3.043 1.401l8.663 5.295c1.477.902 2.216 1.354 2.468 1.935a2 2 0 0 1 0 1.59c-.252.581-.99 1.033-2.468 1.935l-8.663 5.295c-1.592.972-2.388 1.459-3.043 1.401a2 2 0 0 1-1.435-.804C6 20.091 6 19.159 6 17.294Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlay_default = SvgIconPlay;

// src/icons/line/IconPlaylistPlay.tsx
import { jsx as _jsx616 } from "react/jsx-runtime";
var SvgIconPlaylistPlay = (props) => /* @__PURE__ */ _jsx616("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx616("path", {
    d: "M2 4h18M2 9h14M2 14h9m-9 5h7m12.002-1.334-3.758 2.505c-.404.27-.606.404-.774.394a.5.5 0 0 1-.369-.198C16 20.234 16 19.991 16 19.505v-5.01c0-.486 0-.729.101-.862a.5.5 0 0 1 .37-.198c.167-.01.369.125.773.394l3.758 2.505c.333.223.5.334.559.474a.5.5 0 0 1 0 .384c-.059.14-.226.251-.56.474Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlaylistPlay_default = SvgIconPlaylistPlay;

// src/icons/line/IconPlaylistPlus.tsx
import { jsx as _jsx617 } from "react/jsx-runtime";
var SvgIconPlaylistPlus = (props) => /* @__PURE__ */ _jsx617("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx617("path", {
    d: "M2 4h18M2 9h14M2 14h10M2 19h7m10-5v3m0 0v3m0-3h-3m3 0h3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlaylistPlus_default = SvgIconPlaylistPlus;

// src/icons/line/IconPlaylist.tsx
import { jsx as _jsx618 } from "react/jsx-runtime";
var SvgIconPlaylist = (props) => /* @__PURE__ */ _jsx618("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx618("path", {
    d: "M19 19a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 0v-8l3 2M2 4h18M2 9h14M2 14h10M2 19h7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlaylist_default = SvgIconPlaylist;

// src/icons/line/IconPlugSlash.tsx
import { jsx as _jsx619 } from "react/jsx-runtime";
var SvgIconPlugSlash = (props) => /* @__PURE__ */ _jsx619("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx619("path", {
    d: "M8 2v2m8 3V2m6 20-2.284-2.291-.003.004M3 3l4.007 4.007.019.019M11 7h4c1.864 0 2.796 0 3.53.304a4 4 0 0 1 2.165 2.165C21 10.204 21 11.136 21 13c0 1.828 0 3.059-.128 4M7.026 7.026c-.668.032-1.144.107-1.557.278A4 4 0 0 0 3.304 9.47C3 10.204 3 11.136 3 13c0 2.796 0 4.194.457 5.296a6 6 0 0 0 3.247 3.247C7.807 22 9.204 22 12 22c2.796 0 4.194 0 5.296-.457a6 6 0 0 0 2.417-1.83M7.026 7.026l12.687 12.687",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlugSlash_default = SvgIconPlugSlash;

// src/icons/line/IconPlug.tsx
import { jsx as _jsx620 } from "react/jsx-runtime";
var SvgIconPlug = (props) => /* @__PURE__ */ _jsx620("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx620("path", {
    d: "M8 7V2m8 5V2m-4 8.5-2 4h4l-2 4m0 3.5v0c-2.796 0-4.193 0-5.296-.457a6 6 0 0 1-3.247-3.247C3 17.194 3 15.796 3 13v0c0-1.864 0-2.796.304-3.53A4 4 0 0 1 5.47 7.303C6.204 7 7.136 7 9 7h6c1.864 0 2.796 0 3.53.304a4 4 0 0 1 2.165 2.165C21 10.204 21 11.136 21 13v0c0 2.796 0 4.194-.457 5.296a6 6 0 0 1-3.247 3.247C16.194 22 14.796 22 12 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlug_default = SvgIconPlug;

// src/icons/line/IconPlusCircle.tsx
import { jsx as _jsx621 } from "react/jsx-runtime";
var SvgIconPlusCircle = (props) => /* @__PURE__ */ _jsx621("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx621("path", {
    d: "M8 12h4m0 0h4m-4 0V8m0 4v4m10-4c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlusCircle_default = SvgIconPlusCircle;

// src/icons/line/IconPlusRec.tsx
import { jsx as _jsx622 } from "react/jsx-runtime";
var SvgIconPlusRec = (props) => /* @__PURE__ */ _jsx622("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx622("path", {
    d: "M8 12h4m0 0h4m-4 0V8m0 4v4m-.4 6h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlusRec_default = SvgIconPlusRec;

// src/icons/line/IconPlus.tsx
import { jsx as _jsx623 } from "react/jsx-runtime";
var SvgIconPlus = (props) => /* @__PURE__ */ _jsx623("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx623("path", {
    d: "M5 12h7m7 0h-7m0 0V5m0 7v7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPlus_default = SvgIconPlus;

// src/icons/line/IconPos.tsx
import { jsx as _jsx624 } from "react/jsx-runtime";
var SvgIconPos = (props) => /* @__PURE__ */ _jsx624("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx624("path", {
    d: "M6.5 14a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5-4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5-4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m8.5-8h-4m0-3h.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 8.52 22 9.08 22 10.2v3.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 17 19.92 17 18.8 17H18M6.6 10h6.8c.56 0 .84 0 1.054-.109a1 1 0 0 0 .437-.437C15 9.24 15 8.96 15 8.4V6.6c0-.56 0-.84-.109-1.054a1 1 0 0 0-.437-.437C14.24 5 13.96 5 13.4 5H6.6c-.56 0-.84 0-1.054.109a1 1 0 0 0-.437.437C5 5.76 5 6.04 5 6.6v1.8c0 .56 0 .84.109 1.054a1 1 0 0 0 .437.437C5.76 10 6.04 10 6.6 10Zm.2 12h6.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C18 19.72 18 18.88 18 17.2V6.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C15.72 2 14.88 2 13.2 2H6.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 4.28 2 5.12 2 6.8v10.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 22 5.12 22 6.8 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPos_default = SvgIconPos;

// src/icons/line/IconPowerRec.tsx
import { jsx as _jsx625 } from "react/jsx-runtime";
var SvgIconPowerRec = (props) => /* @__PURE__ */ _jsx625("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx625("path", {
    d: "M9 6.803a6 6 0 1 0 6 0M12 5v7m-.4 10h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPowerRec_default = SvgIconPowerRec;

// src/icons/line/IconPower.tsx
import { jsx as _jsx626 } from "react/jsx-runtime";
var SvgIconPower = (props) => /* @__PURE__ */ _jsx626("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx626("path", {
    d: "M12.75 2a.75.75 0 0 0-1.5 0h1.5Zm-1.5 10a.75.75 0 0 0 1.5 0h-1.5Zm5.084-7.736a.75.75 0 1 0-.668 1.343l.668-1.343Zm-8 1.343a.75.75 0 0 0-.668-1.343l.668 1.343ZM11.25 2v10h1.5V2h-1.5Zm9 11A8.25 8.25 0 0 1 12 21.25v1.5c5.385 0 9.75-4.365 9.75-9.75h-1.5ZM12 21.25A8.25 8.25 0 0 1 3.75 13h-1.5c0 5.385 4.365 9.75 9.75 9.75v-1.5Zm3.666-15.643A8.25 8.25 0 0 1 20.25 13h1.5a9.75 9.75 0 0 0-5.416-8.736l-.668 1.343ZM3.75 13a8.25 8.25 0 0 1 4.584-7.393l-.668-1.343A9.75 9.75 0 0 0 2.25 13h1.5Z",
    fill: props.color
  })
});
var IconPower_default = SvgIconPower;

// src/icons/line/IconPresentationChart.tsx
import { jsx as _jsx627 } from "react/jsx-runtime";
var SvgIconPresentationChart = (props) => /* @__PURE__ */ _jsx627("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx627("path", {
    d: "M3 2h18M3 2v7.6c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C6.04 16 7.16 16 9.4 16h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C21 12.96 21 11.84 21 9.6V2M3 2H1.5M21 2h1.5M8 9v2m4-4v4m4-3v3m-4 5v3m0 0-5 3m5-3 5 3m-5-3v3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPresentationChart_default = SvgIconPresentationChart;

// src/icons/line/IconPresentationLine.tsx
import { jsx as _jsx628 } from "react/jsx-runtime";
var SvgIconPresentationLine = (props) => /* @__PURE__ */ _jsx628("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx628("path", {
    d: "M3 2h18M3 2v7.6c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C6.04 16 7.16 16 9.4 16h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C21 12.96 21 11.84 21 9.6V2M3 2H1.5M21 2h1.5M12 16v3m0 0-5 3m5-3 5 3m-5-3v3M8 10l3-2 2 2 3-2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPresentationLine_default = SvgIconPresentationLine;

// src/icons/line/IconPrinterSlash.tsx
import { jsx as _jsx629 } from "react/jsx-runtime";
var SvgIconPrinterSlash = (props) => /* @__PURE__ */ _jsx629("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx629("path", {
    d: "M8 8H7c-1.87 0-2.804 0-3.5.402A3 3 0 0 0 2.402 9.5C2 10.196 2 11.13 2 13v1c0 .93 0 1.395.102 1.777a3 3 0 0 0 2.122 2.12C4.605 18 5.07 18 6 18M8 8 2 2m6 6 10 10m0 0 4 4m-4-4v.8c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C16.48 22 15.92 22 14.8 22H9c-.932 0-1.398 0-1.765-.152a2 2 0 0 1-1.083-1.083C6 20.398 6 19.932 6 19c0-.932 0-1.398.152-1.765a2 2 0 0 1 1.083-1.083C7.602 16 8.068 16 9 16h7m5.236 1c.308-.345.538-.762.662-1.223C22 15.395 22 14.93 22 14v-1c0-1.87 0-2.804-.402-3.5A3 3 0 0 0 20.5 8.402C19.804 8 18.87 8 17 8h-5m6 0V5.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C16.48 2 15.92 2 14.8 2H8m-3 9h1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPrinterSlash_default = SvgIconPrinterSlash;

// src/icons/line/IconPrinter.tsx
import { jsx as _jsx630 } from "react/jsx-runtime";
var SvgIconPrinter = (props) => /* @__PURE__ */ _jsx630("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx630("path", {
    d: "M18 18v0c.93 0 1.395 0 1.776-.102a3 3 0 0 0 2.122-2.121C22 15.395 22 14.93 22 14v-1.2c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C19.72 8 18.88 8 17.2 8H6.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 10.28 2 11.12 2 12.8V14c0 .93 0 1.395.102 1.777a3 3 0 0 0 2.122 2.12C4.605 18 5.07 18 6 18v0M18 8V5.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C16.48 2 15.92 2 14.8 2H9.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C6 3.52 6 4.08 6 5.2V8m-1 3h1m3 11h6c.932 0 1.398 0 1.765-.152a2 2 0 0 0 1.083-1.083C18 20.398 18 19.932 18 19v0c0-.932 0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C16.398 16 15.932 16 15 16H9c-.932 0-1.398 0-1.765.152a2 2 0 0 0-1.083 1.083C6 17.602 6 18.068 6 19v0c0 .932 0 1.398.152 1.765a2 2 0 0 0 1.083 1.083C7.602 22 8.068 22 9 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconPrinter_default = SvgIconPrinter;

// src/icons/line/IconProcentCircle.tsx
import { jsx as _jsx631 } from "react/jsx-runtime";
var SvgIconProcentCircle = (props) => /* @__PURE__ */ _jsx631("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx631("path", {
    d: "M15 7 9 17m13-5c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10ZM10 9.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm7 5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconProcentCircle_default = SvgIconProcentCircle;

// src/icons/line/IconProcent.tsx
import { jsx as _jsx632 } from "react/jsx-runtime";
var SvgIconProcent = (props) => /* @__PURE__ */ _jsx632("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx632("path", {
    d: "M18 3 6 21M9 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm12 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconProcent_default = SvgIconProcent;

// src/icons/line/IconProcessor.tsx
import { jsx as _jsx633 } from "react/jsx-runtime";
var SvgIconProcessor = (props) => /* @__PURE__ */ _jsx633("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx633("path", {
    d: "M8 4V2m0 20v-2M2 8h2m16 0h2M12 4V2m0 20v-2M2 12h2m16 0h2m-6-8V2m0 20v-2M2 16h2m16 0h2m-10.6-1h1.2c.84 0 1.26 0 1.581-.164a1.5 1.5 0 0 0 .655-.655c.164-.32.164-.74.164-1.581v-1.2c0-.84 0-1.26-.164-1.581a1.5 1.5 0 0 0-.655-.656C13.861 9 13.441 9 12.6 9h-1.2c-.84 0-1.26 0-1.581.163a1.5 1.5 0 0 0-.656.656C9 10.139 9 10.559 9 11.4v1.2c0 .84 0 1.26.163 1.581a1.5 1.5 0 0 0 .656.655c.32.164.74.164 1.581.164Zm-1 5h3.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C20 16.96 20 15.84 20 13.6v-3.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C16.96 4 15.84 4 13.6 4h-3.2c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C4 7.04 4 8.16 4 10.4v3.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C7.04 20 8.16 20 10.4 20Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconProcessor_default = SvgIconProcessor;

// src/icons/line/IconProducthunt.tsx
import { jsx as _jsx634 } from "react/jsx-runtime";
var SvgIconProducthunt = (props) => /* @__PURE__ */ _jsx634("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx634("path", {
    d: "M10 17v-4m0 0V7h3a3 3 0 1 1 0 6h-3Zm12-1c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconProducthunt_default = SvgIconProducthunt;

// src/icons/line/IconQuestion.tsx
import { jsx as _jsx635 } from "react/jsx-runtime";
var SvgIconQuestion = (props) => /* @__PURE__ */ _jsx635("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx635("path", {
    d: "M10 10.5V10a2 2 0 1 1 4 0v.121c0 .563-.223 1.102-.621 1.5L12 13m.5 3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconQuestion_default = SvgIconQuestion;

// src/icons/line/IconQuotes2.tsx
import { jsx as _jsx636 } from "react/jsx-runtime";
var SvgIconQuotes2 = (props) => /* @__PURE__ */ _jsx636("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx636("path", {
    d: "M2 16v-4.024c0-1.467 0-2.2.166-2.891a6 6 0 0 1 .718-1.735c.371-.605.89-1.124 1.928-2.162L6 4m8 12v-4.024c0-1.467 0-2.2.166-2.891a6 6 0 0 1 .718-1.735c.371-.605.89-1.124 1.928-2.162L18 4m.8 16h-1.6c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C14 18.48 14 17.92 14 16.8v-1.6c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C15.52 12 16.08 12 17.2 12h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 13.52 22 14.08 22 15.2v1.6c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C20.48 20 19.92 20 18.8 20Zm-12-8H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 13.52 2 14.08 2 15.2v1.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C3.52 20 4.08 20 5.2 20h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C10 18.48 10 17.92 10 16.8v-1.6c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C8.48 12 7.92 12 6.8 12Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconQuotes2_default = SvgIconQuotes2;

// src/icons/line/IconQuotes.tsx
import { jsx as _jsx637 } from "react/jsx-runtime";
var SvgIconQuotes = (props) => /* @__PURE__ */ _jsx637("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx637("path", {
    d: "M10 8v5.25c0 .732 0 1.098-.04 1.444A6 6 0 0 1 8.13 18.35c-.253.24-.545.46-1.131.899v0L6 20M22 8v5.25c0 .732 0 1.098-.04 1.444a6 6 0 0 1-1.829 3.657c-.253.24-.545.46-1.131.899v0L18 20m-.8-8h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C22 10.48 22 9.92 22 8.8V7.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C20.48 4 19.92 4 18.8 4h-1.6c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C14 5.52 14 6.08 14 7.2v1.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 12 16.08 12 17.2 12Zm-12 0h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C10 10.48 10 9.92 10 8.8V7.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C8.48 4 7.92 4 6.8 4H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 5.52 2 6.08 2 7.2v1.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C3.52 12 4.08 12 5.2 12Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconQuotes_default = SvgIconQuotes;

// src/icons/line/IconRadio.tsx
import { jsx as _jsx638 } from "react/jsx-runtime";
var SvgIconRadio = (props) => /* @__PURE__ */ _jsx638("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx638("path", {
    d: "M6 4v0a3 3 0 0 1 3-3h8m-3 8h4m-4 4h4m-8-2a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm0 11h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-2c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 4 16.8 4 14 4h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 6.73C2 7.8 2 9.2 2 12v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRadio_default = SvgIconRadio;

// src/icons/line/IconReceipt.tsx
import { jsx as _jsx639 } from "react/jsx-runtime";
var SvgIconReceipt = (props) => /* @__PURE__ */ _jsx639("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx639("path", {
    d: "M18 12V2l-2.713 1.206c-.611.271-.917.407-1.226.441a2 2 0 0 1-.997-.146c-.287-.12-.541-.338-1.05-.774v0c-.674-.578-1.011-.867-1.384-.991a2 2 0 0 0-1.26 0c-.373.124-.71.413-1.385.991v0c-.508.436-.762.653-1.049.774a2 2 0 0 1-.997.146c-.31-.034-.615-.17-1.226-.441L2 2v13.6c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22H20m-2-10h.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 13.52 22 14.08 22 15.2V20a2 2 0 0 1-2 2v0a2 2 0 0 1-2-2v-8Zm-7.5 5h3m-7-8H11m-.5 4h3m-6 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 4a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReceipt_default = SvgIconReceipt;

// src/icons/line/IconRecord.tsx
import { jsx as _jsx640 } from "react/jsx-runtime";
var SvgIconRecord = (props) => /* @__PURE__ */ _jsx640("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx640("circle", {
    cx: 12,
    cy: 12,
    r: 10,
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRecord_default = SvgIconRecord;

// src/icons/line/IconRedo2Rec.tsx
import { jsx as _jsx641 } from "react/jsx-runtime";
var SvgIconRedo2Rec = (props) => /* @__PURE__ */ _jsx641("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx641("path", {
    d: "M18 14v-4m0 4h-4m4 0s-3-4-6.5-4C7 10 6 14 6 14m4 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRedo2Rec_default = SvgIconRedo2Rec;

// src/icons/line/IconRedo2.tsx
import { jsx as _jsx642 } from "react/jsx-runtime";
var SvgIconRedo2 = (props) => /* @__PURE__ */ _jsx642("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx642("path", {
    d: "M22 15V7m0 8h-7.778M22 15s-5-8-11-8c-8 0-9 8-9 8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRedo2_default = SvgIconRedo2;

// src/icons/line/IconRedoRec.tsx
import { jsx as _jsx643 } from "react/jsx-runtime";
var SvgIconRedoRec = (props) => /* @__PURE__ */ _jsx643("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx643("path", {
    d: "M18 8h-7.5A4.5 4.5 0 0 0 6 12.5v0a4.5 4.5 0 0 0 4.5 4.5H13m5-9-2-2m2 2-2 2m-6 12h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRedoRec_default = SvgIconRedoRec;

// src/icons/line/IconRedo.tsx
import { jsx as _jsx644 } from "react/jsx-runtime";
var SvgIconRedo = (props) => /* @__PURE__ */ _jsx644("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx644("path", {
    d: "M22 6H9a7 7 0 0 0-7 7v0a7 7 0 0 0 7 7h5m8-14-4-4m4 4-4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRedo_default = SvgIconRedo;

// src/icons/line/IconRefreshCcwAlertRec.tsx
import { jsx as _jsx645 } from "react/jsx-runtime";
var SvgIconRefreshCcwAlertRec = (props) => /* @__PURE__ */ _jsx645("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx645("path", {
    d: "M6 12a6 6 0 0 0 6 6c3.314 0 5.5-3 5.5-3m.5-3c0-3.314-2.667-6-6-6-4 0-5.5 3-5.5 3m0 0V6.5m0 2.5h2m9 6h-2m2 0v2.5M12 11V9m.5 5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRefreshCcwAlertRec_default = SvgIconRefreshCcwAlertRec;

// src/icons/line/IconRefreshCcwAlert.tsx
import { jsx as _jsx646 } from "react/jsx-runtime";
var SvgIconRefreshCcwAlert = (props) => /* @__PURE__ */ _jsx646("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx646("path", {
    d: "M2 12c0 5.523 4.477 10 10 10s9-5.5 9-5.5m1-4.5A9.98 9.98 0 0 0 12 2C5.333 2 2 7.5 2 7.5m0 0V4m0 3.5h3.5m15.5 9h-3.5m3.5 0V20m-9-8V8m.5 7a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRefreshCcwAlert_default = SvgIconRefreshCcwAlert;

// src/icons/line/IconRefreshCcwRec.tsx
import { jsx as _jsx647 } from "react/jsx-runtime";
var SvgIconRefreshCcwRec = (props) => /* @__PURE__ */ _jsx647("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx647("path", {
    d: "M6 12a6 6 0 0 0 6 6c3.314 0 5.5-3 5.5-3m.5-3c0-3.314-2.667-6-6-6-4 0-5.5 3-5.5 3m0 0V6.5m0 2.5H9m8.5 6H15m2.5 0v2.5M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRefreshCcwRec_default = SvgIconRefreshCcwRec;

// src/icons/line/IconRefreshCcw.tsx
import { jsx as _jsx648 } from "react/jsx-runtime";
var SvgIconRefreshCcw = (props) => /* @__PURE__ */ _jsx648("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx648("path", {
    d: "M2 12c0 5.523 4.477 10 10 10s9-5.5 9-5.5m1-4.5A9.98 9.98 0 0 0 12 2C5.333 2 2 7.5 2 7.5m0 0V4m0 3.5h3.5m15.5 9h-3.5m3.5 0V20",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRefreshCcw_default = SvgIconRefreshCcw;

// src/icons/line/IconRefreshCwAlertRec.tsx
import { jsx as _jsx649 } from "react/jsx-runtime";
var SvgIconRefreshCwAlertRec = (props) => /* @__PURE__ */ _jsx649("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx649("path", {
    d: "M18 12a6 6 0 0 1-6 6c-3.314 0-5.5-3-5.5-3M6 12c0-3.314 2.667-6 6-6 4 0 5.5 3 5.5 3m0 0V6.5m0 2.5h-2m-9 6h2m-2 0v2.5M12 11V9m.5 5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRefreshCwAlertRec_default = SvgIconRefreshCwAlertRec;

// src/icons/line/IconRefreshCwAlert.tsx
import { jsx as _jsx650 } from "react/jsx-runtime";
var SvgIconRefreshCwAlert = (props) => /* @__PURE__ */ _jsx650("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx650("path", {
    d: "M22 12c0 5.523-4.477 10-10 10s-9-5.5-9-5.5M2 12A9.98 9.98 0 0 1 12 2c6.667 0 10 5.5 10 5.5m0 0V4m0 3.5h-3.5M3 16.5h3.5m-3.5 0V20m9-8V8m.5 7a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRefreshCwAlert_default = SvgIconRefreshCwAlert;

// src/icons/line/IconRefreshCwRec.tsx
import { jsx as _jsx651 } from "react/jsx-runtime";
var SvgIconRefreshCwRec = (props) => /* @__PURE__ */ _jsx651("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx651("path", {
    d: "M18 12a6 6 0 0 1-6 6c-3.314 0-5.5-3-5.5-3M6 12c0-3.314 2.667-6 6-6 4 0 5.5 3 5.5 3m0 0V6.5m0 2.5H15m-8.5 6H9m-2.5 0v2.5M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRefreshCwRec_default = SvgIconRefreshCwRec;

// src/icons/line/IconRefreshCw.tsx
import { jsx as _jsx652 } from "react/jsx-runtime";
var SvgIconRefreshCw = (props) => /* @__PURE__ */ _jsx652("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx652("path", {
    d: "M22 12c0 5.523-4.477 10-10 10s-9-5.5-9-5.5M2 12A9.98 9.98 0 0 1 12 2c6.667 0 10 5.5 10 5.5m0 0V4m0 3.5h-3.5M3 16.5h3.5m-3.5 0V20",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRefreshCw_default = SvgIconRefreshCw;

// src/icons/line/IconRepeatOn.tsx
import { jsx as _jsx653 } from "react/jsx-runtime";
var SvgIconRepeatOn = (props) => /* @__PURE__ */ _jsx653("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx653("path", {
    d: "M12.5 15V9L10 11.5M3 20h13.2c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 17.72 21 16.88 21 15.2V14M3 20l3-3m-3 3 3 3M21 4H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 6.28 3 7.12 3 8.8V10m18-6-3-3m3 3-3 3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRepeatOn_default = SvgIconRepeatOn;

// src/icons/line/IconRepeat.tsx
import { jsx as _jsx654 } from "react/jsx-runtime";
var SvgIconRepeat = (props) => /* @__PURE__ */ _jsx654("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx654("path", {
    d: "M3 20h13.2c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 17.72 21 16.88 21 15.2V14M3 20l3-3m-3 3 3 3M21 4H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 6.28 3 7.12 3 8.8V10m18-6-3-3m3 3-3 3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRepeat_default = SvgIconRepeat;

// src/icons/line/IconReplay10Rec.tsx
import { jsx as _jsx655 } from "react/jsx-runtime";
var SvgIconReplay10Rec = (props) => /* @__PURE__ */ _jsx655("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx655("path", {
    d: "M5.29 10A7 7 0 1 0 7 7.101L8 4m1.5 5.5v5m3.5 0v0a1.5 1.5 0 0 0 1.5-1.5v-2A1.5 1.5 0 0 0 13 9.5v0a1.5 1.5 0 0 0-1.5 1.5v2a1.5 1.5 0 0 0 1.5 1.5ZM10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplay10Rec_default = SvgIconReplay10Rec;

// src/icons/line/IconReplay10.tsx
import { jsx as _jsx656 } from "react/jsx-runtime";
var SvgIconReplay10 = (props) => /* @__PURE__ */ _jsx656("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx656("path", {
    d: "M2 12c0 5.523 4.477 10 10 10s10-4.477 10-10A9.98 9.98 0 0 0 12 2C5.333 2 2 7.556 2 7.556v-5M9 9v6m4.5 0v0a1.5 1.5 0 0 0 1.5-1.5v-3A1.5 1.5 0 0 0 13.5 9v0a1.5 1.5 0 0 0-1.5 1.5v3a1.5 1.5 0 0 0 1.5 1.5Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplay10_default = SvgIconReplay10;

// src/icons/line/IconReplay15Rec.tsx
import { jsx as _jsx657 } from "react/jsx-runtime";
var SvgIconReplay15Rec = (props) => /* @__PURE__ */ _jsx657("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx657("path", {
    d: "M5.29 10A7 7 0 1 0 7 7.101L8 4m3.5 10.5h3V12h-3V9.5h3m-5 0v5M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplay15Rec_default = SvgIconReplay15Rec;

// src/icons/line/IconReplay15.tsx
import { jsx as _jsx658 } from "react/jsx-runtime";
var SvgIconReplay15 = (props) => /* @__PURE__ */ _jsx658("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx658("path", {
    d: "M2 12c0 5.523 4.477 10 10 10s10-4.477 10-10A9.98 9.98 0 0 0 12 2C5.333 2 2 7.556 2 7.556v-5M9 9v6m3 0h3v-3h-3V9h3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplay15_default = SvgIconReplay15;

// src/icons/line/IconReplay5Rec.tsx
import { jsx as _jsx659 } from "react/jsx-runtime";
var SvgIconReplay5Rec = (props) => /* @__PURE__ */ _jsx659("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx659("path", {
    d: "M5.29 10A7 7 0 1 0 7 7.101L8 4m2.5 10.5h3V12h-3V9.5h3M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplay5Rec_default = SvgIconReplay5Rec;

// src/icons/line/IconReplay5.tsx
import { jsx as _jsx660 } from "react/jsx-runtime";
var SvgIconReplay5 = (props) => /* @__PURE__ */ _jsx660("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx660("path", {
    d: "M2 12c0 5.523 4.477 10 10 10s10-4.477 10-10A9.98 9.98 0 0 0 12 2C5.333 2 2 7.556 2 7.556v-5M10 15h4v-3h-4V9h4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplay5_default = SvgIconReplay5;

// src/icons/line/IconReplayRec.tsx
import { jsx as _jsx661 } from "react/jsx-runtime";
var SvgIconReplayRec = (props) => /* @__PURE__ */ _jsx661("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx661("path", {
    d: "M10 6.295A6.805 6.805 0 0 1 12 6a6 6 0 1 1-4.99 2.667M10 6.295 12 4m-2 2.295L12.333 8M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplayRec_default = SvgIconReplayRec;

// src/icons/line/IconReplay.tsx
import { jsx as _jsx662 } from "react/jsx-runtime";
var SvgIconReplay = (props) => /* @__PURE__ */ _jsx662("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx662("path", {
    d: "M9 4.443A10.208 10.208 0 0 1 12 4a9 9 0 1 1-7.484 4M9 4.443 12 1M9 4.443 12.5 7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplay_default = SvgIconReplay;

// src/icons/line/IconReplyAllRec.tsx
import { jsx as _jsx663 } from "react/jsx-runtime";
var SvgIconReplyAllRec = (props) => /* @__PURE__ */ _jsx663("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx663("path", {
    d: "m10 11 3-3m-3 3 3 3m-3-3h4.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C18 12.52 18 13.08 18 14.2V16M9 8l-3 3 3 3m1 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplyAllRec_default = SvgIconReplyAllRec;

// src/icons/line/IconReplyAll.tsx
import { jsx as _jsx664 } from "react/jsx-runtime";
var SvgIconReplyAll = (props) => /* @__PURE__ */ _jsx664("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx664("path", {
    d: "m6 10 5-5m-5 5 5 5m-5-5h11.2c1.68 0 2.52 0 3.162.327a3 3 0 0 1 1.311 1.311C22 12.28 22 13.12 22 14.8V17M7 5l-5 5 5 5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplyAll_default = SvgIconReplyAll;

// src/icons/line/IconReplyRec.tsx
import { jsx as _jsx665 } from "react/jsx-runtime";
var SvgIconReplyRec = (props) => /* @__PURE__ */ _jsx665("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx665("path", {
    d: "m6 11 3-3m-3 3 3 3m-3-3h7.5a4.5 4.5 0 0 1 4.5 4.5v.5m-8 6h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReplyRec_default = SvgIconReplyRec;

// src/icons/line/IconReply.tsx
import { jsx as _jsx666 } from "react/jsx-runtime";
var SvgIconReply = (props) => /* @__PURE__ */ _jsx666("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx666("path", {
    d: "m2 11 5-5m-5 5 5 5m-5-5h13.6c2.24 0 3.36 0 4.216.436a4 4 0 0 1 1.748 1.748C22 14.04 22 15.16 22 17.4v.6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconReply_default = SvgIconReply;

// src/icons/line/IconRocket2.tsx
import { jsx as _jsx667 } from "react/jsx-runtime";
var SvgIconRocket2 = (props) => /* @__PURE__ */ _jsx667("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx667("path", {
    d: "m6 11-2.08 1.56c-.704.527-1.056.791-1.31 1.126a3 3 0 0 0-.495.99C2 15.079 2 15.52 2 16.399V20l4-1m12-8 2.08 1.56c.704.529 1.056.793 1.31 1.127a3 3 0 0 1 .495.99c.115.403.115.844.115 1.723V20l-4-1m-6 3v-3M14 11a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM8.74 4.02l-.687.636c-.756.7-1.134 1.05-1.405 1.466a4 4 0 0 0-.525 1.203C6 7.806 6 8.322 6 9.352V16c0 1.864 0 2.796.305 3.53a4 4 0 0 0 2.164 2.165C9.204 22 10.136 22 12 22v0c1.864 0 2.796 0 3.53-.305a4 4 0 0 0 2.166-2.164C18 18.796 18 17.864 18 16V9.352c0-1.03 0-1.546-.121-2.027a4.002 4.002 0 0 0-.526-1.203c-.27-.416-.649-.766-1.405-1.466l-.687-.636c-1.15-1.065-1.724-1.597-2.378-1.799a3 3 0 0 0-1.766 0c-.653.202-1.228.734-2.378 1.799Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRocket2_default = SvgIconRocket2;

// src/icons/line/IconRocket.tsx
import { jsx as _jsx668 } from "react/jsx-runtime";
var SvgIconRocket = (props) => /* @__PURE__ */ _jsx668("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx668("path", {
    d: "M5.5 10.5 2 8l1.078-1.078c.886-.886 1.329-1.328 1.853-1.608a4 4 0 0 1 1.495-.453c.591-.058 1.205.065 2.433.31L10.5 5.5m8 8 .328 1.64c.246 1.229.37 1.843.311 2.434a4.002 4.002 0 0 1-.452 1.496c-.28.524-.723.967-1.609 1.852L16.001 22l-2.5-3.5M7 17l2-2m8.002-6a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm.197-7h-.549c-.978 0-1.467 0-1.928.11a4 4 0 0 0-1.156.48c-.404.247-.75.593-1.441 1.285L7 9c-1.09 1.09-1.636 1.636-1.927 2.224a4 4 0 0 0 0 3.552c.291.588.836 1.133 1.927 2.224v0c1.09 1.09 1.636 1.636 2.224 1.927a4 4 0 0 0 3.552 0C13.364 18.636 13.91 18.09 15 17l5.125-5.126c.692-.691 1.037-1.037 1.285-1.44.22-.359.38-.749.479-1.157.11-.46.11-.95.11-1.928V6.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.31-1.311C19.718 2 18.878 2 17.198 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRocket_default = SvgIconRocket;

// src/icons/line/IconRotateCcwRec.tsx
import { jsx as _jsx669 } from "react/jsx-runtime";
var SvgIconRotateCcwRec = (props) => /* @__PURE__ */ _jsx669("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx669("path", {
    d: "M6 12a6 6 0 0 0 12 0c0-3.314-2.667-6-6-6-4 0-5.6 3-5.6 3m0 0V6.5m0 2.5h2.1M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRotateCcwRec_default = SvgIconRotateCcwRec;

// src/icons/line/IconRotateCcw.tsx
import { jsx as _jsx670 } from "react/jsx-runtime";
var SvgIconRotateCcw = (props) => /* @__PURE__ */ _jsx670("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx670("path", {
    d: "M2 12c0 5.523 4.477 10 10 10s10-4.477 10-10A9.98 9.98 0 0 0 12 2C5.333 2 2 7.5 2 7.5m0 0V4m0 3.5h3.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRotateCcw_default = SvgIconRotateCcw;

// src/icons/line/IconRotateCwRec.tsx
import { jsx as _jsx671 } from "react/jsx-runtime";
var SvgIconRotateCwRec = (props) => /* @__PURE__ */ _jsx671("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx671("path", {
    d: "M18 12a6 6 0 0 1-12 0c0-3.314 2.667-6 6-6 4 0 5.6 3 5.6 3m0 0V6.5m0 2.5h-2.1M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRotateCwRec_default = SvgIconRotateCwRec;

// src/icons/line/IconRotateCw.tsx
import { jsx as _jsx672 } from "react/jsx-runtime";
var SvgIconRotateCw = (props) => /* @__PURE__ */ _jsx672("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx672("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12A9.98 9.98 0 0 1 12 2c6.667 0 10 5.5 10 5.5m0 0V4m0 3.5h-3.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRotateCw_default = SvgIconRotateCw;

// src/icons/line/IconRoute.tsx
import { jsx as _jsx673 } from "react/jsx-runtime";
var SvgIconRoute = (props) => /* @__PURE__ */ _jsx673("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx673("path", {
    d: "M8 19h8.5a3.5 3.5 0 0 0 3.5-3.5v0a3.5 3.5 0 0 0-3.5-3.5h-10A3.5 3.5 0 0 1 3 8.5v0A3.5 3.5 0 0 1 6.5 5H12M8 19a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm4-14L9.5 2.5M12 5 9.5 7.5M21 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRoute_default = SvgIconRoute;

// src/icons/line/IconRuler.tsx
import { jsx as _jsx674 } from "react/jsx-runtime";
var SvgIconRuler = (props) => /* @__PURE__ */ _jsx674("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx674("path", {
    d: "m11 20-2-2m5-1-3.5-3.5M20 11l-3.5-3.5M17 14l-2-2m-3.106 7.606 7.712-7.712c1.188-1.188 1.782-1.782 2.004-2.467a3 3 0 0 0 0-1.854c-.222-.685-.816-1.28-2.004-2.467l-.712-.712c-1.188-1.188-1.782-1.782-2.467-2.005a3 3 0 0 0-1.854 0c-.685.223-1.28.817-2.467 2.005l-7.712 7.712c-1.188 1.188-1.782 1.782-2.005 2.467a3 3 0 0 0 0 1.854c.223.685.817 1.28 2.005 2.467l.712.712c1.188 1.188 1.782 1.782 2.467 2.004a3 3 0 0 0 1.854 0c.685-.222 1.28-.816 2.467-2.004Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconRuler_default = SvgIconRuler;

// src/icons/line/IconScale.tsx
import { jsx as _jsx675 } from "react/jsx-runtime";
var SvgIconScale = (props) => /* @__PURE__ */ _jsx675("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx675("path", {
    d: "M11 2h-1c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-1m-12 1L22 2M10 14V9m0 5h5m7-12h-5m5 0v5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconScale_default = SvgIconScale;

// src/icons/line/IconScanRec.tsx
import { jsx as _jsx676 } from "react/jsx-runtime";
var SvgIconScanRec = (props) => /* @__PURE__ */ _jsx676("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx676("path", {
    d: "M6 9v-.3 0c0-.651 0-.977.075-1.243a2 2 0 0 1 1.382-1.382C7.723 6 8.05 6 8.7 6H10m4 0h1.3c.651 0 .977 0 1.243.075a2 2 0 0 1 1.382 1.382C18 7.723 18 8.05 18 8.7V9m0 6v.3c0 .651 0 .977-.075 1.243a2 2 0 0 1-1.382 1.382C16.277 18 15.95 18 15.3 18H14m-4.2 0H8.7c-.651 0-.977 0-1.243-.075a2 2 0 0 1-1.382-1.382C6 16.277 6 15.95 6 15.3v0-.3m0-3h12m-8 10h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconScanRec_default = SvgIconScanRec;

// src/icons/line/IconScan.tsx
import { jsx as _jsx677 } from "react/jsx-runtime";
var SvgIconScan = (props) => /* @__PURE__ */ _jsx677("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx677("path", {
    d: "M22 8v-.5 0A5.5 5.5 0 0 0 16.5 2v0H16m6 14v.5a5.5 5.5 0 0 1-5.5 5.5v0H16M2 16v.5A5.5 5.5 0 0 0 7.5 22v0H8M2 8v-.5 0A5.5 5.5 0 0 1 7.5 2v0H8M2 12h20",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconScan_default = SvgIconScan;

// src/icons/line/IconScriptRec.tsx
import { jsx as _jsx678 } from "react/jsx-runtime";
var SvgIconScriptRec = (props) => /* @__PURE__ */ _jsx678("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx678("path", {
    d: "M8.5 9.5 6 12l2.5 2.5m7-5L18 12l-2.5 2.5M13 7l-2 10m-1 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconScriptRec_default = SvgIconScriptRec;

// src/icons/line/IconScript.tsx
import { jsx as _jsx679 } from "react/jsx-runtime";
var SvgIconScript = (props) => /* @__PURE__ */ _jsx679("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx679("path", {
    d: "m6 8-4 4 4 4m12-8 4 4-4 4M14 3l-4 18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconScript_default = SvgIconScript;

// src/icons/line/IconSearch.tsx
import { jsx as _jsx680 } from "react/jsx-runtime";
var SvgIconSearch = (props) => /* @__PURE__ */ _jsx680("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx680("path", {
    d: "m21 21-3.636-3.636m0 0A9 9 0 1 0 4.636 4.636a9 9 0 0 0 12.728 12.728Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconSearch_default = SvgIconSearch;

// src/icons/line/IconSelectionRec.tsx
import { jsx as _jsx681 } from "react/jsx-runtime";
var SvgIconSelectionRec = (props) => /* @__PURE__ */ _jsx681("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx681("path", {
    d: "M11 6h2m2.5 0h.5v0a2 2 0 0 1 2 2v.5m-12 0V8v0a2 2 0 0 1 2-2v0h.5M6 11v2m0 2.6v.4a2 2 0 0 0 2 2v0h.5m2.5 0h2m2.5 0h.5v0a2 2 0 0 0 2-2v0-.5m0-2.5v-2m-8-9h4c2.8 0 4.2 0 5.27.545a5 5 0 0 1 2.185 2.185C22 5.8 22 7.2 22 10v4c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14v-4c0-2.8 0-4.2.545-5.27A5 5 0 0 1 4.73 2.545C5.8 2 7.2 2 10 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSelectionRec_default = SvgIconSelectionRec;

// src/icons/line/IconSelection.tsx
import { jsx as _jsx682 } from "react/jsx-runtime";
var SvgIconSelection = (props) => /* @__PURE__ */ _jsx682("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx682("path", {
    d: "M10 2h4m4 0h.389v0A3.611 3.611 0 0 1 22 5.611V6M2 6v-.389 0A3.611 3.611 0 0 1 5.611 2v0H6m-4 8v4m0 4v.389A3.611 3.611 0 0 0 5.611 22v0H6m4 0h4m4 0h.389v0A3.611 3.611 0 0 0 22 18.389v0V18m0-4v-4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSelection_default = SvgIconSelection;

// src/icons/line/IconSend2.tsx
import { jsx as _jsx683, jsxs as _jsxs19 } from "react/jsx-runtime";
var SvgIconSend2 = (props) => /* @__PURE__ */ _jsxs19("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx683("g", {
    clipPath: "url(#a)",
    children: /* @__PURE__ */ _jsx683("path", {
      d: "m11 13 1.534 3.99c1.055 2.743 1.583 4.114 2.291 4.459a2 2 0 0 0 1.916-.09c.673-.41 1.07-1.825 1.862-4.655l2.863-10.226c.502-1.791.752-2.687.52-3.302a2 2 0 0 0-1.162-1.162c-.615-.233-1.51.018-3.302.52L7.296 5.397C4.466 6.19 3.05 6.586 2.64 7.258a2 2 0 0 0-.09 1.917c.345.708 1.716 1.236 4.459 2.29L11 13Zm0 0 2.5-2.5",
      stroke: props.color,
      strokeWidth: 1.5,
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  }), /* @__PURE__ */ _jsx683("defs", {
    children: /* @__PURE__ */ _jsx683("clipPath", {
      id: "a",
      children: /* @__PURE__ */ _jsx683("path", {
        fill: "#fff",
        d: "M0 0h24v24H0z"
      })
    })
  })]
});
var IconSend2_default = SvgIconSend2;

// src/icons/line/IconSend.tsx
import { jsx as _jsx684, jsxs as _jsxs20 } from "react/jsx-runtime";
var SvgIconSend = (props) => /* @__PURE__ */ _jsxs20("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx684("g", {
    clipPath: "url(#a)",
    children: /* @__PURE__ */ _jsx684("path", {
      d: "m7 12-2.118 3.441c-1.756 2.854-2.634 4.28-2.452 5.078a2 2 0 0 0 1.335 1.458c.778.251 2.277-.498 5.274-1.996l10.237-5.119c1.842-.921 2.763-1.382 3.059-2.005a2 2 0 0 0 0-1.714c-.296-.623-1.217-1.084-3.06-2.005L9.04 4.019C6.042 2.521 4.543 1.772 3.765 2.023A2 2 0 0 0 2.43 3.48c-.182.797.696 2.224 2.452 5.078L7 12Zm0 0h4",
      stroke: props.color,
      strokeWidth: 1.5,
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  }), /* @__PURE__ */ _jsx684("defs", {
    children: /* @__PURE__ */ _jsx684("clipPath", {
      id: "a",
      children: /* @__PURE__ */ _jsx684("path", {
        fill: "#fff",
        d: "M0 0h24v24H0z"
      })
    })
  })]
});
var IconSend_default = SvgIconSend;

// src/icons/line/IconServer2.tsx
import { jsx as _jsx685 } from "react/jsx-runtime";
var SvgIconServer2 = (props) => /* @__PURE__ */ _jsx685("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx685("path", {
    d: "M21 12c0 1.657-4.03 3-9 3s-9-1.343-9-3m18-7c0 1.657-4.03 3-9 3S3 6.657 3 5m18 0c0-1.657-4.03-3-9-3S3 3.343 3 5m18 0v14c0 1.657-4.03 3-9 3s-9-1.343-9-3V5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconServer2_default = SvgIconServer2;

// src/icons/line/IconServer.tsx
import { jsx as _jsx686 } from "react/jsx-runtime";
var SvgIconServer = (props) => /* @__PURE__ */ _jsx686("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx686("path", {
    d: "M17.5 6a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m1 11a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M7 21h10c.93 0 1.395 0 1.776-.102a3 3 0 0 0 2.122-2.122C21 18.395 21 17.93 21 17v0c0-.93 0-1.395-.102-1.777a3 3 0 0 0-2.122-2.12C18.395 13 17.93 13 17 13H7c-.93 0-1.395 0-1.776.102a3 3 0 0 0-2.122 2.121C3 15.605 3 16.07 3 17v0c0 .93 0 1.395.102 1.776a3 3 0 0 0 2.122 2.122C5.605 21 6.07 21 7 21Zm0-11h10c.93 0 1.395 0 1.776-.102a3 3 0 0 0 2.122-2.122C21 7.395 21 6.93 21 6v0c0-.93 0-1.395-.102-1.776a3 3 0 0 0-2.122-2.122C18.395 2 17.93 2 17 2H7c-.93 0-1.395 0-1.776.102a3 3 0 0 0-2.122 2.122C3 4.605 3 5.07 3 6v0c0 .93 0 1.395.102 1.776a3 3 0 0 0 2.122 2.122C5.605 10 6.07 10 7 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconServer_default = SvgIconServer;

// src/icons/line/IconShare.tsx
import { jsx as _jsx687 } from "react/jsx-runtime";
var SvgIconShare = (props) => /* @__PURE__ */ _jsx687("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx687("path", {
    d: "M16 5a3 3 0 1 0 6 0 3 3 0 0 0-6 0Zm0 0H9a4 4 0 0 0-4 4v0m0 6a3 3 0 0 1 0-6m0 6a3 3 0 0 0 0-6m0 6v0a4 4 0 0 0 4 4h7m0 0a3 3 0 1 0 6 0 3 3 0 0 0-6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconShare_default = SvgIconShare;

// src/icons/line/IconShieldCheck.tsx
import { jsx as _jsx688 } from "react/jsx-runtime";
var SvgIconShieldCheck = (props) => /* @__PURE__ */ _jsx688("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx688("path", {
    d: "m9 12 2 2 4.5-4.5M9.4 2.155 5.85 3.733c-1.031.459-1.547.688-1.925 1.05a3 3 0 0 0-.747 1.15C3 6.426 3 6.99 3 8.119v5.456c0 1.252 0 1.878.173 2.447.152.503.403.97.737 1.377.377.459.898.806 1.94 1.5l3.487 2.326c.963.641 1.444.962 1.964 1.087.46.11.938.11 1.398 0 .52-.125 1.001-.446 1.964-1.087L18.15 18.9c1.042-.695 1.563-1.042 1.94-1.5a4 4 0 0 0 .737-1.378c.173-.569.173-1.195.173-2.447V8.119c0-1.129 0-1.693-.178-2.186a3 3 0 0 0-.747-1.15c-.378-.362-.894-.591-1.925-1.05L14.6 2.155c-.958-.425-1.436-.638-1.934-.722a4 4 0 0 0-1.332 0c-.498.084-.976.297-1.933.722Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconShieldCheck_default = SvgIconShieldCheck;

// src/icons/line/IconShieldCross.tsx
import { jsx as _jsx689 } from "react/jsx-runtime";
var SvgIconShieldCross = (props) => /* @__PURE__ */ _jsx689("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx689("path", {
    d: "m2 2 2.371 2.371M21 21l-2.34-2.34m0 0c-.967.944-2.161 1.54-4.046 2.483-1.02.51-1.956.857-2.614.857-.658 0-1.594-.347-2.615-.857-2.57-1.286-3.856-1.929-5.033-3.687a9.648 9.648 0 0 1-.306-.495C3 15.12 3 13.208 3 9.38c0-2.106 0-3.16.617-4.137.049-.077.126-.19.18-.262.179-.239.366-.437.574-.61M18.66 18.658 4.371 4.371M20.696 15C21 13.61 21 11.915 21 9.38c0-2.105 0-3.158-.617-4.136a4.68 4.68 0 0 0-.18-.262c-.693-.926-1.512-1.24-3.152-1.867C15.48 2.513 13.654 2 12 2c-1.296 0-2.698.315-4 .741",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconShieldCross_default = SvgIconShieldCross;

// src/icons/line/IconShield.tsx
import { jsx as _jsx690 } from "react/jsx-runtime";
var SvgIconShield = (props) => /* @__PURE__ */ _jsx690("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx690("path", {
    d: "M3 8.12c0-1.13 0-1.694.178-2.187a3 3 0 0 1 .747-1.15c.378-.362.894-.591 1.926-1.05L9.4 2.155c.957-.425 1.435-.638 1.933-.722a4 4 0 0 1 1.332 0c.498.084.976.297 1.933.722l3.55 1.578c1.032.459 1.548.688 1.926 1.05a3 3 0 0 1 .747 1.15C21 6.426 21 6.99 21 8.119v5.456c0 1.252 0 1.878-.173 2.447a4 4 0 0 1-.737 1.377c-.377.459-.898.806-1.94 1.5l-3.487 2.326c-.963.641-1.444.962-1.964 1.087-.46.11-.938.11-1.398 0-.52-.125-1.001-.446-1.964-1.087L5.85 18.9c-1.042-.695-1.563-1.042-1.94-1.5a4.001 4.001 0 0 1-.737-1.378C3 15.453 3 14.827 3 13.575V8.119Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconShield_default = SvgIconShield;

// src/icons/line/IconShoppingBag2.tsx
import { jsx as _jsx691 } from "react/jsx-runtime";
var SvgIconShoppingBag2 = (props) => /* @__PURE__ */ _jsx691("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx691("path", {
    d: "M8 8V6a4 4 0 1 1 8 0v2m-7.5 3.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m9 0a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M10.582 22h2.836c1.98 0 2.971 0 3.766-.223a6 6 0 0 0 4.256-4.566c.166-.808.097-1.796-.042-3.772v0c-.122-1.742-.184-2.612-.421-3.328a6 6 0 0 0-4.21-3.924C16.037 6 15.163 6 13.417 6h-2.835c-1.745 0-2.618 0-3.35.187a6 6 0 0 0-4.209 3.924c-.237.716-.299 1.586-.421 3.328v0c-.139 1.976-.208 2.964-.042 3.772a6 6 0 0 0 4.256 4.566C7.611 22 8.601 22 10.582 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconShoppingBag2_default = SvgIconShoppingBag2;

// src/icons/line/IconShoppingBag.tsx
import { jsx as _jsx692 } from "react/jsx-runtime";
var SvgIconShoppingBag = (props) => /* @__PURE__ */ _jsx692("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx692("path", {
    d: "M9 7v2a3 3 0 0 0 3 3v0a3 3 0 0 0 3-3V7m-3 15v0c3.068 0 4.602 0 5.766-.517a6 6 0 0 0 3.272-3.639c.392-1.212.23-2.737-.095-5.788l-.236-2.225c-.246-2.319-.37-3.478-.806-4.4a6 6 0 0 0-3.441-3.095C15.498 2 14.332 2 12 2v0c-2.332 0-3.498 0-4.46.336A6 6 0 0 0 4.1 5.432c-.436.92-.56 2.08-.806 4.399l-.236 2.225c-.325 3.051-.487 4.576-.095 5.788a6 6 0 0 0 3.272 3.639C7.398 22 8.932 22 12 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconShoppingBag_default = SvgIconShoppingBag;

// src/icons/line/IconShoppingBasket.tsx
import { jsx as _jsx693 } from "react/jsx-runtime";
var SvgIconShoppingBasket = (props) => /* @__PURE__ */ _jsx693("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx693("path", {
    d: "m6 7 3-5m6 0 3 5M9.247 22h5.506c1.87 0 2.804 0 3.57-.335A4 4 0 0 0 20 20.29c.478-.686.661-1.602 1.028-3.435l.44-2.2c.525-2.624.787-3.936.432-4.963a4 4 0 0 0-1.756-2.142C19.207 7 17.869 7 15.193 7H8.807c-2.676 0-4.014 0-4.952.55A4 4 0 0 0 2.1 9.692c-.355 1.027-.093 2.34.432 4.963l.44 2.2c.367 1.833.55 2.75 1.028 3.435a4 4 0 0 0 1.678 1.375c.766.335 1.7.335 3.57.335Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconShoppingBasket_default = SvgIconShoppingBasket;

// src/icons/line/IconShoppingCart2.tsx
import { jsx as _jsx694 } from "react/jsx-runtime";
var SvgIconShoppingCart2 = (props) => /* @__PURE__ */ _jsx694("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx694("path", {
    d: "M1 1h.5v0c.108 0 .161 0 .21.002a3 3 0 0 1 2.804 2.23l.048.204.13.564m0 0 1.45 6.28c.306 1.328.46 1.992.82 2.488a3 3 0 0 0 1.247.991C8.773 14 9.455 14 10.82 14h5.61c1.281 0 1.922 0 2.462-.218a3 3 0 0 0 1.219-.907c.364-.455.548-1.068.916-2.296l.738-2.46c.419-1.396.628-2.094.477-2.647a2 2 0 0 0-.869-1.168C20.886 4 20.157 4 18.7 4H4.692ZM14 14H6a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h15m-10 2a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm8 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconShoppingCart2_default = SvgIconShoppingCart2;

// src/icons/line/IconShoppingCart.tsx
import { jsx as _jsx695 } from "react/jsx-runtime";
var SvgIconShoppingCart = (props) => /* @__PURE__ */ _jsx695("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx695("path", {
    d: "M1 1h.5v0c.226 0 .339 0 .44.007a3 3 0 0 1 2.62 1.976c.034.095.065.204.127.42l.17.597m0 0 1.817 6.358c.475 1.664.713 2.496 1.198 3.114a4 4 0 0 0 1.633 1.231c.727.297 1.592.297 3.322.297h2.285c1.75 0 2.626 0 3.359-.302a4 4 0 0 0 1.64-1.253c.484-.627.715-1.472 1.175-3.161l.06-.221c.563-2.061.844-3.092.605-3.906a3 3 0 0 0-1.308-1.713C19.92 4 18.853 4 16.716 4H4.857ZM12 20a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm8 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconShoppingCart_default = SvgIconShoppingCart;

// src/icons/line/IconShuffle.tsx
import { jsx as _jsx696 } from "react/jsx-runtime";
var SvgIconShuffle = (props) => /* @__PURE__ */ _jsx696("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx696("path", {
    d: "M2 20h2.252c1.34 0 2.01 0 2.568-.234a3 3 0 0 0 1.24-.968c.361-.484.524-1.134.849-2.434l2.182-8.728c.325-1.3.488-1.95.85-2.434a3 3 0 0 1 1.24-.968C13.737 4 14.407 4 15.747 4H22m0 0-3-3m3 3-3 3M2 4h6m14 16-3 3m3-3-3-3m3 3H12",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconShuffle_default = SvgIconShuffle;

// src/icons/line/IconSketch.tsx
import { jsx as _jsx697 } from "react/jsx-runtime";
var SvgIconSketch = (props) => /* @__PURE__ */ _jsx697("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx697("path", {
    d: "M12 21 3.208 10.952c-.609-.696-.913-1.044-1.04-1.439a2 2 0 0 1-.04-1.075c.096-.403.373-.773.928-1.513L5.04 4.28c.352-.47.528-.704.751-.873a2 2 0 0 1 .66-.33C6.72 3 7.013 3 7.6 3H12m0 18 8.792-10.048c.609-.696.913-1.044 1.04-1.439a2 2 0 0 0 .04-1.075c-.096-.403-.373-.773-.928-1.513L18.96 4.28c-.352-.47-.528-.704-.751-.873a1.999 1.999 0 0 0-.66-.33C17.28 3 16.987 3 16.4 3H12m0 18L8 9m4 12 4-12m-4-6L8 9m4-6 4 6M8 9h8M8 9H2.5M16 9h5.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSketch_default = SvgIconSketch;

// src/icons/line/IconSlash.tsx
import { jsx as _jsx698 } from "react/jsx-runtime";
var SvgIconSlash = (props) => /* @__PURE__ */ _jsx698("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx698("path", {
    d: "M19 19 5 5m17 7c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSlash_default = SvgIconSlash;

// src/icons/line/IconSliderHorizontal2.tsx
import { jsx as _jsx699 } from "react/jsx-runtime";
var SvgIconSliderHorizontal2 = (props) => /* @__PURE__ */ _jsx699("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx699("path", {
    d: "M18 6v12m4-9v6M6.8 21h2.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C14 18.72 14 17.88 14 16.2V7.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C11.72 3 10.88 3 9.2 3H6.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 5.28 2 6.12 2 7.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 21 5.12 21 6.8 21Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSliderHorizontal2_default = SvgIconSliderHorizontal2;

// src/icons/line/IconSliderHorizontal.tsx
import { jsx as _jsx700 } from "react/jsx-runtime";
var SvgIconSliderHorizontal = (props) => /* @__PURE__ */ _jsx700("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx700("path", {
    d: "M2 4v16M22 4v16m-11.2 1h2.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C18 18.72 18 17.88 18 16.2V7.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C15.72 3 14.88 3 13.2 3h-2.4c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C6 5.28 6 6.12 6 7.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C8.28 21 9.12 21 10.8 21Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSliderHorizontal_default = SvgIconSliderHorizontal;

// src/icons/line/IconSliderVertical2.tsx
import { jsx as _jsx701 } from "react/jsx-runtime";
var SvgIconSliderVertical2 = (props) => /* @__PURE__ */ _jsx701("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx701("path", {
    d: "M6 18h12m-9 4h6m-7.2-8h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 11.72 21 10.88 21 9.2V6.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C18.72 2 17.88 2 16.2 2H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 4.28 3 5.12 3 6.8v2.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 14 6.12 14 7.8 14Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSliderVertical2_default = SvgIconSliderVertical2;

// src/icons/line/IconSliderVertical.tsx
import { jsx as _jsx702 } from "react/jsx-runtime";
var SvgIconSliderVertical = (props) => /* @__PURE__ */ _jsx702("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx702("path", {
    d: "M3 2h18M3 22h18M7.8 18h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C21 15.72 21 14.88 21 13.2v-2.4c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C18.72 6 17.88 6 16.2 6H7.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C3 8.28 3 9.12 3 10.8v2.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C5.28 18 6.12 18 7.8 18Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSliderVertical_default = SvgIconSliderVertical;

// src/icons/line/IconSlidersHoriz2.tsx
import { jsx as _jsx703 } from "react/jsx-runtime";
var SvgIconSlidersHoriz2 = (props) => /* @__PURE__ */ _jsx703("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx703("path", {
    d: "M2 18h3M19 6h3m-6 12h6M2 6h6m.2 16h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C13 20.48 13 19.92 13 18.8v-1.6c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C11.48 14 10.92 14 9.8 14H8.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C5 15.52 5 16.08 5 17.2v1.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C6.52 22 7.08 22 8.2 22Zm6-12h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C19 8.48 19 7.92 19 6.8V5.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C17.48 2 16.92 2 15.8 2h-1.6c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C11 3.52 11 4.08 11 5.2v1.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C12.52 10 13.08 10 14.2 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconSlidersHoriz2_default = SvgIconSlidersHoriz2;

// src/icons/line/IconSlidersHoriz.tsx
import { jsx as _jsx704 } from "react/jsx-runtime";
var SvgIconSlidersHoriz = (props) => /* @__PURE__ */ _jsx704("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx704("path", {
    d: "M19 19h3m-3 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM2 12h3m0 0a3 3 0 1 0 6 0 3 3 0 0 0-6 0Zm14-7h3m-3 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0ZM2 19h8m4-7h8M2 5h8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconSlidersHoriz_default = SvgIconSlidersHoriz;

// src/icons/line/IconSlidersVert2.tsx
import { jsx as _jsx705 } from "react/jsx-runtime";
var SvgIconSlidersVert2 = (props) => /* @__PURE__ */ _jsx705("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx705("path", {
    d: "M6 5V2m0 20v-6m12 6v-3m0-11V2m-.8 17h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C22 17.48 22 16.92 22 15.8v-1.6c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C20.48 11 19.92 11 18.8 11h-1.6c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C14 12.52 14 13.08 14 14.2v1.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C15.52 19 16.08 19 17.2 19Zm-12-6h1.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C10 11.48 10 10.92 10 9.8V8.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C8.48 5 7.92 5 6.8 5H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 6.52 2 7.08 2 8.2v1.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C3.52 13 4.08 13 5.2 13Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconSlidersVert2_default = SvgIconSlidersVert2;

// src/icons/line/IconSlidersVert.tsx
import { jsx as _jsx706 } from "react/jsx-runtime";
var SvgIconSlidersVert = (props) => /* @__PURE__ */ _jsx706("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx706("path", {
    d: "M19 5V2m0 3a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm-7 17v-3m0 0a3 3 0 1 1 0-6 3 3 0 0 1 0 6ZM5 5V2m0 3a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm14 17v-8m-7-4V2M5 22v-8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconSlidersVert_default = SvgIconSlidersVert;

// src/icons/line/IconSmallCaps2Rec.tsx
import { jsx as _jsx707 } from "react/jsx-runtime";
var SvgIconSmallCaps2Rec = (props) => /* @__PURE__ */ _jsx707("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx707("path", {
    d: "M10 7v10m0-10H6m4 0h4m2 6v4m0-4h-2m2 0h2m-8 9h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSmallCaps2Rec_default = SvgIconSmallCaps2Rec;

// src/icons/line/IconSmallCaps2.tsx
import { jsx as _jsx708 } from "react/jsx-runtime";
var SvgIconSmallCaps2 = (props) => /* @__PURE__ */ _jsx708("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx708("path", {
    d: "M9 3v18M9 3H2m7 0h7m2 10v8m0-8h-4m4 0h4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSmallCaps2_default = SvgIconSmallCaps2;

// src/icons/line/IconSmallCapsRec.tsx
import { jsx as _jsx709 } from "react/jsx-runtime";
var SvgIconSmallCapsRec = (props) => /* @__PURE__ */ _jsx709("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx709("path", {
    d: "M10 6v12m0-12H6.6c-.56 0-.84 0-1.054.109a1 1 0 0 0-.437.437C5 6.76 5 7.04 5 7.6V8m5-2h3.4c.56 0 .84 0 1.054.109a1 1 0 0 1 .437.437C15 6.76 15 7.04 15 7.6V8m-5 10h1.5M10 18H8.5m8-5v5m0-5H14v1m2.5-1H19v1m-2.5 4h1m-1 0h-1M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSmallCapsRec_default = SvgIconSmallCapsRec;

// src/icons/line/IconSmallCaps.tsx
import { jsx as _jsx710 } from "react/jsx-runtime";
var SvgIconSmallCaps = (props) => /* @__PURE__ */ _jsx710("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx710("path", {
    d: "M18 13v8m0-8h-3.2c-.28 0-.42 0-.527.055a.5.5 0 0 0-.218.218C14 13.38 14 13.52 14 13.8v.7m4-1.5h3.2c.28 0 .42 0 .527.055a.5.5 0 0 1 .218.218c.055.107.055.247.055.527v.7M18 21h1.5M18 21h-1.5M9 3v18M9 3H4.5c-.465 0-.697 0-.89.038A2 2 0 0 0 2.038 4.61C2 4.803 2 5.035 2 5.5V6m7-3h4.5c.465 0 .697 0 .89.038a2 2 0 0 1 1.572 1.572c.038.193.038.425.038.89V6M9 21h2m-2 0H7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSmallCaps_default = SvgIconSmallCaps;

// src/icons/line/IconSmartphone.tsx
import { jsx as _jsx711 } from "react/jsx-runtime";
var SvgIconSmartphone = (props) => /* @__PURE__ */ _jsx711("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx711("path", {
    d: "M12 6h.01M10 19h4M12.5 6a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm-1.1 16h1.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C19 18.96 19 17.84 19 15.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C15.96 2 14.84 2 12.6 2h-1.2c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C5 5.04 5 6.16 5 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C8.04 22 9.16 22 11.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSmartphone_default = SvgIconSmartphone;

// src/icons/line/IconSortNumberRec.tsx
import { jsx as _jsx712 } from "react/jsx-runtime";
var SvgIconSortNumberRec = (props) => /* @__PURE__ */ _jsx712("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx712("path", {
    d: "m6 15 3 3V5m5.667 5V5L13 6.563M14.667 10H16m-1.333 0h-1.334M16 14.5v.8c0 .651 0 .977-.075 1.243a2 2 0 0 1-1.382 1.382C14.277 18 13.95 18 13.3 18H13m3-3.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSortNumberRec_default = SvgIconSortNumberRec;

// src/icons/line/IconSortNumberReverseRec.tsx
import { jsx as _jsx713 } from "react/jsx-runtime";
var SvgIconSortNumberReverseRec = (props) => /* @__PURE__ */ _jsx713("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx713("path", {
    d: "m6 15 3 3V5m5.667 13v-5L13 14.563M14.667 18H16m-1.333 0h-1.334M16 6.5v.8c0 .651 0 .977-.075 1.243a2 2 0 0 1-1.382 1.382C14.277 10 13.95 10 13.3 10H13m3-3.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSortNumberReverseRec_default = SvgIconSortNumberReverseRec;

// src/icons/line/IconSortNumberReverse.tsx
import { jsx as _jsx714 } from "react/jsx-runtime";
var SvgIconSortNumberReverse = (props) => /* @__PURE__ */ _jsx714("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx714("path", {
    d: "M21 4v1.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C18.72 10 17.88 10 16.2 10H16M7 22l-4-4m4 4 4-4m-4 4v-8M7 3v7m12 12v-8l-2.5 2.5M19 22h2m-2 0h-2m4-18v0a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSortNumberReverse_default = SvgIconSortNumberReverse;

// src/icons/line/IconSortNumber.tsx
import { jsx as _jsx715 } from "react/jsx-runtime";
var SvgIconSortNumber = (props) => /* @__PURE__ */ _jsx715("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx715("path", {
    d: "M21 16v1.2c0 1.68 0 2.52-.327 3.162a3 3 0 0 1-1.311 1.311C18.72 22 17.88 22 16.2 22H16m-9 0-4-4m4 4 4-4m-4 4v-8M7 3v7m12 0V2l-2.5 2.5M19 10h2m-2 0h-2m4 6v0a2 2 0 0 0-2-2h-1a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSortNumber_default = SvgIconSortNumber;

// src/icons/line/IconSortTextRec.tsx
import { jsx as _jsx716 } from "react/jsx-runtime";
var SvgIconSortTextRec = (props) => /* @__PURE__ */ _jsx716("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx716("path", {
    d: "m6 15 3 3V5m4 8h3l-3 5h3m-3-8V8.125m0 0V6.5a1.5 1.5 0 0 1 3 0v1.625m-3 0h3m0 0V10m-4.4 12h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSortTextRec_default = SvgIconSortTextRec;

// src/icons/line/IconSortTextReverseRec.tsx
import { jsx as _jsx717 } from "react/jsx-runtime";
var SvgIconSortTextReverseRec = (props) => /* @__PURE__ */ _jsx717("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx717("path", {
    d: "m6 15 3 3V5m4 0h3l-3 5h3m-3 8v-1.875m0 0V14.5a1.5 1.5 0 0 1 3 0v1.625m-3 0h3m0 0V18m-4.4 4h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSortTextReverseRec_default = SvgIconSortTextReverseRec;

// src/icons/line/IconSortTextReverse.tsx
import { jsx as _jsx718 } from "react/jsx-runtime";
var SvgIconSortTextReverse = (props) => /* @__PURE__ */ _jsx718("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx718("path", {
    d: "m7 22-4-4m4 4 4-4m-4 4v-8M7 3v7m9-8h5l-5 8h5m-5 12v-3m0 0v-2.5a2.5 2.5 0 0 1 5 0V19m-5 0h5m0 0v3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSortTextReverse_default = SvgIconSortTextReverse;

// src/icons/line/IconSortText.tsx
import { jsx as _jsx719 } from "react/jsx-runtime";
var SvgIconSortText = (props) => /* @__PURE__ */ _jsx719("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx719("path", {
    d: "m7 22-4-4m4 4 4-4m-4 4v-8M7 3v7m9 4h5l-5 8h5m-5-12V7m0 0V4.5a2.5 2.5 0 0 1 5 0V7m-5 0h5m0 0v3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSortText_default = SvgIconSortText;

// src/icons/line/IconSoundcloud.tsx
import { jsx as _jsx720 } from "react/jsx-runtime";
var SvgIconSoundcloud = (props) => /* @__PURE__ */ _jsx720("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx720("path", {
    d: "M17.881 10.921A6.507 6.507 0 0 0 11.395 5c-.832 0-1.644.164-2.36.44L9 18h10.31c2.038 0 3.69-1.65 3.69-3.682 0-2.604-2.765-4.38-5.119-3.397Zm0 0c.04.36.019 1.279-.381 2.079m-12-5.5V18M2 11.5V18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSoundcloud_default = SvgIconSoundcloud;

// src/icons/line/IconSoundwaveRec.tsx
import { jsx as _jsx721 } from "react/jsx-runtime";
var SvgIconSoundwaveRec = (props) => /* @__PURE__ */ _jsx721("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx721("path", {
    d: "M9 9v6m-3-4v2m6-6v10m3-8v6m3-4v2m-8 9h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSoundwaveRec_default = SvgIconSoundwaveRec;

// src/icons/line/IconSoundwave.tsx
import { jsx as _jsx722 } from "react/jsx-runtime";
var SvgIconSoundwave = (props) => /* @__PURE__ */ _jsx722("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx722("path", {
    d: "M7 7v10m-5-6v2M12 3v18m5-14v10m5-6v2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSoundwave_default = SvgIconSoundwave;

// src/icons/line/IconSpeaker.tsx
import { jsx as _jsx723 } from "react/jsx-runtime";
var SvgIconSpeaker = (props) => /* @__PURE__ */ _jsx723("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx723("path", {
    d: "M14 17h.01m.49 0a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm.5-8a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm-3 13v0c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C20 18.2 20 16.8 20 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C16.2 2 14.8 2 12 2v0c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 4.545 4.73C4 5.8 4 7.2 4 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C7.8 22 9.2 22 12 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSpeaker_default = SvgIconSpeaker;

// src/icons/line/IconSpecialCharachersRec.tsx
import { jsx as _jsx724 } from "react/jsx-runtime";
var SvgIconSpecialCharachersRec = (props) => /* @__PURE__ */ _jsx724("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx724("path", {
    d: "M6 17h3.249C7.319 16.032 6 14.077 6 11.823 6 8.607 8.686 6 12 6s6 2.607 6 5.823c0 2.254-1.319 4.209-3.248 5.177H18m-8 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSpecialCharachersRec_default = SvgIconSpecialCharachersRec;

// src/icons/line/IconSpecialCharachers.tsx
import { jsx as _jsx725 } from "react/jsx-runtime";
var SvgIconSpecialCharachers = (props) => /* @__PURE__ */ _jsx725("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx725("path", {
    d: "M2 21h5.414A10.065 10.065 0 0 1 2 12.059C2 6.503 6.477 2 12 2s10 4.503 10 10.059c0 3.893-2.198 7.269-5.414 8.941H22",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSpecialCharachers_default = SvgIconSpecialCharachers;

// src/icons/line/IconSpeedRec.tsx
import { jsx as _jsx726 } from "react/jsx-runtime";
var SvgIconSpeedRec = (props) => /* @__PURE__ */ _jsx726("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx726("path", {
    d: "M6 15h3m-3-3a6 6 0 1 1 6 6H8.974M12 11v2m-.4 9h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSpeedRec_default = SvgIconSpeedRec;

// src/icons/line/IconSpeed.tsx
import { jsx as _jsx727, jsxs as _jsxs21 } from "react/jsx-runtime";
var SvgIconSpeed = (props) => /* @__PURE__ */ _jsxs21("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx727("g", {
    clipPath: "url(#a)",
    children: /* @__PURE__ */ _jsx727("path", {
      d: "M2 16h5m-3 3h6m-7-6a9 9 0 1 1 9 9H7m7-21h-4m2 9v4",
      stroke: props.color,
      strokeWidth: 1.5,
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })
  }), /* @__PURE__ */ _jsx727("defs", {
    children: /* @__PURE__ */ _jsx727("clipPath", {
      id: "a",
      children: /* @__PURE__ */ _jsx727("path", {
        fill: "#fff",
        d: "M0 0h24v24H0z"
      })
    })
  })]
});
var IconSpeed_default = SvgIconSpeed;

// src/icons/line/IconSpotify.tsx
import { jsx as _jsx728 } from "react/jsx-runtime";
var SvgIconSpotify = (props) => /* @__PURE__ */ _jsx728("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx728("path", {
    d: "M7 9.533s6-2.033 11 1.11M7.5 12.5s5-1.858 9.5 1m-9 1.896s4-1.396 7.5.826M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSpotify_default = SvgIconSpotify;

// src/icons/line/IconStack.tsx
import { jsx as _jsx729 } from "react/jsx-runtime";
var SvgIconStack = (props) => /* @__PURE__ */ _jsx729("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx729("path", {
    d: "M6 9v0c0-.932 0-1.398.152-1.765a2 2 0 0 1 1.083-1.083C7.602 6 8.068 6 9 6h5.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C18 7.52 18 8.08 18 9.2V15c0 .932 0 1.398-.152 1.765a2 2 0 0 1-1.083 1.083C16.398 18 15.932 18 15 18v0M10 5v0c0-.932 0-1.398.152-1.765a2 2 0 0 1 1.083-1.083C11.602 2 12.068 2 13 2h5.8c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C22 3.52 22 4.08 22 5.2V11c0 .932 0 1.398-.152 1.765a2 2 0 0 1-1.083 1.083C20.398 14 19.932 14 19 14v0M5.2 22h5.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C14 20.48 14 19.92 14 18.8v-5.6c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C12.48 10 11.92 10 10.8 10H5.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C2 11.52 2 12.08 2 13.2v5.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C3.52 22 4.08 22 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconStack_default = SvgIconStack;

// src/icons/line/IconStarSlash.tsx
import { jsx as _jsx730 } from "react/jsx-runtime";
var SvgIconStarSlash = (props) => /* @__PURE__ */ _jsx730("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx730("path", {
    d: "m3 3 4.084 4.084M22 22 19.7 19.7m0 0c.08.45.074.791-.036 1.06a2 2 0 0 1-1.335 1.176c-.732.196-1.859-.367-4.111-1.493l-.645-.323c-.663-.331-.995-.497-1.347-.537-.15-.017-.3-.017-.45 0-.352.04-.684.206-1.347.537l-.645.323c-2.252 1.126-3.378 1.69-4.111 1.493a2 2 0 0 1-1.335-1.176c-.287-.702.13-1.89.964-4.267.256-.73.384-1.094.378-1.452a2 2 0 0 0-.215-.868c-.161-.32-.445-.583-1.011-1.109l-.14-.13c-1.796-1.667-2.693-2.5-2.748-3.29a2 2 0 0 1 .475-1.436c.513-.602 1.73-.737 4.165-1.007.38-.043.657-.074.88-.116M19.698 19.7 7.084 7.084M9 5c1.075-2.147 1.646-3.23 2.47-3.456a2 2 0 0 1 1.06 0c.86.236 1.445 1.406 2.615 3.745l.047.095c.313.627.47.94.706 1.173a2 2 0 0 0 .553.385c.3.142.648.18 1.344.258 2.435.27 3.652.405 4.165 1.007a2 2 0 0 1 .475 1.436c-.055.79-.952 1.623-2.747 3.29-.39.362-.786.718-1.188 1.067",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconStarSlash_default = SvgIconStarSlash;

// src/icons/line/IconStar.tsx
import { jsx as _jsx731 } from "react/jsx-runtime";
var SvgIconStar = (props) => /* @__PURE__ */ _jsx731("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx731("path", {
    d: "M9.138 4.724c.921-1.842 1.382-2.763 2.005-3.059a2 2 0 0 1 1.714 0c.623.296 1.084 1.217 2.005 3.06l.36.718c.284.569.426.853.634 1.071.184.193.405.347.65.453.276.12.592.155 1.224.225l.326.036c2.209.246 3.313.369 3.812.878a2 2 0 0 1 .553 1.674c-.098.706-.912 1.462-2.54 2.974l-.345.32c-.556.516-.834.774-.994 1.086a2.001 2.001 0 0 0-.22.893c-.005.352.123.714.377 1.44v0c.83 2.366 1.246 3.549.974 4.24a2 2 0 0 1-1.37 1.209c-.721.182-1.826-.37-4.036-1.475l-.836-.418c-.525-.262-.787-.394-1.062-.445a2.001 2.001 0 0 0-.738 0c-.275.051-.537.183-1.062.445l-.836.418c-2.21 1.105-3.315 1.657-4.035 1.475a2 2 0 0 1-1.371-1.208c-.272-.692.144-1.875.974-4.242v0c.254-.725.382-1.087.378-1.439a2 2 0 0 0-.22-.893c-.161-.312-.44-.57-.995-1.086l-.344-.32C2.49 11.242 1.677 10.486 1.58 9.78a2 2 0 0 1 .552-1.674c.5-.51 1.603-.632 3.812-.878l.326-.036c.632-.07.948-.105 1.224-.225a2 2 0 0 0 .65-.453c.208-.218.35-.502.635-1.071l.359-.719Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconStar_default = SvgIconStar;

// src/icons/line/IconSticker.tsx
import { jsx as _jsx732 } from "react/jsx-runtime";
var SvgIconSticker = (props) => /* @__PURE__ */ _jsx732("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx732("path", {
    d: "M11.637 2A7.78 7.78 0 0 0 22 12.363M11.637 2C6.282 2.193 2 6.595 2 11.997 2 17.52 6.479 22 12.003 22c5.402 0 9.805-4.282 9.997-9.637M11.637 2 22 12.363",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSticker_default = SvgIconSticker;

// src/icons/line/IconStopCircle.tsx
import { jsx as _jsx733, jsxs as _jsxs22 } from "react/jsx-runtime";
var SvgIconStopCircle = (props) => /* @__PURE__ */ _jsxs22("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx733("path", {
    d: "M9 11.4c0-.84 0-1.26.163-1.581a1.5 1.5 0 0 1 .656-.656C10.139 9 10.559 9 11.4 9h1.2c.84 0 1.26 0 1.581.163a1.5 1.5 0 0 1 .655.656c.164.32.164.74.164 1.581v1.2c0 .84 0 1.26-.164 1.581a1.5 1.5 0 0 1-.655.655c-.32.164-.74.164-1.581.164h-1.2c-.84 0-1.26 0-1.581-.164a1.5 1.5 0 0 1-.656-.655C9 13.861 9 13.441 9 12.6v-1.2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx733("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconStopCircle_default = SvgIconStopCircle;

// src/icons/line/IconStop.tsx
import { jsx as _jsx734 } from "react/jsx-runtime";
var SvgIconStop = (props) => /* @__PURE__ */ _jsx734("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx734("rect", {
    x: 2,
    y: 2,
    width: 20,
    height: 20,
    rx: 6,
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconStop_default = SvgIconStop;

// src/icons/line/IconStopwatchSlash.tsx
import { jsx as _jsx735 } from "react/jsx-runtime";
var SvgIconStopwatchSlash = (props) => /* @__PURE__ */ _jsx735("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx735("path", {
    d: "M8 4.936A8.963 8.963 0 0 1 12 4a8.972 8.972 0 0 1 6.364 2.636M20.064 17A8.963 8.963 0 0 0 21 13a8.972 8.972 0 0 0-2.636-6.364m0 12.728A9 9 0 1 1 5.636 6.636m12.728 12.728L21 22m-2.636-2.636L5.636 6.636M3 4l2.636 2.636M20.5 4.5l-2.136 2.136M14 1h-4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconStopwatchSlash_default = SvgIconStopwatchSlash;

// src/icons/line/IconStopwatch.tsx
import { jsx as _jsx736 } from "react/jsx-runtime";
var SvgIconStopwatch = (props) => /* @__PURE__ */ _jsx736("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx736("path", {
    d: "m20.5 4.5-2.136 2.136m0 0A9 9 0 1 0 5.636 19.364 9 9 0 0 0 18.364 6.636ZM12 10v4m2-13h-4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconStopwatch_default = SvgIconStopwatch;

// src/icons/line/IconSun.tsx
import { jsx as _jsx737 } from "react/jsx-runtime";
var SvgIconSun = (props) => /* @__PURE__ */ _jsx737("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx737("path", {
    d: "M12 20v2m0-20v2m8 8h2M2 12h2m14 6 1.5 1.5m-15-15L6 6m12 0 1.5-1.5m-15 15L6 18m11-6a5 5 0 1 1-10 0 5 5 0 0 1 10 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSun_default = SvgIconSun;

// src/icons/line/IconSunrise.tsx
import { jsx as _jsx738 } from "react/jsx-runtime";
var SvgIconSunrise = (props) => /* @__PURE__ */ _jsx738("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx738("path", {
    d: "M7 18a5 5 0 0 1 10 0M6 22h12M12 2l3 3m-3-3L9 5m3-3v7m10 9H2m1.929-8.071L6.05 12.05m11.9 0 2.121-2.122",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSunrise_default = SvgIconSunrise;

// src/icons/line/IconSunset.tsx
import { jsx as _jsx739 } from "react/jsx-runtime";
var SvgIconSunset = (props) => /* @__PURE__ */ _jsx739("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx739("path", {
    d: "M7 18a5 5 0 0 1 10 0M6 22h12M12 9l3-3m-3 3L9 6m3 3V2m10 16H2m1.929-8.071L6.05 12.05m11.9 0 2.121-2.122",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSunset_default = SvgIconSunset;

// src/icons/line/IconSwatchbook.tsx
import { jsx as _jsx740 } from "react/jsx-runtime";
var SvgIconSwatchbook = (props) => /* @__PURE__ */ _jsx740("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx740("path", {
    d: "M8 22h9.5c1.398 0 2.097 0 2.648-.228a3 3 0 0 0 1.624-1.624C22 19.597 22 18.898 22 17.5V17c0-.93 0-1.395-.102-1.777a3 3 0 0 0-2.122-2.12C19.395 13 18.93 13 18 13v0m-6-6 1.119-1.12c.706-.706 1.06-1.059 1.431-1.264a3 3 0 0 1 2.899 0c.372.205.725.558 1.431 1.265v0c.649.648.973.972 1.171 1.318a3 3 0 0 1 .13 2.734c-.165.363-.457.716-1.04 1.423L12 20m-4.5-3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m5.5 5V6.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C9.72 2 8.88 2 7.2 2h-.4c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 4.28 2 5.12 2 6.8v10.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 22 5.12 22 6.8 22H12Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconSwatchbook_default = SvgIconSwatchbook;

// src/icons/line/IconTablet.tsx
import { jsx as _jsx741 } from "react/jsx-runtime";
var SvgIconTablet = (props) => /* @__PURE__ */ _jsx741("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx741("path", {
    d: "M12 6h.01M10 19h4M12.5 6a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0Zm-2.1 16h3.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C20 18.96 20 17.84 20 15.6V8.4c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C16.96 2 15.84 2 13.6 2h-3.2c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C4 5.04 4 6.16 4 8.4v7.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C7.04 22 8.16 22 10.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTablet_default = SvgIconTablet;

// src/icons/line/IconTag2.tsx
import { jsx as _jsx742, jsxs as _jsxs23 } from "react/jsx-runtime";
var SvgIconTag2 = (props) => /* @__PURE__ */ _jsxs23("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx742("path", {
    d: "M3 10.349c0 .978 0 1.468.11 1.928.099.408.26.798.48 1.156.247.404.593.75 1.285 1.441l3.6 3.6c1.584 1.585 2.376 2.377 3.289 2.673a4 4 0 0 0 2.472 0c.913-.296 1.705-1.088 3.29-2.672l.948-.95c1.585-1.584 2.377-2.376 2.673-3.289a4 4 0 0 0 0-2.472c-.296-.913-1.088-1.705-2.672-3.29l-3.6-3.6c-.692-.691-1.038-1.037-1.442-1.285a3.998 3.998 0 0 0-1.156-.478C11.817 3 11.327 3 10.349 3H9.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C3 6.04 3 7.16 3 9.4v.949Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx742("path", {
    d: "M12 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconTag2_default = SvgIconTag2;

// src/icons/line/IconTag.tsx
import { jsx as _jsx743 } from "react/jsx-runtime";
var SvgIconTag = (props) => /* @__PURE__ */ _jsx743("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx743("path", {
    d: "M12 12a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTag_default = SvgIconTag;

// src/icons/line/IconTags2.tsx
import { jsx as _jsx744 } from "react/jsx-runtime";
var SvgIconTags2 = (props) => /* @__PURE__ */ _jsx744("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx744("path", {
    d: "m15 22 .5-.5 1.58-1.58c2.773-2.773 4.159-4.159 4.678-5.757a7 7 0 0 0 0-4.326c-.52-1.598-1.905-2.984-4.678-5.757L16 3l-.5-.5M11 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm5.474-2.525-2.6-2.6c-.691-.692-1.037-1.038-1.44-1.286a3.998 3.998 0 0 0-1.157-.478C10.817 3 10.327 3 9.349 3H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 6.04 2 7.16 2 9.4v.949c0 .978 0 1.468.11 1.928.099.408.26.798.48 1.156.247.404.593.75 1.285 1.441l2.6 2.6c1.584 1.585 2.376 2.377 3.289 2.673a4 4 0 0 0 2.472 0c.913-.296 1.705-1.088 3.29-2.672l.948-.95c1.585-1.584 2.377-2.376 2.673-3.289a4 4 0 0 0 0-2.472c-.296-.913-1.088-1.705-2.672-3.29Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTags2_default = SvgIconTags2;

// src/icons/line/IconTags.tsx
import { jsx as _jsx745 } from "react/jsx-runtime";
var SvgIconTags = (props) => /* @__PURE__ */ _jsx745("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx745("path", {
    d: "M10 2h1.8c3.92 0 5.88 0 7.378.763a7 7 0 0 1 3.059 3.06C23 7.318 23 9.28 23 13.2V16m-11-4a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm1.6-7H9.944c-1.083 0-1.624 0-2.127.133a4 4 0 0 0-1.247.574c-.428.295-.78.706-1.485 1.528l-.514.6C3.303 9.313 2.67 10.052 2.428 10.88a4 4 0 0 0 0 2.238c.242.829.875 1.568 2.143 3.046l.514.6c.704.822 1.057 1.233 1.485 1.528a4 4 0 0 0 1.247.574C8.32 19 8.861 19 9.944 19H13.6c2.24 0 3.36 0 4.217-.436a4 4 0 0 0 1.747-1.748C20 15.96 20 14.84 20 12.6v-1.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C16.961 5 15.841 5 13.601 5Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTags_default = SvgIconTags;

// src/icons/line/IconTarget.tsx
import { jsx as _jsx746, jsxs as _jsxs24 } from "react/jsx-runtime";
var SvgIconTarget = (props) => /* @__PURE__ */ _jsxs24("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx746("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx746("path", {
    d: "M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconTarget_default = SvgIconTarget;

// src/icons/line/IconTerminal.tsx
import { jsx as _jsx747 } from "react/jsx-runtime";
var SvgIconTerminal = (props) => /* @__PURE__ */ _jsx747("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx747("path", {
    d: "m7 10 3 3-3 3m6 0h4m-5.4 6h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTerminal_default = SvgIconTerminal;

// src/icons/line/IconText2Rec.tsx
import { jsx as _jsx748 } from "react/jsx-runtime";
var SvgIconText2Rec = (props) => /* @__PURE__ */ _jsx748("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx748("path", {
    d: "M12 7v10m0-10H8m4 0h4m-6 15h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconText2Rec_default = SvgIconText2Rec;

// src/icons/line/IconText2.tsx
import { jsx as _jsx749 } from "react/jsx-runtime";
var SvgIconText2 = (props) => /* @__PURE__ */ _jsx749("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx749("path", {
    d: "M12 3v18m0-18H4m8 0h8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconText2_default = SvgIconText2;

// src/icons/line/IconTextBlock.tsx
import { jsx as _jsx750 } from "react/jsx-runtime";
var SvgIconTextBlock = (props) => /* @__PURE__ */ _jsx750("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx750("path", {
    d: "M12 8v8m0-8H8m4 0h4m-6 14h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextBlock_default = SvgIconTextBlock;

// src/icons/line/IconTextBoldRec.tsx
import { jsx as _jsx751 } from "react/jsx-runtime";
var SvgIconTextBoldRec = (props) => /* @__PURE__ */ _jsx751("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx751("path", {
    d: "M7 12h6a3 3 0 0 0 3-3v0a3 3 0 0 0-3-3H8.5c-.466 0-.699 0-.883.076a1 1 0 0 0-.54.541C7 6.801 7 7.034 7 7.5V12Zm0 0h8a3 3 0 0 1 3 3v0a3 3 0 0 1-3 3H8.6c-.56 0-.84 0-1.054-.109a1 1 0 0 1-.437-.437C7 17.24 7 16.96 7 16.4V12Zm3 10h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextBoldRec_default = SvgIconTextBoldRec;

// src/icons/line/IconTextBold.tsx
import { jsx as _jsx752 } from "react/jsx-runtime";
var SvgIconTextBold = (props) => /* @__PURE__ */ _jsx752("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx752("path", {
    d: "M4 12h11.5a4.5 4.5 0 0 1 4.5 4.5v0a4.5 4.5 0 0 1-4.5 4.5H5.778A1.778 1.778 0 0 1 4 19.222V12Zm0 0h8.5A4.5 4.5 0 0 0 17 7.5v0A4.5 4.5 0 0 0 12.5 3H5.444C4.647 3 4 3.647 4 4.444V12Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextBold_default = SvgIconTextBold;

// src/icons/line/IconTextFormat.tsx
import { jsx as _jsx753 } from "react/jsx-runtime";
var SvgIconTextFormat = (props) => /* @__PURE__ */ _jsx753("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx753("path", {
    d: "M12 9v6m0-6H9m3 0h3M4 6a2 2 0 0 0 2-2M4 6a2 2 0 1 1 2-2M4 6v12M6 4h12m0 0a2 2 0 0 0 2 2m-2-2a2 2 0 1 1 2 2m0 12a2 2 0 0 0-2 2m2-2a2 2 0 1 1-2 2m2-2V6m-2 14H6m0 0a2 2 0 1 1-2-2m2 2a2 2 0 0 0-2-2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextFormat_default = SvgIconTextFormat;

// src/icons/line/IconTextItalicRec.tsx
import { jsx as _jsx754 } from "react/jsx-runtime";
var SvgIconTextItalicRec = (props) => /* @__PURE__ */ _jsx754("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx754("path", {
    d: "M10 6h7M7 18h7m0-12-4 12m0 4h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextItalicRec_default = SvgIconTextItalicRec;

// src/icons/line/IconTextItalic.tsx
import { jsx as _jsx755 } from "react/jsx-runtime";
var SvgIconTextItalic = (props) => /* @__PURE__ */ _jsx755("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx755("path", {
    d: "M9 3h10M5 21h10M14 3l-4 18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextItalic_default = SvgIconTextItalic;

// src/icons/line/IconTextRec.tsx
import { jsx as _jsx756 } from "react/jsx-runtime";
var SvgIconTextRec = (props) => /* @__PURE__ */ _jsx756("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx756("path", {
    d: "M12 6v12m0-12H8.6c-.56 0-.84 0-1.054.109a1 1 0 0 0-.437.437C7 6.76 7 7.04 7 7.6V8m5-2h3.4c.56 0 .84 0 1.054.109a1 1 0 0 1 .437.437C17 6.76 17 7.04 17 7.6V8m-5 10h3m-3 0H9m1 4h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextRec_default = SvgIconTextRec;

// src/icons/line/IconTextSlash2Rec.tsx
import { jsx as _jsx757 } from "react/jsx-runtime";
var SvgIconTextSlash2Rec = (props) => /* @__PURE__ */ _jsx757("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx757("path", {
    d: "M12 7h4m-4 0h-2m2 0v2m0 3v5M7 7l10 10m-7 5h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextSlash2Rec_default = SvgIconTextSlash2Rec;

// src/icons/line/IconTextSlash2.tsx
import { jsx as _jsx758 } from "react/jsx-runtime";
var SvgIconTextSlash2 = (props) => /* @__PURE__ */ _jsx758("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx758("path", {
    d: "M12 3h7m-7 0H8.5M12 3v4m0 5v9M4 4l16 16",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextSlash2_default = SvgIconTextSlash2;

// src/icons/line/IconTextSlashRec.tsx
import { jsx as _jsx759 } from "react/jsx-runtime";
var SvgIconTextSlashRec = (props) => /* @__PURE__ */ _jsx759("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx759("path", {
    d: "M12 6h4c.736 0 1.334.597 1.334 1.333V8M12 6h-2m2 0v2.5m0 9.5h2.667M12 18H9.334M12 18v-6M6 6l12 12m-8 4h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextSlashRec_default = SvgIconTextSlashRec;

// src/icons/line/IconTextSlash.tsx
import { jsx as _jsx760 } from "react/jsx-runtime";
var SvgIconTextSlash = (props) => /* @__PURE__ */ _jsx760("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx760("path", {
    d: "M12 3h5.5c.465 0 .697 0 .89.038a2 2 0 0 1 1.572 1.572c.038.193.038.425.038.89V6m-8-3H8.5M12 3v4m0 14h4m-4 0H8m4 0v-9M4 4l16 16",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextSlash_default = SvgIconTextSlash;

// src/icons/line/IconTextStrikethroughRec.tsx
import { jsx as _jsx761 } from "react/jsx-runtime";
var SvgIconTextStrikethroughRec = (props) => /* @__PURE__ */ _jsx761("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx761("path", {
    d: "M12.74 12H6m6.74 0c1.637 0 2.963 1.343 2.963 3s-1.326 3-2.962 3h-1.482c-1.636 0-2.963-1.343-2.963-3m4.445-3H18m-2.296-3c0-1.657-1.327-3-2.963-3h-1.482C9.623 6 8.296 7.343 8.296 9c0 .259.033.51.093.75M10 22h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextStrikethroughRec_default = SvgIconTextStrikethroughRec;

// src/icons/line/IconTextStrikethrough.tsx
import { jsx as _jsx762 } from "react/jsx-runtime";
var SvgIconTextStrikethrough = (props) => /* @__PURE__ */ _jsx762("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx762("path", {
    d: "M10.889 12h2.222c2.454 0 4.444 2.015 4.444 4.5s-1.99 4.5-4.444 4.5h-2.222c-2.455 0-4.445-2.015-4.445-4.5m11.111-9c0-2.485-1.99-4.5-4.444-4.5h-2.222C8.434 3 6.444 5.015 6.444 7.5c0 .388.049.765.14 1.125M3 12h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextStrikethrough_default = SvgIconTextStrikethrough;

// src/icons/line/IconTextUnderlineRec.tsx
import { jsx as _jsx763 } from "react/jsx-runtime";
var SvgIconTextUnderlineRec = (props) => /* @__PURE__ */ _jsx763("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx763("path", {
    d: "M8 18h8m0-12v5a4 4 0 0 1-8 0V6m2 16h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextUnderlineRec_default = SvgIconTextUnderlineRec;

// src/icons/line/IconTextUnderline.tsx
import { jsx as _jsx764 } from "react/jsx-runtime";
var SvgIconTextUnderline = (props) => /* @__PURE__ */ _jsx764("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx764("path", {
    d: "M6 21h12m0-18v8a6 6 0 0 1-12 0V3",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTextUnderline_default = SvgIconTextUnderline;

// src/icons/line/IconText.tsx
import { jsx as _jsx765 } from "react/jsx-runtime";
var SvgIconText = (props) => /* @__PURE__ */ _jsx765("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx765("path", {
    d: "M12 3v18m0-18H6.4c-.84 0-1.26 0-1.581.163a1.5 1.5 0 0 0-.656.656C4 4.139 4 4.559 4 5.4V6m8-3h5.6c.84 0 1.26 0 1.581.163a1.5 1.5 0 0 1 .655.656c.164.32.164.74.164 1.581V6m-8 15h5m-5 0H7",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconText_default = SvgIconText;

// src/icons/line/IconThermometer.tsx
import { jsx as _jsx766 } from "react/jsx-runtime";
var SvgIconThermometer = (props) => /* @__PURE__ */ _jsx766("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx766("path", {
    d: "M9 5h1.5M9 8h1.5M9 11h1.5m4.5 2V5a3 3 0 0 0-3-3v0a3 3 0 0 0-3 3v8a5 5 0 1 0 6 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconThermometer_default = SvgIconThermometer;

// src/icons/line/IconThumbsDown2.tsx
import { jsx as _jsx767 } from "react/jsx-runtime";
var SvgIconThumbsDown2 = (props) => /* @__PURE__ */ _jsx767("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx767("path", {
    d: "M18 4v0a2 2 0 0 1 2-2v0a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2v0a2 2 0 0 1-2-2v0m0-9v9m0-9s-1-2-3-2H7.118c-1.461 0-2.192 0-2.782.268A3 3 0 0 0 3.06 3.36c-.354.543-.465 1.265-.687 2.71l-.525 3.414c-.291 1.892-.437 2.837-.16 3.574a3 3 0 0 0 1.335 1.555C3.709 15 4.666 15 6.58 15h.907a1 1 0 0 1 .917 1.39l-.004.01-1.294 3.019A1.852 1.852 0 0 0 8.808 22v0c.123 0 .24-.049.328-.135L18 13",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconThumbsDown2_default = SvgIconThumbsDown2;

// src/icons/line/IconThumbsDown.tsx
import { jsx as _jsx768 } from "react/jsx-runtime";
var SvgIconThumbsDown = (props) => /* @__PURE__ */ _jsx768("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx768("path", {
    d: "M22 3v12M14.2 2H7.118c-1.461 0-2.192 0-2.782.267A3 3 0 0 0 3.06 3.361c-.354.542-.465 1.265-.687 2.71l-.523 3.4c-.293 1.904-.44 2.857-.157 3.598a3 3 0 0 0 1.32 1.539c.69.392 1.653.392 3.58.392h1.867c.234 0 .35 0 .436.01a1.5 1.5 0 0 1 1.265 1.92c-.024.082-.07.189-.162.403l-.917 2.14A1.813 1.813 0 0 0 10.75 22v0c.16 0 .314-.064.427-.177l5.011-5.011c1.038-1.038 1.557-1.557 1.928-2.162a6 6 0 0 0 .718-1.735c.166-.69.166-1.424.166-2.891V6.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C16.72 2 15.88 2 14.2 2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconThumbsDown_default = SvgIconThumbsDown;

// src/icons/line/IconThumbsUp2.tsx
import { jsx as _jsx769 } from "react/jsx-runtime";
var SvgIconThumbsUp2 = (props) => /* @__PURE__ */ _jsx769("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx769("path", {
    d: "M6 19s1 2 3 2h7.883c1.46 0 2.191 0 2.781-.267a3 3 0 0 0 1.275-1.094c.354-.542.465-1.265.687-2.71l.525-3.414c.291-1.891.437-2.837.16-3.574a3 3 0 0 0-1.334-1.554C20.29 8 19.334 8 17.42 8v0c-.804 0-1.207 0-1.459-.168a1 1 0 0 1-.428-.65c-.055-.297.103-.667.42-1.406l1.007-2.35A1.74 1.74 0 0 0 15.36 1v0a.87.87 0 0 0-.615.255L6 10m-4 9v-9a2 2 0 0 1 2-2v0a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2v0a2 2 0 0 1-2-2Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconThumbsUp2_default = SvgIconThumbsUp2;

// src/icons/line/IconThumbsUp.tsx
import { jsx as _jsx770 } from "react/jsx-runtime";
var SvgIconThumbsUp = (props) => /* @__PURE__ */ _jsx770("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx770("path", {
    d: "M2 8v12m7.8 1h7.082c1.461 0 2.192 0 2.782-.267a3 3 0 0 0 1.275-1.094c.354-.542.465-1.265.687-2.71l.523-3.4c.293-1.904.44-2.857.157-3.598a3 3 0 0 0-1.32-1.539C20.296 8 19.333 8 17.406 8h-1.142c-.878 0-1.316 0-1.614-.136a1.5 1.5 0 0 1-.875-1.327c-.008-.327.165-.73.51-1.537l.675-1.574A1.74 1.74 0 0 0 13.36 1v0a.87.87 0 0 0-.615.255L7.812 6.188C6.774 7.226 6.255 7.745 5.884 8.35a6 6 0 0 0-.718 1.735C5 10.775 5 11.509 5 12.976V16.2c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C7.28 21 8.12 21 9.8 21Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconThumbsUp_default = SvgIconThumbsUp;

// src/icons/line/IconTicket.tsx
import { jsx as _jsx771 } from "react/jsx-runtime";
var SvgIconTicket = (props) => /* @__PURE__ */ _jsx771("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx771("path", {
    d: "M9 11v2m0-7v2m0 8v2m-7-2.823V17.4c0 .557 0 .836.037 1.07a3 3 0 0 0 2.494 2.493C4.764 21 5.043 21 5.6 21h12.8c.557 0 .836 0 1.07-.037a3 3 0 0 0 2.493-2.494C22 18.236 22 17.957 22 17.4v-2.223a.176.176 0 0 0-.177-.177v0A2.823 2.823 0 0 1 19 12.177v-.354A2.823 2.823 0 0 1 21.823 9v0A.176.176 0 0 0 22 8.824V6.6c0-.557 0-.836-.037-1.07a3 3 0 0 0-2.494-2.493C19.236 3 18.957 3 18.4 3H5.6c-.557 0-.836 0-1.07.037A3 3 0 0 0 2.038 5.53C2 5.764 2 6.043 2 6.6v2.224c0 .097.079.176.176.176v0A2.824 2.824 0 0 1 5 11.823v.354A2.824 2.824 0 0 1 2.176 15v0a.176.176 0 0 0-.176.177Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTicket_default = SvgIconTicket;

// src/icons/line/IconTime.tsx
import { jsx as _jsx772 } from "react/jsx-runtime";
var SvgIconTime = (props) => /* @__PURE__ */ _jsx772("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx772("path", {
    d: "M12 8v4l3 2m-3 8C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTime_default = SvgIconTime;

// src/icons/line/IconTimer2Rec.tsx
import { jsx as _jsx773 } from "react/jsx-runtime";
var SvgIconTimer2Rec = (props) => /* @__PURE__ */ _jsx773("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx773("path", {
    d: "M10.5 10.5 12 12M7.757 7.757A6 6 0 1 0 12 6v1.5M11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTimer2Rec_default = SvgIconTimer2Rec;

// src/icons/line/IconTimer2.tsx
import { jsx as _jsx774 } from "react/jsx-runtime";
var SvgIconTimer2 = (props) => /* @__PURE__ */ _jsx774("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx774("path", {
    d: "M8.667 8.667 12 12M4.929 4.929A9.969 9.969 0 0 0 2 12c0 5.523 4.477 10 10 10s10-4.477 10-10S17.523 2 12 2v3.333",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTimer2_default = SvgIconTimer2;

// src/icons/line/IconTimer.tsx
import { jsx as _jsx775 } from "react/jsx-runtime";
var SvgIconTimer = (props) => /* @__PURE__ */ _jsx775("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx775("path", {
    d: "m15 14.5.6.5v0c1.66 1.384 2.49 2.076 2.768 2.601a3 3 0 0 1-1.51 4.173c-.55.226-1.63.226-3.792.226H12c-3.038 0-4.558 0-5.278-.44a3 3 0 0 1-1.276-3.526c.271-.799 1.438-1.771 3.773-3.716l5.562-4.636c2.335-1.945 3.502-2.918 3.773-3.716a3 3 0 0 0-1.276-3.526C16.558 2 15.038 2 12 2h-1.066c-2.161 0-3.242 0-3.791.226A3 3 0 0 0 5.633 6.4C5.908 6.924 6.738 7.616 8.4 9v0l.6.5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTimer_default = SvgIconTimer;

// src/icons/line/IconToggleOff.tsx
import { jsx as _jsx776, jsxs as _jsxs25 } from "react/jsx-runtime";
var SvgIconToggleOff = (props) => /* @__PURE__ */ _jsxs25("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx776("path", {
    d: "M1 12a9 9 0 0 1 9-9h4a9 9 0 0 1 9 9v0a9 9 0 0 1-9 9h-4a9 9 0 0 1-9-9v0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx776("path", {
    d: "M14 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconToggleOff_default = SvgIconToggleOff;

// src/icons/line/IconToggleOn.tsx
import { jsx as _jsx777, jsxs as _jsxs26 } from "react/jsx-runtime";
var SvgIconToggleOn = (props) => /* @__PURE__ */ _jsxs26("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx777("path", {
    d: "M1 12a9 9 0 0 1 9-9h4a9 9 0 0 1 9 9v0a9 9 0 0 1-9 9h-4a9 9 0 0 1-9-9v0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx777("path", {
    d: "M18 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconToggleOn_default = SvgIconToggleOn;

// src/icons/line/IconTransform.tsx
import { jsx as _jsx778 } from "react/jsx-runtime";
var SvgIconTransform = (props) => /* @__PURE__ */ _jsx778("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx778("path", {
    d: "M8 5a3 3 0 0 1-3 3m3-3a3 3 0 1 0-3 3m3-3h8M5 8v8M16 5a3 3 0 0 0 3 3m-3-3a3 3 0 1 1 3 3M5 16a3 3 0 1 0 3 3m-3-3a3 3 0 0 1 3 3m0 0h8m0 0a3 3 0 1 0 3-3m-3 3a3 3 0 0 1 3-3m0 0V8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTransform_default = SvgIconTransform;

// src/icons/line/IconTrash2.tsx
import { jsx as _jsx779 } from "react/jsx-runtime";
var SvgIconTrash2 = (props) => /* @__PURE__ */ _jsx779("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx779("path", {
    d: "m4 5 .646 10.976c.124 2.12.187 3.18.64 3.985a4 4 0 0 0 1.733 1.635C7.849 22 8.91 22 11.035 22h1.93c2.124 0 3.186 0 4.016-.404a4 4 0 0 0 1.734-1.635c.452-.805.515-1.865.64-3.985L20 5M4 5H2m2 0h16m0 0h2m-6 0-.453-1.359c-.197-.59-.295-.885-.478-1.103a1.5 1.5 0 0 0-.601-.434C14.203 2 13.892 2 13.27 2h-2.54c-.622 0-.933 0-1.198.104a1.5 1.5 0 0 0-.602.434c-.182.218-.28.513-.477 1.103L8 5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTrash2_default = SvgIconTrash2;

// src/icons/line/IconTrash.tsx
import { jsx as _jsx780 } from "react/jsx-runtime";
var SvgIconTrash = (props) => /* @__PURE__ */ _jsx780("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx780("path", {
    d: "m4 5 .646 10.976c.124 2.12.187 3.18.64 3.985a4 4 0 0 0 1.733 1.635C7.849 22 8.91 22 11.035 22h1.93c2.124 0 3.186 0 4.016-.404a4 4 0 0 0 1.734-1.635c.452-.805.515-1.865.64-3.985L20 5M4 5H2m2 0h16m0 0h2m-6 0-.453-1.359c-.197-.59-.295-.885-.478-1.103a1.5 1.5 0 0 0-.601-.434C14.203 2 13.892 2 13.27 2h-2.54c-.622 0-.933 0-1.198.104a1.5 1.5 0 0 0-.602.434c-.182.218-.28.513-.477 1.103L8 5m2 5v7m4-7v4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTrash_default = SvgIconTrash;

// src/icons/line/IconTrendDownRec.tsx
import { jsx as _jsx781 } from "react/jsx-runtime";
var SvgIconTrendDownRec = (props) => /* @__PURE__ */ _jsx781("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx781("path", {
    d: "M18 16v-4m0 4h-4m4 0-3.58-4.296a2.025 2.025 0 0 0-2.988-.136v0c-.79.79-2.073.79-2.864 0L6 9m4 13h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTrendDownRec_default = SvgIconTrendDownRec;

// src/icons/line/IconTrendDown.tsx
import { jsx as _jsx782 } from "react/jsx-runtime";
var SvgIconTrendDown = (props) => /* @__PURE__ */ _jsx782("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx782("path", {
    d: "M22 18v-6m0 6h-6m6 0-7-7c-.545-.545-.818-.818-1.112-.964a2 2 0 0 0-1.776 0c-.294.146-.567.419-1.112.964v0c-.545.545-.818.818-1.112.964a2 2 0 0 1-1.776 0C7.818 11.818 7.545 11.545 7 11L2 6",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTrendDown_default = SvgIconTrendDown;

// src/icons/line/IconTrendUpRec.tsx
import { jsx as _jsx783 } from "react/jsx-runtime";
var SvgIconTrendUpRec = (props) => /* @__PURE__ */ _jsx783("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx783("path", {
    d: "M18 8v4m0-4h-4m4 0-3.58 4.296a2.025 2.025 0 0 1-2.988.136v0a2.025 2.025 0 0 0-2.864 0L6 15m4 7h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTrendUpRec_default = SvgIconTrendUpRec;

// src/icons/line/IconTrendUp.tsx
import { jsx as _jsx784 } from "react/jsx-runtime";
var SvgIconTrendUp = (props) => /* @__PURE__ */ _jsx784("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx784("path", {
    d: "M22 6v6m0-6h-6m6 0-7 7c-.545.545-.818.818-1.112.964a2 2 0 0 1-1.776 0c-.294-.146-.567-.419-1.112-.964v0c-.545-.545-.818-.818-1.112-.964a2 2 0 0 0-1.776 0c-.294.146-.567.419-1.112.964l-5 5",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTrendUp_default = SvgIconTrendUp;

// src/icons/line/IconTrophy.tsx
import { jsx as _jsx785 } from "react/jsx-runtime";
var SvgIconTrophy = (props) => /* @__PURE__ */ _jsx785("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx785("path", {
    d: "M5 5H3c-.465 0-.697 0-.888.051a1.5 1.5 0 0 0-1.06 1.06C1 6.304 1 6.536 1 7v0c0 .93 0 1.395.102 1.776a3 3 0 0 0 2.122 2.122C3.605 11 4.07 11 5 11v0m14-6h2c.465 0 .698 0 .888.051a1.5 1.5 0 0 1 1.06 1.06C23 6.304 23 6.536 23 7v0c0 .93 0 1.395-.102 1.776a3 3 0 0 1-2.122 2.122C20.395 11 19.93 11 19 11v0m-7 3v4m-2.91 4h5.82A1.09 1.09 0 0 0 16 20.91v0A2.909 2.909 0 0 0 13.09 18h-2.18A2.91 2.91 0 0 0 8 20.91v0c0 .602.488 1.09 1.09 1.09ZM12 14v0c1.861 0 2.792 0 3.545-.245a5 5 0 0 0 3.21-3.21C19 9.792 19 8.861 19 7v-.5c0-1.398 0-2.097-.228-2.648a3 3 0 0 0-1.624-1.624C16.597 2 15.898 2 14.5 2h-5c-1.398 0-2.097 0-2.648.228a3 3 0 0 0-1.624 1.624C5 4.403 5 5.102 5 6.5V7c0 1.861 0 2.792.245 3.545a5 5 0 0 0 3.21 3.21C9.208 14 10.139 14 12 14Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTrophy_default = SvgIconTrophy;

// src/icons/line/IconTruck.tsx
import { jsx as _jsx786 } from "react/jsx-runtime";
var SvgIconTruck = (props) => /* @__PURE__ */ _jsx786("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx786("path", {
    d: "M10 19h4m-4 0a3 3 0 1 1-6 0m6 0a3 3 0 1 0-6 0m10 0a3 3 0 1 0 6 0m-6 0a3 3 0 1 1 6 0m0 0v0a3 3 0 0 0 3-3V6.8c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C20.72 2 19.88 2 18.2 2h-2.4c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C11 4.28 11 5.12 11 6.8v1c0 1.12 0 1.68-.218 2.108a2 2 0 0 1-.874.874C9.48 11 8.92 11 7.8 11H1.5M4 19v0c-.932 0-1.398 0-1.765-.152a2 2 0 0 1-1.083-1.083C1 17.398 1 16.932 1 16v-3.227c0-.66 0-.99.052-1.31.047-.285.124-.564.23-.832.12-.302.29-.585.63-1.151l.224-.373c.68-1.133 1.02-1.7 1.487-2.11a4 4 0 0 1 1.425-.807C5.642 6 6.302 6 7.624 6H11",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTruck_default = SvgIconTruck;

// src/icons/line/IconTv.tsx
import { jsx as _jsx787 } from "react/jsx-runtime";
var SvgIconTv = (props) => /* @__PURE__ */ _jsx787("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx787("path", {
    d: "m8 1 .16.12c1.377 1.033 2.066 1.55 2.822 1.748a4 4 0 0 0 2.036 0c.756-.199 1.445-.715 2.822-1.748L16 1M8 20l-1 2m9-2 1 2m-4.5-6a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m-3.1 4h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 16.96 22 15.84 22 13.6v-3.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 4 17.84 4 15.6 4H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 7.04 2 8.16 2 10.4v3.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 20 6.16 20 8.4 20Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTv_default = SvgIconTv;

// src/icons/line/IconTwitter.tsx
import { jsx as _jsx788 } from "react/jsx-runtime";
var SvgIconTwitter = (props) => /* @__PURE__ */ _jsx788("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx788("path", {
    d: "M23 3.304c0 2.872-1.642 3.931-1.642 3.931v.84C21.358 14.104 16.67 21 8.127 21 5.47 21 3.037 20.263 1 18.961c2.443.3 4.912-.38 6.861-1.865-2.036-.043-3.762-1.344-4.338-3.166.72.1 1.37.088 2.08-.087-2.17-.443-3.718-2.349-3.718-4.51.62.347 1.373.52 2.125.563C2.004 8.61 1.31 5.85 2.55 3.824c2.301 2.733 5.754 4.554 9.605 4.771a4.198 4.198 0 0 1-.133-1.04C12.022 5.038 14.102 3 16.67 3c1.328 0 2.523.52 3.408 1.431A9.936 9.936 0 0 0 23 3.304Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconTwitter_default = SvgIconTwitter;

// src/icons/line/IconUmbrella.tsx
import { jsx as _jsx789 } from "react/jsx-runtime";
var SvgIconUmbrella = (props) => /* @__PURE__ */ _jsx789("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx789("path", {
    d: "M12 2C7.025 2 4.495 5.666 3.226 8.946c-.466 1.207-.7 1.81-.51 2.688.141.652.726 1.505 1.284 1.872C4.75 14 5.572 14 7.217 14h9.566c1.645 0 2.467 0 3.217-.494.558-.367 1.143-1.22 1.284-1.872.19-.878-.044-1.482-.51-2.688C19.505 5.666 16.974 2 12 2Zm0 0C9.5 2 8 8.5 8 14h8c0-6-1.5-12-4-12Zm0 12v6a2 2 0 0 1-2 2v0a2 2 0 0 1-2-2v-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUmbrella_default = SvgIconUmbrella;

// src/icons/line/IconUndo2Rec.tsx
import { jsx as _jsx790 } from "react/jsx-runtime";
var SvgIconUndo2Rec = (props) => /* @__PURE__ */ _jsx790("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx790("path", {
    d: "M6 14v-4m0 4h4m-4 0s3-4 6.5-4c4.5 0 5.5 4 5.5 4m-8 8h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUndo2Rec_default = SvgIconUndo2Rec;

// src/icons/line/IconUndo2.tsx
import { jsx as _jsx791 } from "react/jsx-runtime";
var SvgIconUndo2 = (props) => /* @__PURE__ */ _jsx791("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx791("path", {
    d: "M2 15V7m0 8h7.778M2 15s5-8 11-8c8 0 9 8 9 8",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUndo2_default = SvgIconUndo2;

// src/icons/line/IconUndoRec.tsx
import { jsx as _jsx792 } from "react/jsx-runtime";
var SvgIconUndoRec = (props) => /* @__PURE__ */ _jsx792("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx792("path", {
    d: "M6 8h7.5a4.5 4.5 0 0 1 4.5 4.5v0a4.5 4.5 0 0 1-4.5 4.5H11M6 8l2-2M6 8l2 2m2 12h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUndoRec_default = SvgIconUndoRec;

// src/icons/line/IconUndo.tsx
import { jsx as _jsx793 } from "react/jsx-runtime";
var SvgIconUndo = (props) => /* @__PURE__ */ _jsx793("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx793("path", {
    d: "M2 6h13a7 7 0 0 1 7 7v0a7 7 0 0 1-7 7h-5M2 6l4-4M2 6l4 4",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUndo_default = SvgIconUndo;

// src/icons/line/IconUserAlert.tsx
import { jsx as _jsx794 } from "react/jsx-runtime";
var SvgIconUserAlert = (props) => /* @__PURE__ */ _jsx794("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx794("path", {
    d: "M20 5v5m.5 3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M13 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H6.8A4.8 4.8 0 0 0 2 18.8v0A3.2 3.2 0 0 0 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUserAlert_default = SvgIconUserAlert;

// src/icons/line/IconUserArrowBl.tsx
import { jsx as _jsx795 } from "react/jsx-runtime";
var SvgIconUserArrowBl = (props) => /* @__PURE__ */ _jsx795("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx795("path", {
    d: "m17 12 5-5m-5 5V8m0 4h4m-8-6a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H6.8A4.8 4.8 0 0 0 2 18.8v0A3.2 3.2 0 0 0 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUserArrowBl_default = SvgIconUserArrowBl;

// src/icons/line/IconUserArrowBottom.tsx
import { jsx as _jsx796 } from "react/jsx-runtime";
var SvgIconUserArrowBottom = (props) => /* @__PURE__ */ _jsx796("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx796("path", {
    d: "M20 7v6m0 0 3-3m-3 3-3-3m-4-4a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H6.8A4.8 4.8 0 0 0 2 18.8v0A3.2 3.2 0 0 0 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUserArrowBottom_default = SvgIconUserArrowBottom;

// src/icons/line/IconUserArrowRight.tsx
import { jsx as _jsx797 } from "react/jsx-runtime";
var SvgIconUserArrowRight = (props) => /* @__PURE__ */ _jsx797("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx797("path", {
    d: "M17 10h6m0 0-3-3m3 3-3 3m-7-7a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H6.8A4.8 4.8 0 0 0 2 18.8v0A3.2 3.2 0 0 0 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUserArrowRight_default = SvgIconUserArrowRight;

// src/icons/line/IconUserArrowTop.tsx
import { jsx as _jsx798 } from "react/jsx-runtime";
var SvgIconUserArrowTop = (props) => /* @__PURE__ */ _jsx798("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx798("path", {
    d: "M20 7v6m0-6-3 3m3-3 3 3M13 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H6.8A4.8 4.8 0 0 0 2 18.8v0A3.2 3.2 0 0 0 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUserArrowTop_default = SvgIconUserArrowTop;

// src/icons/line/IconUserArrowTr.tsx
import { jsx as _jsx799 } from "react/jsx-runtime";
var SvgIconUserArrowTr = (props) => /* @__PURE__ */ _jsx799("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx799("path", {
    d: "m17 12 5-5m0 0h-4m4 0v4m-9-5a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H6.8A4.8 4.8 0 0 0 2 18.8v0A3.2 3.2 0 0 0 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUserArrowTr_default = SvgIconUserArrowTr;

// src/icons/line/IconUserCheck.tsx
import { jsx as _jsx800 } from "react/jsx-runtime";
var SvgIconUserCheck = (props) => /* @__PURE__ */ _jsx800("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx800("path", {
    d: "m17 10 2 2 4-4M13 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H6.8A4.8 4.8 0 0 0 2 18.8v0A3.2 3.2 0 0 0 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUserCheck_default = SvgIconUserCheck;

// src/icons/line/IconUserCircle.tsx
import { jsx as _jsx801, jsxs as _jsxs27 } from "react/jsx-runtime";
var SvgIconUserCircle = (props) => /* @__PURE__ */ _jsxs27("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx801("path", {
    d: "M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2s10 4.477 10 10Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx801("path", {
    d: "M14 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconUserCircle_default = SvgIconUserCircle;

// src/icons/line/IconUserCross.tsx
import { jsx as _jsx802 } from "react/jsx-runtime";
var SvgIconUserCross = (props) => /* @__PURE__ */ _jsx802("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx802("path", {
    d: "m18 12 2.5-2.5m0 0L23 7m-2.5 2.5L18 7m2.5 2.5L23 12M13 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H6.8A4.8 4.8 0 0 0 2 18.8v0A3.2 3.2 0 0 0 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUserCross_default = SvgIconUserCross;

// src/icons/line/IconUserMinus.tsx
import { jsx as _jsx803 } from "react/jsx-runtime";
var SvgIconUserMinus = (props) => /* @__PURE__ */ _jsx803("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx803("path", {
    d: "M17 10h6M13 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H6.8A4.8 4.8 0 0 0 2 18.8v0A3.2 3.2 0 0 0 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUserMinus_default = SvgIconUserMinus;

// src/icons/line/IconUserPlus.tsx
import { jsx as _jsx804 } from "react/jsx-runtime";
var SvgIconUserPlus = (props) => /* @__PURE__ */ _jsx804("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx804("path", {
    d: "M20 7v3m0 0v3m0-3h-3m3 0h3M13 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM5.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H6.8A4.8 4.8 0 0 0 2 18.8v0A3.2 3.2 0 0 0 5.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUserPlus_default = SvgIconUserPlus;

// src/icons/line/IconUserSquare.tsx
import { jsx as _jsx805, jsxs as _jsxs28 } from "react/jsx-runtime";
var SvgIconUserSquare = (props) => /* @__PURE__ */ _jsxs28("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx805("path", {
    d: "M14 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /* @__PURE__ */ _jsx805("path", {
    d: "M2 11.6c0-3.36 0-5.04.654-6.324a6 6 0 0 1 2.622-2.622C6.56 2 8.24 2 11.6 2h.8c3.36 0 5.04 0 6.324.654a6 6 0 0 1 2.622 2.622C22 6.56 22 8.24 22 11.6v.8c0 3.36 0 5.04-.654 6.324a6 6 0 0 1-2.622 2.622C17.44 22 15.76 22 12.4 22h-.8c-3.36 0-5.04 0-6.324-.654a6 6 0 0 1-2.622-2.622C2 17.44 2 15.76 2 12.4v-.8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })]
});
var IconUserSquare_default = SvgIconUserSquare;

// src/icons/line/IconUser.tsx
import { jsx as _jsx806 } from "react/jsx-runtime";
var SvgIconUser = (props) => /* @__PURE__ */ _jsx806("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx806("path", {
    d: "M4 18.8A4.8 4.8 0 0 1 8.8 14h6.4a4.8 4.8 0 0 1 4.8 4.8v0a3.2 3.2 0 0 1-3.2 3.2H7.2A3.2 3.2 0 0 1 4 18.8v0ZM16 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUser_default = SvgIconUser;

// src/icons/line/IconUsers.tsx
import { jsx as _jsx807 } from "react/jsx-runtime";
var SvgIconUsers = (props) => /* @__PURE__ */ _jsx807("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx807("path", {
    d: "M15 10a4 4 0 0 0 0-8m2 20h2.8a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H17m-5-8a4 4 0 1 1-8 0 4 4 0 0 1 8 0ZM4.2 22h7.6a3.2 3.2 0 0 0 3.2-3.2v0a4.8 4.8 0 0 0-4.8-4.8H5.8A4.8 4.8 0 0 0 1 18.8v0A3.2 3.2 0 0 0 4.2 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconUsers_default = SvgIconUsers;

// src/icons/line/IconVector.tsx
import { jsx as _jsx808 } from "react/jsx-runtime";
var SvgIconVector = (props) => /* @__PURE__ */ _jsx808("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx808("path", {
    d: "M6 15c0-5 4-9 9-9m7-.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Zm-13 13a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVector_default = SvgIconVector;

// src/icons/line/IconVerified.tsx
import { jsx as _jsx809 } from "react/jsx-runtime";
var SvgIconVerified = (props) => /* @__PURE__ */ _jsx809("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx809("path", {
    d: "m9 12 2 2 5-5M7.612 4H6.366c-.84 0-1.26 0-1.581.163a1.5 1.5 0 0 0-.656.656c-.163.32-.163.74-.163 1.581v1.22c0 .326 0 .489-.033.644a1.5 1.5 0 0 1-.145.395c-.075.14-.18.264-.391.512l-1.082 1.277c-.467.552-.7.827-.79 1.136a1.5 1.5 0 0 0 0 .832c.09.309.323.584.79 1.136l1.082 1.277c.21.248.316.372.39.512a1.5 1.5 0 0 1 .146.395c.033.155.033.318.033.644v1.22c0 .84 0 1.26.163 1.581a1.5 1.5 0 0 0 .656.655c.32.164.74.164 1.58.164h1.247c.329 0 .493 0 .65.034.138.03.272.08.397.147.14.076.266.183.515.397l1.264 1.083c.554.475.832.713 1.143.803.274.08.565.08.838 0 .311-.09.588-.328 1.143-.803l1.264-1.083c.25-.214.374-.321.515-.397.125-.068.259-.117.397-.147.157-.034.321-.034.65-.034H17.6c.84 0 1.26 0 1.581-.163a1.5 1.5 0 0 0 .655-.656c.164-.32.164-.74.164-1.581v-1.212c0-.329 0-.493.034-.65.03-.138.08-.272.147-.397.076-.14.183-.266.397-.515l1.083-1.264c.475-.554.713-.832.803-1.143a1.5 1.5 0 0 0 0-.838c-.09-.311-.328-.588-.803-1.143l-1.083-1.264c-.214-.25-.321-.374-.397-.515a1.497 1.497 0 0 1-.147-.397C20 8.105 20 7.94 20 7.612V6.4c0-.84 0-1.26-.163-1.581a1.5 1.5 0 0 0-.656-.656C18.861 4 18.441 4 17.6 4h-1.212c-.329 0-.493 0-.65-.034a1.498 1.498 0 0 1-.397-.147c-.14-.076-.266-.183-.515-.397L13.562 2.34c-.554-.475-.832-.713-1.143-.804a1.5 1.5 0 0 0-.838 0c-.311.091-.588.329-1.143.804L9.174 3.422c-.25.214-.374.321-.515.397a1.5 1.5 0 0 1-.397.147C8.105 4 7.94 4 7.612 4Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVerified_default = SvgIconVerified;

// src/icons/line/IconVideoCameraSlash.tsx
import { jsx as _jsx810 } from "react/jsx-runtime";
var SvgIconVideoCameraSlash = (props) => /* @__PURE__ */ _jsx810("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx810("path", {
    d: "m1 1 2.621 2.621M21.5 21.501l-3.121-3.122M3.621 3.621a3.623 3.623 0 0 0-.56.334 5 5 0 0 0-1.106 1.106C1 6.375 1 8.251 1 12.001c0 3.749 0 5.624.955 6.938a5 5 0 0 0 1.106 1.106C4.375 21 6.251 21 10 21c3.75 0 5.625 0 6.939-.955a5 5 0 0 0 1.106-1.106c.128-.176.238-.361.334-.56M3.621 3.621 18.379 18.38M7 3.03C7.844 3 8.831 3 10 3c3.75 0 5.625 0 6.939.955a5 5 0 0 1 1.106 1.106c.657.905.862 2.075.926 3.94m0 0c.029.843.029 1.83.029 3v1.171c0 .203 0 .305.013.403a1.5 1.5 0 0 0 .297.717c.06.078.132.15.276.294v0c.71.71 1.064 1.064 1.34 1.179a1.5 1.5 0 0 0 1.96-.812c.114-.276.114-.778.114-1.781v-2.393c0-1.539 0-2.308-.318-2.715a1.5 1.5 0 0 0-1.147-.576c-.517-.012-1.134.447-2.368 1.366L18.97 9Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVideoCameraSlash_default = SvgIconVideoCameraSlash;

// src/icons/line/IconVideoCamera.tsx
import { jsx as _jsx811 } from "react/jsx-runtime";
var SvgIconVideoCamera = (props) => /* @__PURE__ */ _jsx811("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx811("path", {
    d: "M14 8.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m6 6.5.16.12c1.236.927 1.854 1.39 2.371 1.38a1.5 1.5 0 0 0 1.15-.575C23 15.517 23 14.745 23 13.2v-2.4c0-1.545 0-2.317-.319-2.725a1.5 1.5 0 0 0-1.15-.575c-.517-.01-1.135.453-2.371 1.38L19 9v6ZM9 21h2c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C19 17.2 19 15.8 19 13v-2c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C15.2 3 13.8 3 11 3H9c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 1.545 5.73C1 6.8 1 8.2 1 11v2c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C4.8 21 6.2 21 9 21Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVideoCamera_default = SvgIconVideoCamera;

// src/icons/line/IconVideoLibrary.tsx
import { jsx as _jsx812 } from "react/jsx-runtime";
var SvgIconVideoLibrary = (props) => /* @__PURE__ */ _jsx812("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx812("path", {
    d: "M5 19v0a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3v0M5 5v0a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v0m-4.997 8.331-1.515 1.01c-.808.54-1.213.809-1.548.789a1 1 0 0 1-.737-.395C10 14.467 10 13.982 10 13.01v-2.02c0-.972 0-1.457.203-1.725a1 1 0 0 1 .737-.395c.335-.02.74.25 1.548.788l1.515 1.01c.668.446 1.002.668 1.118.949a1 1 0 0 1 0 .766c-.116.28-.45.503-1.118.948ZM8.4 19h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 15.96 22 14.84 22 12.6v-1.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 5 17.84 5 15.6 5H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 8.04 2 9.16 2 11.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 19 6.16 19 8.4 19Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVideoLibrary_default = SvgIconVideoLibrary;

// src/icons/line/IconVideoPlus.tsx
import { jsx as _jsx813 } from "react/jsx-runtime";
var SvgIconVideoPlus = (props) => /* @__PURE__ */ _jsx813("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx813("path", {
    d: "M13.5 4H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 7.04 2 8.16 2 10.4v5.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22h5.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C20 18.96 20 17.84 20 15.6v-5.1M20 1v3m0 0v3m0-3h-3m3 0h3m-9.997 10.331-1.515 1.01c-.808.54-1.213.809-1.548.789a1 1 0 0 1-.738-.395C9 15.467 9 14.982 9 14.01v-2.02c0-.972 0-1.457.202-1.725a1 1 0 0 1 .738-.395c.335-.02.74.25 1.548.788l1.515 1.01c.668.446 1.002.668 1.118.949a1 1 0 0 1 0 .766c-.116.28-.45.503-1.118.948Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVideoPlus_default = SvgIconVideoPlus;

// src/icons/line/IconVideoQueue.tsx
import { jsx as _jsx814 } from "react/jsx-runtime";
var SvgIconVideoQueue = (props) => /* @__PURE__ */ _jsx814("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx814("path", {
    d: "M6 5h12M8 2h8m-1.997 14.331-1.515 1.01c-.808.54-1.213.809-1.548.789a1 1 0 0 1-.737-.395C10 17.467 10 16.982 10 16.01v-2.02c0-.972 0-1.457.203-1.725a1 1 0 0 1 .737-.395c.335-.02.74.25 1.548.788l1.515 1.01c.668.446 1.002.668 1.118.949a1 1 0 0 1 0 .766c-.116.28-.45.503-1.118.948ZM8.4 22h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6v-1.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 8 17.84 8 15.6 8H8.4c-2.24 0-3.36 0-4.216.436a4 4 0 0 0-1.748 1.748C2 11.04 2 12.16 2 14.4v1.2c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVideoQueue_default = SvgIconVideoQueue;

// src/icons/line/IconVoicemail.tsx
import { jsx as _jsx815 } from "react/jsx-runtime";
var SvgIconVoicemail = (props) => /* @__PURE__ */ _jsx815("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx815("path", {
    d: "M6 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0 0h12m0 0a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVoicemail_default = SvgIconVoicemail;

// src/icons/line/IconVolumeCross.tsx
import { jsx as _jsx816 } from "react/jsx-runtime";
var SvgIconVolumeCross = (props) => /* @__PURE__ */ _jsx816("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx816("path", {
    d: "m17 15 2.5-2.5m0 0L22 10m-2.5 2.5L17 10m2.5 2.5L22 15M8.837 6.745v0c1.197-1.796 1.796-2.694 2.274-2.931a2 2 0 0 1 2.622.794C14 5.07 14 6.15 14 8.308v7.384c0 2.158 0 3.237-.267 3.7a2 2 0 0 1-2.622.794c-.479-.237-1.077-1.135-2.274-2.931v0c-.237-.355-.355-.532-.498-.674a2 2 0 0 0-1.01-.54C7.131 16 6.92 16 6.492 16H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 14.48 2 13.92 2 12.8v-1.6c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 8 4.08 8 5.2 8h1.292c.426 0 .64 0 .837-.04a2 2 0 0 0 1.01-.54c.143-.143.261-.32.498-.675Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVolumeCross_default = SvgIconVolumeCross;

// src/icons/line/IconVolumeHigh.tsx
import { jsx as _jsx817 } from "react/jsx-runtime";
var SvgIconVolumeHigh = (props) => /* @__PURE__ */ _jsx817("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx817("path", {
    d: "M17 10c.598.4 1 1.145 1 2s-.402 1.6-1 2m1-8c1.793 1.198 3 3.436 3 6s-1.207 4.802-3 6M8.837 6.745v0c1.197-1.796 1.796-2.694 2.274-2.931a2 2 0 0 1 2.622.794C14 5.07 14 6.15 14 8.308v7.384c0 2.158 0 3.237-.267 3.7a2 2 0 0 1-2.622.794c-.479-.237-1.077-1.135-2.274-2.931v0c-.237-.355-.355-.532-.498-.674a2 2 0 0 0-1.01-.54C7.131 16 6.92 16 6.492 16H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 14.48 2 13.92 2 12.8v-1.6c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 8 4.08 8 5.2 8h1.292c.426 0 .64 0 .837-.04a2 2 0 0 0 1.01-.54c.143-.143.261-.32.498-.675Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVolumeHigh_default = SvgIconVolumeHigh;

// src/icons/line/IconVolumeLow.tsx
import { jsx as _jsx818 } from "react/jsx-runtime";
var SvgIconVolumeLow = (props) => /* @__PURE__ */ _jsx818("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx818("path", {
    d: "M19 10c.598.4 1 1.145 1 2s-.402 1.6-1 2M9.837 6.745v0c1.197-1.796 1.796-2.694 2.274-2.931a2 2 0 0 1 2.622.794C15 5.07 15 6.15 15 8.308v7.384c0 2.158 0 3.237-.267 3.7a2 2 0 0 1-2.622.794c-.479-.237-1.077-1.135-2.274-2.931v0c-.237-.355-.355-.532-.498-.674a2 2 0 0 0-1.01-.54C8.131 16 7.92 16 7.492 16H6.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C3 14.48 3 13.92 3 12.8v-1.6c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C4.52 8 5.08 8 6.2 8h1.292c.426 0 .64 0 .837-.04a2 2 0 0 0 1.01-.54c.143-.143.261-.32.498-.675Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVolumeLow_default = SvgIconVolumeLow;

// src/icons/line/IconVolumeMute.tsx
import { jsx as _jsx819 } from "react/jsx-runtime";
var SvgIconVolumeMute = (props) => /* @__PURE__ */ _jsx819("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx819("path", {
    d: "M5 12.8v-1.6c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C6.52 8 7.08 8 8.2 8h1.292c.426 0 .64 0 .837-.04a2 2 0 0 0 1.01-.54c.143-.143.261-.32.498-.675 1.197-1.796 1.796-2.694 2.274-2.931a2 2 0 0 1 2.622.794C17 5.07 17 6.15 17 8.308v7.384c0 2.158 0 3.237-.267 3.7a2 2 0 0 1-2.622.794c-.479-.237-1.077-1.135-2.274-2.931-.237-.355-.355-.532-.498-.674a2 2 0 0 0-1.01-.54C10.131 16 9.92 16 9.492 16H8.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C5 14.48 5 13.92 5 12.8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVolumeMute_default = SvgIconVolumeMute;

// src/icons/line/IconVolumeSlash.tsx
import { jsx as _jsx820 } from "react/jsx-runtime";
var SvgIconVolumeSlash = (props) => /* @__PURE__ */ _jsx820("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx820("path", {
    d: "M17 10c.598.399 1 1.145 1 2 0 .358-.07.697-.197 1M18 6c1.793 1.197 3 3.436 3 6a7.794 7.794 0 0 1-.59 3c-.147.35-.318.685-.511 1m-5.9-2v1.691c0 2.158 0 3.238-.266 3.701a2 2 0 0 1-2.622.794c-.479-.238-1.077-1.136-2.275-2.931-.236-.355-.354-.533-.498-.675a2 2 0 0 0-1.01-.54c-.197-.04-.41-.04-.837-.04H5.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874C2 14.48 2 13.92 2 12.8v-1.6c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C3.52 8 4.079 8 5.199 8H6.49c.427 0 .64 0 .838-.04A2 2 0 0 0 7.8 7.8M14 14 7.8 7.8M14 14l7 7M3 3l4.8 4.8m1.872-2.3c.671-.981 1.087-1.512 1.44-1.687a2 2 0 0 1 2.621.794C14 5.07 14 6.15 14 8.307V10",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconVolumeSlash_default = SvgIconVolumeSlash;

// src/icons/line/IconWalletMinus.tsx
import { jsx as _jsx821 } from "react/jsx-runtime";
var SvgIconWalletMinus = (props) => /* @__PURE__ */ _jsx821("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx821("path", {
    d: "M2 4v11.6c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6v-3.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 6 17.84 6 15.6 6H12m10 5h-.5a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3h.5M7.5 14h5M17 6l-1.08-1.44c-.704-.939-1.056-1.408-1.502-1.746a4 4 0 0 0-1.32-.66C12.56 2 11.973 2 10.8 2H4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h13Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWalletMinus_default = SvgIconWalletMinus;

// src/icons/line/IconWalletMove.tsx
import { jsx as _jsx822 } from "react/jsx-runtime";
var SvgIconWalletMove = (props) => /* @__PURE__ */ _jsx822("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx822("path", {
    d: "M2 4v11.6c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6v-3.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 6 17.84 6 15.6 6H12m10 5h-.5a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3h.5M7 14h6m0 0-2.5-2.5M13 14l-2.5 2.5M17 6l-1.08-1.44c-.704-.939-1.056-1.408-1.502-1.746a4 4 0 0 0-1.32-.66C12.56 2 11.973 2 10.8 2H4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h13Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWalletMove_default = SvgIconWalletMove;

// src/icons/line/IconWalletPlus.tsx
import { jsx as _jsx823 } from "react/jsx-runtime";
var SvgIconWalletPlus = (props) => /* @__PURE__ */ _jsx823("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx823("path", {
    d: "M2 4v11.6c0 2.24 0 3.36.436 4.216a4 4 0 0 0 1.748 1.748C5.04 22 6.16 22 8.4 22h7.2c2.24 0 3.36 0 4.216-.436a4 4 0 0 0 1.748-1.748C22 18.96 22 17.84 22 15.6v-3.2c0-2.24 0-3.36-.436-4.216a4 4 0 0 0-1.748-1.748C18.96 6 17.84 6 15.6 6H12m10 5h-.5a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3h.5m-12-5.5V14m0 0v2.5m0-2.5H7.5m2.5 0h2.5M17 6l-1.08-1.44c-.704-.939-1.056-1.408-1.502-1.746a4 4 0 0 0-1.32-.66C12.56 2 11.973 2 10.8 2H4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h13Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWalletPlus_default = SvgIconWalletPlus;

// src/icons/line/IconWallet.tsx
import { jsx as _jsx824 } from "react/jsx-runtime";
var SvgIconWallet = (props) => /* @__PURE__ */ _jsx824("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx824("path", {
    d: "M2 4v13.2c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 22 5.12 22 6.8 22h10.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C22 19.72 22 18.88 22 17.2v-6.4c0-1.68 0-2.52-.327-3.162a3 3 0 0 0-1.311-1.311C19.72 6 18.88 6 17.2 6H12m10 5h-.5a3 3 0 0 0-3 3v0a3 3 0 0 0 3 3h.5M17 6l-1.08-1.44c-.704-.939-1.056-1.408-1.502-1.746a4 4 0 0 0-1.32-.66C12.56 2 11.973 2 10.8 2H4a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h13Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWallet_default = SvgIconWallet;

// src/icons/line/IconWatch2.tsx
import { jsx as _jsx825 } from "react/jsx-runtime";
var SvgIconWatch2 = (props) => /* @__PURE__ */ _jsx825("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx825("path", {
    d: "M12 9v3l2 1.5M16 4l-.166-.625c-.227-.85-.34-1.275-.583-1.591a2 2 0 0 0-.821-.631C14.062 1 13.622 1 12.742 1h-1.484c-.88 0-1.32 0-1.687.153a2 2 0 0 0-.822.63c-.242.317-.356.742-.582 1.592L8 4m8 16-.166.625c-.227.85-.34 1.275-.583 1.591a2 2 0 0 1-.821.631c-.368.153-.808.153-1.688.153h-1.484c-.88 0-1.32 0-1.687-.152a2 2 0 0 1-.822-.632c-.242-.316-.356-.741-.582-1.591L8 20m4 0v0c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C20 16.2 20 14.8 20 12v0c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C16.2 4 14.8 4 12 4v0c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 4.545 6.73C4 7.8 4 9.2 4 12v0c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C7.8 20 9.2 20 12 20Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWatch2_default = SvgIconWatch2;

// src/icons/line/IconWatch.tsx
import { jsx as _jsx826 } from "react/jsx-runtime";
var SvgIconWatch = (props) => /* @__PURE__ */ _jsx826("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx826("path", {
    d: "M12 10v2l1.5 1M16 5l-.394-1.576c-.217-.867-.325-1.3-.567-1.623a2 2 0 0 0-.826-.645C13.842 1 13.395 1 12.502 1h-1.004c-.893 0-1.34 0-1.711.156a2 2 0 0 0-.826.645c-.242.323-.35.756-.567 1.623L8 5m8 14-.394 1.576c-.217.867-.325 1.3-.567 1.623a2.001 2.001 0 0 1-.826.645c-.371.156-.818.156-1.711.156h-1.004c-.893 0-1.34 0-1.711-.156a2.001 2.001 0 0 1-.826-.645c-.242-.323-.35-.756-.567-1.623L8 19m4 1v0a8 8 0 0 0 8-8v0a8 8 0 0 0-8-8v0a8 8 0 0 0-8 8v0a8 8 0 0 0 8 8Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWatch_default = SvgIconWatch;

// src/icons/line/IconWebcamSlash.tsx
import { jsx as _jsx827 } from "react/jsx-runtime";
var SvgIconWebcamSlash = (props) => /* @__PURE__ */ _jsx827("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx827("path", {
    d: "M7 16.246A7.966 7.966 0 0 0 12 18c1.698 0 3.273-.529 4.568-1.431M7 16.245A7.985 7.985 0 0 1 4 10c0-1.698.53-3.273 1.432-4.568M7 16.246c0 1.777-3.332 3.252-1.514 5.141.59.613 1.832.613 4.315.613h4.398c2.483 0 3.724 0 4.315-.613.962-1 .375-2.166-.288-3.161m0 0L22 22m-3.774-3.774-1.658-1.657m0 0L5.432 5.431M18.93 14A8 8 0 0 0 8 3.07M5.432 5.432 2 2m10 6a2 2 0 0 1 2 2",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWebcamSlash_default = SvgIconWebcamSlash;

// src/icons/line/IconWebcam.tsx
import { jsx as _jsx828 } from "react/jsx-runtime";
var SvgIconWebcam = (props) => /* @__PURE__ */ _jsx828("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx828("path", {
    d: "m7 16.5-.304.418c-1.192 1.64-1.789 2.46-1.767 3.144A2 2 0 0 0 5.7 21.58c.542.42 1.555.42 3.583.42h5.432c2.028 0 3.041 0 3.583-.42a2 2 0 0 0 .773-1.518c.02-.684-.575-1.504-1.768-3.144L17 16.5m3-6.5a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-6 0a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWebcam_default = SvgIconWebcam;

// src/icons/line/IconWeight.tsx
import { jsx as _jsx829 } from "react/jsx-runtime";
var SvgIconWeight = (props) => /* @__PURE__ */ _jsx829("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx829("path", {
    d: "M8 4.5v-.3c0-1.12 0-1.68.218-2.108a2 2 0 0 1 .874-.874C9.52 1 10.08 1 11.2 1h1.6c1.12 0 1.68 0 2.108.218a2 2 0 0 1 .874.874C16 2.52 16 3.08 16 4.2v.3M12 18a4 4 0 0 1-4-4m4 8v0a9 9 0 0 0 9-9v0a9 9 0 0 0-9-9v0a9 9 0 0 0-9 9v0a9 9 0 0 0 9 9Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWeight_default = SvgIconWeight;

// src/icons/line/IconWeighter2.tsx
import { jsx as _jsx830 } from "react/jsx-runtime";
var SvgIconWeighter2 = (props) => /* @__PURE__ */ _jsx830("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx830("path", {
    d: "M17 6v0c.93 0 1.394 0 1.78.077a4 4 0 0 1 3.143 3.143C22 9.606 22 10.07 22 11v3c0 2.8 0 4.2-.545 5.27a5 5 0 0 1-2.185 2.185C18.2 22 16.8 22 14 22h-4c-2.8 0-4.2 0-5.27-.545a5 5 0 0 1-2.185-2.185C2 18.2 2 16.8 2 14v-3c0-.93 0-1.394.077-1.78A4 4 0 0 1 5.22 6.077C5.606 6 6.07 6 7 6v0m5 4 1-4m-3 11h4m-3.502-7h3.004c.893 0 1.34 0 1.711-.156a2 2 0 0 0 .826-.645c.242-.323.35-.756.567-1.623l.4-1.6c.339-1.355.508-2.033.343-2.567a2 2 0 0 0-.874-1.12C15.997 2 15.298 2 13.9 2h-3.803c-1.397 0-2.095 0-2.573.29a2 2 0 0 0-.874 1.12c-.165.533.004 1.21.343 2.566l.4 1.6c.217.867.325 1.3.567 1.623a2 2 0 0 0 .826.645c.371.156.818.156 1.711.156Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconWeighter2_default = SvgIconWeighter2;

// src/icons/line/IconWeighter.tsx
import { jsx as _jsx831 } from "react/jsx-runtime";
var SvgIconWeighter = (props) => /* @__PURE__ */ _jsx831("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx831("path", {
    d: "M12 13h2.588c.514 0 .77 0 1-.076a1.5 1.5 0 0 0 .546-.32c.177-.164.302-.39.552-.838l.515-.928c.254-.456.38-.685.4-1.028a1.863 1.863 0 0 0-.214-.86c-.178-.295-.334-.397-.646-.6C15.748 7.705 14.168 7 12 7c-2.167 0-3.748.705-4.74 1.35-.313.203-.47.305-.647.6-.125.206-.228.62-.214.86.02.343.146.572.4 1.028l.515.928c.25.449.375.674.552.837a1.5 1.5 0 0 0 .546.321c.23.076.486.076 1 .076H12Zm0 0-2-3m0 7h4m-2.4 5h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round"
  })
});
var IconWeighter_default = SvgIconWeighter;

// src/icons/line/IconWheelchairRec.tsx
import { jsx as _jsx832 } from "react/jsx-runtime";
var SvgIconWheelchairRec = (props) => /* @__PURE__ */ _jsx832("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx832("path", {
    d: "M12.5 5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1M18 17h-.667c-.097 0-.145 0-.186-.003a1.229 1.229 0 0 1-1.144-1.144c-.003-.04-.003-.09-.003-.186v-.7c0-.689 0-1.033-.134-1.296a1.23 1.23 0 0 0-.537-.537C15.066 13 14.722 13 14.034 13h-.068c-.688 0-1.032 0-1.295-.134a1.23 1.23 0 0 1-.537-.537C12 12.066 12 11.722 12 11.034V8a2 2 0 0 0 2 2h1m-6-.87a4.002 4.002 0 0 0 1 7.874c1.014 0 1.94-.378 2.646-1M11.6 22h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWheelchairRec_default = SvgIconWheelchairRec;

// src/icons/line/IconWheelchair.tsx
import { jsx as _jsx833 } from "react/jsx-runtime";
var SvgIconWheelchair = (props) => /* @__PURE__ */ _jsx833("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx833("path", {
    d: "M21 22h-1v0a2 2 0 0 1-2-2v-1c0-.932 0-1.398-.152-1.765a2 2 0 0 0-1.083-1.083C16.398 16 15.932 16 15 16v0c-.932 0-1.398 0-1.765-.152a2 2 0 0 1-1.083-1.083C12 14.398 12 13.932 12 13V9a3 3 0 0 0 3 3h1m-7-2a6 6 0 1 0 4.472 10M14 4a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWheelchair_default = SvgIconWheelchair;

// src/icons/line/IconWifiRec.tsx
import { jsx as _jsx834 } from "react/jsx-runtime";
var SvgIconWifiRec = (props) => /* @__PURE__ */ _jsx834("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx834("path", {
    d: "M5 10a9.586 9.586 0 0 1 7-3 9.586 9.586 0 0 1 7 3m-2 2a7.237 7.237 0 0 0-5-2c-1.935 0-3.694.76-5 2m7.5 2c-.808-.624-1.355-1-2.5-1s-1.692.376-2.5 1m3 2.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m.1 5.5h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWifiRec_default = SvgIconWifiRec;

// src/icons/line/IconWifiSlashRec.tsx
import { jsx as _jsx835 } from "react/jsx-runtime";
var SvgIconWifiSlashRec = (props) => /* @__PURE__ */ _jsx835("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx835("path", {
    d: "M5 10a9.513 9.513 0 0 1 2.903-2.097M19 10a9.586 9.586 0 0 0-7-3c-.338 0-.671.017-1 .05M9.5 14c.808-.624 1.355-1 2.5-1 .48 0 .856.066 1.19.19M17 12a7.256 7.256 0 0 0-3.5-1.844M7 12a7.264 7.264 0 0 1 3.22-1.78M6 6l1.903 1.903M17 17l-3.81-3.81m0 0-2.97-2.97m0 0L7.903 7.903M12.5 16.5a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1m.1 5.5h.8c3.36 0 5.04 0 6.324-.654a6 6 0 0 0 2.622-2.622C22 17.44 22 15.76 22 12.4v-.8c0-3.36 0-5.04-.654-6.324a6 6 0 0 0-2.622-2.622C17.44 2 15.76 2 12.4 2h-.8c-3.36 0-5.04 0-6.324.654a6 6 0 0 0-2.622 2.622C2 6.56 2 8.24 2 11.6v.8c0 3.36 0 5.04.654 6.324a6 6 0 0 0 2.622 2.622C6.56 22 8.24 22 11.6 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWifiSlashRec_default = SvgIconWifiSlashRec;

// src/icons/line/IconWifiSlash.tsx
import { jsx as _jsx836 } from "react/jsx-runtime";
var SvgIconWifiSlash = (props) => /* @__PURE__ */ _jsx836("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx836("path", {
    d: "M5.146 12.718a9.971 9.971 0 0 1 5.022-2.55M14 10.2a9.975 9.975 0 0 1 4.853 2.518M8.108 15.433A5.976 5.976 0 0 1 12 14c.925 0 1.8.21 2.583.583m7.737-4.043A13.963 13.963 0 0 0 12 6c-.68 0-1.347.048-2 .142M1.68 10.54a14.024 14.024 0 0 1 5.262-3.598M3 3l18 18m-8.5-3a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWifiSlash_default = SvgIconWifiSlash;

// src/icons/line/IconWifi.tsx
import { jsx as _jsx837 } from "react/jsx-runtime";
var SvgIconWifi = (props) => /* @__PURE__ */ _jsx837("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx837("path", {
    d: "M1.68 10.54A13.963 13.963 0 0 1 12 6c4.085 0 7.76 1.75 10.32 4.54m-3.467 2.178A9.966 9.966 0 0 0 12 10a9.966 9.966 0 0 0-6.854 2.718m10.745 2.715A5.977 5.977 0 0 0 12 14c-1.485 0-2.844.54-3.892 1.433M12.5 18a.5.5 0 0 1-1 0m1 0a.5.5 0 0 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWifi_default = SvgIconWifi;

// src/icons/line/IconWindows.tsx
import { jsx as _jsx838 } from "react/jsx-runtime";
var SvgIconWindows = (props) => /* @__PURE__ */ _jsx838("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx838("path", {
    d: "M2 12v2.866c0 1.467 0 2.2.269 2.792a3 3 0 0 0 1.098 1.275c.545.354 1.27.463 2.721.68l4.412.662M2 12V9.134c0-1.467 0-2.2.269-2.792a3 3 0 0 1 1.098-1.275c.545-.354 1.27-.463 2.721-.68l4.412-.662M2 12h20m0 0V7.574c0-1.921 0-2.882-.39-3.57a3 3 0 0 0-1.534-1.32c-.738-.285-1.688-.142-3.588.143l-5.988.898M22 12v4.426c0 1.921 0 2.882-.39 3.57a3 3 0 0 1-1.534 1.32c-.738.285-1.688.142-3.588-.143l-5.988-.898m0-16.55v16.55",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWindows_default = SvgIconWindows;

// src/icons/line/IconWrapTextRec.tsx
import { jsx as _jsx839 } from "react/jsx-runtime";
var SvgIconWrapTextRec = (props) => /* @__PURE__ */ _jsx839("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx839("path", {
    d: "M6 17h2.5M6 11.5h10.25A2.75 2.75 0 0 1 19 14.25v0A2.75 2.75 0 0 1 16.25 17H12m0 0 2-2m-2 2 2 2M6 6h12m-8 16h4c2.8 0 4.2 0 5.27-.545a5 5 0 0 0 2.185-2.185C22 18.2 22 16.8 22 14v-4c0-2.8 0-4.2-.545-5.27a5 5 0 0 0-2.185-2.185C18.2 2 16.8 2 14 2h-4c-2.8 0-4.2 0-5.27.545A5 5 0 0 0 2.545 4.73C2 5.8 2 7.2 2 10v4c0 2.8 0 4.2.545 5.27a5 5 0 0 0 2.185 2.185C5.8 22 7.2 22 10 22Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWrapTextRec_default = SvgIconWrapTextRec;

// src/icons/line/IconWrapText.tsx
import { jsx as _jsx840 } from "react/jsx-runtime";
var SvgIconWrapText = (props) => /* @__PURE__ */ _jsx840("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx840("path", {
    d: "M3 19h4m-4-8h15a4 4 0 0 1 4 4v0a4 4 0 0 1-4 4h-6m0 0 3-3m-3 3 3 3M3 3h18",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWrapText_default = SvgIconWrapText;

// src/icons/line/IconWrench.tsx
import { jsx as _jsx841 } from "react/jsx-runtime";
var SvgIconWrench = (props) => /* @__PURE__ */ _jsx841("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx841("path", {
    d: "M15 16a7 7 0 0 0 6.71-9l-2.447 2.447h0c-.792.792-1.188 1.188-1.645 1.337a2 2 0 0 1-1.236 0c-.457-.149-.853-.545-1.645-1.337l-.184-.184c-.792-.792-1.188-1.188-1.337-1.645a2 2 0 0 1 0-1.236c.149-.457.545-.853 1.337-1.645L17 2.29A7 7 0 0 0 8 9c0 1.074.068 2.59.5 3.5l-6 6h0c-.08.08-.12.12-.152.155a2 2 0 0 0 0 2.69c.033.036.072.075.152.155h0c.08.08.12.12.155.151a2 2 0 0 0 2.69 0c.036-.032.075-.071.155-.151l6-6c.91.432 2.426.5 3.5.5Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconWrench_default = SvgIconWrench;

// src/icons/line/IconYinYang.tsx
import { jsx as _jsx842 } from "react/jsx-runtime";
var SvgIconYinYang = (props) => /* @__PURE__ */ _jsx842("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx842("path", {
    d: "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2m0 20C6.477 22 2 17.523 2 12S6.477 2 12 2m0 20c3.314 0 6-2.238 6-5 0-2.761-2.686-5-6-5S6 9.762 6 7c0-2.761 2.686-5 6-5m-.5 5a.5.5 0 1 1-1 0m1 0a.5.5 0 1 0-1 0m1 0h-1m3 10a.5.5 0 1 1-1 0m1 0a.5.5 0 1 0-1 0m1 0h-1",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconYinYang_default = SvgIconYinYang;

// src/icons/line/IconYoutube.tsx
import { jsx as _jsx843 } from "react/jsx-runtime";
var SvgIconYoutube = (props) => /* @__PURE__ */ _jsx843("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx843("path", {
    clipRule: "evenodd",
    d: "m10 15 5-3-5-3v6Z",
    stroke: props.color,
    strokeWidth: 1.5,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })
});
var IconYoutube_default = SvgIconYoutube;

// src/icons/line/IconZoomIn.tsx
import { jsx as _jsx844 } from "react/jsx-runtime";
var SvgIconZoomIn = (props) => /* @__PURE__ */ _jsx844("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx844("path", {
    d: "M20.47 21.53a.75.75 0 1 0 1.06-1.06l-1.06 1.06ZM8 10.25a.75.75 0 0 0 0 1.5v-1.5Zm6 1.5a.75.75 0 0 0 0-1.5v1.5ZM11.75 8a.75.75 0 0 0-1.5 0h1.5Zm-1.5 6a.75.75 0 0 0 1.5 0h-1.5Zm.75 5.25A8.25 8.25 0 0 1 2.75 11h-1.5c0 5.385 4.365 9.75 9.75 9.75v-1.5ZM2.75 11A8.25 8.25 0 0 1 11 2.75v-1.5c-5.385 0-9.75 4.365-9.75 9.75h1.5ZM11 2.75A8.25 8.25 0 0 1 19.25 11h1.5c0-5.385-4.365-9.75-9.75-9.75v1.5ZM19.25 11c0 2.278-.923 4.34-2.416 5.834l1.06 1.06A9.722 9.722 0 0 0 20.75 11h-1.5Zm-2.416 5.834A8.222 8.222 0 0 1 11 19.25v1.5a9.722 9.722 0 0 0 6.894-2.856l-1.06-1.06Zm0 1.06 3.636 3.636 1.06-1.06-3.636-3.636-1.06 1.06ZM8 11.75h3v-1.5H8v1.5Zm3 0h3v-1.5h-3v1.5Zm.75-.75V8h-1.5v3h1.5Zm-1.5 0v3h1.5v-3h-1.5Z",
    fill: props.color
  })
});
var IconZoomIn_default = SvgIconZoomIn;

// src/icons/line/IconZoomOut.tsx
import { jsx as _jsx845 } from "react/jsx-runtime";
var SvgIconZoomOut = (props) => /* @__PURE__ */ _jsx845("svg", {
  width: 24,
  height: 24,
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx845("path", {
    d: "M20.47 21.53a.75.75 0 1 0 1.06-1.06l-1.06 1.06ZM8 10.25a.75.75 0 0 0 0 1.5v-1.5Zm6 1.5a.75.75 0 0 0 0-1.5v1.5Zm-3 7.5A8.25 8.25 0 0 1 2.75 11h-1.5c0 5.385 4.365 9.75 9.75 9.75v-1.5ZM2.75 11A8.25 8.25 0 0 1 11 2.75v-1.5c-5.385 0-9.75 4.365-9.75 9.75h1.5ZM11 2.75A8.25 8.25 0 0 1 19.25 11h1.5c0-5.385-4.365-9.75-9.75-9.75v1.5ZM19.25 11c0 2.278-.923 4.34-2.416 5.834l1.06 1.06A9.722 9.722 0 0 0 20.75 11h-1.5Zm-2.416 5.834A8.222 8.222 0 0 1 11 19.25v1.5a9.722 9.722 0 0 0 6.894-2.856l-1.06-1.06Zm0 1.06 3.636 3.636 1.06-1.06-3.636-3.636-1.06 1.06ZM8 11.75h3v-1.5H8v1.5Zm3 0h3v-1.5h-3v1.5Z",
    fill: props.color
  })
});
var IconZoomOut_default = SvgIconZoomOut;

// src/icons/tiny/IconTinyArrow.tsx
import { jsx as _jsx846 } from "react/jsx-runtime";
var IconTinyArrow = (props) => /* @__PURE__ */ _jsx846("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx846("path", {
    d: "M4.59574 7.74468L7.74468 4.59574C8.08511 4.25532 8.08511 3.74468 7.74468 3.40426L4.59574 0.255319C4.25532 -0.0851065 3.74468 -0.0851066 3.40426 0.255319C3.06383 0.595744 3.06383 1.10638 3.40426 1.44681L5.10638 3.14894L0.851065 3.14894C0.340426 3.14894 8.01149e-07 3.48936 7.78829e-07 4C7.56508e-07 4.51064 0.340426 4.85106 0.851064 4.85106L5.10638 4.85106L3.40426 6.55319C3.23404 6.7234 3.14894 6.97872 3.14894 7.14894C3.14894 7.31915 3.23404 7.57447 3.40426 7.74468C3.74468 8.08511 4.25532 8.08511 4.59574 7.74468Z",
    fill: props.color
  })
});
var IconTinyArrow_default = IconTinyArrow;

// src/icons/tiny/IconTinyArrowLeft.tsx
import { jsx as _jsx847 } from "react/jsx-runtime";
var IconTinyArrowLeft = (props) => /* @__PURE__ */ _jsx847("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx847("path", {
    d: "M3.40426 7.74468L0.255319 4.59574C-0.0851062 4.25532 -0.0851062 3.74468 0.255319 3.40426L3.40426 0.255319C3.74468 -0.0851065 4.25532 -0.0851066 4.59574 0.255319C4.93617 0.595744 4.93617 1.10638 4.59574 1.44681L2.89362 3.14894L7.14894 3.14894C7.65957 3.14894 8 3.48936 8 4C8 4.51064 7.65957 4.85106 7.14894 4.85106L2.89362 4.85106L4.59574 6.55319C4.76596 6.7234 4.85106 6.97872 4.85106 7.14894C4.85106 7.31915 4.76596 7.57447 4.59574 7.74468C4.25532 8.08511 3.74468 8.08511 3.40426 7.74468Z",
    fill: props.color
  })
});
var IconTinyArrowLeft_default = IconTinyArrowLeft;

// src/icons/tiny/IconTinyBell.tsx
import { jsx as _jsx848 } from "react/jsx-runtime";
var IconTinyBell = (props) => /* @__PURE__ */ _jsx848("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx848("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M4 0C5.58658 0 6.87212 1.23282 6.87212 2.75358V4.05197C6.87212 4.38977 6.902 4.93619 6.93887 5.2718L6.99823 5.81233C7.01667 5.9802 6.8886 6.11688 6.7121 6.11688H1.28789C1.11221 6.11688 0.983288 5.98053 1.00176 5.81233L1.06113 5.2718C1.09786 4.93737 1.12787 4.39005 1.12787 4.05197V2.75358C1.12787 1.23154 2.41377 0 4 0ZM3.99999 8C3.38312 8 2.88305 7.49922 2.88305 6.88148H5.11693C5.11693 7.49922 4.61686 8 3.99999 8Z",
    fill: props.color
  })
});
var IconTinyBell_default = IconTinyBell;

// src/icons/tiny/IconTinyCheck.tsx
import { jsx as _jsx849 } from "react/jsx-runtime";
var IconTinyCheck = (props) => /* @__PURE__ */ _jsx849("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx849("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M3.17364 6.92731C2.9068 6.96674 2.63451 6.89426 2.43965 6.70641L0.889466 5.21201C0.549012 4.88381 0.573861 4.32773 0.944966 3.96998C1.31607 3.61223 1.8929 3.58828 2.23336 3.91648L3.26379 4.90983L6.28755 1.99489C6.65865 1.63714 7.23548 1.61318 7.57594 1.94138C7.91639 2.26958 7.89154 2.82566 7.52044 3.18341L3.93013 6.64451C3.71773 6.84927 3.43793 6.94469 3.17364 6.92731Z",
    fill: props.color
  })
});
var IconTinyCheck_default = IconTinyCheck;

// src/icons/tiny/IconTinyClock.tsx
import { jsx as _jsx850 } from "react/jsx-runtime";
var IconTinyClock = (props) => /* @__PURE__ */ _jsx850("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx850("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M8 4C8 6.20914 6.20914 8 4 8C1.79086 8 0 6.20914 0 4C0 1.79086 1.79086 0 4 0C6.20914 0 8 1.79086 8 4ZM6.05781 5.29332C6.11114 5.31554 6.16892 5.32444 6.22225 5.32444C6.40003 5.32444 6.56444 5.21777 6.63556 5.04888C6.72444 4.82222 6.61336 4.56444 6.38669 4.47111L4.28003 3.62666L3.02222 1.94667C2.87555 1.75111 2.5956 1.71111 2.40004 1.85778C2.20449 2.00444 2.16445 2.28445 2.31112 2.48001L3.64445 4.25779C3.69334 4.32445 3.76003 4.37332 3.83559 4.40443L6.05781 5.29332Z",
    fill: props.color
  })
});
var IconTinyClock_default = IconTinyClock;

// src/icons/tiny/IconTinyClose.tsx
import { jsx as _jsx851 } from "react/jsx-runtime";
var IconTinyClose = (props) => /* @__PURE__ */ _jsx851("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx851("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M5.27833 4L7.73961 6.46128C8.0868 6.80847 8.0868 7.37137 7.73961 7.71856L7.71856 7.73961C7.37137 8.0868 6.80847 8.0868 6.46128 7.73961L4 5.27833L1.53872 7.73961C1.19153 8.0868 0.62863 8.0868 0.281443 7.73961L0.26039 7.71856C-0.0867967 7.37137 -0.0867967 6.80847 0.26039 6.46128L2.72167 4L0.26039 1.53872C-0.0867967 1.19153 -0.0867967 0.62863 0.26039 0.281443L0.281443 0.26039C0.62863 -0.0867967 1.19153 -0.0867967 1.53872 0.26039L4 2.72167L6.46128 0.26039C6.80847 -0.0867967 7.37137 -0.0867967 7.71856 0.26039L7.73961 0.281443C8.0868 0.62863 8.0868 1.19153 7.73961 1.53872L5.27833 4Z",
    fill: props.color
  })
});
var IconTinyClose_default = IconTinyClose;

// src/icons/tiny/IconTinyEdit.tsx
import { jsx as _jsx852, jsxs as _jsxs29 } from "react/jsx-runtime";
var IconTinyEdit = (props) => /* @__PURE__ */ _jsxs29("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx852("path", {
    d: "M0 7.99978L2.11843 7.53207L0.467681 5.88135L0 7.99978Z",
    fill: props.color
  }), /* @__PURE__ */ _jsx852("path", {
    d: "M7.60012 2.37833C7.8576 2.1214 7.99949 1.77961 7.99968 1.41586C7.99987 1.05211 7.85833 0.710162 7.6011 0.452985L7.54665 0.398502C7.28971 0.141542 6.94798 0 6.58453 0C6.58426 0 6.58407 0 6.58382 0C6.2201 0.000190501 5.87826 0.142087 5.62133 0.399563L0.693359 5.33779L2.66186 7.3063L7.60012 2.37833Z",
    fill: props.color
  })]
});
var IconTinyEdit_default = IconTinyEdit;

// src/icons/tiny/IconTinyLock.tsx
import { jsx as _jsx853, jsxs as _jsxs30 } from "react/jsx-runtime";
var IconTinyLock = (props) => /* @__PURE__ */ _jsxs30("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [/* @__PURE__ */ _jsx853("path", {
    d: "M6.49922 3.11084V2.22203C6.49922 0.999912 5.37422 0 3.99922 0C2.62422 0 1.49922 0.999912 1.49922 2.22203V3.11084H0.999225C0.815891 3.11084 2.64922 3.11084 6.49922 3.11084ZM2.49922 2.22203C2.49922 1.48876 3.17422 0.888811 3.99922 0.888811C4.82422 0.888811 5.49922 1.48876 5.49922 2.22203V3.11084H2.49922V2.22203Z",
    fill: props.color
  }), /* @__PURE__ */ _jsx853("path", {
    d: "M7 3.11154H1C0.725 3.11154 0.5 3.31152 0.5 3.55595V6.22238C0.5 7.20007 1.4 8 2.5 8H5.5C6.6 8 7.5 7.20007 7.5 6.22238V3.55595C7.5 3.31152 7.275 3.11154 7 3.11154ZM3.9725 4.77806H4.0275C4.28845 4.77806 4.5 4.97146 4.5 5.21002V5.67931C4.5 5.91788 4.28845 6.11128 4.0275 6.11128H3.9725C3.71155 6.11128 3.5 5.91788 3.5 5.67931V5.21002C3.5 4.97146 3.71155 4.77806 3.9725 4.77806Z",
    fill: props.color
  })]
});
var IconTinyLock_default = IconTinyLock;

// src/icons/tiny/IconTinyMinus.tsx
import { jsx as _jsx854 } from "react/jsx-runtime";
var IconTinyMinus = (props) => /* @__PURE__ */ _jsx854("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx854("path", {
    d: "M4.67583 3H3.32417H0.675833C0.302581 3 0 3.44772 0 4C0 4.55228 0.302581 5 0.675833 5H3.32417H4.67583H7.32417C7.69742 5 8 4.55228 8 4C8 3.44772 7.69742 3 7.32417 3H4.67583Z",
    fill: props.color
  })
});
var IconTinyMinus_default = IconTinyMinus;

// src/icons/tiny/IconTinyPerson.tsx
import { jsx as _jsx855 } from "react/jsx-runtime";
var IconTinyPerson = (props) => /* @__PURE__ */ _jsx855("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx855("path", {
    d: "M4 3.94988C2.89543 3.94988 2 3.06567 2 1.97494C2 0.884211 2.89543 0 4 0C5.10457 0 6 0.884211 6 1.97494C6 3.06567 5.10457 3.94988 4 3.94988ZM8 7.10945H0C0 7.10205 0 6.96714 0 6.70472C0 5.61956 1.55832 4.73986 4 4.73986C6.44168 4.73986 8 5.61956 8 6.70472C8 6.98262 8 7.11753 8 7.10945Z",
    fill: props.color
  })
});
var IconTinyPerson_default = IconTinyPerson;

// src/icons/tiny/IconTinyPlus.tsx
import { jsx as _jsx856 } from "react/jsx-runtime";
var IconTinyPlus = (props) => /* @__PURE__ */ _jsx856("svg", {
  width: "8",
  height: "8",
  viewBox: "0 0 8 8",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: /* @__PURE__ */ _jsx856("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M3.32417 3.32417V0.675833C3.32417 0.302581 3.62675 0 4 0C4.37325 0 4.67583 0.302581 4.67583 0.675833V3.32417H7.32417C7.69742 3.32417 8 3.62675 8 4C8 4.37325 7.69742 4.67583 7.32417 4.67583H4.67583V7.32417C4.67583 7.69742 4.37325 8 4 8C3.62675 8 3.32417 7.69742 3.32417 7.32417V4.67583H0.675833C0.302581 4.67583 0 4.37325 0 4C0 3.62675 0.302581 3.32417 0.675833 3.32417H3.32417Z",
    fill: props.color
  })
});
var IconTinyPlus_default = IconTinyPlus;

// src/utils/AutoScrolled.tsx
import { useEffect as useEffect2, useRef } from "react";
import { styled } from "styled-components";
import { jsx as _jsx857 } from "react/jsx-runtime";
var AutoScrolled = ({
  children,
  scrollMargin = "45px",
  block = "start"
}) => {
  const ref = useRef(null);
  useEffect2(() => {
    ref.current?.scrollIntoView({
      behavior: "smooth",
      block
    });
  }, [block]);
  return /* @__PURE__ */ _jsx857(Container, {
    scrollMargin,
    ref,
    children
  });
};
var Container = styled.div.withConfig({
  displayName: "AutoScrolled__Container",
  componentId: "sc-179ka74-0"
})(["scroll-margin:", ";"], ({
  scrollMargin
}) => scrollMargin);

// src/utils/Container.ts
import { styled as styled2 } from "styled-components";
var Container2 = styled2.div.withConfig({
  displayName: "Container",
  componentId: "sc-1m5e5zo-0"
})(["", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", ""], ({
  relative
}) => relative && `position: relative;`, ({
  background
}) => background && `background: ${background};`, ({
  tabletBackground
}) => tabletBackground && tablet`background: ${tabletBackground};`, ({
  radius: radius2
}) => radius2 && `border-radius: ${radius2};`, ({
  border
}) => border && `border: ${border};`, ({
  tabletBorder
}) => tabletBorder && tablet`border: ${tabletBorder};`, ({
  desktopBorder
}) => desktopBorder && desktop`border: ${desktopBorder};`, ({
  borderBottom
}) => borderBottom && `border-bottom: ${borderBottom};`, ({
  borderTop
}) => borderTop && `border-top: ${borderTop};`, ({
  shadow
}) => shadow && `box-shadow: ${shadow};`, ({
  tabletShadow
}) => tabletShadow && tablet`box-shadow: ${tabletShadow};`, ({
  padding
}) => padding && `padding: ${padding};`, ({
  tabletPadding
}) => tabletPadding && tablet`padding: ${tabletPadding};`, ({
  desktopPadding
}) => desktopPadding && desktop`padding: ${desktopPadding};`, ({
  desktopLargePadding
}) => desktopLargePadding && desktopLarge`padding: ${desktopLargePadding};`, ({
  paddingTop
}) => paddingTop && `padding-top: ${paddingTop};`, ({
  tabletPaddingTop
}) => tabletPaddingTop && tablet`padding-top: ${tabletPaddingTop};`, ({
  desktopPaddingTop
}) => desktopPaddingTop && desktop`padding-top: ${desktopPaddingTop};`, ({
  paddingRight
}) => paddingRight && `padding-right: ${paddingRight};`, ({
  tabletPaddingRight
}) => tabletPaddingRight && tablet`padding-right: ${tabletPaddingRight};`, ({
  desktopPaddingRight
}) => desktopPaddingRight && desktop`padding-right: ${desktopPaddingRight};`, ({
  paddingBottom
}) => paddingBottom && `padding-bottom: ${paddingBottom};`, ({
  tabletPaddingBottom
}) => tabletPaddingBottom && tablet`padding-bottom: ${tabletPaddingBottom};`, ({
  desktopPaddingBottom
}) => desktopPaddingBottom && desktop`padding-bottom: ${desktopPaddingBottom};`, ({
  paddingLeft
}) => paddingLeft && `padding-left: ${paddingLeft};`, ({
  tabletPaddingLeft
}) => tabletPaddingLeft && tablet`padding-left: ${tabletPaddingLeft};`, ({
  desktopPaddingLeft
}) => desktopPaddingLeft && desktop`padding-left: ${desktopPaddingLeft};`);

// src/utils/Flexer.ts
import { styled as styled3 } from "styled-components";
var Flexer = styled3.div.withConfig({
  displayName: "Flexer",
  componentId: "sc-bvhptq-0"
})(["display:flex;", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", ""], ({
  gap
}) => gap && `gap: ${gap};`, ({
  tabletGap
}) => tabletGap && tablet`gap: ${tabletGap};`, ({
  desktopGap
}) => desktopGap && desktop`gap: ${desktopGap};`, ({
  desktopLargeGap
}) => desktopLargeGap && desktopLarge`gap: ${desktopLargeGap};`, ({
  row
}) => row && `flex-direction: row;`, ({
  tabletRow
}) => tabletRow && tablet`flex-direction: row;`, ({
  desktopRow
}) => desktopRow && desktop`flex-direction: row;`, ({
  desktopLargeRow
}) => desktopLargeRow && desktopLarge`flex-direction: row;`, ({
  rowReverse
}) => rowReverse && `flex-direction: row-reverse;`, ({
  tabletRowReverse
}) => tabletRowReverse && tablet`flex-direction: row-reverse;`, ({
  desktopRowReverse
}) => desktopRowReverse && desktop`flex-direction: row-reverse;`, ({
  column
}) => column && `flex-direction: column;`, ({
  tabletColumn
}) => tabletColumn && tablet`flex-direction: column;`, ({
  desktopColumn
}) => desktopColumn && desktop`flex-direction: column;`, ({
  columnReverse
}) => columnReverse && `flex-direction: column-reverse;`, ({
  tabletColumnReverse
}) => tabletColumnReverse && tablet`flex-direction: column-reverse;`, ({
  desktopColumnReverse
}) => desktopColumnReverse && desktop`flex-direction: column-reverse;`, ({
  alignBaseline
}) => alignBaseline && `align-items: baseline;`, ({
  tabletAlignBaseline
}) => tabletAlignBaseline && tablet`align-items: baseline;`, ({
  desktopAlignBaseline
}) => desktopAlignBaseline && desktop`align-items: baseline;`, ({
  desktopLargeAlignBaseline
}) => desktopLargeAlignBaseline && desktopLarge`align-items: baseline;`, ({
  alignCenter
}) => alignCenter && `align-items: center;`, ({
  tabletAlignCenter
}) => tabletAlignCenter && tablet`align-items: center;`, ({
  desktopAlignCenter
}) => desktopAlignCenter && desktop`align-items: center;`, ({
  alignStart
}) => alignStart && `align-items: start;`, ({
  tabletAlignStart
}) => tabletAlignStart && tablet`align-items: start;`, ({
  desktopAlignStart
}) => desktopAlignStart && desktop`align-items: start;`, ({
  alignEnd
}) => alignEnd && `align-items: end;`, ({
  tabletAlignEnd
}) => tabletAlignEnd && tablet`align-items: end;`, ({
  desktopAlignEnd
}) => desktopAlignEnd && desktop`align-items: end;`, ({
  justifyStart
}) => justifyStart && `justify-content: start;`, ({
  tabletJustifyStart
}) => tabletJustifyStart && tablet`justify-content: start;`, ({
  desktopJustifyStart
}) => desktopJustifyStart && desktop`justify-content: start;`, ({
  justifyEnd
}) => justifyEnd && `justify-content: end;`, ({
  tabletJustifyEnd
}) => tabletJustifyEnd && tablet`justify-content: end;`, ({
  desktopJustifyEnd
}) => desktopJustifyEnd && desktop`justify-content: end;`, ({
  justifyCenter
}) => justifyCenter && `justify-content: center;`, ({
  tabletJustifyCenter
}) => tabletJustifyCenter && tablet`justify-content: center;`, ({
  desktopJustifyCenter
}) => desktopJustifyCenter && desktop`justify-content: center;`, ({
  spaceBetween
}) => spaceBetween && `justify-content: space-between;`, ({
  tabletSpaceBetween
}) => tabletSpaceBetween && tablet`justify-content: space-between;`, ({
  desktopSpaceBetween
}) => desktopSpaceBetween && desktop`justify-content: space-between;`, ({
  flexWrap
}) => flexWrap && `flex-wrap: wrap;`, ({
  tabletFlexWrap
}) => tabletFlexWrap && tablet`flex-wrap: wrap;`, ({
  desktopFlexWrap
}) => desktopFlexWrap && desktop`flex-wrap: wrap;`, ({
  height
}) => height && `height: ${height};`, ({
  tabletHeight
}) => tabletHeight && tablet`height: ${tabletHeight};`, ({
  desktopHeight
}) => desktopHeight && desktop`height: ${desktopHeight};`, ({
  width
}) => width && `width: ${width};`, ({
  tabletWidth
}) => tabletWidth && tablet`width: ${tabletWidth};`, ({
  desktopWidth
}) => desktopWidth && desktop`width: ${desktopWidth};`, ({
  desktopLargeWidth
}) => desktopLargeWidth && desktopLarge`width: ${desktopLargeWidth};`);

// src/utils/Grid.ts
import { styled as styled4 } from "styled-components";
var Grid = styled4.div.withConfig({
  displayName: "Grid",
  componentId: "sc-1oyw2ml-0"
})(["display:grid;", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", ""], ({
  gap
}) => gap && `gap: ${gap};`, ({
  tabletGap
}) => tabletGap && tablet`gap: ${tabletGap};`, ({
  desktopGap
}) => desktopGap && desktop`gap: ${desktopGap};`, ({
  columnGap
}) => columnGap && `column-gap: ${columnGap};`, ({
  tabletColumnGap
}) => tabletColumnGap && tablet`column-gap: ${tabletColumnGap};`, ({
  desktopColumnGap
}) => desktopColumnGap && desktop`column-gap: ${desktopColumnGap};`, ({
  rowGap
}) => rowGap && `row-gap: ${rowGap};`, ({
  tabletRowGap
}) => tabletRowGap && tablet`row-gap: ${tabletRowGap};`, ({
  desktopRowGap
}) => desktopRowGap && desktop`row-gap: ${desktopRowGap};`, ({
  columns
}) => columns && `grid-template-columns: ${Number.isInteger(columns) ? "1fr ".repeat(Number(columns)) : columns};`, ({
  tabletColumns
}) => tabletColumns && tablet`grid-template-columns: ${Number.isInteger(tabletColumns) ? "1fr ".repeat(Number(tabletColumns)) : tabletColumns};`, ({
  desktopColumns
}) => desktopColumns && desktop`grid-template-columns: ${Number.isInteger(desktopColumns) ? "1fr ".repeat(Number(desktopColumns)) : desktopColumns};`, ({
  desktopLargeColumns
}) => desktopLargeColumns && desktopLarge`grid-template-columns: ${Number.isInteger(desktopLargeColumns) ? "1fr ".repeat(Number(desktopLargeColumns)) : desktopLargeColumns};`, ({
  rows
}) => rows && `grid-template-rows: ${Number.isInteger(rows) ? "1fr ".repeat(Number(rows)) : rows};`, ({
  tabletRows
}) => tabletRows && tablet`grid-template-rows: ${Number.isInteger(tabletRows) ? "1fr ".repeat(Number(tabletRows)) : tabletRows};`, ({
  alignCenter
}) => alignCenter && `align-items: center;`, ({
  tabletAlignCenter
}) => tabletAlignCenter && tablet`align-items: center;`, ({
  desktopAlignCenter
}) => desktopAlignCenter && desktop`align-items: center;`, ({
  justifyEnd
}) => justifyEnd && `justify-content: end;`, ({
  justifyItemsCenter
}) => justifyItemsCenter && `justify-items: center;`, ({
  tabletJustifyItemsCenter
}) => tabletJustifyItemsCenter && tablet`justify-items: center;`, ({
  width
}) => width && `width: ${width};`, ({
  tabletWidth
}) => tabletWidth && tablet`width: ${tabletWidth};`, ({
  desktopWidth
}) => desktopWidth && desktop`width: ${desktopWidth};`);

// src/utils/GridItem.ts
import { styled as styled5 } from "styled-components";
var GridItem = styled5.div.withConfig({
  displayName: "GridItem",
  componentId: "sc-1lbqsk0-0"
})(["", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", ""], ({
  columnStart
}) => columnStart && `grid-column-start: ${columnStart};`, ({
  column
}) => column && `grid-column: ${column};`, ({
  tabletColumn
}) => tabletColumn && tablet`grid-column: ${tabletColumn};`, ({
  desktopColumn
}) => desktopColumn && desktop`grid-column: ${desktopColumn};`, ({
  row
}) => row && `grid-row: ${row};`, ({
  tabletRow
}) => tabletRow && tablet`grid-row: ${tabletRow};`, ({
  desktopRow
}) => desktopRow && desktop`grid-row: ${desktopRow};`, ({
  alignStart
}) => alignStart && `align-self: start;`, ({
  alignEnd
}) => alignEnd && `align-self: end;`, ({
  alignCenter
}) => alignCenter && `align-self: center;`, ({
  alignStretch
}) => alignStretch && `align-self: stretch;`, ({
  justifyStart
}) => justifyStart && `justify-self: start;`, ({
  justifyEnd
}) => justifyEnd && `justify-self: end;`, ({
  justifyCenter
}) => justifyCenter && `justify-self: center;`, ({
  justifyStretch
}) => justifyStretch && `justify-self: stretch;`);

// src/utils/Heading.ts
import { styled as styled6 } from "styled-components";
var Heading = styled6.div.withConfig({
  displayName: "Heading",
  componentId: "sc-1rtovuy-0"
})(["color:", ";font-family:", ";font-size:", ";font-weight:", ";line-height:1.3;text-align:", ";", " ", " ", " ", "{font-size:", ";font-weight:", ";}", "{font-size:", ";font-weight:", ";}", "{font-size:", ";}"], ({
  color
}) => color ?? "inherit", ({
  family
}) => family ?? "inherit", ({
  size: size2
}) => size2 ?? font.title.medium, ({
  weight
}) => weight ?? font.weight.medium, ({
  textAlign
}) => textAlign ?? "inherit", ({
  tabletTextAlign
}) => tabletTextAlign && tablet`text-align: ${tabletTextAlign};`, ({
  desktopTextAlign
}) => desktopTextAlign && desktop`text-align: ${desktopTextAlign};`, ({
  alignCenter
}) => alignCenter && `text-align: center;`, media.tablet, ({
  size: size2,
  tabletSize
}) => tabletSize ?? size2 ?? font.title.large, ({
  weight,
  tabletWeight
}) => tabletWeight ?? weight ?? font.weight.medium, media.desktop, ({
  size: size2,
  tabletSize,
  desktopSize
}) => desktopSize ?? tabletSize ?? size2 ?? font.title.large, ({
  weight,
  desktopWeight
}) => desktopWeight ?? weight ?? font.weight.medium, media.desktopLarge, ({
  size: size2,
  tabletSize,
  desktopSize,
  desktopLargeSize
}) => desktopLargeSize ?? desktopSize ?? tabletSize ?? size2 ?? font.title.large);

// src/utils/InlineLink.ts
import { styled as styled7 } from "styled-components";
var InlineLink = styled7.a.withConfig({
  displayName: "InlineLink",
  componentId: "sc-ftknjn-0"
})(["color:", ";font-weight:", ";cursor:pointer;text-decoration:underline;", "{&:hover{text-decoration:none;color:", ";}}"], ({
  color
}) => color || colors.text.interactive, font.weight.medium, media.pointer, colors.text.base);

// src/utils/Line.ts
import { styled as styled8 } from "styled-components";
var Line = styled8.div.withConfig({
  displayName: "Line",
  componentId: "sc-1tcenjq-0"
})(["color:", ";font-size:", ";font-weight:", ";line-height:", ";text-align:", ";", " ", " ", "{font-size:", ";}"], ({
  color
}) => color || "inherit", ({
  size: size2
}) => size2 || font.body.medium, ({
  weight
}) => weight || font.weight.normal, ({
  lineHeight
}) => lineHeight || 1.5, ({
  textAlign
}) => textAlign || "inherit", ({
  tabletTextAlign
}) => tabletTextAlign && tablet`text-align: ${tabletTextAlign};`, ({
  desktopTextAlign
}) => desktopTextAlign && desktop`text-align: ${desktopTextAlign};`, media.tablet, ({
  size: size2,
  tabletSize
}) => tabletSize || size2 || font.body.large);

// src/utils/Paragraph.ts
import { styled as styled9 } from "styled-components";
var Paragraph = styled9.p.withConfig({
  displayName: "Paragraph",
  componentId: "sc-993ofd-0"
})(["color:", ";font-size:", ";line-height:1.5;text-align:", ";", " ", " margin:0;", " ", "{font-size:", ";}&:not(:last-child){margin-bottom:9px;}strong{font-weight:", ";}"], ({
  color
}) => color || "inherit", ({
  size: size2
}) => size2 || font.body.medium, ({
  textAlign
}) => textAlign || "inherit", ({
  tabletTextAlign
}) => tabletTextAlign && tablet`text-align: ${tabletTextAlign};`, ({
  desktopTextAlign
}) => desktopTextAlign && desktop`text-align: ${desktopTextAlign};`, ({
  alignCenter
}) => alignCenter && `text-align: center;`, media.tablet, ({
  size: size2,
  tabletSize
}) => tabletSize || size2 || font.body.large, font.weight.bold);

// src/utils/Spacer.ts
import { styled as styled10 } from "styled-components";
var Spacer = styled10.div.withConfig({
  displayName: "Spacer",
  componentId: "sc-rzcs7c-0"
})(["", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", ""], ({
  alignSelf
}) => alignSelf && `align-self: ${alignSelf};`, ({
  inline
}) => inline && `display: inline;`, ({
  flex
}) => flex && `flex: ${flex};`, ({
  tabletFlex
}) => tabletFlex && tablet`flex: ${tabletFlex};`, ({
  desktopFlex
}) => desktopFlex && desktop`flex: ${desktopFlex};`, ({
  flexFill
}) => flexFill && `flex: 1 1 auto;`, ({
  margin
}) => margin && `margin: ${margin};`, ({
  tabletMargin
}) => tabletMargin && tablet`margin: ${tabletMargin};`, ({
  desktopMargin
}) => desktopMargin && desktop`margin: ${desktopMargin};`, ({
  marginTop
}) => marginTop && `margin-top: ${marginTop};`, ({
  tabletMarginTop
}) => tabletMarginTop && tablet`margin-top: ${tabletMarginTop};`, ({
  desktopMarginTop
}) => desktopMarginTop && desktop`margin-top: ${desktopMarginTop};`, ({
  marginRight
}) => marginRight && `margin-right: ${marginRight};`, ({
  tabletMarginRight
}) => tabletMarginRight && tablet`margin-right: ${tabletMarginRight};`, ({
  desktopMarginRight
}) => desktopMarginRight && desktop`margin-right: ${desktopMarginRight};`, ({
  marginBottom
}) => marginBottom && `margin-bottom: ${marginBottom};`, ({
  tabletMarginBottom
}) => tabletMarginBottom && tablet`margin-bottom: ${tabletMarginBottom};`, ({
  desktopMarginBottom
}) => desktopMarginBottom && desktop`margin-bottom: ${desktopMarginBottom};`, ({
  marginLeft
}) => marginLeft && `margin-left: ${marginLeft};`, ({
  tabletMarginLeft
}) => tabletMarginLeft && tablet`margin-left: ${tabletMarginLeft};`, ({
  desktopMarginLeft
}) => desktopMarginLeft && desktop`margin-left: ${desktopMarginLeft};`, ({
  width
}) => width && `width: ${width};`, ({
  tabletWidth
}) => tabletWidth && tablet`width: ${tabletWidth};`, ({
  desktopWidth
}) => desktopWidth && desktop`width: ${desktopWidth};`, ({
  desktopLargeWidth
}) => desktopLargeWidth && desktopLarge`width: ${desktopLargeWidth};`);

// src/atoms/ActionButton.tsx
import { styled as styled13 } from "styled-components";
import { isDefined } from "@scouts/helpers";

// src/atoms/Loadable.tsx
import { Transition } from "react-transition-group";
import { styled as styled12 } from "styled-components";
import { hexToRgbA as hexToRgbA2 } from "@scouts/helpers";

// src/atoms/Spinner.tsx
import { keyframes, styled as styled11 } from "styled-components";
import { jsx as _jsx858 } from "react/jsx-runtime";
var keyframesSpin = keyframes`
    to {
        transform: rotate(360deg);
    }
`;
var Container3 = styled11.div.withConfig({
  displayName: "Spinner__Container",
  componentId: "sc-xt3p3n-0"
})(["content:'';box-sizing:border-box;width:", ";height:", ";border:", ";border-top-color:", ";border-radius:", ";animation:", " 0.6s linear infinite;"], ({
  isSmall
}) => isSmall ? "20px" : "42px", ({
  isSmall
}) => isSmall ? "20px" : "42px", ({
  color2
}) => `2px solid ${color2}`, ({
  color
}) => color, radius.full, keyframesSpin);
var Spinner = ({
  color = colors.line.neutral,
  color2 = colors.line.inverted,
  isSmall = false
}) => /* @__PURE__ */ _jsx858(Container3, {
  role: "progressbar",
  "aria-busy": "true",
  "aria-live": "polite",
  "aria-label": "loading",
  color,
  color2,
  isSmall
});

// src/atoms/Loadable.tsx
import { jsx as _jsx859, jsxs as _jsxs31 } from "react/jsx-runtime";
var Loadable = ({
  children,
  isLoading,
  isSmall,
  overlayColor,
  overlayOpacity = 0.8,
  shouldHideContentWhenLoading,
  spinnerColor,
  spinnerColor2,
  ...rest
}) => /* @__PURE__ */ _jsxs31(Container4, {
  isEmpty: !children,
  isSmall,
  isLoading,
  ...rest,
  children: [/* @__PURE__ */ _jsx859(Transition, {
    timeout: {
      appear: 0,
      enter: 0,
      exit: 150
    },
    in: isLoading,
    children: (state) => /* @__PURE__ */ _jsx859(Overlay, {
      state,
      hasOverlay: !shouldHideContentWhenLoading,
      overlayColor,
      overlayOpacity,
      children: /* @__PURE__ */ _jsx859(SpinnerContainer, {
        children: /* @__PURE__ */ _jsx859(Spinner, {
          isSmall,
          color: spinnerColor,
          color2: spinnerColor2
        })
      })
    })
  }), /* @__PURE__ */ _jsx859(Content, {
    shouldHideContentWhenLoading,
    isLoading,
    children
  })]
});
var Container4 = styled12.div.withConfig({
  displayName: "Loadable__Container",
  componentId: "sc-1pvr6bk-0"
})(["position:relative;height:100%;", " ", ""], ({
  isEmpty,
  isLoading,
  isSmall,
  isFullViewportHeight
}) => isEmpty && isLoading && !isFullViewportHeight && `
        min-height: ${isSmall ? "48px" : "96px"};
    `, ({
  isFullViewportHeight
}) => isFullViewportHeight && `
        min-height: 100vh;
    `);
var Overlay = styled12.div.withConfig({
  displayName: "Loadable__Overlay",
  componentId: "sc-1pvr6bk-1"
})(["position:absolute;top:0;left:0;z-index:10;height:100%;width:100%;display:block;opacity:0;transition:opacity 150ms linear;", " ", ";", ";", ";", ";"], ({
  hasOverlay,
  overlayColor,
  overlayOpacity
}) => hasOverlay && `background: ${hexToRgbA2(overlayColor ?? colors.surface.neutral, overlayOpacity)};`, ({
  state
}) => state === "entering" && `
        display: block;
        opacity: 0;
    `, ({
  state
}) => state === "entered" && `
        display: block;
        opacity: 1;
    `, ({
  state
}) => state === "exiting" && `
        display: block;
        opacity: 0;
    `, ({
  state
}) => state === "exited" && `
        display: none;
        opacity: 0;
    `);
var Content = styled12.div.withConfig({
  displayName: "Loadable__Content",
  componentId: "sc-1pvr6bk-2"
})(["height:100%;", " ", ""], ({
  isLoading
}) => isLoading && `
        pointer-events: none;
        user-select: none;
    `, ({
  isLoading,
  shouldHideContentWhenLoading
}) => isLoading && shouldHideContentWhenLoading && `visibility: hidden;
    `);
var SpinnerContainer = styled12.div.withConfig({
  displayName: "Loadable__SpinnerContainer",
  componentId: "sc-1pvr6bk-3"
})(["position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);"]);

// src/atoms/ActionButton.tsx
import { jsx as _jsx860 } from "react/jsx-runtime";
var ActionButton = ({
  as = "button",
  type = "button",
  variant = "primary",
  color = "primary",
  size: size2 = "large",
  disabled,
  isSmall,
  isLoading,
  children,
  ...rest
}) => {
  const isButtonDisabled = disabled || isLoading;
  const updatedSize = isSmall ? "medium" : size2;
  return /* @__PURE__ */ _jsx860(Container5, {
    ...rest,
    as,
    type,
    isSmall,
    isLoading,
    "aria-disabled": isButtonDisabled,
    disabled: isButtonDisabled,
    size: updatedSize,
    variant,
    color,
    children: /* @__PURE__ */ _jsx860(Loadable, {
      isSmall: true,
      isLoading,
      spinnerColor: getColor({
        ...rest,
        variant,
        color,
        disabled,
        isLoading
      }),
      spinnerColor2: getBackgroundColorHover({
        ...rest,
        variant,
        color
      }),
      overlayOpacity: 1,
      shouldHideContentWhenLoading: true,
      overlayColor: getBackgroundColor({
        ...rest,
        variant,
        color,
        disabled,
        isLoading
      }),
      children
    })
  });
};
var getColor = ({
  variant,
  color,
  disabled,
  isLoading
}) => {
  switch (true) {
    case (isDefined(variant) && disabled && !isLoading):
      return theme.text.disabled;
    case (isDefined(variant) && color === "primary"):
      return theme.primary.main;
    case (isDefined(variant) && color === "error"):
      return theme.error.main;
    case (isDefined(variant) && color === "neutral"):
      return theme.neutral.main;
    default:
      return theme.primary.contrast;
  }
};
var getBackgroundColor = ({
  variant,
  color,
  disabled,
  isLoading
}) => {
  switch (true) {
    case (variant === "primary" && disabled && !isLoading):
      return theme.common.disabled1;
    case (variant === "primary" && color === "primary"):
      return theme.primary.primaryAction;
    case (variant === "primary" && color === "error"):
      return theme.error.primaryAction;
    case (variant === "primary" && color === "neutral"):
      return theme.neutral.primaryAction;
    case (variant === "secondary" && disabled && !isLoading):
      return theme.common.disabled2;
    case (variant === "secondary" && color === "primary"):
      return theme.primary.secondaryAction;
    case (variant === "secondary" && color === "error"):
      return theme.error.secondaryAction;
    case (variant === "secondary" && color === "neutral"):
      return theme.neutral.secondaryAction;
    case variant === "tertiary":
      return "transparent";
    default:
      return theme.primary.primaryAction;
  }
};
var getBackgroundColorHover = ({
  variant,
  color
}) => {
  switch (true) {
    case (variant === "secondary" && color === "primary"):
      return theme.primary.hover;
    case (variant === "secondary" && color === "error"):
      return theme.error.hover;
    case (variant === "secondary" && color === "neutral"):
      return theme.neutral.hover;
    case (variant === "tertiary" && color === "primary"):
      return theme.primary.hover;
    case (variant === "tertiary" && color === "error"):
      return theme.error.hover;
    case (variant === "tertiary" && color === "neutral"):
      return theme.neutral.hover;
    case variant === "primary":
      return theme.common.hover;
    default:
      return theme.primary.primaryAction;
  }
};
var getBackgroundColorActive = ({
  variant,
  color
}) => {
  switch (true) {
    case (variant === "secondary" && color === "primary"):
      return theme.primary.pressed;
    case (variant === "secondary" && color === "error"):
      return theme.error.pressed;
    case (variant === "secondary" && color === "neutral"):
      return theme.neutral.pressed;
    case (variant === "tertiary" && color === "primary"):
      return theme.primary.pressed;
    case (variant === "tertiary" && color === "error"):
      return theme.error.pressed;
    case (variant === "tertiary" && color === "neutral"):
      return theme.neutral.pressed;
    case variant === "primary":
      return theme.common.pressed;
    default:
      return theme.primary.primaryAction;
  }
};
var getFontSize = ({
  size: size2
}) => {
  return size2 === "large" ? font.body.large : font.body.medium;
};
var getPadding = ({
  size: size2
}) => {
  return size2 === "large" ? "14px 20px" : "8px 16px";
};
var getBorderRadius = ({
  size: size2
}) => {
  return size2 === "large" ? radius.buttonLarge : radius.buttonMedium;
};
var Container5 = styled13.button.withConfig({
  displayName: "ActionButton__Container",
  componentId: "sc-26hvsz-0"
})(["color:", ";font-family:inherit;font-size:", ";font-weight:", ";line-height:1.3;text-align:center;text-decoration:none;white-space:nowrap;display:", ";position:relative;width:", ";padding:", ";background:", ";border-radius:", ";cursor:pointer;", "{display:", ";width:", ";}&::after{content:'';color:", ";position:absolute;inset:0;border-radius:", ";background-color:rgba(0,0,0,0);transition:background-color 150ms ease-in-out;z-index:0;}> *{position:relative;z-index:1;}", "{&:hover::after{background-color:", ";}}&:active::after{background:", ";outline:none;}&:focus-visible{outline:", ";&::after{background:", ";}}", " ", " ", ""], getColor, getFontSize, font.weight.medium, ({
  isMobileFullWidth,
  isFullWidth
}) => isMobileFullWidth || isFullWidth ? "block" : "inline-block", ({
  isMobileFullWidth,
  isFullWidth
}) => isMobileFullWidth || isFullWidth ? "100%" : "auto", getPadding, getBackgroundColor, getBorderRadius, media.tablet, ({
  isFullWidth
}) => isFullWidth ? "block" : "inline-block", ({
  isFullWidth
}) => isFullWidth ? "100%" : "auto", getColor, getBorderRadius, media.pointer, getBackgroundColorHover, getBackgroundColorActive, borders.interactive.medium, getBackgroundColorActive, ({
  disabled
}) => disabled && `
        pointer-events: none;
        user-select: none;
    `, ({
  isLoading
}) => isLoading && `
        pointer-events: none;
        user-select: none;
    `, ({
  isSelected,
  isSmall
}) => isSelected && `
        position: relative;
        padding-left: ${isSmall ? "32px" : "36px"};
        
        &:before {
            content: '\\002713';
            color: ${getColor};
            font-weight: ${font.weight.medium};
            line-height: 1;
            display: block;
            position: absolute;
            z-index: 1;
            left: ${isSmall ? "12px" : "14px"};
            top: 50%;
            transform: translateY(-50%);
        }
    `);

// src/atoms/ActionPill.tsx
import { styled as styled14 } from "styled-components";
import { jsx as _jsx861, jsxs as _jsxs32 } from "react/jsx-runtime";
var ActionPill = ({
  background,
  IconComponent,
  label,
  onClick
}) => {
  return /* @__PURE__ */ _jsx861(Wrapper, {
    "aria-label": label,
    onClick,
    children: /* @__PURE__ */ _jsx861(Container2, {
      background: background || colors.surface.neutral,
      padding: "12px",
      radius: radius.full,
      children: /* @__PURE__ */ _jsxs32(Flexer, {
        gap: "18px",
        alignCenter: true,
        children: [/* @__PURE__ */ _jsx861(IconContainer, {
          children: /* @__PURE__ */ _jsx861(Icon, {
            Component: IconComponent
          })
        }), /* @__PURE__ */ _jsx861(LabelContainer, {
          children: label
        })]
      })
    })
  });
};
var Wrapper = styled14.button.withConfig({
  displayName: "ActionPill__Wrapper",
  componentId: "sc-1bw4vaw-0"
})(["text-align:left;display:block;width:100%;border-radius:", ";cursor:pointer;&:focus{color:", ";}", "{&:hover{color:", ";}}"], radius.full, colors.text.interactive, media.pointer, colors.text.interactive);
var IconContainer = styled14.div.withConfig({
  displayName: "ActionPill__IconContainer",
  componentId: "sc-1bw4vaw-1"
})(["display:flex;align-items:center;justify-content:center;width:45px;height:45px;background:", ";border-radius:", ";box-shadow:", ";", "{", ":hover &{box-shadow:", ";}}"], colors.surface.base, radius.full, shadows.medium, media.pointer, Wrapper, shadows.high);
var LabelContainer = styled14.div.withConfig({
  displayName: "ActionPill__LabelContainer",
  componentId: "sc-1bw4vaw-2"
})(["font-weight:", ";"], font.weight.medium);

// src/atoms/Avatar.tsx
import { useState as useState3 } from "react";
import styled15 from "styled-components";
import { jsx as _jsx862 } from "react/jsx-runtime";
var Avatar = ({
  width = "48px",
  tabletWidth,
  backgroundColor = theme.neutral.background,
  src,
  shadow,
  alt
}) => {
  const [isLoaded, setIsLoaded] = useState3(false);
  return /* @__PURE__ */ _jsx862(Container6, {
    width,
    tabletWidth,
    backgroundColor,
    shadow,
    children: /* @__PURE__ */ _jsx862(Image, {
      src,
      alt: alt ?? "Avatar",
      onLoad: () => setIsLoaded(true),
      isLoaded
    })
  });
};
var Container6 = styled15.div.withConfig({
  displayName: "Avatar__Container",
  componentId: "sc-1vqt7tt-0"
})(["position:relative;width:", ";height:", ";border-radius:", ";overflow:hidden;background-color:", ";box-shadow:", ";", "{width:", ";height:", ";}"], ({
  width
}) => width, ({
  width
}) => width, radius.full, ({
  backgroundColor
}) => backgroundColor, ({
  shadow
}) => shadow ?? "none", media.tablet, ({
  tabletWidth,
  width
}) => tabletWidth ?? width, ({
  tabletWidth,
  width
}) => tabletWidth ?? width);
var Image = styled15.img.withConfig({
  displayName: "Avatar__Image",
  componentId: "sc-1vqt7tt-1"
})(["width:100%;height:100%;object-fit:cover;opacity:", ";transition:opacity 0.2s;"], ({
  isLoaded
}) => isLoaded ? 1 : 0);

// src/atoms/Badge.ts
import { styled as styled16 } from "styled-components";
var Badge = styled16.div.withConfig({
  displayName: "Badge",
  componentId: "sc-1bizwxb-0"
})(["color:", ";font-size:", ";font-weight:", ";display:inline-flex;align-items:center;justify-content:space-around;position:relative;height:", ";min-width:", ";padding:", ";background:", ";border-radius:", ";&:not(:last-child){margin-right:6px;}"], ({
  color
}) => color ?? theme.primary.contrast, ({
  isSmall
}) => isSmall ? font.body.small : font.body.medium, font.weight.medium, ({
  isSmall
}) => isSmall ? "18px" : "24px", ({
  isSmall
}) => isSmall ? "18px" : "24px", ({
  isSmall
}) => isSmall ? "0 4px" : "0 6px", ({
  background
}) => background ?? theme.primary.primaryAction, radius.full);

// src/atoms/BadgeButton.tsx
import { styled as styled17 } from "styled-components";
import { jsx as _jsx863 } from "react/jsx-runtime";
var getColor2 = ({
  variant
}) => {
  switch (variant) {
    case "default":
      return colors.text.inverted;
    case "dim":
      return colors.text.neutral;
    default:
      return colors.text.interactive;
  }
};
var getFontSize2 = ({
  size: size2
}) => {
  switch (size2) {
    case "small":
      return font.body.small;
    default:
      return font.body.medium;
  }
};
var getBackground = ({
  variant
}) => {
  switch (variant) {
    case "neutral":
      return colors.surface.neutral;
    case "highlight":
      return colors.surface.highlight;
    case "highlightSoft":
      return colors.surface.highlightSoft;
    case "dim":
      return colors.surface.neutral;
    case "inverted":
      return colors.surface.base;
    default:
      return colors.surface.interactive;
  }
};
var getDimension = ({
  size: size2
}) => {
  switch (size2) {
    case "small":
      return "18px";
    default:
      return "24px";
  }
};
var BadgeButton = ({
  children,
  variant = "default",
  size: size2 = "medium",
  onClick,
  disabled
}) => /* @__PURE__ */ _jsx863(Container7, {
  size: size2,
  variant,
  onClick,
  disabled,
  children
});
var Container7 = styled17.button.withConfig({
  displayName: "BadgeButton__Container",
  componentId: "sc-ijuqsx-0"
})(["color:", ";font-family:inherit;font-size:", ";font-weight:", ";display:flex;align-items:center;justify-content:center;position:relative;height:", ";width:", ";min-width:", ";overflow:hidden;background:", ";border-radius:", ";", "{&:hover{opacity:0.8;}}&:disabled{opacity:0.5;pointer-events:none;user-select:none;}", "{&:active{transform:scale(0.96);}}"], getColor2, getFontSize2, font.weight.medium, getDimension, getDimension, getDimension, getBackground, radius.full, media.pointer, media.pointer);

// src/atoms/Button.tsx
import { styled as styled18 } from "styled-components";
import { isDefined as isDefined2 } from "@scouts/helpers";
import { jsx as _jsx864 } from "react/jsx-runtime";
var getColor3 = ({
  variant,
  color,
  disabled,
  isLoading
}) => {
  switch (true) {
    case (isDefined2(variant) && disabled && !isLoading):
      return theme.text.disabled;
    case (isDefined2(variant) && color === "primary"):
      return theme.primary.main;
    case (isDefined2(variant) && color === "error"):
      return theme.error.main;
    case (isDefined2(variant) && color === "neutral"):
      return theme.neutral.main;
    default:
      return theme.primary.contrast;
  }
};
var getBackgroundColor2 = ({
  variant,
  color,
  disabled,
  isLoading
}) => {
  switch (true) {
    case (variant === "primary" && disabled && !isLoading):
      return theme.common.disabled1;
    case (variant === "primary" && color === "primary"):
      return theme.primary.primaryAction;
    case (variant === "primary" && color === "error"):
      return theme.error.primaryAction;
    case (variant === "primary" && color === "neutral"):
      return theme.neutral.primaryAction;
    case (variant === "secondary" && disabled && !isLoading):
      return theme.common.disabled2;
    case (variant === "secondary" && color === "primary"):
      return theme.primary.secondaryAction;
    case (variant === "secondary" && color === "error"):
      return theme.error.secondaryAction;
    case (variant === "secondary" && color === "neutral"):
      return theme.neutral.secondaryAction;
    case variant === "tertiary":
      return "transparent";
    default:
      return theme.primary.primaryAction;
  }
};
var getBackgroundColorHover2 = ({
  variant,
  color
}) => {
  switch (true) {
    case variant === "primary":
      return theme.common.hover;
    case (variant === "secondary" && color === "primary"):
      return theme.primary.hover;
    case (variant === "secondary" && color === "error"):
      return theme.error.hover;
    case (variant === "secondary" && color === "neutral"):
      return theme.neutral.hover;
    case (variant === "tertiary" && color === "primary"):
      return theme.primary.hover;
    case (variant === "tertiary" && color === "error"):
      return theme.error.hover;
    case (variant === "tertiary" && color === "neutral"):
      return theme.neutral.hover;
    default:
      return theme.primary.primaryAction;
  }
};
var getBackgroundColorActive2 = ({
  variant,
  color
}) => {
  switch (true) {
    case variant === "primary":
      return theme.common.pressed;
    case (variant === "secondary" && color === "primary"):
      return theme.primary.pressed;
    case (variant === "secondary" && color === "error"):
      return theme.error.pressed;
    case (variant === "secondary" && color === "neutral"):
      return theme.neutral.pressed;
    case (variant === "tertiary" && color === "primary"):
      return theme.primary.pressed;
    case (variant === "tertiary" && color === "error"):
      return theme.error.pressed;
    case (variant === "tertiary" && color === "neutral"):
      return theme.neutral.pressed;
    default:
      return theme.primary.primaryAction;
  }
};
var getFontSize3 = ({
  size: size2
}) => {
  switch (size2) {
    case "small":
      return font.body.small;
    case "large":
      return font.body.large;
    default:
      return font.body.medium;
  }
};
var getPadding2 = ({
  size: size2
}) => {
  switch (true) {
    case size2 === "large":
      return "14px 20px";
    case size2 === "small":
      return "6px 12px";
    default:
      return "8px 16px";
  }
};
var getBorderRadius2 = ({
  size: size2
}) => {
  switch (true) {
    case size2 === "large":
      return radius.buttonLarge;
    case size2 === "small":
      return radius.small;
    default:
      return radius.buttonMedium;
  }
};
var Button = ({
  as = "button",
  type = "button",
  size: size2 = "medium",
  variant = "primary",
  color = "primary",
  children,
  isLoading,
  disabled,
  ...rest
}) => {
  const shouldBeDisabled = disabled || isLoading;
  return /* @__PURE__ */ _jsx864(Container8, {
    ...rest,
    "aria-disabled": shouldBeDisabled,
    disabled: shouldBeDisabled,
    isLoading,
    variant,
    color,
    as,
    type,
    size: size2,
    children: /* @__PURE__ */ _jsx864(Loadable, {
      isSmall: true,
      isLoading,
      spinnerColor: getColor3({
        ...rest,
        variant,
        color,
        disabled,
        isLoading
      }),
      spinnerColor2: getBackgroundColorHover2({
        ...rest,
        variant,
        color
      }),
      overlayOpacity: 1,
      shouldHideContentWhenLoading: true,
      overlayColor: getBackgroundColor2({
        ...rest,
        variant,
        color,
        disabled,
        isLoading
      }),
      children
    })
  });
};
var Container8 = styled18.button.withConfig({
  displayName: "Button__Container",
  componentId: "sc-1dvo8f5-0"
})(["color:", ";font-family:inherit;font-size:", ";font-weight:", ";line-height:1.3;text-decoration:none;text-align:center;white-space:nowrap;display:", ";position:relative;width:", ";padding:", ";background:", ";border-radius:", ";cursor:pointer;", "{display:", ";width:", ";}&::after{content:'';color:", ";position:absolute;inset:0;border-radius:", ";background-color:rgba(0,0,0,0);transition:background-color 150ms ease-in-out;z-index:0;}> *{position:relative;z-index:1;}", "{&:hover::after{background-color:", ";}}&:active::after{background:", ";outline:none;}&:focus-visible{outline:", ";&::after{background:", ";}}", " ", ""], getColor3, getFontSize3, font.weight.medium, ({
  isMobileFullWidth,
  isFullWidth
}) => isMobileFullWidth || isFullWidth ? "block" : "inline-block", ({
  isMobileFullWidth,
  isFullWidth
}) => isMobileFullWidth || isFullWidth ? "100%" : "auto", getPadding2, getBackgroundColor2, getBorderRadius2, media.tablet, ({
  isFullWidth
}) => isFullWidth ? "block" : "inline-block", ({
  isFullWidth
}) => isFullWidth ? "100%" : "auto", getColor3, getBorderRadius2, media.pointer, getBackgroundColorHover2, getBackgroundColorActive2, borders.interactive.medium, getBackgroundColorActive2, ({
  disabled
}) => disabled && `
        pointer-events: none;
        user-select: none;
    `, ({
  isLoading
}) => isLoading && `
        pointer-events: none;
        user-select: none;
    `);

// src/atoms/ButtonLink.tsx
import { styled as styled19 } from "styled-components";
import { jsx as _jsx865 } from "react/jsx-runtime";
var ButtonLink = ({
  children,
  ...rest
}) => /* @__PURE__ */ _jsx865(Container9, {
  ...rest,
  "aria-disabled": rest.disabled,
  children
});
var Container9 = styled19.button.attrs(({
  type
}) => ({
  type: type || "button"
})).withConfig({
  displayName: "ButtonLink__Container",
  componentId: "sc-1qxwf45-0"
})(["color:", ";font-family:inherit;font-size:", ";font-weight:", ";line-height:1;text-decoration:underline;white-space:nowrap;display:inline-block;padding:0;background:none;border:none;cursor:pointer;-webkit-appearance:none;&:focus{outline:", ";outline-offset:6px;border-radius:", ";}&:active{outline:", ";outline-offset:6px;border-radius:", ";}", "{&:hover{color:", ";text-decoration:none;}}", ""], ({
  color
}) => color || colors.text.interactive, ({
  isSmall,
  size: size2
}) => size2 ? size2 : isSmall ? font.body.small : font.body.medium, font.weight.medium, borders.interactive.medium, radius.small, borders.interactive.medium, radius.small, media.tablet, colors.text.base, ({
  disabled
}) => disabled && `
        opacity: 0.5;
        pointer-events: none;
        user-select: none;
    `);

// src/atoms/Checkbox.tsx
import { forwardRef } from "react";
import { styled as styled20 } from "styled-components";
import { hexToRgbA as hexToRgbA3 } from "@scouts/helpers";
import { jsx as _jsx866, Fragment as _Fragment, jsxs as _jsxs33 } from "react/jsx-runtime";
var getLabelFontSize = ({
  isLabelless,
  isSmall
}) => {
  switch (true) {
    case isLabelless:
      return 0;
    case isSmall:
      return font.body.medium;
    default:
      return font.body.large;
  }
};
var getLabelPaddingLeft = ({
  isLabelless,
  isSmall
}) => {
  switch (true) {
    case isLabelless:
      return "22px";
    case isSmall:
      return "24px";
    default:
      return "34px";
  }
};
var getLabelBeforeMarginLeft = ({
  isLabelless,
  isSmall
}) => {
  switch (true) {
    case isLabelless:
      return "-22px";
    case isSmall:
      return "-24px";
    default:
      return "-34px";
  }
};
var Checkbox = /* @__PURE__ */ forwardRef(({
  checked,
  id: propsId,
  isLabelless,
  isSmall,
  label,
  name,
  onChange,
  readOnly,
  disabled,
  value = "on",
  ...rest
}, ref) => {
  const id = propsId || `${name || ""}${value || ""}`;
  const handleChange = (e) => {
    if (!onChange)
      return;
    const {
      target
    } = e;
    onChange({
      name: target.name,
      checked: target.checked,
      value: target.value
    });
  };
  return /* @__PURE__ */ _jsxs33(_Fragment, {
    children: [/* @__PURE__ */ _jsx866(InputElement, {
      ref,
      type: "checkbox",
      id,
      name,
      value,
      onChange: handleChange,
      checked,
      readOnly,
      disabled: disabled || readOnly,
      "aria-disabled": disabled,
      "aria-readonly": readOnly,
      ...rest
    }), /* @__PURE__ */ _jsx866(Label, {
      htmlFor: id,
      isSmall,
      isLabelless,
      children: label
    })]
  });
});
var InputElement = styled20.input.withConfig({
  displayName: "Checkbox__InputElement",
  componentId: "sc-1estsjf-0"
})(["position:absolute;top:auto;overflow:hidden;clip:rect(1px,1px,1px,1px);width:1px;height:1px;white-space:nowrap;", "{&:hover + label:before{border-color:", ";}}&:focus + label{outline:", ";outline-offset:6px;border-radius:", ";}&:checked + label:before{background-color:", ";border-color:", ";color:", ";line-height:1;}&:disabled + label:before{color:", ";border-color:", ";pointer-events:none;}&:disabled + label{color:", ";pointer-events:none;}&:checked:disabled + label:before{background-color:", ";color:", ";}"], media.pointer, theme.grey.graphics1, borders.interactive.medium, radius.small, theme.primary.light, theme.primary.light, theme.text.contrastText, theme.text.contrastText, theme.common.disabled1, ({
  readOnly
}) => readOnly ? theme.text.primary : theme.text.disabled, theme.common.disabled1, ({
  readOnly
}) => readOnly ? theme.text.primary : theme.text.contrastText);
var Label = styled20.label.withConfig({
  displayName: "Checkbox__Label",
  componentId: "sc-1estsjf-1"
})(["color:", ";font-size:", ";font-weight:", ";line-height:1.3;text-align:left;display:block;height:", ";padding-left:", ";cursor:pointer;&:before{content:'\\002713';color:", ";font-size:", ";font-weight:bold;line-height:1.5;text-align:center;display:block;float:left;width:", ";height:", ";margin-left:", ";margin-top:", ";background:", ";border:", " solid ", ";border-radius:", ";cursor:pointer;transition:all 0.1s ease-out;}a{color:", ";font-weight:", ";&:hover{color:", ";text-decoration:none;}}"], theme.text.primary, getLabelFontSize, font.weight.normal, ({
  isLabelless
}) => isLabelless ? "22px" : "auto", getLabelPaddingLeft, hexToRgbA3(theme.text.title, 0), ({
  isSmall
}) => isSmall ? font.body.small : font.body.large, ({
  isSmall
}) => isSmall ? "16px" : "22px", ({
  isSmall
}) => isSmall ? "16px" : "22px", getLabelBeforeMarginLeft, ({
  isSmall
}) => isSmall ? "1px" : "-1px", theme.grey.background, ({
  isSmall
}) => isSmall ? "1px" : "2px", colors.line.base, radius.small, theme.primary.contrast, font.weight.medium, theme.text.title);

// src/atoms/Chip.tsx
import { css, keyframes as keyframes2, styled as styled21 } from "styled-components";
import { hexToRgbA as hexToRgbA4 } from "@scouts/helpers";
import { jsx as _jsx867 } from "react/jsx-runtime";
var getFontSize4 = ({
  size: size2
}) => {
  switch (true) {
    case size2 === "small":
      return font.body.small;
    case size2 === "large":
      return font.body.large;
    default:
      return font.body.medium;
  }
};
var getPadding3 = ({
  size: size2
}) => {
  switch (true) {
    case size2 === "small":
      return "2px 8px";
    case size2 === "large":
      return "5px 17px";
    default:
      return "5px 11px";
  }
};
var getBorderColor = ({
  withBorder,
  background
}) => {
  switch (true) {
    case withBorder:
      return background || colors.line.neutralSoft;
    default:
      return background || colors.line.neutralSoft;
  }
};
var getBackground2 = ({
  withBorder,
  background
}) => {
  switch (true) {
    case withBorder:
      return colors.surface.base;
    default:
      return background || colors.surface.neutral;
  }
};
var getColor4 = ({
  disabled,
  color
}) => {
  if (disabled)
    return colors.text.base;
  return color ?? colors.text.interactive;
};
var pulse = ({
  background
}) => keyframes2`
    0% {
        // @TODO: Animating background-color is not performant, let's find an alternative
        background-color: ${background};
    }
    50% {
        background-color: ${hexToRgbA4(background, 0.5)};
    }
    100% {
        background-color: ${background};
    }
`;
var Chip = (props) => {
  const {
    children,
    size: size2 = "medium",
    ...rest
  } = props;
  return /* @__PURE__ */ _jsx867(ChipElement, {
    size: size2,
    ...rest,
    children
  });
};
var ChipElement = styled21.span.withConfig({
  displayName: "Chip__ChipElement",
  componentId: "sc-1i5k1ej-0"
})(["color:", ";font-family:inherit;font-size:", ";font-weight:", ";line-height:1.3;text-decoration:none;text-overflow:ellipsis;white-space:nowrap;display:inline-block;overflow:hidden;position:relative;width:", ";max-width:100%;padding:", ";background:", ";border:1px solid ", ";border-radius:", ";", " ", " ", ""], getColor4, getFontSize4, font.weight.normal, ({
  isFullWidth
}) => isFullWidth ? "100%" : "auto", getPadding3, getBackground2, getBorderColor, radius.full, ({
  isInline
}) => isInline && css`
            text-overflow: unset;
            white-space: nowrap;
            overflow: visible;
            max-width: none;
        `, ({
  disabled
}) => disabled && css`
            pointer-events: none;
            user-select: none;
        `, ({
  background,
  isPulsating
}) => isPulsating && background && css`
            animation-duration: 2s;
            animation-iteration-count: infinite;
            animation-name: ${pulse({
  background
})};
            animation-timing-function: ease-in-out;
        `);

// src/atoms/ChipButton.tsx
import { styled as styled22 } from "styled-components";
import { jsx as _jsx868, jsxs as _jsxs34 } from "react/jsx-runtime";
var getColor5 = ({
  variant,
  disabled
}) => {
  switch (true) {
    case disabled:
    case variant === "dim":
      return theme.text.secondary;
    case variant === "affirm":
    case variant === "default":
    case variant === "contrast":
      return theme.primary.contrast;
    default:
      return theme.text.title;
  }
};
var getFontSize5 = ({
  size: size2
}) => {
  switch (size2) {
    case "small":
      return font.body.small;
    default:
      return font.body.medium;
  }
};
var getBackground3 = ({
  variant,
  disabled,
  isLoading
}) => {
  switch (true) {
    case (disabled && !isLoading):
      return colors.surface.neutral;
    case variant === "neutral":
      return colors.surface.neutral;
    case variant === "highlight":
      return colors.surface.highlight;
    case variant === "highlightSoft":
      return colors.surface.highlightSoft;
    case variant === "dim":
      return colors.surface.neutral;
    case variant === "affirm":
      return theme.primary.background;
    case variant === "contrast":
      return theme.primary.primaryAction;
    default:
      return colors.surface.base;
  }
};
var getSpinnerColor = ({
  variant
}) => {
  switch (variant) {
    case "neutral":
      return colors.surface.neutral;
    case "highlight":
      return colors.surface.highlight;
    case "highlightSoft":
      return colors.surface.highlightSoft;
    case "dim":
    case "inverted":
    case "contrast":
      return colors.line.neutralSoft;
    default:
      return colors.surface.interactive;
  }
};
var getPadding4 = ({
  size: size2
}) => {
  switch (size2) {
    case "small":
      return "2px 8px";
    case "large":
      return "11px 23px";
    default:
      return "5px 11px";
  }
};
var ChipButton = ({
  as = "button",
  children,
  IconComponent,
  iconPosition = "after",
  variant = "default",
  size: size2 = "medium",
  withBorder = false,
  onClick,
  isFullWidth,
  isLoading = false,
  disabled = false,
  ...rest
}) => {
  const shouldBeDisabled = disabled || isLoading;
  return /* @__PURE__ */ _jsx868(Container10, {
    ...rest,
    as,
    "aria-disabled": shouldBeDisabled,
    disabled: shouldBeDisabled,
    isLoading,
    isFullWidth,
    onClick,
    size: size2,
    variant,
    withBorder,
    children: /* @__PURE__ */ _jsx868(Loadable, {
      isSmall: true,
      isLoading,
      spinnerColor: getSpinnerColor({
        variant
      }),
      shouldHideContentWhenLoading: true,
      children: /* @__PURE__ */ _jsxs34(Flexer, {
        gap: size2 === "small" ? "6px" : "9px",
        alignCenter: true,
        justifyCenter: true,
        children: [IconComponent && iconPosition === "before" && /* @__PURE__ */ _jsx868(IconComponent, {
          fill: getColor5({
            variant,
            disabled
          })
        }), /* @__PURE__ */ _jsx868(ChipContentWrapper, {
          children
        }), IconComponent && iconPosition === "after" && /* @__PURE__ */ _jsx868(IconComponent, {
          fill: getColor5({
            variant,
            disabled
          })
        })]
      })
    })
  });
};
var Container10 = styled22.button.withConfig({
  displayName: "ChipButton__Container",
  componentId: "sc-cpjetp-0"
})(["color:", ";font-size:", ";font-weight:", ";line-height:1.3;text-decoration:none;text-overflow:ellipsis;white-space:nowrap;display:inline-block;overflow:hidden;position:relative;width:", ";max-width:100%;padding:", ";background:", ";border-radius:", ";&:focus{outline:", ";}", "{cursor:pointer;&:hover{opacity:0.75;}}", " ", ""], getColor5, getFontSize5, font.weight.medium, ({
  isFullWidth
}) => isFullWidth ? "100%" : "auto", getPadding4, getBackground3, radius.full, borders.interactive.medium, media.pointer, ({
  disabled
}) => !disabled && `
        &:focus {
            outline: ${borders.interactive.medium};
        }

        ${media.pointer} {
            &:active {
                transform: scale(0.98);
            }
        }
    `, ({
  disabled
}) => disabled && `
        user-select: none;

        ${media.pointer} {
            cursor: not-allowed;

            &:hover {
                opacity: 1;
            }
        }
    `);
var ChipContentWrapper = styled22.span.withConfig({
  displayName: "ChipButton__ChipContentWrapper",
  componentId: "sc-cpjetp-1"
})(["display:block;overflow:hidden;text-decoration:none;text-overflow:ellipsis;white-space:nowrap;"]);

// src/atoms/DropdownButton.tsx
import { useRef as useRef2, useState as useState4 } from "react";
import FocusLock from "react-focus-lock";
import { styled as styled23 } from "styled-components";
import useOnClickOutside from "use-onclickoutside";
import { useOnEscapeKeydown } from "@scouts/helpers";
import { jsx as _jsx869, Fragment as _Fragment2, jsxs as _jsxs35 } from "react/jsx-runtime";
var getBorderRadius3 = ({
  size: size2
}) => {
  switch (true) {
    case size2 === "large":
      return radius.buttonLarge;
    case size2 === "small":
      return radius.small;
    default:
      return radius.buttonMedium;
  }
};
var getPadding5 = ({
  size: size2
}) => {
  switch (true) {
    case size2 === "large":
      return "14px 36px 14px 20px";
    case size2 === "small":
      return "6px 30px 6px 12px";
    default:
      return "8px 30px 8px 16px";
  }
};
var getBackgroundColorActive3 = ({
  isSecondary
}) => {
  return isSecondary ? theme.primary.pressed : theme.primary.primaryAction;
};
var DropdownButton = ({
  actions,
  alignMobileLeft,
  alignRight,
  children,
  isMobileFullWidth,
  isSecondary,
  isLoading,
  size: size2 = "medium",
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState4(false);
  const handleDropdownClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(true);
  };
  const handleActionClick = (event) => ({
    onClick
  }) => {
    setIsOpen(false);
    onClick(event);
  };
  const ref = useRef2(null);
  useOnClickOutside(ref, () => {
    setIsOpen(false);
  });
  useOnEscapeKeydown(() => setIsOpen(false));
  const hasLabel = !!children;
  return /* @__PURE__ */ _jsxs35(Container11, {
    isMobileFullWidth,
    children: [!hasLabel && /* @__PURE__ */ _jsxs35(LabellessContainer, {
      onClick: handleDropdownClick,
      disabled: isLoading,
      isSecondary,
      ...rest,
      children: [isLoading && /* @__PURE__ */ _jsx869(Spinner, {
        isSmall: true,
        color: isSecondary ? colors.surface.interactive : colors.line.neutral
      }), !isLoading && /* @__PURE__ */ _jsxs35(_Fragment2, {
        children: [/* @__PURE__ */ _jsx869(Dot, {}), /* @__PURE__ */ _jsx869(Dot, {}), /* @__PURE__ */ _jsx869(Dot, {})]
      })]
    }), hasLabel && /* @__PURE__ */ _jsx869(LabelContainer2, {
      isMobileFullWidth,
      isSecondary,
      onClick: handleDropdownClick,
      size: size2,
      ...rest,
      children: /* @__PURE__ */ _jsx869(Loadable, {
        isSmall: true,
        isLoading,
        spinnerColor: isSecondary ? theme.primary.contrast : colors.line.neutral,
        spinnerColor2: isSecondary ? theme.primary.hover : colors.line.neutral,
        shouldHideContentWhenLoading: true,
        children: /* @__PURE__ */ _jsx869(Flexer, {
          alignCenter: true,
          spaceBetween: true,
          children: /* @__PURE__ */ _jsx869(Label2, {
            children
          })
        })
      })
    }), isOpen && /* @__PURE__ */ _jsx869(ActionsContainer, {
      ref,
      alignRight,
      alignMobileLeft,
      children: /* @__PURE__ */ _jsx869(FocusLock, {
        autoFocus: false,
        returnFocus: true,
        children: actions.filter(({
          shouldHide
        }) => !shouldHide).map(({
          title,
          onClick,
          color,
          disabled,
          ...actionRest
        }) => /* @__PURE__ */ _jsx869(Action, {
          onClick: (event) => {
            event.preventDefault();
            handleActionClick(event)({
              onClick
            });
          },
          color,
          disabled,
          ...actionRest,
          children: /* @__PURE__ */ _jsx869(ActionTitle, {
            children: title
          })
        }, title))
      })
    })]
  });
};
var Container11 = styled23.div.withConfig({
  displayName: "DropdownButton__Container",
  componentId: "sc-19v5s15-0"
})(["display:", ";position:relative;", "{display:inline-block;}"], ({
  isMobileFullWidth
}) => isMobileFullWidth ? "block" : "inline-block", media.tablet);
var LabellessContainer = styled23.button.attrs(({
  type
}) => ({
  type: type || "button"
})).withConfig({
  displayName: "DropdownButton__LabellessContainer",
  componentId: "sc-19v5s15-1"
})(["display:flex;flex-direction:column;align-items:center;justify-content:center;width:30px;height:30px;background:", ";border:2px solid ", ";border-radius:9999px;cursor:pointer;&:active{border:", ";outline:none;}&:focus{border:2px solid transparent;outline:", ";}", "{&:hover{opacity:0.75;}}", " ", ""], ({
  isSecondary
}) => isSecondary ? colors.surface.base : colors.surface.neutral, ({
  isSecondary
}) => isSecondary ? colors.surface.base : colors.line.neutralSoft, borders.interactive.medium, borders.interactive.medium, media.pointer, ({
  disabled
}) => disabled && `
        pointer-events: none;
        user-select: none;
    `, ({
  isSecondary
}) => isSecondary && `
        & ${Dot} {
            background: ${theme.primary.contrast};
        }
    `);
var Dot = styled23.div.withConfig({
  displayName: "DropdownButton__Dot",
  componentId: "sc-19v5s15-2"
})(["width:3px;height:3px;margin:1px;background:", ";border-radius:9999px;"], colors.line.neutral);
var LabelContainer2 = styled23.button.attrs(({
  type
}) => ({
  type: type || "button"
})).withConfig({
  displayName: "DropdownButton__LabelContainer",
  componentId: "sc-19v5s15-3"
})(["color:", ";font-family:inherit;line-height:1;text-align:left;display:flex;align-items:center;justify-content:space-between;width:", ";padding:", ";background:", ";background-image:url('data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2210%22%20viewBox%3D%220%200%2016%2010%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M8%209.0625L7.46875%208.53125L1.46875%202.53125L0.9375%202L2%200.9375L2.53125%201.46875L8%206.96875L13.4688%201.5L14%200.96875L15.0625%202L14.5312%202.53125L8.53125%208.53125L8%209.0625Z%22%20fill%3D%22%230C0B0A%22%20fill-opacity%3D%220.65%22%2F%3E%3C%2Fsvg%3E');background-repeat:no-repeat;background-position:right 12px top 50%;background-size:14px 14px;border:2px solid ", ";border-radius:", ";cursor:pointer;&:active::after{background:", ";outline:none;}&:focus-visible{outline:", ";&::after{background:", ";}}", "{width:auto;&:hover{opacity:0.75;}}"], ({
  isSecondary
}) => isSecondary ? theme.primary.contrast : colors.text.base, ({
  isMobileFullWidth
}) => isMobileFullWidth ? "100%" : "auto", getPadding5, ({
  isSecondary
}) => isSecondary ? theme.primary.primaryAction : colors.surface.neutral, ({
  isSecondary
}) => isSecondary ? theme.primary.primaryAction : colors.line.neutralSoft, getBorderRadius3, getBackgroundColorActive3, borders.interactive.medium, getBackgroundColorActive3, media.tablet);
var Label2 = styled23.div.withConfig({
  displayName: "DropdownButton__Label",
  componentId: "sc-19v5s15-4"
})(["font-size:", ";font-weight:", ";"], font.body.medium, font.weight.medium);
var ActionsContainer = styled23.div.withConfig({
  displayName: "DropdownButton__ActionsContainer",
  componentId: "sc-19v5s15-5"
})(["position:absolute;top:100%;right:", ";left:", ";z-index:9999;padding:6px 0;background:", ";border-radius:", ";box-shadow:", ";", " ", " ", "{right:", ";left:", ";}"], ({
  alignRight
}) => alignRight ? 0 : "auto", ({
  alignRight
}) => !alignRight ? 0 : "auto", colors.surface.neutral, radius.medium, shadows.medium, ({
  alignRight
}) => alignRight && `
        left: auto;
        right: 0;
    `, ({
  alignMobileLeft
}) => alignMobileLeft && `
        left: 0;
        right: auto;
    `, media.tablet, ({
  alignRight
}) => alignRight ? 0 : "auto", ({
  alignRight
}) => !alignRight ? 0 : "auto");
var Action = styled23.button.attrs({
  type: "button"
}).withConfig({
  displayName: "DropdownButton__Action",
  componentId: "sc-19v5s15-6"
})(["color:", ";font-family:inherit;font-size:", ";font-weight:", ";text-align:left;white-space:nowrap;display:block;width:100%;padding:9px 30px 10px 18px;background:", ";border:0;cursor:pointer;", "{", "}"], ({
  disabled,
  color
}) => disabled ? colors.text.neutral : color || colors.text.base, font.body.medium, font.weight.medium, colors.surface.neutral, media.pointer, ({
  disabled
}) => !disabled && `
            &:hover {
                color: ${colors.text.interactive};
            }
            &:focus {
                color: ${colors.text.interactive};
            }
        `);
var ActionTitle = styled23.div.withConfig({
  displayName: "DropdownButton__ActionTitle",
  componentId: "sc-19v5s15-7"
})([""]);

// src/atoms/DropdownSelect.tsx
import { useRef as useRef3, useState as useState5 } from "react";
import { isMobile } from "react-device-detect";
import { styled as styled24 } from "styled-components";
import useOnClickOutside2 from "use-onclickoutside";
import { jsx as _jsx870, Fragment as _Fragment3, jsxs as _jsxs36 } from "react/jsx-runtime";
var Container12 = styled24.div.withConfig({
  displayName: "DropdownSelect__Container",
  componentId: "sc-u8shm9-0"
})(["position:relative;width:", ";"], ({
  width
}) => width || "auto");
var LabelContainer3 = styled24.button.withConfig({
  displayName: "DropdownSelect__LabelContainer",
  componentId: "sc-u8shm9-1"
})(["color:", ";font-family:inherit;line-height:1;text-align:left;display:flex;align-items:center;justify-content:space-between;width:", ";padding:", ";background:", ";background-image:url('data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2210%22%20viewBox%3D%220%200%2016%2010%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M8%209.0625L7.46875%208.53125L1.46875%202.53125L0.9375%202L2%200.9375L2.53125%201.46875L8%206.96875L13.4688%201.5L14%200.96875L15.0625%202L14.5312%202.53125L8.53125%208.53125L8%209.0625Z%22%20fill%3D%22%230C0B0A%22%20fill-opacity%3D%220.65%22%2F%3E%3C%2Fsvg%3E');background-repeat:no-repeat;background-position:right 12px top 50%;background-size:14px 14px;border:2px solid ", ";border-radius:", ";cursor:pointer;", "{&:not(:disabled){&:hover{border-color:", ";}&:focus{border-color:", ";outline:", ";}}}"], colors.text.base, ({
  width
}) => width || "auto", ({
  isSmall
}) => isSmall ? "6px 30px 6px 12px" : "8px 30px 8px 16px", ({
  color
}) => color ?? colors.surface.neutral, ({
  color
}) => color ?? colors.line.neutralSoft, radius.small, media.pointer, ({
  color
}) => color ?? colors.line.neutralLight, ({
  color
}) => color ?? colors.line.neutralLight, borders.interactive.medium);
var Label3 = styled24.span.withConfig({
  displayName: "DropdownSelect__Label",
  componentId: "sc-u8shm9-2"
})(["font-size:", ";font-weight:", ";"], font.body.medium, font.weight.medium);
var OptionsContainer = styled24.div.withConfig({
  displayName: "DropdownSelect__OptionsContainer",
  componentId: "sc-u8shm9-3"
})(["position:absolute;top:100%;right:", ";left:", ";z-index:9999;padding:6px 0;background:", ";border-radius:", ";box-shadow:", ";"], ({
  alignRight
}) => alignRight ? 0 : "auto", ({
  alignRight
}) => !alignRight ? 0 : "auto", colors.surface.neutral, radius.medium, shadows.medium);
var Option = styled24.button.withConfig({
  displayName: "DropdownSelect__Option",
  componentId: "sc-u8shm9-4"
})(["color:", ";font-family:inherit;font-size:", ";font-weight:", ";text-align:left;white-space:nowrap;display:block;width:100%;padding:9px 30px 10px 18px;background:", ";border:0;cursor:", ";", "{&:hover{color:", ";}&:focus{color:", ";}}"], ({
  disabled
}) => disabled ? colors.text.neutral : colors.text.base, font.body.medium, font.weight.medium, colors.surface.neutral, ({
  disabled
}) => disabled ? "auto" : "pointer", media.pointer, ({
  disabled
}) => disabled ? colors.text.neutral : colors.text.interactive, ({
  disabled
}) => disabled ? colors.text.neutral : colors.text.interactive);
var OptionTitle = styled24.div.withConfig({
  displayName: "DropdownSelect__OptionTitle",
  componentId: "sc-u8shm9-5"
})([""]);
var DropdownSelect = ({
  alignRight,
  name,
  onChange,
  options,
  value,
  width,
  isSmall
}) => {
  const [isOpen, setIsOpen] = useState5(false);
  const handleClick = (e) => {
    e.preventDefault();
    setIsOpen(true);
  };
  const handleChange = ({
    name: newName,
    value: newValue
  }) => {
    if (!onChange)
      return;
    onChange({
      name: newName,
      value: newValue
    });
  };
  const handlePick = ({
    value: value2
  }) => {
    if (!onChange)
      return;
    setIsOpen(false);
    onChange({
      name,
      value: value2
    });
  };
  const ref = useRef3(null);
  useOnClickOutside2(ref, () => {
    setIsOpen(false);
  });
  const optionIndex = options.findIndex((o) => o.value === value);
  const hasValidValue = optionIndex > -1;
  const selectedOptionTitle = hasValidValue ? options[optionIndex].title : value;
  const getSelectedOptionColor = () => {
    if (!hasValidValue || !options[optionIndex].color)
      return void 0;
    return options[optionIndex].color;
  };
  return /* @__PURE__ */ _jsxs36(Container12, {
    width,
    "aria-expanded": isOpen,
    children: [!isMobile && /* @__PURE__ */ _jsxs36(_Fragment3, {
      children: [/* @__PURE__ */ _jsx870(LabelContainer3, {
        isSmall,
        color: getSelectedOptionColor(),
        onClick: handleClick,
        width,
        children: /* @__PURE__ */ _jsx870(Label3, {
          children: selectedOptionTitle
        })
      }), isOpen && /* @__PURE__ */ _jsx870(OptionsContainer, {
        ref,
        alignRight,
        children: options.map(({
          value: optionValue,
          title: optionTitle,
          disabled = false
        }) => /* @__PURE__ */ _jsx870(Option, {
          onClick: (e) => {
            e.preventDefault();
            handlePick({
              value: optionValue
            });
          },
          disabled,
          children: /* @__PURE__ */ _jsx870(OptionTitle, {
            children: optionTitle
          })
        }, optionValue))
      })]
    }), isMobile && /* @__PURE__ */ _jsx870(MemoizedSelect, {
      options,
      name,
      value,
      onChange: handleChange,
      width
    })]
  });
};

// src/atoms/Icon.tsx
import { useState as useState6 } from "react";
import { styled as styled25 } from "styled-components";
import { handleKeyDownEvent } from "@scouts/helpers";
import { jsx as _jsx871, jsxs as _jsxs37 } from "react/jsx-runtime";
var Icon = ({
  color = colors.text.base,
  hoverColor,
  onClick,
  Component,
  background,
  width,
  height,
  isLoading,
  ariaLabel
}) => {
  const [isHovered, setIsHovered] = useState6(false);
  const [isFocused, setIsFocused] = useState6(false);
  const isInteractive = !!onClick;
  const handleClick = () => {
    if (onClick && !isLoading)
      onClick();
  };
  const handleMouseEnter = () => {
    if (isInteractive)
      setIsHovered(true);
  };
  const handleMouseLeave = () => {
    if (isInteractive)
      setIsHovered(false);
  };
  const handleFocus = () => {
    if (isInteractive)
      setIsFocused(true);
  };
  const handleBlur = () => {
    if (isInteractive)
      setIsFocused(false);
  };
  const shouldShowInteractionStyles = isInteractive && (isHovered || isFocused) && !isLoading;
  return /* @__PURE__ */ _jsxs37(Container13, {
    "aria-label": isInteractive ? ariaLabel : void 0,
    as: isInteractive ? "button" : "div",
    background,
    height,
    isInteractive,
    onBlur: isInteractive ? handleBlur : void 0,
    onClick: isInteractive ? handleClick : void 0,
    onFocus: isInteractive ? handleFocus : void 0,
    onKeyDown: isInteractive ? handleKeyDownEvent(handleClick) : void 0,
    onMouseEnter: isInteractive ? handleMouseEnter : void 0,
    onMouseLeave: isInteractive ? handleMouseLeave : void 0,
    role: isInteractive ? "button" : void 0,
    tabIndex: isInteractive ? 0 : void 0,
    type: isInteractive ? "button" : void 0,
    width,
    children: [isLoading && /* @__PURE__ */ _jsx871(Spinner, {
      isSmall: true,
      color
    }), !isLoading && /* @__PURE__ */ _jsx871(Component, {
      "aria-hidden": isInteractive || !isInteractive && !ariaLabel ? true : void 0,
      "aria-label": !isInteractive && ariaLabel ? ariaLabel : void 0,
      color: shouldShowInteractionStyles && hoverColor ? hoverColor : color,
      role: !isInteractive && ariaLabel ? "img" : void 0
    })]
  });
};
var Container13 = styled25.div.withConfig({
  displayName: "Icon__Container",
  componentId: "sc-1swcmdz-0"
})(["display:flex;align-items:center;justify-content:center;width:", ";height:", ";cursor:", ";background:", ";&[type='button']{border:none;padding:0;font:inherit;color:inherit;text-align:inherit;&:focus-visible{outline:", ";outline-offset:6px;border-radius:", ";}}"], ({
  width
}) => width || "24px", ({
  height
}) => height || "24px", ({
  isInteractive
}) => isInteractive ? "pointer" : "inherit", ({
  background
}) => background ? background : "none", borders.interactive.medium, radius.small);

// src/atoms/Indicators.ts
import { styled as styled26 } from "styled-components";
var Indicators = styled26.div.withConfig({
  displayName: "Indicators",
  componentId: "sc-1xzja5k-0"
})(["display:flex;gap:6px;"]);
var Indicator = styled26.div.withConfig({
  displayName: "Indicators__Indicator",
  componentId: "sc-1xzja5k-1"
})(["color:", ";font-size:", ";font-weight:", ";display:flex;align-items:center;justify-content:center;width:36px;height:36px;background:", ";border:1px solid ", ";border-radius:", ";"], ({
  color
}) => color || colors.text.base, font.body.small, font.weight.medium, ({
  background
}) => background || "transparent", ({
  border,
  background
}) => border || background || "transparent", radius.full);

// src/atoms/Input.tsx
import { forwardRef as forwardRef2 } from "react";
import { styled as styled28 } from "styled-components";

// src/atoms/ErrorMessage.tsx
import { styled as styled27 } from "styled-components";
import { jsx as _jsx872 } from "react/jsx-runtime";
var ErrorMessage = ({
  children
}) => /* @__PURE__ */ _jsx872(Container14, {
  children
});
var Container14 = styled27.div.withConfig({
  displayName: "ErrorMessage__Container",
  componentId: "sc-1ne7ukz-0"
})(["font-size:", ";color:", ";padding-top:6px;line-height:1.3;"], font.body.medium, colors.text.alert);

// src/atoms/Input.tsx
import { jsx as _jsx873, jsxs as _jsxs38 } from "react/jsx-runtime";
var getBorderColor2 = ({
  hasError,
  disabled
}) => {
  switch (true) {
    case disabled:
      return theme.common.disabled1;
    case hasError:
      return theme.error.main;
    default:
      return theme.grey.graphics2;
  }
};
var Input = /* @__PURE__ */ forwardRef2(({
  disabled,
  readOnly,
  floatingLabel,
  formatter = (i) => i,
  isLarge,
  onChange,
  placeholder,
  shouldAlwaysDisplayFloatingLabel,
  suffix,
  value: propValue,
  errorMessage,
  hasError,
  width,
  ...rest
}, ref) => {
  const isFilled = propValue && String(propValue).length > 0;
  const handleBlurOnScroll = (e) => {
    if (e.currentTarget.type === "number") {
      e.currentTarget.blur();
    }
  };
  const handleChange = (e) => {
    if (!onChange)
      return;
    const {
      target
    } = e;
    const {
      name,
      value
    } = target;
    onChange({
      name,
      rawValue: value,
      value: formatter(value)
    }, e);
  };
  if (floatingLabel) {
    return /* @__PURE__ */ _jsxs38(Container15, {
      width,
      children: [/* @__PURE__ */ _jsx873(InputElement2, {
        hasSuffix: !!suffix,
        isLarge,
        onChange: handleChange,
        placeholder: shouldAlwaysDisplayFloatingLabel ? placeholder : floatingLabel || placeholder,
        ref,
        value: propValue,
        hasError,
        disabled,
        readOnly,
        "aria-disabled": disabled,
        "aria-readonly": readOnly,
        onWheel: handleBlurOnScroll,
        ...rest
      }), /* @__PURE__ */ _jsx873(FloatingLabel, {
        isVisible: isFilled || shouldAlwaysDisplayFloatingLabel,
        isLarge,
        hasError,
        children: floatingLabel
      }), suffix && /* @__PURE__ */ _jsx873(Suffix, {
        children: suffix
      }), hasError && errorMessage && /* @__PURE__ */ _jsx873(ErrorMessage, {
        children: errorMessage
      })]
    });
  }
  return /* @__PURE__ */ _jsxs38(Container15, {
    children: [/* @__PURE__ */ _jsx873(InputElement2, {
      hasSuffix: !!suffix,
      hasError,
      isLarge,
      onChange: handleChange,
      placeholder,
      ref,
      value: propValue,
      disabled,
      readOnly,
      "aria-disabled": disabled,
      "aria-readonly": readOnly,
      onWheel: handleBlurOnScroll,
      ...rest
    }), suffix && /* @__PURE__ */ _jsx873(Suffix, {
      children: suffix
    }), hasError && errorMessage && /* @__PURE__ */ _jsx873(ErrorMessage, {
      children: errorMessage
    })]
  });
});
var Container15 = styled28.div.withConfig({
  displayName: "Input__Container",
  componentId: "sc-5hih8x-0"
})(["position:relative;width:", ";"], ({
  width
}) => width || "auto");
var InputElement2 = styled28.input.withConfig({
  displayName: "Input__InputElement",
  componentId: "sc-5hih8x-1"
})(["appearance:none;border-radius:0;color:", ";font-family:inherit;font-size:", ";line-height:1.35;width:100%;padding:", ";background:", ";border:1px solid ", ";border-radius:", ";&::placeholder{color:", ";transition:color 0.1s;}&:focus-visible{color:", ";border:1px solid ", ";outline:", ";::placeholder{color:transparent;}}&:read-only{color:", ";border-color:", ";}&:disabled{color:", ";border:1px solid ", ";}&:focus:read-only{border-color:", ";outline:0;}", "{&:not(:disabled):not(:read-only){&:hover{border-color:", ";}}}", " &::-webkit-search-cancel-button{opacity:0.3;}"], theme.text.primary, font.body.large, ({
  isLarge
}) => isLarge ? "12px 15px 13px" : "7px 9px", theme.grey.background, getBorderColor2, radius.small, theme.text.secondary, theme.text.primary, getBorderColor2, borders.interactive.medium, theme.text.secondary, theme.common.disabled1, theme.text.disabled, getBorderColor2, theme.common.disabled1, media.tablet, theme.grey.graphics1, ({
  hasSuffix
}) => hasSuffix && `
        /* Chrome, Safari, Edge, Opera */
        &:not(:focus, :hover)::-webkit-outer-spin-button,
        &:not(:focus, :hover)::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        /* Firefox */
        &[type=number]:not(:focus, :hover) {
            -moz-appearance: textfield;
        }
    `);
var FloatingLabel = styled28.div.withConfig({
  displayName: "Input__FloatingLabel",
  componentId: "sc-5hih8x-2"
})(["font-size:", ";position:absolute;top:-8px;left:", ";display:block;padding:2px 4px;background:", ";border-radius:", ";opacity:", ";visibility:", ";transform:", ";transition:all 0.1s;color:", ";input:focus ~ &{opacity:1;visibility:visible;transform:translateY(0);}input:disabled ~ &{color:", ";}"], font.body.small, ({
  isLarge
}) => isLarge ? "12px" : "6px", theme.grey.background, radius.small, ({
  isVisible
}) => isVisible ? 1 : 0, ({
  isVisible
}) => isVisible ? "visible" : "hidden", ({
  isVisible
}) => isVisible ? "translateY(0)" : "translateY(6px)", ({
  hasError
}) => hasError ? theme.error.main : "unset", theme.text.disabled);
var Suffix = styled28.div.withConfig({
  displayName: "Input__Suffix",
  componentId: "sc-5hih8x-3"
})(["color:", ";line-height:1.35;display:flex;align-items:center;position:absolute;top:0;right:0;width:auto;height:100%;padding:0 9px;pointer-events:none;input[type='number']:not(:disabled):is(:focus,:hover) ~ &{display:none;}input:disabled ~ &{color:", ";}"], theme.text.primary, theme.text.disabled);

// src/atoms/InputAffix.tsx
import { styled as styled29 } from "styled-components";
import { jsx as _jsx874, jsxs as _jsxs39 } from "react/jsx-runtime";
var InputAffix = ({
  background,
  children,
  color,
  label,
  position = "start"
}) => /* @__PURE__ */ _jsxs39(Flexer, {
  width: "100%",
  gap: "3px",
  children: [position === "start" && /* @__PURE__ */ _jsx874(AffixLabel, {
    background,
    color,
    children: label
  }), /* @__PURE__ */ _jsx874(Content2, {
    children
  }), position === "end" && /* @__PURE__ */ _jsx874(AffixLabel, {
    background,
    color,
    children: label
  })]
});
var AffixLabel = styled29.div.withConfig({
  displayName: "InputAffix__AffixLabel",
  componentId: "sc-1tya94n-0"
})(["color:", ";font-family:inherit;font-size:", ";line-height:1.35;display:flex;flex:0 1 auto;align-items:center;justify-content:center;min-width:36px;padding:0 12px;background:", ";border-radius:", ";", "{font-size:", ";}"], ({
  color
}) => color || colors.text.base, font.body.medium, ({
  background
}) => background || colors.surface.neutral, radius.small, media.tablet, font.body.large);
var Content2 = styled29.div.withConfig({
  displayName: "InputAffix__Content",
  componentId: "sc-1tya94n-1"
})(["flex:1;"]);

// src/atoms/Label.ts
import { styled as styled30 } from "styled-components";
var Label4 = styled30.div.withConfig({
  displayName: "Label",
  componentId: "sc-1cnsp5t-0"
})(["color:", ";font-size:", ";font-weight:", ";display:inline-block;position:relative;padding:", ";background:", ";border-radius:", ";", ""], ({
  color
}) => color || "inherit", ({
  fontSize
}) => fontSize || font.body.medium, font.weight.normal, ({
  background
}) => background ? "4px 6px" : 0, ({
  background
}) => background || "inherit", radius.small, ({
  hasEllipsis
}) => hasEllipsis && `
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 100%;
    `);

// src/atoms/Logo.tsx
import { useId } from "react";
import { styled as styled31 } from "styled-components";
import { handleKeyDownEvent as handleKeyDownEvent2 } from "@scouts/helpers";
import { jsx as _jsx875, jsxs as _jsxs40 } from "react/jsx-runtime";
var logoLabel = "Taxfix logo";
var Logo = ({
  onClick
}) => {
  const handleClick = () => {
    if (onClick)
      onClick();
  };
  const isLink = onClick !== void 0;
  const svgTitleId = useId();
  return /* @__PURE__ */ _jsx875(LogoContainer, {
    isLink,
    onClick: handleClick,
    onKeyDown: isLink ? handleKeyDownEvent2(handleClick) : void 0,
    role: isLink ? "button" : void 0,
    tabIndex: isLink ? 0 : void 0,
    "aria-label": isLink ? logoLabel : void 0,
    children: /* @__PURE__ */ _jsx875(Svg, {
      isDecorative: isLink,
      titleText: logoLabel,
      titleId: svgTitleId
    })
  });
};
var LogoContainer = styled31.div.withConfig({
  displayName: "Logo__LogoContainer",
  componentId: "sc-1ity6z-0"
})(["width:100%;height:100%;position:relative;cursor:", ";svg{width:100%;height:100%;}"], ({
  isLink
}) => isLink ? "pointer" : "inherit");
var Svg = ({
  titleText,
  titleId,
  isDecorative,
  ...props
}) => /* @__PURE__ */ _jsxs40("svg", {
  width: 120,
  height: 30,
  viewBox: "0 0 120 30",
  fill: "none",
  "aria-labelledby": isDecorative ? void 0 : titleId,
  "aria-hidden": isDecorative ? "true" : void 0,
  role: isDecorative ? void 0 : "img",
  xmlns: "http://www.w3.org/2000/svg",
  ...props,
  children: [titleId && titleText && /* @__PURE__ */ _jsx875("title", {
    id: titleId,
    children: titleText
  }), /* @__PURE__ */ _jsx875("path", {
    d: "M84.6698 29.109H92.3846C92.5766 29.109 92.7047 28.9809 92.7047 28.7888V9.42201C92.7047 9.22995 92.5766 9.1019 92.3846 9.1019H84.6698C84.4778 9.1019 84.3497 9.22995 84.3497 9.42201V28.7888C84.3497 28.9809 84.4778 29.109 84.6698 29.109ZM69.2724 29.109H76.9871C77.1792 29.109 77.3072 28.9809 77.3072 28.7888V14.7039C77.3072 14.5118 77.4353 14.3838 77.6273 14.3838H82.0609C82.253 14.3838 82.381 14.2557 82.381 14.0637V9.42201C82.381 9.22995 82.253 9.1019 82.0609 9.1019H77.4993C77.3072 9.1019 77.1792 8.97386 77.1792 8.78179V7.98151C77.1792 6.50899 77.9475 5.77273 79.9322 5.77273C80.9245 5.77273 81.4527 5.80474 82.0129 5.93278C82.253 6.01281 82.365 5.90077 82.365 5.6927V1.32316C82.365 1.1631 82.285 1.00305 82.0769 0.971036C80.7645 0.570895 79.1159 0.330811 77.3232 0.330811C71.3211 0.330811 68.9203 2.8597 68.9203 7.61338V28.7888C68.9523 28.9809 69.0643 29.109 69.2724 29.109ZM62.2619 29.6691C64.9509 29.6691 67.1917 27.4284 67.1917 24.7394C67.1917 22.0505 64.9509 19.8097 62.2619 19.8097C59.573 19.8097 57.3802 22.0505 57.3802 24.7394C57.3802 27.4284 59.573 29.6691 62.2619 29.6691ZM42.655 29.109H51.106C51.2661 29.109 51.4261 29.0289 51.5061 28.9169L66.0393 9.50204C66.1993 9.26196 66.0713 9.1019 65.8472 9.1019H57.3962C57.2362 9.1019 57.0761 9.18193 56.9961 9.29397L42.463 28.7088C42.2869 28.9489 42.4149 29.109 42.655 29.109ZM46.2243 18.3852C48.9132 18.3852 51.106 16.1444 51.106 13.4554C51.106 10.7665 48.8972 8.5257 46.2243 8.5257C43.5513 8.5257 41.2946 10.7665 41.2946 13.4554C41.2946 16.1444 43.5353 18.3852 46.2243 18.3852ZM27.7698 23.9871C25.3689 23.9871 24.0085 22.0985 24.0085 19.1054C24.0085 16.0644 25.3689 14.2237 27.7698 14.2237C30.0426 14.2237 31.3711 16.1124 31.3711 19.1054C31.3711 22.0665 30.0586 23.9871 27.7698 23.9871ZM24.6167 29.6211C27.7378 29.6211 29.7385 28.2607 31.099 25.9718C31.227 25.8118 31.4191 25.8438 31.4191 26.0999V28.7888C31.4191 28.9809 31.5471 29.109 31.7392 29.109H39.2138C39.4059 29.109 39.5339 28.9809 39.5339 28.7888V9.42201C39.5339 9.22995 39.4059 9.1019 39.2138 9.1019H31.7232C31.5311 9.1019 31.4031 9.22995 31.4031 9.42201V12.0629C31.4031 12.303 31.163 12.351 31.0509 12.143C29.7385 9.90218 27.7218 8.58972 24.6167 8.58972C19.1267 8.58972 15.4134 13.0713 15.4134 19.1214C15.3974 25.1075 19.1267 29.6211 24.6167 29.6211ZM94.7534 29.109H103.364C103.524 29.109 103.653 29.0289 103.717 28.9169L106.95 24.6274C107.078 24.4353 107.302 24.4353 107.43 24.6274L110.631 28.9169C110.711 29.0449 110.871 29.109 111.031 29.109H119.722C120.01 29.109 120.074 28.9169 119.914 28.7088L112.312 19.3455C112.184 19.2175 112.184 19.0254 112.312 18.8653L119.914 9.50204C120.074 9.30997 119.994 9.1019 119.722 9.1019H111.111C110.951 9.1019 110.871 9.13391 110.759 9.29397L107.526 13.6155C107.398 13.7755 107.174 13.7755 107.046 13.6155L103.845 9.29397C103.717 9.13391 103.605 9.1019 103.444 9.1019H94.7534C94.4653 9.1019 94.4013 9.26196 94.5613 9.50204L102.116 18.8974C102.244 19.0254 102.244 19.2175 102.116 19.3775L94.5613 28.7088C94.4013 28.9009 94.4653 29.109 94.7534 29.109ZM8.40296 29.6691C10.3556 29.6691 11.7962 29.5091 13.6848 28.9009C13.8769 28.8689 13.9729 28.7088 13.9729 28.5488V23.619C13.9729 23.4269 13.8449 23.2989 13.6208 23.3789C13.0286 23.539 12.3884 23.667 11.6681 23.667C9.26727 23.667 8.38696 22.5466 8.38696 20.5779V14.7039C8.38696 14.5118 8.515 14.3838 8.70707 14.3838H13.5888C13.7809 14.3838 13.9089 14.2557 13.9089 14.0637V9.42201C13.9089 9.22995 13.7809 9.1019 13.5888 9.1019H8.67506C8.48299 9.1019 8.35494 8.97386 8.35494 8.78179V3.05177C8.35494 2.8597 8.2269 2.76367 8.03483 2.76367L0.320113 3.01976C0.128045 3.01976 0 3.1478 0 3.33987V22.5146C0 27.4604 3.23314 29.6691 8.40296 29.6691Z",
    fill: "#154618"
  })]
});

// src/atoms/Logomark.tsx
import { styled as styled32 } from "styled-components";
import { handleKeyDownEvent as handleKeyDownEvent3 } from "@scouts/helpers";
import { jsx as _jsx876, jsxs as _jsxs41 } from "react/jsx-runtime";
var Logomark = ({
  onClick
}) => {
  const handleClick = () => {
    if (onClick)
      onClick();
  };
  const isLink = onClick !== void 0;
  return /* @__PURE__ */ _jsx876(Container16, {
    isLink,
    onClick: handleClick,
    onKeyDown: isLink ? handleKeyDownEvent3(handleClick) : void 0,
    role: isLink ? "button" : void 0,
    tabIndex: isLink ? 0 : void 0,
    children: /* @__PURE__ */ _jsx876(Svg2, {})
  });
};
var Container16 = styled32.div.withConfig({
  displayName: "Logomark__Container",
  componentId: "sc-1nmzfx2-0"
})(["display:flex;align-items:center;position:relative;width:100%;height:100%;cursor:", ";svg{width:100%;height:100%;}"], ({
  isLink
}) => isLink ? "pointer" : "inherit");
var Svg2 = () => /* @__PURE__ */ _jsxs41("svg", {
  color: "#154618",
  viewBox: "0 0 30 24",
  "data-testid": "TaxfixLogomarkIcon",
  style: {
    aspectRatio: "1.25 / 1"
  },
  children: [/* @__PURE__ */ _jsx876("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M29.5913 18.4087C29.5913 21.4537 27.045 24 24 24C20.955 24 18.4558 21.4537 18.4558 18.4087C18.4558 15.3637 20.955 12.8174 24 12.8174C27.045 12.8174 29.5913 15.3637 29.5913 18.4087Z",
    fill: "currentColor"
  }), /* @__PURE__ */ _jsx876("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M11.227 23.3627H1.6375C1.3632 23.3627 1.22743 23.1799 1.4103 22.9083L17.91 0.864466C18.0014 0.728701 18.1815 0.637266 18.3644 0.637266H27.9538C28.2254 0.637266 28.3639 0.820135 28.181 1.09167L11.6814 23.1355C11.5899 23.2713 11.4098 23.3627 11.227 23.3627Z",
    fill: "currentColor"
  }), /* @__PURE__ */ _jsx876("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M11.1355 5.59132C11.1355 8.63634 8.63634 11.1826 5.59132 11.1826C2.54629 11.1826 0 8.63634 0 5.59132C0 2.54629 2.54629 0 5.59132 0C8.63634 0 11.1355 2.54629 11.1355 5.59132Z",
    fill: "currentColor"
  })]
});

// src/atoms/MultiEmailInput.tsx
import { useCallback } from "react";
import { ReactMultiEmail } from "react-multi-email";
import { styled as styled33 } from "styled-components";
import { handleKeyDownEvent as handleKeyDownEvent4 } from "@scouts/helpers";
import { jsx as _jsx877, jsxs as _jsxs42 } from "react/jsx-runtime";
var useTabKeyHandling = () => {
  const keyDownHandler = (event) => {
    if (event && event.key === "Tab") {
      const targetInput = event.target;
      if (targetInput.value) {
        event.preventDefault();
        targetInput.blur();
        setTimeout(() => targetInput.focus());
      }
    }
  };
  const handleKeyDown = useCallback(keyDownHandler, []);
  return {
    handleKeyDown
  };
};
var MultiEmailInput = ({
  emails,
  onChange,
  onChangeInput,
  placeholder,
  autoFocus,
  allowDuplicate,
  hasError,
  floatingLabel,
  style
}) => {
  const {
    handleKeyDown
  } = useTabKeyHandling();
  const hasAnyEmailItems = emails.length > 0;
  const handleChangeEmails = useCallback((newEmails) => {
    if (onChange)
      onChange(newEmails);
    const hasAddedNewEmail = newEmails.length > emails.length;
    if (hasAddedNewEmail && onChangeInput)
      onChangeInput("");
  }, [emails, onChange, onChangeInput]);
  return /* @__PURE__ */ _jsxs42(Styles, {
    hasAnyEmailItems,
    hasError,
    children: [floatingLabel && /* @__PURE__ */ _jsx877(FloatingLabel2, {
      children: floatingLabel
    }), /* @__PURE__ */ _jsx877(ReactMultiEmail, {
      allowDuplicate,
      autoFocus,
      placeholder,
      emails,
      allowDisplayName: true,
      stripDisplayName: true,
      onChange: handleChangeEmails,
      onChangeInput,
      onKeyDown: handleKeyDown,
      style,
      getLabel: (email, index, removeEmail) => {
        return /* @__PURE__ */ _jsx877(Chip, {
          color: colors.text.base,
          background: colors.surface.highlightSoft,
          children: /* @__PURE__ */ _jsxs42(Flexer, {
            gap: "6px",
            alignCenter: true,
            children: [/* @__PURE__ */ _jsx877(TextOverflow, {
              children: /* @__PURE__ */ _jsx877(Line, {
                as: "span",
                lineHeight: 1.3,
                children: email
              })
            }), /* @__PURE__ */ _jsx877(IconRemove, {
              onClick: () => removeEmail(index),
              onKeyDown: handleKeyDownEvent4(() => removeEmail(index)),
              role: "button",
              tabIndex: 0,
              children: "\u2715"
            })]
          })
        }, index);
      }
    })]
  });
};
var Styles = styled33.div.withConfig({
  displayName: "MultiEmailInput__Styles",
  componentId: "sc-upjhi1-0"
})(["position:relative;.react-multi-email{display:flex;flex-wrap:wrap;align-content:flex-start;align-items:center;gap:8px;padding:8px;border:1px solid ", ";border-radius:", ";outline:none;", " span[data-placeholder]{color:", ";display:none;position:absolute;left:12px;}input{font-size:", ";line-height:1;display:inline-block;flex:1 1;width:auto;padding:", ";border:none;outline:none;}&:hover{border:1px solid ", ";}&.focused{color:", ";border:1px solid ", ";outline:", ";}&.empty{span[data-placeholder]{display:inline;}}&.fill{span[data-placeholder]{display:none;}}}"], theme.grey.graphics2, radius.small, ({
  hasError
}) => hasError && `
            border-color: ${theme.error.main};
        `, theme.text.secondary, font.body.large, (props) => props.hasAnyEmailItems ? "9px 14px" : "9px 4px", theme.grey.graphics1, theme.text.primary, theme.grey.graphics1, borders.interactive.medium);
var FloatingLabel2 = styled33.div.withConfig({
  displayName: "MultiEmailInput__FloatingLabel",
  componentId: "sc-upjhi1-1"
})(["color:", ";font-size:", ";display:block;position:absolute;top:-8px;left:6px;padding:2px 4px;background:", ";border-radius:", ";"], ({
  hasError
}) => hasError ? colors.text.alert : "unset", font.body.small, theme.grey.background, radius.small);
var TextOverflow = styled33.span.withConfig({
  displayName: "MultiEmailInput__TextOverflow",
  componentId: "sc-upjhi1-2"
})(["text-overflow:ellipsis;white-space:nowrap;overflow:hidden;"]);
var IconRemove = styled33.span.withConfig({
  displayName: "MultiEmailInput__IconRemove",
  componentId: "sc-upjhi1-3"
})(["color:", ";position:relative;padding:3px;cursor:pointer;&:hover{color:", ";}&::before{content:'';position:absolute;top:-6px;right:-6px;bottom:-6px;left:-6px;border-radius:50%;}"], theme.text.primary, theme.error.main);

// src/atoms/Notice.tsx
import { styled as styled34 } from "styled-components";
import { jsx as _jsx878 } from "react/jsx-runtime";
var Notice = ({
  children,
  background = colors.surface.attention,
  textAlignLeft,
  isCompact,
  borderRadius = "0"
}) => /* @__PURE__ */ _jsx878(Container17, {
  background,
  isCompact,
  borderRadius,
  role: "status",
  children: /* @__PURE__ */ _jsx878(Body, {
    textAlignLeft,
    children
  })
});
var NoticeAction = styled34.div.withConfig({
  displayName: "Notice__NoticeAction",
  componentId: "sc-bimam-0"
})(["margin:6px 12px;display:inline-block;", "{margin:0 12px;}"], media.tablet);
var Container17 = styled34.div.withConfig({
  displayName: "Notice__Container",
  componentId: "sc-bimam-1"
})(["padding:", ";background:", ";border-radius:", ";", "{padding:", ";}&:not(:last-child){margin-bottom:2px;}"], ({
  isCompact
}) => isCompact ? "12px" : "12px 24px", ({
  background
}) => background, ({
  borderRadius
}) => borderRadius, media.tablet, ({
  isCompact
}) => isCompact ? "12px" : "18px 48px");
var Body = styled34.div.withConfig({
  displayName: "Notice__Body",
  componentId: "sc-bimam-2"
})(["font-size:", ";line-height:1.5;text-align:", ";p:not(:first-child){margin-top:6px;}a{color:", ";font-weight:", ";text-decoration:underline;", "{&:hover{color:", ";text-decoration:none;}}}"], font.body.medium, ({
  textAlignLeft
}) => textAlignLeft ? "left" : "center", colors.text.interactive, font.weight.medium, media.pointer, colors.text.base);

// src/atoms/OptionButton.tsx
import { styled as styled35 } from "styled-components";
import { jsx as _jsx879, jsxs as _jsxs43 } from "react/jsx-runtime";
var OptionButton = ({
  children,
  description,
  isActive,
  isDisabled,
  onClick
}) => {
  const handleClick = () => onClick();
  return /* @__PURE__ */ _jsx879(Container18, {
    isActive,
    onClick: isDisabled ? void 0 : handleClick,
    isDisabled,
    children: /* @__PURE__ */ _jsxs43(Flexer, {
      column: true,
      gap: "3px",
      tabletGap: "6px",
      children: [/* @__PURE__ */ _jsx879(Line, {
        color: colors.text.interactive,
        size: font.title.medium,
        weight: font.weight.medium,
        lineHeight: 1.3,
        children
      }), !!description && /* @__PURE__ */ _jsx879(Line, {
        size: font.body.small,
        tabletSize: font.body.medium,
        children: description
      }), isActive && /* @__PURE__ */ _jsx879(BadgeContainer, {
        children: /* @__PURE__ */ _jsx879(Badge, {
          background: theme.primary.main,
          color: colors.text.inverted,
          children: "\u2713"
        })
      })]
    })
  });
};
var Container18 = styled35.button.withConfig({
  displayName: "OptionButton__Container",
  componentId: "sc-1j6iuzz-0"
})(["color:", ";position:relative;padding:12px 60px 12px 18px;width:100%;text-align:left;background-color:", ";border:2px solid ", ";border-radius:", ";opacity:", ";", "{&:hover{box-shadow:", ";}}", "{padding:18px 72px 18px 36px;&:after{right:36px;}}"], ({
  isActive
}) => isActive ? colors.text.base : colors.text.neutral, ({
  isActive
}) => isActive ? theme.common.active : colors.surface.base, ({
  isActive
}) => isActive ? "transparent" : colors.line.neutralSoft, radius.large, ({
  isDisabled
}) => isDisabled ? 0.6 : 1, media.pointer, ({
  isActive,
  isDisabled
}) => isActive || isDisabled ? "none" : shadows.medium, media.tablet);
var BadgeContainer = styled35.div.withConfig({
  displayName: "OptionButton__BadgeContainer",
  componentId: "sc-1j6iuzz-1"
})(["position:absolute;top:50%;right:18px;transform:translateY(-50%);", "{right:36px;}"], media.tablet);

// src/atoms/ProgressLine.tsx
import { styled as styled36 } from "styled-components";
import { jsx as _jsx880 } from "react/jsx-runtime";
var ProgressLine = ({
  background,
  width
}) => {
  return /* @__PURE__ */ _jsx880(Container19, {
    children: /* @__PURE__ */ _jsx880(Line2, {
      width,
      background
    })
  });
};
var Container19 = styled36.div.withConfig({
  displayName: "ProgressLine__Container",
  componentId: "sc-1ffgdqj-0"
})(["position:relative;width:100%;height:2px;"]);
var Line2 = styled36.div.withConfig({
  displayName: "ProgressLine__Line",
  componentId: "sc-1ffgdqj-1"
})(["position:absolute;top:0;left:0;width:", ";height:100%;background-color:", ";transition:width 0.2s;"], ({
  width = 0
}) => `${width}%`, ({
  background
}) => background || colors.surface.highlight);

// src/atoms/Radio.tsx
import { forwardRef as forwardRef3 } from "react";
import { styled as styled37 } from "styled-components";
import { jsx as _jsx881, jsxs as _jsxs44 } from "react/jsx-runtime";
var getLabelFontSize2 = ({
  isSmall
}) => {
  switch (true) {
    case isSmall:
      return font.body.medium;
    default:
      return font.body.large;
  }
};
var getLabelPaddingLeft2 = ({
  isSmall
}) => {
  switch (true) {
    case isSmall:
      return "24px";
    default:
      return "30px";
  }
};
var getLabelBeforeMarginLeft2 = ({
  isSmall
}) => {
  switch (true) {
    case isSmall:
      return "-24px";
    default:
      return "-30px";
  }
};
var Radio = /* @__PURE__ */ forwardRef3(({
  checked,
  id: propsId,
  isSmall,
  label,
  name,
  onChange,
  readOnly,
  disabled,
  value,
  ...rest
}, ref) => {
  const id = propsId || `${name}${value}`;
  const handleChange = (e) => {
    if (!onChange)
      return;
    const {
      target
    } = e;
    onChange({
      name: target.name,
      value: target.value
    });
  };
  return /* @__PURE__ */ _jsxs44(Container20, {
    children: [/* @__PURE__ */ _jsx881(Input2, {
      ref,
      type: "radio",
      id,
      name,
      value,
      onChange: handleChange,
      checked,
      readOnly,
      disabled: disabled || readOnly,
      "aria-disabled": disabled,
      ...rest
    }), /* @__PURE__ */ _jsx881(Label5, {
      htmlFor: id,
      isSmall,
      children: label
    })]
  });
});
var Container20 = styled37.div.withConfig({
  displayName: "Radio__Container",
  componentId: "sc-17usbo2-0"
})(["position:relative;"]);
var Input2 = styled37.input.withConfig({
  displayName: "Radio__Input",
  componentId: "sc-17usbo2-1"
})(["position:absolute;top:auto;overflow:hidden;clip:rect(1px,1px,1px,1px);width:1px;height:1px;white-space:nowrap;&:hover + label:before{", "{border-color:", ";}}&:focus + label{outline:", ";outline-offset:6px;border-radius:", ";}&:checked + label:before{background-color:", ";border-color:", ";box-shadow:inset 0 0 0 4px ", ";}&:disabled + label:before{border-color:", ";background-color:", ";pointer-events:none;}&:disabled + label{color:", ";pointer-events:none;}&:checked:disabled + label:before{background-color:", ";box-shadow:inset 0 0 0 4px ", ";}"], media.pointer, theme.grey.graphics1, borders.interactive.medium, radius.small, theme.primary.light, theme.primary.light, theme.grey.background, theme.common.disabled1, theme.grey.background, ({
  readOnly
}) => readOnly ? theme.text.secondary : theme.text.disabled, theme.common.disabled1, theme.grey.background);
var Label5 = styled37.label.withConfig({
  displayName: "Radio__Label",
  componentId: "sc-17usbo2-2"
})(["color:", ";font-size:", ";font-weight:", ";line-height:1.5;display:block;padding-left:", ";cursor:pointer;&:before{content:'';text-align:center;display:block;float:left;width:", ";height:", ";margin-left:", ";margin-top:", ";background:", ";border:", " solid ", ";border-radius:100%;cursor:pointer;transition:all 0.1s ease-out;}"], theme.text.primary, getLabelFontSize2, font.weight.normal, getLabelPaddingLeft2, ({
  isSmall
}) => isSmall ? "16px" : "22px", ({
  isSmall
}) => isSmall ? "16px" : "22px", getLabelBeforeMarginLeft2, ({
  isSmall
}) => isSmall ? "2px" : "1px", theme.grey.background, ({
  isSmall
}) => isSmall ? "1px" : "2px", theme.grey.graphics2);

// src/atoms/Select.tsx
import { forwardRef as forwardRef4, memo } from "react";
import { styled as styled38 } from "styled-components";
import { jsx as _jsx882, jsxs as _jsxs45 } from "react/jsx-runtime";
var Select = /* @__PURE__ */ forwardRef4(({
  name,
  onChange,
  disabled,
  readOnly,
  value,
  options,
  width,
  ...rest
}, ref) => {
  const handleChange = (e) => {
    const {
      target
    } = e;
    const {
      value: newValue
    } = target;
    if (!onChange)
      return;
    onChange({
      name,
      value: newValue
    });
  };
  const currentValueIndex = options.findIndex((o) => String(o.value) === String(value) || !o.value && !value);
  const hasCurrentValueInOptions = currentValueIndex > -1;
  return /* @__PURE__ */ _jsxs45(Container21, {
    ref,
    name,
    value,
    width,
    onChange: handleChange,
    readOnly,
    disabled: disabled || readOnly,
    "aria-disabled": disabled,
    "aria-readonly": readOnly,
    ...rest,
    children: [!hasCurrentValueInOptions && /* @__PURE__ */ _jsx882("option", {
      children: " "
    }), options.map((option) => {
      const {
        value: optionValue,
        title: optionTitle,
        disabled: optionDisabled,
        key: optionKey
      } = option;
      return /* @__PURE__ */ _jsx882("option", {
        value: optionValue || "",
        disabled: !!optionDisabled,
        ...!!option["data-testid"] && {
          "data-testid": option["data-testid"]
        },
        children: optionTitle || optionValue
      }, optionKey || `${optionTitle}-${optionValue}`);
    })]
  });
});
var Container21 = styled38.select.withConfig({
  displayName: "Select__Container",
  componentId: "sc-1ouoww1-0"
})(["color:", ";font-size:", ";line-height:1.35;width:", ";padding:", ";background-color:", ";background-image:url('data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2210%22%20viewBox%3D%220%200%2016%2010%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M8%209.0625L7.46875%208.53125L1.46875%202.53125L0.9375%202L2%200.9375L2.53125%201.46875L8%206.96875L13.4688%201.5L14%200.96875L15.0625%202L14.5312%202.53125L8.53125%208.53125L8%209.0625Z%22%20fill%3D%22%230C0B0A%22%20fill-opacity%3D%220.65%22%2F%3E%3C%2Fsvg%3E');background-position:", ";background-repeat:no-repeat;background-size:14px 14px;border:", ";border-radius:", ";appearance:none;&:disabled{color:", ";background-image:url('data:image/svg+xml,%3Csvg%20width%3D%2216%22%20height%3D%2210%22%20viewBox%3D%220%200%2016%2010%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M8%209.0625L7.46875%208.53125L1.46875%202.53125L0.9375%202L2%200.9375L2.53125%201.46875L8%206.96875L13.4688%201.5L14%200.96875L15.0625%202L14.5312%202.53125L8.53125%208.53125L8%209.0625Z%22%20fill%3D%22%230C0B0A%22%20fill-opacity%3D%220.5%22%2F%3E%3C%2Fsvg%3E');border-color:", ";}&::-ms-expand{display:none;}&:focus-visible{color:", ";border-color:", ";outline:", ";}", "{&:not(:disabled){&:hover{border-color:", ";}}}"], theme.text.primary, font.body.large, ({
  width
}) => width || "auto", ({
  isLarge
}) => isLarge ? "12px 36px 12px 15px" : "7px 24px 7px 9px", theme.grey.background, ({
  isLarge
}) => isLarge ? "right 12px top 50%" : "right 7px top 50%", ({
  isBorderless
}) => isBorderless ? "none" : `1px solid ${theme.grey.graphics2}`, radius.small, ({
  readOnly
}) => readOnly ? theme.text.secondary : theme.text.disabled, theme.common.disabled1, theme.text.primary, theme.grey.graphics2, borders.interactive.medium, media.pointer, theme.grey.graphics1);
var MemoizedSelect = /* @__PURE__ */ memo(Select);

// src/atoms/SelectWithLabel.tsx
import { forwardRef as forwardRef5 } from "react";
import { styled as styled39 } from "styled-components";
import { jsx as _jsx883, jsxs as _jsxs46 } from "react/jsx-runtime";
var SelectWithLabel = /* @__PURE__ */ forwardRef5((props, ref) => {
  const {
    id,
    name,
    label,
    width = "auto",
    disabled,
    readOnly,
    isLarge
  } = props;
  return /* @__PURE__ */ _jsxs46(Fieldset, {
    ref,
    width,
    disabled: disabled || readOnly,
    readOnly,
    children: [/* @__PURE__ */ _jsx883(Legend, {
      isDisabled: disabled,
      isLarge,
      children: /* @__PURE__ */ _jsx883("label", {
        htmlFor: id || name,
        children: label
      })
    }), /* @__PURE__ */ _jsx883(MemoizedSelect, {
      ...props,
      isBorderless: true,
      width: "100%"
    })]
  });
});
var Fieldset = styled39.fieldset.withConfig({
  displayName: "SelectWithLabel__Fieldset",
  componentId: "sc-i3eepj-0"
})(["position:relative;width:", ";margin:0;padding:0;background-color:", ";border:1px solid ", ";border-radius:", ";&:disabled{color:", ";border-color:", ";}", "{&:not(:disabled){&:hover{border-color:", ";}}}"], ({
  width
}) => width, theme.grey.background, ({
  isFocused
}) => isFocused ? theme.grey.graphics1 : theme.grey.graphics2, radius.small, theme.text.disabled, theme.common.disabled1, media.pointer, theme.grey.graphics1);
var Legend = styled39.legend.withConfig({
  displayName: "SelectWithLabel__Legend",
  componentId: "sc-i3eepj-1"
})(["color:", ";font-size:", ";position:absolute;top:-8px;left:", ";z-index:1;margin:0;padding:2px 4px;background-color:", ";border-radius:", ";", ""], theme.text.primary, font.body.small, ({
  isLarge
}) => isLarge ? "12px" : "6px", theme.grey.background, radius.small, ({
  isDisabled
}) => isDisabled && `color: ${theme.text.disabled};`);

// src/atoms/Skeleton.tsx
import { keyframes as keyframes3, styled as styled40 } from "styled-components";
import { hexToRgbA as hexToRgbA5 } from "@scouts/helpers";
import { jsx as _jsx884, jsxs as _jsxs47, Fragment as _Fragment4 } from "react/jsx-runtime";
var Skeleton = ({
  children,
  gap,
  padding,
  background,
  ...rest
}) => /* @__PURE__ */ _jsxs47(Container22, {
  gap,
  padding,
  radius: rest.radius,
  background,
  children: [/* @__PURE__ */ _jsx884(ShineLine, {
    background
  }), children]
});
var keyframesPulse = keyframes3`
    0% {
		opacity: 1;
	}
    50% {
		opacity: 0.3;
	}
    100% {
		opacity: 1;
	}
`;
var SkeletonLine = ({
  repeat = 1,
  ...props
}) => /* @__PURE__ */ _jsx884(_Fragment4, {
  children: [...Array(Math.abs(repeat)).keys()].map((_, i) => /* @__PURE__ */ _jsx884(SkeletonLineItem, {
    ...props
  }, i))
});
var SkeletonLineItem = styled40.div.withConfig({
  displayName: "Skeleton__SkeletonLineItem",
  componentId: "sc-b2k5s0-0"
})(["width:", ";height:", ";background-color:", ";border-radius:", ";opacity:1;animation:", " 2s ease-in-out infinite;"], ({
  width
}) => width ?? "100%", ({
  height
}) => height ?? "18px", ({
  backgroundColor
}) => backgroundColor ?? colors.line.neutralLight, (props) => props.radius ?? radius.medium, keyframesPulse);
var Container22 = styled40.div.withConfig({
  displayName: "Skeleton__Container",
  componentId: "sc-b2k5s0-1"
})(["overflow:hidden;position:relative;top:0;left:0;width:100%;padding:", ";background:", ";border-radius:", ";& > div:not(:last-child){margin-bottom:", ";}"], ({
  padding
}) => padding ?? "12px", ({
  background
}) => background ?? colors.surface.neutral, (props) => props.radius ?? radius.medium, ({
  gap
}) => gap ?? "12px");
var keyframesShine = keyframes3`
    to {
		background-position: right -80px top 0;
	}
`;
var ShineLine = styled40.div.withConfig({
  displayName: "Skeleton__ShineLine",
  componentId: "sc-b2k5s0-2"
})(["position:absolute;top:0;left:0;z-index:1090;width:100%;height:100%;background-image:linear-gradient( 90deg,", " 0%,", " 50%,", " 100% );background-position:left -80px top 0;background-repeat:no-repeat;background-size:120px 100%;animation:", " 2s ease-in-out infinite;"], ({
  background
}) => hexToRgbA5(background || colors.surface.neutral, 0), ({
  background
}) => hexToRgbA5(background || colors.surface.neutral, 0.5), ({
  background
}) => hexToRgbA5(background || colors.surface.neutral, 0), keyframesShine);

// src/atoms/TextArea.tsx
import { forwardRef as forwardRef6 } from "react";
import { styled as styled41 } from "styled-components";
import { jsx as _jsx885, jsxs as _jsxs48 } from "react/jsx-runtime";
var getBorderColor3 = ({
  hasError,
  disabled
}) => {
  switch (true) {
    case disabled:
      return theme.common.disabled1;
    case hasError:
      return theme.error.main;
    default:
      return theme.grey.graphics2;
  }
};
var TextArea = /* @__PURE__ */ forwardRef6(({
  floatingLabel,
  disabled,
  readOnly,
  onChange,
  placeholder,
  shouldAlwaysDisplayFloatingLabel,
  value: propValue,
  ...rest
}, ref) => {
  const isFilled = propValue && String(propValue).length > 0;
  const handleChange = (e) => {
    const {
      target
    } = e;
    const {
      name,
      value
    } = target;
    if (!onChange)
      return;
    onChange({
      name,
      value
    });
  };
  if (floatingLabel) {
    return /* @__PURE__ */ _jsxs48(Container23, {
      children: [/* @__PURE__ */ _jsx885(TextAreaElement, {
        onChange: handleChange,
        placeholder: shouldAlwaysDisplayFloatingLabel ? placeholder : floatingLabel || placeholder,
        ref,
        value: propValue,
        disabled,
        readOnly,
        "aria-disabled": disabled,
        "aria-readonly": readOnly,
        ...rest
      }), /* @__PURE__ */ _jsx885(FloatingLabel3, {
        isVisible: !!isFilled || !!shouldAlwaysDisplayFloatingLabel,
        children: floatingLabel
      })]
    });
  }
  return /* @__PURE__ */ _jsx885(TextAreaElement, {
    ref,
    onChange: handleChange,
    value: propValue,
    placeholder,
    disabled,
    readOnly,
    "aria-disabled": disabled,
    "aria-readonly": readOnly,
    ...rest
  });
});
var Container23 = styled41.div.withConfig({
  displayName: "TextArea__Container",
  componentId: "sc-jx12a2-0"
})(["position:relative;"]);
var TextAreaElement = styled41.textarea.withConfig({
  displayName: "TextArea__TextAreaElement",
  componentId: "sc-jx12a2-1"
})(["appearance:none;border-radius:0;resize:", ";color:", ";font-family:inherit;font-size:", ";line-height:1.35;display:block;width:100%;height:", ";padding:7px 9px;background:", ";border:1px solid ", ";border-radius:", ";&::placeholder{color:", ";transition:color 0.1s;}&:focus-visible{color:", ";border:1px solid ", ";outline:", ";::placeholder{color:transparent;}}&:read-only{color:", ";border-color:", ";}&:disabled{color:", ";border:1px solid ", ";}&:focus:read-only{border-color:", ";outline:0;}", "{&:not(:disabled):not(:read-only){&:hover{border-color:", ";}}}"], ({
  resize
}) => resize || "both", theme.text.primary, font.body.large, ({
  height
}) => height || "auto", theme.grey.background, getBorderColor3, radius.small, theme.text.secondary, theme.text.primary, getBorderColor3, borders.interactive.medium, theme.text.secondary, theme.common.disabled1, theme.text.disabled, getBorderColor3, theme.common.disabled1, media.pointer, theme.grey.graphics1);
var FloatingLabel3 = styled41.div.withConfig({
  displayName: "TextArea__FloatingLabel",
  componentId: "sc-jx12a2-2"
})(["font-size:", ";position:absolute;top:-8px;left:6px;display:block;padding:2px 4px;background:", ";border-radius:", ";opacity:", ";visibility:", ";transform:", ";transition:all 0.1s;input:active + &,input:focus + &{opacity:1;visibility:visible;transform:translateY(0);}"], font.body.small, theme.grey.background, radius.small, ({
  isVisible
}) => isVisible ? 1 : 0, ({
  isVisible
}) => isVisible ? "visible" : "hidden", ({
  isVisible
}) => isVisible ? "translateY(0)" : "translateY(6px)");

// src/atoms/Toggle.tsx
import { keyframes as keyframes4, styled as styled42 } from "styled-components";
import { handleKeyDownEvent as handleKeyDownEvent5 } from "@scouts/helpers";
import { jsx as _jsx886, jsxs as _jsxs49, Fragment as _Fragment5 } from "react/jsx-runtime";
var Toggle = ({
  background,
  onChange,
  name,
  stateBackground,
  size: size2 = "medium",
  optionLabel = {
    false: "No",
    true: "Yes"
  },
  value,
  isLoading
}) => {
  if (value === null) {
    return /* @__PURE__ */ _jsxs49(PickerContainer, {
      size: size2,
      children: [/* @__PURE__ */ _jsx886(PickerOptionFalse, {
        onClick: () => {
          if (!isLoading)
            onChange({
              name,
              value: false
            });
        },
        children: optionLabel.false
      }), /* @__PURE__ */ _jsx886(PickerDivider, {}), /* @__PURE__ */ _jsx886(PickerOptionTrue, {
        onClick: () => {
          if (!isLoading)
            onChange({
              name,
              value: true
            });
        },
        children: optionLabel.true
      })]
    });
  }
  if (value === true) {
    return /* @__PURE__ */ _jsx886(StateTrueContainer, {
      size: size2,
      background,
      onClick: () => {
        if (!isLoading)
          onChange({
            name,
            value: false
          });
      },
      onKeyDown: () => {
        if (!isLoading)
          handleKeyDownEvent5(() => onChange({
            name,
            value: true
          }));
      },
      children: /* @__PURE__ */ _jsx886(StateTrue, {
        size: size2,
        stateBackground,
        children: isLoading ? /* @__PURE__ */ _jsx886(StateSpinner, {
          background: colors.surface.affirmSoft,
          color: colors.surface.affirm
        }) : /* @__PURE__ */ _jsx886(_Fragment5, {
          children: "\u2713"
        })
      })
    });
  }
  return /* @__PURE__ */ _jsx886(StateFalseContainer, {
    size: size2,
    "aria-label": "Toggle off",
    background,
    onClick: () => {
      if (!isLoading)
        onChange({
          name,
          value: true
        });
    },
    onKeyDown: () => {
      if (!isLoading)
        handleKeyDownEvent5(() => onChange({
          name,
          value: true
        }));
    },
    tabIndex: 0,
    children: /* @__PURE__ */ _jsx886(StateFalse, {
      size: size2,
      stateBackground,
      children: isLoading ? /* @__PURE__ */ _jsx886(StateSpinner, {
        background: colors.surface.noteStrong,
        color: colors.surface.alert
      }) : /* @__PURE__ */ _jsx886("span", {
        style: {
          paddingBottom: "1px"
        },
        children: "\xD7"
      })
    })
  });
};
var PickerContainer = styled42.div.withConfig({
  displayName: "Toggle__PickerContainer",
  componentId: "sc-11dcjs4-0"
})(["display:flex;align-items:center;justify-content:space-between;width:", ";height:", ";background-color:", ";border-radius:", ";"], ({
  size: size2
}) => size2 === "small" ? "42px" : "72px", ({
  size: size2
}) => size2 === "small" ? "20px" : "30px", colors.surface.neutral, radius.full);
var PickerOptionBase = styled42.button.withConfig({
  displayName: "Toggle__PickerOptionBase",
  componentId: "sc-11dcjs4-1"
})(["color:", ";font-size:", ";font-weight:", ";text-align:center;flex:1 1 auto;height:100%;", "{&:hover{color:", ";}}"], colors.text.neutral, font.body.small, font.weight.medium, media.pointer, colors.text.interactive);
var PickerOptionFalse = styled42(PickerOptionBase).withConfig({
  displayName: "Toggle__PickerOptionFalse",
  componentId: "sc-11dcjs4-2"
})(["padding-left:4px;border-radius:", " 0 0 ", ";", "{&:hover{background:linear-gradient(90deg,", " 0%,", " 100%);}}"], radius.full, radius.full, media.pointer, colors.surface.highlight, colors.surface.neutral);
var PickerOptionTrue = styled42(PickerOptionBase).withConfig({
  displayName: "Toggle__PickerOptionTrue",
  componentId: "sc-11dcjs4-3"
})(["padding-right:4px;border-radius:0 ", " ", " 0;", "{&:hover{background:linear-gradient(90deg,", " 0%,", " 100%);}}"], radius.full, radius.full, media.pointer, colors.surface.neutral, colors.surface.highlight);
var PickerDivider = styled42.div.withConfig({
  displayName: "Toggle__PickerDivider",
  componentId: "sc-11dcjs4-4"
})(["width:2px;height:2px;background:", ";border-radius:", ";"], colors.surface.base, radius.full);
var StateContainerBase = styled42.button.withConfig({
  displayName: "Toggle__StateContainerBase",
  componentId: "sc-11dcjs4-5"
})(["display:flex;align-items:center;padding:4px;width:", ";height:", ";border-radius:", ";"], ({
  size: size2
}) => size2 === "small" ? "42px" : "72px", ({
  size: size2
}) => size2 === "small" ? "20px" : "30px", radius.full);
var StateTrueContainer = styled42(StateContainerBase).withConfig({
  displayName: "Toggle__StateTrueContainer",
  componentId: "sc-11dcjs4-6"
})(["background-color:", ";justify-content:flex-end;"], ({
  background
}) => background ?? theme.primary.primaryAction);
var StateFalseContainer = styled42(StateContainerBase).withConfig({
  displayName: "Toggle__StateFalseContainer",
  componentId: "sc-11dcjs4-7"
})(["background-color:", ";justify-content:flex-start;"], ({
  background
}) => background ?? theme.error.primaryAction);
var StateBase = styled42.div.withConfig({
  displayName: "Toggle__StateBase",
  componentId: "sc-11dcjs4-8"
})(["color:", ";line-height:1;font-size:", ";font-weight:", ";display:flex;align-items:center;justify-content:center;width:", ";height:", ";border-radius:", ";"], colors.text.inverted, ({
  size: size2
}) => size2 === "small" ? font.body.small : font.body.medium, font.weight.medium, ({
  size: size2
}) => size2 === "small" ? "14px" : "22px", ({
  size: size2
}) => size2 === "small" ? "14px" : "22px", radius.full);
var StateTrue = styled42(StateBase).withConfig({
  displayName: "Toggle__StateTrue",
  componentId: "sc-11dcjs4-9"
})(["color:", ";background-color:", ";"], theme.primary.contrast, ({
  stateBackground
}) => stateBackground ?? colors.surface.affirm);
var StateFalse = styled42(StateBase).withConfig({
  displayName: "Toggle__StateFalse",
  componentId: "sc-11dcjs4-10"
})(["background-color:", ";"], ({
  stateBackground
}) => stateBackground ?? colors.surface.alert);
var keyframesSpin2 = keyframes4`
    to {
        transform: rotate(360deg);
    }
`;
var StateSpinner = styled42.div.withConfig({
  displayName: "Toggle__StateSpinner",
  componentId: "sc-11dcjs4-11"
})(["content:'';box-sizing:border-box;width:12px;height:12px;border:2px solid ", ";border-top-color:", ";border-radius:", ";animation:", " 0.6s linear infinite;"], ({
  background
}) => background, ({
  color
}) => color, radius.full, keyframesSpin2);

// src/atoms/TypeSelect.tsx
import { useRef as useRef4, useState as useState7 } from "react";
import { styled as styled43 } from "styled-components";
import useOnClickOutside3 from "use-onclickoutside";
import { handleKeyDownEvent as handleKeyDownEvent6, Locale } from "@scouts/helpers";
import { jsx as _jsx887, jsxs as _jsxs50 } from "react/jsx-runtime";
var translations = {
  "en-GB": {
    textNoResultsFound: "No results found"
  },
  "es-ES": {
    textNoResultsFound: "No se han encontrado resultados"
  }
};
var TypeSelect = ({
  onChange,
  value,
  options,
  name,
  showOptions,
  isRequired,
  autoClear,
  locale = Locale.GB,
  ...props
}) => {
  const refComponent = useRef4(null);
  const refInput = useRef4(null);
  const refListItem = useRef4(null);
  const optionIndex = options.findIndex((o) => o.value === value);
  const hasValidValue = optionIndex > -1;
  const label = hasValidValue ? options[optionIndex].title : value;
  const [isOpen, setIsOpen] = useState7(false);
  const [searchValue, setSearchValue] = useState7(null);
  const [selectedIndex, setSelectedIndex] = useState7(0);
  const handleChange = (e) => {
    const {
      target
    } = e;
    const {
      value: newValue
    } = target;
    setSearchValue(String(newValue));
    setSelectedIndex(0);
    setIsOpen(true);
  };
  const handlePick = (pickedValue) => {
    setSearchValue(null);
    onChange({
      name,
      value: pickedValue || ""
    });
    setIsOpen(false);
  };
  const handleFocus = () => {
    setIsOpen(true);
    if (autoClear) {
      setSearchValue("");
    }
  };
  const filteredOptions = options.filter((o) => {
    const {
      title
    } = o;
    return title.toLowerCase().includes(searchValue?.toLowerCase() ?? "");
  });
  const hasOptions = filteredOptions.length > 0 || showOptions;
  const hasSearched = (searchValue?.length ?? 0) > 2 || showOptions;
  useOnClickOutside3(refComponent, () => {
    if (isOpen) {
      setIsOpen(false);
      setSearchValue(null);
    }
  });
  const scrollContainer = () => {
    if (refListItem && refListItem.current) {
      refListItem.current.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      const index = Math.min(filteredOptions.length - 1, selectedIndex + 1);
      setSelectedIndex(index);
      scrollContainer();
    } else if (e.key === "ArrowUp") {
      const index = Math.max(0, selectedIndex - 1);
      setSelectedIndex(index);
      scrollContainer();
    } else if (e.key === "Enter") {
      const selectedItem = filteredOptions[selectedIndex];
      if (searchValue === "" && !isRequired && selectedItem === void 0) {
        handlePick("");
      } else if (selectedItem === void 0 && isRequired) {
        handlePick(value);
      } else if (hasOptions && selectedItem !== void 0 && hasSearched || isRequired && selectedItem !== void 0) {
        handlePick(filteredOptions[selectedIndex].value);
      }
    } else if (e.key === "Esc" || e.key === "Escape") {
      setSearchValue(null);
    }
  };
  const {
    textNoResultsFound
  } = translations[locale];
  return /* @__PURE__ */ _jsxs50(Container24, {
    ref: refComponent,
    children: [/* @__PURE__ */ _jsx887(InputElement3, {
      name,
      autoComplete: "off",
      onChange: handleChange,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      ref: refInput,
      value: searchValue ?? label,
      ...props
    }), isOpen && hasSearched && hasOptions && /* @__PURE__ */ _jsxs50(Results, {
      children: [filteredOptions.length === 0 && /* @__PURE__ */ _jsx887(ResultsItem, {
        isEmpty: true,
        children: textNoResultsFound
      }), filteredOptions.map((option, i) => {
        const isSelected = i === selectedIndex;
        const {
          value: optionValue,
          title
        } = option;
        return /* @__PURE__ */ _jsx887(ResultsItem, {
          isSelected,
          onClick: (e) => {
            e.preventDefault();
            handlePick(optionValue);
          },
          onKeyDown: handleKeyDownEvent6(() => handlePick(optionValue)),
          role: "button",
          tabIndex: 0,
          ref: isSelected ? refListItem : null,
          children: title || optionValue
        }, optionValue);
      })]
    })]
  });
};
var InputElement3 = styled43.input.withConfig({
  displayName: "TypeSelect__InputElement",
  componentId: "sc-f6sskn-0"
})(["appearance:none;border-radius:0;color:", ";font-family:inherit;font-size:", ";line-height:1.35;width:100%;padding:7px 9px;border-radius:", ";background:", ";border:1px solid ", ";&::placeholder{color:", ";transition:color 0.15s;}&:focus-visible{color:", ";border:1px solid ", ";outline:", ";::placeholder{color:transparent;}}&:read-only{color:", ";border-color:", ";}&:disabled{color:", ";border-color:", ";}&:focus:read-only{border-color:", ";outline:0;}", "{&:not(:disabled):not(:read-only){&:hover{border-color:", ";}}}"], theme.text.primary, font.body.large, radius.small, theme.grey.background, theme.grey.graphics2, theme.text.secondary, theme.text.primary, theme.grey.graphics1, borders.interactive.medium, theme.text.secondary, theme.common.disabled1, theme.text.disabled, theme.common.disabled1, theme.common.disabled1, media.pointer, theme.grey.graphics1);
var Container24 = styled43.div.withConfig({
  displayName: "TypeSelect__Container",
  componentId: "sc-f6sskn-1"
})(["position:relative;"]);
var Results = styled43.div.withConfig({
  displayName: "TypeSelect__Results",
  componentId: "sc-f6sskn-2"
})(["position:absolute;z-index:1000;overflow:auto;top:100%;max-height:156px;width:100%;padding:6px 0;background:", ";box-shadow:", ";"], theme.grey.surface2, shadows.low);
var ResultsItem = styled43.div.withConfig({
  displayName: "TypeSelect__ResultsItem",
  componentId: "sc-f6sskn-3"
})(["color:", ";font-size:", ";font-weight:", ";text-align:left;white-space:nowrap;display:block;width:100%;padding:9px 30px 10px 18px;background:", ";border:0;cursor:pointer;", "{&:hover,&:focus{color:", ";}}", " ", ""], theme.text.primary, font.body.medium, font.weight.medium, theme.grey.surface2, media.pointer, theme.primary.main, ({
  isSelected
}) => isSelected && `
        color: ${theme.primary.main};
    `, ({
  isEmpty
}) => isEmpty && `
        color: ${theme.text.disabled};
        cursor: default;

        ${media.pointer} {
            &:hover,
            &:focus {
                color: ${theme.text.disabled};
            }
        };
    `);

// src/atoms/Typography.tsx
import styled44 from "styled-components";
import { isDefined as isDefined3 } from "@scouts/helpers";
import { jsx as _jsx888 } from "react/jsx-runtime";
var getAs = (variant, as) => {
  if (isDefined3(as)) {
    return as;
  } else {
    switch (variant) {
      case "heading1Large":
      case "h1":
      case "accent-h1":
        return "h1";
      case "heading2Large":
      case "h2":
      case "accent-h2":
        return "h2";
      case "h3":
      case "accent-h3":
        return "h3";
      case "h4":
      case "accent-h4":
        return "h4";
      case "h5":
      case "accent-h5":
        return "h5";
      case "h6":
      case "accent-h6":
        return "h6";
      default:
        return "p";
    }
  }
};
var Typography = ({
  children,
  as,
  variant,
  textAlign = "left"
}) => /* @__PURE__ */ _jsx888(Container25, {
  as: getAs(variant, as),
  variant,
  textAlign,
  children
});
var Container25 = styled44.div.withConfig({
  displayName: "Typography__Container",
  componentId: "sc-11p6563-0"
})(["font-family:", ";text-align:", ";", " ", " ", " ", " ", " ", " ", ""], themeFonts.fonts.ABCROM, ({
  textAlign
}) => textAlign, ({
  variant
}) => (variant === "heading1Large" || variant === "heading2Large" || variant === "h1" || variant === "h2" || variant === "h3" || variant === "accent-h1" || variant === "accent-h2" || variant === "accent-h3") && `
        color: ${theme.text.title};
        font-weight: ${themeFonts.typography[variant].fontWeight};
        font-size: ${`${themeFonts.typography[variant].fontSize.xs}px`};
        line-height: ${`${themeFonts.typography[variant].lineHeight.xs}px`};
        letter-spacing:  ${`${themeFonts.typography[variant].letterSpacing.xs}px`};

        ${media.tablet} {
            font-size: ${`${themeFonts.typography[variant].fontSize.sm}px`};
            line-height: ${`${themeFonts.typography[variant].lineHeight.sm}px`};
            letter-spacing:  ${`${themeFonts.typography[variant].letterSpacing.sm}px`};
        }

        ${media.desktop} {
            font-size: ${`${themeFonts.typography[variant].fontSize.lg}px`};
            line-height: ${`${themeFonts.typography[variant].lineHeight.lg}px`};
            letter-spacing:  ${`${themeFonts.typography[variant].letterSpacing.lg}px`};
        }
    `, ({
  variant
}) => (variant === "h4" || variant === "accent-h4") && `
        font-weight: ${themeFonts.typography[variant].fontWeight};
        font-size: ${`${themeFonts.typography[variant].fontSize.xs}px`};
        line-height: ${`${themeFonts.typography[variant].lineHeight.xs}px`};
        letter-spacing:  ${`${themeFonts.typography[variant].letterSpacing.xs}px`};

        ${media.tablet} {
            font-size: ${`${themeFonts.typography[variant].fontSize.sm}px`};
            line-height: ${`${themeFonts.typography[variant].lineHeight.sm}px`};
            letter-spacing:  ${`${themeFonts.typography[variant].letterSpacing.sm}px`};
        }
        `, ({
  variant
}) => (variant === "h5" || variant === "h6" || variant === "accent-h5" || variant === "accent-h6") && `
        font-weight: ${themeFonts.typography[variant].fontWeight};
        font-size: ${`${themeFonts.typography[variant].fontSize.xs}px`};
        line-height: ${`${themeFonts.typography[variant].lineHeight.xs}px`};
        letter-spacing:  ${`${themeFonts.typography[variant].letterSpacing.xs}px`};
        `, ({
  variant
}) => (variant === "accent-h1" || variant === "accent-h2" || variant === "accent-h3" || variant === "accent-h4" || variant === "accent-h5" || variant === "accent-h6") && `
            font-family: ${themeFonts.fonts.ABCROMExtended};
        `, ({
  variant
}) => variant === "bodyLarge" && `
        color: ${theme.text.primary};
        font-weight: ${themeFonts.typography[variant].fontWeight};
        font-size: ${`${themeFonts.typography[variant].fontSize.xs}px`};
        line-height: ${`${themeFonts.typography[variant].lineHeight.xs}px`};
        
        ${media.tablet} {
            font-size: ${`${themeFonts.typography[variant].fontSize.sm}px`};
            line-height: ${`${themeFonts.typography[variant].lineHeight.sm}px`};
        }

        ${media.desktop} {
            font-size: ${`${themeFonts.typography[variant].fontSize.lg}px`};
            line-height: ${`${themeFonts.typography[variant].lineHeight.lg}px`};
        
        }
        `, ({
  variant
}) => (variant === "body" || variant === "bodyLong" || variant === "bodySmall") && `
        color: ${theme.text.primary};
        font-weight: ${themeFonts.typography[variant].fontWeight};
        font-size: ${`${themeFonts.typography[variant].fontSize.xs}px`};
        line-height: ${`${themeFonts.typography[variant].lineHeight.xs}px`};
        `, ({
  variant
}) => variant === "overline" && `
        color: ${theme.text.secondary};
        font-weight: ${themeFonts.typography[variant].fontWeight};
        font-size: ${`${themeFonts.typography[variant].fontSize.xs}px`};
        line-height: ${`${themeFonts.typography[variant].lineHeight.xs}px`};
        letter-spacing:  ${`${themeFonts.typography[variant].letterSpacing.xs}px`};
        text-transform: uppercase;
        
        ${media.desktop} {
            font-size: ${`${themeFonts.typography[variant].fontSize.lg}px`};
            letter-spacing:  ${`${themeFonts.typography[variant].letterSpacing.lg}px`};
        }
    `);

// src/molecules/GlobalSpinner.tsx
import { styled as styled45 } from "styled-components";
import { hexToRgbA as hexToRgbA6 } from "@scouts/helpers";
import { jsx as _jsx889 } from "react/jsx-runtime";
var Container26 = styled45.div.withConfig({
  displayName: "GlobalSpinner__Container",
  componentId: "sc-xu7txs-0"
})(["display:flex;align-items:center;justify-content:center;position:fixed;top:0;left:0;z-index:", ";width:100%;height:100%;background:", ";"], Z_INDEX_GLOBAL_SPINNER, hexToRgbA6(colors.surface.base, 0.5));
var GlobalSpinner = (props) => /* @__PURE__ */ _jsx889(Container26, {
  children: /* @__PURE__ */ _jsx889(Spinner, {
    ...props
  })
});

// src/molecules/ConfirmationModal.tsx
import { styled as styled53 } from "styled-components";

// src/organisms/ModalLegacy/Modal.tsx
import { useEffect as useEffect3, useRef as useRef5, useState as useState8 } from "react";
import FocusLock2 from "react-focus-lock";
import { CSSTransition } from "react-transition-group";
import { styled as styled46 } from "styled-components";
import { hexToRgbA as hexToRgbA7, useOnEscapeKeydown as useOnEscapeKeydown2, useResizeObserver } from "@scouts/helpers";

// src/organisms/ModalLegacy/ModalPortal.ts
import { createPortal } from "react-dom";
var ModalPortal = ({
  children
}) => {
  const root = document.body;
  return /* @__PURE__ */ createPortal(children, root);
};

// src/organisms/ModalLegacy/Modal.tsx
import { jsx as _jsx890, jsxs as _jsxs51 } from "react/jsx-runtime";
var Modal = ({
  children,
  isFullScreen,
  isWide,
  onClose,
  width,
  isLoading,
  isError,
  errorMessage,
  ...rest
}) => {
  const [windowHeight, setWindowHeight] = useState8(null);
  const ref = useRef5(null);
  const handleClose = onClose;
  useResizeObserver(ref, () => {
    setWindowHeight(window.innerHeight);
  });
  useOnEscapeKeydown2(onClose);
  useEffect3(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);
  return /* @__PURE__ */ _jsx890(ModalPortal, {
    children: /* @__PURE__ */ _jsx890(CSSTransition, {
      nodeRef: ref,
      in: true,
      appear: true,
      timeout: 300,
      classNames: "transition",
      children: /* @__PURE__ */ _jsxs51(Container27, {
        role: "dialog",
        ref,
        ...rest,
        children: [isLoading && /* @__PURE__ */ _jsx890(SpinnerContainer2, {
          children: /* @__PURE__ */ _jsx890(Spinner, {
            isSmall: true
          })
        }), !isLoading && /* @__PURE__ */ _jsx890(FocusLock2, {
          autoFocus: false,
          returnFocus: true,
          children: /* @__PURE__ */ _jsxs51(ModalContainer, {
            isFullScreen,
            isWide,
            maxHeight: windowHeight,
            width,
            children: [isError && /* @__PURE__ */ _jsx890(Container2, {
              padding: "24px 48px 24px 24px",
              tabletPadding: "48px",
              children: /* @__PURE__ */ _jsx890(Line, {
                children: errorMessage || "Oops! There was an error loading the data\u2026"
              })
            }), !isError && children, handleClose && /* @__PURE__ */ _jsx890(Close, {
              onClick: handleClose,
              children: "\u2715"
            })]
          })
        })]
      })
    })
  });
};
var Container27 = styled46.div.withConfig({
  displayName: "Modal__Container",
  componentId: "sc-1bctzlz-0"
})(["color:", ";text-align:left;display:flex;flex-direction:column;justify-content:flex-end;overflow:auto;position:fixed;top:0;left:0;z-index:", ";width:100%;height:100%;background:", ";transition:background-color 0.15s;&.transition-appear-active,&.transition-enter-active{overflow:hidden;}&.transition-appear-active,&.transition-appear-done,&.transition-enter-active,&.transition-enter-done{background-color:", ";}", "{display:block;padding:0 0 192px;}", "{padding:72px 0;}@media (max-height:540px){display:block;}"], colors.text.base, ({
  zIndex
}) => zIndex || Z_INDEX_MODALS, hexToRgbA7(colors.surface.neutral, 0), hexToRgbA7(colors.surface.neutral, 0.9), media.tablet, media.desktop);
var SpinnerContainer2 = styled46.div.withConfig({
  displayName: "Modal__SpinnerContainer",
  componentId: "sc-1bctzlz-1"
})(["display:flex;align-items:center;justify-content:center;height:100%;"]);
var ModalContainer = styled46.div.withConfig({
  displayName: "Modal__ModalContainer",
  componentId: "sc-1bctzlz-2"
})(["position:relative;max-width:100%;max-height:", ";background:", ";border-top-left-radius:", ";border-top-right-radius:", ";box-shadow:", ";opacity:0;transform:translateY(10%);transition:opacity 0.3s,transform 0.3s;.transition-appear-active &,.transition-appear-done &,.transition-enter-active &,.transition-enter-done &{opacity:1;transform:translateY(0);}", "{max-height:unset;min-height:unset;margin:0 auto;border-radius:", ";", " ", " ", " ", "}@media (max-height:540px){max-height:unset;}"], ({
  maxHeight
}) => maxHeight ? `${maxHeight}px` : "unset", colors.surface.base, radius.large, radius.large, shadows.high, media.tablet, radius.large, ({
  isFullScreen
}) => isFullScreen && `
            width: 90%;
        `, ({
  isWide
}) => isWide && `
            width: 864px;
        `, ({
  width
}) => width && `
            width: ${width};
        `, ({
  isFullScreen,
  isWide,
  width
}) => !isFullScreen && !isWide && !width && `
            width: 564px;
        `);
var Close = styled46.button.attrs({
  type: "button"
}).withConfig({
  displayName: "Modal__Close",
  componentId: "sc-1bctzlz-3"
})(["content:'\u2715';color:", ";font-size:18px;line-height:1;display:flex;align-items:center;justify-content:center;position:absolute;top:9px;right:9px;z-index:4;width:36px;height:36px;background:", ";border:2px solid ", ";border-radius:", ";cursor:pointer;&:focus{outline:none;}&:focus,&:active{border:2px solid ", ";}", "{&:hover{opacity:0.75;}}", "{font-size:21px;top:-24px;right:-24px;width:48px;height:48px;}"], colors.text.base, colors.surface.neutral, colors.line.neutralSoft, radius.full, colors.line.interactive, media.pointer, media.desktop);

// src/organisms/ModalLegacy/ModalActions.ts
import { styled as styled47 } from "styled-components";
var ModalActions = styled47.div.withConfig({
  displayName: "ModalActions",
  componentId: "sc-5i6zl5-0"
})(["display:flex;flex-wrap:wrap;align-items:center;gap:12px 0;justify-content:flex-end;&:last-child{flex:1 0 auto;}&:not(:last-child){margin-bottom:24px;}> :not(:first-child){margin-left:12px;}", "{&:not(:last-child){margin:0;}}"], media.tablet);

// src/organisms/ModalLegacy/ModalBody.tsx
import { styled as styled48 } from "styled-components";
import { hexToRgbA as hexToRgbA8 } from "@scouts/helpers";
import { jsx as _jsx891, jsxs as _jsxs52 } from "react/jsx-runtime";
var ModalBody = ({
  children,
  ...rest
}) => /* @__PURE__ */ _jsxs52(Container28, {
  ...rest,
  children: [/* @__PURE__ */ _jsx891(TopFade, {}), /* @__PURE__ */ _jsx891(Content3, {
    children
  }), /* @__PURE__ */ _jsx891(BottomFade, {})]
});
var Container28 = styled48.div.withConfig({
  displayName: "ModalBody__Container",
  componentId: "sc-na8god-0"
})(["overflow:auto;position:relative;max-height:60vh;", "{overflow:unset;max-height:unset;}@media (max-height:540px){max-height:unset;}"], media.tablet);
var TopFade = styled48.div.withConfig({
  displayName: "ModalBody__TopFade",
  componentId: "sc-na8god-1"
})(["position:sticky;top:0;left:0;z-index:3;width:100%;height:12px;background:linear-gradient(to top,", ",", ");border-radius:", " ", " 0 0;pointer-events:none;", "{position:static;}"], hexToRgbA8(colors.surface.base, 0), colors.surface.base, radius.large, radius.large, media.tablet);
var BottomFade = styled48.div.withConfig({
  displayName: "ModalBody__BottomFade",
  componentId: "sc-na8god-2"
})(["position:sticky;bottom:0;left:0;z-index:3;width:100%;height:12px;background:linear-gradient(to bottom,", ",", ");border-radius:0 0 ", " ", ";pointer-events:none;", "{position:static;}"], hexToRgbA8(colors.surface.base, 0), colors.surface.base, radius.large, radius.large, media.tablet);
var Content3 = styled48.div.withConfig({
  displayName: "ModalBody__Content",
  componentId: "sc-na8god-3"
})(["height:100%;padding:0 24px;", "{padding-right:48px;padding-left:48px;}p:not(:last-child){margin-bottom:12px;}h1,h2,h3,h4{font-weight:", ";margin-bottom:12px;}"], media.tablet, font.weight.medium);

// src/organisms/ModalLegacy/ModalFooter.ts
import { styled as styled49 } from "styled-components";
var ModalFooter = styled49.div.withConfig({
  displayName: "ModalFooter",
  componentId: "sc-1c2l2rc-0"
})(["padding:18px 24px 36px 24px;", "{padding:24px 48px 36px;}"], media.tablet);

// src/organisms/ModalLegacy/ModalHeader.ts
import { styled as styled50 } from "styled-components";
var ModalHeader = styled50.div.withConfig({
  displayName: "ModalHeader",
  componentId: "sc-vkdr4q-0"
})(["position:relative;padding:18px 60px 9px 24px;", "{padding:36px 48px 18px;}"], media.tablet);

// src/organisms/ModalLegacy/ModalSubtitle.ts
import { styled as styled51 } from "styled-components";
var ModalSubtitle = styled51.div.withConfig({
  displayName: "ModalSubtitle",
  componentId: "sc-13tp8ga-0"
})(["font-size:", ";line-height:1.5;margin-top:12px;"], font.title.small);

// src/organisms/ModalLegacy/ModalTitle.ts
import { styled as styled52 } from "styled-components";
var ModalTitle = styled52.div.withConfig({
  displayName: "ModalTitle",
  componentId: "sc-kw5e6w-0"
})(["font-size:", ";font-weight:", ";line-height:1.3;", "{font-size:", ";}"], font.title.medium, font.weight.medium, media.tablet, font.title.large);

// src/molecules/ConfirmationModal.tsx
import { jsx as _jsx892, jsxs as _jsxs53 } from "react/jsx-runtime";
var ConfirmationModal = ({
  children,
  labelCancel = "Cancel",
  labelConfirm = "Confirm",
  onCancel,
  onClose,
  onConfirm,
  title = "Are you sure?",
  isFullScreen,
  isLoading
}) => /* @__PURE__ */ _jsxs53(Modal, {
  isFullScreen,
  onClose: onClose || onCancel,
  "data-testid": "confirmation-dialog",
  children: [/* @__PURE__ */ _jsx892(ModalHeader, {
    children: /* @__PURE__ */ _jsx892(ModalTitle, {
      children: title
    })
  }), !!children && /* @__PURE__ */ _jsx892(ModalBody, {
    children: /* @__PURE__ */ _jsx892(Body2, {
      children
    })
  }), /* @__PURE__ */ _jsx892(ModalFooter, {
    children: /* @__PURE__ */ _jsxs53(ModalActions, {
      children: [onCancel && /* @__PURE__ */ _jsx892(Button, {
        disabled: isLoading,
        onClick: onCancel,
        size: "large",
        variant: "tertiary",
        children: labelCancel
      }), /* @__PURE__ */ _jsx892(Button, {
        isLoading,
        onClick: onConfirm,
        size: "large",
        children: labelConfirm
      })]
    })
  })]
});
var Body2 = styled53.div.withConfig({
  displayName: "ConfirmationModal__Body",
  componentId: "sc-k77q9e-0"
})(["line-height:1.5;"]);

// src/molecules/CookieConsentOverlay.tsx
import { styled as styled54 } from "styled-components";
import { useDisablePageScroll, Locale as Locale2 } from "@scouts/helpers";
import { jsx as _jsx893, jsxs as _jsxs54 } from "react/jsx-runtime";
var translationsByLocale = {
  [Locale2.GB]: {
    imageAlt: "Cookie",
    title: "Can we offer you some (optional) cookies?",
    body: "Optional cookies help us to improve our product and make your experience as seamless as possible. But we'll only turn them on if you're happy for us to.",
    linkLearnMore: "Learn more in our cookie policy",
    linkHref: "https://taxfix.com/en-uk/cookies-policy/",
    btnSettings: "Settings",
    btnReject: "Deny",
    btnAccept: "Accept"
  },
  [Locale2.ES]: {
    imageAlt: "La galleta",
    title: "\xBFUnas cookies?",
    body: "Estas cookies nos permiten afinar nuestro producto y hacen que tengas una mejor experiencia en Taxfix. Solo las activaremos si las aceptas.",
    linkLearnMore: "M\xE1s informaci\xF3n en nuestra pol\xEDtica de cookies",
    linkHref: "https://taxfix.com/es-es/politica-de-cookies/",
    btnSettings: "Ajustes",
    btnReject: "Rechazar",
    btnAccept: "Aceptar"
  }
};
var CookieConsentOverlay = ({
  onReject,
  onAccept,
  onOpenPreferences,
  locale,
  imageSrc
}) => {
  useDisablePageScroll();
  const translations4 = translationsByLocale[locale];
  return /* @__PURE__ */ _jsx893(Container2, {
    relative: true,
    "data-pw": "cookie-consent-overlay",
    children: /* @__PURE__ */ _jsx893(Backdrop, {
      children: /* @__PURE__ */ _jsx893(Positioner, {
        children: /* @__PURE__ */ _jsx893(Container2, {
          padding: "18px",
          tabletPadding: "30px 36px",
          radius: radius.large,
          shadow: shadows.high,
          background: colors.surface.base,
          tabletBackground: colors.surface.base,
          role: "dialog",
          children: /* @__PURE__ */ _jsxs54(Flexer, {
            column: true,
            tabletRow: true,
            gap: "24px",
            tabletGap: "36px",
            children: [/* @__PURE__ */ _jsx893(Flexer, {
              justifyCenter: true,
              alignCenter: true,
              children: /* @__PURE__ */ _jsx893(CookieImage, {
                src: imageSrc,
                alt: translations4.imageAlt
              })
            }), /* @__PURE__ */ _jsxs54(Flexer, {
              column: true,
              gap: "6px",
              tabletWidth: "500px",
              justifyCenter: true,
              children: [/* @__PURE__ */ _jsx893(Line, {
                weight: font.weight.medium,
                children: translations4.title
              }), /* @__PURE__ */ _jsxs54(Line, {
                size: font.body.small,
                tabletSize: font.body.medium,
                children: [translations4.body, " ", /* @__PURE__ */ _jsx893(InlineLink, {
                  href: translations4.linkHref,
                  target: "_blank",
                  children: translations4.linkLearnMore
                }), "."]
              })]
            }), /* @__PURE__ */ _jsxs54(Flexer, {
              column: true,
              tabletColumnReverse: true,
              gap: "18px",
              tabletGap: "12px",
              tabletWidth: "220px",
              justifyCenter: true,
              children: [/* @__PURE__ */ _jsxs54(Flexer, {
                row: true,
                tabletColumnReverse: true,
                gap: "15px",
                tabletGap: "12px",
                children: [/* @__PURE__ */ _jsx893(Button, {
                  isFullWidth: true,
                  variant: "secondary",
                  size: "large",
                  onClick: onOpenPreferences,
                  children: translations4.btnSettings
                }), /* @__PURE__ */ _jsx893(Button, {
                  isFullWidth: true,
                  variant: "secondary",
                  size: "large",
                  onClick: onReject,
                  children: translations4.btnReject
                })]
              }), /* @__PURE__ */ _jsx893(Flexer, {
                column: true,
                children: /* @__PURE__ */ _jsx893(Button, {
                  variant: "primary",
                  size: "large",
                  onClick: onAccept,
                  children: translations4.btnAccept
                })
              })]
            })]
          })
        })
      })
    })
  });
};
var Backdrop = styled54.div.withConfig({
  displayName: "CookieConsentOverlay__Backdrop",
  componentId: "sc-1sczuao-0"
})(["position:fixed;top:0;right:0;bottom:0;left:0;z-index:2147483201;background:rgba(0,0,0,0.4);"]);
var Positioner = styled54.div.withConfig({
  displayName: "CookieConsentOverlay__Positioner",
  componentId: "sc-1sczuao-1"
})(["position:absolute;right:24px;bottom:48px;left:24px;max-width:960px;margin:0 auto;"]);
var CookieImage = styled54.img.withConfig({
  displayName: "CookieConsentOverlay__CookieImage",
  componentId: "sc-1sczuao-2"
})(["width:72px;height:72px;", "{width:96px;height:96px;}"], media.tablet);

// src/molecules/DatePicker.tsx
import { forwardRef as forwardRef7, useCallback as useCallback2, useMemo } from "react";
import { styled as styled55 } from "styled-components";
import { Locale as Locale3 } from "@scouts/helpers";

// src/helpers.js
var formatDateValue = (value) => {
  let dateValue = value;
  if (!(value instanceof Date) && value != null) {
    dateValue = value.toString().replace(/-/g, "/").replace(/T.+/, "");
  }
  return dateValue;
};
var getDayFromDate = (value) => {
  const dateValue = formatDateValue(value);
  const date = value ? new Date(dateValue) : "";
  return date ? date.getDate() : "";
};
var getMonthFromDate = (value) => {
  const dateValue = formatDateValue(value);
  const date = value ? new Date(dateValue) : "";
  return date ? date.getMonth() + 1 : "";
};
var getYearFromDate = (value) => {
  const dateValue = formatDateValue(value);
  const date = value ? new Date(dateValue) : "";
  return date ? date.getFullYear() : "";
};
var addLeadingZero = (value) => {
  if (!value)
    return value;
  if (String(value).length > 1)
    return value;
  return `0${value}`;
};
var isValidDate = (value) => {
  if (typeof value === "string") {
    const ISO_8601 = /^\d{4}(-\d\d(-\d\d(T\d\d:\d\d(:\d\d)?(\.\d+)?(([+-]\d\d:\d\d)|Z)?)?)?)?$/i;
    return ISO_8601.test(value);
  }
  return value instanceof Date;
};
var isLeapYear = (year) => year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
var callAll = (...fns) => (...args) => fns.forEach((fn) => typeof fn === "function" ? fn(...args) : void 0);
var backgroundColorOptions = {
  options: Array.from({
    length: Object.keys(colors.surface).length
  }, (_, i) => i),
  mapping: Object.values(colors.surface),
  control: {
    type: "select",
    labels: Object.keys(colors.surface)
  }
};
var shadowOptions = {
  options: Array.from({
    length: Object.keys(shadows).length
  }, (_, i) => i),
  mapping: Object.values(shadows),
  control: {
    type: "select",
    labels: Object.keys(shadows)
  }
};
var textColorOptions = {
  options: Array.from({
    length: Object.keys(colors.text).length
  }, (_, i) => i),
  mapping: Object.values(colors.text),
  control: {
    type: "select",
    labels: Object.keys(colors.text)
  }
};

// src/molecules/DatePicker.tsx
import { jsx as _jsx894, jsxs as _jsxs55 } from "react/jsx-runtime";
var currentYear = (/* @__PURE__ */ new Date()).getFullYear();
var days = [...Array(31).keys()].map((val) => val + 1);
var months = [...Array(12).keys()].map((val) => val + 1);
var getYearsToDisplay = (minYear, maxYear) => {
  const years = [];
  for (let i = Number(minYear); i <= Number(maxYear); i += 1)
    years.push(i);
  return years;
};
var translations2 = {
  [Locale3.GB]: {
    monthsShortNamesArray: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    textDateAfter: "Please select a date that is after",
    textDateBefore: "Please select a date that is before",
    textDay: "Day",
    textIncluded: "(including)",
    textInvalidDateValue: "Invalid date value",
    textInvalidDefaultDate: "Invalid default date",
    textInvalidMaxDate: "Invalid max date",
    textInvalidMinDate: "Invalid min date",
    textMonth: "Month",
    textYear: "Year"
  },
  [Locale3.ES]: {
    monthsShortNamesArray: ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"],
    textDateAfter: "Seleccione una fecha posterior",
    textDateBefore: "Seleccione una fecha anterior",
    textDay: "D\xEDa",
    textIncluded: "(incluso)",
    textInvalidDateValue: "Valor de fecha no v\xE1lido",
    textInvalidDefaultDate: "Fecha predeterminada no v\xE1lida",
    textInvalidMaxDate: "Fecha m\xE1xima no v\xE1lida",
    textInvalidMinDate: "Fecha m\xEDnima no v\xE1lida",
    textMonth: "Mes",
    textYear: "A\xF1o"
  }
};
var DatePicker = /* @__PURE__ */ forwardRef7(({
  defaultDate = "",
  disabled,
  isBorderless,
  isFullWidth,
  locale = "en-GB",
  maxDate = `${currentYear}-12-31`,
  minDate = "1900-01-01",
  readOnly,
  name,
  onChange,
  required = false,
  value = "",
  ...rest
}, ref) => {
  const {
    monthsShortNamesArray,
    textDateAfter,
    textDateBefore,
    textDay,
    textIncluded,
    textInvalidDateValue,
    textInvalidDefaultDate,
    textInvalidMaxDate,
    textInvalidMinDate,
    textMonth,
    textYear
  } = translations2[locale];
  if (!isValidDate(defaultDate) && defaultDate !== "")
    throw new Error(textInvalidDefaultDate);
  if (!isValidDate(maxDate))
    throw new Error(textInvalidMaxDate);
  if (!isValidDate(minDate))
    throw new Error(textInvalidMinDate);
  if (!isValidDate(value) && value !== "" && value !== null)
    throw new Error(textInvalidDateValue);
  const validateDate = useCallback2((date) => {
    let validatedDate = date;
    if (new Date(date) > new Date(maxDate)) {
      validatedDate = maxDate;
      const alertText = `${textDateBefore} ${addLeadingZero(getDayFromDate(maxDate))}.${addLeadingZero(getMonthFromDate(maxDate))}.${getYearFromDate(maxDate)} ${textIncluded}`;
      alert(alertText);
    } else if (new Date(date) < new Date(minDate)) {
      validatedDate = minDate;
      const alertText = `${textDateAfter} ${addLeadingZero(getDayFromDate(minDate))}.${addLeadingZero(getMonthFromDate(minDate))}.${getYearFromDate(minDate)} ${textIncluded}`;
      alert(alertText);
    }
    const dateArray = [getYearFromDate(validatedDate), addLeadingZero(getMonthFromDate(validatedDate)), addLeadingZero(getDayFromDate(validatedDate))];
    return dateArray.indexOf("") === -1 ? dateArray.join("-") : "";
  }, [maxDate, minDate, textDateAfter, textDateBefore, textIncluded]);
  const handleChange = useCallback2(({
    name: changedName,
    value: changedValue
  }) => {
    const yearNumber = changedName === "year" ? changedValue : getYearFromDate(value) || getYearFromDate(defaultDate) || getYearFromDate(minDate);
    const monthNumber = changedName === "month" ? changedValue : getMonthFromDate(value) || getMonthFromDate(defaultDate) || getMonthFromDate(minDate);
    let dayNumber = changedName === "day" ? changedValue : getDayFromDate(value) || getDayFromDate(defaultDate) || getDayFromDate(minDate);
    const isSelectedYearALeapYear = isLeapYear(yearNumber);
    const monthsWithLessThan31Days = [4, 6, 9, 11];
    if (Number(dayNumber) === 31 && monthsWithLessThan31Days.includes(Number(monthNumber))) {
      dayNumber = 30;
    }
    if (Number(dayNumber) === 31 && Number(monthNumber) === 2 || Number(dayNumber) === 30 && Number(monthNumber) === 2 || Number(dayNumber) === 29 && Number(monthNumber) === 2) {
      dayNumber = isSelectedYearALeapYear ? 29 : 28;
    }
    const dateArray = [yearNumber, addLeadingZero(monthNumber), addLeadingZero(dayNumber)];
    const date = dateArray.indexOf("") === -1 ? dateArray.join("-") : "";
    const validatedDate = dateArray.indexOf("") === -1 ? validateDate(date) : date;
    onChange({
      name,
      value: validatedDate
    });
  }, [defaultDate, minDate, name, onChange, validateDate, value]);
  const day = getDayFromDate(value) || "";
  const month = getMonthFromDate(value) || "";
  const year = getYearFromDate(value) || "";
  const maxYear = getYearFromDate(maxDate);
  const minYear = getYearFromDate(minDate);
  const years = useMemo(() => getYearsToDisplay(Number(minYear), Number(maxYear)), [minYear, maxYear]);
  const optionsDay = useMemo(() => [{
    title: textDay
  }, ...days.map((d) => ({
    value: d,
    ...!!rest["data-testid"] && {
      "data-testid": `${rest["data-testid"]}_day_${d}`
    }
  }))], [rest, textDay]);
  const optionsMonth = useMemo(() => [{
    title: textMonth
  }, ...months.map((m) => ({
    value: m,
    title: `${monthsShortNamesArray[m - 1]}`,
    ...!!rest["data-testid"] && {
      "data-testid": `${rest["data-testid"]}_month_${m}`
    }
  }))], [monthsShortNamesArray, rest, textMonth]);
  const optionsYear = useMemo(() => [{
    title: textYear
  }, ...years.reverse().map((y) => ({
    value: y,
    ...!!rest["data-testid"] && {
      "data-testid": `${rest["data-testid"]}_year_${y}`
    }
  }))], [rest, textYear, years]);
  return /* @__PURE__ */ _jsxs55(Flexer, {
    width: "100%",
    children: [/* @__PURE__ */ _jsx894(DayContainer, {
      isBorderless,
      isFullWidth,
      children: /* @__PURE__ */ _jsx894(MemoizedSelect, {
        ref,
        disabled,
        readOnly,
        isBorderless,
        name: "day",
        value: day,
        onChange: handleChange,
        width: "100%",
        options: optionsDay,
        "aria-label": textDay,
        required,
        ...!!rest["data-testid"] && {
          "data-testid": `${rest["data-testid"]}_day`
        }
      })
    }), /* @__PURE__ */ _jsx894(MonthContainer, {
      isBorderless,
      isFullWidth,
      children: /* @__PURE__ */ _jsx894(MemoizedSelect, {
        disabled,
        readOnly,
        isBorderless,
        name: "month",
        value: month,
        onChange: handleChange,
        width: "100%",
        options: optionsMonth,
        "aria-label": textMonth,
        required,
        ...!!rest["data-testid"] && {
          "data-testid": `${rest["data-testid"]}_month`
        }
      })
    }), /* @__PURE__ */ _jsx894(YearContainer, {
      isBorderless,
      isFullWidth,
      children: /* @__PURE__ */ _jsx894(MemoizedSelect, {
        disabled,
        readOnly,
        isBorderless,
        name: "year",
        value: year,
        onChange: handleChange,
        width: "100%",
        options: optionsYear,
        "aria-label": textYear,
        required,
        ...!!rest["data-testid"] && {
          "data-testid": `${rest["data-testid"]}_year`
        }
      })
    })]
  });
});
var DayContainer = styled55.div.withConfig({
  displayName: "DatePicker__DayContainer",
  componentId: "sc-100tnnq-0"
})(["width:", ";max-width:", ";min-width:53px;margin:", ";"], ({
  isBorderless
}) => isBorderless ? "30%" : "calc(30% - 12px)", ({
  isFullWidth
}) => isFullWidth ? "none" : "84px", ({
  isBorderless
}) => isBorderless ? "0" : "0 12px 0 0");
var MonthContainer = styled55.div.withConfig({
  displayName: "DatePicker__MonthContainer",
  componentId: "sc-100tnnq-1"
})(["width:", ";max-width:", ";min-width:64px;margin:", ";"], ({
  isBorderless
}) => isBorderless ? "35%" : "calc(35% - 12px)", ({
  isFullWidth
}) => isFullWidth ? "none" : "120px", ({
  isBorderless
}) => isBorderless ? "0" : "0 12px 0 0");
var YearContainer = styled55.div.withConfig({
  displayName: "DatePicker__YearContainer",
  componentId: "sc-100tnnq-2"
})(["width:35%;max-width:", ";min-width:72px;"], ({
  isFullWidth
}) => isFullWidth ? "none" : "96px");

// src/molecules/DatePickerWithLabel.tsx
import { styled as styled56 } from "styled-components";
import { jsx as _jsx895, jsxs as _jsxs56, Fragment as _Fragment6 } from "react/jsx-runtime";
var Fieldset2 = styled56.fieldset.withConfig({
  displayName: "DatePickerWithLabel__Fieldset",
  componentId: "sc-10uynju-0"
})(["position:relative;width:", ";margin:0;padding:0;background:", ";border:1px solid ", ";border-radius:", ";&:disabled{border-color:", ";}", "{&:not(:disabled){&:hover{border-color:", ";}}}"], ({
  width
}) => width, colors.surface.base, ({
  hasError
}) => hasError ? theme.error.main : colors.line.base, radius.small, ({
  readOnly
}) => readOnly ? colors.line.neutralLight : colors.line.neutral, media.pointer, theme.grey.graphics2);
var Legend2 = styled56.legend.withConfig({
  displayName: "DatePickerWithLabel__Legend",
  componentId: "sc-10uynju-1"
})(["color:", ";font-size:", ";position:absolute;top:-8px;left:6px;z-index:1;margin:0;padding:2px 4px;background:", ";border-radius:2px;", ""], ({
  hasError
}) => hasError ? colors.text.alert : colors.text.base, font.body.small, colors.surface.base, ({
  isDisabled
}) => isDisabled && `
        color: ${colors.text.neutral};
    `);
var DatePickerWithLabel = ({
  disabled,
  hasError,
  errorMessage,
  id,
  label,
  name,
  readOnly,
  width = "auto",
  ...rest
}) => /* @__PURE__ */ _jsxs56(_Fragment6, {
  children: [/* @__PURE__ */ _jsxs56(Fieldset2, {
    width,
    disabled: disabled || readOnly,
    readOnly,
    hasError,
    children: [/* @__PURE__ */ _jsx895(Legend2, {
      isDisabled: disabled,
      hasError,
      children: /* @__PURE__ */ _jsx895("label", {
        htmlFor: id || name,
        children: label
      })
    }), /* @__PURE__ */ _jsx895(DatePicker, {
      name,
      isBorderless: true,
      isFullWidth: true,
      readOnly,
      ...rest
    })]
  }), hasError && errorMessage && /* @__PURE__ */ _jsx895(Spacer, {
    marginTop: "6px",
    children: /* @__PURE__ */ _jsx895(Line, {
      color: colors.text.alert,
      size: font.body.medium,
      lineHeight: "1.3",
      children: errorMessage
    })
  })]
});

// src/molecules/IconButton.tsx
import { useState as useState9 } from "react";
import { styled as styled57 } from "styled-components";
import { jsx as _jsx896, jsxs as _jsxs57 } from "react/jsx-runtime";
var IconButton = ({
  color = colors.text.base,
  colorHover,
  onClick,
  IconComponent,
  background = colors.surface.neutral,
  backgroundHover,
  isFullWidth = false,
  isLoading,
  children,
  isActive,
  ...remainingProps
}) => {
  const [isHovered, setIsHovered] = useState9(false);
  const [isFocused, setIsFocused] = useState9(false);
  const handleClick = () => {
    if (onClick && !isLoading)
      onClick();
  };
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleBlur = () => {
    setIsFocused(false);
  };
  const shouldShowInteractionStyles = (isHovered || isFocused || !!isActive) && !isLoading;
  return /* @__PURE__ */ _jsxs57(Wrapper2, {
    onClick: handleClick,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleFocus,
    onBlur: handleBlur,
    isFullWidth,
    ...remainingProps,
    children: [/* @__PURE__ */ _jsxs57(IconContainer2, {
      background: shouldShowInteractionStyles ? backgroundHover || background : background,
      children: [isLoading && /* @__PURE__ */ _jsx896(Spinner, {
        isSmall: true,
        color
      }), !isLoading && /* @__PURE__ */ _jsx896(IconComponent, {
        "aria-hidden": "true",
        color: shouldShowInteractionStyles ? colorHover || color : color
      })]
    }), /* @__PURE__ */ _jsx896(Line, {
      size: font.body.medium,
      weight: font.weight.medium,
      color: shouldShowInteractionStyles ? colorHover || color : color,
      children
    })]
  });
};
var Wrapper2 = styled57.button.withConfig({
  displayName: "IconButton__Wrapper",
  componentId: "sc-dou3i7-0"
})(["display:flex;gap:12px;align-items:center;cursor:pointer;", ";border:none;padding:0;background:transparent;font:inherit;color:inherit;text-align:inherit;&:focus-visible{outline:", ";outline-offset:6px;border-radius:", ";}"], ({
  isFullWidth
}) => isFullWidth ? "width: 100%" : "", borders.interactive.medium, radius.small);
var IconContainer2 = styled57.div.withConfig({
  displayName: "IconButton__IconContainer",
  componentId: "sc-dou3i7-1"
})(["display:flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:", ";background-color:", ";"], radius.full, ({
  background
}) => background);

// src/molecules/InputWithSave.tsx
import { useEffect as useEffect4, useRef as useRef6, useState as useState10 } from "react";
import { jsx as _jsx897, jsxs as _jsxs58 } from "react/jsx-runtime";
var InputWithSave = ({
  name,
  cancelButtonLabel = "\u2715",
  onSave,
  saveButtonLabel = "Save",
  value,
  onChange,
  ...inputProps
}) => {
  const initialValueRef = useRef6(value);
  const refInput = useRef6(null);
  const [isActive, setIsActive] = useState10(false);
  const [componentStateValue, setComponentStateValue] = useState10(value);
  useEffect4(() => {
    setComponentStateValue(value);
    initialValueRef.current = value;
  }, [setComponentStateValue, value]);
  const handleBlur = (e) => {
    if (componentStateValue !== value)
      return;
    const {
      onBlur
    } = inputProps;
    cancel();
    if (onBlur)
      onBlur(e);
  };
  const handleChange = ({
    value: changeValue
  }) => {
    setComponentStateValue(changeValue);
  };
  const handleFocus = (e) => {
    activate();
    const {
      onFocus
    } = inputProps;
    if (onFocus)
      onFocus(e);
  };
  const handleKeyDown = (event) => {
    const {
      onKeyDown
    } = inputProps;
    switch (event.key) {
      case "Enter":
        event.preventDefault();
        handleSave();
        if (onKeyDown)
          onKeyDown(event);
        break;
      case "Esc":
      case "Escape":
        event.preventDefault();
        cancel();
        if (onKeyDown)
          onKeyDown(event);
        break;
      default:
    }
  };
  const handleSave = () => {
    onSave({
      name,
      value: componentStateValue
    });
    deactivate();
  };
  const activate = () => {
    setIsActive(true);
  };
  const deactivate = () => {
    if (refInput && refInput.current) {
      refInput.current.blur();
    }
    setIsActive(false);
  };
  const cancel = () => {
    deactivate();
    setComponentStateValue(initialValueRef.current);
  };
  const inputWithSaveProps = {
    ...inputProps,
    name,
    value: componentStateValue,
    onChange: onChange || handleChange,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown
  };
  return /* @__PURE__ */ _jsxs58(Flexer, {
    gap: "6px",
    width: "100%",
    children: [/* @__PURE__ */ _jsx897(Spacer, {
      flex: "1 1 auto",
      children: /* @__PURE__ */ _jsx897(Input, {
        ...inputWithSaveProps,
        ref: refInput
      })
    }), isActive && /* @__PURE__ */ _jsxs58(Flexer, {
      gap: "6px",
      alignCenter: true,
      children: [/* @__PURE__ */ _jsx897(Button, {
        onClick: handleSave,
        disabled: inputWithSaveProps.disabled,
        children: saveButtonLabel
      }), /* @__PURE__ */ _jsx897(Button, {
        onClick: cancel,
        variant: "secondary",
        children: cancelButtonLabel
      })]
    })]
  });
};

// src/molecules/File.tsx
import { Locale as Locale4 } from "@scouts/helpers";
import { jsx as _jsx898, jsxs as _jsxs59 } from "react/jsx-runtime";
var translations3 = {
  [Locale4.GB]: {
    textDelete: "Delete"
  },
  [Locale4.ES]: {
    textDelete: "Eliminar"
  }
};
var File = ({
  children,
  background,
  onClick,
  onDelete,
  variant = "highlight",
  locale = Locale4.GB,
  disabled = false
}) => {
  const {
    textDelete
  } = translations3[locale];
  return /* @__PURE__ */ _jsxs59(Flexer, {
    gap: "9px",
    alignCenter: true,
    children: [onClick ? /* @__PURE__ */ _jsx898(ChipButton, {
      variant,
      onClick,
      disabled,
      children
    }) : /* @__PURE__ */ _jsx898(Chip, {
      background,
      color: colors.text.base,
      disabled,
      children
    }), onDelete && /* @__PURE__ */ _jsx898(ButtonLink, {
      isSmall: true,
      color: colors.text.alert,
      onClick: onDelete,
      children: textDelete
    })]
  });
};

// src/molecules/FileDropZone.tsx
import { styled as styled58 } from "styled-components";
import { openFileDialog } from "@scouts/helpers";
import { jsx as _jsx899, jsxs as _jsxs60 } from "react/jsx-runtime";
var FileDropZone = ({
  onChange,
  buttonLabel = "Choose a file",
  label = "Drag & drop or choose a file"
}) => {
  const dragProps = useFileDragAndDrop({
    onDropHandler: onChange
  });
  const open = () => {
    openFileDialog({
      onChange,
      multiple: true
    });
  };
  return /* @__PURE__ */ _jsxs60(Container29, {
    ...dragProps,
    children: [/* @__PURE__ */ _jsx899(Line, {
      size: font.body.small,
      color: colors.text.neutral,
      children: label
    }), /* @__PURE__ */ _jsx899(Button, {
      onClick: open,
      children: buttonLabel
    })]
  });
};
var Container29 = styled58.div.withConfig({
  displayName: "FileDropZone__Container",
  componentId: "sc-aj3z9y-0"
})(["display:grid;gap:12px;place-content:center;padding:24px 36px;background-color:", ";border:2px solid ", ";border-style:dashed;border-radius:", ";"], ({
  isDragging
}) => isDragging ? colors.surface.neutral : "inherit", colors.line.neutralSoft, radius.large);

// src/molecules/OptionButtonGroup.tsx
import { jsx as _jsx900 } from "react/jsx-runtime";
var OptionButtonGroup = ({
  isDisabled = false,
  onChange,
  options,
  value
}) => /* @__PURE__ */ _jsx900(Flexer, {
  column: true,
  gap: "9px",
  tabletGap: "12px",
  children: options.map((option) => /* @__PURE__ */ _jsx900(OptionButton, {
    description: option.description,
    isActive: option.value === value,
    isDisabled,
    onClick: () => onChange(option.value),
    children: option.title
  }, option.value))
});

// src/molecules/UploadButton.tsx
import { openFileDialog as openFileDialog2 } from "@scouts/helpers";
import { jsx as _jsx901 } from "react/jsx-runtime";
var UploadButton = ({
  label,
  onUpload,
  acceptedFileExtensions = "",
  acceptMultiple = true,
  ...props
}) => {
  const handleClick = () => {
    openFileDialog2({
      multiple: acceptMultiple,
      accept: acceptedFileExtensions,
      onChange: (files) => onUpload(files)
    });
  };
  return /* @__PURE__ */ _jsx901(Button, {
    onClick: handleClick,
    ...props,
    children: label
  });
};

// src/organisms/AccountantPortalChrome/AccountantPortalChrome.tsx
import { useState as useState11 } from "react";

// src/organisms/AccountantPortalChrome/accountant-portal-chrome-context/accountant-portal-chrome-context.ts
import { createContext } from "react";
var AccountantPortalChromeContext = /* @__PURE__ */ createContext(null);

// src/organisms/AccountantPortalChrome/accountant-portal-chrome-context/use-accountant-portal-chrome.ts
import { useContext } from "react";
var useAccountantPortalChrome = () => {
  const value = useContext(AccountantPortalChromeContext);
  if (value === null)
    throw new Error("`useAccountantPortalChrome` must be used within a `AccountantPortalChromeContext.Provider`");
  return value;
};

// src/organisms/AccountantPortalChrome/Container.tsx
import { styled as styled59 } from "styled-components";
import { jsx as _jsx902 } from "react/jsx-runtime";
var Container30 = ({
  children
}) => {
  return /* @__PURE__ */ _jsx902(Wrapper3, {
    children
  });
};
var Wrapper3 = styled59.div.withConfig({
  displayName: "Container__Wrapper",
  componentId: "sc-1dhecuq-0"
})(["background:", ";"], colors.surface.neutral);

// src/organisms/AccountantPortalChrome/Content.ts
import { styled as styled60 } from "styled-components";
var Content4 = styled60.div.withConfig({
  displayName: "Content",
  componentId: "sc-1sry3uy-0"
})(["flex:1 1 auto;min-width:1px;min-height:100%;background:", ";border-radius:12px 0 0 0;box-shadow:", ";"], colors.surface.base, shadows.high);

// src/organisms/AccountantPortalChrome/Customer/Container.ts
import { styled as styled61 } from "styled-components";
var Container31 = styled61.div.withConfig({
  displayName: "Container",
  componentId: "sc-1kxen0s-0"
})(["", "{display:flex;}"], media.tablet);

// src/organisms/AccountantPortalChrome/Customer/Content.ts
import { styled as styled62 } from "styled-components";
var Content5 = styled62.div.withConfig({
  displayName: "Content",
  componentId: "sc-xh2xsf-0"
})(["", "{overflow-y:auto;position:absolute;top:0;left:0;right:0;bottom:0;border-radius:", " 0 0 0;}"], media.tablet, radius.large);

// src/organisms/AccountantPortalChrome/Customer/ContentContainer.ts
import { styled as styled63 } from "styled-components";
var ContentContainer = styled63.div.withConfig({
  displayName: "ContentContainer",
  componentId: "sc-lqx7vn-0"
})(["", "{flex:1 1 auto;position:relative;min-height:calc(100vh - 60px);border-radius:", " 0 0 0;overflow:hidden;}"], media.tablet, radius.large);

// src/organisms/AccountantPortalChrome/Customer/Nav/Container.tsx
import { useRef as useRef7 } from "react";
import { styled as styled64 } from "styled-components";
import { hexToRgbA as hexToRgbA9, useResizeObserver as useResizeObserver2 } from "@scouts/helpers";
import { jsx as _jsx903 } from "react/jsx-runtime";
var NAV_ITEM_AVG_WIDTH = 135;
var NAV_TITLE_WIDTH = 180;
var Container32 = ({
  children
}) => {
  const isDesktop = useIsDesktop();
  const {
    setVisibleCustomerMenuItemsCount
  } = useAccountantPortalChrome();
  const contentRef = useRef7(null);
  useResizeObserver2(contentRef, () => {
    const content = contentRef.current?.clientWidth ? contentRef.current?.clientWidth : 0;
    const navAreaWidth = isDesktop ? content : content - NAV_TITLE_WIDTH;
    const visibleItems = Math.floor(navAreaWidth / NAV_ITEM_AVG_WIDTH);
    setVisibleCustomerMenuItemsCount(visibleItems);
  });
  return /* @__PURE__ */ _jsx903(Wrapper4, {
    ref: contentRef,
    children
  });
};
var Wrapper4 = styled64.div.withConfig({
  displayName: "Container__Wrapper",
  componentId: "sc-fbhcxp-0"
})(["padding:12px;", "{display:flex;justify-content:flex-end;align-items:center;position:sticky;top:0;z-index:101;width:100%;height:60px;padding:9px 36px;background:", ";backdrop-filter:blur(9px);border-radius:", " 0 0 0;}", "{justify-content:center;padding:9px 48px;background:", ";}"], media.tablet, hexToRgbA9(colors.surface.base, 0.95), radius.large, media.desktopLarge, hexToRgbA9(colors.surface.base, 0.85));

// src/organisms/AccountantPortalChrome/Customer/Nav/Item.ts
import { styled as styled65 } from "styled-components";
var Item = styled65.div.withConfig({
  displayName: "Item",
  componentId: "sc-b5fhf0-0"
})(["color:", ";font-size:", ";font-weight:", ";text-decoration:none;display:block;padding:12px;border-radius:", ";", " &.is-active{background:", ";}"], colors.text.base, font.body.medium, font.weight.medium, radius.medium, tablet`
        display: flex;
        align-items: center;
        height: 100%;
        padding: 12px 21px;

        &:hover {
            color: ${colors.text.interactive};
        }
    `, colors.surface.neutral);

// src/organisms/AccountantPortalChrome/Customer/Nav/Items.tsx
import { styled as styled66 } from "styled-components";
import { hexToRgbA as hexToRgbA10 } from "@scouts/helpers";
import { jsx as _jsx904 } from "react/jsx-runtime";
var Items = ({
  children
}) => {
  return /* @__PURE__ */ _jsx904(Container33, {
    children
  });
};
var Container33 = styled66.div.withConfig({
  displayName: "Items__Container",
  componentId: "sc-x3tlnu-0"
})(["position:relative;z-index:2;display:flex;justify-content:flex-end;align-items:center;background-color:", ";", "{background-color:transparent;}&:before{content:'';position:absolute;left:-120px;display:block;width:120px;height:60px;background:linear-gradient(to right,", ",", ");pointer-events:none;", "{display:none;}}"], colors.surface.base, media.desktopLarge, hexToRgbA10(colors.surface.base, 0), colors.surface.base, media.desktopLarge);

// src/organisms/AccountantPortalChrome/Customer/Nav/Secondary.tsx
import { useMemo as useMemo2 } from "react";
import { useHistory } from "react-router-dom";
import { jsx as _jsx905 } from "react/jsx-runtime";
var Secondary = ({
  items
}) => {
  const {
    push
  } = useHistory();
  const actions = useMemo2(() => items?.filter(({
    shouldHide
  }) => !shouldHide).map(({
    title,
    path
  }) => {
    return {
      title,
      onClick: () => push(path || "")
    };
  }), [items, push]);
  if (!items || !actions || actions?.length === 0)
    return null;
  return /* @__PURE__ */ _jsx905(Spacer, {
    marginLeft: "12px",
    children: /* @__PURE__ */ _jsx905(DropdownButton, {
      alignRight: true,
      actions
    })
  });
};

// src/organisms/AccountantPortalChrome/Customer/Nav/Title.tsx
import { styled as styled67 } from "styled-components";
import { jsx as _jsx906 } from "react/jsx-runtime";
var Title = ({
  children,
  onClick
}) => {
  const isTablet = useIsTablet();
  const isDesktopLarge = useIsDesktopLarge();
  if (!isTablet || isDesktopLarge)
    return null;
  return /* @__PURE__ */ _jsx906(Container34, {
    children: /* @__PURE__ */ _jsx906(ButtonLink, {
      onClick,
      children
    })
  });
};
var Container34 = styled67.div.withConfig({
  displayName: "Title__Container",
  componentId: "sc-1vt8k6v-0"
})(["position:absolute;left:36px;"]);

// src/organisms/AccountantPortalChrome/Customer/Nav/index.ts
var Nav = {
  Container: Container32,
  Items,
  Item,
  Title,
  Secondary
};

// src/organisms/AccountantPortalChrome/Customer/Page/Container.tsx
import { styled as styled68 } from "styled-components";
import { jsx as _jsx907 } from "react/jsx-runtime";
var Container35 = ({
  children,
  backgroundColor,
  isFullWidth,
  padding,
  tabletPadding,
  desktopPadding,
  desktopLargePadding,
  radius: radius2
}) => /* @__PURE__ */ _jsx907(Wrapper5, {
  backgroundColor,
  padding,
  tabletPadding,
  desktopPadding,
  desktopLargePadding,
  radius: radius2,
  children: /* @__PURE__ */ _jsx907(Content6, {
    isFullWidth,
    children
  })
});
var Wrapper5 = styled68.div.withConfig({
  displayName: "Container__Wrapper",
  componentId: "sc-10cgegf-0"
})(["padding:", ";background:", ";border-radius:", ";", "{padding:", ";}", "{padding:", ";}", "{padding:", ";}"], ({
  padding
}) => padding || "24px 24px 318px", ({
  backgroundColor
}) => backgroundColor || "white", ({
  radius: radius2
}) => radius2 || 0, media.tablet, ({
  tabletPadding
}) => tabletPadding || "0 36px 40vh", media.desktop, ({
  desktopPadding,
  tabletPadding
}) => desktopPadding || tabletPadding || "0 36px 40vh", media.desktopLarge, ({
  desktopLargePadding,
  desktopPadding,
  tabletPadding
}) => desktopLargePadding || desktopPadding || tabletPadding || "0 48px 40vh");
var Content6 = styled68.div.withConfig({
  displayName: "Container__Content",
  componentId: "sc-10cgegf-1"
})(["", "{max-width:", ";margin:0 auto;}"], media.tablet, ({
  isFullWidth
}) => isFullWidth ? "100%" : "1280px");

// src/organisms/AccountantPortalChrome/Customer/Page/Header.ts
import { styled as styled69 } from "styled-components";
import { hexToRgbA as hexToRgbA11 } from "@scouts/helpers";
var Header = styled69.div.withConfig({
  displayName: "Header",
  componentId: "sc-1lbg4ha-0"
})(["margin-bottom:30px;display:flex;gap:18px;flex-direction:column;", "{flex-direction:row;align-items:center;justify-content:space-between;width:100%;min-height:60px;margin-bottom:24px;", "}"], media.tablet, ({
  isSticky
}) => isSticky && `
            position: sticky;
            top: 60px;
            z-index: 100;
            margin-bottom: 0;
            backdrop-filter: blur(9px);
            background: ${hexToRgbA11(colors.surface.base, 0.85)};
        `);

// src/organisms/AccountantPortalChrome/Customer/Page/HeaderGroup.ts
import { styled as styled70 } from "styled-components";
var HeaderGroup = styled70.div.withConfig({
  displayName: "HeaderGroup",
  componentId: "sc-1z097qt-0"
})(["display:flex;justify-content:flex-start;align-items:center;gap:12px;", "{flex:1 1 auto;gap:24px;&:last-child{justify-content:flex-end;}}"], media.tablet);

// src/organisms/AccountantPortalChrome/Customer/Page/SubTitle.ts
import { styled as styled71 } from "styled-components";
var SubTitle = styled71.div.withConfig({
  displayName: "SubTitle",
  componentId: "sc-h2gavf-0"
})(["font-size:", ";font-weight:", ";", "{font-size:", ";flex:0 0 auto;}"], font.title.medium, font.weight.medium, media.tablet, font.title.large);

// src/organisms/AccountantPortalChrome/Customer/Page/Title.ts
import { styled as styled72 } from "styled-components";
var Title2 = styled72.div.withConfig({
  displayName: "Title",
  componentId: "sc-1jtx3xq-0"
})(["font-size:", ";font-weight:", ";", ""], font.title.large, font.weight.medium, tablet`
        font-size: ${font.title.xlarge};
        flex: 0 0 auto;
    `);

// src/organisms/AccountantPortalChrome/Customer/Page/index.ts
var Page = {
  Container: Container35,
  Header,
  HeaderGroup,
  Title: Title2,
  SubTitle
};

// src/organisms/AccountantPortalChrome/Customer/Sidebar/Container.ts
import { styled as styled73 } from "styled-components";
var Container36 = styled73.div.withConfig({
  displayName: "Container",
  componentId: "sc-1olqf32-0"
})(["", "{border-radius:", " 0 0 0;display:flex;display:", ";flex-direction:column;position:absolute;top:60px;width:360px;max-height:calc(100vh - 60px);background-color:", ";height:", ";z-index:", ";box-shadow:", ";}", "{display:flex;position:sticky;box-shadow:none;}"], media.tablet, radius.large, ({
  tabletIsVisible
}) => tabletIsVisible ? "flex" : "none", colors.surface.base, ({
  tabletIsVisible
}) => tabletIsVisible ? "100%" : "auto", ({
  tabletIsVisible
}) => tabletIsVisible ? 102 : "inherit", shadows.medium, media.desktopLarge);

// src/organisms/AccountantPortalChrome/Customer/Sidebar/Content.ts
import { styled as styled74 } from "styled-components";
var Content7 = styled74.div.withConfig({
  displayName: "Content",
  componentId: "sc-1perfsh-0"
})(["", "{overflow-y:auto;flex:1 1 auto;}"], media.tablet);

// src/organisms/AccountantPortalChrome/Customer/Sidebar/Nav.ts
import { styled as styled75 } from "styled-components";
var Nav2 = styled75.div.withConfig({
  displayName: "Nav",
  componentId: "sc-13rxx2f-0"
})(["display:flex;justify-content:center;"]);

// src/organisms/AccountantPortalChrome/Customer/Sidebar/NavItem.ts
import { styled as styled76 } from "styled-components";
var NavItem = styled76.div.withConfig({
  displayName: "NavItem",
  componentId: "sc-nhmc6y-0"
})(["display:flex;align-items:center;justify-content:center;position:relative;width:60px;height:60px;border-bottom:1px solid ", ";&.is-active{border-bottom:1px solid ", ";}"], colors.line.neutralSoft, colors.line.base);

// src/organisms/AccountantPortalChrome/Customer/Sidebar/NavItemBadge.ts
import { styled as styled77 } from "styled-components";
var NavItemBadge = styled77.div.withConfig({
  displayName: "NavItemBadge",
  componentId: "sc-1xoyn2i-0"
})(["position:absolute;top:11px;right:8px;"]);

// src/organisms/AccountantPortalChrome/Customer/Sidebar/Profile/Container.ts
import { styled as styled78 } from "styled-components";
var Container37 = styled78.div.withConfig({
  displayName: "Container",
  componentId: "sc-sjrilm-0"
})(["padding:0 0 24px;"]);

// src/organisms/AccountantPortalChrome/Customer/Sidebar/Profile/Field.ts
import { styled as styled79 } from "styled-components";
var Field = styled79.div.withConfig({
  displayName: "Field",
  componentId: "sc-4xlv2p-0"
})(["&:not(:last-child){margin-bottom:15px;}"]);

// src/organisms/AccountantPortalChrome/Customer/Sidebar/Profile/FieldLabel.ts
import { styled as styled80 } from "styled-components";
var FieldLabel = styled80.div.withConfig({
  displayName: "FieldLabel",
  componentId: "sc-11gc6x7-0"
})(["color:", ";font-size:", ";line-height:1.3;margin-bottom:6px;a{color:", ";text-decoration:none;&:hover{color:", ";text-decoration:underline;}}"], colors.text.neutral, font.body.small, colors.text.neutral, colors.text.neutral);

// src/organisms/AccountantPortalChrome/Customer/Sidebar/Profile/Fieldset.ts
import { styled as styled81 } from "styled-components";
var Fieldset3 = styled81.div.withConfig({
  displayName: "Fieldset",
  componentId: "sc-91jg9d-0"
})(["padding:18px 0;margin:0 24px;background-color:", ";border-top:1px solid ", ";"], colors.surface.base, colors.line.neutralSoft);

// src/organisms/AccountantPortalChrome/Customer/Sidebar/Profile/FieldValue.ts
import { styled as styled82 } from "styled-components";
var FieldValue = styled82.div.withConfig({
  displayName: "FieldValue",
  componentId: "sc-18oo1nv-0"
})(["font-size:", ";line-height:1.3;white-space:pre-line;position:relative;&:not(:last-child){margin-bottom:6px;}"], font.body.medium);

// src/organisms/AccountantPortalChrome/Customer/Sidebar/Profile/Head.ts
import { styled as styled83 } from "styled-components";
import { hexToRgbA as hexToRgbA12 } from "@scouts/helpers";
var Head = styled83.div.withConfig({
  displayName: "Head",
  componentId: "sc-10c316r-0"
})(["padding:18px 24px;background-color:", ";", "{position:sticky;top:0;z-index:1;background:", ";backdrop-filter:blur(9px);}"], colors.surface.base, media.tablet, hexToRgbA12(colors.surface.base, 0.85));

// src/organisms/AccountantPortalChrome/Customer/Sidebar/Profile/index.ts
var Profile = {
  Container: Container37,
  Field,
  FieldLabel,
  Fieldset: Fieldset3,
  FieldValue,
  Head
};

// src/organisms/AccountantPortalChrome/Customer/Sidebar/index.ts
var Sidebar = {
  Container: Container36,
  Content: Content7,
  Nav: Nav2,
  NavItem,
  NavItemBadge,
  Profile
};

// src/organisms/AccountantPortalChrome/Customer/index.ts
var Customer = {
  Container: Container31,
  Content: Content5,
  ContentContainer,
  Nav,
  Page,
  Sidebar
};

// src/organisms/AccountantPortalChrome/Header.ts
import { styled as styled84 } from "styled-components";
var Header2 = styled84.div.withConfig({
  displayName: "Header",
  componentId: "sc-14sxxzg-0"
})(["display:flex;align-items:center;justify-content:flex-end;width:calc(100% - 60px);height:60px;z-index:1000;padding:0;", "{padding:0 24px;justify-content:space-between;}"], media.tablet);

// src/organisms/AccountantPortalChrome/HeaderGroup.ts
import { styled as styled85 } from "styled-components";
var HeaderGroup2 = styled85.div.withConfig({
  displayName: "HeaderGroup",
  componentId: "sc-11ctya2-0"
})(["display:flex;align-items:center;gap:12px;", " ", "{gap:24px;height:100%;", " ", "}"], ({
  isDesktopOnly
}) => isDesktopOnly && `
            display: none;
        `, media.tablet, ({
  isMobileOnly
}) => isMobileOnly && `
            display: none;
        `, ({
  isDesktopOnly
}) => isDesktopOnly && `
            display: flex;
        `);

// src/organisms/AccountantPortalChrome/Logo.tsx
import { styled as styled86 } from "styled-components";
import { jsx as _jsx908 } from "react/jsx-runtime";
var Logo2 = ({
  onClick
}) => /* @__PURE__ */ _jsx908(Container38, {
  children: /* @__PURE__ */ _jsx908(Logomark, {
    onClick
  })
});
var Container38 = styled86.div.withConfig({
  displayName: "Logo__Container",
  componentId: "sc-i7lf7e-0"
})(["height:60px;width:60px;padding:18px;"]);

// src/organisms/AccountantPortalChrome/Main.ts
import { styled as styled87 } from "styled-components";
var Main = styled87.div.withConfig({
  displayName: "Main",
  componentId: "sc-1n6j464-0"
})(["display:flex;min-height:calc(100vh - 60px);background-color:", ";"], colors.surface.neutral);

// src/organisms/AccountantPortalChrome/MainMenu.tsx
import { styled as styled88 } from "styled-components";
import { jsx as _jsx909 } from "react/jsx-runtime";
var MainMenu = ({
  items,
  itemsAs
}) => /* @__PURE__ */ _jsx909(Container39, {
  children: items.map(({
    path,
    title,
    url,
    className
  }) => {
    if (url) {
      return /* @__PURE__ */ _jsx909(ItemExternal, {
        href: url,
        target: "_blank",
        rel: "noreferrer noopener",
        children: title
      }, url);
    }
    return /* @__PURE__ */ _jsx909(Item2, {
      as: itemsAs,
      to: path || "",
      className,
      children: title
    }, path);
  })
});
var Container39 = styled88.div.withConfig({
  displayName: "MainMenu__Container",
  componentId: "sc-ckejt1-0"
})(["display:flex;gap:36px;height:100%;"]);
var Item2 = styled88.div.withConfig({
  displayName: "MainMenu__Item",
  componentId: "sc-ckejt1-1"
})(["color:", ";font-size:", ";font-weight:", ";text-decoration:none;display:flex;align-items:center;border-bottom:1px solid transparent;&.active{border-bottom:1px solid ", ";}", "{&:hover{color:", ";}}"], colors.text.base, font.body.medium, font.weight.medium, colors.line.base, media.tablet, colors.text.interactive);
var ItemExternal = styled88.a.withConfig({
  displayName: "MainMenu__ItemExternal",
  componentId: "sc-ckejt1-2"
})(["color:", ";font-size:", ";font-weight:", ";text-decoration:none;display:flex;align-items:center;border-bottom:1px solid transparent;&:hover{opacity:0.75;}"], colors.text.interactive, font.body.medium, font.weight.medium);

// src/organisms/AccountantPortalChrome/MainMenuSecondary.tsx
import { useMemo as useMemo3 } from "react";
import { useHistory as useHistory2 } from "react-router-dom";
import { jsx as _jsx910 } from "react/jsx-runtime";
var MainMenuSecondary = ({
  items
}) => {
  const {
    push
  } = useHistory2();
  const actions = useMemo3(() => items?.map(({
    title,
    path,
    url,
    color
  }) => {
    if (url) {
      return {
        title,
        onClick: () => window.open(url, "_blank"),
        color
      };
    }
    return {
      title,
      onClick: () => push(path || ""),
      color
    };
  }), [items, push]);
  if (!items || !actions || actions?.length === 0)
    return null;
  return /* @__PURE__ */ _jsx910(DropdownButton, {
    alignRight: true,
    actions
  });
};

// src/organisms/AccountantPortalChrome/Sidebar.ts
import { styled as styled89 } from "styled-components";
var Sidebar2 = styled89.div.withConfig({
  displayName: "Sidebar",
  componentId: "sc-bryboi-0"
})(["display:none;flex:0 0 60px;width:60px;", "{display:block;}"], media.tablet);

// src/organisms/AccountantPortalChrome/SidebarContent.ts
import { styled as styled90 } from "styled-components";
var SidebarContent = styled90.div.withConfig({
  displayName: "SidebarContent",
  componentId: "sc-19feipo-0"
})(["display:flex;flex-direction:column;justify-content:space-between;height:calc(100% - 60px);position:fixed;bottom:0;"]);

// src/organisms/AccountantPortalChrome/SidebarGroup.ts
import { styled as styled91 } from "styled-components";
var SidebarGroup = styled91.div.withConfig({
  displayName: "SidebarGroup",
  componentId: "sc-2nso1v-0"
})(["&:only-child{margin-top:auto;}"]);

// src/organisms/AccountantPortalChrome/SidebarMenuItem.tsx
import { useRouteMatch } from "react-router-dom";
import { styled as styled92 } from "styled-components";
import { jsx as _jsx911 } from "react/jsx-runtime";
var SidebarMenuItem = ({
  IconComponent,
  path,
  title,
  as
}) => {
  const isActive = useRouteMatch(path);
  return /* @__PURE__ */ _jsx911(Item3, {
    as,
    to: path,
    title,
    children: /* @__PURE__ */ _jsx911(Icon, {
      Component: IconComponent,
      color: isActive ? colors.text.base : colors.text.neutral,
      hoverColor: colors.text.interactive,
      width: "100%",
      height: "100%"
    })
  });
};
var Item3 = styled92.div.withConfig({
  displayName: "SidebarMenuItem__Item",
  componentId: "sc-1b2zme8-0"
})(["display:flex;align-items:center;justify-content:center;position:relative;width:60px;height:60px;"]);

// src/organisms/AccountantPortalChrome/SidebarMenu.tsx
import { jsx as _jsx912, Fragment as _Fragment7 } from "react/jsx-runtime";
var SidebarMenu = ({
  items,
  itemsAs
}) => /* @__PURE__ */ _jsx912(_Fragment7, {
  children: items.filter(({
    shouldHide
  }) => !shouldHide).map(({
    path,
    title,
    IconComponent
  }) => /* @__PURE__ */ _jsx912(SidebarMenuItem, {
    as: itemsAs,
    path,
    title,
    IconComponent
  }, path))
});

// src/organisms/AccountantPortalChrome/SidebarMenuAction.tsx
import { styled as styled93 } from "styled-components";
import { jsx as _jsx913 } from "react/jsx-runtime";
var SidebarMenuAction = ({
  IconComponent,
  onClick,
  title,
  isActive
}) => {
  return /* @__PURE__ */ _jsx913(Item4, {
    onClick,
    title,
    children: /* @__PURE__ */ _jsx913(Icon, {
      Component: IconComponent,
      color: isActive ? colors.text.base : colors.text.neutral,
      hoverColor: colors.text.interactive,
      width: "100%",
      height: "100%"
    })
  });
};
var Item4 = styled93.button.withConfig({
  displayName: "SidebarMenuAction__Item",
  componentId: "sc-r0x1f4-0"
})(["display:flex;align-items:center;justify-content:center;position:relative;width:60px;height:60px;"]);

// src/organisms/AccountantPortalChrome/Top.tsx
import { useRef as useRef8 } from "react";
import { styled as styled94 } from "styled-components";
import { useResizeObserver as useResizeObserver3 } from "@scouts/helpers";
import { jsx as _jsx914 } from "react/jsx-runtime";
var NAV_ITEM_AVG_WIDTH2 = 120;
var RESERVED_AREA_WIDTH = 500 + 60 + 48;
var Top = ({
  children
}) => {
  const {
    setVisibleMainMenuItemsCount
  } = useAccountantPortalChrome();
  const contentRef = useRef8(null);
  useResizeObserver3(contentRef, () => {
    const content = contentRef.current?.clientWidth ? contentRef.current?.clientWidth : 0;
    const navAreaWidth = content - RESERVED_AREA_WIDTH;
    const visibleItems = Math.floor(navAreaWidth / NAV_ITEM_AVG_WIDTH2);
    setVisibleMainMenuItemsCount(visibleItems);
  });
  return /* @__PURE__ */ _jsx914(Wrapper6, {
    ref: contentRef,
    children
  });
};
var Wrapper6 = styled94.div.withConfig({
  displayName: "Top__Wrapper",
  componentId: "sc-nxrkuc-0"
})(["display:flex;width:100%;height:60px;"]);

// src/organisms/AccountantPortalChrome/UserMenu.tsx
import { useMemo as useMemo4 } from "react";
import { useHistory as useHistory3 } from "react-router-dom";
import { styled as styled95 } from "styled-components";
import { jsx as _jsx915, jsxs as _jsxs61 } from "react/jsx-runtime";
var UserMenu = ({
  items,
  imageUrl,
  buttonLabel = "Menu"
}) => {
  const {
    push
  } = useHistory3();
  const actions = useMemo4(() => items.filter(({
    shouldHide
  }) => !shouldHide).map(({
    title,
    path
  }) => ({
    title,
    onClick: () => push(path)
  })), [items, push]);
  return /* @__PURE__ */ _jsx915(DropdownButton, {
    alignRight: true,
    actions,
    children: /* @__PURE__ */ _jsxs61(Flexer, {
      gap: "9px",
      alignCenter: true,
      children: [imageUrl && /* @__PURE__ */ _jsx915(Image2, {
        alt: buttonLabel,
        src: imageUrl
      }), buttonLabel]
    })
  });
};
var Image2 = styled95.img.withConfig({
  displayName: "UserMenu__Image",
  componentId: "sc-131ppc4-0"
})(["border-radius:", ";height:36px;width:36px;display:block;overflow:hidden;box-shadow:", ";"], radius.full, shadows.medium);

// src/organisms/AccountantPortalChrome/AccountantPortalChrome.tsx
import { jsx as _jsx916 } from "react/jsx-runtime";
var AccountantPortalChrome = ({
  children
}) => {
  const [isCustomerSidebarVisible, setIsCustomerSidebarVisible] = useState11(false);
  const [visibleCustomerMenuItemsCount, setVisibleCustomerMenuItemsCount] = useState11(100);
  const [visibleMainMenuItemsCount, setVisibleMainMenuItemsCount] = useState11(100);
  const value = {
    visibleCustomerMenuItemsCount,
    setVisibleCustomerMenuItemsCount,
    visibleMainMenuItemsCount,
    setVisibleMainMenuItemsCount,
    isCustomerSidebarVisible,
    setIsCustomerSidebarVisible
  };
  return /* @__PURE__ */ _jsx916(AccountantPortalChromeContext.Provider, {
    value,
    children
  });
};
AccountantPortalChrome.Container = Container30;
AccountantPortalChrome.Content = Content4;
AccountantPortalChrome.Customer = Customer;
AccountantPortalChrome.Header = Header2;
AccountantPortalChrome.HeaderGroup = HeaderGroup2;
AccountantPortalChrome.HeaderGroup = HeaderGroup2;
AccountantPortalChrome.Logo = Logo2;
AccountantPortalChrome.Main = Main;
AccountantPortalChrome.MainMenu = MainMenu;
AccountantPortalChrome.MainMenuSecondary = MainMenuSecondary;
AccountantPortalChrome.Sidebar = Sidebar2;
AccountantPortalChrome.SidebarContent = SidebarContent;
AccountantPortalChrome.SidebarGroup = SidebarGroup;
AccountantPortalChrome.SidebarMenu = SidebarMenu;
AccountantPortalChrome.Top = Top;
AccountantPortalChrome.UserMenu = UserMenu;
AccountantPortalChrome.SidebarMenuAction = SidebarMenuAction;

// src/organisms/AccountantPortalSearchFilter/AccountantPortalSearchFilter.tsx
import { cloneElement, useRef as useRef9, Children } from "react";
import { styled as styled110 } from "styled-components";
import useOnClickOutside4 from "use-onclickoutside";
import { getClickedOnScrollbar, handleKeyDownEvent as handleKeyDownEvent7, useToggle } from "@scouts/helpers";

// src/organisms/AccountantPortalSearchFilter/AccountantPortalSearchFilterDropdown.tsx
import { styled as styled96 } from "styled-components";
import { jsx as _jsx917 } from "react/jsx-runtime";
var AccountantPortalSearchFilterDropdown = ({
  children
}) => /* @__PURE__ */ _jsx917(Container40, {
  children
});
var Container40 = styled96.div.withConfig({
  displayName: "AccountantPortalSearchFilterDropdown__Container",
  componentId: "sc-qqfxpw-0"
})(["position:absolute;top:6px;left:0;z-index:1000;min-width:240px;padding:18px;border-radius:", ";background:", ";box-shadow:", ";"], radius.medium, colors.surface.neutral, shadows.medium);

// src/organisms/AccountantPortalSearchFilter/FilterCheckboxes/FilterCheckboxes.tsx
import { useEffect as useEffect5, useState as useState12 } from "react";

// src/organisms/Form/Actions.tsx
import { styled as styled97 } from "styled-components";
var Actions = styled97.div.withConfig({
  displayName: "Actions",
  componentId: "sc-1jkfoq0-0"
})(["display:flex;align-items:center;justify-content:", ";padding:", ";border-radius:", ";", "{padding:", ";}", ";&:not(:first-child){margin-top:", ";}", "{&:not(:first-child){margin-top:", ";}}&:last-child{flex:1 0 auto;}&:not(:last-child){margin-bottom:24px;}", "{&:not(:last-child){margin:0;}}> :not(:first-child){margin-left:12px;}"], ({
  alignRight
}) => alignRight ? "flex-end" : "flex-start", ({
  background
}) => background ? "18px" : "0", radius.medium, media.tablet, ({
  background
}) => background ? "24px 36px" : "0", ({
  background
}) => background && `background: ${background};`, ({
  background
}) => background ? "24px" : "12px", media.tablet, ({
  background
}) => background ? "36px" : "12px", media.tablet);
Actions.displayName = "Form.Actions";

// src/organisms/Form/Column/Column.tsx
import { styled as styled100 } from "styled-components";

// src/organisms/Form/Column/Content.tsx
import { styled as styled98 } from "styled-components";
var Content8 = styled98.div.withConfig({
  displayName: "Content",
  componentId: "sc-1aqiwj4-0"
})([""]);
Content8.displayName = "Form.Column.Content";

// src/organisms/Form/Column/Title.tsx
import { styled as styled99 } from "styled-components";
import { jsx as _jsx918 } from "react/jsx-runtime";
var Title3 = ({
  children,
  ...props
}) => /* @__PURE__ */ _jsx918(Container41, {
  ...props,
  children
});
var Container41 = styled99.div.withConfig({
  displayName: "Title__Container",
  componentId: "sc-rlglef-0"
})(["line-height:1.3;display:block;width:100%;margin-bottom:12px;", "{display:flex;align-items:center;justify-content:space-between;}"], media.tablet);
var Required = styled99.span.withConfig({
  displayName: "Title__Required",
  componentId: "sc-rlglef-1"
})(["&:after{content:'", "';color:", ";margin-right:auto;margin-left:4px;}"], ({
  label = "(required)"
}) => label, colors.text.neutral);
Title3.displayName = "Form.Column.Title";
Title3.Required = Required;
Title3.Required.displayName = "Form.Column.Title.Required";

// src/organisms/Form/Column/Column.tsx
import { jsx as _jsx919 } from "react/jsx-runtime";
var Column = ({
  children,
  alignSelfCenter,
  width
}) => {
  return /* @__PURE__ */ _jsx919(Container42, {
    alignSelfCenter,
    width,
    children
  });
};
Column.displayName = "Form.Column";
Column.Content = Content8;
Column.Title = Title3;
var Container42 = styled100.div.withConfig({
  displayName: "Column__Container",
  componentId: "sc-uedhms-0"
})(["width:100%;margin-bottom:12px;", " ", "{width:", ";margin-right:0;margin-bottom:0;}&:last-child{margin-bottom:0;}"], ({
  alignSelfCenter
}) => alignSelfCenter && `align-self: center;`, media.tablet, ({
  width
}) => width || "100%");

// src/organisms/Form/Columns.tsx
import { styled as styled101 } from "styled-components";
var Columns = styled101.div.withConfig({
  displayName: "Columns",
  componentId: "sc-13eum3v-0"
})(["display:flex;flex-wrap:wrap;justify-content:space-between;"]);
Columns.displayName = "Form.Columns";

// src/organisms/Form/Control.tsx
import { styled as styled102 } from "styled-components";
var Control = styled102.div.withConfig({
  displayName: "Control",
  componentId: "sc-qkbqlu-0"
})(["&:not(:last-child){margin-bottom:18px;}"]);
Control.displayName = "Form.Control";

// src/organisms/Form/InlineControl.tsx
import { styled as styled103 } from "styled-components";
var InlineControl = styled103.div.withConfig({
  displayName: "InlineControl",
  componentId: "sc-suxd85-0"
})(["margin-bottom:9px;", "{display:inline-block;margin-right:18px;margin-bottom:0;}"], media.tablet);
InlineControl.displayName = "Form.InlineControl";

// src/organisms/Form/Row/Row.tsx
import { styled as styled108 } from "styled-components";

// src/organisms/Form/Row/Action.tsx
import { styled as styled104 } from "styled-components";
var Action2 = styled104.div.withConfig({
  displayName: "Action",
  componentId: "sc-1mkdnk7-0"
})(["margin-top:9px;", "{margin-top:0;}"], media.tablet);
Action2.displayName = "Form.Row.Action";

// src/organisms/Form/Row/Content.tsx
import { styled as styled105 } from "styled-components";
var Content9 = styled105.div.withConfig({
  displayName: "Content",
  componentId: "sc-1ykc1q9-0"
})([""]);
Content9.displayName = "Form.Row.Content";

// src/organisms/Form/Row/Description.tsx
import { styled as styled106 } from "styled-components";
var Description = styled106.div.withConfig({
  displayName: "Description",
  componentId: "sc-1x8w18p-0"
})(["font-size:", ";line-height:1.5;margin-top:12px;&:not(:last-child){margin-bottom:12px;}"], font.body.medium);
Description.displayName = "Form.Row.Description";

// src/organisms/Form/Row/Title.tsx
import { styled as styled107 } from "styled-components";
import { jsx as _jsx920 } from "react/jsx-runtime";
var Title4 = ({
  children,
  ...props
}) => /* @__PURE__ */ _jsx920(Container43, {
  ...props,
  children
});
var Container43 = styled107.div.withConfig({
  displayName: "Title__Container",
  componentId: "sc-oxnctl-0"
})(["line-height:1.3;display:block;width:100%;margin-bottom:12px;", "{display:flex;align-items:center;justify-content:space-between;}"], media.tablet);
var Required2 = styled107.span.withConfig({
  displayName: "Title__Required",
  componentId: "sc-oxnctl-1"
})(["&:after{content:'", "';color:", ";margin-right:auto;margin-left:4px;}"], ({
  label = "(required)"
}) => label, colors.text.neutral);
Title4.displayName = "Form.Row.Title";
Title4.Required = Required2;
Title4.Required.displayName = "Form.Row.Title.Required";

// src/organisms/Form/Row/Row.tsx
import { jsx as _jsx921 } from "react/jsx-runtime";
var Row = ({
  children,
  ...props
}) => /* @__PURE__ */ _jsx921(Container44, {
  ...props,
  children
});
Row.displayName = "Form.Row";
Row.Action = Action2;
Row.Content = Content9;
Row.Description = Description;
Row.Title = Title4;
var Container44 = styled108.div.withConfig({
  displayName: "Row__Container",
  componentId: "sc-1o325nx-0"
})(["padding:", ";", "{padding:", ";}&:after,&:before{width:24px;}&:not(:first-child){padding-top:", ";}", "{&:not(:first-child){padding-top:", ";}}&:not(:last-child){padding-bottom:", ";}", "{&:not(:last-child){padding-bottom:", ";}}", ";"], ({
  background
}) => background ? "12px 0" : "0", media.tablet, ({
  background
}) => background ? "18px 0" : "0", ({
  isCompact
}) => isCompact ? "9px" : "12px", media.tablet, ({
  isCompact
}) => isCompact ? "9px" : "18px", ({
  isCompact
}) => isCompact ? "9px" : "12px", media.tablet, ({
  isCompact
}) => isCompact ? "9px" : "18px", ({
  background
}) => background && `
        position: relative;
        /* stacking context for pseudo elements */
        z-index: 0;
        background: ${background};

        &:not(:last-child) {
            margin: 1px 0;
        }

        &:after,
        &:before {
            content: '';
            position: absolute;
            top: 0;
            /* stack pseudo elements below parent */
            z-index: -1;
            height: 100%;
            background: ${background};
        }

        &:after {
            left: 100%;
            border-radius: 0 4px 4px 0;
        }

        &:before {
            right: 100%;
            border-radius: 4px 0 0 4px;
        }
    `);

// src/organisms/Form/ValidationError.tsx
import { jsx as _jsx922, jsxs as _jsxs62 } from "react/jsx-runtime";
var ValidationError = ({
  children
}) => /* @__PURE__ */ _jsxs62(Flexer, {
  gap: "9px",
  alignCenter: true,
  children: [/* @__PURE__ */ _jsx922(Icon, {
    color: theme.error.main,
    Component: IconAlertCircle_default
  }), /* @__PURE__ */ _jsx922(Line, {
    size: font.body.medium,
    color: theme.error.main,
    children
  })]
});
ValidationError.displayName = "Form.ValidationError";

// src/organisms/Form/Form.tsx
import { jsx as _jsx923 } from "react/jsx-runtime";
var Form = ({
  children,
  onSubmit,
  ...rest
}) => {
  const handleSubmit = (event) => {
    event.preventDefault();
    if (onSubmit)
      onSubmit();
  };
  return /* @__PURE__ */ _jsx923("form", {
    ...rest,
    onSubmit: handleSubmit,
    children
  });
};
Form.displayName = "Form";
Form.Actions = Actions;
Form.Column = Column;
Form.Columns = Columns;
Form.Control = Control;
Form.InlineControl = InlineControl;
Form.Row = Row;
Form.ValidationError = ValidationError;

// src/organisms/AccountantPortalSearchFilter/FilterCheckboxes/FilterCheckboxes.tsx
import { jsx as _jsx924, jsxs as _jsxs63 } from "react/jsx-runtime";
var FilterCheckboxes = ({
  name,
  values,
  applyFilter,
  removeFilter,
  closeDropdown,
  applyButtonLabel = "Apply",
  removeButtonLabel = "Remove"
}) => {
  const [localValues, setLocalValues] = useState12({});
  useEffect5(() => {
    const activeItems = {};
    values.filter((o) => o.checked).forEach(({
      value,
      checked
    }) => {
      activeItems[value] = checked;
    });
    setLocalValues(activeItems);
  }, [values]);
  const handleSubmit = () => {
    const possibleValues = Object.keys(localValues);
    const selectedValues = possibleValues.filter((key) => localValues[key]);
    const filter = {
      [name]: selectedValues
    };
    applyFilter(filter);
    if (closeDropdown)
      closeDropdown();
  };
  const handleChange = ({
    value,
    checked
  }) => {
    setLocalValues({
      ...localValues,
      ...{
        [value]: checked
      }
    });
  };
  return /* @__PURE__ */ _jsx924(Form, {
    onSubmit: handleSubmit,
    children: /* @__PURE__ */ _jsxs63(Flexer, {
      column: true,
      gap: "18px",
      children: [/* @__PURE__ */ _jsx924(Flexer, {
        column: true,
        gap: "6px",
        children: values.map(({
          label,
          value
        }) => /* @__PURE__ */ _jsx924(Spacer, {
          children: /* @__PURE__ */ _jsx924(Checkbox, {
            isSmall: true,
            name,
            value,
            label,
            checked: localValues[value] || false,
            onChange: handleChange
          })
        }, label + value))
      }), /* @__PURE__ */ _jsxs63(Flexer, {
        gap: "6px",
        children: [/* @__PURE__ */ _jsx924(Button, {
          type: "submit",
          size: "small",
          children: applyButtonLabel
        }), /* @__PURE__ */ _jsx924(Button, {
          color: "error",
          size: "small",
          onClick: removeFilter,
          children: removeButtonLabel
        })]
      })]
    })
  });
};

// src/organisms/AccountantPortalSearchFilter/FilterDateRange/FilterDateRange.tsx
import { useEffect as useEffect6, useState as useState13 } from "react";
import { formatDateUTC, Locale as Locale5 } from "@scouts/helpers";
import { jsx as _jsx925, jsxs as _jsxs64 } from "react/jsx-runtime";
var FilterDateRange = ({
  dateFromName,
  dateToName,
  dateFrom,
  dateTo,
  applyFilter,
  removeFilter,
  closeDropdown,
  locale = Locale5.GB,
  applyButtonLabel = "Apply",
  removeButtonLabel = "Remove"
}) => {
  const [localDateFrom, setLocalDateFrom] = useState13("");
  const [localDateTo, setLocalDateTo] = useState13("");
  useEffect6(() => {
    setLocalDateFrom(dateFrom);
    setLocalDateTo(dateTo);
  }, [dateFrom, dateTo]);
  const handleSubmit = () => {
    const filter = {
      [dateFromName]: String(localDateFrom),
      [dateToName]: String(localDateTo)
    };
    applyFilter(filter);
    if (closeDropdown)
      closeDropdown();
  };
  const handleChange = (date, set) => {
    if (date) {
      const newDate = formatDateUTC(date, "YYYY-MM-DD");
      if (set === "from")
        setLocalDateFrom(newDate);
      if (set === "to")
        setLocalDateTo(newDate);
    }
  };
  return /* @__PURE__ */ _jsx925(Form, {
    onSubmit: handleSubmit,
    children: /* @__PURE__ */ _jsxs64(Flexer, {
      column: true,
      gap: "18px",
      children: [/* @__PURE__ */ _jsxs64(Flexer, {
        column: true,
        gap: "6px",
        children: [/* @__PURE__ */ _jsx925(DatePicker, {
          locale,
          name: "dateFrom",
          value: localDateFrom,
          onChange: ({
            value
          }) => handleChange(value, "from")
        }), /* @__PURE__ */ _jsx925(DatePicker, {
          locale,
          name: "dateTo",
          value: localDateTo,
          onChange: ({
            value
          }) => handleChange(value, "to")
        })]
      }), /* @__PURE__ */ _jsxs64(Flexer, {
        gap: "6px",
        children: [/* @__PURE__ */ _jsx925(Button, {
          type: "submit",
          size: "small",
          children: applyButtonLabel
        }), /* @__PURE__ */ _jsx925(Button, {
          color: "error",
          size: "small",
          onClick: removeFilter,
          children: removeButtonLabel
        })]
      })]
    })
  });
};

// src/organisms/AccountantPortalSearchFilter/FilterInput/FilterInput.tsx
import { useEffect as useEffect7, useState as useState14 } from "react";
import { jsx as _jsx926, jsxs as _jsxs65 } from "react/jsx-runtime";
var FilterInput = ({
  name,
  value,
  applyFilter,
  removeFilter,
  closeDropdown,
  applyButtonLabel = "Apply",
  removeButtonLabel = "Remove"
}) => {
  const [localValue, setLocalValue] = useState14(value);
  useEffect7(() => {
    setLocalValue(value);
  }, [value]);
  const handleSubmit = () => {
    const filter = {
      [name]: localValue
    };
    applyFilter(filter);
    if (closeDropdown)
      closeDropdown();
  };
  const handleChange = ({
    value: value2
  }) => {
    setLocalValue(value2);
  };
  return /* @__PURE__ */ _jsx926(Form, {
    onSubmit: handleSubmit,
    children: /* @__PURE__ */ _jsxs65(Flexer, {
      column: true,
      gap: "18px",
      children: [/* @__PURE__ */ _jsx926(Input, {
        autoFocus: true,
        name,
        value: localValue,
        onChange: handleChange
      }), /* @__PURE__ */ _jsxs65(Flexer, {
        gap: "6px",
        children: [/* @__PURE__ */ _jsx926(Button, {
          type: "submit",
          size: "small",
          children: applyButtonLabel
        }), /* @__PURE__ */ _jsx926(Button, {
          color: "error",
          size: "small",
          onClick: removeFilter,
          children: removeButtonLabel
        })]
      })]
    })
  });
};

// src/organisms/AccountantPortalSearchFilter/FilterInputs/FilterInputs.tsx
import { useEffect as useEffect8, useState as useState15 } from "react";
import { jsx as _jsx927, jsxs as _jsxs66 } from "react/jsx-runtime";
var FilterInputs = ({
  values,
  applyFilter,
  removeFilter,
  closeDropdown,
  applyButtonLabel = "Apply",
  removeButtonLabel = "Remove"
}) => {
  const [localValues, setLocalValues] = useState15([]);
  useEffect8(() => {
    setLocalValues(values);
  }, [values]);
  const handleSubmit = () => {
    const filter = {};
    localValues.forEach(({
      name,
      value
    }) => {
      filter[name] = value;
    });
    applyFilter(filter);
    if (closeDropdown)
      closeDropdown();
  };
  const handleChange = ({
    name,
    value
  }) => {
    const updatedValues = [...localValues];
    const index = updatedValues.findIndex((item) => item.name === name);
    const typedValue = value;
    if (index > -1) {
      updatedValues[index].value = typedValue;
      setLocalValues(updatedValues);
    }
  };
  return /* @__PURE__ */ _jsx927(Form, {
    onSubmit: handleSubmit,
    children: /* @__PURE__ */ _jsxs66(Flexer, {
      column: true,
      gap: "18px",
      children: [/* @__PURE__ */ _jsx927(Flexer, {
        column: true,
        gap: "6px",
        children: values.map(({
          name,
          label
        }, index) => /* @__PURE__ */ _jsx927(Spacer, {
          children: /* @__PURE__ */ _jsx927(Input, {
            name,
            autoFocus: index === 0,
            value: localValues.find((item) => item.name === name)?.value || "",
            onChange: handleChange,
            placeholder: label
          })
        }, name))
      }), /* @__PURE__ */ _jsxs66(Flexer, {
        gap: "6px",
        children: [/* @__PURE__ */ _jsx927(Button, {
          type: "submit",
          size: "small",
          children: applyButtonLabel
        }), /* @__PURE__ */ _jsx927(Button, {
          color: "error",
          size: "small",
          onClick: removeFilter,
          children: removeButtonLabel
        })]
      })]
    })
  });
};

// src/organisms/AccountantPortalSearchFilter/FilterRadios/FilterRadios.tsx
import { useEffect as useEffect9, useState as useState16 } from "react";
import { styled as styled109 } from "styled-components";
import { fromString } from "@scouts/helpers";
import { jsx as _jsx928, jsxs as _jsxs67 } from "react/jsx-runtime";
var FilterRadios = ({
  name,
  values,
  applyFilter,
  removeFilter,
  closeDropdown,
  applyButtonLabel = "Apply",
  removeButtonLabel = "Remove"
}) => {
  const [localValue, setLocalValue] = useState16(null);
  useEffect9(() => {
    const value = values.find((o) => o.checked)?.value ?? null;
    setLocalValue(String(value));
  }, [values]);
  const handleSubmit = () => {
    const filter = {
      [name]: fromString(localValue)
    };
    applyFilter(filter);
    if (closeDropdown)
      closeDropdown();
  };
  const handleChange = ({
    value
  }) => {
    setLocalValue(value);
  };
  return /* @__PURE__ */ _jsx928(Form, {
    onSubmit: handleSubmit,
    children: /* @__PURE__ */ _jsxs67(Flexer, {
      column: true,
      gap: "18px",
      children: [/* @__PURE__ */ _jsx928(OverflowContainer, {
        children: /* @__PURE__ */ _jsx928(Flexer, {
          column: true,
          gap: "6px",
          children: values.map(({
            label,
            value
          }) => /* @__PURE__ */ _jsx928(Spacer, {
            children: /* @__PURE__ */ _jsx928(Radio, {
              isSmall: true,
              name,
              value: String(value),
              label,
              checked: String(value) === localValue,
              onChange: handleChange
            })
          }, label + value))
        })
      }), /* @__PURE__ */ _jsxs67(Flexer, {
        gap: "6px",
        children: [/* @__PURE__ */ _jsx928(Button, {
          type: "submit",
          size: "small",
          children: applyButtonLabel
        }), /* @__PURE__ */ _jsx928(Button, {
          color: "error",
          size: "small",
          onClick: removeFilter,
          children: removeButtonLabel
        })]
      })]
    })
  });
};
var OverflowContainer = styled109.div.withConfig({
  displayName: "FilterRadios__OverflowContainer",
  componentId: "sc-1ohm36n-0"
})(["max-height:320px;overflow-y:auto;"]);

// src/organisms/AccountantPortalSearchFilter/AccountantPortalSearchFilter.tsx
import { jsx as _jsx929, jsxs as _jsxs68 } from "react/jsx-runtime";
var AccountantPortalSearchFilter = ({
  children,
  label,
  activeValues,
  removeFilter,
  isNeutral,
  isOpenByDefault
}) => {
  const [isDropdownOpen, openDropdown, closeDropdown] = useToggle(isOpenByDefault);
  const closeDropdownOnClick = (e) => {
    if (e instanceof MouseEvent && !getClickedOnScrollbar(e.pageX)) {
      closeDropdown();
    }
  };
  const ref = useRef9(null);
  useOnClickOutside4(ref, closeDropdownOnClick);
  return /* @__PURE__ */ _jsxs68(Wrapper7, {
    children: [/* @__PURE__ */ _jsxs68(Container45, {
      onClick: openDropdown,
      onKeyDown: handleKeyDownEvent7(openDropdown),
      role: "button",
      tabIndex: 0,
      children: [/* @__PURE__ */ _jsx929(Spacer, {
        marginBottom: "9px",
        children: /* @__PURE__ */ _jsx929(Line, {
          size: font.body.medium,
          lineHeight: 1,
          weight: font.weight.medium,
          children: label
        })
      }), /* @__PURE__ */ _jsx929(Values, {
        children: activeValues.map((value) => {
          if (!value)
            return null;
          return /* @__PURE__ */ _jsx929(Chip, {
            size: "small",
            background: isNeutral ? colors.surface.neutral : theme.primary.primaryAction,
            color: isNeutral ? colors.text.base : theme.primary.contrast,
            children: value
          }, value);
        })
      })]
    }), /* @__PURE__ */ _jsx929(DropdownContainer, {
      ref,
      children: isDropdownOpen && /* @__PURE__ */ _jsx929(AccountantPortalSearchFilterDropdown, {
        children: Children.map(children, (child) => /* @__PURE__ */ cloneElement(child, {
          closeDropdown
        }))
      })
    }), removeFilter && /* @__PURE__ */ _jsx929(Remove, {
      onClick: removeFilter,
      onKeyDown: handleKeyDownEvent7(removeFilter),
      role: "button",
      tabIndex: 0,
      children: "\u2715"
    })]
  });
};
AccountantPortalSearchFilter.Checkboxes = FilterCheckboxes;
AccountantPortalSearchFilter.Radios = FilterRadios;
AccountantPortalSearchFilter.DateRange = FilterDateRange;
AccountantPortalSearchFilter.Input = FilterInput;
AccountantPortalSearchFilter.Inputs = FilterInputs;
AccountantPortalSearchFilter.Dropdown = AccountantPortalSearchFilterDropdown;
var Remove = styled110.div.withConfig({
  displayName: "AccountantPortalSearchFilter__Remove",
  componentId: "sc-1tod72b-0"
})(["color:", ";font-size:", ";font-weight:", ";display:none;align-items:center;justify-content:center;position:absolute;top:0;right:0;z-index:2000;width:24px;height:24px;cursor:pointer;", "{&:hover{color:", ";}}"], colors.text.base, font.body.small, font.weight.medium, media.pointer, colors.text.alert);
var Container45 = styled110.div.withConfig({
  displayName: "AccountantPortalSearchFilter__Container",
  componentId: "sc-1tod72b-1"
})(["padding:12px 24px 12px 12px;border-radius:", ";border:1px solid ", ";cursor:pointer;"], radius.medium, colors.line.neutralSoft);
var Wrapper7 = styled110.div.withConfig({
  displayName: "AccountantPortalSearchFilter__Wrapper",
  componentId: "sc-1tod72b-2"
})(["margin-right:6px;position:relative;", "{&:hover{", "{display:flex;}", "{border-color:", ";}}}"], media.pointer, Remove, Container45, colors.line.neutral);
var Values = styled110.div.withConfig({
  displayName: "AccountantPortalSearchFilter__Values",
  componentId: "sc-1tod72b-3"
})(["display:flex;gap:3px;flex-wrap:wrap;max-width:240px;"]);
var DropdownContainer = styled110.div.withConfig({
  displayName: "AccountantPortalSearchFilter__DropdownContainer",
  componentId: "sc-1tod72b-4"
})(["position:relative;top:-12px;left:12px;"]);

// src/organisms/CustomerChrome/Content.ts
import { styled as styled111 } from "styled-components";
var Content10 = styled111.div.withConfig({
  displayName: "Content",
  componentId: "sc-19vc6o0-0"
})(["background:", ";"], colors.surface.base);

// src/organisms/CustomerChrome/Header.ts
import { styled as styled112 } from "styled-components";
var Header3 = styled112.div.withConfig({
  displayName: "Header",
  componentId: "sc-dfj5on-0"
})(["display:flex;align-items:center;justify-content:space-between;position:sticky;top:0;z-index:1000;width:100%;height:60px;padding:0 24px;background:", ";"], colors.surface.base);

// src/organisms/CustomerChrome/HeaderGroup.ts
import { styled as styled113 } from "styled-components";
var HeaderGroup3 = styled113.div.withConfig({
  displayName: "HeaderGroup",
  componentId: "sc-1w7xcuq-0"
})(["display:flex;align-items:center;gap:12px;", " ", "{gap:24px;height:100%;", " ", "}"], ({
  isDesktopOnly
}) => isDesktopOnly && `
            display: none;
        `, media.tablet, ({
  isMobileOnly
}) => isMobileOnly && `
            display: none;
        `, ({
  isDesktopOnly
}) => isDesktopOnly && `
            display: flex;
        `);

// src/organisms/CustomerChrome/Logo.tsx
import { Link } from "react-router-dom";
import { styled as styled114 } from "styled-components";
import { jsx as _jsx930 } from "react/jsx-runtime";
var Logo3 = ({
  href
}) => /* @__PURE__ */ _jsx930(Container46, {
  "data-pw": "header-logo",
  children: href ? /* @__PURE__ */ _jsx930(Link, {
    "aria-label": "Taxfix logo",
    to: href,
    children: /* @__PURE__ */ _jsx930(Logo, {})
  }) : /* @__PURE__ */ _jsx930(Logo, {})
});
var Container46 = styled114.div.withConfig({
  displayName: "Logo__Container",
  componentId: "sc-127jdwl-0"
})(["display:flex;align-items:center;width:84px;height:60px;", "{width:108px;}"], media.tablet);

// src/organisms/CustomerChrome/Main.ts
import { styled as styled115 } from "styled-components";
var Main2 = styled115.div.withConfig({
  displayName: "Main",
  componentId: "sc-ebhzqf-0"
})([""]);

// src/organisms/CustomerChrome/MainMenu.tsx
import { NavLink } from "react-router-dom";
import { styled as styled116 } from "styled-components";
import { jsx as _jsx931, jsxs as _jsxs69 } from "react/jsx-runtime";
var MainMenu2 = ({
  items
}) => /* @__PURE__ */ _jsx931(Container47, {
  children: items.filter(({
    shouldHide
  }) => !shouldHide).map(({
    badge,
    path,
    title,
    url
  }) => {
    if (url) {
      return /* @__PURE__ */ _jsx931(ItemExternal2, {
        href: url,
        target: "_blank",
        rel: "noreferrer noopener",
        children: title
      }, url);
    }
    return /* @__PURE__ */ _jsxs69(Item5, {
      to: path || "",
      children: [title, !!badge && /* @__PURE__ */ _jsx931(ItemBadgeContainer, {
        children: /* @__PURE__ */ _jsx931(ItemBadge, {
          children: badge
        })
      })]
    }, path);
  })
});
var Container47 = styled116.div.withConfig({
  displayName: "MainMenu__Container",
  componentId: "sc-knipiw-0"
})(["display:flex;gap:24px;justify-content:center;height:42px;", "{gap:36px;position:fixed;top:0;left:50%;z-index:1001;height:60px;transform:translateX(-50%);}"], media.tablet);
var Item5 = styled116(NavLink).withConfig({
  displayName: "MainMenu__Item",
  componentId: "sc-knipiw-1"
})(["color:", ";font-size:", ";font-weight:", ";text-decoration:none;display:flex;align-items:center;position:relative;border-bottom:1px solid transparent;&.active{border-bottom:1px solid ", ";}", "{&:hover{color:", ";}}"], colors.text.base, font.body.medium, font.weight.medium, colors.line.base, media.pointer, colors.text.interactive);
var ItemExternal2 = styled116.a.withConfig({
  displayName: "MainMenu__ItemExternal",
  componentId: "sc-knipiw-2"
})(["color:", ";font-size:", ";font-weight:", ";text-decoration:none;display:flex;align-items:center;border-bottom:1px solid transparent;", "{opacity:0.75;}"], colors.text.interactive, font.body.medium, font.weight.medium, media.pointer);
var ItemBadgeContainer = styled116.div.withConfig({
  displayName: "MainMenu__ItemBadgeContainer",
  componentId: "sc-knipiw-3"
})(["white-space:nowrap;position:absolute;top:3px;right:-12px;z-index:1;", "{top:11px;right:-12px;}"], media.tablet);
var ItemBadge = styled116.div.withConfig({
  displayName: "MainMenu__ItemBadge",
  componentId: "sc-knipiw-4"
})(["color:", ";font-size:10px;font-weight:", ";display:inline-flex;align-items:center;justify-content:space-around;height:15px;min-width:15px;padding:0 3px;background:", ";border-radius:", ";"], theme.primary.contrast, font.weight.normal, theme.primary.primaryAction, radius.full);

// src/organisms/CustomerChrome/MessagesLink.tsx
import { Link as Link2 } from "react-router-dom";
import { keyframes as keyframes5, styled as styled117 } from "styled-components";
import { hexToRgbA as hexToRgbA13 } from "@scouts/helpers";
var pulseMessages = keyframes5`
    0% {
        background-color: ${colors.surface.noteStrong};
    }
    50% {
        background-color: ${hexToRgbA13(colors.surface.noteStrong, 0.3)};
    }
    100% {
        background-color: ${colors.surface.noteStrong};
    }
`;
var MessagesLink = styled117(Link2).withConfig({
  displayName: "MessagesLink",
  componentId: "sc-d4x4cp-0"
})(["color:", ";font-size:", ";font-weight:", ";text-decoration:none;align-self:center;padding:4px 6px;background:", ";border-radius:", ";animation-duration:3s;animation-iteration-count:infinite;animation-name:", ";"], colors.text.base, font.body.medium, font.weight.medium, colors.surface.noteStrong, radius.medium, pulseMessages);

// src/organisms/CustomerChrome/ReferralLink.tsx
import { Link as Link3 } from "react-router-dom";
import { keyframes as keyframes6, styled as styled118 } from "styled-components";
import { jsx as _jsx932, jsxs as _jsxs70 } from "react/jsx-runtime";
var pulseReferral = keyframes6`
    50% {
        opacity: 0.3;
    }
`;
var CustomLink = styled118(Link3).withConfig({
  displayName: "ReferralLink__CustomLink",
  componentId: "sc-1xwyp8y-0"
})(["position:relative;color:", ";font-size:", ";font-weight:", ";text-decoration:none;padding:4px 6px;border-radius:", ";"], colors.text.base, font.body.medium, font.weight.medium, radius.medium);
var BackgroundPulse = styled118.div.withConfig({
  displayName: "ReferralLink__BackgroundPulse",
  componentId: "sc-1xwyp8y-1"
})(["position:absolute;z-index:1;top:0;left:0;right:0;bottom:0;border-radius:", ";background:", ";opacity:1;animation-duration:3s;animation-iteration-count:infinite;animation-name:", ";"], radius.medium, colors.surface.accent, pulseReferral);
var TextContainer = styled118.span.withConfig({
  displayName: "ReferralLink__TextContainer",
  componentId: "sc-1xwyp8y-2"
})(["position:relative;z-index:2;"]);
var ReferralLink = (props) => {
  return /* @__PURE__ */ _jsxs70(CustomLink, {
    to: props.to,
    onClick: props.onClick,
    children: [/* @__PURE__ */ _jsx932(BackgroundPulse, {}), /* @__PURE__ */ _jsx932(TextContainer, {
      children: props.children
    })]
  });
};

// src/organisms/CustomerChrome/UserMenu.tsx
import { useMemo as useMemo5 } from "react";
import { useHistory as useHistory4 } from "react-router-dom";
import styled119 from "styled-components";
import { jsx as _jsx933 } from "react/jsx-runtime";
var UserMenu2 = ({
  title,
  items
}) => {
  const {
    push
  } = useHistory4();
  const isDesktopLarge = useIsDesktopLarge();
  const actions = useMemo5(() => items.filter(({
    shouldHide
  }) => !shouldHide).map(({
    title: itemTitle,
    path,
    onClick
  }) => ({
    title: itemTitle,
    onClick: () => {
      push(path);
      if (onClick)
        onClick();
    }
  })), [items, push]);
  return /* @__PURE__ */ _jsx933(DropdownButton, {
    alignRight: true,
    actions,
    "data-pw": "header-usermenu",
    children: isDesktopLarge ? /* @__PURE__ */ _jsx933(OverflowWrapper, {
      children: title
    }) : null
  });
};
var OverflowWrapper = styled119.div.withConfig({
  displayName: "UserMenu__OverflowWrapper",
  componentId: "sc-wht9ih-0"
})(["", "{max-width:120px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;}"], media.tablet);

// src/organisms/CustomerChrome/CustomerChrome.tsx
import { jsx as _jsx934 } from "react/jsx-runtime";
var CustomerChrome = ({
  children
}) => /* @__PURE__ */ _jsx934(Container2, {
  children
});
CustomerChrome.Content = Content10;
CustomerChrome.Header = Header3;
CustomerChrome.HeaderGroup = HeaderGroup3;
CustomerChrome.Logo = Logo3;
CustomerChrome.Main = Main2;
CustomerChrome.MainMenu = MainMenu2;
CustomerChrome.UserMenu = UserMenu2;
CustomerChrome.MessagesLink = MessagesLink;
CustomerChrome.ReferralLink = ReferralLink;

// src/organisms/ContactWidget/InlineAction/InlineAction.tsx
import { styled as styled120 } from "styled-components";
var InlineAction = styled120.button.withConfig({
  displayName: "InlineAction",
  componentId: "sc-cmzc6f-0"
})(["display:block;color:", ";text-decoration:underline;font-weight:", ";word-break:break-all;font-size:", ";line-height:", ";", "{&:hover{color:", ";text-decoration:none;}}"], colors.text.interactive, font.weight.medium, `${themeFonts.typography.bodySmall.fontSize.xs}px`, `${themeFonts.typography.bodySmall.lineHeight.xs}px`, media.pointer, colors.text.base);

// src/organisms/ContactWidget/Line/Line.tsx
import { styled as styled121 } from "styled-components";
var Line3 = styled121.div.withConfig({
  displayName: "Line",
  componentId: "sc-hgdpwk-0"
})(["color:", ";font-weight:", ";font-size:", ";line-height:", ";display:block;&:not(:last-child){margin-right:3px;", "{margin:0 0 6px 0;}}"], theme.text.primary, themeFonts.typography.bodySmall.fontWeight, `${themeFonts.typography.bodySmall.fontSize.xs}px`, `${themeFonts.typography.bodySmall.lineHeight.xs}px`, media.tablet);

// src/organisms/ContactWidget/ContactWidget.tsx
import { jsx as _jsx935, jsxs as _jsxs71 } from "react/jsx-runtime";
var ContactWidget = ({
  action,
  actionLabel,
  children,
  hasBackground = false,
  imageSrc
}) => {
  const buttonProps = {
    isMobileFullWidth: true,
    onClick: action
  };
  return /* @__PURE__ */ _jsx935(Container2, {
    background: hasBackground ? colors.surface.highlight : void 0,
    border: hasBackground ? void 0 : `1px solid ${colors.line.neutralSoft}`,
    radius: radius.large,
    padding: "18px",
    tabletPadding: "24px 36px",
    children: /* @__PURE__ */ _jsxs71(Flexer, {
      column: true,
      tabletRow: true,
      gap: "12px",
      tabletGap: "30px",
      tabletAlignCenter: true,
      spaceBetween: true,
      children: [/* @__PURE__ */ _jsxs71(Flexer, {
        gap: "12px",
        tabletGap: "30px",
        tabletAlignCenter: true,
        justifyStart: true,
        children: [/* @__PURE__ */ _jsx935(Spacer, {
          width: "36px",
          tabletWidth: "72px",
          children: /* @__PURE__ */ _jsx935(Avatar, {
            src: imageSrc,
            width: "36px",
            tabletWidth: "72px"
          })
        }), /* @__PURE__ */ _jsx935(Spacer, {
          children
        })]
      }), !!action && /* @__PURE__ */ _jsx935(Button, {
        variant: "secondary",
        ...buttonProps,
        children: actionLabel
      })]
    })
  });
};
ContactWidget.Line = Line3;
ContactWidget.InlineAction = InlineAction;

// src/organisms/CrossOver/Body/Image/Image.tsx
import { styled as styled122 } from "styled-components";
import { jsx as _jsx936 } from "react/jsx-runtime";
var Image3 = ({
  src,
  background
}) => /* @__PURE__ */ _jsx936(Container2, {
  relative: true,
  paddingBottom: "72px",
  children: /* @__PURE__ */ _jsx936(ImageWrapper, {
    children: /* @__PURE__ */ _jsx936(Avatar, {
      src,
      backgroundColor: background,
      width: "96px"
    })
  })
});
Image3.displayName = "CrossOver.Body.Image";
var ImageWrapper = styled122.div.withConfig({
  displayName: "Image__ImageWrapper",
  componentId: "sc-15ahe1e-0"
})(["top:-48px;position:absolute;"]);

// src/organisms/CrossOver/Body/Body.tsx
import { jsx as _jsx937 } from "react/jsx-runtime";
var Body3 = ({
  children
}) => /* @__PURE__ */ _jsx937(GridItem, {
  children: /* @__PURE__ */ _jsx937(Container2, {
    padding: "0 24px",
    tabletPadding: "0",
    children
  })
});
Body3.displayName = "CrossOver.Body";
Body3.Image = Image3;

// src/organisms/CrossOver/Overlay/Overlay.tsx
import { styled as styled123 } from "styled-components";
import { jsx as _jsx938 } from "react/jsx-runtime";
var Overlay2 = ({
  children
}) => /* @__PURE__ */ _jsx938(GridItem, {
  children: /* @__PURE__ */ _jsx938(Wrapper8, {
    children
  })
});
Overlay2.displayName = "CrossOver.Overlay";
var Wrapper8 = styled123.div.withConfig({
  displayName: "Overlay__Wrapper",
  componentId: "sc-8mv53k-0"
})(["position:relative;padding:24px;box-shadow:", ";border-radius:", ";background:", ";", "{height:calc(100% + 96px);margin:-48px 0;}"], shadows.high, radius.large, colors.surface.base, media.tablet);

// src/organisms/CrossOver/CrossOver.tsx
import { jsx as _jsx939 } from "react/jsx-runtime";
var CrossOver = ({
  children,
  background
}) => /* @__PURE__ */ _jsx939(Container2, {
  padding: "24px 18px 18px",
  tabletPadding: "24px 48px",
  background: background || colors.surface.highlight,
  radius: radius.large,
  children: /* @__PURE__ */ _jsx939(Grid, {
    columns: 1,
    tabletColumns: "1fr 360px",
    gap: "24px",
    children
  })
});
CrossOver.Body = Body3;
CrossOver.Overlay = Overlay2;

// src/organisms/Dialogs/DialogAffirm.tsx
import { styled as styled125 } from "styled-components";

// src/organisms/Dialogs/Dialog/dialog-context/DialogContextProvider.ts
import { createContext as createContext2 } from "react";
var DialogContext = /* @__PURE__ */ createContext2(null);
var DialogContextProvider = DialogContext.Provider;

// src/organisms/Dialogs/Dialog/use-dialog.tsx
import { useDismiss, useFloating, useInteractions, useRole } from "@floating-ui/react";
import { useMemo as useMemo6, useState as useState17 } from "react";
var useDialog = ({
  isOpen,
  onIsOpenChange
} = {}) => {
  const [labelId, setLabelId] = useState17();
  const [descriptionId, setDescriptionId] = useState17();
  const setIsOpen = onIsOpenChange;
  const data = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen
  });
  const context = data.context;
  const dismiss = useDismiss(context, {
    outsidePressEvent: "mousedown"
  });
  const role = useRole(context);
  const interactions = useInteractions([dismiss, role]);
  return useMemo6(() => ({
    isOpen,
    setIsOpen,
    ...interactions,
    ...data,
    labelId,
    descriptionId,
    setLabelId,
    setDescriptionId
  }), [isOpen, setIsOpen, interactions, data, labelId, descriptionId]);
};

// src/organisms/Dialogs/Dialog/Dialog.tsx
import { jsx as _jsx940 } from "react/jsx-runtime";
var Dialog = ({
  children,
  ...options
}) => {
  const dialog = useDialog(options);
  return /* @__PURE__ */ _jsx940(DialogContextProvider, {
    value: dialog,
    children
  });
};

// src/organisms/Dialogs/Dialog/DialogClose.tsx
import { cloneElement as cloneElement2, forwardRef as forwardRef8, isValidElement } from "react";

// src/organisms/Dialogs/Dialog/dialog-context/useDialogContext.ts
import { useContext as useContext2 } from "react";
var useDialogContext = () => {
  const context = useContext2(DialogContext);
  if (context == null) {
    throw new Error("Dialog components must be wrapped in <Dialog />");
  }
  return context;
};

// src/organisms/Dialogs/Dialog/DialogClose.tsx
import { jsx as _jsx941 } from "react/jsx-runtime";
var DialogClose = /* @__PURE__ */ forwardRef8(function DialogClose2({
  children,
  asChild = false
}, ref) {
  const {
    getReferenceProps,
    setIsOpen
  } = useDialogContext();
  const handleClose = () => {
    if (!setIsOpen)
      return;
    setIsOpen(false);
  };
  if (asChild && /* @__PURE__ */ isValidElement(children)) {
    return /* @__PURE__ */ cloneElement2(children, getReferenceProps({
      ref,
      ...children.props,
      onClick: handleClose
    }));
  }
  return /* @__PURE__ */ _jsx941("button", {
    type: "button",
    ref,
    onClick: handleClose,
    children
  });
});

// src/organisms/Dialogs/Dialog/DialogContent.tsx
import { useMergeRefs, FloatingFocusManager, FloatingOverlay, FloatingPortal } from "@floating-ui/react";
import { forwardRef as forwardRef9, useEffect as useEffect10, useState as useState18 } from "react";
import { styled as styled124 } from "styled-components";
import { jsx as _jsx942, jsxs as _jsxs72 } from "react/jsx-runtime";
var DialogContent = /* @__PURE__ */ forwardRef9(function DialogContent2(props, propRef) {
  const {
    isOpen,
    context: floatingContext,
    ...context
  } = useDialogContext();
  const [isVisible, setIsVisible] = useState18(isOpen);
  const ref = useMergeRefs([context.refs.setFloating, propRef]);
  useEffect10(() => {
    setIsVisible(isOpen);
  }, [isOpen]);
  if (!isOpen && !isVisible)
    return null;
  return /* @__PURE__ */ _jsx942(FloatingPortal, {
    children: /* @__PURE__ */ _jsx942(FloatingOverlay, {
      lockScroll: true,
      style: {
        zIndex: Z_INDEX_MODALS
      },
      children: /* @__PURE__ */ _jsxs72(DialogWrapper, {
        children: [/* @__PURE__ */ _jsx942(DialogOverlayBackground, {
          isVisible
        }), /* @__PURE__ */ _jsx942(FloatingFocusManager, {
          context: floatingContext,
          children: /* @__PURE__ */ _jsx942("div", {
            ref,
            "aria-labelledby": context.labelId,
            "aria-describedby": context.descriptionId,
            ...context.getFloatingProps(props),
            children: /* @__PURE__ */ _jsx942(DialogContainer, {
              isVisible,
              children: props.children
            })
          })
        })]
      })
    })
  });
});
var DialogWrapper = styled124.div.withConfig({
  displayName: "DialogContent__DialogWrapper",
  componentId: "sc-8llkuq-0"
})(["display:grid;place-items:center;height:100%;"]);
var DialogOverlayBackground = styled124.div.withConfig({
  displayName: "DialogContent__DialogOverlayBackground",
  componentId: "sc-8llkuq-1"
})(["position:fixed;left:0;top:0;right:0;bottom:0;background:", ";opacity:", ";transition:opacity 0.15s;"], colors.surface.neutral, ({
  isVisible
}) => isVisible ? 0.9 : 0);
var DialogContainer = styled124.div.withConfig({
  displayName: "DialogContent__DialogContainer",
  componentId: "sc-8llkuq-2"
})(["background:", ";border-radius:", ";box-shadow:", ";margin:0 9px;padding:18px;opacity:", ";transform:", ";transition:opacity 0.15s,transform 0.15s;", "{width:384px;margin:0;padding:30px 36px;}"], colors.surface.base, radius.large, shadows.high, ({
  isVisible
}) => isVisible ? 1 : 0, ({
  isVisible
}) => isVisible ? "scale(1)" : "scale(0.9)", media.tablet);

// src/organisms/Dialogs/Dialog/DialogDescription.tsx
import { useId as useId2 } from "@floating-ui/react";
import { cloneElement as cloneElement3, forwardRef as forwardRef10, isValidElement as isValidElement2, useLayoutEffect } from "react";
import { jsx as _jsx943 } from "react/jsx-runtime";
var DialogDescription = /* @__PURE__ */ forwardRef10(function DialogDescription2({
  children,
  asChild = false
}, ref) {
  const {
    setDescriptionId,
    getReferenceProps
  } = useDialogContext();
  const id = useId2();
  useLayoutEffect(() => {
    setDescriptionId(id);
    return () => setDescriptionId(void 0);
  }, [id, setDescriptionId]);
  if (asChild && /* @__PURE__ */ isValidElement2(children)) {
    return /* @__PURE__ */ cloneElement3(children, getReferenceProps({
      ref,
      ...children.props
    }));
  }
  return /* @__PURE__ */ _jsx943("p", {
    ref,
    id,
    children
  });
});

// src/organisms/Dialogs/Dialog/DialogHeading.tsx
import { useId as useId3 } from "@floating-ui/react";
import { cloneElement as cloneElement4, forwardRef as forwardRef11, isValidElement as isValidElement3, useLayoutEffect as useLayoutEffect2 } from "react";
import { jsx as _jsx944 } from "react/jsx-runtime";
var DialogHeading = /* @__PURE__ */ forwardRef11(function DialogHeading2({
  children,
  asChild = false
}, ref) {
  const {
    setLabelId,
    getReferenceProps
  } = useDialogContext();
  const id = useId3();
  useLayoutEffect2(() => {
    setLabelId(id);
    return () => setLabelId(void 0);
  }, [id, setLabelId]);
  if (asChild && /* @__PURE__ */ isValidElement3(children)) {
    return /* @__PURE__ */ cloneElement4(children, getReferenceProps({
      ref,
      ...children.props
    }));
  }
  return /* @__PURE__ */ _jsx944("h2", {
    ref,
    id,
    children
  });
});

// src/organisms/Dialogs/DialogAffirm.tsx
import { jsx as _jsx945, jsxs as _jsxs73 } from "react/jsx-runtime";
var DialogAffirm = ({
  children,
  isOpen,
  onIsOpenChange,
  labelClose = "Close"
}) => {
  return /* @__PURE__ */ _jsx945(Dialog, {
    isOpen,
    onIsOpenChange,
    children: /* @__PURE__ */ _jsx945(DialogContent, {
      children: /* @__PURE__ */ _jsx945("div", {
        style: {
          textAlign: "center"
        },
        children: /* @__PURE__ */ _jsxs73(Flexer, {
          column: true,
          gap: "24px",
          tabletGap: "36px",
          children: [/* @__PURE__ */ _jsx945(Flexer, {
            column: true,
            gap: "12px",
            tabletGap: "18px",
            children
          }), /* @__PURE__ */ _jsx945(DialogClose, {
            asChild: true,
            children: /* @__PURE__ */ _jsx945(Button, {
              size: "large",
              variant: "tertiary",
              children: labelClose
            })
          })]
        })
      })
    })
  });
};
DialogAffirm.Heading = ({
  children
}) => /* @__PURE__ */ _jsx945(DialogHeading, {
  asChild: true,
  children: /* @__PURE__ */ _jsx945(Heading, {
    as: "h2",
    color: colors.text.affirm,
    children
  })
});
DialogAffirm.Description = ({
  children
}) => /* @__PURE__ */ _jsx945(DialogDescription, {
  asChild: true,
  children: /* @__PURE__ */ _jsx945(Line, {
    as: "p",
    children
  })
});
DialogAffirm.Body = styled125.div.withConfig({
  displayName: "DialogAffirm__Body",
  componentId: "sc-1onuqig-0"
})([""]);

// src/organisms/Dialogs/DialogConfirm.tsx
import { styled as styled126 } from "styled-components";
import { jsx as _jsx946, jsxs as _jsxs74 } from "react/jsx-runtime";
var DialogConfirm = ({
  children,
  isLoading,
  isOpen,
  labelCancel = "Cancel",
  labelConfirm = "Confirm",
  onConfirm,
  onIsOpenChange,
  variant = "default"
}) => {
  return /* @__PURE__ */ _jsx946(Dialog, {
    isOpen,
    onIsOpenChange,
    children: /* @__PURE__ */ _jsx946(DialogContent, {
      children: /* @__PURE__ */ _jsx946("div", {
        style: {
          textAlign: "center"
        },
        children: /* @__PURE__ */ _jsxs74(Flexer, {
          column: true,
          gap: "24px",
          tabletGap: "36px",
          children: [/* @__PURE__ */ _jsx946(Flexer, {
            column: true,
            gap: "12px",
            tabletGap: "18px",
            children
          }), /* @__PURE__ */ _jsxs74(Flexer, {
            gap: "12px",
            children: [/* @__PURE__ */ _jsx946(DialogClose, {
              asChild: true,
              children: /* @__PURE__ */ _jsx946(Button, {
                size: "large",
                variant: "secondary",
                isFullWidth: true,
                disabled: isLoading,
                children: labelCancel
              })
            }), /* @__PURE__ */ _jsx946(Button, {
              onClick: onConfirm,
              size: "large",
              variant: "secondary",
              color: variant === "danger" ? "error" : "primary",
              isFullWidth: true,
              isLoading,
              children: labelConfirm
            })]
          })]
        })
      })
    })
  });
};
DialogConfirm.Heading = ({
  children
}) => /* @__PURE__ */ _jsx946(DialogHeading, {
  asChild: true,
  children: /* @__PURE__ */ _jsx946(Heading, {
    as: "h2",
    children
  })
});
DialogConfirm.Description = ({
  children
}) => /* @__PURE__ */ _jsx946(DialogDescription, {
  asChild: true,
  children: /* @__PURE__ */ _jsx946(Line, {
    as: "p",
    children
  })
});
DialogConfirm.Body = styled126.div.withConfig({
  displayName: "DialogConfirm__Body",
  componentId: "sc-zjdyzc-0"
})([""]);

// src/organisms/Dialogs/DialogInfo.tsx
import { styled as styled127 } from "styled-components";
import { jsx as _jsx947, jsxs as _jsxs75 } from "react/jsx-runtime";
var DialogInfo = ({
  children,
  isOpen,
  onIsOpenChange,
  labelClose = "Close"
}) => {
  return /* @__PURE__ */ _jsx947(Dialog, {
    isOpen,
    onIsOpenChange,
    children: /* @__PURE__ */ _jsx947(DialogContent, {
      children: /* @__PURE__ */ _jsxs75(Flexer, {
        column: true,
        gap: "24px",
        tabletGap: "36px",
        children: [/* @__PURE__ */ _jsx947(Flexer, {
          column: true,
          gap: "12px",
          tabletGap: "18px",
          children
        }), /* @__PURE__ */ _jsx947(DialogClose, {
          asChild: true,
          children: /* @__PURE__ */ _jsx947(Button, {
            size: "large",
            children: labelClose
          })
        })]
      })
    })
  });
};
DialogInfo.Heading = ({
  children
}) => /* @__PURE__ */ _jsx947(DialogHeading, {
  asChild: true,
  children: /* @__PURE__ */ _jsx947(Heading, {
    as: "h2",
    children
  })
});
DialogInfo.Body = styled127.div.withConfig({
  displayName: "DialogInfo__Body",
  componentId: "sc-g586y2-0"
})([""]);

// src/organisms/Folder/Content/Empty/Empty.tsx
import { Fragment as _Fragment8, jsx as _jsx948 } from "react/jsx-runtime";
var Empty = ({
  children = /* @__PURE__ */ _jsx948(_Fragment8, {
    children: "No files uploaded"
  })
}) => /* @__PURE__ */ _jsx948(Spacer, {
  margin: "9px 0 0",
  children: /* @__PURE__ */ _jsx948(Flexer, {
    justifyCenter: true,
    children: /* @__PURE__ */ _jsx948(Line, {
      size: font.body.medium,
      color: colors.text.neutral,
      children
    })
  })
});
Empty.displayName = "Folder.Content.Empty";

// src/organisms/Folder/Content/List/Header/Header.tsx
import { jsx as _jsx949 } from "react/jsx-runtime";
var Header4 = ({
  columns,
  columnItems
}) => {
  const isDesktop = useIsDesktop();
  if (!isDesktop)
    return null;
  return /* @__PURE__ */ _jsx949(Grid, {
    columns,
    alignCenter: true,
    gap: "18px",
    children: columnItems.map((item, index) => {
      if (item === "")
        return /* @__PURE__ */ _jsx949(GridItem, {}, `${index}_${item}`);
      return /* @__PURE__ */ _jsx949(GridItem, {
        children: /* @__PURE__ */ _jsx949(Line, {
          size: font.body.small,
          color: colors.text.neutral,
          children: item
        })
      }, `${index}_${item}`);
    })
  });
};
Header4.displayName = "Folder.Content.List.Header";

// src/organisms/Folder/Content/List/Item/Item.tsx
import { jsx as _jsx950 } from "react/jsx-runtime";
var Item6 = ({
  children,
  columns,
  tabletColumns,
  desktopColumns
}) => /* @__PURE__ */ _jsx950(Grid, {
  columns,
  tabletColumns: tabletColumns ? tabletColumns : void 0,
  desktopColumns: desktopColumns ? desktopColumns : void 0,
  alignCenter: true,
  tabletGap: "18px",
  gap: "6px",
  children
});
Item6.displayName = "Folder.Content.List.Item";

// src/organisms/Folder/Content/List/List.tsx
import { jsx as _jsx951 } from "react/jsx-runtime";
var List = ({
  children
}) => /* @__PURE__ */ _jsx951(Flexer, {
  column: true,
  gap: "9px",
  justifyCenter: true,
  children
});
List.displayName = "Folder.Content.List";
List.Header = Header4;
List.Item = Item6;

// src/organisms/Folder/Content/Content.tsx
import { jsx as _jsx952 } from "react/jsx-runtime";
var Content11 = ({
  children
}) => /* @__PURE__ */ _jsx952(Container2, {
  padding: "15px 0",
  children
});
Content11.displayName = "Folder.Content";
Content11.Empty = Empty;
Content11.List = List;

// src/organisms/Folder/Header/Header.tsx
import { styled as styled128 } from "styled-components";

// src/organisms/Folder/Header/Title/Title.tsx
import { jsx as _jsx953, jsxs as _jsxs76 } from "react/jsx-runtime";
var Title5 = ({
  children,
  fileCount
}) => /* @__PURE__ */ _jsxs76(Flexer, {
  gap: "9px",
  children: [/* @__PURE__ */ _jsx953(Line, {
    size: font.body.medium,
    children
  }), fileCount !== void 0 && /* @__PURE__ */ _jsx953(Badge, {
    background: colors.surface.neutral,
    color: colors.text.base,
    children: fileCount
  })]
});
Title5.displayName = "Folder.Header.Title";

// src/organisms/Folder/Header/Header.tsx
import { jsx as _jsx954 } from "react/jsx-runtime";
var Header5 = ({
  children,
  isCompact
}) => {
  return /* @__PURE__ */ _jsx954(Container48, {
    isCompact,
    children: /* @__PURE__ */ _jsx954(Flexer, {
      alignCenter: true,
      spaceBetween: true,
      children
    })
  });
};
Header5.displayName = "Folder.Header";
Header5.Title = Title5;
var Container48 = styled128.div.withConfig({
  displayName: "Header__Container",
  componentId: "sc-dpp4yb-0"
})(["padding-bottom:", ";border-bottom:", ";"], ({
  isCompact
}) => isCompact ? "0" : "15px", ({
  isCompact
}) => isCompact ? "none" : `1px solid ${colors.line.neutralSoft}`);

// src/organisms/Folder/Folder.tsx
import { jsx as _jsx955 } from "react/jsx-runtime";
var Folder = ({
  children
}) => /* @__PURE__ */ _jsx955(Container2, {
  padding: "15px 24px",
  background: colors.surface.base,
  radius: radius.large,
  border: `1px solid ${colors.line.neutralSoft}`,
  children
});
Folder.Header = Header5;
Folder.Content = Content11;

// src/organisms/List/List.tsx
import { styled as styled130 } from "styled-components";

// src/organisms/List/Item/Item.tsx
import { useContext as useContext3 } from "react";
import { styled as styled129 } from "styled-components";

// src/organisms/List/list-context.ts
import { createContext as createContext3 } from "react";
var ListContext = /* @__PURE__ */ createContext3({});

// src/organisms/List/Item/Item.tsx
import { jsx as _jsx956 } from "react/jsx-runtime";
var Item7 = ({
  children,
  background,
  bulletColor,
  isOutlined,
  icon
}) => {
  const {
    itemsBackground,
    itemsMargin,
    itemsIcon,
    isNumbered
  } = useContext3(ListContext);
  return /* @__PURE__ */ _jsx956(Container49, {
    isOutlined,
    icon: icon || itemsIcon,
    background: background || itemsBackground,
    margin: itemsMargin,
    isNumbered,
    bulletColor,
    children
  });
};
Item7.displayName = "List.Item";
var Container49 = styled129.li.withConfig({
  displayName: "Item__Container",
  componentId: "sc-1k9fszs-0"
})(["position:relative;padding-left:", ";counter-increment:steps;&:before{color:", ";position:absolute;left:0;top:0;", " ", " ", "}&:not(:last-child){margin-bottom:", ";}"], ({
  background,
  isOutlined
}) => background || isOutlined ? "36px" : "24px", ({
  bulletColor
}) => bulletColor || "inherit", ({
  isNumbered,
  icon
}) => isNumbered ? `content: counter(steps);` : `content: ${icon ? `'${icon}'` : `'\u2192'`};`, ({
  isOutlined
}) => isOutlined && `
            border: 2px solid ${theme.neutral.main};
            border-radius: ${radius.full};
            font-size: ${font.body.medium};
            font-weight: ${font.weight.bold};
            display: flex;
            top: -2px;
            align-items: center;
            justify-content: center;
            line-height: 24px;
            height: 24px;
            width: 24px;
        `, ({
  background
}) => background && `
            font-size: ${font.body.medium};
            display: flex;
            align-items: center;
            justify-content: center;
            line-height: 24px;
            height: 24px;
            width: 24px;
            top: -1px;
            background-color: ${background};
            border-radius: ${radius.full};
        `, ({
  margin
}) => margin ? margin : "12px");

// src/organisms/List/List.tsx
import { jsx as _jsx957 } from "react/jsx-runtime";
var List2 = ({
  children,
  size: size2,
  tabletSize,
  lineHeight,
  isNumbered,
  ...rest
}) => {
  return /* @__PURE__ */ _jsx957(ListContext.Provider, {
    value: {
      ...rest,
      isNumbered
    },
    children: /* @__PURE__ */ _jsx957(Container50, {
      as: isNumbered ? "ol" : "ul",
      isNumbered,
      lineHeight,
      size: size2,
      tabletSize,
      children
    })
  });
};
List2.Item = Item7;
var Container50 = styled130.ul.withConfig({
  displayName: "List__Container",
  componentId: "sc-sokgam-0"
})(["color:", ";font-weight:", ";font-size:", ";line-height:", ";list-style:none;margin:0;padding:0;", ""], theme.text.primary, themeFonts.typography.body.fontWeight, `${themeFonts.typography.body.fontSize.xs}px`, `${themeFonts.typography.body.lineHeight.xs}px`, ({
  isNumbered
}) => isNumbered && `counter-reset: steps;`);

// src/organisms/Message/Message.tsx
import { styled as styled135 } from "styled-components";

// src/organisms/Message/Content/Content.tsx
import { styled as styled133 } from "styled-components";

// src/organisms/Message/Content/Body/Body.tsx
import { styled as styled131 } from "styled-components";
import { jsx as _jsx958 } from "react/jsx-runtime";
var Body4 = ({
  background,
  children
}) => /* @__PURE__ */ _jsx958(Container51, {
  background,
  children
});
var Container51 = styled131.div.withConfig({
  displayName: "Body__Container",
  componentId: "sc-yies2v-0"
})(["text-align:left;padding:18px;background-color:", ";border-radius:", ";", "{padding:24px 30px;}"], ({
  background
}) => background || colors.surface.neutral, radius.medium, media.tablet);

// src/organisms/Message/Content/Header/Header.tsx
import { styled as styled132 } from "styled-components";
import { jsx as _jsx959, jsxs as _jsxs77 } from "react/jsx-runtime";
var Header6 = ({
  text,
  labelText = "",
  labelBackground = colors.surface.neutral,
  hasImage,
  alignLeft
}) => /* @__PURE__ */ _jsxs77(Container52, {
  hasImage,
  alignLeft,
  children: [/* @__PURE__ */ _jsx959(Column2, {
    hasImage,
    children: text
  }), labelText && /* @__PURE__ */ _jsx959(Column2, {
    children: /* @__PURE__ */ _jsx959(Label4, {
      background: labelBackground,
      children: labelText
    })
  })]
});
var Container52 = styled132.div.withConfig({
  displayName: "Header__Container",
  componentId: "sc-1usd41c-0"
})(["font-size:", ";margin-bottom:6px;display:flex;flex-wrap:wrap;justify-content:space-between;", ";", ";", " ", "{margin-bottom:9px;font-size:", ";}", "{", "}"], font.body.small, ({
  hasImage,
  alignLeft
}) => hasImage && alignLeft && `margin-left: 30px;`, ({
  hasImage,
  alignLeft
}) => hasImage && !alignLeft && `margin-right: 30px;`, ({
  alignLeft
}) => !alignLeft && `flex-direction: row-reverse; `, media.tablet, font.body.medium, media.desktop, ({
  hasImage
}) => hasImage && `margin-inline: 0;`);
var Column2 = styled132.div.withConfig({
  displayName: "Header__Column",
  componentId: "sc-1usd41c-1"
})(["white-space:pre-wrap;align-self:center;", "  ", "{", "}"], ({
  hasImage
}) => hasImage && `line-height: 24px;`, media.desktop, ({
  hasImage
}) => hasImage && `line-height: inherit;`);

// src/organisms/Message/Content/Content.tsx
import { jsx as _jsx960 } from "react/jsx-runtime";
var Content12 = ({
  children
}) => /* @__PURE__ */ _jsx960(Container53, {
  children
});
Content12.Header = Header6;
Content12.Body = Body4;
var Container53 = styled133.div.withConfig({
  displayName: "Content__Container",
  componentId: "sc-2vsg89-0"
})(["width:100%;position:relative;max-width:95%;", "{max-width:83%;}"], media.tablet);

// src/organisms/Message/Image/Image.tsx
import { styled as styled134 } from "styled-components";
import { jsx as _jsx961 } from "react/jsx-runtime";
var Image4 = ({
  alt,
  src,
  withBorder,
  size: size2 = "24px",
  tabletSize,
  desktopSize
}) => /* @__PURE__ */ _jsx961(Container54, {
  size: size2,
  tabletSize,
  desktopSize,
  children: /* @__PURE__ */ _jsx961(Img, {
    alt,
    src,
    withBorder
  })
});
var Container54 = styled134.div.withConfig({
  displayName: "Image__Container",
  componentId: "sc-erbier-0"
})(["position:absolute;", " ", " ", " ", "{position:initial;}"], ({
  size: size2
}) => size2 && `width: ${size2}; height: ${size2};`, ({
  tabletSize
}) => tabletSize && tablet`width: ${tabletSize}; height: ${tabletSize};`, ({
  desktopSize
}) => desktopSize && desktop`width: ${desktopSize}; height: ${desktopSize};`, media.desktop);
var Img = styled134.img.withConfig({
  displayName: "Image__Img",
  componentId: "sc-erbier-1"
})(["border-radius:", ";width:100%;height:100%;", ""], radius.full, ({
  withBorder
}) => withBorder && `border: 1px solid ${colors.line.neutralSoft};`);

// src/organisms/Message/Message.tsx
import { jsx as _jsx962 } from "react/jsx-runtime";
var Message = ({
  alignLeft,
  children
}) => {
  return /* @__PURE__ */ _jsx962(Container55, {
    alignLeft,
    children
  });
};
Message.Content = Content12;
Message.Image = Image4;
var Container55 = styled135.div.withConfig({
  displayName: "Message__Container",
  componentId: "sc-1mgfuf8-0"
})(["position:relative;margin-bottom:30px;display:flex;justify-content:", ";gap:12px;"], ({
  alignLeft
}) => alignLeft ? "flex-start" : "flex-end");

// src/organisms/Page/Page.tsx
import { styled as styled150 } from "styled-components";

// src/organisms/Page/Actions.ts
import { styled as styled136 } from "styled-components";
var Actions2 = styled136.div.withConfig({
  displayName: "Actions",
  componentId: "sc-18f54sa-0"
})(["", " padding:15px;background:", ";border-radius:", ";p{font-size:", ";line-height:1.5;", "{font-size:", ";}&:not(:first-child){margin-top:12px;}&:not(:last-child){margin-bottom:12px;}}"], ({
  alignCenter
}) => alignCenter ? "text-align: center;" : "", ({
  background
}) => background || "none", radius.large, font.body.small, media.tablet, font.body.medium);
Actions2.displayName = "Page.Actions";

// src/organisms/Page/Heading.tsx
import { styled as styled137 } from "styled-components";
var Heading2 = styled137.h1.withConfig({
  displayName: "Heading",
  componentId: "sc-a68f6-0"
})(["font-size:", ";font-weight:", ";line-height:1.3;margin-bottom:6px;", "{font-size:", ";margin-bottom:9px;}"], font.title.medium, font.weight.medium, media.tablet, font.title.xlarge);
Heading2.displayName = "Page.Heading";

// src/organisms/Page/Main.ts
import { styled as styled138 } from "styled-components";
var Main3 = styled138.main.withConfig({
  displayName: "Main",
  componentId: "sc-ywx8u-0"
})(["position:relative;padding:18px 18px 30px 18px;", "{width:100%;margin:0 auto;padding:", ";}", "{max-width:768px;}"], media.desktop, ({
  withPadding
}) => withPadding ? "0 48px" : "0", media.desktopLarge);
Main3.displayName = "Page.Main";

// src/organisms/Page/MainColumn.ts
import { styled as styled139 } from "styled-components";
var MainColumn = styled139.div.withConfig({
  displayName: "MainColumn",
  componentId: "sc-zt17ra-0"
})(["background:", ";", "{flex:0 0 78%;padding:48px 0 144px;}", "{flex:0 0 56%;}"], colors.surface.base, media.desktop, media.desktopLarge);
MainColumn.displayName = "Page.MainColumn";

// src/organisms/Page/PreHeading/PreHeading.tsx
import { styled as styled141 } from "styled-components";

// src/organisms/Page/PreHeading/Meta.ts
import { styled as styled140 } from "styled-components";
var Meta = styled140.div.withConfig({
  displayName: "Meta",
  componentId: "sc-1ymcv14-0"
})(["margin-left:auto;"]);
Meta.displayName = "Page.PreHeading.Meta";

// src/organisms/Page/PreHeading/PreHeading.tsx
import { jsx as _jsx963 } from "react/jsx-runtime";
var PreHeading = ({
  children
}) => /* @__PURE__ */ _jsx963(Container56, {
  children
});
PreHeading.Meta = Meta;
var Container56 = styled141.div.withConfig({
  displayName: "PreHeading__Container",
  componentId: "sc-11hvodj-0"
})(["display:flex;color:", ";font-size:", ";line-height:1.5;margin-bottom:3px;"], colors.text.neutral, font.body.medium);

// src/organisms/Page/RightColumn.ts
import { styled as styled142 } from "styled-components";
var RightColumn = styled142.div.withConfig({
  displayName: "RightColumn",
  componentId: "sc-9d3ps1-0"
})(["display:none;", "{display:block;flex:0 0 22%;background:", ";}"], media.desktopLarge, ({
  background
}) => background || colors.surface.neutral);
RightColumn.displayName = "Page.RightColumn";

// src/organisms/Page/Section/Section.tsx
import { styled as styled144 } from "styled-components";

// src/organisms/Page/Section/Title.ts
import { styled as styled143 } from "styled-components";
var Title6 = styled143.div.withConfig({
  displayName: "Title",
  componentId: "sc-a3e1d7-0"
})(["color:", ";font-weight:", ";font-size:", ";line-height:", ";letter-spacing:", ";text-transform:uppercase;margin-bottom:15px;", "{margin-bottom:24px;}", "{font-size:", ";letter-spacing:", ";}"], theme.text.secondary, themeFonts.typography.overline.fontWeight, `${themeFonts.typography.overline.fontSize.xs}px`, `${themeFonts.typography.overline.lineHeight.xs}px`, `${themeFonts.typography.overline.letterSpacing.xs}px`, media.tablet, media.desktop, `${themeFonts.typography.overline.fontSize.lg}px`, `${themeFonts.typography.overline.letterSpacing.lg}px`);
Title6.displayName = "Page.Section.Title";

// src/organisms/Page/Section/Section.tsx
import { jsx as _jsx964 } from "react/jsx-runtime";
var Section = ({
  background,
  children,
  isMobileNarrowMargin
}) => /* @__PURE__ */ _jsx964(Container57, {
  background,
  isMobileNarrowMargin,
  children
});
Section.Title = Title6;
var Container57 = styled144.div.withConfig({
  displayName: "Section__Container",
  componentId: "sc-1p0eezj-0"
})(["margin:36px 0;border-radius:", ";&:first-child{margin-top:0;}&:last-child{margin-bottom:0;}", " ", " ", "{margin:48px 0;&:first-child{margin-top:0;}", "}"], radius.large, ({
  background
}) => background && `
        padding: 15px 15px 18px;
        background: ${background};
    `, ({
  isMobileNarrowMargin
}) => isMobileNarrowMargin && `
        margin: 24px -15px;
    `, media.tablet, ({
  background
}) => background && `
            padding: 30px 36px 30px;
        `);

// src/organisms/Page/Sidebar/Sidebar.tsx
import { styled as styled147 } from "styled-components";

// src/organisms/Page/Sidebar/Item/Item.tsx
import { styled as styled146 } from "styled-components";

// src/organisms/Page/Sidebar/Item/Link.ts
import { styled as styled145 } from "styled-components";
var Link4 = styled145.div.withConfig({
  displayName: "Link",
  componentId: "sc-r5vmaa-0"
})(["color:", ";font-size:", ";line-height:1.3;text-align:center;text-decoration:none;display:block;width:100%;padding:12px 18px;white-space:nowrap;&:focus-visible{outline:2px solid ", ";outline-offset:-2px;}", "{font-size:", ";font-weight:", ";text-align:left;padding:18px 24px;}", "{&:hover{color:", ";}}"], colors.text.base, font.body.small, colors.line.interactive, media.desktop, font.body.medium, font.weight.medium, media.pointer, colors.text.interactive);
Link4.displayName = "Page.Sidebar.Item.Link";

// src/organisms/Page/Sidebar/Item/Item.tsx
import { jsx as _jsx965 } from "react/jsx-runtime";
var Item8 = ({
  children,
  isActive
}) => /* @__PURE__ */ _jsx965(Container58, {
  "aria-current": isActive,
  isActive,
  children
});
Item8.Link = Link4;
Item8.displayName = "Page.Sidebar.Item";
var Container58 = styled146.div.withConfig({
  displayName: "Item__Container",
  componentId: "sc-h1c5mm-0"
})(["position:relative;overflow:hidden;display:inline-block;align-items:center;margin:0 2px 0 0;width:auto;background:", ";&:last-child{margin-right:72px;}", "{display:block;width:100%;margin:0;background:", ";&:last-child{margin-right:0;}}"], ({
  isActive
}) => isActive ? colors.surface.base : colors.line.neutralLight, media.desktop, ({
  isActive
}) => isActive ? colors.line.neutralLight : "none");

// src/organisms/Page/Sidebar/Sidebar.tsx
import { jsx as _jsx966 } from "react/jsx-runtime";
var Sidebar3 = ({
  ariaLabel,
  children
}) => /* @__PURE__ */ _jsx966(Container59, {
  "aria-label": ariaLabel,
  children
});
Sidebar3.Item = Item8;
var Container59 = styled147.nav.withConfig({
  displayName: "Sidebar__Container",
  componentId: "sc-1i9gx1o-0"
})(["overflow:auto;display:block;white-space:nowrap;font-size:0;", "{white-space:normal;flex:0 0 35%;}"], media.desktop);

// src/organisms/Page/SidebarColumn.ts
import { styled as styled148 } from "styled-components";
import { hexToRgbA as hexToRgbA14 } from "@scouts/helpers";
var SidebarColumn = styled148.aside.withConfig({
  displayName: "SidebarColumn",
  componentId: "sc-1boj09g-0"
})(["position:relative;background:", ";&:after{content:'';position:absolute;top:0;right:0;height:100%;width:72px;pointer-events:none;", "}", "{flex:0 0 22%;width:22%;margin:0;padding:72px 0 192px;}"], ({
  background
}) => background || colors.surface.neutral, ({
  background
}) => !background && `
            background-image: linear-gradient(
                to right,
                ${hexToRgbA14(colors.surface.neutral, 0)},
                ${hexToRgbA14(colors.surface.neutral, 1)}
            );
        `, media.desktop);
SidebarColumn.displayName = "Page.SidebarColumn";

// src/organisms/Page/SubHeading.ts
import { styled as styled149 } from "styled-components";
var SubHeading = styled149.div.withConfig({
  displayName: "SubHeading",
  componentId: "sc-1k0oxxv-0"
})(["font-size:", ";line-height:1.5;margin-bottom:18px;", "{margin-bottom:24px;}"], font.title.small, media.tablet);
SubHeading.displayName = "Page.SubHeading";

// src/organisms/Page/Page.tsx
import { jsx as _jsx967 } from "react/jsx-runtime";
var Page2 = ({
  children,
  isMobileWide,
  isWide,
  withSidebar
}) => /* @__PURE__ */ _jsx967(Container60, {
  isMobileWide,
  isWide,
  withSidebar,
  children
});
Page2.Actions = Actions2;
Page2.Heading = Heading2;
Page2.Main = Main3;
Page2.MainColumn = MainColumn;
Page2.PreHeading = PreHeading;
Page2.Section = Section;
Page2.RightColumn = RightColumn;
Page2.SidebarColumn = SidebarColumn;
Page2.Sidebar = Sidebar3;
Page2.SubHeading = SubHeading;
var Container60 = styled150.div.withConfig({
  displayName: "Page__Container",
  componentId: "sc-4v8mef-0"
})(["padding:", ";", "{width:", ";margin:0 auto;padding:", ";}", "{display:", ";width:", ";}", "{width:", ";}"], ({
  withSidebar,
  isMobileWide
}) => {
  switch (true) {
    case isMobileWide:
      return "15px 9px 144px";
    case withSidebar:
      return "0 0 120px";
    default:
      return "18px 18px 144px";
  }
}, media.tablet, ({
  isWide,
  withSidebar
}) => {
  switch (true) {
    case withSidebar:
      return "100%";
    case isWide:
      return "720px";
    default:
      return "672px";
  }
}, ({
  withSidebar
}) => withSidebar ? "0 0 144px" : "48px 0 144px", media.desktop, ({
  withSidebar
}) => withSidebar ? "flex" : "block", ({
  isWide,
  withSidebar
}) => {
  switch (true) {
    case withSidebar:
      return "100%";
    case isWide:
      return "960px";
    default:
      return "768px";
  }
}, media.desktopLarge, ({
  isWide,
  withSidebar
}) => {
  switch (true) {
    case withSidebar:
      return "100%";
    case isWide:
      return "1152px";
    default:
      return "768px";
  }
});

// src/organisms/Pagination/Pagination.tsx
import { styled as styled156 } from "styled-components";

// src/organisms/Pagination/Actions/Actions.tsx
import { styled as styled152 } from "styled-components";

// src/organisms/Pagination/Actions/Label.ts
import { styled as styled151 } from "styled-components";
var Label6 = styled151.div.withConfig({
  displayName: "Label",
  componentId: "sc-gl34i6-0"
})(["font-size:", ";white-space:nowrap;margin-right:12px;"], font.body.medium);

// src/organisms/Pagination/Actions/Actions.tsx
import { jsx as _jsx968 } from "react/jsx-runtime";
var Actions3 = ({
  children
}) => /* @__PURE__ */ _jsx968(Container61, {
  children
});
Actions3.Label = Label6;
var Container61 = styled152.div.withConfig({
  displayName: "Actions__Container",
  componentId: "sc-aalmsr-0"
})(["flex:0 0 auto;display:flex;align-items:center;"]);

// src/organisms/Pagination/Item.ts
import { styled as styled153 } from "styled-components";
var Item9 = styled153.button.withConfig({
  displayName: "Item",
  componentId: "sc-11jrqhq-0"
})(["color:", ";font-family:inherit;font-size:", ";font-weight:", ";text-decoration:none;display:flex;align-items:center;justify-content:center;min-width:36px;padding:6px;background:", ";border:2px solid ", ";border-radius:", ";cursor:pointer;&:focus{border:2px solid ", ";outline:none;}", "{&:hover{border:2px solid transparent;background:", ";}}"], theme.primary.contrast, font.body.medium, font.weight.medium, ({
  isActive
}) => isActive ? theme.primary.primaryAction : theme.grey.surface1, ({
  isActive
}) => isActive ? theme.primary.primaryAction : theme.grey.surface1, radius.small, colors.line.interactive, media.pointer, ({
  isActive
}) => isActive ? theme.primary.primaryAction : theme.primary.secondaryAction);

// src/organisms/Pagination/Items.ts
import { styled as styled154 } from "styled-components";
var Items2 = styled154.div.withConfig({
  displayName: "Items",
  componentId: "sc-1y562b1-0"
})(["display:flex;flex-wrap:wrap;justify-content:center;gap:9px;"]);

// src/organisms/Pagination/Separator.ts
import { styled as styled155 } from "styled-components";
var Separator = styled155.div.withConfig({
  displayName: "Separator",
  componentId: "sc-jxk84v-0"
})(["color:", ";font-size:", ";font-weight:", ";text-decoration:none;display:flex;align-items:center;justify-content:center;width:36px;padding:6px;"], colors.text.base, font.body.medium, font.weight.medium);

// src/organisms/Pagination/Pagination.tsx
import { jsx as _jsx969 } from "react/jsx-runtime";
var Pagination = ({
  children
}) => /* @__PURE__ */ _jsx969(Container62, {
  children
});
Pagination.Items = Items2;
Pagination.Item = Item9;
Pagination.Separator = Separator;
Pagination.Actions = Actions3;
var Container62 = styled156.div.withConfig({
  displayName: "Pagination__Container",
  componentId: "sc-umvpgt-0"
})(["font-family:inherit;display:flex;align-items:center;flex-direction:column;gap:18px;", "{flex-direction:row;gap:24px;}"], media.tablet);

// src/organisms/ProductOverview/Description/Image/Image.tsx
import { styled as styled157 } from "styled-components";
import { jsx as _jsx970 } from "react/jsx-runtime";
var Image5 = ({
  alt = "",
  src
}) => /* @__PURE__ */ _jsx970(ImageWrapper2, {
  children: /* @__PURE__ */ _jsx970(Img2, {
    alt,
    src
  })
});
Image5.displayName = "ProductOverview.Body.Image";
var ImageWrapper2 = styled157.div.withConfig({
  displayName: "Image__ImageWrapper",
  componentId: "sc-1laxk12-0"
})(["", "{position:relative;padding-bottom:24px;margin-bottom:24px;}"], media.tablet);
var Img2 = styled157.img.withConfig({
  displayName: "Image__Img",
  componentId: "sc-1laxk12-1"
})(["display:block;width:100%;", "{position:absolute;top:auto;bottom:-96px;left:50%;right:auto;width:100%;margin:0 auto;transform:translateX(-50%);}"], media.tablet);

// src/organisms/ProductOverview/Description/Description.tsx
import { jsx as _jsx971 } from "react/jsx-runtime";
var Description2 = ({
  children
}) => /* @__PURE__ */ _jsx971(GridItem, {
  children
});
Description2.displayName = "ProductOverview.Description";
Description2.Image = Image5;

// src/organisms/ProductOverview/Main/Main.tsx
import { jsx as _jsx972 } from "react/jsx-runtime";
var Main4 = ({
  children
}) => /* @__PURE__ */ _jsx972(GridItem, {
  children
});
Main4.displayName = "ProductOverview.Main";

// src/organisms/ProductOverview/ProductOverview.tsx
import { jsx as _jsx973 } from "react/jsx-runtime";
var ProductOverview = ({
  children,
  background
}) => /* @__PURE__ */ _jsx973(Container2, {
  background,
  radius: radius.large,
  padding: "24px 24px 30px",
  tabletPadding: "36px 48px 42px",
  relative: true,
  children: /* @__PURE__ */ _jsx973(Grid, {
    gap: "36px",
    tabletColumns: 2,
    tabletGap: "60px",
    children
  })
});
ProductOverview.Main = Main4;
ProductOverview.Description = Description2;

// src/organisms/ProductProgress/Footer/Footer.tsx
import { jsx as _jsx974 } from "react/jsx-runtime";
var Footer = ({
  children
}) => /* @__PURE__ */ _jsx974(Container2, {
  children
});
Footer.displayName = "ProductProgress.Footer";

// src/organisms/ProductProgress/Header/Header.tsx
import { jsx as _jsx975 } from "react/jsx-runtime";
var Header7 = ({
  children
}) => /* @__PURE__ */ _jsx975(Heading, {
  children
});
Header7.displayName = "ProductProgress.Header";

// src/organisms/ProductProgress/List/Item/Item.tsx
import { styled as styled158 } from "styled-components";
import { jsx as _jsx976, jsxs as _jsxs78 } from "react/jsx-runtime";
var getBackground4 = ({
  status,
  isActive,
  isTablet,
  background
}) => {
  if (!isTablet || !isActive)
    return void 0;
  if (status === "completed")
    return colors.surface.affirm;
  if (status === "requiresAction")
    return colors.surface.attention;
  if (status === "active")
    return colors.surface.highlight;
  if (status === "pending")
    return colors.surface.neutral;
  return background;
};
var getBadgeBackground = ({
  status,
  isActive,
  isTablet,
  background
}) => {
  if (isTablet && isActive)
    return colors.surface.base;
  if (status === "completed")
    return colors.surface.affirm;
  if (status === "requiresAction")
    return colors.surface.attention;
  if (status === "active")
    return colors.surface.highlight;
  if (status === "pending")
    return colors.surface.neutral;
  return background;
};
var getTextColor = ({
  status,
  textColor
}) => {
  if (status === "pending")
    return colors.text.neutral;
  return textColor;
};
var getIcon = ({
  status,
  icon
}) => {
  if (status === "completed")
    return "\u2713";
  if (status === "active")
    return "\u23F3";
  if (status === "pending")
    return "\u{1F551}";
  return icon;
};
var Item10 = ({
  actions,
  background,
  children,
  textColor = colors.text.base,
  icon = "\u2192",
  isActive,
  title,
  status
}) => {
  const isTablet = useIsTablet();
  const computedBackground = getBackground4({
    status,
    isActive,
    isTablet,
    background
  });
  const computedBadgeBackground = getBadgeBackground({
    status,
    isActive,
    isTablet,
    background
  });
  const computedTextColor = getTextColor({
    status,
    textColor
  });
  const computedIcon = getIcon({
    status,
    icon
  });
  return /* @__PURE__ */ _jsxs78(Container2, {
    background: computedBackground,
    radius: isActive ? radius.medium : void 0,
    tabletPadding: isActive ? "12px 15px" : void 0,
    children: [/* @__PURE__ */ _jsxs78(Flexer, {
      gap: "12px",
      spaceBetween: true,
      tabletAlignCenter: true,
      alignBaseline: true,
      children: [/* @__PURE__ */ _jsx976(Badge, {
        color: computedTextColor,
        background: computedBadgeBackground,
        children: computedIcon
      }), /* @__PURE__ */ _jsx976(Spacer, {
        width: "100%",
        children: /* @__PURE__ */ _jsxs78(Flexer, {
          column: true,
          tabletRow: true,
          gap: "6px",
          tabletGap: "9px",
          alignStart: true,
          justifyStart: true,
          tabletAlignCenter: true,
          tabletSpaceBetween: true,
          children: [/* @__PURE__ */ _jsx976(Title7, {
            color: computedTextColor,
            isLarge: isActive,
            children: title
          }), actions && /* @__PURE__ */ _jsx976(Spacer, {
            marginTop: "12px",
            tabletMargin: "0",
            children: /* @__PURE__ */ _jsx976(Flexer, {
              alignCenter: true,
              gap: "12px",
              rowReverse: true,
              tabletRow: true,
              children: actions
            })
          })]
        })
      })]
    }), children]
  });
};
Item10.displayName = "ProductProgress.List.Item";
var Title7 = styled158.div.withConfig({
  displayName: "Item__Title",
  componentId: "sc-lctn40-0"
})(["color:", ";font-size:", ";line-height:1.3;", "{font-size:", ";font-weight:", ";}"], ({
  color
}) => color || colors.text.base, font.title.small, media.tablet, font.title.medium, ({
  isLarge
}) => isLarge ? font.weight.medium : font.weight.normal);

// src/organisms/ProductProgress/List/List.tsx
import { jsx as _jsx977 } from "react/jsx-runtime";
var List3 = ({
  children
}) => /* @__PURE__ */ _jsx977(Flexer, {
  column: true,
  gap: "15px",
  children
});
List3.Item = Item10;
List3.displayName = "ProductProgress.List";

// src/organisms/ProductProgress/ProductProgress.tsx
import { jsx as _jsx978 } from "react/jsx-runtime";
var ProductProgress = ({
  children
}) => {
  return /* @__PURE__ */ _jsx978(Container2, {
    padding: "18px 18px 21px",
    tabletPadding: "24px 36px 30px",
    radius: radius.large,
    border: `1px solid ${colors.line.neutralSoft}`,
    shadow: shadows.high,
    background: colors.surface.base,
    children: /* @__PURE__ */ _jsx978(Flexer, {
      column: true,
      gap: "24px",
      children
    })
  });
};
ProductProgress.Header = Header7;
ProductProgress.List = List3;
ProductProgress.Footer = Footer;

// src/organisms/ProgressBar/ProgressBar.tsx
import { cloneElement as cloneElement5, useEffect as useEffect11, useRef as useRef10, Children as Children2 } from "react";
import { styled as styled161 } from "styled-components";
import { hexToRgbA as hexToRgbA15 } from "@scouts/helpers";

// src/organisms/ProgressBar/Item/Item.tsx
import { forwardRef as forwardRef12 } from "react";
import { styled as styled160 } from "styled-components";

// src/organisms/ProgressBar/Item/Link.ts
import { styled as styled159 } from "styled-components";
var ProgressBarItemLink = styled159.div.withConfig({
  displayName: "Link__ProgressBarItemLink",
  componentId: "sc-75wmqi-0"
})(["color:", ";font-size:", ";font-weight:", ";text-decoration:none;display:inline-block;margin:0 12px;padding:0;&:before{color:", ";font-size:", ";margin-right:6px;}", "{position:relative;margin:0;padding:12px 24px 12px 32px;&:before{color:", ";display:flex;align-items:center;justify-content:center;position:absolute;top:7px;left:0;width:24px;height:24px;border-radius:12px;border:1px solid ", ";}}"], colors.text.base, font.body.medium, font.weight.medium, colors.text.base, font.body.medium, media.desktop, colors.text.neutral, colors.line.neutralSoft);
ProgressBarItemLink.displayName = "ProgressBar.Item.Link";

// src/organisms/ProgressBar/Item/Item.tsx
import { jsx as _jsx979 } from "react/jsx-runtime";
var ProgressBarItemForwardRef = /* @__PURE__ */ forwardRef12(({
  children,
  ...props
}, ref) => /* @__PURE__ */ _jsx979(ProgressBarItemContainer, {
  ...props,
  ref,
  children
}));
var ProgressBarItem = {
  ...ProgressBarItemForwardRef,
  Link: ProgressBarItemLink,
  displayName: "ProgressBar.Item"
};
var ProgressBarItemContainer = styled160.div.withConfig({
  displayName: "Item__ProgressBarItemContainer",
  componentId: "sc-k7r00w-0"
})(["color:", ";display:flex;align-items:center;position:relative;height:100%;padding:6px 0;", " ", " ", " ", "{text-transform:none;", " ", " &:first-child{", "{padding-right:0;}}& ", "{&:before{content:counter(progress);counter-increment:progress;}}}"], colors.text.neutral, ({
  isActive
}) => isActive && `
            ${ProgressBarItemLink} {
                color: ${colors.text.interactive};
                &:before {
                    color: ${colors.text.interactive};
                }
            }
    `, ({
  isDisabled
}) => isDisabled && `
        pointer-events: none;
        display: none;
        ${media.tablet} {
            display: flex;
        }

        ${ProgressBarItemLink} {
            color: ${colors.text.neutral};
            pointer-events: none;

            &:before {
                color: ${colors.text.neutral};
            }
        }
    `, () => `
        &:first-child {
            ${ProgressBarItemLink} {
                padding-right: 0;
            }
        }

        & ${ProgressBarItemLink} {
            &:before {
                content: counter(progress);
                counter-increment: progress;
            }
        }
    `, media.desktop, ({
  isActive
}) => isActive && `
                ${ProgressBarItemLink} {
                    color: ${colors.text.base};
                    &:before {
                        color: ${colors.text.base};
                        background: ${colors.surface.affirm};
                        border-color: ${colors.surface.affirm};
                    }
                }
        `, ({
  isActive
}) => !isActive && `
                ${ProgressBarItemLink} {
                    &:hover {
                        color: ${colors.text.interactive};
                    }
                }
        `, ProgressBarItemLink, ProgressBarItemLink);

// src/organisms/ProgressBar/ProgressBar.tsx
import { jsx as _jsx980, jsxs as _jsxs79 } from "react/jsx-runtime";
var ProgressBar = ({
  children
}) => {
  const refActiveItem = useRef10(null);
  const scrollToActiveItem = () => {
    const {
      current
    } = refActiveItem;
    if (!current)
      return;
    setTimeout(() => current.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "center"
    }), 0);
  };
  const scrollToItem = (e) => {
    e.preventDefault();
    if (!e.target)
      return;
    const target = e.target;
    target.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "center"
    });
  };
  useEffect11(() => {
    scrollToActiveItem();
  }, []);
  return /* @__PURE__ */ _jsx980(ProgressBarWrapper, {
    children: /* @__PURE__ */ _jsxs79(ProgressBarContainer, {
      children: [/* @__PURE__ */ _jsx980(Padding, {
        children: "\xA0"
      }), Children2.map(children, (child) => {
        if (!child)
          return null;
        const {
          props
        } = child;
        const {
          onClick: childOnClick
        } = props;
        return /* @__PURE__ */ cloneElement5(child, {
          ref: props.isActive ? refActiveItem : null,
          onClick: callAll(scrollToItem, childOnClick)
        });
      }), /* @__PURE__ */ _jsx980(Padding, {
        children: "\xA0"
      })]
    })
  });
};
ProgressBar.Item = ProgressBarItem;
var Padding = styled161.div.withConfig({
  displayName: "ProgressBar__Padding",
  componentId: "sc-1jvnuwq-0"
})(["flex:0 0 50vw;width:50vw;", "{display:none;}"], media.desktop);
var ProgressBarWrapper = styled161.div.withConfig({
  displayName: "ProgressBar__ProgressBarWrapper",
  componentId: "sc-1jvnuwq-1"
})(["background:", ";position:relative;&:before,&:after{content:'';position:absolute;z-index:1;top:0;width:40%;height:100%;pointer-events:none;}&:before{left:0;background:linear-gradient(to left,", ",", ");}&:after{right:0;background:linear-gradient(to right,", ",", ");}", "{position:fixed;top:0;left:20%;z-index:1010;width:60%;height:60px;background:none;&:before,&:after{display:none;}}"], colors.surface.base, hexToRgbA15(colors.surface.base, 0), colors.surface.base, hexToRgbA15(colors.surface.base, 0), colors.surface.base, media.desktop);
var ProgressBarContainer = styled161.div.withConfig({
  displayName: "ProgressBar__ProgressBarContainer",
  componentId: "sc-1jvnuwq-2"
})(["display:flex;overflow-x:scroll;overflow-y:hidden;position:relative;align-items:center;justify-content:flex-start;padding:9px 0;counter-reset:progress;", "{overflow:hidden;justify-content:center;max-width:960px;height:100%;margin:0 auto;padding:0;}"], media.desktop);

// src/organisms/ProgressNav/ProgressNav.tsx
import { autoUpdate, shift, size, useClick, useDismiss as useDismiss2, useFloating as useFloating2, useInteractions as useInteractions2, FloatingFocusManager as FloatingFocusManager2 } from "@floating-ui/react";
import { useMemo as useMemo7, useState as useState19 } from "react";
import { flushSync } from "react-dom";
import { styled as styled164 } from "styled-components";

// src/organisms/ProgressNav/Dropdown/Dropdown.tsx
import { styled as styled162 } from "styled-components";
import { jsx as _jsx981 } from "react/jsx-runtime";
var Dropdown = ({
  children,
  height
}) => /* @__PURE__ */ _jsx981(Container63, {
  height,
  children: /* @__PURE__ */ _jsx981(Flexer, {
    column: true,
    gap: "15px",
    children
  })
});
var Container63 = styled162.div.withConfig({
  displayName: "Dropdown__Container",
  componentId: "sc-zkhu0-0"
})(["position:relative;width:100%;padding:18px 21px;background-color:", ";box-shadow:", ";border-radius:", ";max-height:", ";overflow:auto;"], colors.surface.base, shadows.high, radius.medium, ({
  height
}) => height ? `${height}px` : "auto");

// src/organisms/ProgressNav/List/Item/Item.tsx
import { styled as styled163 } from "styled-components";

// src/organisms/ProgressNav/progress-nav-context/progress-nav-context.ts
import { createContext as createContext4 } from "react";
var ProgressNavContext = /* @__PURE__ */ createContext4(null);

// src/organisms/ProgressNav/progress-nav-context/use-progress-nav.ts
import { useContext as useContext4 } from "react";
var useProgressNav = () => {
  const value = useContext4(ProgressNavContext);
  if (value === null)
    throw new Error("`useProgressNav` must be used within a `ProgressNavContext.Provider`");
  return value;
};

// src/organisms/ProgressNav/List/Item/Item.tsx
import { jsx as _jsx982, jsxs as _jsxs80 } from "react/jsx-runtime";
var getBackground5 = ({
  variant
}) => {
  switch (variant) {
    case "affirm":
      return colors.surface.affirm;
    case "active":
    case "highlight":
      return colors.surface.highlight;
    default:
      return colors.surface.neutral;
  }
};
var getColor6 = ({
  variant,
  color
}) => {
  switch (true) {
    case !!color:
      return color;
    case variant === "active":
      return colors.text.interactive;
    case variant === "highlight":
      return colors.text.base;
    case variant === "dim":
      return colors.text.neutral;
    default:
      return colors.text.base;
  }
};
var Item11 = ({
  children,
  variant,
  onClick,
  withoutLine = false,
  icon = IconTinyArrow_default,
  color
}) => {
  const {
    setIsOpen
  } = useProgressNav();
  const handleClick = () => {
    if (!onClick)
      return;
    onClick();
    setIsOpen(false);
  };
  return /* @__PURE__ */ _jsxs80(Container64, {
    onClick: onClick ? handleClick : void 0,
    color: getColor6({
      variant,
      color
    }),
    children: [/* @__PURE__ */ _jsxs80(VerticalLineContainer, {
      children: [!withoutLine && /* @__PURE__ */ _jsx982(VerticalLine, {
        variant
      }), /* @__PURE__ */ _jsx982(IconWrapper, {
        children: /* @__PURE__ */ _jsx982(Icon, {
          Component: icon,
          width: "18px",
          height: "18px",
          background: getBackground5({
            variant
          }),
          color: getColor6({
            variant
          })
        })
      })]
    }), /* @__PURE__ */ _jsx982(Label7, {
      children
    })]
  });
};
var VerticalLineContainer = styled163.div.withConfig({
  displayName: "Item__VerticalLineContainer",
  componentId: "sc-119z3df-0"
})(["position:absolute;top:0;left:0;width:18px;height:100%;"]);
var VerticalLine = styled163.div.withConfig({
  displayName: "Item__VerticalLine",
  componentId: "sc-119z3df-1"
})(["position:absolute;display:block;top:1px;bottom:0;left:9px;width:2px;height:calc(100% - 2px);background-color:", ";transform:translateX(-50%);border-radius:", ";"], getBackground5, radius.small);
var IconWrapper = styled163.div.withConfig({
  displayName: "Item__IconWrapper",
  componentId: "sc-119z3df-2"
})(["border-radius:", ";overflow:hidden;position:absolute;top:0;left:0;"], radius.full);
var Container64 = styled163.button.withConfig({
  displayName: "Item__Container",
  componentId: "sc-119z3df-3"
})(["color:", ";position:relative;padding:0 0 18px 36px;text-align:left;cursor:", ";", " &:last-child{padding-bottom:0;", "{display:none;}}"], ({
  color
}) => color ? color : "inherit", ({
  onClick
}) => onClick ? "pointer" : "auto", ({
  onClick
}) => onClick && `
        ${media.pointer} {
            &:hover {
                color: ${colors.text.interactive};
            }
        }
    `, VerticalLine);
var Label7 = styled163.div.withConfig({
  displayName: "Item__Label",
  componentId: "sc-119z3df-4"
})(["font-size:", ";font-weight:", ";line-height:1.3;"], font.body.medium, font.weight.medium);

// src/organisms/ProgressNav/List/Sublist/Sublist.tsx
import { jsx as _jsx983 } from "react/jsx-runtime";
var Sublist = ({
  children
}) => /* @__PURE__ */ _jsx983(Container2, {
  padding: "6px 0 24px 36px",
  children: /* @__PURE__ */ _jsx983(Flexer, {
    column: true,
    children
  })
});

// src/organisms/ProgressNav/List/List.tsx
import { jsx as _jsx984 } from "react/jsx-runtime";
var List4 = ({
  children
}) => /* @__PURE__ */ _jsx984(Container2, {
  children: /* @__PURE__ */ _jsx984(Flexer, {
    column: true,
    gap: "2px",
    children
  })
});
List4.Item = Item11;
List4.Sublist = Sublist;

// src/organisms/ProgressNav/Title/Title.tsx
import { jsx as _jsx985 } from "react/jsx-runtime";
var Title8 = ({
  children
}) => /* @__PURE__ */ _jsx985(Line, {
  color: colors.text.neutral,
  size: font.title.small,
  children
});

// src/organisms/ProgressNav/ProgressNav.tsx
import { jsx as _jsx986, jsxs as _jsxs81 } from "react/jsx-runtime";
var ProgressNav = ({
  children,
  parent,
  child
}) => {
  const [isOpen, setIsOpen] = useState19(false);
  const [maxHeight, setMaxHeight] = useState19(null);
  const [width, setWidth] = useState19(null);
  const floatingData = useFloating2({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [size({
      apply({
        availableHeight,
        rects
      }) {
        flushSync(() => {
          setMaxHeight(availableHeight);
          setWidth(rects.reference.width);
        });
      }
    }), shift({
      padding: 12
    })],
    whileElementsMounted: autoUpdate
  });
  const {
    refs,
    floatingStyles,
    context
  } = floatingData;
  const click = useClick(context);
  const dismiss = useDismiss2(context);
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions2([click, dismiss]);
  const dropdownMaxHeight = maxHeight ? maxHeight - 24 : maxHeight;
  const value = useMemo7(() => ({
    setIsOpen
  }), []);
  return /* @__PURE__ */ _jsx986(ProgressNavContext.Provider, {
    value,
    children: /* @__PURE__ */ _jsxs81(Container2, {
      children: [/* @__PURE__ */ _jsx986(Button2, {
        ref: refs.setReference,
        ...getReferenceProps(),
        children: /* @__PURE__ */ _jsxs81(Flexer, {
          alignCenter: true,
          spaceBetween: true,
          children: [/* @__PURE__ */ _jsxs81(Label8, {
            children: [child ? /* @__PURE__ */ _jsx986("span", {
              children: parent
            }) : /* @__PURE__ */ _jsx986("strong", {
              children: parent
            }), child ? /* @__PURE__ */ _jsx986("strong", {
              children: child
            }) : ""]
          }), " ", /* @__PURE__ */ _jsx986(Arrow, {
            children: "\u25BC"
          })]
        })
      }), isOpen && /* @__PURE__ */ _jsx986(FloatingFocusManager2, {
        context,
        modal: false,
        children: /* @__PURE__ */ _jsx986("div", {
          ref: refs.setFloating,
          style: {
            ...floatingStyles,
            width: width || "auto"
          },
          ...getFloatingProps(),
          children: /* @__PURE__ */ _jsx986(Dropdown, {
            height: dropdownMaxHeight,
            children
          })
        })
      })]
    })
  });
};
ProgressNav.List = List4;
ProgressNav.Title = Title8;
var Button2 = styled164.button.withConfig({
  displayName: "ProgressNav__Button",
  componentId: "sc-15m1m19-0"
})(["border:1px solid ", ";background-color:", ";border-radius:", ";box-shadow:", ";padding:9px 18px;width:100%;strong:not(:only-child){&:before{content:'/';color:", ";margin:0 6px;}}"], colors.line.neutralSoft, colors.surface.base, radius.medium, shadows.high, colors.text.neutral);
var Label8 = styled164.div.withConfig({
  displayName: "ProgressNav__Label",
  componentId: "sc-15m1m19-1"
})(["font-size:", ";color:", ";strong{color:", ";}"], font.body.medium, colors.text.neutral, colors.text.base);
var Arrow = styled164.span.withConfig({
  displayName: "ProgressNav__Arrow",
  componentId: "sc-15m1m19-2"
})(["font-size:8px;margin-left:12px;"]);

// src/organisms/ProgressSteps/Item/Item.tsx
import { styled as styled165 } from "styled-components";
import { jsx as _jsx987, jsxs as _jsxs82 } from "react/jsx-runtime";
var PADDING_TOP = 18;
var PADDING_TOP_TABLET = 30;
var PADDING_LEFT = 48;
var PADDING_LEFT_TABLET = 60;
var PADDING_RIGHT = 18;
var PADDING_RIGHT_TABLET = 36;
var PADDING_BOTTOM = 18;
var PADDING_BOTTOM_TABLET = 30;
var BADGE_MARGIN_TOP = PADDING_TOP - 1;
var BADGE_MARGIN_TOP_TABLET = PADDING_TOP_TABLET;
var BADGE_WIDTH = 24;
var VERTICAL_LINE_WIDTH = 2;
var State = {
  Collapsed: "collapsed",
  Expanded: "expanded"
};
var Variant = {
  Accent: "accent",
  Affirm: "affirm",
  Attention: "attention",
  Highlight: "highlight",
  Neutral: "neutral"
};
var getBackground6 = ({
  variant,
  state
}) => {
  if (state !== State.Expanded)
    return void 0;
  return colors.surface[variant];
};
var getBadgeBackground2 = ({
  variant,
  state
}) => {
  if (state === State.Expanded)
    return colors.surface.base;
  return colors.surface[variant];
};
var getBadgeForeground = ({
  variant,
  state
}) => {
  if (state === State.Collapsed && variant === Variant.Neutral)
    return colors.text.neutral;
  return colors.text.base;
};
var getLineBackground = ({
  variant,
  state
}) => {
  if (state === State.Expanded)
    return getBackground6({
      variant,
      state
    });
  return getBadgeBackground2({
    variant,
    state
  });
};
var Item12 = ({
  children,
  icon,
  variant = Variant.Neutral,
  state = State.Collapsed,
  shouldDisplayStartingVerticalLine,
  shouldDisplayEndingVerticalLine
}) => {
  const background = getBackground6({
    variant,
    state
  });
  const badgeBackground = getBadgeBackground2({
    variant,
    state
  });
  const badgeForeground = getBadgeForeground({
    variant,
    state
  });
  const lineBackground = getLineBackground({
    variant,
    state
  });
  return /* @__PURE__ */ _jsxs82(Container65, {
    background,
    "data-state": state,
    "data-starting-vertical-line": !!shouldDisplayStartingVerticalLine,
    "data-ending-vertical-line": !!shouldDisplayEndingVerticalLine,
    children: [/* @__PURE__ */ _jsxs82(VerticalLineContainer2, {
      children: [/* @__PURE__ */ _jsx987(VerticalLine2, {
        background: lineBackground
      }), /* @__PURE__ */ _jsx987(BadgeContainer2, {
        children: /* @__PURE__ */ _jsx987(Badge, {
          background: badgeBackground,
          color: badgeForeground,
          children: icon || "\u2192"
        })
      })]
    }), children]
  });
};
var VerticalLineContainer2 = styled165.div.withConfig({
  displayName: "Item__VerticalLineContainer",
  componentId: "sc-xuf6p4-0"
})(["position:absolute;top:0;left:0;width:", "px;height:100%;", "{width:", "px;}"], PADDING_LEFT, media.tablet, PADDING_LEFT_TABLET);
var VerticalLine2 = styled165.div.withConfig({
  displayName: "Item__VerticalLine",
  componentId: "sc-xuf6p4-1"
})(["position:absolute;top:0;bottom:0;right:", "px;width:", "px;background:", ";transform:translateX(-50%);", "{right:", "px;}"], PADDING_LEFT / 2 - VERTICAL_LINE_WIDTH, VERTICAL_LINE_WIDTH, ({
  background
}) => background || "none", media.tablet, PADDING_LEFT_TABLET / 2 - VERTICAL_LINE_WIDTH);
var BadgeContainer2 = styled165.div.withConfig({
  displayName: "Item__BadgeContainer",
  componentId: "sc-xuf6p4-2"
})(["position:absolute;padding-top:", "px;top:0;left:calc(50% - ", "px / 2);", "{padding-top:", "px;}"], BADGE_MARGIN_TOP, BADGE_WIDTH, media.tablet, BADGE_MARGIN_TOP_TABLET);
var Container65 = styled165.div.withConfig({
  displayName: "Item__Container",
  componentId: "sc-xuf6p4-3"
})(["position:relative;padding:", "px ", "px ", "px ", "px;background:", ";border-radius:", ";", "{padding:", "px ", "px ", "px ", "px;}&[data-state='collapsed'][data-starting-vertical-line='false']:first-child{padding-top:0;", "{padding-top:0;}}&[data-state='collapsed'] + &[data-state='collapsed']{padding-top:0;", "{padding-top:0;}}&[data-state='collapsed'] + &[data-state='collapsed'][data-ending-vertical-line='false']:last-child{", "{height:0;}}&[data-state='collapsed'][data-ending-vertical-line='false']:last-child{padding-bottom:0;", "{height:50%;}}"], PADDING_TOP, PADDING_RIGHT, PADDING_BOTTOM, PADDING_LEFT, ({
  background
}) => background || "none", radius.large, media.tablet, PADDING_TOP_TABLET, PADDING_RIGHT_TABLET, PADDING_BOTTOM_TABLET, PADDING_LEFT_TABLET, BadgeContainer2, BadgeContainer2, VerticalLine2, VerticalLine2);

// src/organisms/ProgressSteps/ProgressSteps.tsx
import { jsx as _jsx988 } from "react/jsx-runtime";
var ProgressSteps = ({
  children
}) => {
  return /* @__PURE__ */ _jsx988(Flexer, {
    column: true,
    gap: "2px",
    children
  });
};
ProgressSteps.Item = Item12;

// src/organisms/Tabs/Tabs.tsx
import { styled as styled168 } from "styled-components";

// src/organisms/Tabs/Item/Item.tsx
import { styled as styled167 } from "styled-components";

// src/organisms/Tabs/Item/Counter.ts
import { styled as styled166 } from "styled-components";
var Counter = styled166.div.withConfig({
  displayName: "Counter",
  componentId: "sc-1gsm8pi-0"
})(["display:inline-block;margin-left:6px;"]);

// src/organisms/Tabs/Item/Item.tsx
import { jsx as _jsx989 } from "react/jsx-runtime";
var Item13 = ({
  children,
  isActive,
  onClick = void 0
}) => {
  const handleClick = (e) => {
    e.preventDefault();
    if (onClick)
      onClick();
  };
  return /* @__PURE__ */ _jsx989(Container66, {
    isActive,
    onClick: handleClick,
    children
  });
};
Item13.Counter = Counter;
var Container66 = styled167.button.withConfig({
  displayName: "Item__Container",
  componentId: "sc-eyb54w-0"
})(["color:", ";font-family:inherit;font-size:", ";text-decoration:none;display:inline-block;width:100%;padding:7px 12px;border-radius:", ";background:", ";cursor:pointer;", "{width:auto;flex:0 0 auto;}&:focus-visible{border:", ";outline:none;}", "{&:hover{background:", ";}}"], theme.primary.contrast, font.body.medium, radius.medium, ({
  isActive
}) => isActive ? theme.primary.primaryAction : theme.grey.surface1, media.tablet, borders.interactive.medium, media.pointer, ({
  isActive
}) => isActive ? theme.primary.primaryAction : theme.primary.secondaryAction);

// src/organisms/Tabs/Tabs.tsx
import { jsx as _jsx990 } from "react/jsx-runtime";
var Tabs = ({
  children,
  gap
}) => /* @__PURE__ */ _jsx990(Container67, {
  gap,
  children
});
Tabs.Item = Item13;
var Container67 = styled168.div.withConfig({
  displayName: "Tabs__Container",
  componentId: "sc-m54zil-0"
})(["", "{display:flex;flex-wrap:wrap;gap:", ";}"], media.tablet, ({
  gap
}) => gap ?? "2px");

// src/organisms/Table/Body/Cell/Cell.tsx
import styled171, { css as css2 } from "styled-components";

// src/organisms/Table/Body/Cell/Content/Content.tsx
import { styled as styled169 } from "styled-components";
import { jsx as _jsx991 } from "react/jsx-runtime";
var Content13 = ({
  children,
  alignRight,
  tabletAlignRight
}) => /* @__PURE__ */ _jsx991(Container68, {
  alignRight,
  tabletAlignRight,
  children
});
var Container68 = styled169.div.withConfig({
  displayName: "Content__Container",
  componentId: "sc-wnfkjg-0"
})(["display:flex;align-items:center;padding:6px 0;", " ", " ", "{width:100%;height:100%;margin:0;padding:12px 0;", " ", "}"], ({
  alignRight
}) => alignRight && `text-align: right;`, ({
  alignRight
}) => alignRight && `justify-content: flex-end;`, media.tablet, ({
  tabletAlignRight
}) => tabletAlignRight && `text-align: right;`, ({
  tabletAlignRight
}) => tabletAlignRight && `justify-content: flex-end;`);

// src/organisms/Table/Body/Cell/ContentLink/ContentLink.tsx
import { styled as styled170 } from "styled-components";
import { jsx as _jsx992 } from "react/jsx-runtime";
var ContentLink = ({
  children,
  alignRight,
  to,
  Link: Link5,
  tabletAlignRight
}) => /* @__PURE__ */ _jsx992(Wrapper9, {
  as: Link5,
  to,
  children: /* @__PURE__ */ _jsx992(Container69, {
    alignRight,
    tabletAlignRight,
    children
  })
});
var Wrapper9 = styled170.a.withConfig({
  displayName: "ContentLink__Wrapper",
  componentId: "sc-1bhhi70-0"
})(["color:", ";text-decoration:none;width:100%;height:100%;cursor:pointer;", "{&:hover{color:", ";}}"], colors.text.base, media.pointer, colors.text.interactive);
var Container69 = styled170.div.withConfig({
  displayName: "ContentLink__Container",
  componentId: "sc-1bhhi70-1"
})(["display:flex;align-items:center;padding:6px 0;", " ", " ", "{width:100%;height:100%;margin:0;padding:12px 0;", " ", "}"], ({
  alignRight
}) => alignRight && `text-align: right;`, ({
  alignRight
}) => alignRight && `justify-content: flex-end;`, media.tablet, ({
  tabletAlignRight
}) => tabletAlignRight && `text-align: right;`, ({
  tabletAlignRight
}) => tabletAlignRight && `justify-content: flex-end;`);

// src/organisms/Table/Body/Cell/Cell.tsx
import { jsx as _jsx993 } from "react/jsx-runtime";
var Cell = ({
  children,
  width
}) => /* @__PURE__ */ _jsx993(Container70, {
  width,
  children: !!children && children
});
Cell.Content = Content13;
Cell.ContentLink = ContentLink;
var Container70 = styled171.div.withConfig({
  displayName: "Cell__Container",
  componentId: "sc-maki7u-0"
})(["", ""], ({
  width
}) => css2(["min-width:0;word-break:break-word;margin:3px 0;", "{flex:0 0 ", ";margin:0;}"], media.tablet, width || "auto"));

// src/organisms/Table/Body/Row/Row.tsx
import { styled as styled172 } from "styled-components";
import { handleKeyDownEvent as handleKeyDownEvent8 } from "@scouts/helpers";
import { jsx as _jsx994 } from "react/jsx-runtime";
var Row2 = ({
  children,
  onClick
}) => {
  const isInteractive = !!onClick;
  return /* @__PURE__ */ _jsx994(Container71, {
    onClick,
    onKeyDown: isInteractive ? handleKeyDownEvent8(onClick) : void 0,
    role: isInteractive ? "button" : void 0,
    tabIndex: isInteractive ? 0 : void 0,
    children
  });
};
var Container71 = styled172.div.withConfig({
  displayName: "Row__Container",
  componentId: "sc-1gxm59g-0"
})(["padding:12px 0;border-top:1px solid ", ";", "{position:relative;display:flex;padding:0;&:hover{background:", ";&:before,&:after{content:'';position:absolute;top:-1px;width:18px;height:calc(100% + 2px);background:", ";}&:before{right:100%;}&:after{left:100%;}}}", "{", "}"], colors.line.neutralSoft, media.tablet, colors.surface.neutral, colors.surface.neutral, media.pointer, ({
  onClick
}) => onClick && "cursor: pointer;");

// src/organisms/Table/Body/Body.tsx
import { jsx as _jsx995 } from "react/jsx-runtime";
var Body5 = ({
  children
}) => /* @__PURE__ */ _jsx995(Container2, {
  children
});
Body5.Row = Row2;
Body5.Cell = Cell;

// src/organisms/Table/Footer/Footer.tsx
import { styled as styled174 } from "styled-components";

// src/organisms/Table/Footer/Cell/Cell.tsx
import { styled as styled173 } from "styled-components";
import { handleKeyDownEvent as handleKeyDownEvent9 } from "@scouts/helpers";
import { jsx as _jsx996 } from "react/jsx-runtime";
var Cell2 = ({
  width,
  alignRight,
  children = null,
  action,
  tabletAlignRight
}) => {
  const handleClick = (e) => {
    e.preventDefault();
    if (action)
      action();
  };
  const isAction = !!action;
  return /* @__PURE__ */ _jsx996(Container72, {
    width,
    tabletAlignRight,
    alignRight,
    isAction,
    onClick: isAction ? handleClick : void 0,
    onKeyDown: isAction ? handleKeyDownEvent9(action) : void 0,
    role: isAction ? "button" : void 0,
    tabIndex: isAction ? 0 : void 0,
    children
  });
};
var Container72 = styled173.div.withConfig({
  displayName: "Cell__Container",
  componentId: "sc-1994bf8-0"
})(["color:", ";font-size:", ";flex:0 0 ", ";padding:12px 0;", " ", " ", "{", " ", "}", ""], colors.text.neutral, font.body.medium, ({
  width
}) => width || "auto", ({
  alignRight
}) => alignRight && `text-align: right;`, ({
  alignRight
}) => alignRight && `justify-content: flex-end;`, media.tablet, ({
  tabletAlignRight
}) => tabletAlignRight && `text-align: right;`, ({
  tabletAlignRight
}) => tabletAlignRight && `justify-content: flex-end;`, ({
  isAction
}) => isAction && `
        ${media.pointer} {
            &:hover {
                color: ${colors.text.base};
                cursor: pointer;
            }
        }
    `);

// src/organisms/Table/Footer/Row/Row.tsx
import { jsx as _jsx997 } from "react/jsx-runtime";
var Row3 = ({
  children
}) => /* @__PURE__ */ _jsx997(Flexer, {
  children
});

// src/organisms/Table/Footer/Footer.tsx
import { jsx as _jsx998 } from "react/jsx-runtime";
var Footer2 = ({
  children
}) => /* @__PURE__ */ _jsx998(Container73, {
  children
});
Footer2.Row = Row3;
Footer2.Cell = Cell2;
var Container73 = styled174.div.withConfig({
  displayName: "Footer__Container",
  componentId: "sc-umtoae-0"
})(["display:none;border-top:1px solid ", ";", "{display:block;}"], colors.line.neutralSoft, media.tablet);

// src/organisms/Table/Header/Header.tsx
import { styled as styled176 } from "styled-components";

// src/organisms/Table/Header/Cell/Cell.tsx
import { styled as styled175 } from "styled-components";
import { handleKeyDownEvent as handleKeyDownEvent10 } from "@scouts/helpers";
import { jsx as _jsx999 } from "react/jsx-runtime";
var Cell3 = ({
  width,
  alignRight,
  children = null,
  action,
  tabletAlignRight
}) => {
  const handleClick = (e) => {
    e.preventDefault();
    if (action)
      action();
  };
  const isAction = !!action;
  return /* @__PURE__ */ _jsx999(Container74, {
    width,
    tabletAlignRight,
    alignRight,
    isAction,
    onClick: isAction ? handleClick : void 0,
    onKeyDown: isAction ? handleKeyDownEvent10(action) : void 0,
    role: isAction ? "button" : void 0,
    tabIndex: isAction ? 0 : void 0,
    children
  });
};
var Container74 = styled175.div.withConfig({
  displayName: "Cell__Container",
  componentId: "sc-nw66wr-0"
})(["color:", ";font-size:", ";flex:0 0 ", ";padding:12px 0;", " ", " ", "{", " ", "}", ""], colors.text.neutral, font.body.medium, ({
  width
}) => width || "auto", ({
  alignRight
}) => alignRight && `text-align: right;`, ({
  alignRight
}) => alignRight && `justify-content: flex-end;`, media.tablet, ({
  tabletAlignRight
}) => tabletAlignRight && `text-align: right;`, ({
  tabletAlignRight
}) => tabletAlignRight && `justify-content: flex-end;`, ({
  isAction
}) => isAction && `
        ${media.pointer} {
            &:hover {
                color: ${colors.text.base};
                cursor: pointer;
            }
        }
    `);

// src/organisms/Table/Header/Row/Row.tsx
import { jsx as _jsx1000 } from "react/jsx-runtime";
var Row4 = ({
  children
}) => /* @__PURE__ */ _jsx1000(Flexer, {
  children
});

// src/organisms/Table/Header/Header.tsx
import { jsx as _jsx1001 } from "react/jsx-runtime";
var Header8 = ({
  children
}) => /* @__PURE__ */ _jsx1001(Container75, {
  children
});
Header8.Row = Row4;
Header8.Cell = Cell3;
var Container75 = styled176.div.withConfig({
  displayName: "Header__Container",
  componentId: "sc-1uj6xvc-0"
})(["display:none;", "{display:block;}"], media.tablet);

// src/organisms/Table/Table.tsx
import { jsx as _jsx1002 } from "react/jsx-runtime";
var Table = ({
  children
}) => /* @__PURE__ */ _jsx1002(Container2, {
  children: !!children && children
});
Table.Header = Header8;
Table.Body = Body5;
Table.Footer = Footer2;

// src/organisms/Toast/Toaster.tsx
import { Toaster as SonnerToaster } from "sonner";
import { styled as styled177 } from "styled-components";
import { jsx as _jsx1003 } from "react/jsx-runtime";
var Toaster = () => {
  const isTablet = useMediaQuery("(min-width: 600px)");
  return /* @__PURE__ */ _jsx1003(Wrapper10, {
    children: /* @__PURE__ */ _jsx1003(SonnerToaster, {
      position: isTablet ? "bottom-center" : "top-center",
      duration: 1e4,
      style: {
        zIndex: Z_INDEX_TOASTER
      },
      visibleToasts: 3,
      closeButton: true,
      richColors: true,
      expand: true
    })
  });
};
var Wrapper10 = styled177.div.withConfig({
  displayName: "Toaster__Wrapper",
  componentId: "sc-al52td-0"
})(["& ol[data-sonner-toaster]{--width:480px !important;}"]);

// src/organisms/Toast/toasts.tsx
import { toast } from "sonner";

// src/organisms/Toast/Toast.ts
import { styled as styled178 } from "styled-components";
import { hexToRgbA as hexToRgbA16 } from "@scouts/helpers";
var ToastClose = styled178.button.withConfig({
  displayName: "Toast__ToastClose",
  componentId: "sc-1pzkzhh-0"
})(["color:", ";font-size:", ";line-height:1.5;display:flex;align-items:center;justify-content:center;position:absolute;top:0;right:0;padding:12px;background:none;border:0;cursor:pointer;@media (min-width:600px){color:", ";font-size:", ";}"], ({
  alertType
}) => alertType === "error" ? colors.text.base : colors.text.inverted, font.body.medium, colors.text.base, font.body.large);
var Toast = styled178.div.withConfig({
  displayName: "Toast",
  componentId: "sc-1pzkzhh-1"
})(["color:", ";font-size:", ";font-family:", ";font-weight:", ";line-height:1.5;text-align:center;word-break:break-word;background:", ";border-radius:", ";box-shadow:", ";@media (min-width:600px){color:", ";font-size:", ";font-weight:", ";width:480px;background:", ";box-shadow:", ";}"], ({
  alertType
}) => alertType === "error" ? colors.text.base : colors.text.inverted, font.body.medium, font.family.body, font.weight.medium, ({
  alertType
}) => alertType === "error" ? hexToRgbA16(colors.surface.alert, 0.8) : hexToRgbA16(colors.surface.inverted, 0.8), radius.medium, shadows.medium, colors.text.base, font.body.large, font.weight.normal, ({
  backgroundColor
}) => backgroundColor, shadows.low);
var ToastBody = styled178.div.withConfig({
  displayName: "Toast__ToastBody",
  componentId: "sc-1pzkzhh-2"
})(["max-height:240px;overflow-y:auto;padding:12px 42px;"]);

// src/organisms/Toast/toasts.tsx
import { jsx as _jsx1004, jsxs as _jsxs83 } from "react/jsx-runtime";
var addErrorToast = ({
  body
}) => {
  return toast.custom((t) => /* @__PURE__ */ _jsxs83(Toast, {
    backgroundColor: colors.surface.alert,
    alertType: "error",
    role: "alert",
    children: [/* @__PURE__ */ _jsx1004(ToastClose, {
      alertType: "error",
      onClick: () => toast.dismiss(t),
      children: "\u2715"
    }), /* @__PURE__ */ _jsx1004(ToastBody, {
      "data-sentry-unmask": "true",
      children: body
    })]
  }));
};
var addSuccessToast = ({
  body
}) => {
  return toast.custom((t) => /* @__PURE__ */ _jsxs83(Toast, {
    backgroundColor: colors.surface.affirm,
    alertType: "success",
    role: "status",
    children: [/* @__PURE__ */ _jsx1004(ToastClose, {
      alertType: "success",
      onClick: () => toast.dismiss(t),
      children: "\u2715"
    }), /* @__PURE__ */ _jsx1004(ToastBody, {
      "data-sentry-unmask": "true",
      children: body
    })]
  }));
};

// src/organisms/Questionnaire/Questionnaire.tsx
import { useCallback as useCallback3, useMemo as useMemo8, useState as useState21 } from "react";
import { useScrollToHashElement as useScrollToHashElement2 } from "@scouts/helpers";

// src/organisms/Questionnaire/List/List.tsx
import { cloneElement as cloneElement6, useEffect as useEffect12, useState as useState20, Children as Children4 } from "react";
import { useHistory as useHistory5 } from "react-router-dom";
import { styled as styled180 } from "styled-components";
import { useScrollToHashElement } from "@scouts/helpers";

// src/organisms/Questionnaire/questionnaire-context/questionnaire-context.ts
import { createContext as createContext5 } from "react";
var QuestionnaireStateContext = /* @__PURE__ */ createContext5(null);
var QuestionnaireApiContext = /* @__PURE__ */ createContext5(null);

// src/organisms/Questionnaire/questionnaire-context/use-questionnaire.ts
import { useContext as useContext5 } from "react";
var useQuestionnaireState = () => {
  const value = useContext5(QuestionnaireStateContext);
  if (value === null)
    throw new Error("`useQuestionnaire` must be used within a `QuestionnaireStateContext.Provider`");
  return value;
};
var useQuestionnaireApi = () => {
  const value = useContext5(QuestionnaireApiContext);
  if (value === null)
    throw new Error("`useQuestionnaireApi` must be used within a `QuestionnaireApiContext.Provider`");
  return value;
};
var useQuestionnaire = () => {
  const questionnaireState = useQuestionnaireState();
  const questionnaireApi = useQuestionnaireApi();
  return {
    ...questionnaireState,
    ...questionnaireApi
  };
};

// src/organisms/Questionnaire/List/Item/Item.tsx
import { styled as styled179 } from "styled-components";

// src/organisms/Questionnaire/List/Item/Body/Body.tsx
import { jsx as _jsx1005 } from "react/jsx-runtime";
var Body6 = ({
  children
}) => /* @__PURE__ */ _jsx1005(Container2, {
  children
});
Body6.displayName = "Questionnaire.List.Item.Body";

// src/organisms/Questionnaire/List/Item/Footer/Footer.tsx
import { Children as Children3 } from "react";
import { jsx as _jsx1006 } from "react/jsx-runtime";
var Footer3 = ({
  children
}) => {
  const childCount = Children3.toArray(children).length;
  const hasSingleButton = childCount <= 1;
  const gap = !hasSingleButton ? "12px" : void 0;
  return /* @__PURE__ */ _jsx1006(Spacer, {
    marginTop: "12px",
    children: /* @__PURE__ */ _jsx1006(Flexer, {
      justifyEnd: hasSingleButton,
      spaceBetween: !hasSingleButton,
      gap,
      width: "100%",
      flexWrap: true,
      children
    })
  });
};
Footer3.displayName = "Questionnaire.List.Item.Footer";

// src/organisms/Questionnaire/List/Item/Header/Description.tsx
import { jsx as _jsx1007 } from "react/jsx-runtime";
var Description3 = ({
  children
}) => /* @__PURE__ */ _jsx1007(Line, {
  size: font.body.small,
  tabletSize: font.body.large,
  children
});
Description3.displayName = "Questionnaire.List.Item.Header.Description";

// src/organisms/Questionnaire/List/Item/Header/Heading/Help.tsx
import { useToggle as useToggle2 } from "@scouts/helpers";
import { jsx as _jsx1008, jsxs as _jsxs84, Fragment as _Fragment9 } from "react/jsx-runtime";
var Help = ({
  children,
  modalTitle = "Help"
}) => {
  const [isModalOpen, open, close] = useToggle2();
  return /* @__PURE__ */ _jsxs84(_Fragment9, {
    children: [/* @__PURE__ */ _jsx1008(Spacer, {
      tabletMarginTop: "3px",
      children: /* @__PURE__ */ _jsx1008(BadgeButton, {
        onClick: open,
        variant: "dim",
        size: "small",
        children: "?"
      })
    }), isModalOpen && /* @__PURE__ */ _jsxs84(Modal, {
      onClose: close,
      children: [/* @__PURE__ */ _jsx1008(ModalHeader, {
        children: /* @__PURE__ */ _jsx1008(ModalTitle, {
          children: modalTitle
        })
      }), /* @__PURE__ */ _jsx1008(ModalBody, {
        children: typeof children === "function" ? children({
          closeModal: close,
          openModal: open
        }) : children
      }), /* @__PURE__ */ _jsx1008(ModalFooter, {
        children: /* @__PURE__ */ _jsx1008(ModalActions, {
          children: /* @__PURE__ */ _jsx1008(Button, {
            size: "large",
            isMobileFullWidth: true,
            onClick: close,
            children: "Got it"
          })
        })
      })]
    })]
  });
};
Help.displayName = "Questionnaire.List.Item.Header.Heading.Help";

// src/organisms/Questionnaire/List/Item/Header/Heading/Title.tsx
import { jsx as _jsx1009 } from "react/jsx-runtime";
var Title9 = ({
  children
}) => /* @__PURE__ */ _jsx1009(Heading, {
  children
});
Title9.displayName = "Questionnaire.List.Item.Header.Heading.Title";

// src/organisms/Questionnaire/List/Item/Header/Heading/Heading.tsx
import { jsx as _jsx1010 } from "react/jsx-runtime";
var Heading3 = ({
  children
}) => /* @__PURE__ */ _jsx1010(Container2, {
  relative: true,
  children: /* @__PURE__ */ _jsx1010(Flexer, {
    spaceBetween: true,
    gap: "12px",
    alignStart: true,
    children
  })
});
Heading3.displayName = "Questionnaire.List.Item.Header.Heading";
Heading3.Title = Title9;
Heading3.Help = Help;

// src/organisms/Questionnaire/List/Item/Header/Header.tsx
import { jsx as _jsx1011 } from "react/jsx-runtime";
var Header9 = ({
  children
}) => /* @__PURE__ */ _jsx1011(Container2, {
  relative: true,
  children: /* @__PURE__ */ _jsx1011(Flexer, {
    column: true,
    gap: "6px",
    children
  })
});
Header9.displayName = "Questionnaire.List.Item.Header";
Header9.Heading = Heading3;
Header9.Description = Description3;

// src/organisms/Questionnaire/List/Item/Item.tsx
import { jsx as _jsx1012 } from "react/jsx-runtime";
var Item14 = ({
  children,
  padding = "48px 24px 120px",
  id,
  tabletPadding = "120px 0",
  tabletWidth = "576px"
}) => {
  const props = {
    ...id ? {
      id
    } : void 0,
    padding,
    tabletPadding,
    tabletWidth
  };
  return /* @__PURE__ */ _jsx1012(Container76, {
    ...props,
    children: /* @__PURE__ */ _jsx1012(Flexer, {
      column: true,
      gap: "30px",
      width: "100%",
      style: {
        minWidth: 0
      },
      children
    })
  });
};
var Container76 = styled179.div.withConfig({
  displayName: "Item__Container",
  componentId: "sc-7lmaqk-0"
})(["width:100%;height:100%;display:grid;place-items:center;", " ", "{", " margin:0 auto;", "}"], ({
  padding
}) => padding && `padding: ${padding};`, media.tablet, ({
  tabletWidth
}) => tabletWidth && `width: ${tabletWidth};`, ({
  tabletPadding
}) => tabletPadding && `padding: ${tabletPadding};`);
Item14.displayName = "Questionnaire.List.Item";
Item14.Body = Body6;
Item14.Header = Header9;
Item14.Footer = Footer3;

// src/organisms/Questionnaire/List/List.tsx
import { jsx as _jsx1013 } from "react/jsx-runtime";
var List5 = ({
  children,
  firstIncompleteItemIndex = 0
}) => {
  const history = useHistory5();
  const {
    indexInView
  } = useQuestionnaire();
  const {
    hashElement,
    scrollToHashElement
  } = useScrollToHashElement({
    skipAutoScroll: true
  });
  const [urlHash, setUrlHash] = useState20();
  const childrenToDisplay = children.filter((child) => child);
  useEffect12(() => {
    if (indexInView !== void 0) {
      const updatedNextChildId2 = childrenToDisplay[indexInView]?.props.id;
      setUrlHash(updatedNextChildId2);
      return scrollToHashElement({
        isSmooth: true
      });
    }
    if (hashElement !== void 0) {
      return scrollToHashElement({
        isSmooth: false
      });
    }
    const updatedNextChildId = childrenToDisplay[firstIncompleteItemIndex]?.props.id;
    setUrlHash(updatedNextChildId);
  }, [childrenToDisplay, firstIncompleteItemIndex, hashElement, indexInView, scrollToHashElement]);
  useEffect12(() => {
    if (urlHash) {
      history.push(`#${urlHash}`);
    }
  }, [history, urlHash]);
  return /* @__PURE__ */ _jsx1013(ScrollContainer, {
    children: Children4.map(childrenToDisplay, (element, index) => {
      return element && /* @__PURE__ */ _jsx1013(ScrollItem, {
        children: /* @__PURE__ */ cloneElement6(element, {
          id: element.props.id,
          nextIndex: index === childrenToDisplay.length - 1 ? void 0 : index + 1,
          currentIndex: index,
          prevIndex: index === 0 ? void 0 : index - 1
        })
      });
    })
  });
};
var ScrollContainer = styled180.div.withConfig({
  displayName: "List__ScrollContainer",
  componentId: "sc-pl79az-0"
})(["overflow-y:scroll;height:100%;"]);
var ScrollItem = styled180.div.withConfig({
  displayName: "List__ScrollItem",
  componentId: "sc-pl79az-1"
})(["min-height:100%;display:grid;place-items:center;"]);
List5.Item = Item14;

// src/organisms/Questionnaire/Progress/Progress.tsx
import { jsx as _jsx1014 } from "react/jsx-runtime";
var Progress = ({
  lastCompletedItemIndex,
  totalItemsCount
}) => {
  const canComputeProgress = lastCompletedItemIndex !== void 0 && totalItemsCount !== void 0;
  if (!canComputeProgress || totalItemsCount <= 1)
    return null;
  const percentage = canComputeProgress ? ((lastCompletedItemIndex ?? 0) + 1) / totalItemsCount * 100 : void 0;
  return /* @__PURE__ */ _jsx1014(ProgressLine, {
    width: percentage
  });
};

// src/organisms/Questionnaire/Questionnaire.tsx
import { jsx as _jsx1015 } from "react/jsx-runtime";
var Questionnaire = ({
  children
}) => {
  const {
    scrollToHashElement
  } = useScrollToHashElement2({
    skipAutoScroll: true
  });
  const [indexInView, setIndexInView] = useState21();
  const scrollToNext = useCallback3((currentIndex) => {
    const index = (currentIndex ?? 0) + 1;
    setIndexInView(index);
    if (index === indexInView)
      scrollToHashElement({
        isSmooth: true
      });
  }, [indexInView, scrollToHashElement]);
  const scrollToPrevious = useCallback3((currentIndex) => {
    const index = Math.max((currentIndex ?? 0) - 1, 0);
    setIndexInView(index);
    if (index === indexInView)
      scrollToHashElement({
        isSmooth: true
      });
  }, [indexInView, scrollToHashElement]);
  const scrollTo = useCallback3((index) => {
    setIndexInView(index);
    if (index === indexInView)
      scrollToHashElement({
        isSmooth: true
      });
  }, [indexInView, scrollToHashElement]);
  const stateContextValue = useMemo8(() => ({
    indexInView
  }), [indexInView]);
  const apiContextValue = useMemo8(() => ({
    scrollTo,
    scrollToPrevious,
    scrollToNext
  }), [scrollTo, scrollToPrevious, scrollToNext]);
  return /* @__PURE__ */ _jsx1015(QuestionnaireStateContext.Provider, {
    value: stateContextValue,
    children: /* @__PURE__ */ _jsx1015(QuestionnaireApiContext.Provider, {
      value: apiContextValue,
      children
    })
  });
};
Questionnaire.List = List5;
Questionnaire.Progress = Progress;

// src/organisms/Questionnaire/use-questionnaire-config.ts
import { useCallback as useCallback4, useMemo as useMemo9, useState as useState22 } from "react";
var useQuestionnaireConfig = (config) => {
  const {
    questions
  } = config;
  const [skippedQuestions, setSkippedQuestions] = useState22([]);
  const getIsSkipped = useCallback4((questionId) => skippedQuestions.includes(questionId), [skippedQuestions]);
  const handleSkipQuestion = useCallback4((questionId) => {
    setSkippedQuestions((prevState) => {
      if (!prevState.includes(questionId)) {
        return [...prevState, questionId];
      }
      return prevState;
    });
  }, []);
  const mappedQuestions = useMemo9(() => questions.map((question) => ({
    ...question,
    isAnswered: getIsSkipped(question.id) || question.isAnswered
  })), [questions, getIsSkipped]);
  const filteredQuestions = useMemo9(() => mappedQuestions.filter((question) => !question.shouldHide), [mappedQuestions]);
  const hasAnsweredAllQuestions = useMemo9(() => filteredQuestions.every((question) => question.isAnswered), [filteredQuestions]);
  const currentQuestionIndex = useMemo9(() => {
    const firstUnansweredIndex = filteredQuestions.findIndex((question) => !question.isAnswered);
    return Math.max(firstUnansweredIndex, 0);
  }, [filteredQuestions]);
  const getIsLastQuestion = useCallback4((questionId) => {
    const index = filteredQuestions.findIndex((question) => question.id === questionId);
    return index !== -1 && index === filteredQuestions.length - 1;
  }, [filteredQuestions]);
  const totalQuestions = filteredQuestions.length;
  return {
    filteredQuestions,
    currentQuestionIndex,
    hasAnsweredAllQuestions,
    handleSkipQuestion,
    getIsSkipped,
    getIsLastQuestion,
    totalQuestions,
    skippedQuestions
  };
};

// src/organisms/Questionnaire/ConfiguredQuestionnaire.tsx
import { useCallback as useCallback5 } from "react";
import { jsx as _jsx1016, Fragment as _Fragment10, jsxs as _jsxs85 } from "react/jsx-runtime";
var ConfiguredQuestionnaire = ({
  questions,
  onComplete,
  onNext,
  showProgressBar = false,
  isLoading = false
}) => {
  const {
    filteredQuestions,
    currentQuestionIndex,
    hasAnsweredAllQuestions,
    handleSkipQuestion,
    getIsLastQuestion,
    totalQuestions
  } = useQuestionnaireConfig({
    questions,
    onComplete,
    showProgressBar,
    isLoading
  });
  return /* @__PURE__ */ _jsx1016(Questionnaire, {
    children: /* @__PURE__ */ _jsx1016(QuestionnaireContents, {
      filteredQuestions,
      currentQuestionIndex,
      hasAnsweredAllQuestions,
      handleSkipQuestion,
      getIsLastQuestion,
      totalQuestions,
      showProgressBar,
      onComplete,
      onNext
    })
  });
};
var QuestionnaireContents = ({
  filteredQuestions,
  currentQuestionIndex,
  hasAnsweredAllQuestions,
  handleSkipQuestion,
  getIsLastQuestion,
  totalQuestions,
  showProgressBar,
  onComplete,
  onNext
}) => {
  const {
    scrollToNext,
    scrollToPrevious
  } = useQuestionnaire();
  const handleSkipClick = useCallback5((currentIndex, questionId) => {
    handleSkipQuestion(questionId);
    const isLastQuestion = getIsLastQuestion(questionId);
    if (isLastQuestion && onComplete) {
      onComplete();
    } else {
      scrollToNext(currentIndex);
    }
  }, [handleSkipQuestion, getIsLastQuestion, onComplete, scrollToNext]);
  const handleNextClick = useCallback5((currentIndex, questionId) => {
    const isLastQuestion = getIsLastQuestion(questionId);
    if (onNext) {
      onNext(currentIndex, questionId);
      return;
    }
    if (isLastQuestion && onComplete) {
      onComplete();
    } else {
      scrollToNext(currentIndex);
    }
  }, [getIsLastQuestion, onNext, onComplete, scrollToNext]);
  const handleBackClick = useCallback5((currentIndex) => {
    scrollToPrevious(currentIndex);
  }, [scrollToPrevious]);
  return /* @__PURE__ */ _jsxs85(_Fragment10, {
    children: [showProgressBar && /* @__PURE__ */ _jsx1016(Questionnaire.Progress, {
      lastCompletedItemIndex: currentQuestionIndex,
      totalItemsCount: totalQuestions
    }), /* @__PURE__ */ _jsx1016(Questionnaire.List, {
      firstIncompleteItemIndex: currentQuestionIndex,
      children: filteredQuestions.map((question, index) => {
        const {
          component: Component,
          id,
          isReadOnly,
          props = {}
        } = question;
        const isQuestionVisible = hasAnsweredAllQuestions || index <= currentQuestionIndex;
        if (!isQuestionVisible) {
          return null;
        }
        return /* @__PURE__ */ _jsx1016(Component, {
          id,
          isLastQuestion: getIsLastQuestion(id),
          isReadOnly,
          onBack: () => handleBackClick(index),
          onNext: () => handleNextClick(index, id),
          onSkip: () => handleSkipClick(index, id),
          ...props
        }, id);
      })
    })]
  });
};

// src/components/PopoverProvider.jsx
import "react-hint/css/index.css";
import * as PropTypes from "prop-types";
import * as React from "react";
import ReactHintFactory from "react-hint";
import { styled as styled181 } from "styled-components";
import { jsx as _jsx1017 } from "react/jsx-runtime";
var ReactHint = ReactHintFactory(React);
var PopoverContent = styled181.div.withConfig({
  displayName: "PopoverProvider__PopoverContent",
  componentId: "sc-clekpk-0"
})(["background:", ";border-radius:4px;box-shadow:", ";padding:12px 16px;white-space:pre-line;"], colors.surface.base, shadows.medium);
var PopoverProviderContainer = styled181.div.withConfig({
  displayName: "PopoverProvider__PopoverProviderContainer",
  componentId: "sc-clekpk-1"
})([".react-hint:after{content:none;}"]);
var PopoverProvider = /* @__PURE__ */ React.forwardRef((props, ref) => /* @__PURE__ */ _jsx1017(PopoverProviderContainer, {
  children: /* @__PURE__ */ _jsx1017(ReactHint, {
    ref,
    ...props
  })
}));
PopoverProvider.defaultProps = {
  attribute: "data-popover-default",
  autoPosition: true,
  className: "react-hint",
  events: {
    click: true
  },
  delay: {
    show: 0
  },
  onRenderContent: (target, content) => /* @__PURE__ */ _jsx1017(PopoverContent, {
    children: content
  }),
  persist: true
};
PopoverProvider.propTypes = {
  attribute: PropTypes.string,
  autoPosition: PropTypes.bool,
  className: PropTypes.string,
  events: PropTypes.object,
  delay: PropTypes.object,
  onRenderContent: PropTypes.func,
  persist: PropTypes.bool
};

// src/components/Tooltips.jsx
import "react-hint/css/index.css";
import * as PropTypes2 from "prop-types";
import * as React2 from "react";
import ReactHintFactory2 from "react-hint";
import { styled as styled182 } from "styled-components";
import { jsx as _jsx1018 } from "react/jsx-runtime";
var ReactHint2 = ReactHintFactory2(React2);
var Body7 = styled182.div.withConfig({
  displayName: "Tooltips__Body",
  componentId: "sc-1curwgh-0"
})(["color:", ";font-size:", ";line-height:1.3;white-space:pre-line;max-width:75vw;padding:10px;background:", ";border-radius:5px;", ""], colors.text.inverted, font.body.medium, colors.surface.inverted, tablet`
        max-width: 50vw;
    `);
var getHintAttribute = (mode) => `data-hint-${mode}`;
var getHintPosition = (mode) => `data-hint-${mode}-at`;
var TooltipProvider = ({
  autoPosition,
  events,
  delay,
  onRenderContent,
  persist
}) => /* @__PURE__ */ _jsx1018(ReactHint2, {
  attribute: getHintAttribute("default"),
  autoPosition,
  events,
  delay,
  onRenderContent,
  persist
});
TooltipProvider.defaultProps = {
  autoPosition: true,
  events: {
    focus: true,
    hover: true
  },
  delay: {
    show: 100
  },
  onRenderContent: (target, content) => /* @__PURE__ */ _jsx1018(Body7, {
    children: content
  }),
  persist: false
};
TooltipProvider.propTypes = {
  autoPosition: PropTypes2.bool,
  events: PropTypes2.object,
  delay: PropTypes2.object,
  onRenderContent: PropTypes2.func,
  persist: PropTypes2.bool
};
var Tooltip = ({
  children,
  content,
  position,
  shouldDisplay
}) => {
  if (!shouldDisplay)
    return children;
  const tooltipProps = {
    [getHintAttribute("default")]: content,
    [getHintPosition("default")]: position
  };
  return /* @__PURE__ */ _jsx1018("span", {
    ...tooltipProps,
    children
  });
};
Tooltip.defaultProps = {
  children: null,
  position: null,
  shouldDisplay: true
};
Tooltip.propTypes = {
  children: PropTypes2.node,
  content: PropTypes2.string.isRequired,
  position: PropTypes2.string,
  shouldDisplay: PropTypes2.bool
};
export {
  AccountantPortalChrome,
  AccountantPortalSearchFilter,
  ActionButton,
  ActionPill,
  AutoScrolled,
  Avatar,
  Badge,
  BadgeButton,
  Button,
  ButtonLink,
  Checkbox,
  Chip,
  ChipButton,
  ConfiguredQuestionnaire,
  ConfirmationModal,
  ContactWidget,
  Container2 as Container,
  CookieConsentOverlay,
  CrossOver,
  CustomerChrome,
  DatePicker,
  DatePickerWithLabel,
  DialogAffirm,
  DialogConfirm,
  DialogInfo,
  DropdownButton,
  DropdownSelect,
  File,
  FileDropZone,
  Flexer,
  FloatingLabel3 as FloatingLabel,
  Folder,
  Form,
  GlobalSpinner,
  Grid,
  GridItem,
  Heading,
  Icon,
  Icon360Degrees_default as Icon360Degrees,
  IconActivity2_default as IconActivity2,
  IconActivityRec_default as IconActivityRec,
  IconAirplay_default as IconAirplay,
  IconAlertCircle_default as IconAlertCircle,
  IconAlertOctagon_default as IconAlertOctagon,
  IconAlertTriangle_default as IconAlertTriangle,
  IconAlignBottom_default as IconAlignBottom,
  IconAlignBottom2_default as IconAlignBottom2,
  IconAlignBottomRec_default as IconAlignBottomRec,
  IconAlignCenter_default as IconAlignCenter,
  IconAlignCenter2_default as IconAlignCenter2,
  IconAlignCenter2Rec_default as IconAlignCenter2Rec,
  IconAlignCenterRec_default as IconAlignCenterRec,
  IconAlignHorizontally_default as IconAlignHorizontally,
  IconAlignJustify_default as IconAlignJustify,
  IconAlignJustify2_default as IconAlignJustify2,
  IconAlignJustify2Rec_default as IconAlignJustify2Rec,
  IconAlignJustifyRec_default as IconAlignJustifyRec,
  IconAlignLeft_default as IconAlignLeft,
  IconAlignLeft2_default as IconAlignLeft2,
  IconAlignLeft2Rec_default as IconAlignLeft2Rec,
  IconAlignLeft3_default as IconAlignLeft3,
  IconAlignLeftRec_default as IconAlignLeftRec,
  IconAlignMiddle_default as IconAlignMiddle,
  IconAlignMiddleRec_default as IconAlignMiddleRec,
  IconAlignRight_default as IconAlignRight,
  IconAlignRight2_default as IconAlignRight2,
  IconAlignRight2Rec_default as IconAlignRight2Rec,
  IconAlignRight3_default as IconAlignRight3,
  IconAlignRightRec_default as IconAlignRightRec,
  IconAlignTop_default as IconAlignTop,
  IconAlignTop2_default as IconAlignTop2,
  IconAlignTopRec_default as IconAlignTopRec,
  IconAlignVertically_default as IconAlignVertically,
  IconAnchor_default as IconAnchor,
  IconAnchorRec_default as IconAnchorRec,
  IconAndroid_default as IconAndroid,
  IconApperture_default as IconApperture,
  IconApple_default as IconApple,
  IconAppstore_default as IconAppstore,
  IconArchive_default as IconArchive,
  IconArrowBottom_default as IconArrowBottom,
  IconArrowBottomCircle_default as IconArrowBottomCircle,
  IconArrowBottomLeft_default as IconArrowBottomLeft,
  IconArrowBottomLeftCircle_default as IconArrowBottomLeftCircle,
  IconArrowBottomLeftRec_default as IconArrowBottomLeftRec,
  IconArrowBottomRec_default as IconArrowBottomRec,
  IconArrowBottomRight_default as IconArrowBottomRight,
  IconArrowBottomRightCircle_default as IconArrowBottomRightCircle,
  IconArrowBottomRightRec_default as IconArrowBottomRightRec,
  IconArrowCornerCcwLb_default as IconArrowCornerCcwLb,
  IconArrowCornerCcwLbRec_default as IconArrowCornerCcwLbRec,
  IconArrowCornerCcwLt_default as IconArrowCornerCcwLt,
  IconArrowCornerCcwLtRec_default as IconArrowCornerCcwLtRec,
  IconArrowCornerCcwRb_default as IconArrowCornerCcwRb,
  IconArrowCornerCcwRbRec_default as IconArrowCornerCcwRbRec,
  IconArrowCornerCcwRt_default as IconArrowCornerCcwRt,
  IconArrowCornerCcwRtRec_default as IconArrowCornerCcwRtRec,
  IconArrowCornerCwLb_default as IconArrowCornerCwLb,
  IconArrowCornerCwLbRec_default as IconArrowCornerCwLbRec,
  IconArrowCornerCwLt_default as IconArrowCornerCwLt,
  IconArrowCornerCwLtRec_default as IconArrowCornerCwLtRec,
  IconArrowCornerCwRb_default as IconArrowCornerCwRb,
  IconArrowCornerCwRbRec_default as IconArrowCornerCwRbRec,
  IconArrowCornerCwRt_default as IconArrowCornerCwRt,
  IconArrowCornerCwRtRec_default as IconArrowCornerCwRtRec,
  IconArrowLeft_default as IconArrowLeft,
  IconArrowLeftCircle_default as IconArrowLeftCircle,
  IconArrowLeftCurvedCircle_default as IconArrowLeftCurvedCircle,
  IconArrowLeftRec_default as IconArrowLeftRec,
  IconArrowRight_default as IconArrowRight,
  IconArrowRightCircle_default as IconArrowRightCircle,
  IconArrowRightCurvedCircle_default as IconArrowRightCurvedCircle,
  IconArrowRightRec_default as IconArrowRightRec,
  IconArrowSwap_default as IconArrowSwap,
  IconArrowSwapRec_default as IconArrowSwapRec,
  IconArrowTop_default as IconArrowTop,
  IconArrowTopCircle_default as IconArrowTopCircle,
  IconArrowTopLeft_default as IconArrowTopLeft,
  IconArrowTopLeftCircle_default as IconArrowTopLeftCircle,
  IconArrowTopLeftRec_default as IconArrowTopLeftRec,
  IconArrowTopRec_default as IconArrowTopRec,
  IconArrowTopRight_default as IconArrowTopRight,
  IconArrowTopRightCircle_default as IconArrowTopRightCircle,
  IconArrowTopRightRec_default as IconArrowTopRightRec,
  IconArrowsExpand_default as IconArrowsExpand,
  IconArrowsExpand2_default as IconArrowsExpand2,
  IconArrowsExpand2Rec_default as IconArrowsExpand2Rec,
  IconArrowsExpandRec_default as IconArrowsExpandRec,
  IconArrowsMove_default as IconArrowsMove,
  IconArrowsMove2_default as IconArrowsMove2,
  IconArrowsMove2Rec_default as IconArrowsMove2Rec,
  IconArrowsMoveRec_default as IconArrowsMoveRec,
  IconAt_default as IconAt,
  IconAtRec_default as IconAtRec,
  IconAtom_default as IconAtom,
  IconAward_default as IconAward,
  IconBabyCarriage_default as IconBabyCarriage,
  IconBarchart_default as IconBarchart,
  IconBattery_default as IconBattery,
  IconBattery1_default as IconBattery1,
  IconBattery2_default as IconBattery2,
  IconBattery3_default as IconBattery3,
  IconBatteryCharging_default as IconBatteryCharging,
  IconBatterySlash_default as IconBatterySlash,
  IconBed2_default as IconBed2,
  IconBed3_default as IconBed3,
  IconBehance_default as IconBehance,
  IconBell_default as IconBell,
  IconBellCheck_default as IconBellCheck,
  IconBellMinus_default as IconBellMinus,
  IconBellPlus_default as IconBellPlus,
  IconBellSlash_default as IconBellSlash,
  IconBluetooth_default as IconBluetooth,
  IconBluetoothRec_default as IconBluetoothRec,
  IconBook_default as IconBook,
  IconBookLibrary_default as IconBookLibrary,
  IconBookOpen_default as IconBookOpen,
  IconBookPlus_default as IconBookPlus,
  IconBookQueue_default as IconBookQueue,
  IconBookmark_default as IconBookmark,
  IconBookmarkCheck_default as IconBookmarkCheck,
  IconBookmarkMinus_default as IconBookmarkMinus,
  IconBookmarkPlus_default as IconBookmarkPlus,
  IconBookmarkSlash_default as IconBookmarkSlash,
  IconBookmarks_default as IconBookmarks,
  IconBorderBottom_default as IconBorderBottom,
  IconBorderClear_default as IconBorderClear,
  IconBorderCommon_default as IconBorderCommon,
  IconBorderHorizontal_default as IconBorderHorizontal,
  IconBorderInner_default as IconBorderInner,
  IconBorderLeft_default as IconBorderLeft,
  IconBorderOuter_default as IconBorderOuter,
  IconBorderRight_default as IconBorderRight,
  IconBorderTop_default as IconBorderTop,
  IconBorderVertical_default as IconBorderVertical,
  IconBox_default as IconBox,
  IconBox2_default as IconBox2,
  IconBrackets_default as IconBrackets,
  IconBracketsRec_default as IconBracketsRec,
  IconBriefcase_default as IconBriefcase,
  IconBriefcase2_default as IconBriefcase2,
  IconBroadcast_default as IconBroadcast,
  IconBroadcastRec_default as IconBroadcastRec,
  IconBrush_default as IconBrush,
  IconBug_default as IconBug,
  IconBulb_default as IconBulb,
  IconBulbSlash_default as IconBulbSlash,
  IconButton,
  IconCalendar_default as IconCalendar,
  IconCalendarArrowRight_default as IconCalendarArrowRight,
  IconCalendarCross_default as IconCalendarCross,
  IconCalendarDates_default as IconCalendarDates,
  IconCalendarEvent_default as IconCalendarEvent,
  IconCalendarMinus_default as IconCalendarMinus,
  IconCalendarNote_default as IconCalendarNote,
  IconCalendarNumber_default as IconCalendarNumber,
  IconCalendarPerson_default as IconCalendarPerson,
  IconCalendarPlus_default as IconCalendarPlus,
  IconCallAdd_default as IconCallAdd,
  IconCallCalling_default as IconCallCalling,
  IconCallDecline_default as IconCallDecline,
  IconCallEnd_default as IconCallEnd,
  IconCallIncoming_default as IconCallIncoming,
  IconCallMissed_default as IconCallMissed,
  IconCallMuted_default as IconCallMuted,
  IconCallOnHold_default as IconCallOnHold,
  IconCallOutgoing_default as IconCallOutgoing,
  IconCallReceived_default as IconCallReceived,
  IconCallRedirected_default as IconCallRedirected,
  IconCallRemove_default as IconCallRemove,
  IconCallSlash_default as IconCallSlash,
  IconCamera_default as IconCamera,
  IconCameraSlash_default as IconCameraSlash,
  IconCaptions_default as IconCaptions,
  IconCaptions2_default as IconCaptions2,
  IconCash_default as IconCash,
  IconCast_default as IconCast,
  IconCd_default as IconCd,
  IconChartBar_default as IconChartBar,
  IconChartColumn_default as IconChartColumn,
  IconChartLine_default as IconChartLine,
  IconChartPie_default as IconChartPie,
  IconCheck_default as IconCheck,
  IconCheckCircle_default as IconCheckCircle,
  IconCheckCircle2_default as IconCheckCircle2,
  IconCheckRec_default as IconCheckRec,
  IconCheckboxCircle_default as IconCheckboxCircle,
  IconCheckboxRec_default as IconCheckboxRec,
  IconChevronDown_default as IconChevronDown,
  IconChevronDownRec_default as IconChevronDownRec,
  IconChevronLeft_default as IconChevronLeft,
  IconChevronLeftRec_default as IconChevronLeftRec,
  IconChevronRight_default as IconChevronRight,
  IconChevronRightRec_default as IconChevronRightRec,
  IconChevronUp_default as IconChevronUp,
  IconChevronUpRec_default as IconChevronUpRec,
  IconChevronsDown_default as IconChevronsDown,
  IconChevronsDownRec_default as IconChevronsDownRec,
  IconChevronsLeft_default as IconChevronsLeft,
  IconChevronsLeftRec_default as IconChevronsLeftRec,
  IconChevronsRight_default as IconChevronsRight,
  IconChevronsRightRec_default as IconChevronsRightRec,
  IconChevronsUp_default as IconChevronsUp,
  IconChevronsUpRec_default as IconChevronsUpRec,
  IconClipboard_default as IconClipboard,
  IconClipboardCheck_default as IconClipboardCheck,
  IconClipboardCross_default as IconClipboardCross,
  IconClipboardMinus_default as IconClipboardMinus,
  IconClipboardPlus_default as IconClipboardPlus,
  IconClipboardSlash_default as IconClipboardSlash,
  IconClipboardText_default as IconClipboardText,
  IconClock_default as IconClock,
  IconClockCheck_default as IconClockCheck,
  IconClockCross_default as IconClockCross,
  IconClockMinus_default as IconClockMinus,
  IconClockPlus_default as IconClockPlus,
  IconClockSlash_default as IconClockSlash,
  IconClockZzz_default as IconClockZzz,
  IconCloud_default as IconCloud,
  IconCloudCheck_default as IconCloudCheck,
  IconCloudCross_default as IconCloudCross,
  IconCloudDownload_default as IconCloudDownload,
  IconCloudDrizzle_default as IconCloudDrizzle,
  IconCloudLightning_default as IconCloudLightning,
  IconCloudSnow_default as IconCloudSnow,
  IconCloudUpload_default as IconCloudUpload,
  IconCode_default as IconCode,
  IconCodeRec_default as IconCodeRec,
  IconCoffee_default as IconCoffee,
  IconColorFilter_default as IconColorFilter,
  IconColumnSpacing_default as IconColumnSpacing,
  IconColumnSpacingRec_default as IconColumnSpacingRec,
  IconComment_default as IconComment,
  IconComment2_default as IconComment2,
  IconComment2Alert_default as IconComment2Alert,
  IconComment2Cancel_default as IconComment2Cancel,
  IconComment2Check_default as IconComment2Check,
  IconComment2Dots_default as IconComment2Dots,
  IconComment2Info_default as IconComment2Info,
  IconComment2Minus_default as IconComment2Minus,
  IconComment2Plus_default as IconComment2Plus,
  IconComment2Question_default as IconComment2Question,
  IconComment2Slash_default as IconComment2Slash,
  IconComment2Text_default as IconComment2Text,
  IconCommentAlert_default as IconCommentAlert,
  IconCommentCancel_default as IconCommentCancel,
  IconCommentCheck_default as IconCommentCheck,
  IconCommentDots_default as IconCommentDots,
  IconCommentMinus_default as IconCommentMinus,
  IconCommentPlus_default as IconCommentPlus,
  IconCommentSlash_default as IconCommentSlash,
  IconCommentText_default as IconCommentText,
  IconComments_default as IconComments,
  IconComments2_default as IconComments2,
  IconCompass_default as IconCompass,
  IconCopy_default as IconCopy,
  IconCopyright_default as IconCopyright,
  IconCreditCard_default as IconCreditCard,
  IconCreditCardIncome_default as IconCreditCardIncome,
  IconCreditCardMinus_default as IconCreditCardMinus,
  IconCreditCardOutcome_default as IconCreditCardOutcome,
  IconCreditCardPlus_default as IconCreditCardPlus,
  IconCreditCardScan_default as IconCreditCardScan,
  IconCreditCardSlash_default as IconCreditCardSlash,
  IconCrop_default as IconCrop,
  IconCropRec_default as IconCropRec,
  IconCropRotate_default as IconCropRotate,
  IconCropRotateRec_default as IconCropRotateRec,
  IconCross_default as IconCross,
  IconCrossCircle_default as IconCrossCircle,
  IconCrossOctagon_default as IconCrossOctagon,
  IconCrossRec_default as IconCrossRec,
  IconCrosshair_default as IconCrosshair,
  IconCut_default as IconCut,
  IconCutlery_default as IconCutlery,
  IconCutlery2_default as IconCutlery2,
  IconCutlery3_default as IconCutlery3,
  IconCutlery4_default as IconCutlery4,
  IconDashboard_default as IconDashboard,
  IconDashboard2_default as IconDashboard2,
  IconDevices_default as IconDevices,
  IconDialpad_default as IconDialpad,
  IconDirection_default as IconDirection,
  IconDirection2_default as IconDirection2,
  IconDiskette_default as IconDiskette,
  IconDistributeHorizontally_default as IconDistributeHorizontally,
  IconDistributeVertically_default as IconDistributeVertically,
  IconDollar_default as IconDollar,
  IconDollarCircle_default as IconDollarCircle,
  IconDribbble_default as IconDribbble,
  IconDroplet_default as IconDroplet,
  IconDropletSlash_default as IconDropletSlash,
  IconDumbell_default as IconDumbell,
  IconEdit_default as IconEdit,
  IconEdit2_default as IconEdit2,
  IconEdit3_default as IconEdit3,
  IconEditSlash_default as IconEditSlash,
  IconElements_default as IconElements,
  IconElements2_default as IconElements2,
  IconElements3_default as IconElements3,
  IconElements4_default as IconElements4,
  IconElements5_default as IconElements5,
  IconElementsPlus_default as IconElementsPlus,
  IconEmail_default as IconEmail,
  IconEmails_default as IconEmails,
  IconEmotionHappy_default as IconEmotionHappy,
  IconEmotionNeutral_default as IconEmotionNeutral,
  IconEmotionSad_default as IconEmotionSad,
  IconEmotionSmile_default as IconEmotionSmile,
  IconEmotionWow_default as IconEmotionWow,
  IconEqualizer_default as IconEqualizer,
  IconEuro_default as IconEuro,
  IconEuroCircle_default as IconEuroCircle,
  IconExport_default as IconExport,
  IconExport2_default as IconExport2,
  IconExport3_default as IconExport3,
  IconExport4_default as IconExport4,
  IconExternalLink_default as IconExternalLink,
  IconExternalLink2_default as IconExternalLink2,
  IconExternalLinkRec_default as IconExternalLinkRec,
  IconEye_default as IconEye,
  IconEyeSlash_default as IconEyeSlash,
  IconFaceId_default as IconFaceId,
  IconFaceIdRec_default as IconFaceIdRec,
  IconFacebook_default as IconFacebook,
  IconFastForward_default as IconFastForward,
  IconFastRewind_default as IconFastRewind,
  IconFatRows_default as IconFatRows,
  IconFigma_default as IconFigma,
  IconFile_default as IconFile,
  IconFileAgreement_default as IconFileAgreement,
  IconFileCheck_default as IconFileCheck,
  IconFileCode_default as IconFileCode,
  IconFileCross_default as IconFileCross,
  IconFileDownload_default as IconFileDownload,
  IconFileDraft_default as IconFileDraft,
  IconFileHeart_default as IconFileHeart,
  IconFileInvoice_default as IconFileInvoice,
  IconFileMinus_default as IconFileMinus,
  IconFileMove_default as IconFileMove,
  IconFilePlus_default as IconFilePlus,
  IconFileScan_default as IconFileScan,
  IconFileShredder_default as IconFileShredder,
  IconFileSlash_default as IconFileSlash,
  IconFileText_default as IconFileText,
  IconFileUpload_default as IconFileUpload,
  IconFilmBoard_default as IconFilmBoard,
  IconFilmReel_default as IconFilmReel,
  IconFilmTape_default as IconFilmTape,
  IconFilter_default as IconFilter,
  IconFilter2_default as IconFilter2,
  IconFilterRec_default as IconFilterRec,
  IconFingerprint_default as IconFingerprint,
  IconFingerprint2_default as IconFingerprint2,
  IconFingerprint2Rec_default as IconFingerprint2Rec,
  IconFingerprintRec_default as IconFingerprintRec,
  IconFire_default as IconFire,
  IconFirstLine1_default as IconFirstLine1,
  IconFirstLine1Rec_default as IconFirstLine1Rec,
  IconFirstLine2_default as IconFirstLine2,
  IconFirstLine2Rec_default as IconFirstLine2Rec,
  IconFirstLine3_default as IconFirstLine3,
  IconFirstLine3Rec_default as IconFirstLine3Rec,
  IconFlag_default as IconFlag,
  IconFlag2_default as IconFlag2,
  IconFlag3_default as IconFlag3,
  IconFlashcard_default as IconFlashcard,
  IconFolder_default as IconFolder,
  IconFolderCheck_default as IconFolderCheck,
  IconFolderCloud_default as IconFolderCloud,
  IconFolderCompressed_default as IconFolderCompressed,
  IconFolderCross_default as IconFolderCross,
  IconFolderDownload_default as IconFolderDownload,
  IconFolderHeart_default as IconFolderHeart,
  IconFolderMinus_default as IconFolderMinus,
  IconFolderMove_default as IconFolderMove,
  IconFolderMusic_default as IconFolderMusic,
  IconFolderOpen_default as IconFolderOpen,
  IconFolderPhoto_default as IconFolderPhoto,
  IconFolderPlus_default as IconFolderPlus,
  IconFolderSlash_default as IconFolderSlash,
  IconFolderUpload_default as IconFolderUpload,
  IconForward_default as IconForward,
  IconForward10_default as IconForward10,
  IconForward10Rec_default as IconForward10Rec,
  IconForward15_default as IconForward15,
  IconForward15Rec_default as IconForward15Rec,
  IconForward5_default as IconForward5,
  IconForward5Rec_default as IconForward5Rec,
  IconForwardAll_default as IconForwardAll,
  IconForwardAllRec_default as IconForwardAllRec,
  IconForwardRec_default as IconForwardRec,
  IconFrame_default as IconFrame,
  IconFrameBounds_default as IconFrameBounds,
  IconFrameBoundsRec_default as IconFrameBoundsRec,
  IconFramer_default as IconFramer,
  IconFunctions_default as IconFunctions,
  IconFunctionsRec_default as IconFunctionsRec,
  IconGames_default as IconGames,
  IconGear_default as IconGear,
  IconGear2_default as IconGear2,
  IconGift_default as IconGift,
  IconGithub_default as IconGithub,
  IconGlassCocktail_default as IconGlassCocktail,
  IconGlassWine_default as IconGlassWine,
  IconGlasses_default as IconGlasses,
  IconGlassesVr_default as IconGlassesVr,
  IconGlobe_default as IconGlobe,
  IconGlobe2_default as IconGlobe2,
  IconGoogleplay_default as IconGoogleplay,
  IconGps_default as IconGps,
  IconGpsSlash_default as IconGpsSlash,
  IconGrid_default as IconGrid,
  IconGrid02_default as IconGrid02,
  IconGrid03_default as IconGrid03,
  IconGrid04_default as IconGrid04,
  IconGrid05_default as IconGrid05,
  IconGrid06_default as IconGrid06,
  IconGrid07_default as IconGrid07,
  IconGrid08_default as IconGrid08,
  IconGrid09_default as IconGrid09,
  IconGrid10_default as IconGrid10,
  IconGrid11_default as IconGrid11,
  IconGrid12_default as IconGrid12,
  IconGrid13_default as IconGrid13,
  IconGrid14_default as IconGrid14,
  IconGrid15_default as IconGrid15,
  IconGrid16_default as IconGrid16,
  IconHardDrive_default as IconHardDrive,
  IconHashtag_default as IconHashtag,
  IconHashtagCircle_default as IconHashtagCircle,
  IconHashtagRec_default as IconHashtagRec,
  IconHeadphones_default as IconHeadphones,
  IconHeadphones2_default as IconHeadphones2,
  IconHeart_default as IconHeart,
  IconHeartSlash_default as IconHeartSlash,
  IconHistory_default as IconHistory,
  IconHistoryRec_default as IconHistoryRec,
  IconHome_default as IconHome,
  IconHome2_default as IconHome2,
  IconHome3_default as IconHome3,
  IconHome4_default as IconHome4,
  IconIframe_default as IconIframe,
  IconIllustrator_default as IconIllustrator,
  IconImage_default as IconImage,
  IconImage2_default as IconImage2,
  IconImage3_default as IconImage3,
  IconImageCheck_default as IconImageCheck,
  IconImageCross_default as IconImageCross,
  IconImageDownload_default as IconImageDownload,
  IconImageEdit_default as IconImageEdit,
  IconImagePlus_default as IconImagePlus,
  IconImageSlash_default as IconImageSlash,
  IconImageUpload_default as IconImageUpload,
  IconImport_default as IconImport,
  IconImport2_default as IconImport2,
  IconImport3_default as IconImport3,
  IconImport4_default as IconImport4,
  IconInbox_default as IconInbox,
  IconInboxFilled_default as IconInboxFilled,
  IconInboxIn_default as IconInboxIn,
  IconInboxOut_default as IconInboxOut,
  IconIndentDecrease_default as IconIndentDecrease,
  IconIndentDecreaseRec_default as IconIndentDecreaseRec,
  IconIndentFirstLine_default as IconIndentFirstLine,
  IconIndentFirstLineRec_default as IconIndentFirstLineRec,
  IconIndentIncrease_default as IconIndentIncrease,
  IconIndentIncreaseRec_default as IconIndentIncreaseRec,
  IconInfinity_default as IconInfinity,
  IconInfinityRec_default as IconInfinityRec,
  IconInfo_default as IconInfo,
  IconInstagram_default as IconInstagram,
  IconInvoice_default as IconInvoice,
  IconKanban_default as IconKanban,
  IconKey_default as IconKey,
  IconKeyboard_default as IconKeyboard,
  IconKeyboardHide_default as IconKeyboardHide,
  IconKeyboardShow_default as IconKeyboardShow,
  IconLabel4K_default as IconLabel4K,
  IconLabelExplicit_default as IconLabelExplicit,
  IconLabelHd_default as IconLabelHd,
  IconLabelHq_default as IconLabelHq,
  IconLaptop_default as IconLaptop,
  IconLayers_default as IconLayers,
  IconLetterSpacing_default as IconLetterSpacing,
  IconLetterSpacing2_default as IconLetterSpacing2,
  IconLetterSpacing2Rec_default as IconLetterSpacing2Rec,
  IconLetterSpacingRec_default as IconLetterSpacingRec,
  IconLifebuoy_default as IconLifebuoy,
  IconLightning_default as IconLightning,
  IconLightningSlash_default as IconLightningSlash,
  IconLineHeight_default as IconLineHeight,
  IconLineHeight2_default as IconLineHeight2,
  IconLineHeight2Rec_default as IconLineHeight2Rec,
  IconLineHeightRec_default as IconLineHeightRec,
  IconLink1_default as IconLink1,
  IconLink1Rec_default as IconLink1Rec,
  IconLink2_default as IconLink2,
  IconLink2Rec_default as IconLink2Rec,
  IconLinkedin_default as IconLinkedin,
  IconListOrdered_default as IconListOrdered,
  IconListOrderedRec_default as IconListOrderedRec,
  IconListUnordered_default as IconListUnordered,
  IconListUnordered2_default as IconListUnordered2,
  IconListUnordered2Rec_default as IconListUnordered2Rec,
  IconListUnordered3_default as IconListUnordered3,
  IconListUnordered4_default as IconListUnordered4,
  IconListUnordered4Rec_default as IconListUnordered4Rec,
  IconListUnordered5_default as IconListUnordered5,
  IconListUnordered5Rec_default as IconListUnordered5Rec,
  IconListUnorderedRec_default as IconListUnorderedRec,
  IconLoader_default as IconLoader,
  IconLoader2_default as IconLoader2,
  IconLoader2Rec_default as IconLoader2Rec,
  IconLoaderRec_default as IconLoaderRec,
  IconLoadingTrolley_default as IconLoadingTrolley,
  IconLock_default as IconLock,
  IconLock2_default as IconLock2,
  IconLock2Opened_default as IconLock2Opened,
  IconLock2Slash_default as IconLock2Slash,
  IconLockOpened_default as IconLockOpened,
  IconLockSlash_default as IconLockSlash,
  IconLogIn_default as IconLogIn,
  IconLogIn2_default as IconLogIn2,
  IconLogIn3_default as IconLogIn3,
  IconLogOut_default as IconLogOut,
  IconLogOut2_default as IconLogOut2,
  IconLogOut3_default as IconLogOut3,
  IconMapView_default as IconMapView,
  IconMaximize_default as IconMaximize,
  IconMaximize2_default as IconMaximize2,
  IconMaximize2Flipped_default as IconMaximize2Flipped,
  IconMaximize2FlippedRec_default as IconMaximize2FlippedRec,
  IconMaximize2Rec_default as IconMaximize2Rec,
  IconMaximize3_default as IconMaximize3,
  IconMaximize3Rec_default as IconMaximize3Rec,
  IconMaximizeFlipped_default as IconMaximizeFlipped,
  IconMaximizeFlippedRec_default as IconMaximizeFlippedRec,
  IconMaximizeRec_default as IconMaximizeRec,
  IconMegaphone_default as IconMegaphone,
  IconMemoryCard_default as IconMemoryCard,
  IconMenu_default as IconMenu,
  IconMenu2_default as IconMenu2,
  IconMenu2Rec_default as IconMenu2Rec,
  IconMenuRec_default as IconMenuRec,
  IconMessanger_default as IconMessanger,
  IconMic_default as IconMic,
  IconMicSlash_default as IconMicSlash,
  IconMinimize_default as IconMinimize,
  IconMinimizeRec_default as IconMinimizeRec,
  IconMiniplayer_default as IconMiniplayer,
  IconMinus_default as IconMinus,
  IconMinusCircle_default as IconMinusCircle,
  IconMinusOctagon_default as IconMinusOctagon,
  IconMinusRec_default as IconMinusRec,
  IconModules_default as IconModules,
  IconMonitor_default as IconMonitor,
  IconMoon_default as IconMoon,
  IconMoreHoriz_default as IconMoreHoriz,
  IconMoreHorizRec_default as IconMoreHorizRec,
  IconMoreVert_default as IconMoreVert,
  IconMoreVertRec_default as IconMoreVertRec,
  IconMouse_default as IconMouse,
  IconMusicLibrary_default as IconMusicLibrary,
  IconMusicNote_default as IconMusicNote,
  IconMusicNote2_default as IconMusicNote2,
  IconMusicNote3_default as IconMusicNote3,
  IconMusicPlus_default as IconMusicPlus,
  IconMusicQueue_default as IconMusicQueue,
  IconNewRelease_default as IconNewRelease,
  IconNewspaper_default as IconNewspaper,
  IconNote_default as IconNote,
  IconNote2_default as IconNote2,
  IconNote2Text_default as IconNote2Text,
  IconNoteText_default as IconNoteText,
  IconNotebook_default as IconNotebook,
  IconNotificationIndicator_default as IconNotificationIndicator,
  IconNotion_default as IconNotion,
  IconNut_default as IconNut,
  IconPadding_default as IconPadding,
  IconPageBreak_default as IconPageBreak,
  IconPaintBucket_default as IconPaintBucket,
  IconPaintTool_default as IconPaintTool,
  IconPaperclip_default as IconPaperclip,
  IconPaperclip2_default as IconPaperclip2,
  IconPaperclip2Rec_default as IconPaperclip2Rec,
  IconPaperclip2Slash_default as IconPaperclip2Slash,
  IconPaperclip2SlashRec_default as IconPaperclip2SlashRec,
  IconPaperclipRec_default as IconPaperclipRec,
  IconPaperclipSlash_default as IconPaperclipSlash,
  IconPaperclipSlashRec_default as IconPaperclipSlashRec,
  IconPause_default as IconPause,
  IconPauseCircle_default as IconPauseCircle,
  IconPenTool_default as IconPenTool,
  IconPenTool2_default as IconPenTool2,
  IconPharagraphSpacing_default as IconPharagraphSpacing,
  IconPharagraphSpacingRec_default as IconPharagraphSpacingRec,
  IconPhone_default as IconPhone,
  IconPhotoshop_default as IconPhotoshop,
  IconPin_default as IconPin,
  IconPin2_default as IconPin2,
  IconPin3_default as IconPin3,
  IconPinCheck_default as IconPinCheck,
  IconPinCross_default as IconPinCross,
  IconPinMinus_default as IconPinMinus,
  IconPinPlus_default as IconPinPlus,
  IconPinSlash_default as IconPinSlash,
  IconPinterest_default as IconPinterest,
  IconPlanet_default as IconPlanet,
  IconPlay_default as IconPlay,
  IconPlayCircle_default as IconPlayCircle,
  IconPlayNext_default as IconPlayNext,
  IconPlayPrevious_default as IconPlayPrevious,
  IconPlaySlowmotion_default as IconPlaySlowmotion,
  IconPlaylist_default as IconPlaylist,
  IconPlaylistPlay_default as IconPlaylistPlay,
  IconPlaylistPlus_default as IconPlaylistPlus,
  IconPlug_default as IconPlug,
  IconPlugSlash_default as IconPlugSlash,
  IconPlus_default as IconPlus,
  IconPlusCircle_default as IconPlusCircle,
  IconPlusRec_default as IconPlusRec,
  IconPos_default as IconPos,
  IconPower_default as IconPower,
  IconPowerRec_default as IconPowerRec,
  IconPresentationChart_default as IconPresentationChart,
  IconPresentationLine_default as IconPresentationLine,
  IconPrinter_default as IconPrinter,
  IconPrinterSlash_default as IconPrinterSlash,
  IconProcent_default as IconProcent,
  IconProcentCircle_default as IconProcentCircle,
  IconProcessor_default as IconProcessor,
  IconProducthunt_default as IconProducthunt,
  IconQuestion_default as IconQuestion,
  IconQuotes_default as IconQuotes,
  IconQuotes2_default as IconQuotes2,
  IconRadio_default as IconRadio,
  IconReceipt_default as IconReceipt,
  IconRecord_default as IconRecord,
  IconRedo_default as IconRedo,
  IconRedo2_default as IconRedo2,
  IconRedo2Rec_default as IconRedo2Rec,
  IconRedoRec_default as IconRedoRec,
  IconRefreshCcw_default as IconRefreshCcw,
  IconRefreshCcwAlert_default as IconRefreshCcwAlert,
  IconRefreshCcwAlertRec_default as IconRefreshCcwAlertRec,
  IconRefreshCcwRec_default as IconRefreshCcwRec,
  IconRefreshCw_default as IconRefreshCw,
  IconRefreshCwAlert_default as IconRefreshCwAlert,
  IconRefreshCwAlertRec_default as IconRefreshCwAlertRec,
  IconRefreshCwRec_default as IconRefreshCwRec,
  IconRepeat_default as IconRepeat,
  IconRepeatOn_default as IconRepeatOn,
  IconReplay_default as IconReplay,
  IconReplay10_default as IconReplay10,
  IconReplay10Rec_default as IconReplay10Rec,
  IconReplay15_default as IconReplay15,
  IconReplay15Rec_default as IconReplay15Rec,
  IconReplay5_default as IconReplay5,
  IconReplay5Rec_default as IconReplay5Rec,
  IconReplayRec_default as IconReplayRec,
  IconReply_default as IconReply,
  IconReplyAll_default as IconReplyAll,
  IconReplyAllRec_default as IconReplyAllRec,
  IconReplyRec_default as IconReplyRec,
  IconRocket_default as IconRocket,
  IconRocket2_default as IconRocket2,
  IconRotateCcw_default as IconRotateCcw,
  IconRotateCcwRec_default as IconRotateCcwRec,
  IconRotateCw_default as IconRotateCw,
  IconRotateCwRec_default as IconRotateCwRec,
  IconRoute_default as IconRoute,
  IconRuler_default as IconRuler,
  IconScale_default as IconScale,
  IconScan_default as IconScan,
  IconScanRec_default as IconScanRec,
  IconScript_default as IconScript,
  IconScriptRec_default as IconScriptRec,
  IconSearch_default as IconSearch,
  IconSelection_default as IconSelection,
  IconSelectionRec_default as IconSelectionRec,
  IconSend_default as IconSend,
  IconSend2_default as IconSend2,
  IconServer_default as IconServer,
  IconServer2_default as IconServer2,
  IconShare_default as IconShare,
  IconShield_default as IconShield,
  IconShieldCheck_default as IconShieldCheck,
  IconShieldCross_default as IconShieldCross,
  IconShoppingBag_default as IconShoppingBag,
  IconShoppingBag2_default as IconShoppingBag2,
  IconShoppingBasket_default as IconShoppingBasket,
  IconShoppingCart_default as IconShoppingCart,
  IconShoppingCart2_default as IconShoppingCart2,
  IconShuffle_default as IconShuffle,
  IconSketch_default as IconSketch,
  IconSlash_default as IconSlash,
  IconSliderHorizontal_default as IconSliderHorizontal,
  IconSliderHorizontal2_default as IconSliderHorizontal2,
  IconSliderVertical_default as IconSliderVertical,
  IconSliderVertical2_default as IconSliderVertical2,
  IconSlidersHoriz_default as IconSlidersHoriz,
  IconSlidersHoriz2_default as IconSlidersHoriz2,
  IconSlidersVert_default as IconSlidersVert,
  IconSlidersVert2_default as IconSlidersVert2,
  IconSmallCaps_default as IconSmallCaps,
  IconSmallCaps2_default as IconSmallCaps2,
  IconSmallCaps2Rec_default as IconSmallCaps2Rec,
  IconSmallCapsRec_default as IconSmallCapsRec,
  IconSmartphone_default as IconSmartphone,
  IconSortNumber_default as IconSortNumber,
  IconSortNumberRec_default as IconSortNumberRec,
  IconSortNumberReverse_default as IconSortNumberReverse,
  IconSortNumberReverseRec_default as IconSortNumberReverseRec,
  IconSortText_default as IconSortText,
  IconSortTextRec_default as IconSortTextRec,
  IconSortTextReverse_default as IconSortTextReverse,
  IconSortTextReverseRec_default as IconSortTextReverseRec,
  IconSoundcloud_default as IconSoundcloud,
  IconSoundwave_default as IconSoundwave,
  IconSoundwaveRec_default as IconSoundwaveRec,
  IconSpeaker_default as IconSpeaker,
  IconSpecialCharachers_default as IconSpecialCharachers,
  IconSpecialCharachersRec_default as IconSpecialCharachersRec,
  IconSpeed_default as IconSpeed,
  IconSpeedRec_default as IconSpeedRec,
  IconSpotify_default as IconSpotify,
  IconStack_default as IconStack,
  IconStar_default as IconStar,
  IconStarSlash_default as IconStarSlash,
  IconSticker_default as IconSticker,
  IconStop_default as IconStop,
  IconStopCircle_default as IconStopCircle,
  IconStopwatch_default as IconStopwatch,
  IconStopwatchSlash_default as IconStopwatchSlash,
  IconSun_default as IconSun,
  IconSunrise_default as IconSunrise,
  IconSunset_default as IconSunset,
  IconSwatchbook_default as IconSwatchbook,
  IconTablet_default as IconTablet,
  IconTag_default as IconTag,
  IconTag2_default as IconTag2,
  IconTags_default as IconTags,
  IconTags2_default as IconTags2,
  IconTarget_default as IconTarget,
  IconTerminal_default as IconTerminal,
  IconText_default as IconText,
  IconText2_default as IconText2,
  IconText2Rec_default as IconText2Rec,
  IconTextBlock_default as IconTextBlock,
  IconTextBold_default as IconTextBold,
  IconTextBoldRec_default as IconTextBoldRec,
  IconTextFormat_default as IconTextFormat,
  IconTextItalic_default as IconTextItalic,
  IconTextItalicRec_default as IconTextItalicRec,
  IconTextRec_default as IconTextRec,
  IconTextSlash_default as IconTextSlash,
  IconTextSlash2_default as IconTextSlash2,
  IconTextSlash2Rec_default as IconTextSlash2Rec,
  IconTextSlashRec_default as IconTextSlashRec,
  IconTextStrikethrough_default as IconTextStrikethrough,
  IconTextStrikethroughRec_default as IconTextStrikethroughRec,
  IconTextUnderline_default as IconTextUnderline,
  IconTextUnderlineRec_default as IconTextUnderlineRec,
  IconThermometer_default as IconThermometer,
  IconThumbsDown_default as IconThumbsDown,
  IconThumbsDown2_default as IconThumbsDown2,
  IconThumbsUp_default as IconThumbsUp,
  IconThumbsUp2_default as IconThumbsUp2,
  IconTicket_default as IconTicket,
  IconTime_default as IconTime,
  IconTimer_default as IconTimer,
  IconTimer2_default as IconTimer2,
  IconTimer2Rec_default as IconTimer2Rec,
  IconTinyArrow_default as IconTinyArrow,
  IconTinyArrowLeft_default as IconTinyArrowLeft,
  IconTinyBell_default as IconTinyBell,
  IconTinyCheck_default as IconTinyCheck,
  IconTinyClock_default as IconTinyClock,
  IconTinyClose_default as IconTinyClose,
  IconTinyEdit_default as IconTinyEdit,
  IconTinyLock_default as IconTinyLock,
  IconTinyMinus_default as IconTinyMinus,
  IconTinyPerson_default as IconTinyPerson,
  IconTinyPlus_default as IconTinyPlus,
  IconToggleOff_default as IconToggleOff,
  IconToggleOn_default as IconToggleOn,
  IconTransform_default as IconTransform,
  IconTrash_default as IconTrash,
  IconTrash2_default as IconTrash2,
  IconTrendDown_default as IconTrendDown,
  IconTrendDownRec_default as IconTrendDownRec,
  IconTrendUp_default as IconTrendUp,
  IconTrendUpRec_default as IconTrendUpRec,
  IconTrophy_default as IconTrophy,
  IconTruck_default as IconTruck,
  IconTv_default as IconTv,
  IconTwitter_default as IconTwitter,
  IconUmbrella_default as IconUmbrella,
  IconUndo_default as IconUndo,
  IconUndo2_default as IconUndo2,
  IconUndo2Rec_default as IconUndo2Rec,
  IconUndoRec_default as IconUndoRec,
  IconUser_default as IconUser,
  IconUserAlert_default as IconUserAlert,
  IconUserArrowBl_default as IconUserArrowBl,
  IconUserArrowBottom_default as IconUserArrowBottom,
  IconUserArrowRight_default as IconUserArrowRight,
  IconUserArrowTop_default as IconUserArrowTop,
  IconUserArrowTr_default as IconUserArrowTr,
  IconUserCheck_default as IconUserCheck,
  IconUserCircle_default as IconUserCircle,
  IconUserCross_default as IconUserCross,
  IconUserMinus_default as IconUserMinus,
  IconUserPlus_default as IconUserPlus,
  IconUserSquare_default as IconUserSquare,
  IconUsers_default as IconUsers,
  IconVector_default as IconVector,
  IconVerified_default as IconVerified,
  IconVideoCamera_default as IconVideoCamera,
  IconVideoCameraSlash_default as IconVideoCameraSlash,
  IconVideoLibrary_default as IconVideoLibrary,
  IconVideoPlus_default as IconVideoPlus,
  IconVideoQueue_default as IconVideoQueue,
  IconVoicemail_default as IconVoicemail,
  IconVolumeCross_default as IconVolumeCross,
  IconVolumeHigh_default as IconVolumeHigh,
  IconVolumeLow_default as IconVolumeLow,
  IconVolumeMute_default as IconVolumeMute,
  IconVolumeSlash_default as IconVolumeSlash,
  IconWallet_default as IconWallet,
  IconWalletMinus_default as IconWalletMinus,
  IconWalletMove_default as IconWalletMove,
  IconWalletPlus_default as IconWalletPlus,
  IconWatch_default as IconWatch,
  IconWatch2_default as IconWatch2,
  IconWebcam_default as IconWebcam,
  IconWebcamSlash_default as IconWebcamSlash,
  IconWeight_default as IconWeight,
  IconWeighter_default as IconWeighter,
  IconWeighter2_default as IconWeighter2,
  IconWheelchair_default as IconWheelchair,
  IconWheelchairRec_default as IconWheelchairRec,
  IconWifi_default as IconWifi,
  IconWifiRec_default as IconWifiRec,
  IconWifiSlash_default as IconWifiSlash,
  IconWifiSlashRec_default as IconWifiSlashRec,
  IconWindows_default as IconWindows,
  IconWrapText_default as IconWrapText,
  IconWrapTextRec_default as IconWrapTextRec,
  IconWrench_default as IconWrench,
  IconYinYang_default as IconYinYang,
  IconYoutube_default as IconYoutube,
  IconZoomIn_default as IconZoomIn,
  IconZoomOut_default as IconZoomOut,
  Indicator,
  Indicators,
  InlineLink,
  Input,
  InputAffix,
  InputWithSave,
  Label4 as Label,
  Line,
  List2 as List,
  Loadable,
  Logo,
  Logomark,
  Message,
  ModalActions as ModalActionsLegacy,
  ModalBody as ModalBodyLegacy,
  ModalFooter as ModalFooterLegacy,
  ModalHeader as ModalHeaderLegacy,
  Modal as ModalLegacy,
  ModalSubtitle as ModalSubtitleLegacy,
  ModalTitle as ModalTitleLegacy,
  MultiEmailInput,
  Notice,
  NoticeAction,
  OptionButton,
  OptionButtonGroup,
  Page2 as Page,
  Pagination,
  Paragraph,
  PopoverProvider,
  ProductOverview,
  ProductProgress,
  ProgressBar,
  ProgressLine,
  ProgressNav,
  ProgressSteps,
  Questionnaire,
  Radio,
  MemoizedSelect as Select,
  SelectWithLabel,
  Skeleton,
  SkeletonLine,
  SkeletonLineItem,
  Spacer,
  Spinner,
  Table,
  Tabs,
  TextArea,
  Toaster,
  Toggle,
  Tooltip,
  TooltipProvider,
  TypeSelect,
  Typography,
  UploadButton,
  addErrorToast,
  addSuccessToast,
  borders,
  colors,
  desktop,
  desktopLarge,
  font,
  media,
  radius,
  shadows,
  tablet,
  theme,
  themeFonts,
  useAccountantPortalChrome,
  useFileDragAndDrop,
  useIsDesktop,
  useIsDesktopLarge,
  useIsTablet,
  useMediaQuery,
  useQuestionnaire,
  useQuestionnaireConfig
};
//# sourceMappingURL=index.js.map